var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/hooks/use-intl.tsx
import React, { useMemo } from "react";
import { genesisStyled, Box } from "@leagueplatform/genesis-commons";
import { useIntl as useReactIntl } from "react-intl";
var MessageContainer = genesisStyled(Box)`
  p:last-of-type {
    margin-bottom: 0;
  }
`;
var useIntl = () => {
  const intl = useReactIntl();
  return useMemo(
    () => __spreadProps(__spreadValues({}, intl), {
      $tmap: (map) => (
        /**
         * There is currently no way to retain the overloaded funtions'
         * parameter and return types from `react-intl` when wrapping them in
         * the alternative forms $tmap and $tifelse.
         *
         * For now, the type safety from the perspective of the consumer's end
         * is worth the TS error supression comments are worth
         *
         * See source for context https://github.com/formatjs/formatjs/blob/main/packages/intl/src/create-intl.ts#L143-L145
         */
        // @ts-expect-error
        (key, ...restArgs) => (
          // @ts-expect-error
          // we suppress the rule here, since this "the one internal use"
          // eslint-disable-next-line @leagueplatform/literal-i18n-message-id
          intl.$t({ id: map[key] || key }, ...restArgs)
        )
      ),
      // @ts-expect-error
      $tifelse: (condition, thenId, elseId, ...restArgs) => condition ? (
        // we suppress the rule here, since this "the one internal use"
        // eslint-disable-next-line @leagueplatform/literal-i18n-message-id
        intl.$t({ id: thenId }, ...restArgs)
      ) : (
        // eslint-disable-next-line @leagueplatform/literal-i18n-message-id
        intl.$t({ id: elseId }, ...restArgs)
      ),
      formatMoney: ({ F = 0, C = "USD" } = {}, {
        minimumFractionDigits = 2,
        style = "currency",
        currencyDisplay = "symbol"
      } = {}) => intl.formatNumber(F, {
        currency: C,
        minimumFractionDigits,
        style,
        currencyDisplay
      }),
      formatHTMLMessage: (props, values) => /* @__PURE__ */ React.createElement(
        MessageContainer,
        {
          as: "span",
          dangerouslySetInnerHTML: {
            // we suppress the rule here, since this "the one internal use"
            // eslint-disable-next-line @leagueplatform/literal-i18n-message-id
            __html: intl.formatMessage(props, values)
          }
        }
      )
    }),
    [intl]
  );
};

// src/get-current-locale.ts
import { ConfigStoreStatus, getConfig } from "@leagueplatform/config";

// src/strings/en.json
var en_default = {
  COMPLETED_ACTIVITIES: "Completed Activities",
  AVAILABLE: "Available",
  SEE_MORE_ACTIVITIES: "See More",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "See More Completed Activities",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "When you complete activities, they will appear here!",
  MISSED_ACTIVITIES: "Missed Activities",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "See More Missed Activities",
  MISSED_ACTIVITIES_EMPTY_STATE: "When you miss activities, they will appear here!",
  DISPLAYING_ACTIVITIES_FROM_DATE: "Displaying activities since {date}",
  STANDALONE_ACTIVITIES: "Standalone Activities",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "Nothing to do here yet! Come back later.",
  VIEW_ALL_STANDALONE_ACTIVITIES: "View All Standalone Activities",
  MODULE_TEMPLATE_BUTTON: "Module Template Button",
  ACHIEVEMENTS: "Achievements",
  VIEW_ACHIEVEMENTS: "View achievements",
  DEPENDENT_NOTIFICATION_MESSAGE: "New dependents could be invited",
  DEPENDENT_COVERED_BY_HSA_ONLY: "Note: This dependent will be only covered under your Health Spending Account.",
  WALLET_LANDING_NAV_LABEL: "Wallet Navigation",
  VIEW_CARD_FOR_DEPENDENT: "View card for {dependentName}",
  LEARN_MORE_LEAGUE_FAMILY: "about League Family for your dependents",
  GO_TO_LIFEMARKET: "Go to Lifemarket to view order number {invoiceId}",
  GO_TO_CLAIM: "View claim details for {title}",
  GO_TO_RECEIPT: "View receipt for {title}",
  LINK_TO_TRANSACTION_DETAILS: "Link to details",
  VIEW_CLAIM_LABEL_HIDDEN: "View {vendor} claim {referenceId}",
  QLE_LINK_LABEL: "Learn more about qualifying life events",
  PLEASE_CALL: "Please call {phoneNumber}",
  BROWSE_FILES: "Browse Files",
  TOTAL_BALANCE: "Total Balance",
  LIFEMARKET: "Lifemarket",
  ABOUT: "About",
  LEAGUE_CREDIT_DESCRIPTION_1: `You can spend League Credit in {lifemarketLink} on a variety of lifestyle and wellness products and services. The only thing you can't spend it on is anything marked as a "League Partner Offer".`,
  LEAGUE_CREDIT_DESCRIPTION_2: 'To use your League Credit, select "League Credit" from the list of payment options when you checkout from Lifemarket. {learnMoreLink}',
  DID_YOU_GET_A_PROMO_CODE: "Did You Get a Promo Code?",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "If you received a store credit or a promo code, you can enter it here to receive the credits. Once entered they will be automatically applied to your account.",
  GET_CARE: "Get Care",
  DISCLAIMER: "Disclaimer",
  JOINED: "Joined",
  ROLLED_OVER: "Rolled Over",
  NEED_MORE_INFO_SINGLE_CLAIM: "Your claim needs more info to be processed. View details below.",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "Your claims need more info to be processed. View details below.",
  RECENT_CLAIMS: "Recent Claims",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "If you have an urgent claim, please contact <a>Customer Support</a>.",
  ABOUT_THIS_BENEFIT: "About this Benefit",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "This benefit is made available to you by your employer. For questions or detailed information, please reach out to your HR administrator.",
  ID_LIKE_TO_DOTDOTDOT: "I'd like to ...",
  HAS_DEPENDENTS_DESCRIPTION: "Your dependents for this benefit are listed below. If you recently experienced a life event, you may be eligible to make changes to your dependents. Click the button below to check if you qualify in just a few steps.",
  HAS_NO_DEPENDENTS_DESCRIPTION: "You don't have any dependents associated with this benefit. If you recently experienced a life event, you may be eligible to add or change your dependents. Click the button below to check if you qualify in just a few steps.",
  ADD_A_DEPENDENT_CAPITAL: "Add a Dependent",
  WALLET_FEEDBACK_TITLE: "Help us improve the Wallet experience",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: "How helpful is the information provided for your \u201C{benefitName}\u201D benefit?",
  WALLET_FEEDBACK_SUBTITLE_OTHER: "How helpful is the information provided for your {cardName}?",
  GO_BACK: "Go back",
  WALLET_LANDING_TITLE: "Your benefits and coverage info, all in one place.",
  WALLET_LANDING_BODY: "Your digital wallet is home to your employer benefits, programs and coverage details. Find what you need in a quick tap.",
  POLICY_ACTIVE: "Policy Active",
  REMAINING_BALANCE: "Remaining Balance",
  CONTACT_CUSTOMER_CARE: "contact Customer Care",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "Uh oh, looks like something went wrong",
  WERE_HAVING_TROUBLE: "We\u2019re having trouble loading this information, please try to refresh the page. If the problem persists, please {custCareLink}.",
  IN_NETWORK: "In-Network",
  OUT_OF_NETWORK: "Out-of-Network",
  PROGRAMS: "Programs",
  PROGRAM_CREATED_BY: "Program Created by",
  TOP_FOCUS_AREA: "Top focus area",
  DIMENSION_OF_HEALTH: "DIMENSION OF HEALTH",
  READING_MORE_ABOUT_DIMENSIONS: "Interested in reading more about how dimensions impact your overall health?",
  READ_OUR_ARTICLE: "Read Our Article",
  HEALTH_PROGRAMS_FOR_YOU: "Health Programs for you",
  COMPLETE_ACTIVITIES: "Complete activities to reach your health goals. Recommended based on your Health Profile.",
  FT: "ft",
  CM: "cm",
  IN: "in",
  LB: "lb",
  KG: "kg",
  LETS_BEGIN: "Let\u2019s Begin!",
  KEEP_GOING: "Keep Going",
  SKIP: "Skip",
  HP_ONBOARDING_SUBMIT: "Let's begin",
  FOR_PROVIDERS_AND_PHARMACISTS: "For Providers/Pharmacists",
  WEVE_PICKED_UP: "We've picked up where you left off.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "You're only a few questions away from earning {points} points.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "You're only a few questions away from completing the assessment.",
  PRESS_ENTER: "Press Enter",
  SELECT_ALL_THAT_APPLY: "Select all that apply",
  SELECT_UP_TO: "Select up to {number}",
  AMOUNT_QUESTIONS: "{amount} questions",
  QUESTION_PROGRESS: "QUESTION {questionNumber} OF {totalQuestions}",
  COMPLETE: "Complete",
  VIEW_MORE: "View More",
  DOWNLOAD_APP_STORE: "Download on the App Store",
  DOWNLOAD_PLAY_STORE: "Get it on Google Play",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "We\u2019re sorry, the page you\u2019ve requested has either expired or is no longer valid.",
  EARNED_X_TIMES: "Earned {amount}X",
  EARNED: "Earned",
  YOUVE_EARNED_TOTAL: "You've earned a total of",
  YOUVE_EARNED_GRAND_TOTAL: "You've earned a grand total of",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "You've earned a Achievement Badge",
  CONGRATS_COMPLETED: "Congrats! You've completed",
  VIEW_MY_HEALTH_DASHBOARD: "View My Health Dashboard",
  LATEST_INSIGHTS_METRICS_AND_MORE: "Latest insights, metrics, and more",
  FEEDBACK_TITLE: "How helpful was this information?",
  FEEDBACK_MIN_SCALE: "Unhelpful",
  FEEDBACK_MAX_SCALE: "Very helpful",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "Have you recently experienced changes in your life or family?",
  DID_YOU_RECENTLY: "Did you recently get married or have a baby? Certain life events may qualify you for re-enrollment.",
  LEARN_MORE: "Learn More",
  VIEW_ACTIVITY: "View Activity",
  CONTINUE: "Continue",
  ARIA_CIRCLED_CHECKMARK: "White checkmark enclosed within a circle",
  NOT_NOW: "Not Now",
  VAL_POINTS: "{points} points",
  ACTIONS: "Actions",
  VIEW_ALL_CLAIMS: "View All Claims",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "This box must be checked to continue",
  PLEASE_NOTE: "Please Note",
  COPY: "Copy",
  STATUS: "Status",
  STARTED: "Started",
  APPROVED: "Approved",
  CANCELLED: "Cancelled",
  UPLOADED_DOCUMENT: "Uploaded {date} \u2022 Uploaded by {user}",
  MORE_INFORMATION_NEEDED: "Needs Info",
  PAID: "Paid",
  PENDING_EXTERNAL_REVIEW: "Sent for External Review",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "Pre-approval More Info",
  PREAPPROVAL_REJECTED: "Pre-approval Rejected",
  PREAPPROVED: "Pre-approved",
  REJECTED: "Rejected",
  SUBMITTED_FOR_PREAPPROVAL: "Submitted for Pre-approval",
  SUBMITTED: "Submitted",
  WALLET: "Wallet",
  AMOUNT: "Amount",
  ADD: "Add",
  EDIT: "Edit",
  DETAILS: "Details",
  AND: "and",
  TODAY: "Today",
  CLAIMS: "Claims",
  GROUP_ID: "Group ID",
  ZIP_CODE: "Zip Code",
  CITY: "City",
  STATE: "State",
  REMOVE: "Remove",
  HEALTH_SPENDING_ACCOUNT: "Health Spending Account",
  HSA: "HSA",
  LIFESTYLE_SPENDING_ACCOUNT: "Lifestyle Spending Account",
  LSA: "LSA",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "Health Reimbursement Arrangement",
  DRUGS: "Drugs",
  TRAVEL: "Travel",
  HEALTH_INSURANCE: "Health Insurance",
  SETTINGS: "Settings",
  ACTIVE: "Active",
  ENDED: "Ended",
  SUSPENDED: "Suspended",
  PENDING: "Pending",
  LOADING: "Loading",
  ERROR_STATE_TITLE: "Something went wrong",
  HELP_EMAIL: "help@league.com",
  SUBMIT: "Submit",
  EARN_NUMBER_POINTS_FULL: "Earn {points} points",
  CLOSE: "Close",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "This challenge runs year-round",
  END_DAY_AT_TIME: "Ends {day}",
  ENDED_DAY_AT_TIME: "Ended {day}",
  START_DAY_AT_TIME: "Starts {day}",
  SPONSOR_LOGO: "{name} logo",
  START_AND_END_DATES: "Starts {startDate} \u2022 Ends {endDate}",
  SIGN_UP_BY_DATE: "Sign up by {date}",
  SIGN_UP_BEGINS_DATE: "Sign-up begins {date}",
  SIGN_UP_DATE_PASSED: "Sign-up date passed",
  SIGN_UP_PERIOD_PASSED: "Sign-up period passed",
  CHALLENGE_DETAILS: "Challenge Details",
  CHALLENGE_PROGRESS: "Challenge Progress",
  X_PERCENT_COMPLETE: "{number}% Complete",
  SIGN_UP_FOR_CHALLENGE: "Sign Up",
  GROUP_CHALLENGE: "Group Challenge",
  SOLO_CHALLENGE: "Solo Challenge",
  TRACK_WEARABLES_ALT_TEXT: "Person looking at smart watch data",
  CONNECT_A_DEVICE: "Connect a Device",
  DEVICES_CONNECTED_TITLE: "Looks like you\u2019re already connected!",
  DEVICES_CONNECTED_DESCRIPTION: "We\u2019re already syncing your data. If you choose to track automatically, we can automatically mark activities as complete using your device's {dataPoint}.",
  DEVICES_UNCONNECTED_TITLE: "Let\u2019s connect your device!",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "Tracking automatically",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "Tracking manually",
  DEVICES_UNCONNECTED_DESCRIPTION: "Connect your favorite health apps and devices to automatically mark activities as complete.",
  TRACK_CHALLENGE_AUTOMATICALLY: "Track automatically",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "If you choose to track automatically, after you\u2019ve signed up, {instructionsLink} to connect your device using the League mobile app.",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "follow these steps",
  TRACK_CHALLENGE_MANUALLY: "Track manually instead",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "If you choose to track manually, you can still connect a device later. {learnMoreLink} about tracking your progress manually.",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "Learn more",
  TRACK_HEALTH_DATA_ALT: "Dashboard with graph tracking health data",
  TRACK_MANUALLY_CONFIRM_TITLE: "Are you sure you want to track manually?",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "You will still be able to connect a device at a later time",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "Are you sure you want to exit?",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "If you choose to exit, your changes may not be saved.",
  EXIT: "Exit",
  CHALLENGE: "Challenge",
  CHALLENGES: "Challenges",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "Sign up for a challenge to complete daily activities and compete against your peers!",
  THIS_CHALLENGE_HAS_ENDED: "This challenge has ended",
  COMPLETED_ON_DATE: "Completed on {date}",
  ENDED_ON_DATE: "Ended on {date}",
  LEAVE_CHALLENGE: "Leave Challenge",
  LEAVE_CHALLENGE_QUESTION: "Leave Challenge?",
  LEAVE_CHALLENGE_DETAIL: "Are you sure you want to remove this challenge from your journey? Any progress in this challenge will be reset.",
  LEAVE: "Leave",
  SIGN_UP_FOR_THE_CHALLENGE: "Sign Up for the Challenge",
  CHALLENGE_SENSITIVE_DATA: "Your health data and personal information are highly sensitive and it\u2019s our responsibility to keep it private and secure. We take this responsibility very seriously.",
  CHALLENGE_USING_DATA_TO: "When you sign up for a challenge, we are using your data to:",
  COMPLETE_ACTIVITIES_SHORT: "Complete activities",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "Share your challenge progress with your employer",
  SHARE_CHALLENGE_PROGRESS: "Share your challenge progress with your peers (and employer)",
  RANK_SCORE: "Rank your score in a leaderboard",
  DETERMINE_CHALLENGE_WINNERS: "Determine challenge winners",
  ACCEPT_BEFORE_CONTINUE: "You must accept the privacy policy before continuing.",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "By checking this box, I agree to the <a>privacy policy.</a>",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "Learn more about how League manages and uses your data in our <a>privacy policy.</a>",
  PRIZE: "Prize",
  RULES: "Rules",
  LEADERBOARD: "Leaderboard",
  TOOLTIP_ACTION_DESCRIPTION: "See leaderboard ranking criterion",
  MARK_AS_DONE: "Mark as done",
  CLOSE_ACTIVITY: "Close Activity",
  GOAL_INCOMPLETE: "You must complete the goal before continuing.",
  PROGRESS_RECORD_ERROR: "There was an issue recording your last progress.",
  YOUVE_COMPLETED: "You completed",
  ACTIVITY_COMPLETE: "Activity complete",
  PAGE_NOT_FOUND_HEADER: "Page Not Found",
  PAGE_NOT_FOUND_CONTENT: "Looks like you got a little lost!",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "Let's help you find yourself.",
  PAGE_NOT_FOUND_BUTTON: "Return Home",
  REWARD_POINTS: "Reward Points",
  REWARD_POINTS_EARNED: "Reward Points Earned",
  REWARD_POINTS_MISSED: "Reward Points Missed",
  X_REWARD_POINTS: "{points} Reward Points",
  X_LEAGUE_POINTS: "{points} League Points",
  REWARDS_PROGRAM_ELIGIBLE: "Rewards Program Eligible",
  EXPIRY_DATE: "Expiry date",
  NO_EXPIRY_DATE: "No expiry date",
  POINTS_BALANCE: "Points Balance",
  REDEEM_NEXT_TIER_POINTS: "You\u2019re {points} away from redeeming {value}.",
  VIEW_YOUR_REWARDS: "View Your Rewards",
  REDEEMABLE: "Redeemable for {value}.",
  ACTIVATION_PENDING: "Activation Pending",
  EXPIRED: "Expired",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "Your account is active and in the process of being set up.",
  PLEASE_TRY_AGAIN_SHORTLY: "Please try again shortly",
  MANAGE_DEPENDENTS: "Manage Dependents",
  VIEW_CARD: "View Card",
  MEMBER_ID: "Member ID",
  SPOUSE: "Spouse",
  COMMON_LAW: "Common-law",
  CHILD: "Child",
  PARENT: "Parent",
  GRANDPARENT: "Grandparent",
  GRANDCHILD: "Grandchild",
  UNCLE_AUNT: "Uncle / Aunt",
  NEPHEW_NIECE: "Nephew / Niece",
  INELIGIBLE_CHILD: "Ineligible Child",
  SIBLING: "Sibling",
  OTHER: "Other",
  DEPENDENT_CARD_HOLDER: "{dependentName}'s card for",
  DATE: "Date",
  DESCRIPTION: "Description",
  TYPE: "Type",
  ID: "ID",
  CLAIM: "Claim",
  TOP_UP: "Top Up",
  ADJUSTMENT: "Adjustment",
  REFUND: "Refund",
  LEAGUE_CREDIT: "League Credit",
  CREDIT_CARD: "Credit Card",
  CGA: "CGA",
  NO_TRANSACTIONS_YET: "No transactions yet",
  NO_MORE_TRANSACTIONS: "No more transactions",
  PROMO_CODE: "Promo code",
  PROMO_CODE_NOT_FOUND: "Invalid promo code",
  PROMO_CODE_SUCCESS: "Promo code redeemed!",
  TOTAL_CREDIT: "Total Credit:",
  CREDIT_INFORMATION: "Credit Information",
  ENTER_CODE: "Enter Code",
  DOCUMENT_NAME: "Document name",
  REQUIRED: "Required",
  CANCEL: "Cancel",
  CONFIRM: "Confirm",
  ALLOCATION: "Allocation",
  COVERED: "Covered",
  NOT_COVERED: "Not covered",
  COVERED_WITH_RESTRICTIONS: "Covered with restrictions",
  EXPAND_ALL: "Expand All",
  COLLAPSE_ALL: "Collapse All",
  EXAMPLES: "Examples: ",
  RESTRICTIONS: "Restrictions",
  SEARCH_COVERAGE: "Search your coverage",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "Search results will update automatically as you type",
  INDIVIDUAL: "individual",
  INDIVIDUAL_CAP: "Individual",
  DEPENDENTS: "Dependents",
  SELECT_ONE: "Select one",
  MORE_OPTIONS: "More Options",
  VENDOR: "Vendor",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "they will automatically be added to your {benefitName}.",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "<strong>{percent}%</strong> will be added to your <strong>{benefitName}</strong>",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "<strong>{percent}%</strong> of it will be added to your <strong>{benefitName}</strong>.",
  DOCUMENT_TITLE: "{title} - League",
  MEDICAL: "Medical",
  TOTAL: "Total",
  PROFILE_UPDATE_FAILED: "Profile Update Failed",
  PROFILE_PHOTO: "Profile photo",
  PROFILE_PHOTO_DESCRIPTION: "This photo shows up on your user profile",
  SELECT_AVATAR: "Select Avatar",
  SELECT_AVATAR_ERROR: "You must choose an avatar before continuing.",
  GET_AVATARS_ERROR: "Oops! There was an issue loading the avatars. Please go back and select a different option.",
  ADDRESS_LINE_1: "Address Line 1",
  ADDRESS_LINE_2: "Address Line 2",
  LANGUAGE_SELECTOR_ENGLISH: "English",
  LANGUAGE_SELECTOR_FRENCH: "Fran\xE7ais",
  LANGUAGE_SELECTOR_SPANISH: "Espa\xF1ol",
  DOMESTIC_PARTNERSHIP: "Domestic Partnership",
  YES: "Yes",
  NO: "No",
  WHY_DO_YOU_ASK: "Why do you ask?",
  LAST_DAY_OF_ENROLLMENT: "Last day of enrollment period: {lastDay}",
  WALLET_WELCOME: "Welcome to your Wallet!",
  WALLET_DESCRIPTION: "Your wallet is where you'll be able to access your benefits, submit claims, and check your coverage.  You will be able to submit claims for costs incurred during the enrollment period once your wallet becomes active. In the meantime, {healthProgramLink}!",
  HEALTH_PROGRAM_LINK_COPY: "try one of our health programs",
  FINALIZE_SELECTIONS_HEADER_COPY: "Do you want access to your benefits sooner?",
  FINALIZE_SELECTIONS_COPY_1: "By finalizing your selections, we\u2019ll be able to send your plan details to the carrier right away! This means you won't have to wait until the end of your enrollment period to view your wallet.",
  FINALIZE_SELECTIONS_COPY_2: "This also means that you won\u2019t be able to make any changes to your plan after clicking submit.",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "I understand I won't be able to change my selections",
  FINALIZE_PLAN_SELECTIONS: "Finalize Plan Selections",
  ERROR_FINALIZING_SELECTIONS: "There was an error submitting your selections.",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "Looks like your selections are incomplete. Please complete enrollment and try again.",
  SELECTIONS_FINALIZED_SUCCESS: "Selections finalized!",
  TOTAL_REWARDS_LABEL: "Coverage Summary",
  TOTAL_REWARDS_DESCRIPTION: "Review the plan selections that you made during your enrollment.",
  TOTAL_REWARDS_LINK: "View Coverage Summary",
  CHANGE_PLAN_LABEL: "Change Plan Selections",
  CHANGE_PLAN_DESCRIPTION: "Want to make changes to your plan or personal information?",
  CHANGE_PLAN_LINK: "Change Plan Selections",
  DISMISS_TOAST: "Dismiss notification",
  UPLOAD_DOCUMENT: "Upload Document",
  REQUIRED_FORM: "Required",
  PERCENTAGE_USED: "Percentage used.",
  SUCCESS: "Success!",
  ERROR: "Error",
  OVERVIEW: "Overview",
  THIS_FIELD_IS_REQUIRED: "This field is required",
  INVALID_ZIP_CODE_NO_FORMAT: "Invalid zip code",
  INVALID_DAY_ERROR_MESSAGE: "The day you selected is invalid.",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "Must be at most {length} characters",
  COMPLETED_STATE_TITLE: "Completed",
  DRAG_OR_UPLOAD_PHOTO: "Upload a photo of this document",
  ADD_IMAGE: "Add more pages to this document (if applicable)",
  IDENTIFICATION_TYPE_TWO_FILES: "Identification documents can only have up to 2 supporting images.",
  WELCOME: "Welcome",
  GOT_IT: "Got It!",
  SAVE: "Save",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "View more details",
  LAST_UPDATED: "Last Updated",
  CLAIM_REF_NUMBER: "Claim #",
  VIEW_ALL: "View All",
  HRA: "HRA",
  SEARCH: "Search",
  DAY: "Day",
  MONTH: "Month",
  YEAR: "Year",
  BACK: "Back",
  NEXT: "Next",
  PREVIOUS: "Previous",
  HELP_CENTER: "Help Center",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "Minimize chat",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "Suggested replies available",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "Chat participant is typing",
  VIEW_DOCUMENT: "View Document:",
  CATEGORIES: "Categories",
  ACTIVE_PROGRAM: "Active {length, plural, one {Program} other {Programs}}",
  ACTIVE_CHALLENGE: "Active {length, plural, one {Challenge} other {Challenges}}",
  PROGRAM_PROGRESS: "Program Progress",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "You can also type to create a new option!",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "Select your options",
  COVERAGE: "Coverage",
  HEALTH_PROFILE: "Health Profile",
  HEALTH_PROGRAM: "Health Program",
  HEALTH_PROGRAMS: "Health Programs",
  STEPS: "steps",
  ACTIVE_DURATION: "active minutes",
  MINDFUL_DURATION: "mindful minutes",
  FLOORS_CLIMBED: "flights climbed",
  DISTANCE: "distance traveled",
  ENERGY_BURNED: "calories burned",
  METRIC_DETAILS: "Metric Details",
  HEALTH_PROGRAMS_SYNC_DEVICE: "Sync your {device} for the optimal program experience.",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "Connected health program",
  TRACK_AUTOMATICALLY: "Track Your Progress Automatically",
  SYNC_ISSUE: "SYNC ISSUE",
  FIX_SYNC_ISSUE: "Fix this issue",
  DATAPOINT_SYNC_ISSUE: "There was an issue syncing your {dataPoint}.",
  CONNECT_TO_TRACK: "Connect your favourite health apps and devices to automatically mark activities as complete.",
  CONNECT_APPS_DEVICES: "Connect apps & devices",
  ADD_APPS_DEVICES: "View Apps & Devices",
  MANAGE_UNITS: "Change units of measurement",
  DONT_CONNECT_APPS_DEVICES: "Don't track my progress automatically",
  CONNECTING_UNAVAILABLE: "Connecting apps and devices is not available on the web",
  CONNECTING_UNAVAILABLE_DETAILS: "For now, please visit the League mobile app to connect your favourite health apps and devices.",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "For now, please visit the League mobile app to connect your favourite health apps and devices. If you\u2019d like to learn more, you can find a full list of the",
  CONNECT_DEVICES_LEARN_MORE: "Learn More",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "apps and devices currently supported",
  DONT_HAVE_THE_APP: "Don\u2019t have the League mobile app?",
  WHAT_DEVICES_CAN_CONNECT: "What apps and devices are supported?",
  CONNECTED_DEVICES_IMAGE_ALT: "Person reviewing data from connected device",
  MORE_GREAT_RESOURCES: "More great resources",
  HISTORY: "History",
  TOP_MATCH: "Top match",
  CHAT: "Chat",
  END_CHAT: "End Chat",
  BECOMES_ACTIVE_ON_DATE: "Becomes active on {date}",
  BECOMES_ACTIVE_IN_X_DAYS: "This benefit becomes active in {days} days",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "You can only submit claims with a purchase/service date between <strong>{dateRange}</strong> for this benefit. View your coverage details for more info.",
  SUBMIT_PAST_CLAIMS_BY_DATE: "Submit past claims by {date}",
  THIS_POLICY_HAS_EXPIRED: "This policy period has expired, but you have {days} days left to submit past claims for this benefit",
  THIS_POLICY_EXPIRES_TOMORROW: "This policy period has expired, but today is your last day to submit past claims for this benefit",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "You have until <strong>{inactiveDate}</strong> to submit past claims with a purchase/service date on or before <strong>{endDate}</strong>",
  EXPIRES_ON_DATE: "Expires on {date}",
  ASSIGNED: "Assigned",
  ASSIGNED_ON: "on {date}",
  ASSIGNED_TODAY: "today",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "You have {days} days left to use this benefit before the policy period expires",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "After expiry, you\u2019ll have until <strong>{inactiveDate}</strong> to submit claims with a purchase/service date on or before <strong>{endDate}</strong>",
  LOAD_MORE: "Load more",
  SHOW_MORE: "Show more",
  LINK_OUT_MESSAGE: "Must See",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "Oops, there seems to have been a problem.",
  AGENT_PERMISSION_ERROR: "You don't have permission to view this.",
  TRY_AGAIN: "Try Again",
  WARNING_OPENS_NEW_TAB: "Opens in a new tab",
  ANY_HEALTH_CHANGES: "Have there been any recent changes in your health?",
  LAST_UPDATED_DATE: "Last updated {date}",
  UPDATE_HEALTH_PROFILE: "Update your Health Profile",
  HEALTH_PROFILE_COMPLETION_POINTS: "You've earned {points} points",
  OPENS_IN_A_NEW_TAB: "Opens in a new tab",
  COMPLETED: "Completed",
  COMPLETED_X_TIMES: "Completed {amount} {amount, plural, one {time} other {times}}",
  REMOVE_FROM_JOURNEY: "Remove from journey",
  GO_TO_JOURNEY: "Go to Journey",
  ABOUT_THIS_PROGRAM: "About this program",
  HEALTH_JOURNEY: "Health Journey",
  REMOVE_ACTIVITY: "Remove Activity?",
  REMOVE_ACTIVITY_PROMPT: "Remove Activity",
  REMOVE_ACTIVITY_BUTTON: "Remove",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "Are you sure you want to remove this activity from your journey? This action can\u2019t be undone.",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "You will no longer see it in your journey",
  DISCOVER_ACTIVITIES: "Discover new activities",
  DISCOVER_ACTIVITIES_CAPTION: "Browse activities we think you\u2019ll love",
  ACTIVITIES: "Activities",
  ACTIVITIES_PLURALIZATION: "{count, plural, one {Activity} other {Activities}}",
  ACTIVITIES_PILL: "Activities",
  PROGRAM_LIBRARY: "Program Library",
  BROWSE_PROGRAMS: "Browse Programs",
  BACK_TO_JOURNEY: "Back to Journey",
  GO_TO_HEALTH_JOURNEY: "Go to health journey",
  ADD_TO_JOURNEY: "Add to Journey",
  REQUEST_WAIT: "Please wait while we complete your request",
  JOURNEY_DELIGHT_1: "Motivation gets you started. Habit keeps you going. You got this!",
  JOURNEY_DELIGHT_2: "Active. Focused. Staying in your lane. We love to see it.",
  JOURNEY_DELIGHT_3: "Small steps can make a big difference. You are making that step!",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "This is a free-text option; use the text box to enter your own value. The current value is: {hasValue, select, true {{value}} other {empty}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "This option is exclusive; selecting it will deselect all others",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "Play or pause the currently selected animation",
  CENTIMETERS: "Centimeters",
  FEET: "Feet",
  INCHES: "Inches",
  KILOGRAMS: "Kilograms",
  POUNDS: "Pounds",
  EARN_UP_TO_X_POINTS: "Earn up to {points} points",
  EXPIRES_TODAY: "Expires today",
  PROGRAM_DETAILS: "Program Details",
  PROGRESS: "Progress",
  X_OF_Y_COMPLETE: "{completed}/{total} complete",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "{missed} missed",
  MISSED: "Missed",
  SCROLL_CAROUSEL_RIGHT: "Scroll the carousel forwards",
  SCROLL_CAROUSEL_LEFT: "Scroll the carousel backwards",
  SCROLL_CAROUSEL_FORWARD: "Scroll the carousel forwards",
  SCROLL_CAROUSEL_BACK: "Scroll the carousel backwards",
  YOU_HAVENT_STARTED_PROGRAMS: "You haven't started any health programs",
  ADD_PROGRAM: "Add Program",
  TAP_ADD_PROGRAM_BUTTON: "Tap the \u2018Add Program\u2019 button to browse programs.",
  YOUVE_REACHED_PROGRAM_LIMIT: "You've reached your program limit!",
  START_X_MORE_PROGRAMS: "Start {duration, plural, one {# more program} other {# more programs}}. Keep the momentum.",
  OK: "Ok",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "Complete one of your active programs to add this to your journey.",
  SHOW_LESS: "Show Less",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "Looks like you\u2019ve reached your program limit!",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "Complete an active program before adding a new one. <strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "Remove program?",
  REMOVE_PROGRAM_BUTTON: "Remove",
  ARE_YOU_SURE_REMOVE_PROGRAM: "Are you sure you want to remove this program from your journey? Any progress in this program will be reset and you will have to start over from the beginning.",
  PROGRAM_REMOVED: "Program removed",
  PROGRAM_ADDED: "Program added",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "The program will now appear in your journey",
  PROGRAM_ADD_ERROR: "We were unable to add the program at this time. Please try again or choose another program.",
  PROGRAM_REMOVE_ERROR: "We were unable to remove the program at this time. Please try again.",
  SKIP_TO_CONTENT_MESSAGE: "Skip to content",
  MAIN_NAVIGATION_LIST_LABEL: "League navigation items",
  PAGE_TITLE_ACTIVITY_DETAILS: "Activity details",
  X_OF_Y: "{current} of {total}",
  ACTIVITY_COUNT_EDIT: "Edit {unit}",
  ACTIVITY_COUNT_GOAL: "Goal: {goal, number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: 'To complete this activity, click "Mark as Done".',
  ACTIVITY_COMPLETE_LATER_PROMPT: 'To keep going, click "Back to Journey" and come back later to mark it done.',
  ACTIVITY_COMPLETE_ERROR: "We were unable to complete the activity at this time. Please try again.",
  ACTIVITY_DISMISS_ERROR: "We were unable to remove the activity at this time. Please try again.",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "Please select a day, month and year",
  TOOLBOX_VALIDATION_MONTH_YEAR: "Please select a month and year",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "Characters limit is {limit}",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "Enter a number equal to or greater than {value}",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "Enter a number equal to or less than {value}",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "This is a required field",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "Please make a selection",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "Please make a selection",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "Select a date later than {date, date, medium}",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "Select a date earlier than {date, date, medium}",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "Enter a whole number",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "Select a maximum of {integer} options",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "Select {integer} options to continue",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "Field can\u2019t be empty when selected",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "Weight needs to be at least {imperial}lb ({metric}kg)",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "Weight needs to be at most {imperial}lb ({metric}kg)",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "Height needs to be at least {imperial} ({metric} cm)",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "Height needs to be at most {imperial} ({metric} cm)",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "Enter your weight in kilograms",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "Enter your weight in pounds",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "Enter your height in centimeters",
  ENTER_YOUR_HEIGHT_IN_FEET: "Enter your height in feet",
  ENTER_YOUR_HEIGHT_IN_INCHES: "Enter your height in inches",
  GOAL_REACHED: "You hit the goal!",
  COMPLETE_ACTIVITY: "Complete Activity",
  EXIT_ACTIVITY_TITLE: "Are you sure you want to leave this activity?",
  EXIT_ACTIVITY_BODY: "Your progress won\u2019t be saved and you will have to restart the activity from the beginning.",
  EXIT_ACTIVITY_CTA: "Leave Activity",
  ACTIVITY_NEXT_STEP: "Continue activity",
  ACTIVITY_PREV_STEP: "Return to the previous step",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "Health Program Category",
  NEXT_STEP: "Next Step",
  YOU_EARNED: "You earned",
  YOU_EARNED_X_POINTS: "You earned {points} points!",
  CREATE: "Create {label}",
  TOMORROW: "Tomorrow",
  YESTERDAY: "Yesterday",
  BACK_TO_TODAY: "Back to today",
  PREVIOUS_DAY: "Previous day",
  NEXT_DAY: "Next day",
  ACTIVITY_STATUS: "Activity status: ",
  ACTIVITY_STATUS_EXPIRED: "Expired",
  ACTIVITY_STATUS_REMOVED: "Skipped",
  ACTIVITY_STATUS_UPCOMING: "Available Soon",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "Nothing planned - yet!",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: "Tap the \u2018Add\u2019 button to find new programs or browse curated list of activities below.",
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "Well done, you\u2019re on a roll!",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "Come back tomorrow for more or browse through new programs and suggested activities.",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "You don\u2019t have any activities scheduled for this day.",
  NO_ACTIVITIES_PAST_HEADING: "Rest Day",
  NO_ACTIVITIES_PAST_DESCRIPTION: "Looks like you took this day to relax and recover.",
  BUTTON_BROWSE_PROGRAMS: "Browse Programs",
  IMG_ALT_HIGH_FIVE_HANDS: "Two hands slapping a high five",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "Woman standing and scrolling a wall-sized list of programs",
  IMG_ALT_EMPTY_STATE_REST: "Man resting in a comfy chair with a warm mug and headphones",
  THIS_IS_JUST_A_SNEAK_PEAK: "This is just a sneak peek!",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "Come back and complete this activity on {date}.",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "Come back later to complete this activity.",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "Looks like you missed this one!",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "This activity expired on {date}.",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "Looks like this one wasn\u2019t meant for you",
  YOU_SKIPPED_THIS_ACTIVITY: "You skipped this activity.",
  NO_PROGRAMS: "Nothing to see here just yet",
  NO_ACTIVITY_TITLE: "Nothing here (yet!)",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "We'll display your ranking here once the challenge starts.",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "Check back here to see challenge highlights soon.",
  LEADERBOARD_ALL_TIME_RANKING: "All Time Ranking",
  LEADERBOARD_CURRENT_USER_NAME: "{user} (You)",
  ACTIVITY_PROGRESS: "Activity Progress",
  YOUR_JOURNEY: "Your journey",
  SUGGESTED_FOR_YOU: "Suggested For You",
  NO_ACHIEVEMENTS_DESCRIPTION: "Complete activities in your Journey to start unlocking achievements.",
  UNLOCK_ACHIEVEMENTS: "Unlock an achievement for completing all activities in this program.",
  MEMBERS: "Members",
  SIGN_UP_PERIOD_PASSED_DETAIL: "You can't sign up for this challenge anymore because the sign-up period has passed.",
  REFERENCES: "References",
  SIGN_UP_BY: "Sign up by {date}",
  YOURE_GOOD_TO_GO: "You're good to go!",
  CHECK_CHALLENGE_DETAILS: "Check out the challenge details to familiarize yourself with the activities",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "You're ready to start the challenge. Do you want to join or create a team?",
  CHALLENGE_ABOUT_TO_START: "Your challenge is about to start!",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "Daily activities will appear here when your challenge starts on <strong>{date}</strong>.",
  NO_CHALLENGE_ACTIVITIES_TODAY: "There are no challenge activities for you today.",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: '"{query}" could not be found.',
  COVERAGE_SEARCH_TRY_AGAIN: "Try searching again using a different keyword.",
  EARN: "Earn",
  WORD_WITH_EXCLAMATION_POINT: "{word}!",
  UPDATED: "Updated",
  YOUR_PROGRESS: "Your progress",
  YOUR_PROGRESS_IN_NUMBERS: "{current} of {total} {unit} or {percentage} percent complete",
  OUTCOMES_FRICTION_SCREEN_TITLE: "Personalizing suggestions...",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "Give us a moment to find some helpful resources for you based on your answers",
  RELOAD_MY_SUGGESTIONS: "Reload my suggestions",
  YOUR_RANKING: "Your ranking",
  TOP_RANKED: "Top Ranked",
  VIEW_CHALLENGE_DETAILS: "View Challenge Details",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "Wondering why it's beneficial to add your dependents?",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "Your dependents can access League and enjoy a healthier life too!",
  UNABLE_TO_LEAVE_CHALLENGE: "Unable to leave the challenge, please try again!",
  SUCCESSFULLY_LEFT_CHALLENGE: "You have successfully left the challenge.",
  UNABLE_TO_JOIN_CHALLENGE: "Unable to join the challenge, please try again!",
  JOIN_TEAM: "Join Team",
  X_TEAM_MEMBERS: "{memberTotal, plural, one {# team member} other {# team members}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, one {# spot available} other {# spots available}}",
  HIGHLIGHTS: "Highlights",
  CURRENT_RANK: "Current Rank",
  X_LIKES: "{activityLikes, plural, one {# Like} other {# Likes}}",
  LIKE: "Like",
  LIKED: "Liked",
  TEAM_INVITE_HEADING: "We're better together!",
  TEAM_INVITE_DESCRIPTION: "Invite friends to join your team to complete activities together.",
  TEAM_INVITE_LINK_DESCRIPTION: "The more the merrier! Share this link with friends so they can join your team.",
  TEAM_INVITE_LINK: "Team invite link",
  INVITE: "Invite",
  LEAVE_TEAM: "Leave Team",
  INVITE_FRIENDS: "Invite Friends",
  PRIVACY: "Privacy Settings",
  JOIN_TEAM_SUCCESS_TITLE: "Welcome to the team!",
  JOIN_TEAM_SUCCESS_DETAIL: "Let\u2019s conquer these activities together.",
  JOIN_TEAM_SUCCESS_BUTTON: "Go to Team Details",
  CANT_JOIN_TEAM_TITLE: "Unable to join {teamName}",
  CANT_JOIN_TEAM_DETAIL: "Please try again! If you've already joined a team, you'll need to leave your current team before you can join {teamName}.",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "Looks like you haven't signed up for the challenge yet. You'll need to sign up before you can join {teamName}.",
  LEAVE_TEAM_QUESTION: "Leave {teamName}?",
  LEAVE_TEAM_DESCRIPTION: "Are you sure you want to leave {teamName}? Your score will no longer be considered as part of the team\u2019s average.",
  CHALLENGE_ACTIVITY_DETAIL: "Allow others to see your activity in this challenge.",
  SHOW_MY_ACTIVITY: "Show My Activity",
  YOUR_TEAM: "Your team",
  JOIN_OR_CREATE_TEAM: "Join or Create a Team",
  TEAMS: "Teams",
  CHALLENGE_TEAMS_DESCRIPTION: "Join an existing team or create your own to compete in the challenge together!",
  JOIN_A_TEAM: "Join a Team",
  JOIN_TEAM_DESCRIPTION: "Search for an existing team to join and compete together!",
  SEARCH_FOR_TEAM: "Search for a team",
  CREATE_A_TEAM: "Create a Team",
  CREATE_TEAM_DESCRIPTION: "Customize your team by setting your own avatar and team name. Invite others to join you!",
  UP_YOUR_GAME: "Level up your game",
  UP_YOUR_GAME_DESCRIPTION: "There's strength in numbers. Join a team or create your own.",
  XTH_PLACE: "{currentRank, selectordinal, one {#st} two {#nd} few {#rd} other {#th} } Place",
  LEADERBOARD_RANKING: "Ranking",
  LEADERBOARD_USERNAME: "Username",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "Invite link copied to clipboard!",
  DATE_AND_TIME: "Date and Time",
  SELECT_TEAM_AVATAR: "Select Team Avatar",
  CHANGE_TEAM_AVATAR: "Change Team Avatar",
  TEAM_NAME_LABEL: "Team Name",
  CREATE_TEAM: "Create Team",
  TEAM_NAME_ERROR: "Please enter a name for your team.",
  SLIDE_OF_TOTAL: "Slide {key} of {total}",
  PAGE_OF_TOTAL: "Page {current} of {total}",
  NO_SOLUTIONS_AVAILABLE: "We're sorry, there are no solutions available at the moment.",
  SEE_ALL_ACHIEVEMENTS: "See all achievements",
  VIEW_ALL_ACHIEVEMENTS: "View All Achievements",
  TAKE_ME_BACK: "Take Me Back",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "This avatar shows up on your team\u2019s profile and on the leaderboard.",
  CREATE_TEAM_SUCCESS: "Your team is ready!",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "Start building your team. Share this link with others so you can complete activities together.",
  VIEW_TEAM_DETAILS: "View Team Details",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "Features highlight carousel",
  CHALLENGE_FAQ_HEADING: "Have questions about Challenges?",
  CHALLENGE_FAQ_CONTENT: "Check out our {helpCenterLink} for all your frequently asked questions.",
  VIEW_THIS_RESOURCE: "View This Resource",
  RECOMMENDED_NEXT_STEP: "Recommended Next Step",
  CARE_COLLECTIONS: "Care Collections",
  PLAN_PROGRESS: "Plan Progress",
  SINCE_LAST_WEEK: "since last week",
  INSIGHT_CARD_HEADER_DISTANCE: "Distance",
  INSIGHT_CARD_HEADER_STEPS: "Steps",
  MIN: "min",
  CAL: "Cal",
  KM: "km",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, one {step} other {steps}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "Flights Climbed",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, one {flight} other {flights}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "Active Minutes",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "Energy Burned",
  INSIGHT_CARD_HEADER_WEIGHT: "Weight",
  INSIGHT_CARD_HEADER_HEIGHT: "Height",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "Mindful Minutes",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "Waist Circumference",
  DAY_OF_THE_WEEK: "DAY OF THE WEEK",
  DAY_OF_THE_MONTH: "DAY OF THE MONTH",
  INSIGHT_CHART_WEEK_TITLE: "Weekly {type} bar chart",
  INSIGHT_CHART_MONTH_TITLE: "Monthly {type} bar chart",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "The number of {unit} you've walked each day of the week.",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "The number of {unit} you've walked each day of the month.",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "You've walked {distance} {unit} on {date}",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "The number of steps you've walked each day of the week.",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "The number of steps you've walked each day of the month.",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "You've walked {distance} {unit} on {date}",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "The number of {unit} you've burned each day of the week.",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "The number of {unit} you've burned each day of the month.",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "You've burned {energy} {unit} on {date}",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "The number of active minutes for each day of the week.",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "The number of active minutes each day of the month.",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "You had {value} active minutes on {date}",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "The number of flights you've climbed each day of the week.",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "The number of flights you've climbed each day of the month.",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "You've climbed {value} flights of stairs on {date}",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "The number of mindful minutes for each day of the week.",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "The number of mindful minutes each day of the month.",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "You had {value} mindful minutes on {date}",
  DATAPOINT_CATEGORY_ACTIVITY: "Activity",
  DATAPOINT_CATEGORY_BODY: "Body",
  MANAGE_UNITS_APPLYING_CHANGES: "Applying changes...",
  MANAGE_UNITS_CHANGES_SAVED: "Changes saved at {time}",
  START_TRACKING_HEALTH_HEADER: "Start tracking your health",
  START_TRACKING_HEALTH_BODY: "Easily track your metrics over time to see how far you've come and how you can work towards your health goals.",
  BROWSE_SUPPORTED_DATA: "What can I track?",
  LAST_UPDATED_CARRIER_DATE: "Last updated: {date} from {carrier}",
  ACCUMULATION_PERIOD: "Accumulation period: <strong>{start} - {end}</strong>",
  NO_DATA_AVAILABLE: "No data available",
  LAST_7_DAYS: "Last 7 Days",
  LAST_30_DAYS: "Last 30 Days",
  OLDER: "Older",
  GET_ASSISTANCE_NOW: "Chat with your care team",
  GET_ASSISTANCE_NOW_SUBHEADING: "Chat with our certified health professionals and customer care staff. They can help you save money by directing you to the most affordable, highest quality care in your area as well as answer any questions about your benefits.",
  HOW_CAN_WE_HELP: "How Can We Help?",
  LIVE_BALANCE_SPEND: "{spentAmount} of {totalAmount} spent",
  LIVE_BALANCE_REMAINING: "{amount} left",
  UPDATING_UNITS_OF_MEASURE: "Updating units of measure",
  CHANGING_YOUR_UNITS: "Changing your units will affect how they are displayed in the app.",
  MESSAGES: "Messages",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "Messages",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "{fileName} removed.",
  SEND_NEW_MESSAGE: "Send new message",
  NEW_MESSAGE_DEFAULT: "This is the start of a new message",
  NETWORK_MISSING_COVERAGE: "Your plan doesn't have {network} coverage.",
  CARD_FRONT_FILENAME: "{benefitName}-front",
  CARD_BACK_FILENAME: "{benefitName}-back",
  START_A_VISIT: "Start a Visit",
  UNREAD: "Unread",
  NO_MESSAGES_HEADING: "No messages yet",
  NO_MESSAGES_DESCRIPTION: "Get started by sending a new message!",
  RETURN_TO: "Return to {location}",
  NO_RECOMENDATIONS: "Sorry, we don\u2019t have any recommendations for this health area right now.",
  CHALLENGES_LEGAL_DISCLAIMER: "This Challenge is intended to be a fun way for everyone to participate and get a fair chance at winning. At times, cash or other prizes may be offered by the Challenge organizer. League doesn't design the Challenge structure or decide whether to offer prizes. If you have concerns about the structure of this Challenge or believe you've been excluded from the chance to win, please talk to the Challenge organizer.",
  ALL_RECORDED_DATA: "All Recorded Data",
  NEW_MESSAGE: "New Message",
  MESSAGE_SUBJECT: "Message subject (optional)",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "You are over the maximum character limit ({maximum})",
  CHAR_LIMIT_EXCEEDED: "Character limit exceed ({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "Set the message topic",
  TO: "To",
  CREATE_MESSAGE: "Create Message",
  MEMBER_LABEL: "Member",
  RESULT_COUNT: "{count, plural, one {# result} other {# results}}",
  USER_COLON: "{user}:",
  JOINED_CHALLENGE_HIGHLIGHT: "Joined <strong>{challengeTitle}</strong>!",
  CREATED_TEAM_HIGHLIGHT: "Created <strong>{teamName}</strong>!",
  JOINED_TEAM_HIGHLIGHT: "Joined <strong>{teamName}</strong>!",
  STEPS_WALKED_HIGHLIGHT: "<strong>Walked {total} steps!</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong>Moved {total} minutes!</strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong>Completed {total} minutes!</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong>Earned {achievement} Achievement!</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "<strong>Completed an activity!</strong>",
  NO_CARE_COLLECTIONS: "Sorry, there are no care collections available.",
  WRITE_A_MESSAGE: "Write a message...",
  UPLOAD_MULTIPLE_FILES: "Upload multiple files",
  SEND: "Send",
  SEND_MESSAGE: "Send message",
  DATA_SOURCES: "Data Sources",
  SOURCES_CAN_UPDATE: "The following sources are allowed to update your",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "When multiple sources are available, the data source with the largest value will be recorded at the end of the day (11:59pm).",
  APPLE_HEALTH: "Apple Health",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "Garmin",
  POLAR: "Polar",
  WITHINGS: "Withings",
  HEALTH_PLAN: "Health Plan",
  OMRON: "Omron",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "Updating units of measurement",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "Changing your units will affect how they are displayed in the app",
  DOCUMENTS: "Documents",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "Documents",
  DOCUMENTS_FILTER_LOADING_TEXT: "Tabs and filters are loading",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "{filter} is required",
  DOCUMENT_FILTER_X_REQUIRED: "{filter} (required)",
  SHOW_RESULTS: "Show Results",
  RESET_FILTERS: "Reset Filters",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "Selected filters",
  DOCUMENTS_RESULTS_HEADING: "Results",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "Skip to upload document",
  AVERAGE: "Average",
  VIEW_ALL_METRIC_DATA: "View all {metric} data",
  EDIT_METRIC_DATA: "Edit {metric} data",
  NEXT_WEEK: "Next week",
  PREVIOUS_WEEK: "Previous week",
  NEXT_30_DAYS: "Next 30 Days",
  PREVIOUS_30_DAYS: "Previous 30 Days",
  UNABLE_TO_CREATE_TEAM: "Unable to create a new team - please try again! If you've already joined a team, you'll need to leave your current team before creating a new one.",
  START_TRACKING_METRIC: "Start tracking {metric}",
  CURRENTLY_NOT_SHARING_DATA: "You\u2019re currently not sharing any data for {metric}",
  SUCCESSFULLY_LEFT_TEAM: "You have successfully left {teamName}",
  LEAVE_TEAM_FAILURE: "Something went wrong, please try again!",
  VIEW_DATA_SOURCES: "View data sources",
  OR_CALL_PHONE: "or call {phoneNumber}{extension}",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "No documents matching your filters",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "Try removing some of your filters to get more results",
  DOCUMENTS_EMPTY_STATE_HEADING: "No documents here (yet!)",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "One place to view and organize all your documents",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team} (Your team)",
  YOUR_CARE_ADVISOR: "Your Care Guide",
  COMPOSE: "Compose",
  COMPOSE_NEW_MESSAGE: "Compose a new message",
  POLICY_INFORMATION_UNAVAILABLE: "There is no information available for this policy period.",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "There's no information for this member. Please try another member.",
  MESSAGE_THREADS_LIST_SUBTITLE: "It may take up to 2 business days for our team to respond. If this is a medical emergency, call 911.",
  ALL_MESSAGES_FOR_THREAD_BANNER: "Please do not send us specific information about your health conditions through this chat.",
  TROUBLE_DISPLAYING_DATA: "We're having trouble displaying your data",
  RETRY: "Retry",
  YOU: "You",
  MESSAGES_IN_CONVERSATION_TITLED: "Messages in conversation titled {subject}",
  TEAM_AVERAGE: "Team Average",
  YOUR_METRICS: "My Metrics",
  YOUR_METRICS_SUBTITLE: "The most recent health data from your connected apps and devices.",
  APPS_AND_DEVICES: "Apps & Devices",
  APP_AND_DEVICE_METRICS: "App & Device Metrics",
  VIEW_ALL_METRICS: "View apps & devices metrics",
  RENAME: "Rename",
  ENTER_DOCUMENT_NAME: "Please enter a document name",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "This is a low priority activity",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "This is a medium priority activity",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "This is a high priority activity",
  ACTIVITY_EXPIRY_DATE: "Expires {date}",
  GENERIC_MESSAGING_ERROR: "Looks like there was a problem, please refresh the page and try again.",
  VIEW_THE_ACTIVITIES: "View the Activities",
  DRAG_FILES_HERE_OR: "Drag files here or",
  ADD_A_DOCUMENT: "Add a document",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "Upload a document",
  ADD_DOCUMENT: "Add document",
  REMOVE_FILE: "Remove file",
  REMOVE_FILE_FILENAME: "Remove file: {filename} ({fileSize})",
  UNABLE_TO_SEND: "Unable to send, please try again",
  ERROR_HEADING: "Something went wrong on our end.",
  FILE_TYPE_NOT_SUPPORTED: "File type not supported",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "Use a file in one of these formats: {fileTypes}",
  RECENT_MESSAGES: "Recent messages",
  NO_MESSAGES_YET: "You have no messages (yet!)",
  NO_MESSAGES_DETAIL: "Your assigned Care Guide will assist you in making smarter health care decisions, every step of the way.",
  START_A_NEW_MESSAGE: "Start a new message",
  USER_ADDED_DOCUMENT: "{user} added a document",
  ERROR_SAVING_TRY_AGAIN: "Error saving changes. Please try again",
  MODIFIED_ON_DATE: "Modified on {date}",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "Uploaded {date}",
  FILE_TYPE_TOO_LARGE: "File size is too large",
  MAXIMUM_FILE_SIZE_IS: "Maximum file size is {fileSize}.",
  FILE_TOO_LARGE: "Oops. That file is too large.",
  FILE_SHOULD_BE_UNDER: "Your file must be under {fileSize}",
  ATTACHMENT_LIMIT_REACHED: "Oops. That\u2019s too many files.",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "You can attach up to {maxNumberOfDocs, plural, one {# file} other {# files}}.",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "Oops. That file type isn\u2019t supported.",
  ATTACHMENT_TYPES_ALLOWED: "You can attach your file in these formats: {fileTypes}",
  MESSAGES_PAGINATION_ERROR: "Unable to load messages. <button>Try again.</button>",
  TRANSACTION_TABLE_CAPTION: "Transaction History",
  SELECT_PARTICIPANT_ERROR: "Select someone to send a message to",
  PREVIEW_OF_FILE: "Preview of {fileName}",
  VIEW_DOCUMENT_ACTIONS: "View {documentName} actions.",
  VISIT_LINK: "Visit Link",
  COMPLETE_BY_DATE: "Complete by {date}",
  ERROR_UPLOADING_YOUR_FILE: "Error uploading your file. Try again.",
  DOCUMENT_SUBMITTED: "Document submitted",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "Your document will be available soon. Please wait a few moments and refresh the page.",
  ERROR_RENAMING_YOUR_DOCUMENT: "Error renaming your document. Try again.",
  DOCUMENT_RENAMED_SUCCESSFULLY: "Document renamed successfully.",
  ERROR_HEADING_GENERIC: "Something went wrong",
  ERROR_HEADING_NO_CONNECTION: "No internet connection",
  ERROR_CAPTION_BACKEND: "There was a problem connecting to our server.\n Please try again later.",
  ERROR_CAPTION_NO_CONNECTION: "There was a problem loading data.\n Check your internet and try again.",
  SURE_YOU_WANT_TO_DELETE: "Are you sure you want to delete {name}",
  CANNOT_BE_UNDONE: "This action cannot be undone",
  DELETE_DOCUMENT: "Delete document",
  DOCUMENT_DELETED: "Document deleted",
  ERROR_DELETING_YOUR_DOCUMENT: "Error deleting your document",
  ABOUT_ME: "About Me",
  SEE_MORE: "SEE MORE",
  X_ABOUT_X: "{left} about {right}",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "We're unable to load this page. Check your internet and try again.",
  WHO_IS_THIS_DOCUMENT_ABOUT: "Who is this document about?",
  SELECT_A_USER: "Select a user",
  SELECT_DOCUMENT: "Select document",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "This helps your Care Guide identify the person this document relates to.",
  VIEW_ALL_MESSAGE_THREADS: "View all message threads",
  TOGGLE_MENU: "Toggle message list",
  METRICS: "Metrics",
  UNREAD_MESSAGES: "Unread Messages",
  WITH_DOCUMENT: "with document",
  X_TOKENS: "{tokenCount, plural, one {# token} other {# tokens}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "Programs & Challenges",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "Ready for something new?",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "Find Health Programs and Challenges that match your personal goals.",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "Programs & Challenges",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "Ready for something new?",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "Find Health Programs and Challenges that match your personal goals.",
  EXPLORE_LIBRARY: "Explore Library",
  YOUVE_GOT_THIS: "You've got this!",
  YOUVE_GOT_THIS_NAME: "You've got this, {firstName}!",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "Welcome to your health journey!",
  HEALTH_JOURNEY_PAGE: "Health Journey Page",
  VIEW_DETAILS: "View Details",
  NEXT_ACTIVITY: "Next Activity",
  X_OF_Y_ACTIVITIES: "{completed} of {total} activities",
  X_PERCENTAGE: "{percent}%",
  SOLUTIONS: "Solutions",
  EXPLORE: "Explore",
  DOCUMENT_NAME_TOO_LONG: "You are over the maximum character limit ({maximum})",
  CLAIM_PATIENT_NAME: "For: {patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "No Programs. No Challenges. Just one-off activities.",
  FEEDBACK_LINK_LABEL: "Rate information {value} out of {maximum} in helpfulness",
  RECENT_ACHIEVEMENTS: "Recent Achievements",
  ACHIEVEMENTS_BANNER_HEADER: "Congrats on earning these badges!",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "We love to see you prioritizing your health and well-being! Check out other achievements you're working towards.",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "We love to see you prioritizing your health and well-being! Check out other achievements you've earned and those you're working towards.",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "No achievements (yet!)",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "Complete activities in your Journey to start earning achievements.",
  HISTORY_EMPTY_STATE_HEADER: "Nothing here - yet!",
  HISTORY_EMPTY_STATE_DESCRIPTION: "When you complete an activity, program, or challenge it will appear here.",
  INDIVIDUAL_BALANCE: "{membersFullName}'s {balance}",
  FAMILY_BALANCE: "Family {balance}",
  UPCOMING: "Upcoming",
  REMOVED: "Removed",
  COMPLETED_TODAY: "Completed",
  CLAIMS_BENEFIT_CARD_TITLE: "Insurance Claims",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "Keep track of what you owe for your care and how much insurance will cover",
  CLAIMS_FILTER_PROPERTY_TYPE: "Service Type",
  CLAIMS_FILTER_PROPERTY_STATUS: "Claim Status",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "Member",
  CLAIMS_FILTER_VALUE_ORAL: "Dental",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "Medical",
  CLAIMS_FILTER_VALUE_VISION: "Vision",
  CLAIMS_FILTER_VALUE_PHARMACY: "Pharmacy",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "Professional",
  CLAIMS_FILTER_VALUE_APPROVED: "We'll Pay Our Part",
  CLAIMS_FILTER_VALUE_PENDING: "Processing",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "Partially Covered",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "Not Covered",
  CLAIMS_FILTER_VALUE_ADJUSTED: "Adjusted",
  DRINKS_PER_WEEK: "Drinks per week",
  OR_SELECT_BETWEEN_THESE: "or select between these",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "Date Range",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "Year to Date",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "Prior Year",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "Last 12 Months",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "Last 2 Years",
  CLAIMS_VIEW_ALL: "View All",
  CHALLENGE_TYPE_GROUP_TAG: "Group",
  CHALLENGE_TYPE_SOLO_TAG: "Solo",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "Group Challenges",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "Solo Challenges",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "Stay accountable as you challenge yourself with mindfulness work, healthy eating initiatives, fitness competitions, stress reduction activities, step challenges, and more.",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "Go solo and challenge yourself to complete groups of activities focused on physical fitness, nutrition, mental health, stress management, and more.",
  HELPFUL_LINKS: "Helpful Links",
  HOW_CLAIMS_WORK: "How Claims Work",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "Understanding how your claims work",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>When you visit one of the doctors in your network, they request payment. If you have a copay, they collect it from you and then they submit a claim to the insurance company.</p><p>We then review your insurance plan, also known as your benefit contract, to determine if the service is covered. If it is, we will approve it based on the terms of the benefit contract. If it is not covered as part of your benefit plan, you are responsible for paying for the service.</p>",
  CLAIMS_STATUS_DEFINITIONS: "Claim Status Definitions",
  CLAIMS_STATUS_DEFINITION_APPROVED: "We'll pay our part means the claim is approved and we agree to pay some or all of the claim based on your plan.",
  CLAIMS_STATUS_DEFINITION_PENDING: "Processing means we're figuring out who owes what.",
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: "Partially covered means we will cover a portion of your claim.",
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: "Not covered means that the service is not covered in your benefit plan and you will have to pay for it.",
  CLAIMS_STATUS_DEFINITION_ADJUSTED: "Adjusted means something changed here, so we had to take another look at this claim.",
  YOUR_COST: "Your Cost:",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {Medical Claim} oral {Dental Claim} vision {Vision Claim} pharmacy {Pharmacy Claim} professional {Professional Claim} other {Claim}}",
  FILTER_BY: "Filter By",
  CLAIMS_LIST_EMPTY_HEADING: "Nothing here, yet!",
  CLAIMS_LIST_EMPTY_MESSAGE: "Claims that are submitted will show up here.",
  CLAIMS_GENERIC_ERROR_HEADING: "Something went wrong",
  CLAIMS_GENERIC_ERROR_MESSAGE: "There was a technical problem on our end.{br}Please try again in a few minutes.",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "View what your insurance has covered, and what you may still owe your provider. You can access your claims submitted within two years to today's date.",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "No results",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "No claims match the {filterCount, plural, one {filter} other {filters}} you\u2019ve chosen",
  VIEW_MY_PROFILE: "View My Profile",
  PLAN_PROGRESS_COST_COVERAGE: "Plan progress and costs",
  PLAN_PROGRESS_POP_UP_LABEL: "What is a {title}?",
  RECENT_CLAIMS_LIST_EMPTY: "Claims submitted within the past 90 days will show up here. You can access all claims by selecting \u2018View all\u2019. ",
  DOWNLOAD_CLAIMS: "Download Claims",
  PLAIN_SOMETHING_WENT_WRONG: "Something went wrong",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "There was a problem downloading your claims.",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "Your benefits, claims, and wellness info, all in one place.",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "Ready to Get Started?",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "Browse our library to find Health Programs and Challenges that match your personal goals.",
  USER_DROPDOWN_BTN_LABEL: "Profile Menu",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "What is a copay and coinsurance?",
  ANSWERS_NOT_SAVED: "We're not able to save your answers right now. Please try again later",
  SESSION_ABOUT_TO_TIMEOUT: "You\u2019ve been inactive for a while and your session is about to time out. Do you want to continue your session?",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "You\u2019ve been inactive for a while and your session is about to time out.",
  SESSION_TIMEOUT: "Session Timeout",
  CONTINUE_SESSION: "Continue Session",
  SESSION_LOG_OUT: "Log Out",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "Select a delivery address",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "Who needs a new card?",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "Select one or more members.",
  DEFAULT_ADDRESS: "Default Address",
  CHOOSE_NEW_ADDRESS: "Choose a new address",
  CHOOSE_NEW_ADDRESS_HINT: "Provide a different mailing address for card delivery. This will not change your account's default address.",
  CARD_ACTIONS: "Card actions",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "Earn an achievement for completing all activities in this challenge",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "Nothing here - yet!",
  CHALLENGE_ACTIVITY_UP_NEXT: "Up Next",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "Come back when the challenge starts!",
  CHALLENGE_ACTIVITY_COMPLETED: "Completed",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "Activities you complete will show up here.",
  CHALLENGE_ACTIVITY_MISSED: "Missed",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "Activities you miss will show up here.",
  CHALLENGE_COMPLETED_TITLE: "That's a wrap!",
  CHALLENGE_COMPLETED_BODY: "There are no more activities to complete. Nicely done!",
  ACHIEVEMENT_EARNED: "Achievement earned celebration",
  ACHIEVEMENT_PROGRESS: "Achievement progress",
  ADD_DATA: "Add Data",
  CONFIRM_DETAILS: "Confirm your details",
  MEDICAL_ID_CARD: "Medical ID card",
  NEW_ADDRESS: "New delivery address",
  MESSAGE_CENTER: "Message Center",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "Messaging",
  CHOOSE_NEW_ADDRESS_DESC: "Provide a different mailing address for card delivery. This will not change your account's default address. Mailing address must be within the United States.",
  SELECT_STATE: "Select a state",
  REQUIRED_FIELD: "{field} is a required field",
  CLAIMS_YOUR_COST: "Your cost",
  CLAIMS_PROCESSING_DESCRIPTION: "We\u2019re still processing this claim. You\u2019ll be notified when its ready for you to review.",
  DOWNLOAD_BENEFITS_EXPLANATION: "Download Explanation of Benefits",
  DATA_COLLECTED: "Data collected {date}",
  MEMBER_CARD_ON_ITS_WAY: "Your new member ID card is on its way.",
  MEMBER_CARD_ARRIVAL: "The new member ID card(s) will arrive in approximately 10 business days.",
  TRY_LATER: "Please try again later.",
  RESUBMIT: "Resubmit",
  ORDER_NEW_CARD_HEADING: "Order a new card",
  CANCEL_ORDER_NEW_CARD_TITLE: "Are you sure you want to cancel your order?",
  CANCEL_ORDER_NEW_CARD_DESC: "You can always come back later.",
  CANCEL_ORDER: "Cancel order",
  STEP: "Step",
  ORDER_CARD: "Order card",
  PLEASE_MAKE_A_SELECTION: "Please make a selection",
  TOTAL_COST_OVERVIEW: "Total Cost Overview",
  TOTAL_COST_DESCRIPTION: "The sum of costs from your claim\u2019s services.",
  SUPPORT: "Support",
  GET_SUPPORT: "Get Support",
  MESSAGE_WITH_A_MEMBER: "Contact a member advocate for questions about your claim.",
  CLAIMS_SERVICE_DETAILS_HEADING: "Service Details",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "Additional Resources",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "A breakdown of the individual services you received and the costs associated with them.",
  CLAIMS_SERVICE_DETAILS_SERVICE: "Service",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "Name of Rx",
  CLAIMS_SERVICE_DETAILS_DATE: "Date of Service",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "Procedure Code",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "Rx Number",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "Your Cost",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "Cost Details",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "Show Cost Details",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "Hide Cost Details",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "Breakdown of your cost",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "The amounts below go towards your plan progress",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "Select one or more members to continue",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "Field contains invalid characters",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "Provider Charged",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "Other Insurance Paid",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "Your Member Discount",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "Your plan negotiated with your provider to give you a discounted rate.",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "Your member discount explanation",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "The amount your other insurance pays.",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: "Other insurance paid explanation",
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {Your Plan Paid on {paymentDate}} undefined {Your Plan Paid}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "The amount your plan covered for health care services.",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: "Your plan paid explanation",
  CLAIMS_COST_DETAILS_YOUR_COST: "Your Cost",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "Service Remark",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "In-Network",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "Out-Of-Network",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "Select an address option to continue",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "City can not contain numbers",
  MESSAGE_CLOSED_TEXT: "This message has been delivered.",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "Get an overview of your accumulated costs, cost-share, and more.",
  VIEW_PLAN_PROGRESS: "View Plan Progress",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "You\u2019ll receive a bill associated with this claim from your provider.",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "There was a problem downloading your explanation of benefits.",
  END_CHAT_MODAL_DESCRIPTION: "Continuing will end the chat. You will not be able to return to this conversation in the future.",
  RETURN_TO_CHAT: "Return to Chat",
  CHATS_IN_CONVERSATION: "Chats in conversation",
  LIVE_CHAT_CHARACTER_LIMIT: "Character limit reached ({characterLimit})",
  CHAT_MESSAGE_INPUT: "Chat message input",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {Deductible} non_covered {Non Covered} copayment {Copayment} coinsurance {Coinsurance} may_be_billed {You May Be Billed} amount_exceeding_maximum {Amount Exceeding Maximum} additional_member_liability {Additional Member Liability} special_processing {Special Processing} paid_to_member {Paid To Member} plan_paid_to_you {Plan Paid To You} other {Other}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "New Message",
  QUESTIONNAIRE_FLOW_CLOSE: "Close",
  QUESTIONNAIRE_FLOW_GO_BACK: "Go Back",
  QUESTIONNAIRE_FLOW_CONTINUE: "Continue",
  QUESTIONNAIRE_FLOW_RETRY: "Retry",
  CLEAR_ALL: "Clear All",
  HEALTH_REPORT_PAGE_HEADING: "Your Health Report",
  USERNAME_HAS_JOINED_THE_CHAT: "{username} has joined the chat",
  USERNAME_HAS_LEFT_THE_CHAT: "{username} has left the chat",
  PERVIOUS_MONTH: "Previous month button",
  NEXT_MONTH: "Next month button",
  CHAT_CLOSED: "This chat has been closed.",
  CONNECTING_YOU_WITH_AN_AGENT: "Connecting you with an agent...",
  LAST_DAY_TO_USE_BALANCE: "Last day to use balance",
  LAST_DAY_TO_SUBMIT_CLAIMS: "Last day to submit claims",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "Something went wrong",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "There was an issue, please try again",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "Something went wrong",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "There was an issue, please try again",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "Close",
  LIVE_CHAT_LANDING_PAGE_TITLE: "Chat",
  LIVE_CHAT_DELIVERY_FAILURE: "Not delivered",
  LIVE_CHAT_RESEND_MESSAGE: "Resend message",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "Message failed to send",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "Sorry about that, it looks like your message didn\u2019t make it to our team.",
  VIEW_TOOLTIP: "view {tooltipLabel} tooltip",
  NO_DATA_SOURCES: "No Data Sources",
  MAX_VALUE_ERROR_MESSAGE: "Please enter a value lower than or equal to {max}",
  MIN_VALUE_ERROR_MESSAGE: "Please enter a value higher than or equal to {min}",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "Please Select a value",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "The start date must be earlier than the end date",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "The duration cannot exceed 24 hours",
  NO_FUTURE_DATES: "Date can't be in the future",
  CLAIMS_REMOVE_FILTER: "Remove {label} filter.",
  REMOVE_X_FILTER: "Remove {label} filter.",
  WALLET_BENEFIT_STATUS_PENDING: "Pending",
  WALLET_BENEFIT_STATUS_ACTIVE: "Active",
  WALLET_BENEFIT_STATUS_PAST: "Past",
  CLAIMS_CLAIMS_LIST_HEADING: "Your Claims",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "Nothing here, yet!",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "Benefits that are active will show up here.",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "Benefits",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "Benefits - Id Cards",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "Claims",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "Claim Details",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "Claim Details - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "Pending",
  CLAIMS_STATUS_PENDING: "Pending",
  CSE_END_IMPERSONATION_MODE: "End impersonation mode",
  CSE_RESTRICTED_ACTION: "Restricted action",
  CSE_RESTRICTED_ACTION_MESSAGE: "During user simulation mode, you can only view the member\u2019s portal. Actions on behalf of members are not allowed.",
  CSE_RESTRICTED_VIEW_MESSAGE: "You don't have permission to access this section.",
  CSE_IMPERSONATION_MASONRY_HEADER: "Restricted content for support agents",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "You may not have permission to view certain widgets or content.",
  CSE_IMPERSONATION_BANNER_TEXT: "View-only mode for { memberName }.",
  CLAIMS_SUMMARY_PAGE_HEADING: "Claims",
  HEALTH_INSURANCE_GLOSSARY: "Health Insurance Glossary",
  CLAIMS_FILTER_LOADING_TEXT: "Tabs and filters are loading",
  ANDROID_HEALTH_CONNECT: "Health Connect",
  GET_CARE_SERVICE_CARD_CTA: "View service",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "Next step",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "Previous step",
  HEIGHT: "Height",
  WEIGHT: "Weight"
};

// src/strings/es-MX.json
var es_MX_default = {
  COMPLETED_ACTIVITIES: "Actividades completadas",
  AVAILABLE: "Disponible",
  SEE_MORE_ACTIVITIES: "Ver m\xE1s",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "Ver m\xE1s actividades completadas",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "Cuando completes actividades, aparecer\xE1n aqu\xED.",
  MISSED_ACTIVITIES: "Actividades perdidas",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "Ver m\xE1s actividades perdidas",
  MISSED_ACTIVITIES_EMPTY_STATE: "Cuando te pierdas actividades, aparecer\xE1n aqu\xED.",
  DISPLAYING_ACTIVITIES_FROM_DATE: "Visualizaci\xF3n de actividades desde {date}",
  STANDALONE_ACTIVITIES: "Actividades independientes",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "\xA1Todav\xEDa no hay nada que hacer aqu\xED! Regresa m\xE1s tarde.",
  VIEW_ALL_STANDALONE_ACTIVITIES: "Ver todas las actividades individuales",
  MODULE_TEMPLATE_BUTTON: "Bot\xF3n de plantilla de m\xF3dulo",
  ACHIEVEMENTS: "Logros",
  VIEW_ACHIEVEMENTS: "Ver logros",
  DEPENDENT_NOTIFICATION_MESSAGE: "Se podr\xEDa invitar a nuevos dependientes",
  DEPENDENT_COVERED_BY_HSA_ONLY: "Nota: Este dependiente solo recibir\xE1 cobertura mediante tu cuenta de gastos de salud.",
  WALLET_LANDING_NAV_LABEL: "Navegaci\xF3n de la cartera",
  VIEW_CARD_FOR_DEPENDENT: "Ver tarjeta para {dependentName}",
  LEARN_MORE_LEAGUE_FAMILY: "sobre la Familia League para tus dependientes",
  GO_TO_LIFEMARKET: "Ve a Lifemarket para ver el n\xFAmero de pedido {invoiceId}",
  GO_TO_CLAIM: "Ver detalles de la reclamaci\xF3n para {title}",
  GO_TO_RECEIPT: "Ver recibo para {title}",
  LINK_TO_TRANSACTION_DETAILS: "Enlace a los detalles",
  VIEW_CLAIM_LABEL_HIDDEN: "Ver reclamaci\xF3n {referenceId} de {vendor}",
  QLE_LINK_LABEL: "M\xE1s informaci\xF3n sobre eventos vitales cualificadores",
  PLEASE_CALL: "Llama al {phoneNumber}",
  BROWSE_FILES: "Explorar archivos",
  TOTAL_BALANCE: "Saldo total",
  LIFEMARKET: "Lifemarket",
  ABOUT: "Acerca de",
  LEAGUE_CREDIT_DESCRIPTION_1: 'Puedes gastar Cr\xE9dito League en {lifemarketLink} en una variedad de productos y servicios de estilo de vida y bienestar. Lo \xFAnico en lo que no puedes gastarlo es nada que est\xE9 marcado como "Oferta para socios de League".',
  LEAGUE_CREDIT_DESCRIPTION_2: 'Para usar tu Cr\xE9dito League, selecciona "Cr\xE9dito League" de la lista de opciones de pago cuando realices el pago en Lifemarket. {learnMoreLink}',
  DID_YOU_GET_A_PROMO_CODE: "\xBFRecibiste un c\xF3digo promocional?",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "Si recibiste un cr\xE9dito de la tienda o un c\xF3digo promocional, puedes ingresarlo aqu\xED para recibir los cr\xE9ditos. Una vez ingresados, se aplicar\xE1n autom\xE1ticamente a tu cuenta.",
  GET_CARE: "Obtener atenci\xF3n",
  DISCLAIMER: "Descargo de responsabilidad",
  JOINED: "Se uni\xF3",
  ROLLED_OVER: "Transferidos",
  NEED_MORE_INFO_SINGLE_CLAIM: "Tu reclamaci\xF3n necesita m\xE1s informaci\xF3n para ser procesada. Consulta los detalles a continuaci\xF3n.",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "Tus reclamaciones necesitan m\xE1s informaci\xF3n para ser procesadas. Consulta los detalles a continuaci\xF3n.",
  RECENT_CLAIMS: "Reclamaciones recientes",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "Si tienes una reclamaci\xF3n urgente, ponte en contacto con <a>Atenci\xF3n al Cliente</a>.",
  ABOUT_THIS_BENEFIT: "Acerca de este beneficio",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "Este beneficio est\xE1 a tu disposici\xF3n a trav\xE9s de tu empleador. Si tienes preguntas o necesitas informaci\xF3n detallada, comun\xEDcate con tu administrador de recursos humanos.",
  ID_LIKE_TO_DOTDOTDOT: "Me gustar\xEDa...",
  HAS_DEPENDENTS_DESCRIPTION: "Tus dependientes para este beneficio se mencionan a continuaci\xF3n. Si recientemente experimentaste un evento de vida, puedes ser elegible para hacer cambios a tus dependientes. Haz clic en el bot\xF3n de abajo para verificar si calificas en solo unos pocos pasos.",
  HAS_NO_DEPENDENTS_DESCRIPTION: "No tienes ning\xFAn dependiente asociado con este beneficio. Si recientemente experimentaste un evento de vida, puedes ser elegible para agregar o cambiar tus dependientes. Haz clic en el bot\xF3n de abajo para verificar si calificas en solo unos pocos pasos.",
  ADD_A_DEPENDENT_CAPITAL: "Agregar un dependiente",
  WALLET_FEEDBACK_TITLE: "Ay\xFAdanos a mejorar la experiencia de Wallet",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: '\xBFQu\xE9 tan \xFAtil es la informaci\xF3n proporcionada para tu beneficio "{benefitName}"?',
  WALLET_FEEDBACK_SUBTITLE_OTHER: "\xBFQu\xE9 tan \xFAtil es la informaci\xF3n proporcionada para tu {cardName}?",
  GO_BACK: "Volver",
  WALLET_LANDING_TITLE: "Tus beneficios e informaci\xF3n de cobertura, todo en un solo lugar.",
  WALLET_LANDING_BODY: "Tu cartera digital es donde puedes encontrar los beneficios, programas y detalles de la cobertura de tu empleador. Encuentra lo que necesitas con unos pocos toques.",
  POLICY_ACTIVE: "P\xF3liza activa",
  REMAINING_BALANCE: "Saldo restante",
  CONTACT_CUSTOMER_CARE: "Ponte en contacto con Atenci\xF3n al cliente",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "Vaya, parece que algo sali\xF3 mal",
  WERE_HAVING_TROUBLE: "Tenemos problemas para cargar esta informaci\xF3n, intenta actualizar la p\xE1gina. Si el problema persiste, {custCareLink}.",
  IN_NETWORK: "Dentro de la red",
  OUT_OF_NETWORK: "Fuera de la red",
  PROGRAMS: "programas",
  PROGRAM_CREATED_BY: "Programa creado por",
  TOP_FOCUS_AREA: "\xC1rea de enfoque principal",
  DIMENSION_OF_HEALTH: "LA DIMENSI\xD3N DE LA SALUD",
  READING_MORE_ABOUT_DIMENSIONS: "\xBFTe interesa leer m\xE1s sobre c\xF3mo las dimensiones afectan tu salud en general?",
  READ_OUR_ARTICLE: "Lee nuestro art\xEDculo",
  HEALTH_PROGRAMS_FOR_YOU: "Programas de salud para ti",
  COMPLETE_ACTIVITIES: "Completa actividades para alcanzar tus objetivos de salud. Recomendado en funci\xF3n de tu perfil de salud.",
  FT: "ft",
  CM: "cm",
  IN: "in",
  LB: "lb",
  KG: "kg",
  LETS_BEGIN: "\xA1Comencemos!",
  KEEP_GOING: "Sigue adelante",
  SKIP: "Omitir",
  HP_ONBOARDING_SUBMIT: "Comencemos",
  FOR_PROVIDERS_AND_PHARMACISTS: "Para proveedores/farmac\xE9uticos",
  WEVE_PICKED_UP: "Hemos continuado donde lo dejaste.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "Solo te faltan unas pocas preguntas para ganar {points} puntos.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "Solo te faltan unas pocas preguntas para completar la evaluaci\xF3n.",
  PRESS_ENTER: "Presiona Enter",
  SELECT_ALL_THAT_APPLY: "Selecciona todas las que correspondan",
  SELECT_UP_TO: "Selecciona hasta {number}",
  AMOUNT_QUESTIONS: "{amount} preguntas",
  QUESTION_PROGRESS: "Pregunta {questionNumber} de {totalQuestions}",
  COMPLETE: "Completa",
  VIEW_MORE: "Ver m\xE1s",
  DOWNLOAD_APP_STORE: "Descargar en la App Store",
  DOWNLOAD_PLAY_STORE: "Cons\xEDguelo en Google Play",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "Lo sentimos, la p\xE1gina que solicitaste ha expirado o ya no es v\xE1lida.",
  EARNED_X_TIMES: "Ganaste {amount}X",
  EARNED: "Ganado",
  YOUVE_EARNED_TOTAL: "Has ganado un total de",
  YOUVE_EARNED_GRAND_TOTAL: "Has ganado un total general de",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "Has ganado una insignia de Logro",
  CONGRATS_COMPLETED: "\xA1Felicitaciones! Has completado",
  VIEW_MY_HEALTH_DASHBOARD: "Ver mi panel de salud",
  LATEST_INSIGHTS_METRICS_AND_MORE: "Informaci\xF3n m\xE1s reciente, m\xE9tricas y m\xE1s",
  FEEDBACK_TITLE: "\xBFQu\xE9 tan \xFAtil fue esta informaci\xF3n?",
  FEEDBACK_MIN_SCALE: "Nada \xFAtil",
  FEEDBACK_MAX_SCALE: "Muy \xFAtil",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "\xBFHas experimentado recientemente cambios en tu vida o en tu familia?",
  DID_YOU_RECENTLY: "\xBFRecientemente te casaste o tuviste un beb\xE9? Ciertos eventos de vida podr\xEDan calificarte para una reinscripci\xF3n.",
  LEARN_MORE: "M\xE1s informaci\xF3n",
  VIEW_ACTIVITY: "Ver actividad",
  CONTINUE: "Continuar",
  ARIA_CIRCLED_CHECKMARK: "Marca de verificaci\xF3n blanca dentro de un c\xEDrculo",
  NOT_NOW: "Ahora no",
  VAL_POINTS: "{points} puntos",
  ACTIONS: "Acciones",
  VIEW_ALL_CLAIMS: "Ver todas las reclamaciones",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "Esta casilla debe estar marcada para continuar",
  PLEASE_NOTE: "Ten en cuenta",
  COPY: "Copiar",
  STATUS: "Estado",
  STARTED: "Comenzado",
  APPROVED: "Aprobada",
  CANCELLED: "Cancelado",
  UPLOADED_DOCUMENT: "Cargado el {date} \u2022 Cargado por {user}",
  MORE_INFORMATION_NEEDED: "Necesita informaci\xF3n",
  PAID: "Pagada",
  PENDING_EXTERNAL_REVIEW: "Enviado para revisi\xF3n externa",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "Aprobaci\xF3n previa M\xE1s informaci\xF3n",
  PREAPPROVAL_REJECTED: "Aprobaci\xF3n previa Rechazada",
  PREAPPROVED: "Preaprobado",
  REJECTED: "Rechazada",
  SUBMITTED_FOR_PREAPPROVAL: "Presentado para aprobaci\xF3n previa",
  SUBMITTED: "Enviada",
  WALLET: "Billetera",
  AMOUNT: "Monto",
  ADD: "Agregar",
  EDIT: "Editar",
  DETAILS: "Detalles",
  AND: "y",
  TODAY: "Hoy",
  CLAIMS: "Reclamaciones",
  GROUP_ID: "ID de grupo",
  ZIP_CODE: "C\xF3digo postal",
  CITY: "Ciudad",
  STATE: "Estado",
  REMOVE: "Eliminar",
  HEALTH_SPENDING_ACCOUNT: "Cuenta de gastos de salud",
  HSA: "HSA",
  LIFESTYLE_SPENDING_ACCOUNT: "Cuenta de gastos para el bienestar",
  LSA: "LSA",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "Acuerdo de reembolso de salud",
  DRUGS: "Drogas",
  TRAVEL: "Viajes",
  HEALTH_INSURANCE: "Seguro m\xE9dico",
  SETTINGS: "Ajustes",
  ACTIVE: "Activo",
  ENDED: "Finalizado",
  SUSPENDED: "Suspendido",
  PENDING: "Pendiente",
  LOADING: "Cargando",
  ERROR_STATE_TITLE: "Algo sali\xF3 mal",
  HELP_EMAIL: "help@league.com",
  SUBMIT: "Enviar",
  EARN_NUMBER_POINTS_FULL: "GANA {points} PUNTOS",
  CLOSE: "Cerrar",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "Este desaf\xEDo se realiza durante todo el a\xF1o",
  END_DAY_AT_TIME: "Termina {day}",
  ENDED_DAY_AT_TIME: "Finalizado {day}",
  START_DAY_AT_TIME: "Comienza {day}",
  SPONSOR_LOGO: "Logotipo de {name}",
  START_AND_END_DATES: "Comienza {startDate} \u2022 Termina {endDate}",
  SIGN_UP_BY_DATE: "Reg\xEDstrate antes del {date}",
  SIGN_UP_BEGINS_DATE: "El registro comienza el {date}",
  SIGN_UP_DATE_PASSED: "Fecha de registro pasada",
  SIGN_UP_PERIOD_PASSED: "Periodo de registro superado",
  CHALLENGE_DETAILS: "Datos del desaf\xEDo",
  CHALLENGE_PROGRESS: "Progreso del desaf\xEDo",
  X_PERCENT_COMPLETE: "{number}\xA0% completado",
  SIGN_UP_FOR_CHALLENGE: "Reg\xEDstrate",
  GROUP_CHALLENGE: "Desaf\xEDo grupal",
  SOLO_CHALLENGE: "Desaf\xEDo en solitario",
  TRACK_WEARABLES_ALT_TEXT: "Persona que mira los datos de su reloj inteligente",
  CONNECT_A_DEVICE: "Conectar un dispositivo",
  DEVICES_CONNECTED_TITLE: "\xA1Parece que ya est\xE1s conectado!",
  DEVICES_CONNECTED_DESCRIPTION: "Ya estamos sincronizando tus datos. Si eliges realizar un seguimiento autom\xE1tico, podemos marcar autom\xE1ticamente las actividades como completadas utilizando el {dataPoint} de tu dispositivo.",
  DEVICES_UNCONNECTED_TITLE: "\xA1Conectemos tu dispositivo!",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "Seguimiento autom\xE1tico",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "Seguimiento manual",
  DEVICES_UNCONNECTED_DESCRIPTION: "Conecta tus aplicaciones y dispositivos de salud favoritos para marcar autom\xE1ticamente las actividades como completadas.",
  TRACK_CHALLENGE_AUTOMATICALLY: "Seguimiento autom\xE1tico",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "Si eliges hacer un seguimiento autom\xE1tico, una vez que te hayas registrado, {instructionsLink} para conectar tu dispositivo a trav\xE9s de la aplicaci\xF3n m\xF3vil de League.",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "sigue estos pasos",
  TRACK_CHALLENGE_MANUALLY: "En su lugar, realiza un seguimiento manual",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "Si eliges hacer un seguimiento manual, a\xFAn puedes conectar un dispositivo en un momento posterior. {learnMoreLink} sobre el seguimiento manual de tu progreso.",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "M\xE1s informaci\xF3n",
  TRACK_HEALTH_DATA_ALT: "Panel de control con gr\xE1ficos para el seguimiento de tus datos de salud",
  TRACK_MANUALLY_CONFIRM_TITLE: "\xBFEst\xE1s seguro de que quieres hacer un seguimiento manual?",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "Todav\xEDa podr\xE1s conectar un dispositivo en un momento posterior",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "\xBFConfirmas que quieres salir?",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "Si decides salir, es posible que los cambios no se guarden.",
  EXIT: "Salir",
  CHALLENGE: "Desaf\xEDo",
  CHALLENGES: "Desaf\xEDos",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "\xA1Reg\xEDstrate en un desaf\xEDo para completar las actividades diarias y competir contra tus compa\xF1eros!",
  THIS_CHALLENGE_HAS_ENDED: "Este desaf\xEDo ha terminado",
  COMPLETED_ON_DATE: "Completado el {date}",
  ENDED_ON_DATE: "Finaliz\xF3 el {date}",
  LEAVE_CHALLENGE: "Abandonar desaf\xEDo",
  LEAVE_CHALLENGE_QUESTION: "\xBFAbandonar el desaf\xEDo?",
  LEAVE_CHALLENGE_DETAIL: "\xBFEst\xE1s seguro de que deseas eliminar este desaf\xEDo de tu trayectoria? Cualquier progreso en este desaf\xEDo se restablecer\xE1.",
  LEAVE: "Abandonar",
  SIGN_UP_FOR_THE_CHALLENGE: "Reg\xEDstrate en el desaf\xEDo",
  CHALLENGE_SENSITIVE_DATA: "Tus datos de salud e informaci\xF3n personal son muy confidenciales y es nuestra responsabilidad mantenerlos privados y seguros. Nos tomamos muy en serio esta responsabilidad.",
  CHALLENGE_USING_DATA_TO: "Cuando te registras en un desaf\xEDo, utilizamos tus datos para:",
  COMPLETE_ACTIVITIES_SHORT: "Actividades completas",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "Comparte el progreso de tu desaf\xEDo con tu empleador",
  SHARE_CHALLENGE_PROGRESS: "Comparte el progreso de tu desaf\xEDo con tus compa\xF1eros (y tu empleador)",
  RANK_SCORE: "Clasifica tu puntuaci\xF3n en una tabla de clasificaci\xF3n",
  DETERMINE_CHALLENGE_WINNERS: "Determinar los ganadores del desaf\xEDo",
  ACCEPT_BEFORE_CONTINUE: "Debes aceptar la pol\xEDtica de privacidad antes de continuar.",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "Al marcar esta casilla, acepto la <a>pol\xEDtica de privacidad.</a>",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "Obt\xE9n m\xE1s informaci\xF3n sobre c\xF3mo League gestiona y usa tus datos en nuestra <a>pol\xEDtica de privacidad.</a>",
  PRIZE: "Premio",
  RULES: "Reglas",
  LEADERBOARD: "Tabla de clasificaci\xF3n",
  TOOLTIP_ACTION_DESCRIPTION: "Ver criterio de clasificaci\xF3n de la tabla de clasificaci\xF3n",
  MARK_AS_DONE: "Marcar como hecho",
  CLOSE_ACTIVITY: "Cerrar actividad",
  GOAL_INCOMPLETE: "Debes completar el objetivo antes de continuar.",
  PROGRESS_RECORD_ERROR: "Hubo un problema al registrar tu \xFAltimo progreso.",
  YOUVE_COMPLETED: "Has completado",
  ACTIVITY_COMPLETE: "Actividad completada",
  PAGE_NOT_FOUND_HEADER: "P\xE1gina no encontrada",
  PAGE_NOT_FOUND_CONTENT: "\xA1Parece que te perdiste un poco!",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "Perm\xEDtenos ayudarte a encontrarte.",
  PAGE_NOT_FOUND_BUTTON: "Volver al Inicio",
  REWARD_POINTS: "Puntos de recompensa",
  REWARD_POINTS_EARNED: "Puntos de recompensa ganados",
  REWARD_POINTS_MISSED: "Puntos de recompensa perdidos",
  X_REWARD_POINTS: "{points} puntos de recompensa",
  X_LEAGUE_POINTS: "{points} puntos de League",
  REWARDS_PROGRAM_ELIGIBLE: "Elegible para el Programa de Recompensas",
  EXPIRY_DATE: "Fecha de vencimiento",
  NO_EXPIRY_DATE: "Sin fecha de vencimiento",
  POINTS_BALANCE: "Saldo de puntos",
  REDEEM_NEXT_TIER_POINTS: "Est\xE1s a {points} de canjear {value}.",
  VIEW_YOUR_REWARDS: "Ver tus recompensas",
  REDEEMABLE: "Canjeable por {value}.",
  ACTIVATION_PENDING: "Activaci\xF3n pendiente",
  EXPIRED: "Caducado",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "Tu cuenta est\xE1 activa y en proceso de configuraci\xF3n.",
  PLEASE_TRY_AGAIN_SHORTLY: "Int\xE9ntalo de nuevo en un momento",
  MANAGE_DEPENDENTS: "Administrar dependientes",
  VIEW_CARD: "Ver tarjeta",
  MEMBER_ID: "ID de miembro",
  SPOUSE: "C\xF3nyuge",
  COMMON_LAW: "Derecho consuetudinario",
  CHILD: "Hijo/a",
  PARENT: "Padre/madre",
  GRANDPARENT: "Abuelo/a",
  GRANDCHILD: "Nieto/a",
  UNCLE_AUNT: "T\xEDo/T\xEDa",
  NEPHEW_NIECE: "Sobrino/a",
  INELIGIBLE_CHILD: "Hijo no elegible",
  SIBLING: "Hermano",
  OTHER: "Otro",
  DEPENDENT_CARD_HOLDER: "Tarjeta de {dependentName} para",
  DATE: "Fecha",
  DESCRIPTION: "Descripci\xF3n",
  TYPE: "Tipo",
  ID: "Id.",
  CLAIM: "Reclamaci\xF3n",
  TOP_UP: "Completar",
  ADJUSTMENT: "Ajuste",
  REFUND: "Reembolso",
  LEAGUE_CREDIT: "Cr\xE9dito League",
  CREDIT_CARD: "Tarjeta de cr\xE9dito",
  CGA: "CGA",
  NO_TRANSACTIONS_YET: "Todav\xEDa no hay transacciones",
  NO_MORE_TRANSACTIONS: "No hay m\xE1s transacciones",
  PROMO_CODE: "C\xF3digo promocional",
  PROMO_CODE_NOT_FOUND: "C\xF3digo promocional no v\xE1lido",
  PROMO_CODE_SUCCESS: "\xA1C\xF3digo promocional canjeado!",
  TOTAL_CREDIT: "Cr\xE9dito total:",
  CREDIT_INFORMATION: "Informaci\xF3n de cr\xE9dito",
  ENTER_CODE: "Ingresa el c\xF3digo",
  DOCUMENT_NAME: "Nombre del documento",
  REQUIRED: "Necesario",
  CANCEL: "Cancelar",
  CONFIRM: "Confirmar",
  ALLOCATION: "Asignaci\xF3n",
  COVERED: "Cubierto",
  NOT_COVERED: "No cubierto",
  COVERED_WITH_RESTRICTIONS: "Cubierto con restricciones",
  EXPAND_ALL: "Expandir todo",
  COLLAPSE_ALL: "Contraer todo",
  EXAMPLES: "Ejemplos: ",
  RESTRICTIONS: "Restricciones",
  SEARCH_COVERAGE: "Busca tu cobertura",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "Los resultados de la b\xFAsqueda se actualizar\xE1n autom\xE1ticamente a medida que escribes",
  INDIVIDUAL: "Individual",
  INDIVIDUAL_CAP: "Individual",
  DEPENDENTS: "Dependientes",
  SELECT_ONE: "Selecciona una",
  MORE_OPTIONS: "M\xE1s opciones",
  VENDOR: "Vendedor",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "se agregar\xE1n autom\xE1ticamente a tu {benefitName}.",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "Se agregar\xE1 un <strong>{percent}\xA0%</strong> a tu <strong>{benefitName}</strong>",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "Se agregar\xE1 un <strong>{percent}\xA0%</strong> de este a tu <strong>{benefitName}</strong>.",
  DOCUMENT_TITLE: "{title} - League",
  MEDICAL: "M\xE9dico",
  TOTAL: "Total",
  PROFILE_UPDATE_FAILED: "Error al actualizar el perfil",
  PROFILE_PHOTO: "Foto de perfil",
  PROFILE_PHOTO_DESCRIPTION: "Esta foto aparece en tu perfil de usuario",
  SELECT_AVATAR: "Seleccionar Avatar",
  SELECT_AVATAR_ERROR: "Debes elegir un avatar antes de continuar.",
  GET_AVATARS_ERROR: "\xA1Ups! Hubo un problema al cargar los avatares. Regresa y selecciona una opci\xF3n diferente.",
  ADDRESS_LINE_1: "L\xEDnea 1 de direcci\xF3n",
  ADDRESS_LINE_2: "L\xEDnea 2 de direcci\xF3n",
  LANGUAGE_SELECTOR_ENGLISH: "Ingl\xE9s",
  LANGUAGE_SELECTOR_FRENCH: "Fran\xE7ais",
  LANGUAGE_SELECTOR_SPANISH: "Espa\xF1ol",
  DOMESTIC_PARTNERSHIP: "Pareja de hecho",
  YES: "S\xED",
  NO: "No",
  WHY_DO_YOU_ASK: "\xBFPor qu\xE9 preguntas?",
  LAST_DAY_OF_ENROLLMENT: "\xDAltimo d\xEDa del per\xEDodo de inscripci\xF3n: {lastDay}",
  WALLET_WELCOME: "\xA1Bienvenido a tu billetera!",
  WALLET_DESCRIPTION: "Tu cartera es donde podr\xE1s acceder a tus beneficios, enviar reclamaciones y verificar tu cobertura.  Podr\xE1s enviar reclamaciones por los costos incurridos durante el per\xEDodo de inscripci\xF3n una vez que tu cartera est\xE9 activa. \xA1Mientras tanto, {healthProgramLink}!",
  HEALTH_PROGRAM_LINK_COPY: "prueba uno de nuestros programas de salud",
  FINALIZE_SELECTIONS_HEADER_COPY: "\xBFQuieres acceder a tus beneficios antes?",
  FINALIZE_SELECTIONS_COPY_1: "\xA1Una vez que finalices tus selecciones, podremos enviar los detalles de tu plan al proveedor de inmediato! Esto significa que no tendr\xE1s que esperar hasta el final del per\xEDodo de inscripci\xF3n para ver tu cartera.",
  FINALIZE_SELECTIONS_COPY_2: "Esto tambi\xE9n significa que no podr\xE1s realizar ning\xFAn cambio en tu plan despu\xE9s de hacer clic en enviar.",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "Entiendo que no podr\xE9 cambiar mis selecciones",
  FINALIZE_PLAN_SELECTIONS: "Finaliza las selecciones del plan",
  ERROR_FINALIZING_SELECTIONS: "Ocurri\xF3 un error al enviar tus selecciones.",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "Parece que tus selecciones est\xE1n incompletas. Completa la inscripci\xF3n y vuelve a intentarlo.",
  SELECTIONS_FINALIZED_SUCCESS: "\xA1Selecciones finalizadas!",
  TOTAL_REWARDS_LABEL: "Resumen de cobertura",
  TOTAL_REWARDS_DESCRIPTION: "Revisa las selecciones de planes que realizaste durante tu inscripci\xF3n.",
  TOTAL_REWARDS_LINK: "Ver resumen de cobertura",
  CHANGE_PLAN_LABEL: "Cambiar las selecciones del plan",
  CHANGE_PLAN_DESCRIPTION: "\xBFQuieres realizar cambios en tu plan o informaci\xF3n personal?",
  CHANGE_PLAN_LINK: "Cambiar las selecciones del plan",
  DISMISS_TOAST: "Descartar notificaci\xF3n",
  UPLOAD_DOCUMENT: "Cargar documento",
  REQUIRED_FORM: "Necesario",
  PERCENTAGE_USED: "Porcentaje usado.",
  SUCCESS: "\xA1Excelente!",
  ERROR: "Error",
  OVERVIEW: "Descripci\xF3n general",
  THIS_FIELD_IS_REQUIRED: "Este campo es obligatorio",
  INVALID_ZIP_CODE_NO_FORMAT: "C\xF3digo postal no v\xE1lido",
  INVALID_DAY_ERROR_MESSAGE: "El d\xEDa que seleccionaste no es v\xE1lido.",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "Debe tener como m\xE1ximo {length} caracteres",
  COMPLETED_STATE_TITLE: "Completada",
  DRAG_OR_UPLOAD_PHOTO: "Sube una foto de este documento",
  ADD_IMAGE: "Agrega m\xE1s p\xE1ginas a este documento (si corresponde)",
  IDENTIFICATION_TYPE_TWO_FILES: "Los documentos de identificaci\xF3n solo pueden tener hasta 2 im\xE1genes de respaldo.",
  WELCOME: "Te damos la bienvenida",
  GOT_IT: "\xA1Entiendo!",
  SAVE: "Guardar",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "Ver m\xE1s detalles",
  LAST_UPDATED: "\xDAltima actualizaci\xF3n",
  CLAIM_REF_NUMBER: "Reclamaci\xF3n #",
  VIEW_ALL: "Ver todo",
  HRA: "HRA",
  SEARCH: "Buscar",
  DAY: "D\xEDa",
  MONTH: "Mes",
  YEAR: "A\xF1o",
  BACK: "Atr\xE1s",
  NEXT: "Siguiente",
  PREVIOUS: "Anterior",
  HELP_CENTER: "Centro de ayuda",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "Minimizar chat",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "Respuestas sugeridas disponibles",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "El participante del chat est\xE1 escribiendo",
  VIEW_DOCUMENT: "Ver documento:",
  CATEGORIES: "Categor\xEDas",
  ACTIVE_PROGRAM: "{length, plural, one {Programa activo} other {Programas activos}}",
  ACTIVE_CHALLENGE: "{length, plural, one {Desaf\xEDo activo} other {Desaf\xEDos activos}}",
  PROGRAM_PROGRESS: "Progreso del programa",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "\xA1Tambi\xE9n puedes escribir para crear una nueva opci\xF3n!",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "Selecciona tus opciones",
  COVERAGE: "Cobertura",
  HEALTH_PROFILE: "Perfil de salud",
  HEALTH_PROGRAM: "Programa de salud",
  HEALTH_PROGRAMS: "Programas de salud",
  STEPS: "pasos",
  ACTIVE_DURATION: "minutos activos",
  MINDFUL_DURATION: "minutos de atenci\xF3n plena",
  FLOORS_CLIMBED: "pisos subidos",
  DISTANCE: "Distancia recorrida",
  ENERGY_BURNED: "Calor\xEDas quemadas",
  METRIC_DETAILS: "Detalles de la m\xE9trica",
  HEALTH_PROGRAMS_SYNC_DEVICE: "Sincroniza tu {device} para disfrutar de una experiencia \xF3ptima en el programa.",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "PROGRAMA DE SALUD CONECTADA",
  TRACK_AUTOMATICALLY: "Haz un seguimiento de tu progreso autom\xE1ticamente",
  SYNC_ISSUE: "Problema de sincronizaci\xF3n",
  FIX_SYNC_ISSUE: "Solucionar este problema",
  DATAPOINT_SYNC_ISSUE: "Ocurri\xF3 un problema al sincronizar tu {dataPoint}.",
  CONNECT_TO_TRACK: "Conecta tus aplicaciones y dispositivos de salud favoritos para marcar autom\xE1ticamente las actividades como completadas.",
  CONNECT_APPS_DEVICES: "Conectar aplicaciones y dispositivos",
  ADD_APPS_DEVICES: "Ver aplicaciones y dispositivos",
  MANAGE_UNITS: "Cambiar las unidades de medida",
  DONT_CONNECT_APPS_DEVICES: "No hacer un seguimiento autom\xE1tico de mi progreso",
  CONNECTING_UNAVAILABLE: "La conexi\xF3n de aplicaciones y dispositivos no est\xE1 disponible en la web",
  CONNECTING_UNAVAILABLE_DETAILS: "Por ahora, visita la aplicaci\xF3n m\xF3vil de League para conectar tus aplicaciones y dispositivos de salud favoritos.",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "Por ahora, visita la aplicaci\xF3n m\xF3vil de League para conectar tus aplicaciones y dispositivos de salud favoritos. Si quieres obtener m\xE1s informaci\xF3n, puedes encontrar una lista completa de las",
  CONNECT_DEVICES_LEARN_MORE: "M\xE1s informaci\xF3n",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "aplicaciones y los dispositivos compatibles actualmente",
  DONT_HAVE_THE_APP: "\xBFNo tienes la aplicaci\xF3n m\xF3vil de League?",
  WHAT_DEVICES_CAN_CONNECT: "\xBFQu\xE9 aplicaciones y dispositivos son compatibles?",
  CONNECTED_DEVICES_IMAGE_ALT: "Persona que revisa los datos desde un dispositivo conectado",
  MORE_GREAT_RESOURCES: "M\xE1s recursos excelentes",
  HISTORY: "Historia",
  TOP_MATCH: "Mejores resultados",
  CHAT: "Chat",
  END_CHAT: "Finalizar chat",
  BECOMES_ACTIVE_ON_DATE: "se activa el {date}",
  BECOMES_ACTIVE_IN_X_DAYS: "Este beneficio se activa en {days} d\xEDas",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "Solo puedes enviar reclamaciones con una fecha de compra/servicio entre <strong>{dateRange}</strong> para este beneficio. Consulta los detalles de tu cobertura para obtener m\xE1s informaci\xF3n.",
  SUBMIT_PAST_CLAIMS_BY_DATE: "enviar reclamaciones anteriores antes del {date}",
  THIS_POLICY_HAS_EXPIRED: "El per\xEDodo de esta p\xF3liza ha expirado, pero te quedan {days} d\xEDas para enviar reclamaciones pasadas para este beneficio",
  THIS_POLICY_EXPIRES_TOMORROW: "El periodo de esta p\xF3liza ha expirado, pero hoy es tu \xFAltimo d\xEDa para enviar reclamaciones pasadas para este beneficio.",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "Tienes hasta <strong>{inactiveDate}</strong> para enviar reclamaciones anteriores con una fecha de compra/servicio el <strong>{endDate}</strong> o antes",
  EXPIRES_ON_DATE: "Caduca el {date}",
  ASSIGNED: "Asignado",
  ASSIGNED_ON: "el {date}",
  ASSIGNED_TODAY: "Hoy",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "Te quedan {days} d\xEDas para usar este beneficio antes de que expire el per\xEDodo de la p\xF3liza",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "Despu\xE9s del vencimiento, tendr\xE1s hasta el <strong>{inactiveDate}</strong> para enviar reclamaciones con una fecha de compra/servicio el <strong>{endDate}</strong> o antes",
  LOAD_MORE: "Cargar m\xE1s",
  SHOW_MORE: "Mostrar m\xE1s",
  LINK_OUT_MESSAGE: "Debes ver",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "Vaya, parece que hubo un problema.",
  AGENT_PERMISSION_ERROR: "No tienes permiso para ver esto.",
  TRY_AGAIN: "Intentar otra vez",
  WARNING_OPENS_NEW_TAB: "Se abre en una nueva pesta\xF1a",
  ANY_HEALTH_CHANGES: "\xBFHa habido alg\xFAn cambio reciente en tu salud?",
  LAST_UPDATED_DATE: "\xDAltima actualizaci\xF3n {date}",
  UPDATE_HEALTH_PROFILE: "Actualiza tu perfil de salud",
  HEALTH_PROFILE_COMPLETION_POINTS: "HAS GANADO {points} PUNTOS",
  OPENS_IN_A_NEW_TAB: "Se abre en una nueva pesta\xF1a",
  COMPLETED: "Completada",
  COMPLETED_X_TIMES: "{amount, plural, one {Se complet\xF3 {amount} vez} other {Se complet\xF3 {amount} veces}}",
  REMOVE_FROM_JOURNEY: "Quitar de la trayectoria",
  GO_TO_JOURNEY: "Ir a Trayectoria",
  ABOUT_THIS_PROGRAM: "Acerca de este programa",
  HEALTH_JOURNEY: "Trayectoria de salud",
  REMOVE_ACTIVITY: "\xBFQuitar actividad?",
  REMOVE_ACTIVITY_PROMPT: "Quitar actividad",
  REMOVE_ACTIVITY_BUTTON: "Eliminar",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "\xBFEst\xE1s seguro de que deseas eliminar esta actividad de tu trayectoria? Esta acci\xF3n no se puede deshacer.",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "Ya no lo ver\xE1s en tu trayectoria",
  DISCOVER_ACTIVITIES: "Descubre nuevas actividades",
  DISCOVER_ACTIVITIES_CAPTION: "Explora actividades que creemos que te encantar\xE1n",
  ACTIVITIES: "Actividades",
  ACTIVITIES_PLURALIZATION: "{count, plural, one {Actividad} other {Actividades}}",
  ACTIVITIES_PILL: "Actividades",
  PROGRAM_LIBRARY: "Lista de programas",
  BROWSE_PROGRAMS: "Explorar programas",
  BACK_TO_JOURNEY: "Volver a la trayectoria",
  GO_TO_HEALTH_JOURNEY: "Ir a la trayectoria de salud",
  ADD_TO_JOURNEY: "Agregar a la trayectoria",
  REQUEST_WAIT: "Espera mientras completamos tu solicitud",
  JOURNEY_DELIGHT_1: "La motivaci\xF3n te ayuda a empezar. El h\xE1bito te mantiene en marcha. \xA1Ya lo tienes!",
  JOURNEY_DELIGHT_2: "Activo. Enfocado. Centrado en el camino. Nos encanta verlo.",
  JOURNEY_DELIGHT_3: "Los peque\xF1os pasos pueden marcar una gran diferencia. \xA1Est\xE1s dando ese paso!",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "{hasValue, select, true {Esta es una opci\xF3n de texto libre; usa el cuadro de texto para ingresar tu propio valor. El valor actual es: {value}} other {Esta es una opci\xF3n de texto libre; usa el cuadro de texto para ingresar tu propio valor. El valor actual es: vac\xEDo}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "Esta opci\xF3n es exclusiva; al seleccionarla, se anular\xE1 la selecci\xF3n de todas las dem\xE1s",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "Reproduce o pausa la animaci\xF3n seleccionada actualmente",
  CENTIMETERS: "Cent\xEDmetros",
  FEET: "pies",
  INCHES: "Pulgadas",
  KILOGRAMS: "Kilogramos",
  POUNDS: "Libras",
  EARN_UP_TO_X_POINTS: "Gana hasta {points} puntos",
  EXPIRES_TODAY: "Caduca hoy",
  PROGRAM_DETAILS: "Detalles del programa",
  PROGRESS: "Progreso",
  X_OF_Y_COMPLETE: "{completed}/{total} completadas",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "{missed} perdidas",
  MISSED: "Perdida",
  SCROLL_CAROUSEL_RIGHT: "Desplaza el carrusel hacia delante",
  SCROLL_CAROUSEL_LEFT: "Desplaza el carrusel hacia atr\xE1s",
  SCROLL_CAROUSEL_FORWARD: "Desplaza el carrusel hacia delante",
  SCROLL_CAROUSEL_BACK: "Desplaza el carrusel hacia atr\xE1s",
  YOU_HAVENT_STARTED_PROGRAMS: "No has iniciado ning\xFAn programa de salud",
  ADD_PROGRAM: "Agregar programa",
  TAP_ADD_PROGRAM_BUTTON: 'Toca el bot\xF3n "Agregar programa" para buscar programas.',
  YOUVE_REACHED_PROGRAM_LIMIT: "\xA1Has alcanzado el l\xEDmite de tu programa!",
  START_X_MORE_PROGRAMS: "{duration, plural, one {Comienza {duration,number} programa m\xE1s. Mant\xE9n el impulso.} other {Comienza {duration,number} programas m\xE1s. Mant\xE9n el impulso.}}",
  OK: "Ok",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "Completa uno de tus programas activos para agregar esto a tu trayectoria",
  SHOW_LESS: "Mostrar menos",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "\xA1Parece que has alcanzado el l\xEDmite de programas!",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "Completa un programa activo antes de a\xF1adir uno nuevo. <strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "\xBFEliminar programa?",
  REMOVE_PROGRAM_BUTTON: "Eliminar",
  ARE_YOU_SURE_REMOVE_PROGRAM: "\xBFEst\xE1s seguro de que quieres eliminar este programa de tu trayectoria? Cualquier progreso en este programa se restablecer\xE1 y tendr\xE1s que comenzar de nuevo desde el principio.",
  PROGRAM_REMOVED: "Programa eliminado",
  PROGRAM_ADDED: "Programa a\xF1adido",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "El programa aparecer\xE1 ahora en tu trayectoria",
  PROGRAM_ADD_ERROR: "No pudimos agregar el programa en este momento. Int\xE9ntalo de nuevo o elige otro programa.",
  PROGRAM_REMOVE_ERROR: "No pudimos eliminar el programa en este momento. Int\xE9ntalo de nuevo.",
  SKIP_TO_CONTENT_MESSAGE: "Saltar al contenido",
  MAIN_NAVIGATION_LIST_LABEL: "Elementos de navegaci\xF3n de League",
  PAGE_TITLE_ACTIVITY_DETAILS: "Detalles de la actividad",
  X_OF_Y: "{current} de {total}",
  ACTIVITY_COUNT_EDIT: "Editar {unit}",
  ACTIVITY_COUNT_GOAL: "Objetivo: {goal,number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: 'Para completar esta actividad, haz clic en "Marcar como completado".',
  ACTIVITY_COMPLETE_LATER_PROMPT: 'Para continuar, haz clic en "Volver a la trayectoria" y regresa m\xE1s tarde para marcarlo como completado.',
  ACTIVITY_COMPLETE_ERROR: "No pudimos completar la actividad en este momento. Int\xE9ntalo de nuevo.",
  ACTIVITY_DISMISS_ERROR: "No pudimos eliminar la actividad en este momento. Int\xE9ntalo de nuevo.",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "Selecciona un d\xEDa, un mes y un a\xF1o",
  TOOLBOX_VALIDATION_MONTH_YEAR: "Por favor, selecciona un mes y un a\xF1o",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "El l\xEDmite de caracteres es {limit}",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "Ingresa un n\xFAmero igual o superior a {value}",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "Ingresa un n\xFAmero igual o inferior a {value}",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "Este es un campo obligatorio",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "Haz una selecci\xF3n",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "Haz una selecci\xF3n",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "Selecciona una fecha posterior a {date,date,medium}",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "Selecciona una fecha anterior a {date,date,medium}",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "Ingresa un n\xFAmero entero",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "Selecciona un m\xE1ximo de {integer} opciones",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "Selecciona {integer} opciones para continuar",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "El campo no puede estar vac\xEDo cuando se selecciona",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "El peso debe ser de al menos {imperial} libras ({metric} kg)",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "El peso debe ser como m\xE1ximo de {imperial} libras ({metric} kg)",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "La altura debe ser de al menos {imperial} ({metric} cm)",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "La altura debe ser como m\xE1ximo {imperial} ({metric} cm)",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "Introduce tu peso en kilogramos",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "Introduce tu peso en libras",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "Introduce tu altura en cent\xEDmetros",
  ENTER_YOUR_HEIGHT_IN_FEET: "Introduce tu altura en pies",
  ENTER_YOUR_HEIGHT_IN_INCHES: "Introduce tu altura en pulgadas",
  GOAL_REACHED: "\xA1Has alcanzado el objetivo!",
  COMPLETE_ACTIVITY: "Completa la actividad",
  EXIT_ACTIVITY_TITLE: "\xBFEst\xE1s seguro de que quieres abandonar esta actividad?",
  EXIT_ACTIVITY_BODY: "Tu progreso no se guardar\xE1 y tendr\xE1s que reiniciar la actividad desde el principio.",
  EXIT_ACTIVITY_CTA: "Dejar actividad",
  ACTIVITY_NEXT_STEP: "Continuar la actividad",
  ACTIVITY_PREV_STEP: "Volver al paso anterior",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "Categor\xEDa del programa de salud",
  NEXT_STEP: "Pr\xF3ximo paso",
  YOU_EARNED: "Ganaste",
  YOU_EARNED_X_POINTS: "\xA1Has ganado {points} puntos!",
  CREATE: "Crear {label}",
  TOMORROW: "Ma\xF1ana",
  YESTERDAY: "Ayer",
  BACK_TO_TODAY: "Volver a hoy",
  PREVIOUS_DAY: "D\xEDa anterior",
  NEXT_DAY: "Al d\xEDa siguiente",
  ACTIVITY_STATUS: "Estado de la actividad: ",
  ACTIVITY_STATUS_EXPIRED: "Caducado",
  ACTIVITY_STATUS_REMOVED: "Omitido",
  ACTIVITY_STATUS_UPCOMING: "Disponible pr\xF3ximamente",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "\xA1Todav\xEDa no hay nada planeado!",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: 'Pulsa el bot\xF3n "Agregar" para encontrar nuevos programas o explorar la lista seleccionada de actividades a continuaci\xF3n.',
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "Bien hecho, \xA1est\xE1s en racha!",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "Regresa ma\xF1ana para obtener m\xE1s informaci\xF3n o explora nuevos programas y actividades sugeridas.",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "No tienes ninguna actividad programada para este d\xEDa.",
  NO_ACTIVITIES_PAST_HEADING: "D\xEDa de descanso",
  NO_ACTIVITIES_PAST_DESCRIPTION: "Parece que te tomaste este d\xEDa para relajarte y recuperarte.",
  BUTTON_BROWSE_PROGRAMS: "Explorar programas",
  IMG_ALT_HIGH_FIVE_HANDS: "Dos manos chocando los cinco",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "Mujer de pie y desplaz\xE1ndose por una lista de programas del tama\xF1o de una pared",
  IMG_ALT_EMPTY_STATE_REST: "Hombre descansando en una silla c\xF3moda con una taza caliente y auriculares",
  THIS_IS_JUST_A_SNEAK_PEAK: "\xA1Esto es solo un adelanto!",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "Regresa y completa esta actividad el {date}.",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "Vuelve m\xE1s tarde para completar esta actividad.",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "\xA1Parece que te lo perdiste!",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "Esta actividad caduc\xF3 el {date}.",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "Parece que este no era para ti",
  YOU_SKIPPED_THIS_ACTIVITY: "Te has saltado esta actividad.",
  NO_PROGRAMS: "Todav\xEDa no hay nada para ver aqu\xED",
  NO_ACTIVITY_TITLE: "Aqu\xED no hay nada (\xA1todav\xEDa!)",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "Mostraremos tu clasificaci\xF3n aqu\xED una vez que comience el desaf\xEDo.",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "Vuelve aqu\xED para ver lo m\xE1s destacado del desaf\xEDo pronto.",
  LEADERBOARD_ALL_TIME_RANKING: "Clasificaci\xF3n de todos los tiempos",
  LEADERBOARD_CURRENT_USER_NAME: "{user} (T\xFA)",
  ACTIVITY_PROGRESS: "Progreso de la actividad",
  YOUR_JOURNEY: "Tu trayectoria",
  SUGGESTED_FOR_YOU: "Sugerido para ti",
  NO_ACHIEVEMENTS_DESCRIPTION: "Completa actividades en tu trayectoria para comenzar a desbloquear logros.",
  UNLOCK_ACHIEVEMENTS: "Desbloquea un logro por completar todas las actividades de este programa.",
  MEMBERS: "Miembros",
  SIGN_UP_PERIOD_PASSED_DETAIL: "Ya no puedes registrarse en este desaf\xEDo porque el periodo de inscripci\xF3n ha pasado.",
  REFERENCES: "Referencias",
  SIGN_UP_BY: "Reg\xEDstrate antes del {date}",
  YOURE_GOOD_TO_GO: "\xA1Ya puedes empezar!",
  CHECK_CHALLENGE_DETAILS: "Revisa los detalles del desaf\xEDo para familiarizarte con las actividades",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "Ya est\xE1s listo para comenzar el desaf\xEDo. \xBFQuieres unirte o crear un equipo?",
  CHALLENGE_ABOUT_TO_START: "\xA1Tu desaf\xEDo est\xE1 a punto de empezar!",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "Las actividades diarias aparecer\xE1n aqu\xED cuando comience tu desaf\xEDo el <strong>{date}</strong>.",
  NO_CHALLENGE_ACTIVITIES_TODAY: "No hay actividades desafiantes para ti hoy.",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: "No se pudo encontrar \u201C{query}\u201D.",
  COVERAGE_SEARCH_TRY_AGAIN: "Intenta buscar de nuevo con una palabra clave diferente.",
  EARN: "Gana",
  WORD_WITH_EXCLAMATION_POINT: "{word}",
  UPDATED: "Actualizado",
  YOUR_PROGRESS: "Tu progreso",
  YOUR_PROGRESS_IN_NUMBERS: "{current} de {total} {unit} o {percentage} por ciento completado",
  OUTCOMES_FRICTION_SCREEN_TITLE: "Sugerencias personalizadas...",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "Danos un momento para encontrar algunos recursos \xFAtiles para ti en funci\xF3n de tus respuestas",
  RELOAD_MY_SUGGESTIONS: "Volver a cargar mis sugerencias",
  YOUR_RANKING: "Tu clasificaci\xF3n",
  TOP_RANKED: "Los mejores clasificados",
  VIEW_CHALLENGE_DETAILS: "Ver detalles del desaf\xEDo",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "\xBFTe preguntas por qu\xE9 es beneficioso agregar a tus dependientes?",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "\xA1Tus dependientes tambi\xE9n pueden acceder a League y disfrutar de una vida m\xE1s saludable!",
  UNABLE_TO_LEAVE_CHALLENGE: "No se puede abandonar el desaf\xEDo, \xA1int\xE9ntalo de nuevo!",
  SUCCESSFULLY_LEFT_CHALLENGE: "Has abandonado el desaf\xEDo correctamente.",
  UNABLE_TO_JOIN_CHALLENGE: "No pudiste unirte al desaf\xEDo, \xA1int\xE9ntalo de nuevo!",
  JOIN_TEAM: "Unirse al equipo",
  X_TEAM_MEMBERS: "{memberTotal, plural, one {{memberTotal,number} miembro del equipo} other {{memberTotal,number} miembros del equipo}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, one {{spotsAvailable,number} lugar disponible} other {{spotsAvailable,number} lugares disponibles}}",
  HIGHLIGHTS: "Res\xFAmenes",
  CURRENT_RANK: "Clasificaci\xF3n actual",
  X_LIKES: "{activityLikes, plural, one {{activityLikes,number} me gusta} other {{activityLikes,number} me gusta}}",
  LIKE: "Como",
  LIKED: "Me gust\xF3",
  TEAM_INVITE_HEADING: "\xA1Juntos somos mejores!",
  TEAM_INVITE_DESCRIPTION: "Invita a tus amigos a unirse a tu equipo para completar actividades juntos.",
  TEAM_INVITE_LINK_DESCRIPTION: "\xA1Cuanto m\xE1s, mejor! Comparte este enlace con tus amigos para que puedan unirse a tu equipo.",
  TEAM_INVITE_LINK: "Enlace de invitaci\xF3n al equipo",
  INVITE: "Invitar",
  LEAVE_TEAM: "Abandonar equipo",
  INVITE_FRIENDS: "Invitar a amigos",
  PRIVACY: "Configuraci\xF3n de privacidad",
  JOIN_TEAM_SUCCESS_TITLE: "\xA1Bienvenido al equipo!",
  JOIN_TEAM_SUCCESS_DETAIL: "Conquistemos estas actividades juntos.",
  JOIN_TEAM_SUCCESS_BUTTON: "Ir a Detalles del equipo",
  CANT_JOIN_TEAM_TITLE: "No te puedes unir a {teamName}",
  CANT_JOIN_TEAM_DETAIL: "\xA1Int\xE9ntalo de nuevo! Si ya te has unido a un equipo, tendr\xE1s que abandonar tu equipo actual antes de poder unirte a {teamName}.",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "Parece que a\xFAn no te has registrado en el desaf\xEDo. Tendr\xE1s que registrarte antes de poder unirte a {teamName}.",
  LEAVE_TEAM_QUESTION: "\xBFAbandonar {teamName}?",
  LEAVE_TEAM_DESCRIPTION: "\xBFEst\xE1s seguro de que quieres abandonar {teamName}? Tu puntuaci\xF3n ya no se considerar\xE1 parte del promedio del equipo.",
  CHALLENGE_ACTIVITY_DETAIL: "Deja que otros vean tu actividad en este desaf\xEDo.",
  SHOW_MY_ACTIVITY: "Mostrar mi actividad",
  YOUR_TEAM: "Tu equipo",
  JOIN_OR_CREATE_TEAM: "Unirse o crear un equipo",
  TEAMS: "Equipos",
  CHALLENGE_TEAMS_DESCRIPTION: "\xA1\xDAnete a un equipo existente o crea el tuyo para competir juntos en el desaf\xEDo!",
  JOIN_A_TEAM: "Unirse a un equipo",
  JOIN_TEAM_DESCRIPTION: "\xA1Busca un equipo existente para unirte y competir juntos!",
  SEARCH_FOR_TEAM: "Buscar un equipo",
  CREATE_A_TEAM: "Crear un equipo",
  CREATE_TEAM_DESCRIPTION: "Personaliza tu equipo estableciendo tu propio avatar y nombre de equipo. \xA1Invita a otros a unirse a ti!",
  UP_YOUR_GAME: "Sube tu nivel",
  UP_YOUR_GAME_DESCRIPTION: "La uni\xF3n hace la fuerza. \xDAnete a un equipo o crea el tuyo.",
  XTH_PLACE: "{currentRank, selectordinal, one {#st} two {#nd} few {#rd} other {#th} } Place",
  LEADERBOARD_RANKING: "Clasificaci\xF3n",
  LEADERBOARD_USERNAME: "Nombre de usuario",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "\xA1Enlace de invitaci\xF3n copiado al portapapeles!",
  DATE_AND_TIME: "Fecha y hora",
  SELECT_TEAM_AVATAR: "Selecciona el avatar del equipo",
  CHANGE_TEAM_AVATAR: "Cambia el avatar del equipo",
  TEAM_NAME_LABEL: "Nombre del equipo",
  CREATE_TEAM: "Crear equipo",
  TEAM_NAME_ERROR: "Ingresa un nombre para tu equipo.",
  SLIDE_OF_TOTAL: "Diapositiva {key} de {total}",
  PAGE_OF_TOTAL: "P\xE1gina {current} de {total}",
  NO_SOLUTIONS_AVAILABLE: "Lo sentimos, no hay soluciones disponibles en este momento.",
  SEE_ALL_ACHIEVEMENTS: "Ver todos tus logros",
  VIEW_ALL_ACHIEVEMENTS: "Ver todos los logros",
  TAKE_ME_BACK: "Llevarme de vuelta",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "Este avatar aparece en el perfil de tu equipo y en la tabla de clasificaci\xF3n.",
  CREATE_TEAM_SUCCESS: "\xA1Tu equipo est\xE1 listo!",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "Empieza a construir tu equipo. Comparte este enlace con otras personas para que puedan completar actividades juntos.",
  VIEW_TEAM_DETAILS: "Ver detalles del equipo",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "Carrusel de caracter\xEDsticas destacadas",
  CHALLENGE_FAQ_HEADING: "\xBFTienes preguntas sobre los desaf\xEDos?",
  CHALLENGE_FAQ_CONTENT: "Consulta nuestro {helpCenterLink} para obtener una respuesta a todas tus preguntas frecuentes.",
  VIEW_THIS_RESOURCE: "Ver este recurso",
  RECOMMENDED_NEXT_STEP: "SIGUIENTE PASO RECOMENDADO",
  CARE_COLLECTIONS: "Colecciones de atenci\xF3n",
  PLAN_PROGRESS: "Progreso del plan",
  SINCE_LAST_WEEK: "desde la semana pasada",
  INSIGHT_CARD_HEADER_DISTANCE: "distancia",
  INSIGHT_CARD_HEADER_STEPS: "Pasos",
  MIN: "min",
  CAL: "Cal",
  KM: "km",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, one {paso} other {pasos}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "pisos subidos",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, one {piso} other {pisos}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "minutos activos",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "Energ\xEDa quemada",
  INSIGHT_CARD_HEADER_WEIGHT: "peso",
  INSIGHT_CARD_HEADER_HEIGHT: "Altura",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "Minutos de atenci\xF3n plena",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "Circunferencia de la cintura",
  DAY_OF_THE_WEEK: "D\xEDa de la semana",
  DAY_OF_THE_MONTH: "D\xEDa del mes",
  INSIGHT_CHART_WEEK_TITLE: "Gr\xE1fico de barras semanal de {type}",
  INSIGHT_CHART_MONTH_TITLE: "Gr\xE1fico de barras mensual de {type}",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "La cantidad de {unit} que has caminado cada d\xEDa de la semana.",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "La cantidad de {unit} que has caminado cada d\xEDa del mes.",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "Has caminado {distance} {unit} el {date}",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "La cantidad de pasos que has caminado cada d\xEDa de la semana.",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "La cantidad de pasos que has caminado cada d\xEDa del mes.",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "Has caminado {distance} {unit} el {date}",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "La cantidad de {unit} que has quemado cada d\xEDa de la semana.",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "La cantidad de {unit} que has quemado cada d\xEDa del mes.",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "Has quemado {energy} {unit} el {date}",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "La cantidad de minutos de actividad para cada d\xEDa de la semana.",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "La cantidad de minutos de actividad cada d\xEDa del mes.",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "Hiciste {value} minutos de actividad el {date}",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "La cantidad de pisos que has subido cada d\xEDa de la semana.",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "La cantidad de pisos que has subido cada d\xEDa del mes.",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "Has subido {value} pisos de escaleras el {date}",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "La cantidad de minutos de atenci\xF3n plena para cada d\xEDa de la semana.",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "La cantidad de minutos de atenci\xF3n plena cada d\xEDa del mes.",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "Hiciste {value} minutos de atenci\xF3n plena el {date}",
  DATAPOINT_CATEGORY_ACTIVITY: "Actividad",
  DATAPOINT_CATEGORY_BODY: "Cuerpo",
  MANAGE_UNITS_APPLYING_CHANGES: "Aplicando cambios...",
  MANAGE_UNITS_CHANGES_SAVED: "Cambios guardados a las {time}",
  START_TRACKING_HEALTH_HEADER: "Comenzar el seguimiento de tu salud",
  START_TRACKING_HEALTH_BODY: "Realiza f\xE1cilmente un seguimiento de tus m\xE9tricas a lo largo del tiempo para ver hasta d\xF3nde has llegado y c\xF3mo puedes trabajar para alcanzar tus objetivos de salud.",
  BROWSE_SUPPORTED_DATA: "\xBFQu\xE9 puedo rastrear?",
  LAST_UPDATED_CARRIER_DATE: "\xDAltima actualizaci\xF3n: {date} desde {carrier}",
  ACCUMULATION_PERIOD: "Per\xEDodo de acumulaci\xF3n: <strong>{start} - {end}</strong>",
  NO_DATA_AVAILABLE: "No hay datos disponibles",
  LAST_7_DAYS: "\xDAltimos 7 d\xEDas",
  LAST_30_DAYS: "\xDAltimos 30 d\xEDas",
  OLDER: "M\xE1s antiguos",
  GET_ASSISTANCE_NOW: "Chatea con tu equipo especializado en salud",
  GET_ASSISTANCE_NOW_SUBHEADING: "Chatea con nuestros profesionales de la salud certificados y el personal de atenci\xF3n al cliente. Pueden ayudarte a ahorrar dinero al dirigirte a la atenci\xF3n m\xE1s asequible y de mayor calidad en tu \xE1rea, as\xED como responder cualquier pregunta sobre tus beneficios.",
  HOW_CAN_WE_HELP: "\xBFC\xF3mo podemos ayudar?",
  LIVE_BALANCE_SPEND: "{spentAmount} de {totalAmount} gastados",
  LIVE_BALANCE_REMAINING: "{amount} restantes",
  UPDATING_UNITS_OF_MEASURE: "Actualizando unidades de medida",
  CHANGING_YOUR_UNITS: "Cambiar tus unidades afectar\xE1 la forma en que se muestran en la aplicaci\xF3n.",
  MESSAGES: "Mensajes",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "Mensajes",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "{fileName} eliminado.",
  SEND_NEW_MESSAGE: "Enviar nuevo mensaje",
  NEW_MESSAGE_DEFAULT: "Este es el comienzo de un mensaje nuevo",
  NETWORK_MISSING_COVERAGE: "Tu plan no tiene cobertura {network}.",
  CARD_FRONT_FILENAME: "{benefitName}-frente",
  CARD_BACK_FILENAME: "{benefitName}-dorso",
  START_A_VISIT: "Comenzar una visita",
  UNREAD: "Sin leer",
  NO_MESSAGES_HEADING: "A\xFAn no hay mensajes",
  NO_MESSAGES_DESCRIPTION: "\xA1Empieza enviando un mensaje nuevo!",
  RETURN_TO: "Volver a {location}",
  NO_RECOMENDATIONS: "Lo sentimos, no tenemos ninguna recomendaci\xF3n para esta \xE1rea de salud en este momento.",
  CHALLENGES_LEGAL_DISCLAIMER: "Este desaf\xEDo pretende ser una forma divertida para que todos participen y tengan una oportunidad justa de ganar. A veces, el organizador del desaf\xEDo puede ofrecer dinero en efectivo u otros premios. League no dise\xF1a la estructura del desaf\xEDo ni decide si ofrece premios. Si te preocupa la estructura de este desaf\xEDo o crees que se te ha excluido de la oportunidad de ganar, habla con el organizador del desaf\xEDo.",
  ALL_RECORDED_DATA: "Todos los datos registrados",
  NEW_MESSAGE: "Mensaje nuevo",
  MESSAGE_SUBJECT: "Asunto del mensaje (opcional)",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "Superaste el l\xEDmite m\xE1ximo de caracteres ({maximum})",
  CHAR_LIMIT_EXCEEDED: "Se excedi\xF3 el l\xEDmite de caracteres ({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "Establecer el tema del mensaje",
  TO: "Para",
  CREATE_MESSAGE: "Crear mensaje",
  MEMBER_LABEL: "Miembro",
  RESULT_COUNT: "{count, plural, one {{count,number} resultado} other {{count,number} resultados}}",
  USER_COLON: "{user}:",
  JOINED_CHALLENGE_HIGHLIGHT: "\xA1Te uniste a <strong>{challengeTitle}</strong>!",
  CREATED_TEAM_HIGHLIGHT: "\xA1Creaste a <strong>{teamName}</strong>!",
  JOINED_TEAM_HIGHLIGHT: "\xA1Te uniste a <strong>{teamName}</strong>!",
  STEPS_WALKED_HIGHLIGHT: "<strong>\xA1Caminaste {total} pasos!</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong>\xA1Estuviste en movimiento minutos!{total} </strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong>\xA1Se completa en {total} minutos!</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong>\xA1Conseguiste el logro {achievement}!</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "<strong>\xA1Has completado una actividad!</strong>",
  NO_CARE_COLLECTIONS: "Lo sentimos, no hay colecciones de atenci\xF3n disponibles.",
  WRITE_A_MESSAGE: "Escribe un mensaje...",
  UPLOAD_MULTIPLE_FILES: "Cargar varios archivos",
  SEND: "Enviar",
  SEND_MESSAGE: "Enviar mensaje",
  DATA_SOURCES: "Fuentes de datos",
  SOURCES_CAN_UPDATE: "Las siguientes fuentes pueden actualizar tu",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "Cuando hay varias fuentes disponibles, la fuente de datos con el valor m\xE1s grande se registrar\xE1 al final del d\xEDa (11:59 p.\xA0m.).",
  APPLE_HEALTH: "Apple Health",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "Garmin",
  POLAR: "Polar",
  WITHINGS: "Withings",
  HEALTH_PLAN: "Plan de salud",
  OMRON: "Omron",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "Actualizando tus unidades de medida",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "Cambiar tus unidades afectar\xE1 la forma en que se muestran en la aplicaci\xF3n.",
  DOCUMENTS: "Documentos",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "Documentos",
  DOCUMENTS_FILTER_LOADING_TEXT: "Las pesta\xF1as y los filtros se est\xE1n cargando",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "{filter} es obligatorio",
  DOCUMENT_FILTER_X_REQUIRED: "{filter} (obligatorio)",
  SHOW_RESULTS: "Mostrar resultados",
  RESET_FILTERS: "Restablecer los filtros",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "Filtros seleccionados",
  DOCUMENTS_RESULTS_HEADING: "Resultados",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "Omitir para cargar documento",
  AVERAGE: "Promedio",
  VIEW_ALL_METRIC_DATA: "Ver todos los datos de {metric}",
  EDIT_METRIC_DATA: "Editar los datos de {metric}",
  NEXT_WEEK: "Semana siguiente",
  PREVIOUS_WEEK: "Semana anterior",
  NEXT_30_DAYS: "Pr\xF3ximos 30 d\xEDas",
  PREVIOUS_30_DAYS: "\xDAltimos 30 d\xEDas",
  UNABLE_TO_CREATE_TEAM: "No se puede crear un nuevo equipo. \xA1Int\xE9ntalo de nuevo! Si ya te has unido a un equipo, tendr\xE1s que abandonar tu equipo actual antes de crear uno nuevo.",
  START_TRACKING_METRIC: "Comenzar el seguimiento de {metric}",
  CURRENTLY_NOT_SHARING_DATA: "Actualmente, no est\xE1s compartiendo ning\xFAn dato para {metric}",
  SUCCESSFULLY_LEFT_TEAM: "Has salido de {teamName} correctamente",
  LEAVE_TEAM_FAILURE: "Algo sali\xF3 mal, \xA1int\xE9ntalo de nuevo!",
  VIEW_DATA_SOURCES: "Ver fuentes de datos",
  OR_CALL_PHONE: "o llama al {phoneNumber}{extension}",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "No hay documentos que coincidan con tus filtros",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "Intenta eliminar algunos de tus filtros para obtener m\xE1s resultados",
  DOCUMENTS_EMPTY_STATE_HEADING: "No hay documentos aqu\xED (\xA1todav\xEDa!)",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "Un lugar para ver y organizar todos tus documentos",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team} (Tu equipo)",
  YOUR_CARE_ADVISOR: "Tu gu\xEDa de atenci\xF3n",
  COMPOSE: "Redactar",
  COMPOSE_NEW_MESSAGE: "Redactar un nuevo mensaje",
  POLICY_INFORMATION_UNAVAILABLE: "No hay informaci\xF3n disponible para este periodo de la p\xF3liza.",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "No hay informaci\xF3n para este miembro. Prueba con otro miembro.",
  MESSAGE_THREADS_LIST_SUBTITLE: "Nuestro equipo puede tardar hasta 2 d\xEDas h\xE1biles en responder. Si se trata de una emergencia m\xE9dica, llama al 911.",
  ALL_MESSAGES_FOR_THREAD_BANNER: "No nos env\xEDes informaci\xF3n espec\xEDfica sobre tus afecciones de salud a trav\xE9s de este chat.",
  TROUBLE_DISPLAYING_DATA: "Tenemos problemas para mostrar tus datos",
  RETRY: "Volver a intentar",
  YOU: "T\xFA",
  MESSAGES_IN_CONVERSATION_TITLED: "Mensajes en conversaciones tituladas {subject}",
  TEAM_AVERAGE: "Promedio del equipo",
  YOUR_METRICS: "Mis m\xE9tricas",
  YOUR_METRICS_SUBTITLE: "Los datos de salud m\xE1s recientes de tus aplicaciones y dispositivos conectados.",
  APPS_AND_DEVICES: "Aplicaciones y dispositivos",
  APP_AND_DEVICE_METRICS: "M\xE9tricas de aplicaciones y dispositivos",
  VIEW_ALL_METRICS: "Ver m\xE9tricas de aplicaciones y dispositivos",
  RENAME: "Cambiar el nombre",
  ENTER_DOCUMENT_NAME: "Ingresa un nombre para el documento",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "Esta es una actividad de prioridad baja",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "Esta es una actividad de prioridad media",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "Esta es una actividad de prioridad alta",
  ACTIVITY_EXPIRY_DATE: "Caduca {date}",
  GENERIC_MESSAGING_ERROR: "Parece que hubo un problema; actualiza la p\xE1gina e int\xE9ntalo de nuevo.",
  VIEW_THE_ACTIVITIES: "Ver las actividades",
  DRAG_FILES_HERE_OR: "Arrastra los archivos aqu\xED o",
  ADD_A_DOCUMENT: "Agregar un documento",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "Cargar un documento",
  ADD_DOCUMENT: "Agregar documento",
  REMOVE_FILE: "Elimina el archivo",
  REMOVE_FILE_FILENAME: "Eliminar archivo: {filename} ({fileSize})",
  UNABLE_TO_SEND: "No se pudo enviar, int\xE9ntalo de nuevo",
  ERROR_HEADING: "Algo sali\xF3 mal por nuestra parte.",
  FILE_TYPE_NOT_SUPPORTED: "El tipo de archivo no es compatible",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "Usa un archivo en uno de estos formatos: {fileTypes}",
  RECENT_MESSAGES: "Mensajes recientes",
  NO_MESSAGES_YET: "No tienes mensajes (\xA1todav\xEDa!)",
  NO_MESSAGES_DETAIL: "Tu gu\xEDa de atenci\xF3n asignado te ayudar\xE1 a tomar decisiones m\xE9dicas m\xE1s inteligentes, en cada paso del camino.",
  START_A_NEW_MESSAGE: "Escribir un mensaje nuevo",
  USER_ADDED_DOCUMENT: "{user} agreg\xF3 un documento",
  ERROR_SAVING_TRY_AGAIN: "Error al guardar los cambios. Int\xE9ntalo de nuevo",
  MODIFIED_ON_DATE: "Modificado el {date}",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "Subido {date}",
  FILE_TYPE_TOO_LARGE: "El tama\xF1o del archivo es demasiado grande",
  MAXIMUM_FILE_SIZE_IS: "El tama\xF1o m\xE1ximo del archivo es {fileSize}.",
  FILE_TOO_LARGE: "Vaya. Ese archivo es demasiado grande.",
  FILE_SHOULD_BE_UNDER: "Tu archivo debe ser inferior a {fileSize}",
  ATTACHMENT_LIMIT_REACHED: "Vaya. Son demasiados archivos.",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "{maxNumberOfDocs, plural, one {Puedes adjuntar hasta {maxNumberOfDocs,number} archivo.} other {Puedes adjuntar hasta {maxNumberOfDocs,number} archivos.}}",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "Vaya. Ese tipo de archivo no es compatible.",
  ATTACHMENT_TYPES_ALLOWED: "Puedes adjuntar tu archivo en estos formatos: {fileTypes}",
  MESSAGES_PAGINATION_ERROR: "No se pueden cargar los mensajes. <button>Int\xE9ntalo de nuevo.</button>",
  TRANSACTION_TABLE_CAPTION: "Historial de transacciones",
  SELECT_PARTICIPANT_ERROR: "Seleccionar a alguien para enviarle un mensaje",
  PREVIEW_OF_FILE: "Vista previa de {fileName}",
  VIEW_DOCUMENT_ACTIONS: "Ver {documentName} acciones.",
  VISIT_LINK: "Visita el enlace",
  COMPLETE_BY_DATE: "Completar antes del {date}",
  ERROR_UPLOADING_YOUR_FILE: "Error al cargar el archivo. Int\xE9ntalo de nuevo.",
  DOCUMENT_SUBMITTED: "Documento enviado",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "Tu documento estar\xE1 disponible en breve. Espera unos momentos y actualiza la p\xE1gina.",
  ERROR_RENAMING_YOUR_DOCUMENT: "Error al cambiar el nombre del documento. Int\xE9ntalo de nuevo.",
  DOCUMENT_RENAMED_SUCCESSFULLY: "Se ha cambiado el nombre del documento correctamente.",
  ERROR_HEADING_GENERIC: "Algo sali\xF3 mal",
  ERROR_HEADING_NO_CONNECTION: "Sin conexi\xF3n a internet",
  ERROR_CAPTION_BACKEND: "Ocurri\xF3 un problema de conexi\xF3n con nuestro servidor.\n Vuelve a intentarlo m\xE1s tarde.",
  ERROR_CAPTION_NO_CONNECTION: "Ocurri\xF3 un problema al cargar los datos.\n Revisa tu conexi\xF3n a Internet y vuelve a intentarlo.",
  SURE_YOU_WANT_TO_DELETE: "\xBFEst\xE1s seguro de que quieres eliminar a {name}?",
  CANNOT_BE_UNDONE: "Esta acci\xF3n no se puede deshacer",
  DELETE_DOCUMENT: "Eliminar documento",
  DOCUMENT_DELETED: "Documento eliminado",
  ERROR_DELETING_YOUR_DOCUMENT: "Error al eliminar tu documento",
  ABOUT_ME: "Acerca de m\xED",
  SEE_MORE: "VER M\xC1S",
  X_ABOUT_X: "{left} sobre {right}",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "No podemos cargar esta p\xE1gina. Revisa tu conexi\xF3n a Internet y vuelve a intentarlo.",
  WHO_IS_THIS_DOCUMENT_ABOUT: "\xBFDe qui\xE9n se trata este documento?",
  SELECT_A_USER: "Seleccionar un usuario",
  SELECT_DOCUMENT: "Seleccionar documento",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "Esto ayuda a tu gu\xEDa de atenci\xF3n a identificar a la persona a la que se refiere este documento.",
  VIEW_ALL_MESSAGE_THREADS: "Ver todos los hilos de mensajes",
  TOGGLE_MENU: "Alterna la lista de mensajes",
  METRICS: "M\xE9tricas",
  UNREAD_MESSAGES: "mensajes no le\xEDdos",
  WITH_DOCUMENT: "con documento",
  X_TOKENS: "{tokenCount, plural, one {{tokenCount,number} token} other {{tokenCount,number} tokens}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "PROGRAMAS Y DESAF\xCDOS",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "\xBFEst\xE1s listo para algo nuevo?",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "Encuentra programas y desaf\xEDos de salud que coincidan con tus metas personales.",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "PROGRAMAS Y DESAF\xCDOS",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "\xBFEst\xE1s listo para algo nuevo?",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "Encuentra programas y desaf\xEDos de salud que coincidan con tus metas personales.",
  EXPLORE_LIBRARY: "Explora la biblioteca",
  YOUVE_GOT_THIS: "\xA1Ya lo tienes!",
  YOUVE_GOT_THIS_NAME: "\xA1Ya lo tienes, {firstName}!",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "\xA1Te damos la bienvenida de tu trayectoria de salud!",
  HEALTH_JOURNEY_PAGE: "P\xE1gina de Trayectoria de salud",
  VIEW_DETAILS: "Ver detalles",
  NEXT_ACTIVITY: "PR\xD3XIMA ACTIVIDAD",
  X_OF_Y_ACTIVITIES: "{completed} de {total} actividades",
  X_PERCENTAGE: "{percent}\xA0%",
  SOLUTIONS: "Soluciones",
  EXPLORE: "Explorar",
  DOCUMENT_NAME_TOO_LONG: "Superaste el l\xEDmite m\xE1ximo de caracteres ({maximum})",
  CLAIM_PATIENT_NAME: "Para: {patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "No hay programas. Sin desaf\xEDos. Solo actividades puntuales.",
  FEEDBACK_LINK_LABEL: "Califica la informaci\xF3n {value} de {maximum} en cuanto a la utilidad",
  RECENT_ACHIEVEMENTS: "Logros recientes",
  ACHIEVEMENTS_BANNER_HEADER: "\xA1Felicidades por ganar estas insignias!",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "\xA1Nos encanta ver que priorizas tu salud y bienestar! Consulta otros logros por los que est\xE1s trabajando.",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "\xA1Nos encanta ver que priorizas tu salud y bienestar! Consulta otros logros que has obtenido y aquellos por los que est\xE1s trabajando.",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "Sin logros (\xA1todav\xEDa!)",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "Completa actividades en tu trayectoria para comenzar a obtener logros.",
  HISTORY_EMPTY_STATE_HEADER: "Aqu\xED no hay nada, \xA1todav\xEDa!",
  HISTORY_EMPTY_STATE_DESCRIPTION: "Cuando completes una actividad, programa o desaf\xEDo, aparecer\xE1 aqu\xED.",
  INDIVIDUAL_BALANCE: "{balance} de {membersFullName}",
  FAMILY_BALANCE: "{balance} de la familia",
  UPCOMING: "Pr\xF3ximamente",
  REMOVED: "Eliminado",
  COMPLETED_TODAY: "Completada",
  CLAIMS_BENEFIT_CARD_TITLE: "Reclamaciones de seguros",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "Lleva un registro de lo que debes por tu atenci\xF3n y cu\xE1nto cubrir\xE1 el seguro",
  CLAIMS_FILTER_PROPERTY_TYPE: "Tipo de servicio",
  CLAIMS_FILTER_PROPERTY_STATUS: "Estado de la reclamaci\xF3n",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "Miembro",
  CLAIMS_FILTER_VALUE_ORAL: "Dental",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "M\xE9dico",
  CLAIMS_FILTER_VALUE_VISION: "Visi\xF3n",
  CLAIMS_FILTER_VALUE_PHARMACY: "Farmacia",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "Profesional",
  CLAIMS_FILTER_VALUE_APPROVED: "Pagaremos nuestra parte",
  CLAIMS_FILTER_VALUE_PENDING: "Procesamiento",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "Cobertura parcial",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "No cubierto",
  CLAIMS_FILTER_VALUE_ADJUSTED: "Ajustado",
  DRINKS_PER_WEEK: "Bebidas por semana",
  OR_SELECT_BETWEEN_THESE: "o selecciona entre estos",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "Intervalo de fechas",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "En lo que va del a\xF1o",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "A\xF1o anterior",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "\xDAltimos 12 meses",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "\xDAltimos 2 a\xF1os",
  CLAIMS_VIEW_ALL: "Ver todo",
  CHALLENGE_TYPE_GROUP_TAG: "Grupo",
  CHALLENGE_TYPE_SOLO_TAG: "Solitario",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "Desaf\xEDos grupales",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "Desaf\xEDos en solitario",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "Mantente responsable mientras te retas a ti mismo con atenci\xF3n plena, iniciativas de alimentaci\xF3n sana, competiciones de fitness, actividades de reducci\xF3n del estr\xE9s, desaf\xEDos de pasos y mucho m\xE1s.",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "Compite en solitario y desaf\xEDate a ti mismo para completar grupos de actividades centradas en la aptitud f\xEDsica, la nutrici\xF3n, la salud mental, el manejo del estr\xE9s y m\xE1s.",
  HELPFUL_LINKS: "Enlaces \xFAtiles",
  HOW_CLAIMS_WORK: "C\xF3mo funcionan las reclamaciones",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "Entender c\xF3mo funcionan tus reclamaciones",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>Cuando visitas a uno de los m\xE9dicos de tu red, este solicita el pago. Si tienes un copago, te lo cobran y luego presenta una reclamaci\xF3n a la compa\xF1\xEDa de seguros.</p><p>Luego revisamos tu plan de seguro, tambi\xE9n conocido como tu contrato de beneficios, para determinar si el servicio est\xE1 cubierto. Si es as\xED, lo aprobaremos seg\xFAn los t\xE9rminos del contrato de beneficios. Si no est\xE1 cubierto como parte de tu plan de beneficios, eres responsable de pagar el servicio.</p>",
  CLAIMS_STATUS_DEFINITIONS: "Definiciones del estado de la reclamaci\xF3n",
  CLAIMS_STATUS_DEFINITION_APPROVED: "Pagaremos nuestra parte significa que la reclamaci\xF3n est\xE1 aprobada y que aceptamos pagar una parte o la totalidad de la reclamaci\xF3n en funci\xF3n de tu plan.",
  CLAIMS_STATUS_DEFINITION_PENDING: "Procesar significa que estamos averiguando qui\xE9n debe qu\xE9.",
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: "Cobertura parcial significa que cubriremos una parte de tu reclamo.",
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: "Sin cobertura significa que el servicio no est\xE1 cubierto en tu plan de beneficios y tendr\xE1s que pagarlo.",
  CLAIMS_STATUS_DEFINITION_ADJUSTED: "Ajustada significa que algo ha cambiado aqu\xED, as\xED que tuvimos que revisar otra vez esta reclamaci\xF3n.",
  YOUR_COST: "Tu costo:",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {Reclamaci\xF3n m\xE9dica} oral {Reclamaci\xF3n dental} vision {Reclamaci\xF3n de visi\xF3n} pharmacy {Reclamaci\xF3n de farmacia} professional {Reclamaci\xF3n profesional} other {Reclamaci\xF3n}}",
  FILTER_BY: "Filtrar por",
  CLAIMS_LIST_EMPTY_HEADING: "No hay nada aqu\xED, \xA1todav\xEDa!",
  CLAIMS_LIST_EMPTY_MESSAGE: "Las reclamaciones que se env\xEDen aparecer\xE1n aqu\xED.",
  CLAIMS_GENERIC_ERROR_HEADING: "Algo sali\xF3 mal",
  CLAIMS_GENERIC_ERROR_MESSAGE: "Ocurri\xF3 un problema t\xE9cnico por nuestra parte.{br}Int\xE9ntalo de nuevo en unos minutos.",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "Ve lo que tu seguro cubre y lo que a\xFAn le puedes deber a tu proveedor. Puedes acceder a tus reclamaciones enviadas dentro de los dos a\xF1os anteriores a la fecha de hoy.",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "Sin resultados",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "{filterCount, plural, one {No hay reclamaciones que coincidan con el filtro que has elegido} other {No hay reclamaciones que coincidan con los filtros que has elegido}}",
  VIEW_MY_PROFILE: "Ver mi perfil",
  PLAN_PROGRESS_COST_COVERAGE: "Planifica el progreso y los costos",
  PLAN_PROGRESS_POP_UP_LABEL: "\xBFQu\xE9 es un {title}?",
  RECENT_CLAIMS_LIST_EMPTY: "Las reclamaciones presentadas en los \xFAltimos 90 d\xEDas aparecer\xE1n aqu\xED. Puedes acceder a todas las reclamaciones seleccionando 'Ver todo'. ",
  DOWNLOAD_CLAIMS: "Descargar reclamaciones",
  PLAIN_SOMETHING_WENT_WRONG: "Algo sali\xF3 mal",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "Ocurri\xF3 un problema al descargar tus reclamaciones.",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "Tus beneficios, reclamaciones e informaci\xF3n de bienestar, todo en un solo lugar.",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "\xBFListo para empezar?",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "Navega por nuestra biblioteca para encontrar programas y desaf\xEDos de salud que coincidan con tus metas personales.",
  USER_DROPDOWN_BTN_LABEL: "Men\xFA del perfil",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "\xBFQu\xE9 es un copago y coseguro?",
  ANSWERS_NOT_SAVED: "No podemos guardar tus respuestas en este momento. Vuelve a intentarlo m\xE1s tarde.",
  SESSION_ABOUT_TO_TIMEOUT: "Has tenido poca actividad durante un tiempo y tu sesi\xF3n est\xE1 a punto de expirar. \xBFQuieres continuar la sesi\xF3n?",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "Has tenido poca actividad durante un tiempo y tu sesi\xF3n est\xE1 a punto de expirar.",
  SESSION_TIMEOUT: "La sesi\xF3n expir\xF3",
  CONTINUE_SESSION: "Continuar sesi\xF3n",
  SESSION_LOG_OUT: "Cerrar sesi\xF3n",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "Seleccionar una direcci\xF3n de entrega",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "\xBFQui\xE9n necesita una nueva tarjeta?",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "Selecciona uno o m\xE1s miembros.",
  DEFAULT_ADDRESS: "Direcci\xF3n predeterminada",
  CHOOSE_NEW_ADDRESS: "Elige una nueva direcci\xF3n",
  CHOOSE_NEW_ADDRESS_HINT: "Proporciona una direcci\xF3n postal diferente para la entrega de la tarjeta. Esto no cambiar\xE1 la direcci\xF3n predeterminada de tu cuenta.",
  CARD_ACTIONS: "Acciones de la tarjeta",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "Consigue un logro por completar todas las actividades de este desaf\xEDo",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "Aqu\xED no hay nada, \xA1todav\xEDa!",
  CHALLENGE_ACTIVITY_UP_NEXT: "A continuaci\xF3n",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "\xA1Regresa cuando comience el desaf\xEDo!",
  CHALLENGE_ACTIVITY_COMPLETED: "Completada",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "Las actividades que completes aparecer\xE1n aqu\xED.",
  CHALLENGE_ACTIVITY_MISSED: "Perdida",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "Las actividades que te pierdas aparecer\xE1n aqu\xED.",
  CHALLENGE_COMPLETED_TITLE: "\xA1Eso es todo!",
  CHALLENGE_COMPLETED_BODY: "No hay m\xE1s actividades que completar. \xA1Bien hecho!",
  ACHIEVEMENT_EARNED: "Celebraci\xF3n de los logros obtenidos",
  ACHIEVEMENT_PROGRESS: "Progreso de logros",
  ADD_DATA: "Agregar datos",
  CONFIRM_DETAILS: "Confirma tus datos",
  MEDICAL_ID_CARD: "Tarjeta de identificaci\xF3n m\xE9dica",
  NEW_ADDRESS: "Nueva direcci\xF3n de entrega",
  MESSAGE_CENTER: "Centro de mensajes",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "Mensajer\xEDa",
  CHOOSE_NEW_ADDRESS_DESC: "Proporciona una direcci\xF3n postal diferente para la entrega de la tarjeta. Esto no cambiar\xE1 la direcci\xF3n predeterminada de tu cuenta. La direcci\xF3n postal debe estar dentro de Estados Unidos.",
  SELECT_STATE: "Seleccionar un estado",
  REQUIRED_FIELD: "{field} es un campo obligatorio",
  CLAIMS_YOUR_COST: "Tu costo",
  CLAIMS_PROCESSING_DESCRIPTION: "Todav\xEDa estamos procesando esta reclamaci\xF3n. Recibir\xE1s una notificaci\xF3n cuando est\xE9 lista para que la revises.",
  DOWNLOAD_BENEFITS_EXPLANATION: "Descargar explicaci\xF3n de beneficios",
  DATA_COLLECTED: "Datos recopilados el {date}",
  MEMBER_CARD_ON_ITS_WAY: "Tu nueva tarjeta de identificaci\xF3n de miembro est\xE1 en camino.",
  MEMBER_CARD_ARRIVAL: "Las nuevas tarjetas de identificaci\xF3n de miembro llegar\xE1n en aproximadamente 10 d\xEDas h\xE1biles.",
  TRY_LATER: "Vuelve a intentarlo m\xE1s tarde.",
  RESUBMIT: "Volver a enviar",
  ORDER_NEW_CARD_HEADING: "Solicitar una nueva tarjeta",
  CANCEL_ORDER_NEW_CARD_TITLE: "\xBFEst\xE1s seguro de que quieres cancelar tu pedido?",
  CANCEL_ORDER_NEW_CARD_DESC: "Siempre puedes volver m\xE1s tarde.",
  CANCEL_ORDER: "Cancelar pedido",
  STEP: "Paso",
  ORDER_CARD: "Solicitar tarjeta",
  PLEASE_MAKE_A_SELECTION: "Haz una selecci\xF3n",
  TOTAL_COST_OVERVIEW: "Resumen del costo total",
  TOTAL_COST_DESCRIPTION: "La suma de los costos de los servicios de tu reclamaci\xF3n.",
  SUPPORT: "Ayuda",
  GET_SUPPORT: "Obtener soporte",
  MESSAGE_WITH_A_MEMBER: "Ponte en contacto con un defensor del miembro si tienes preguntas sobre tu reclamaci\xF3n.",
  CLAIMS_SERVICE_DETAILS_HEADING: "Detalles del servicio",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "Recursos adicionales",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "Un desglose de los servicios individuales que recibiste y los costos asociados con ellos.",
  CLAIMS_SERVICE_DETAILS_SERVICE: "Servicio",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "Nombre de la receta",
  CLAIMS_SERVICE_DETAILS_DATE: "Fecha del servicio",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "C\xF3digo de procedimiento",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "N\xFAmero de la receta",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "Tu costo",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "Detalles de costos",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "Mostrar detalles de costos",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "Ocultar detalles de costos",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "Desglose de tu costo",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "Las cantidades que se indican a continuaci\xF3n se destinan al progreso de tu plan",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "Selecciona uno o m\xE1s miembros para continuar",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "El campo contiene caracteres no v\xE1lidos",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "Cobrado por el proveedor",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "Otros seguros pagados",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "Tu descuento de miembro",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "Tu plan negoci\xF3 con tu proveedor \npara ofrecerte una tarifa con descuento.",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "Explicaci\xF3n de tu descuento para miembros",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "La cantidad que paga tu otro seguro.",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: "Otra explicaci\xF3n del pago del seguro",
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {Tu plan se pag\xF3 el {paymentDate}} undefined {Tu plan pagado}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "La cantidad por la que tu plan cubre \nservicios de atenci\xF3n m\xE9dica.",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: "Explicaci\xF3n de tu plan pagado",
  CLAIMS_COST_DETAILS_YOUR_COST: "Tu costo",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "Observaci\xF3n del servicio",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "Dentro de la red",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "Fuera de la red",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "Selecciona una opci\xF3n de direcci\xF3n para continuar",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "La ciudad no puede contener n\xFAmeros",
  MESSAGE_CLOSED_TEXT: "Este mensaje ha sido entregado.",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "Obt\xE9n una visi\xF3n general de tus costos acumulados, costos compartidos y m\xE1s.",
  VIEW_PLAN_PROGRESS: "Ver el progreso del plan",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "Recibir\xE1s una factura asociada con esta reclamaci\xF3n de tu proveedor.",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "Ocurri\xF3 un problema al descargar tu explicaci\xF3n de beneficios.",
  END_CHAT_MODAL_DESCRIPTION: "Si contin\xFAas, finalizar\xE1 el chat. No podr\xE1s volver a esta conversaci\xF3n en el futuro.",
  RETURN_TO_CHAT: "Volver al chat",
  CHATS_IN_CONVERSATION: "Chats en la conversaci\xF3n",
  LIVE_CHAT_CHARACTER_LIMIT: "Se alcanz\xF3 el l\xEDmite de caracteres ({characterLimit})",
  CHAT_MESSAGE_INPUT: "Entrada de mensaje de chat",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {Deducible} non_covered {No cubierto} copayment {Copago} coinsurance {Coseguro} may_be_billed {Te pueden facturar} amount_exceeding_maximum {Cantidad que excede el m\xE1ximo} additional_member_liability {Responsabilidad adicional de los miembros} special_processing {Procesamiento especial} paid_to_member {Pagado al miembro} plan_paid_to_you {Plan pagado a ti} other {Otro}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "Mensaje nuevo",
  QUESTIONNAIRE_FLOW_CLOSE: "Cerrar",
  QUESTIONNAIRE_FLOW_GO_BACK: "Volver",
  QUESTIONNAIRE_FLOW_CONTINUE: "Continuar",
  QUESTIONNAIRE_FLOW_RETRY: "Volver a intentar",
  CLEAR_ALL: "Borrar todo",
  HEALTH_REPORT_PAGE_HEADING: "Tu informe de salud",
  USERNAME_HAS_JOINED_THE_CHAT: "{username} se uni\xF3 al chat",
  USERNAME_HAS_LEFT_THE_CHAT: "{username} abandon\xF3 el chat",
  PERVIOUS_MONTH: "Bot\xF3n del mes pasado",
  NEXT_MONTH: "Bot\xF3n del mes pr\xF3ximo",
  CHAT_CLOSED: "Este chat ha sido cerrado.",
  CONNECTING_YOU_WITH_AN_AGENT: "Conect\xE1ndote con un agente...",
  LAST_DAY_TO_USE_BALANCE: "\xDAltimo d\xEDa para usar el saldo",
  LAST_DAY_TO_SUBMIT_CLAIMS: "\xDAltimo d\xEDa para enviar una reclamaci\xF3n",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "Algo sali\xF3 mal",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "Ocurri\xF3 un problema, int\xE9ntalo de nuevo",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "Algo sali\xF3 mal",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "Ocurri\xF3 un problema, int\xE9ntalo de nuevo",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "Cerrar",
  LIVE_CHAT_LANDING_PAGE_TITLE: "Chat",
  LIVE_CHAT_DELIVERY_FAILURE: "No entregado",
  LIVE_CHAT_RESEND_MESSAGE: "Reenviar mensaje",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "El mensaje no se pudo enviar",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "Lo siento, parece que tu mensaje no lleg\xF3 a nuestro equipo.",
  VIEW_TOOLTIP: "ver descripci\xF3n emergente {tooltipLabel}",
  NO_DATA_SOURCES: "Sin fuentes de datos",
  MAX_VALUE_ERROR_MESSAGE: "Ingresa un valor inferior o igual a {max}",
  MIN_VALUE_ERROR_MESSAGE: "Ingresa un valor mayor o igual a {min}",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "Selecciona un valor",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "La fecha de inicio debe ser anterior a la fecha de finalizaci\xF3n",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "La duraci\xF3n no puede exceder las 24 horas",
  NO_FUTURE_DATES: "La fecha no puede ser en el futuro",
  CLAIMS_REMOVE_FILTER: "Elimina el filtro {label}.",
  REMOVE_X_FILTER: "Elimina el filtro {label}.",
  WALLET_BENEFIT_STATUS_PENDING: "Pendiente",
  WALLET_BENEFIT_STATUS_ACTIVE: "Activo",
  WALLET_BENEFIT_STATUS_PAST: "Anteriores",
  CLAIMS_CLAIMS_LIST_HEADING: "Tus reclamaciones",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "No hay nada aqu\xED, \xA1todav\xEDa!",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "Los beneficios que est\xE1n activos aparecer\xE1n aqu\xED.",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "Beneficios",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "Beneficios - Tarjetas de identificaci\xF3n",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "Reclamaciones",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "Detalles de la reclamaci\xF3n",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "Detalles de la reclamaci\xF3n - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "Pendiente",
  CLAIMS_STATUS_PENDING: "Pendiente",
  CSE_END_IMPERSONATION_MODE: "Finalizar el modo de suplantaci\xF3n",
  CSE_RESTRICTED_ACTION: "Acci\xF3n restringida",
  CSE_RESTRICTED_ACTION_MESSAGE: "Durante el modo de simulaci\xF3n de usuario, solo puedes ver el portal del miembro. No se permiten acciones en nombre de los miembros.",
  CSE_RESTRICTED_VIEW_MESSAGE: "No tienes permiso para acceder a esta secci\xF3n.",
  CSE_IMPERSONATION_MASONRY_HEADER: "Contenido restringido para agentes de soporte",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "Es posible que no tengas permiso para ver ciertos widgets o cierto contenido.",
  CSE_IMPERSONATION_BANNER_TEXT: "Modo de solo lectura para {memberName}.",
  CLAIMS_SUMMARY_PAGE_HEADING: "Reclamaciones",
  HEALTH_INSURANCE_GLOSSARY: "Glosario de seguro de salud",
  CLAIMS_FILTER_LOADING_TEXT: "Las pesta\xF1as y los filtros se est\xE1n cargando",
  ANDROID_HEALTH_CONNECT: "Health Connect",
  GET_CARE_SERVICE_CARD_CTA: "Ver servicio",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "Pr\xF3ximo paso",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "Paso anterior",
  HEIGHT: "Altura",
  WEIGHT: "Peso"
};

// src/strings/ko.json
var ko_default = {
  COMPLETED_ACTIVITIES: "\uC644\uB8CC\uB41C \uD65C\uB3D9",
  AVAILABLE: "\uC0AC\uC6A9 \uAC00\uB2A5",
  SEE_MORE_ACTIVITIES: "\uB354 \uBCF4\uAE30",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "\uB354 \uB9CE\uC740 \uC644\uB8CC\uB41C \uD65C\uB3D9 \uBCF4\uAE30",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "\uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uBA74 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4!",
  MISSED_ACTIVITIES: "\uB193\uCE5C \uD65C\uB3D9",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "\uB354 \uB9CE\uC740 \uB193\uCE5C \uD65C\uB3D9 \uBCF4\uAE30",
  MISSED_ACTIVITIES_EMPTY_STATE: "\uB193\uCE5C \uD65C\uB3D9\uC774 \uC788\uC73C\uBA74 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4!",
  DISPLAYING_ACTIVITIES_FROM_DATE: "{date}\uC77C \uC774\uD6C4 \uD65C\uB3D9 \uBCF4\uC5EC\uC8FC\uAE30",
  STANDALONE_ACTIVITIES: "\uB3C5\uB9BD\uD615 \uD65C\uB3D9",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "\uC544\uC9C1 \uD560 \uC77C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4! \uB098\uC911\uC5D0 \uB2E4\uC2DC \uC624\uC138\uC694.",
  VIEW_ALL_STANDALONE_ACTIVITIES: "\uBAA8\uB4E0 \uB3C5\uB9BD\uD615 \uD65C\uB3D9 \uBCF4\uAE30",
  MODULE_TEMPLATE_BUTTON: "\uBAA8\uB4C8 \uD15C\uD50C\uB9BF \uBC84\uD2BC",
  ACHIEVEMENTS: "\uC5C5\uC801",
  VIEW_ACHIEVEMENTS: "\uC5C5\uC801 \uBCF4\uAE30",
  DEPENDENT_NOTIFICATION_MESSAGE: "\uC0C8\uB85C\uC6B4 \uBD80\uC591\uAC00\uC871\uC744 \uCD08\uB300\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  DEPENDENT_COVERED_BY_HSA_ONLY: "\uCC38\uACE0: \uC774 \uBD80\uC591\uAC00\uC871\uC740 \uADC0\uD558\uC758 \uAC74\uAC15 \uC9C0\uCD9C \uACC4\uC88C\uC5D0\uC11C\uB9CC \uBCF4\uC7A5\uC744 \uBC1B\uC2B5\uB2C8\uB2E4.",
  WALLET_LANDING_NAV_LABEL: "\uC9C0\uAC11 \uB0B4\uBE44\uAC8C\uC774\uC158",
  VIEW_CARD_FOR_DEPENDENT: "{dependentName}\uC5D0 \uB300\uD55C \uCE74\uB4DC \uBCF4\uAE30",
  LEARN_MORE_LEAGUE_FAMILY: "\uBD80\uC591\uAC00\uC871\uC744 \uC704\uD55C League Family\uC5D0 \uB300\uD558\uC5EC",
  GO_TO_LIFEMARKET: "\uC8FC\uBB38 \uBC88\uD638 {invoiceId}\uC744(\uB97C) \uBCF4\uB824\uBA74 Lifemarket\uC73C\uB85C \uC774\uB3D9\uD558\uC138\uC694",
  GO_TO_CLAIM: "{title} \uCCAD\uAD6C \uC138\uBD80 \uC815\uBCF4 \uBCF4\uAE30",
  GO_TO_RECEIPT: "{title} \uC601\uC218\uC99D \uBCF4\uAE30",
  LINK_TO_TRANSACTION_DETAILS: "\uC790\uC138\uD55C \uB0B4\uC6A9 \uB9C1\uD06C",
  VIEW_CLAIM_LABEL_HIDDEN: "{vendor} \uCCAD\uAD6C {referenceId} \uBCF4\uAE30",
  QLE_LINK_LABEL: "\uC801\uACA9 \uC0DD\uD65C \uC774\uBCA4\uD2B8\uC5D0 \uB300\uD574 \uC790\uC138\uD788 \uC54C\uC544\uBCF4\uAE30",
  PLEASE_CALL: "{phoneNumber}(\uC73C)\uB85C \uC804\uD654\uD558\uC138\uC694",
  BROWSE_FILES: "\uD30C\uC77C \uCC3E\uC544\uBCF4\uAE30",
  TOTAL_BALANCE: "\uCD1D \uC794\uC561",
  LIFEMARKET: "Lifemarket",
  ABOUT: "\uC815\uBCF4",
  LEAGUE_CREDIT_DESCRIPTION_1: '\uB9AC\uADF8 \uD06C\uB808\uB527\uC740 {lifemarketLink}\uC5D0\uC11C \uB2E4\uC591\uD55C \uB77C\uC774\uD504\uC2A4\uD0C0\uC77C \uBC0F \uC6F0\uB2C8\uC2A4 \uC81C\uD488\uACFC \uC11C\uBE44\uC2A4\uC5D0 \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. "\uB9AC\uADF8 \uD30C\uD2B8\uB108 \uD61C\uD0DD(League Partner Offer)"\uC774\uB77C\uACE0 \uD45C\uC2DC\uB41C \uC0C1\uD488\uC5D0\uB9CC \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.',
  LEAGUE_CREDIT_DESCRIPTION_2: '\uB9AC\uADF8 \uD06C\uB808\uB527\uC744 \uC0AC\uC6A9\uD558\uB824\uBA74 Lifemarket\uC5D0\uC11C \uACB0\uC81C \uC2DC \uACB0\uC81C \uC635\uC158 \uBAA9\uB85D\uC5D0\uC11C "\uB9AC\uADF8 \uD06C\uB808\uB527"\uC744 \uC120\uD0DD\uD558\uC138\uC694. {learnMoreLink}',
  DID_YOU_GET_A_PROMO_CODE: "\uD504\uB85C\uBAA8\uC158 \uCF54\uB4DC\uB97C \uBC1B\uC73C\uC168\uB098\uC694?",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "\uB9E4\uC7A5 \uD06C\uB808\uB527\uC774\uB098 \uD504\uB85C\uBAA8\uC158 \uCF54\uB4DC\uB97C \uBC1B\uC73C\uC2E0 \uACBD\uC6B0, \uC5EC\uAE30\uC5D0 \uC785\uB825\uD558\uC2DC\uBA74 \uD06C\uB808\uB527\uC744 \uBC1B\uC73C\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC785\uB825\uD558\uBA74 \uACC4\uC815\uC5D0 \uC790\uB3D9\uC73C\uB85C \uC801\uC6A9\uB429\uB2C8\uB2E4.",
  GET_CARE: "\uAD00\uB9AC \uBC1B\uAE30",
  DISCLAIMER: "\uBA74\uCC45 \uC870\uD56D",
  JOINED: "\uAC00\uC785\uD588\uB2E4",
  ROLLED_OVER: "\uB864\uC624\uBC84",
  NEED_MORE_INFO_SINGLE_CLAIM: "\uCCAD\uAD6C\uB97C \uCC98\uB9AC\uD558\uB824\uBA74 \uCD94\uAC00 \uC815\uBCF4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4. \uC790\uC138\uD55C \uB0B4\uC6A9\uC740 \uC544\uB798\uB97C \uCC38\uC870\uD558\uC138\uC694.",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "\uCCAD\uAD6C\uB97C \uCC98\uB9AC\uD558\uB824\uBA74 \uCD94\uAC00 \uC815\uBCF4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4. \uC790\uC138\uD55C \uB0B4\uC6A9\uC740 \uC544\uB798\uC5D0\uC11C \uD655\uC778\uD558\uC138\uC694.",
  RECENT_CLAIMS: "\uCD5C\uADFC \uCCAD\uAD6C",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "\uAE34\uAE09\uD558\uAC8C \uCCAD\uAD6C\uD558\uACE0 \uC2F6\uC740 \uC0AC\uD56D\uC774 \uC788\uB294 \uACBD\uC6B0 <a>\uACE0\uAC1D \uC9C0\uC6D0\uD300</a>\uC5D0 \uBB38\uC758\uD558\uC138\uC694.",
  ABOUT_THIS_BENEFIT: "\uC774 \uD61C\uD0DD \uC815\uBCF4",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "\uC774 \uD61C\uD0DD\uC740 \uADC0\uD558\uC758 \uACE0\uC6A9\uC8FC\uB97C \uD1B5\uD574 \uC81C\uACF5\uB429\uB2C8\uB2E4. \uC9C8\uBB38\uC774\uB098 \uC790\uC138\uD55C \uC815\uBCF4\uAC00 \uD544\uC694\uD558\uBA74 HR \uAD00\uB9AC\uC790\uC5D0\uAC8C \uBB38\uC758\uD558\uC138\uC694.",
  ID_LIKE_TO_DOTDOTDOT: "\uB0B4\uAC00 \uD558\uACE0 \uC2F6\uC740 \uC77C",
  HAS_DEPENDENTS_DESCRIPTION: "\uC774 \uD61C\uD0DD\uC744 \uBC1B\uC744 \uC218 \uC788\uB294 \uBD80\uC591\uAC00\uC871\uC740 \uB2E4\uC74C\uACFC \uAC19\uC2B5\uB2C8\uB2E4. \uCD5C\uADFC\uC5D0 \uC778\uC0DD\uC5D0\uC11C \uC911\uC694\uD55C \uC0AC\uAC74\uC774 \uBC1C\uC0DD\uD55C \uACBD\uC6B0 \uBD80\uC591\uAC00\uC871\uC744 \uBCC0\uACBD\uD560 \uC790\uACA9\uC774 \uC788\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC544\uB798 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC5EC \uBA87 \uB2E8\uACC4\uB9CC\uC73C\uB85C \uC790\uACA9\uC774 \uB418\uB294\uC9C0 \uD655\uC778\uD558\uC138\uC694.",
  HAS_NO_DEPENDENTS_DESCRIPTION: "\uC774 \uD61C\uD0DD\uACFC \uAD00\uB828\uB41C \uBD80\uC591\uAC00\uC871\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uCD5C\uADFC\uC5D0 \uC778\uC0DD\uC5D0\uC11C \uC911\uC694\uD55C \uC0AC\uAC74\uC774 \uBC1C\uC0DD\uD55C \uACBD\uC6B0 \uBD80\uC591\uAC00\uC871\uC744 \uCD94\uAC00\uD558\uAC70\uB098 \uBCC0\uACBD\uD560 \uC790\uACA9\uC774 \uC788\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC544\uB798 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC5EC \uBA87 \uB2E8\uACC4\uB9CC\uC73C\uB85C \uC790\uACA9\uC774 \uB418\uB294\uC9C0 \uD655\uC778\uD558\uC138\uC694.",
  ADD_A_DEPENDENT_CAPITAL: "\uBD80\uC591\uAC00\uC871 \uCD94\uAC00",
  WALLET_FEEDBACK_TITLE: "\uC9C0\uAC11 \uACBD\uD5D8\uC744 \uAC1C\uC120\uD558\uB294\uB370 \uB3C4\uC6C0\uC744 \uC8FC\uC138\uC694",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: "\uADC0\uD558\uC758 \u201C{benefitName}\u201D \uD61C\uD0DD\uC5D0 \uB300\uD574 \uC81C\uACF5\uB41C \uC815\uBCF4\uAC00 \uC5BC\uB9C8\uB098 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
  WALLET_FEEDBACK_SUBTITLE_OTHER: "{cardName}\uC5D0 \uB300\uD574 \uC81C\uACF5\uB41C \uC815\uBCF4\uAC00 \uC5BC\uB9C8\uB098 \uB3C4\uC6C0\uC774 \uB418\uB098\uC694?",
  GO_BACK: "\uB4A4\uB85C \uAC00\uAE30",
  WALLET_LANDING_TITLE: "\uD61C\uD0DD \uBC0F \uBCF4\uC7A5 \uC815\uBCF4\uB97C \uD55C\uACF3\uC5D0\uC11C \uD655\uC778\uD558\uC138\uC694.",
  WALLET_LANDING_BODY: "\uADC0\uD558\uC758 \uB514\uC9C0\uD138 \uC9C0\uAC11\uC5D0\uB294 \uACE0\uC6A9\uC8FC\uC758 \uD61C\uD0DD, \uD504\uB85C\uADF8\uB7A8 \uBC0F \uBCF4\uC7A5 \uBC94\uC704\uC5D0 \uB300\uD55C \uC138\uBD80 \uC815\uBCF4\uAC00 \uBCF4\uAD00\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4. \uD0ED \uD55C \uBC88\uC73C\uB85C \uD544\uC694\uD55C \uC815\uBCF4\uB97C \uBE60\uB974\uAC8C \uCC3E\uC544\uBCF4\uC138\uC694.",
  POLICY_ACTIVE: "\uC815\uCC45 \uD65C\uC131\uD654",
  REMAINING_BALANCE: "\uB0A8\uC740 \uC794\uC561",
  CONTACT_CUSTOMER_CARE: "\uACE0\uAC1D \uC9C0\uC6D0 \uC13C\uD130\uC5D0 \uBB38\uC758",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "\uBB54\uAC00 \uC798\uBABB\uB41C \uAC83 \uAC19\uC2B5\uB2C8\uB2E4",
  WERE_HAVING_TROUBLE: "\uC774 \uC815\uBCF4\uB97C \uB85C\uB529\uD558\uB294 \uB370 \uBB38\uC81C\uAC00 \uC788\uC2B5\uB2C8\uB2E4. \uD398\uC774\uC9C0\uB97C \uC0C8\uB85C \uACE0\uCCD0 \uBCF4\uC138\uC694. \uBB38\uC81C\uAC00 \uC9C0\uC18D\uB418\uBA74 {custCareLink}(\uC73C)\uB85C \uBB38\uC758\uD574 \uC8FC\uC138\uC694.",
  IN_NETWORK: "\uB124\uD2B8\uC6CC\uD06C \uB0B4",
  OUT_OF_NETWORK: "\uB124\uD2B8\uC6CC\uD06C \uC678\uBD80",
  PROGRAMS: "\uD504\uB85C\uADF8\uB7A8",
  PROGRAM_CREATED_BY: "\uD504\uB85C\uADF8\uB7A8 \uC791\uC131\uC790",
  TOP_FOCUS_AREA: "\uC8FC\uC694 \uAD00\uC2EC \uBD84\uC57C",
  DIMENSION_OF_HEALTH: "\uAC74\uAC15\uC758 \uCC28\uC6D0",
  READING_MORE_ABOUT_DIMENSIONS: "\uCE58\uC218\uAC00 \uC804\uBC18\uC801\uC778 \uAC74\uAC15\uC5D0 \uC5B4\uB5A4 \uC601\uD5A5\uC744 \uBBF8\uCE58\uB294\uC9C0 \uC790\uC138\uD788 \uC54C\uC544\uBCF4\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
  READ_OUR_ARTICLE: "\uAD00\uB828 \uAE30\uC0AC\uB97C \uC77D\uC5B4\uBCF4\uC138\uC694",
  HEALTH_PROGRAMS_FOR_YOU: "\uADC0\uD558\uB97C \uC704\uD55C \uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8",
  COMPLETE_ACTIVITIES: "\uAC74\uAC15 \uBAA9\uD45C \uB2EC\uC131\uC744 \uC704\uD55C \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uC138\uC694. \uAC74\uAC15 \uD504\uB85C\uD544\uC744 \uAE30\uC900\uC73C\uB85C \uCD94\uCC9C\uD574 \uB4DC\uB9BD\uB2C8\uB2E4.",
  FT: "\uD53C\uD2B8",
  CM: "cm",
  IN: "in",
  LB: "lb",
  KG: "kg",
  LETS_BEGIN: "\uADF8\uB7FC \uC2DC\uC791\uD558\uACA0\uC2B5\uB2C8\uB2E4!",
  KEEP_GOING: "\uACC4\uC18D\uD574",
  SKIP: "\uC2A4\uD0B5",
  HP_ONBOARDING_SUBMIT: "\uC2DC\uC791\uD558\uAE30",
  FOR_PROVIDERS_AND_PHARMACISTS: "\uACF5\uAE09\uC790/\uC57D\uC0AC\uB97C \uC704\uD55C",
  WEVE_PICKED_UP: "\uC911\uB2E8\uB41C \uBD80\uBD84\uC744 \uB2E4\uC2DC \uC2DC\uC791\uD558\uACA0\uC2B5\uB2C8\uB2E4.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "\uBA87 \uAC00\uC9C0 \uC9C8\uBB38\uB9CC \uB2F5\uD558\uBA74 {points} \uD3EC\uC778\uD2B8\uB97C \uC801\uB9BD\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "\uBA87 \uBB38\uC81C\uB9CC \uD480\uBA74 \uD3C9\uAC00\uB97C \uC644\uB8CC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  PRESS_ENTER: "Enter \uB204\uB974\uAE30",
  SELECT_ALL_THAT_APPLY: "\uD574\uB2F9\uB418\uB294 \uD56D\uBAA9 \uBAA8\uB450 \uC120\uD0DD",
  SELECT_UP_TO: "\uCD5C\uB300 {number}\uAC1C \uC120\uD0DD",
  AMOUNT_QUESTIONS: "{amount} \uC9C8\uBB38",
  QUESTION_PROGRESS: "{questionNumber}/{totalQuestions}",
  COMPLETE: "\uC644\uB8CC",
  VIEW_MORE: "\uB354 \uBCF4\uAE30",
  DOWNLOAD_APP_STORE: "App Store\uC5D0\uC11C \uB2E4\uC6B4\uB85C\uB4DC",
  DOWNLOAD_PLAY_STORE: "Google Play\uC5D0\uC11C \uBC1B\uC73C\uC138\uC694",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uC694\uCCAD\uD558\uC2E0 \uD398\uC774\uC9C0\uB294 \uB9CC\uB8CC\uB418\uC5C8\uAC70\uB098 \uB354 \uC774\uC0C1 \uC720\uD6A8\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  EARNED_X_TIMES: "{amount}\uBC30 \uD68D\uB4DD",
  EARNED: "\uD68D\uB4DD",
  YOUVE_EARNED_TOTAL: "\uC801\uB9BD \uAE08\uC561 \uC18C\uACC4",
  YOUVE_EARNED_GRAND_TOTAL: "\uC801\uB9BD \uAE08\uC561 \uCD1D\uACC4",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "\uC5C5\uC801 \uBC30\uC9C0\uB97C \uD68D\uB4DD\uD588\uC2B5\uB2C8\uB2E4",
  CONGRATS_COMPLETED: "\uCD95\uD558\uD569\uB2C8\uB2E4! \uB2E4\uC74C\uC744 \uC644\uB8CC\uD588\uC2B5\uB2C8\uB2E4:",
  VIEW_MY_HEALTH_DASHBOARD: "\uB0B4 \uAC74\uAC15 \uB300\uC2DC\uBCF4\uB4DC \uBCF4\uAE30",
  LATEST_INSIGHTS_METRICS_AND_MORE: "\uCD5C\uC2E0 \uD1B5\uCC30\uB825, \uC9C0\uD45C \uB4F1",
  FEEDBACK_TITLE: "\uC774 \uC815\uBCF4\uAC00 \uC5BC\uB9C8\uB098 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
  FEEDBACK_MIN_SCALE: "\uB3C4\uC6C0\uC774 \uB418\uC9C0 \uC54A\uC74C",
  FEEDBACK_MAX_SCALE: "\uB9E4\uC6B0 \uB3C4\uC6C0\uC774 \uB429\uB2C8\uB2E4",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "\uCD5C\uADFC\uC5D0 \uADC0\uD558\uC758 \uC0B6\uC774\uB098 \uAC00\uC871\uC5D0 \uBCC0\uD654\uAC00 \uC788\uC5C8\uB098\uC694?",
  DID_YOU_RECENTLY: "\uCD5C\uADFC\uC5D0 \uACB0\uD63C\uD588\uAC70\uB098 \uC544\uAE30\uB97C \uB0B3\uC558\uB098\uC694? \uD2B9\uC815 \uC0DD\uD65C \uC0AC\uAC74\uC73C\uB85C \uC778\uD574 \uC7AC\uB4F1\uB85D \uC790\uACA9\uC774 \uC0DD\uAE38 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  LEARN_MORE: "\uB354 \uC54C\uC544\uBCF4\uAE30",
  VIEW_ACTIVITY: "\uD65C\uB3D9 \uBCF4\uAE30",
  CONTINUE: "\uACC4\uC18D\uD558\uAE30",
  ARIA_CIRCLED_CHECKMARK: "\uC6D0 \uC548\uC5D0 \uC788\uB294 \uD770\uC0C9 \uCCB4\uD06C \uD45C\uC2DC",
  NOT_NOW: "\uC9C0\uAE08\uC740 \uC548 \uB3FC",
  VAL_POINTS: "{points} \uD3EC\uC778\uD2B8",
  ACTIONS: "\uD65C\uB3D9",
  VIEW_ALL_CLAIMS: "\uBAA8\uB4E0 \uCCAD\uAD6C \uBCF4\uAE30",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "\uACC4\uC18D\uD558\uB824\uBA74 \uC774 \uC0C1\uC790\uB97C \uC120\uD0DD\uD574\uC57C \uD569\uB2C8\uB2E4",
  PLEASE_NOTE: "\uCC38\uACE0\uC0AC\uD56D",
  COPY: "\uBCF5\uC0AC",
  STATUS: "\uC0C1\uD0DC",
  STARTED: "\uC2DC\uC791",
  APPROVED: "\uC2B9\uC778\uB428",
  CANCELLED: "\uCDE8\uC18C\uB428",
  UPLOADED_DOCUMENT: "{date}\uC5D0 \uC5C5\uB85C\uB4DC\u2022 {user}\uC774(\uAC00) \uC5C5\uB85C\uB4DC",
  MORE_INFORMATION_NEEDED: "\uC815\uBCF4 \uD544\uC694",
  PAID: "\uC720\uB8CC",
  PENDING_EXTERNAL_REVIEW: "\uC678\uBD80 \uAC80\uD1A0\uB97C \uC704\uD574 \uC804\uC1A1\uB428",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "\uC0AC\uC804 \uC2B9\uC778 \uCD94\uAC00 \uC815\uBCF4",
  PREAPPROVAL_REJECTED: "\uC0AC\uC804 \uC2B9\uC778 \uAC70\uBD80\uB428",
  PREAPPROVED: "\uC0AC\uC804 \uC2B9\uC778",
  REJECTED: "\uAC70\uBD80\uB428",
  SUBMITTED_FOR_PREAPPROVAL: "\uC0AC\uC804 \uC2B9\uC778\uC744 \uC704\uD574 \uC81C\uCD9C\uB428",
  SUBMITTED: "\uC81C\uCD9C\uB428",
  WALLET: "\uC9C0\uAC11",
  AMOUNT: "\uAE08\uC561",
  ADD: "\uCD94\uAC00",
  EDIT: "\uD3B8\uC9D1",
  DETAILS: "\uC138\uBD80 \uC815\uBCF4",
  AND: "\uADF8\uB9AC\uACE0",
  TODAY: "\uC624\uB298",
  CLAIMS: "\uCCAD\uAD6C",
  GROUP_ID: "\uADF8\uB8F9 ID",
  ZIP_CODE: "\uC6B0\uD3B8\uBC88\uD638",
  CITY: "\uB3C4\uC2DC",
  STATE: "\uC0C1\uD0DC",
  REMOVE: "\uC81C\uAC70",
  HEALTH_SPENDING_ACCOUNT: "\uAC74\uAC15 \uC9C0\uCD9C \uACC4\uC815",
  HSA: "HSA",
  LIFESTYLE_SPENDING_ACCOUNT: "\uB77C\uC774\uD504\uC2A4\uD0C0\uC77C \uC9C0\uCD9C \uACC4\uC815",
  LSA: "LSA",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "\uC758\uB8CC\uBE44 \uD658\uAE09 \uC57D\uC815",
  DRUGS: "\uC57D\uBB3C",
  TRAVEL: "\uC5EC\uC815",
  HEALTH_INSURANCE: "\uAC74\uAC15 \uBCF4\uD5D8",
  SETTINGS: "\uC124\uC815",
  ACTIVE: "\uD65C\uC131",
  ENDED: "\uC885\uB8CC",
  SUSPENDED: "\uC77C\uC2DC \uC911\uB2E8\uB428",
  PENDING: "\uBCF4\uB958 \uC911",
  LOADING: "\uB85C\uB4DC \uC911",
  ERROR_STATE_TITLE: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  HELP_EMAIL: "help@league.com",
  SUBMIT: "\uC81C\uCD9C\uD558\uAE30",
  EARN_NUMBER_POINTS_FULL: "{points} \uD3EC\uC778\uD2B8 \uC801\uB9BD",
  CLOSE: "\uB2EB\uAE30",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "\uC774 \uCC4C\uB9B0\uC9C0\uB294 \uC5F0\uC911 \uB0B4\uB0B4 \uC9C4\uD589\uB429\uB2C8\uB2E4",
  END_DAY_AT_TIME: "\uC885\uB8CC: {day}",
  ENDED_DAY_AT_TIME: "\uC885\uB8CC: {day}",
  START_DAY_AT_TIME: "\uC2DC\uC791 {day}",
  SPONSOR_LOGO: "{name} \uB85C\uACE0",
  START_AND_END_DATES: "\uC2DC\uC791 {startDate} \u2022 \uC885\uB8CC {endDate}",
  SIGN_UP_BY_DATE: "{date}\uB85C \uAC00\uC785",
  SIGN_UP_BEGINS_DATE: "\uAC00\uC785 \uC2DC\uC791: {date}",
  SIGN_UP_DATE_PASSED: "\uAC00\uC785 \uB0A0\uC9DC\uAC00 \uC9C0\uB0AC\uC2B5\uB2C8\uB2E4",
  SIGN_UP_PERIOD_PASSED: "\uAC00\uC785 \uAE30\uAC04\uC774 \uC9C0\uB0AC\uC2B5\uB2C8\uB2E4",
  CHALLENGE_DETAILS: "\uB3C4\uC804 \uC138\uBD80 \uC815\uBCF4",
  CHALLENGE_PROGRESS: "\uCC4C\uB9B0\uC9C0 \uC9C4\uD589 \uC0C1\uD669",
  X_PERCENT_COMPLETE: "{number}% \uC644\uB8CC",
  SIGN_UP_FOR_CHALLENGE: "\uAC00\uC785\uD558\uAE30",
  GROUP_CHALLENGE: "\uADF8\uB8F9 \uCC4C\uB9B0\uC9C0",
  SOLO_CHALLENGE: "\uC194\uB85C \uCC4C\uB9B0\uC9C0",
  TRACK_WEARABLES_ALT_TEXT: "\uC2A4\uB9C8\uD2B8\uC6CC\uCE58 \uB370\uC774\uD130\uB97C \uBCF4\uB294 \uC0AC\uB78C",
  CONNECT_A_DEVICE: "\uAE30\uAE30 \uC5F0\uACB0",
  DEVICES_CONNECTED_TITLE: "\uC774\uBBF8 \uC5F0\uACB0\uB418\uC5B4 \uC788\uB294 \uAC83 \uAC19\uC2B5\uB2C8\uB2E4!",
  DEVICES_CONNECTED_DESCRIPTION: "\uC774\uBBF8 \uB370\uC774\uD130\uB97C \uB3D9\uAE30\uD654\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4. \uC790\uB3D9\uC73C\uB85C \uCD94\uC801\uD558\uB3C4\uB85D \uC120\uD0DD\uD558\uBA74 \uAE30\uAE30\uC758 {dataPoint}\uC744(\uB97C) \uC0AC\uC6A9\uD558\uC5EC \uD65C\uB3D9\uC744 \uC790\uB3D9\uC73C\uB85C \uC644\uB8CC\uB41C \uAC83\uC73C\uB85C \uD45C\uC2DC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  DEVICES_UNCONNECTED_TITLE: "\uAE30\uAE30\uB97C \uC5F0\uACB0\uD574 \uBCF4\uC138\uC694!",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "\uC790\uB3D9 \uCD94\uC801",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "\uC218\uB3D9 \uCD94\uC801",
  DEVICES_UNCONNECTED_DESCRIPTION: "\uC990\uACA8 \uC0AC\uC6A9\uD558\uB294 \uAC74\uAC15 \uC571\uACFC \uAE30\uAE30\uB97C \uC5F0\uACB0\uD558\uC5EC \uD65C\uB3D9\uC744 \uC790\uB3D9\uC73C\uB85C \uC644\uB8CC\uD558\uC138\uC694.",
  TRACK_CHALLENGE_AUTOMATICALLY: "\uC790\uB3D9 \uCD94\uC801",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "\uC790\uB3D9 \uCD94\uC801\uC744 \uC120\uD0DD\uD55C \uACBD\uC6B0, \uAC00\uC785 \uD6C4 {instructionsLink}\uC744(\uB97C) \uD1B5\uD574 League \uBAA8\uBC14\uC77C \uC571\uC744 \uC0AC\uC6A9\uD558\uC5EC \uAE30\uAE30\uB97C \uC5F0\uACB0\uD558\uC138\uC694.",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "\uB2E4\uC74C \uB2E8\uACC4\uB97C \uB530\uB974\uC138\uC694",
  TRACK_CHALLENGE_MANUALLY: "\uB300\uC2E0 \uC218\uB3D9\uC73C\uB85C \uCD94\uC801",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "\uC218\uB3D9\uC73C\uB85C \uCD94\uC801\uD558\uB3C4\uB85D \uC120\uD0DD\uD55C \uACBD\uC6B0\uC5D0\uB3C4 \uB098\uC911\uC5D0 \uAE30\uAE30\uB97C \uC5F0\uACB0\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. {learnMoreLink} \uC9C4\uD589 \uC0C1\uD669\uC744 \uC218\uB3D9\uC73C\uB85C \uCD94\uC801\uD558\uB294 \uBC29\uBC95\uC5D0 \uB300\uD574 \uC54C\uC544\uBCF4\uC138\uC694.",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "\uB354 \uC54C\uC544\uBCF4\uAE30",
  TRACK_HEALTH_DATA_ALT: "\uAC74\uAC15 \uB370\uC774\uD130\uB97C \uCD94\uC801\uD558\uB294 \uADF8\uB798\uD504\uAC00 \uC788\uB294 \uB300\uC2DC\uBCF4\uB4DC",
  TRACK_MANUALLY_CONFIRM_TITLE: "\uC218\uB3D9\uC73C\uB85C \uCD94\uC801\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "\uB098\uC911\uC5D0 \uAE30\uAE30\uB97C \uACC4\uC18D \uC5F0\uACB0\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "\uC815\uB9D0\uB85C \uB098\uAC00\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "\uC885\uB8CC\uB97C \uC120\uD0DD\uD558\uBA74 \uBCC0\uACBD \uC0AC\uD56D\uC774 \uC800\uC7A5\uB418\uC9C0 \uC54A\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  EXIT: "\uCD9C\uAD6C",
  CHALLENGE: "\uCC4C\uB9B0\uC9C0",
  CHALLENGES: "\uCC4C\uB9B0\uC9C0",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "\uB9E4\uC77C \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uACE0 \uB3D9\uB8CC\uB4E4\uACFC \uACBD\uC7C1\uD558\uB294 \uCC4C\uB9B0\uC9C0\uC5D0 \uB4F1\uB85D\uD558\uC138\uC694!",
  THIS_CHALLENGE_HAS_ENDED: "\uC774 \uCC4C\uB9B0\uC9C0\uB294 \uC885\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
  COMPLETED_ON_DATE: "{date}\uC5D0 \uC644\uB8CC\uB428",
  ENDED_ON_DATE: "{date}\uC77C\uC5D0 \uC885\uB8CC",
  LEAVE_CHALLENGE: "\uCC4C\uB9B0\uC9C0 \uB098\uAC00\uAE30",
  LEAVE_CHALLENGE_QUESTION: "\uCC4C\uB9B0\uC9C0\uB97C \uB098\uAC00\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  LEAVE_CHALLENGE_DETAIL: "\uC5EC\uC815\uC5D0\uC11C \uC774 \uCC4C\uB9B0\uC9C0\uB97C \uC81C\uAC70\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uCC4C\uB9B0\uC9C0\uC758 \uBAA8\uB4E0 \uC9C4\uD589 \uC0C1\uD669\uC740 \uCD08\uAE30\uD654\uB429\uB2C8\uB2E4.",
  LEAVE: "\uB098\uAC00\uAE30",
  SIGN_UP_FOR_THE_CHALLENGE: "\uCC4C\uB9B0\uC9C0 \uC2E0\uCCAD\uD558\uAE30",
  CHALLENGE_SENSITIVE_DATA: "\uADC0\uD558\uC758 \uAC74\uAC15 \uB370\uC774\uD130\uC640 \uAC1C\uC778\uC815\uBCF4\uB294 \uB9E4\uC6B0 \uBBFC\uAC10\uD558\uBA70, \uC774\uB97C \uBE44\uACF5\uAC1C\uB85C \uC548\uC804\uD558\uAC8C \uBCF4\uD638\uD558\uB294 \uAC83\uC740 \uB2F9\uC0AC\uC758 \uCC45\uC784\uC785\uB2C8\uB2E4. \uC800\uD76C\uB294 \uC774 \uCC45\uC784\uC744 \uB9E4\uC6B0 \uC911\uC694\uD558\uAC8C \uC0DD\uAC01\uD569\uB2C8\uB2E4.",
  CHALLENGE_USING_DATA_TO: "\uCC4C\uB9B0\uC9C0\uC5D0 \uB4F1\uB85D\uD558\uBA74 \uADC0\uD558\uC758 \uB370\uC774\uD130\uB97C \uB2E4\uC74C\uACFC \uAC19\uC740 \uBAA9\uC801\uC73C\uB85C \uC0AC\uC6A9\uD569\uB2C8\uB2E4.",
  COMPLETE_ACTIVITIES_SHORT: "\uC644\uB8CC\uB41C \uD65C\uB3D9",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "\uACE0\uC6A9\uC8FC\uC640 \uCC4C\uB9B0\uC9C0 \uC9C4\uD589 \uC0C1\uD669\uC744 \uACF5\uC720\uD558\uC138\uC694",
  SHARE_CHALLENGE_PROGRESS: "\uADC0\uD558\uC758 \uB3C4\uC804 \uACFC\uC81C \uC9C4\uD589 \uC0C1\uD669\uC744 \uB3D9\uB8CC(\uBC0F \uACE0\uC6A9\uC8FC)\uC640 \uACF5\uC720\uD558\uC138\uC694",
  RANK_SCORE: "\uC21C\uC704\uD45C\uC5D0\uC11C \uC810\uC218 \uC21C\uC704 \uB9E4\uAE30\uAE30",
  DETERMINE_CHALLENGE_WINNERS: "\uCC4C\uB9B0\uC9C0 \uC6B0\uC2B9\uC790 \uACB0\uC815",
  ACCEPT_BEFORE_CONTINUE: "\uACC4\uC18D\uD558\uAE30 \uC804\uC5D0 \uAC1C\uC778\uC815\uBCF4 \uCDE8\uAE09\uBC29\uCE68\uC5D0 \uB3D9\uC758\uD574\uC57C \uD569\uB2C8\uB2E4.",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "\uC774 \uD655\uC778\uB780\uC744 \uC120\uD0DD\uD558\uBA74 <a>\uAC1C\uC778\uC815\uBCF4 \uCDE8\uAE09\uBC29\uCE68</a>\uC5D0 \uB3D9\uC758\uD558\uAC8C \uB429\uB2C8\uB2E4.",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "League\uAC00 \uADC0\uD558\uC758 \uB370\uC774\uD130\uB97C \uAD00\uB9AC\uD558\uACE0 \uC0AC\uC6A9\uD558\uB294 \uBC29\uBC95\uC5D0 \uB300\uD55C \uC790\uC138\uD55C \uB0B4\uC6A9\uC740 <a>\uAC1C\uC778\uC815\uBCF4 \uCDE8\uAE09\uBC29\uCE68</a>\uC5D0\uC11C \uD655\uC778\uD558\uC138\uC694.",
  PRIZE: "\uC0C1\uD488",
  RULES: "\uADDC\uCE59",
  LEADERBOARD: "\uB9AC\uB354\uBCF4\uB4DC",
  TOOLTIP_ACTION_DESCRIPTION: "\uB9AC\uB354\uBCF4\uB4DC \uC21C\uC704 \uAE30\uC900 \uBCF4\uAE30",
  MARK_AS_DONE: "\uC644\uB8CC\uB85C \uD45C\uC2DC",
  CLOSE_ACTIVITY: "\uD65C\uB3D9 \uB2EB\uAE30",
  GOAL_INCOMPLETE: "\uACC4\uC18D\uD558\uAE30 \uC804\uC5D0 \uBAA9\uD45C\uB97C \uC644\uB8CC\uD574\uC57C \uD569\uB2C8\uB2E4.",
  PROGRESS_RECORD_ERROR: "\uB9C8\uC9C0\uB9C9 \uC9C4\uD589 \uC0C1\uD669\uC744 \uAE30\uB85D\uD558\uB294 \uB3D9\uC548 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
  YOUVE_COMPLETED: "\uC644\uB8CC\uD588\uC2B5\uB2C8\uB2E4",
  ACTIVITY_COMPLETE: "\uD65C\uB3D9 \uC644\uB8CC",
  PAGE_NOT_FOUND_HEADER: "\uD398\uC774\uC9C0\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC74C",
  PAGE_NOT_FOUND_CONTENT: "\uAE38\uC744 \uC783\uC73C\uC2E0 \uAC83 \uAC19\uB124\uC694!",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "\uC800\uD76C\uAC00 \uB3C4\uC640\uB4DC\uB9AC\uACA0\uC2B5\uB2C8\uB2E4.",
  PAGE_NOT_FOUND_BUTTON: "\uC9D1\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
  REWARD_POINTS: "\uB9AC\uC6CC\uB4DC \uD3EC\uC778\uD2B8",
  REWARD_POINTS_EARNED: "\uC801\uB9BD\uB41C \uB9AC\uC6CC\uB4DC \uD3EC\uC778\uD2B8",
  REWARD_POINTS_MISSED: "\uB193\uCE5C \uB9AC\uC6CC\uB4DC \uD3EC\uC778\uD2B8",
  X_REWARD_POINTS: "{points} \uB9AC\uC6CC\uB4DC \uD3EC\uC778\uD2B8",
  X_LEAGUE_POINTS: "{points} \uB9AC\uADF8 \uD3EC\uC778\uD2B8",
  REWARDS_PROGRAM_ELIGIBLE: "\uB9AC\uC6CC\uB4DC \uD504\uB85C\uADF8\uB7A8 \uB300\uC0C1",
  EXPIRY_DATE: "\uB9CC\uB8CC \uB0A0\uC9DC",
  NO_EXPIRY_DATE: "\uB9CC\uB8CC\uC77C \uC5C6\uC74C",
  POINTS_BALANCE: "\uD3EC\uC778\uD2B8 \uC794\uC561",
  REDEEM_NEXT_TIER_POINTS: "{value}\uAE4C\uC9C0 {points} \uB0A8\uC558\uC2B5\uB2C8\uB2E4.",
  VIEW_YOUR_REWARDS: "\uBCF4\uC0C1 \uBCF4\uAE30",
  REDEEMABLE: "{value}(\uC73C)\uB85C \uAD50\uD658 \uAC00\uB2A5\uD569\uB2C8\uB2E4.",
  ACTIVATION_PENDING: "\uD65C\uC131\uD654 \uBCF4\uB958 \uC911",
  EXPIRED: "\uB9CC\uB8CC\uB428",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "\uACC4\uC815\uC774 \uD65C\uC131\uD654\uB418\uC5B4 \uC788\uC73C\uBA70 \uC124\uC815 \uC911\uC785\uB2C8\uB2E4.",
  PLEASE_TRY_AGAIN_SHORTLY: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694",
  MANAGE_DEPENDENTS: "\uBD80\uC591\uAC00\uC871 \uAD00\uB9AC",
  VIEW_CARD: "\uCE74\uB4DC \uBCF4\uAE30",
  MEMBER_ID: "\uD68C\uC6D0 ID",
  SPOUSE: "\uBC30\uC6B0\uC790",
  COMMON_LAW: "\uC77C\uBC18\uBC95",
  CHILD: "\uC5B4\uB9B0\uC774",
  PARENT: "\uBD80\uBAA8",
  GRANDPARENT: "\uC870\uBD80\uBAA8",
  GRANDCHILD: "\uC190\uC8FC",
  UNCLE_AUNT: "\uC0BC\uCD0C / \uC774\uBAA8",
  NEPHEW_NIECE: "\uC870\uCE74/\uC870\uCE74",
  INELIGIBLE_CHILD: "\uBD80\uC801\uACA9 \uC544\uB3D9",
  SIBLING: "\uD615\uC81C\uC790\uB9E4",
  OTHER: "\uAE30\uD0C0",
  DEPENDENT_CARD_HOLDER: "{dependentName} \uCE74\uB4DC",
  DATE: "\uB0A0\uC9DC",
  DESCRIPTION: "\uC124\uBA85",
  TYPE: "\uC720\uD615",
  ID: "ID",
  CLAIM: "\uCCAD\uAD6C",
  TOP_UP: "\uCDA9\uC804",
  ADJUSTMENT: "\uC870\uC808",
  REFUND: "\uD658\uBD88",
  LEAGUE_CREDIT: "\uB9AC\uADF8 \uD06C\uB808\uB527",
  CREDIT_CARD: "\uC2E0\uC6A9 \uCE74\uB4DC",
  CGA: "CGA",
  NO_TRANSACTIONS_YET: "\uC544\uC9C1 \uAC70\uB798\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  NO_MORE_TRANSACTIONS: "\uB354 \uC774\uC0C1 \uAC70\uB798\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  PROMO_CODE: "\uD504\uB85C\uBAA8\uC158 \uCF54\uB4DC",
  PROMO_CODE_NOT_FOUND: "\uC720\uD6A8\uD558\uC9C0 \uC54A\uC740 \uD504\uB85C\uBAA8\uC158 \uCF54\uB4DC",
  PROMO_CODE_SUCCESS: "\uD504\uB85C\uBAA8\uC158 \uCF54\uB4DC\uAC00 \uC0AC\uC6A9\uB418\uC5C8\uC2B5\uB2C8\uB2E4!",
  TOTAL_CREDIT: "\uCD1D \uD06C\uB808\uB527:",
  CREDIT_INFORMATION: "\uC2E0\uC6A9 \uC815\uBCF4",
  ENTER_CODE: "\uCF54\uB4DC \uC785\uB825",
  DOCUMENT_NAME: "\uBB38\uC11C \uC774\uB984",
  REQUIRED: "\uD544\uC218",
  CANCEL: "\uCDE8\uC18C",
  CONFIRM: "\uD655\uC778",
  ALLOCATION: "\uBC30\uBD84",
  COVERED: "\uC801\uC6A9\uB428",
  NOT_COVERED: "\uD3EC\uD568\uB418\uC9C0 \uC54A\uC74C",
  COVERED_WITH_RESTRICTIONS: "\uC81C\uD55C \uC0AC\uD56D \uC801\uC6A9",
  EXPAND_ALL: "\uBAA8\uB450 \uD655\uC7A5",
  COLLAPSE_ALL: "\uBAA8\uB450 \uCD95\uC18C",
  EXAMPLES: "\uC608\uC2DC: ",
  RESTRICTIONS: "\uC81C\uD55C \uC0AC\uD56D",
  SEARCH_COVERAGE: "\uBCF4\uC7A5 \uBC94\uC704 \uAC80\uC0C9",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "\uAC80\uC0C9 \uACB0\uACFC\uB294 \uC785\uB825\uD558\uB294 \uB300\uB85C \uC790\uB3D9\uC73C\uB85C \uC5C5\uB370\uC774\uD2B8\uB429\uB2C8\uB2E4",
  INDIVIDUAL: "\uAC1C\uC778",
  INDIVIDUAL_CAP: "\uAC1C\uC778",
  DEPENDENTS: "\uBD80\uC591\uAC00\uC871",
  SELECT_ONE: "\uD558\uB098\uB97C \uC120\uD0DD\uD558\uC138\uC694",
  MORE_OPTIONS: "\uB354 \uB9CE\uC740 \uC635\uC158",
  VENDOR: "\uACF5\uAE09\uC5C5\uCCB4",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "\uC790\uB3D9\uC73C\uB85C {benefitName}\uC5D0 \uCD94\uAC00\uB429\uB2C8\uB2E4.",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "<strong>{percent}%</strong>\uAC00 <strong>{benefitName}</strong>\uC5D0 \uCD94\uAC00\uB429\uB2C8\uB2E4",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "<strong>{percent}%</strong>\uAC00 <strong>{benefitName}</strong>\uC5D0 \uCD94\uAC00\uB429\uB2C8\uB2E4.",
  DOCUMENT_TITLE: "{title} - \uB9AC\uADF8",
  MEDICAL: "\uC758\uB8CC",
  TOTAL: "\uCD1D",
  PROFILE_UPDATE_FAILED: "\uD504\uB85C\uD544 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328",
  PROFILE_PHOTO: "\uD504\uB85C\uD544 \uC0AC\uC9C4",
  PROFILE_PHOTO_DESCRIPTION: "\uC774 \uC0AC\uC9C4\uC740 \uC0AC\uC6A9\uC790 \uD504\uB85C\uD544\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4",
  SELECT_AVATAR: "\uC544\uBC14\uD0C0 \uC120\uD0DD",
  SELECT_AVATAR_ERROR: "\uACC4\uC18D\uD558\uAE30 \uC804\uC5D0 \uC544\uBC14\uD0C0\uB97C \uC120\uD0DD\uD574\uC57C \uD569\uB2C8\uB2E4.",
  GET_AVATARS_ERROR: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4! \uC544\uBC14\uD0C0\uB97C \uB85C\uB4DC\uD558\uB294 \uB370 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB3CC\uC544\uAC00\uC11C \uB2E4\uB978 \uC635\uC158\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.",
  ADDRESS_LINE_1: "\uC8FC\uC18C\uB780 1",
  ADDRESS_LINE_2: "\uC8FC\uC18C\uB780 2",
  LANGUAGE_SELECTOR_ENGLISH: "\uC601\uC5B4",
  LANGUAGE_SELECTOR_FRENCH: "Fran\xE7ais",
  LANGUAGE_SELECTOR_SPANISH: "Espa\xF1ol",
  DOMESTIC_PARTNERSHIP: "\uAD6D\uB0B4 \uD30C\uD2B8\uB108\uC2ED",
  YES: "\uC608",
  NO: "\uC544\uB2C8\uC694",
  WHY_DO_YOU_ASK: "\uBB38\uC758 \uC0AC\uC720",
  LAST_DAY_OF_ENROLLMENT: "\uB4F1\uB85D \uAE30\uAC04 \uB9C8\uC9C0\uB9C9 \uB0A0: {lastDay}",
  WALLET_WELCOME: "\uC9C0\uAC11\uC5D0 \uC624\uC2E0 \uAC83\uC744 \uD658\uC601\uD569\uB2C8\uB2E4!",
  WALLET_DESCRIPTION: "\uC9C0\uAC11\uC744 \uD1B5\uD574 \uD61C\uD0DD\uC744 \uC774\uC6A9\uD558\uACE0, \uCCAD\uAD6C\uC11C\uB97C \uC81C\uCD9C\uD558\uACE0, \uBCF4\uC7A5 \uBC94\uC704\uB97C \uD655\uC778\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.  \uC9C0\uAC11\uC774 \uD65C\uC131\uD654\uB418\uBA74 \uB4F1\uB85D \uAE30\uAC04 \uB3D9\uC548 \uBC1C\uC0DD\uD55C \uBE44\uC6A9\uC5D0 \uB300\uD55C \uCCAD\uAD6C\uB97C \uC81C\uCD9C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uADF8\uB3D9\uC548, {healthProgramLink}\uC744(\uB97C) \uC990\uACA8\uBCF4\uC138\uC694!",
  HEALTH_PROGRAM_LINK_COPY: "\uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8 \uC911 \uD558\uB098\uB97C \uC2DC\uB3C4\uD574\uBCF4\uC138\uC694",
  FINALIZE_SELECTIONS_HEADER_COPY: "\uD61C\uD0DD\uC744 \uB354 \uBE68\uB9AC \uC774\uC6A9\uD558\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
  FINALIZE_SELECTIONS_COPY_1: "\uC120\uD0DD \uC0AC\uD56D\uC744 \uD655\uC815\uD558\uC2DC\uBA74, \uADC0\uD558\uC758 \uD50C\uB79C \uC138\uBD80 \uC815\uBCF4\uB97C \uBC14\uB85C \uD1B5\uC2E0\uC0AC\uC5D0 \uC804\uC1A1\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4! \uC989, \uC9C0\uAC11\uC744 \uD655\uC778\uD558\uAE30 \uC704\uD574 \uB4F1\uB85D \uAE30\uAC04\uC774 \uB05D\uB0A0 \uB54C\uAE4C\uC9C0 \uAE30\uB2E4\uB9B4 \uD544\uC694\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
  FINALIZE_SELECTIONS_COPY_2: "\uC774\uB294 \uB610\uD55C \uC81C\uCD9C\uC744 \uD074\uB9AD\uD55C \uD6C4\uC5D0\uB294 \uD50C\uB79C\uC744 \uBCC0\uACBD\uD560 \uC218 \uC5C6\uC74C\uC744 \uC758\uBBF8\uD569\uB2C8\uB2E4.",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "\uB0B4 \uC120\uD0DD \uC0AC\uD56D\uC744 \uBCC0\uACBD\uD560 \uC218 \uC5C6\uB2E4\uB294 \uC810\uC744 \uC774\uD574\uD569\uB2C8\uB2E4",
  FINALIZE_PLAN_SELECTIONS: "\uD50C\uB79C \uC120\uD0DD \uB9C8\uBB34\uB9AC",
  ERROR_FINALIZING_SELECTIONS: "\uC120\uD0DD \uD56D\uBAA9\uC744 \uC81C\uCD9C\uD558\uB294 \uB3D9\uC548 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "\uC120\uD0DD \uD56D\uBAA9\uC774 \uBD88\uC644\uC804\uD55C \uAC83 \uAC19\uC2B5\uB2C8\uB2E4. \uB4F1\uB85D\uC744 \uC644\uB8CC\uD558\uACE0 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  SELECTIONS_FINALIZED_SUCCESS: "\uC120\uD0DD\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4!",
  TOTAL_REWARDS_LABEL: "\uC801\uC6A9 \uBC94\uC704 \uC694\uC57D",
  TOTAL_REWARDS_DESCRIPTION: "\uAC00\uC785\uD560 \uB54C \uC120\uD0DD\uD55C \uD50C\uB79C\uC744 \uAC80\uD1A0\uD558\uC138\uC694.",
  TOTAL_REWARDS_LINK: "\uC801\uC6A9 \uBC94\uC704 \uC694\uC57D \uBCF4\uAE30",
  CHANGE_PLAN_LABEL: "\uD50C\uB79C \uC120\uD0DD \uBCC0\uACBD",
  CHANGE_PLAN_DESCRIPTION: "\uD50C\uB79C \uB610\uB294 \uAC1C\uC778 \uC815\uBCF4\uB97C \uBCC0\uACBD\uD558\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
  CHANGE_PLAN_LINK: "\uD50C\uB79C \uC120\uD0DD \uBCC0\uACBD",
  DISMISS_TOAST: "\uC54C\uB9BC \uD574\uC81C",
  UPLOAD_DOCUMENT: "\uBB38\uC11C \uC5C5\uB85C\uB4DC",
  REQUIRED_FORM: "\uD544\uC218",
  PERCENTAGE_USED: "\uC0AC\uC6A9 \uBE44\uC728.",
  SUCCESS: "\uC131\uACF5!",
  ERROR: "\uC624\uB958",
  OVERVIEW: "\uAC1C\uC694",
  THIS_FIELD_IS_REQUIRED: "\uC774 \uD544\uB4DC\uB294 \uD544\uC218\uC785\uB2C8\uB2E4",
  INVALID_ZIP_CODE_NO_FORMAT: "\uC798\uBABB\uB41C \uC6B0\uD3B8\uBC88\uD638\uC785\uB2C8\uB2E4",
  INVALID_DAY_ERROR_MESSAGE: "\uC120\uD0DD\uD55C \uB0A0\uC9DC\uAC00 \uC720\uD6A8\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "\uCD5C\uB300 {length}\uC790\uC5EC\uC57C \uD569\uB2C8\uB2E4",
  COMPLETED_STATE_TITLE: "\uC644\uB8CC",
  DRAG_OR_UPLOAD_PHOTO: "\uC774 \uBB38\uC11C\uC758 \uC0AC\uC9C4 \uC5C5\uB85C\uB4DC",
  ADD_IMAGE: "\uC774 \uBB38\uC11C\uC5D0 \uD398\uC774\uC9C0\uB97C \uB354 \uCD94\uAC00\uD569\uB2C8\uB2E4(\uD574\uB2F9\uB418\uB294 \uACBD\uC6B0)",
  IDENTIFICATION_TYPE_TWO_FILES: "\uC2E0\uBD84 \uC99D\uBA85\uC11C\uC5D0\uB294 \uCD5C\uB300 2\uAC1C\uC758 \uBCF4\uC870 \uC774\uBBF8\uC9C0\uAC00 \uD3EC\uD568\uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  WELCOME: "\uD658\uC601\uD569\uB2C8\uB2E4",
  GOT_IT: "\uD655\uC778\uD588\uC2B5\uB2C8\uB2E4!",
  SAVE: "\uC800\uC7A5",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "\uC790\uC138\uD55C \uB0B4\uC6A9 \uBCF4\uAE30",
  LAST_UPDATED: "\uB9C8\uC9C0\uB9C9 \uC5C5\uB370\uC774\uD2B8",
  CLAIM_REF_NUMBER: "\uC8FC\uC7A5 #",
  VIEW_ALL: "\uBAA8\uB450 \uBCF4\uAE30",
  HRA: "HRA",
  SEARCH: "\uAC80\uC0C9",
  DAY: "\uC77C",
  MONTH: "\uC6D4",
  YEAR: "\uB144",
  BACK: "\uB4A4\uB85C",
  NEXT: "\uB2E4\uC74C",
  PREVIOUS: "\uC774\uC804",
  HELP_CENTER: "\uB3C4\uC6C0\uB9D0 \uC13C\uD130",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "\uCC44\uD305 \uCD5C\uC18C\uD654",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "\uCD94\uCC9C \uB2F5\uAE00 \uC0AC\uC6A9 \uAC00\uB2A5",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "\uCC44\uD305 \uCC38\uC5EC\uC790\uAC00 \uC785\uB825 \uC911\uC785\uB2C8\uB2E4",
  VIEW_DOCUMENT: "\uBB38\uC11C \uBCF4\uAE30:",
  CATEGORIES: "\uCE74\uD14C\uACE0\uB9AC",
  ACTIVE_PROGRAM: "{length, plural, other {\uD65C\uC131 \uD504\uB85C\uADF8\uB7A8}}",
  ACTIVE_CHALLENGE: "{length, plural, other {\uC9C4\uD589 \uC911\uC778 \uCC4C\uB9B0\uC9C0}}",
  PROGRAM_PROGRESS: "\uD504\uB85C\uADF8\uB7A8 \uC9C4\uD589 \uC0C1\uD669",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "\uC785\uB825\uD558\uC5EC \uC0C8 \uC635\uC158\uC744 \uB9CC\uB4E4 \uC218\uB3C4 \uC788\uC2B5\uB2C8\uB2E4!",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "\uC635\uC158 \uC120\uD0DD",
  COVERAGE: "\uC801\uC6A9 \uBC94\uC704",
  HEALTH_PROFILE: "\uAC74\uAC15 \uD504\uB85C\uD544",
  HEALTH_PROGRAM: "\uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8",
  HEALTH_PROGRAMS: "\uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8",
  STEPS: "\uB2E8\uACC4",
  ACTIVE_DURATION: "\uD65C\uB3D9 \uC2DC\uAC04",
  MINDFUL_DURATION: "\uB9C8\uC74C\uCC59\uAE40(\uBD84)",
  FLOORS_CLIMBED: "\uC624\uB978 \uACC4\uB2E8 \uC218",
  DISTANCE: "\uC774\uB3D9 \uAC70\uB9AC",
  ENERGY_BURNED: "\uC18C\uBAA8\uB41C \uCE7C\uB85C\uB9AC",
  METRIC_DETAILS: "\uBA54\uD2B8\uB9AD \uC138\uBD80 \uC815\uBCF4",
  HEALTH_PROGRAMS_SYNC_DEVICE: "\uCD5C\uC801\uC758 \uD504\uB85C\uADF8\uB7A8 \uACBD\uD5D8\uC744 \uC704\uD574 {device}\uC744(\uB97C) \uB3D9\uAE30\uD654\uD558\uC138\uC694.",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "\uC5F0\uACB0\uB41C \uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8",
  TRACK_AUTOMATICALLY: "\uC790\uB3D9\uC73C\uB85C \uC9C4\uD589 \uC0C1\uD669 \uCD94\uC801",
  SYNC_ISSUE: "\uB3D9\uAE30\uD654 \uBB38\uC81C",
  FIX_SYNC_ISSUE: "\uC774 \uBB38\uC81C \uD574\uACB0",
  DATAPOINT_SYNC_ISSUE: "{dataPoint}\uC744(\uB97C) \uB3D9\uAE30\uD654\uD558\uB294 \uB3D9\uC548 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
  CONNECT_TO_TRACK: "\uC990\uACA8 \uC0AC\uC6A9\uD558\uB294 \uAC74\uAC15 \uC571\uACFC \uAE30\uAE30\uB97C \uC5F0\uACB0\uD558\uC5EC \uD65C\uB3D9\uC744 \uC790\uB3D9\uC73C\uB85C \uC644\uB8CC\uD558\uC138\uC694.",
  CONNECT_APPS_DEVICES: "\uC571 \uBC0F \uAE30\uAE30 \uC5F0\uACB0",
  ADD_APPS_DEVICES: "\uC571 \uBC0F \uAE30\uAE30 \uBCF4\uAE30",
  MANAGE_UNITS: "\uCE21\uC815 \uB2E8\uC704 \uBCC0\uACBD",
  DONT_CONNECT_APPS_DEVICES: "\uB0B4 \uC9C4\uD589 \uC0C1\uD669\uC744 \uC790\uB3D9\uC73C\uB85C \uCD94\uC801\uD558\uC9C0 \uC54A\uAE30",
  CONNECTING_UNAVAILABLE: "\uC6F9\uC5D0\uC11C\uB294 \uC571 \uBC0F \uAE30\uAE30 \uC5F0\uACB0 \uAE30\uB2A5\uC744 \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  CONNECTING_UNAVAILABLE_DETAILS: "\uC9C0\uAE08\uC740 League \uBAA8\uBC14\uC77C \uC571\uC744 \uBC29\uBB38\uD558\uC5EC \uC990\uACA8 \uC4F0\uB294 \uAC74\uAC15 \uC571\uACFC \uAE30\uAE30\uB97C \uC5F0\uACB0\uD558\uC138\uC694.",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "\uC9C0\uAE08\uC740 League \uBAA8\uBC14\uC77C \uC571\uC744 \uBC29\uBB38\uD558\uC5EC \uC990\uACA8 \uC4F0\uB294 \uAC74\uAC15 \uC571\uACFC \uAE30\uAE30\uB97C \uC5F0\uACB0\uD558\uC138\uC694. \uC790\uC138\uD55C \uB0B4\uC6A9\uC744 \uC54C\uACE0 \uC2F6\uC73C\uC2DC\uBA74 \uC804\uCCB4 \uBAA9\uB85D\uC744 \uD655\uC778\uD558\uC138\uC694",
  CONNECT_DEVICES_LEARN_MORE: "\uB354 \uC54C\uC544\uBCF4\uAE30",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "\uD604\uC7AC \uC9C0\uC6D0\uB418\uB294 \uC571 \uBC0F \uAE30\uAE30",
  DONT_HAVE_THE_APP: "League \uBAA8\uBC14\uC77C \uC571\uC774 \uC5C6\uC73C\uC2E0\uAC00\uC694?",
  WHAT_DEVICES_CAN_CONNECT: "\uC5B4\uB5A4 \uC571\uACFC \uAE30\uAE30\uAC00 \uC9C0\uC6D0\uB418\uB098\uC694?",
  CONNECTED_DEVICES_IMAGE_ALT: "\uC5F0\uACB0\uB41C \uAE30\uAE30\uC5D0\uC11C \uB370\uC774\uD130\uB97C \uAC80\uD1A0\uD558\uB294 \uC0AC\uB78C",
  MORE_GREAT_RESOURCES: "\uB354 \uB9CE\uC740 \uD6CC\uB96D\uD55C \uB9AC\uC18C\uC2A4",
  HISTORY: "\uC5ED\uC0AC",
  TOP_MATCH: "\uD0D1 \uB9E4\uCE58",
  CHAT: "\uCC44\uD305",
  END_CHAT: "\uCC44\uD305 \uC885\uB8CC",
  BECOMES_ACTIVE_ON_DATE: "{date}\uC5D0 \uD65C\uC131\uD654\uB429\uB2C8\uB2E4",
  BECOMES_ACTIVE_IN_X_DAYS: "\uC774 \uD61C\uD0DD\uC740 {days}\uC77C \uD6C4\uC5D0 \uD65C\uC131\uD654\uB429\uB2C8\uB2E4",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "\uC774 \uD61C\uD0DD\uC5D0 \uB300\uD574\uC11C\uB294 \uAD6C\uB9E4/\uC11C\uBE44\uC2A4 \uB0A0\uC9DC\uAC00 <strong>{dateRange}</strong> \uC0AC\uC774\uC778 \uACBD\uC6B0\uC5D0\uB9CC \uCCAD\uAD6C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC790\uC138\uD55C \uB0B4\uC6A9\uC740 \uBCF4\uC7A5 \uC138\uBD80 \uC815\uBCF4\uB97C \uD655\uC778\uD558\uC138\uC694.",
  SUBMIT_PAST_CLAIMS_BY_DATE: "{date}\uAE4C\uC9C0 \uACFC\uAC70 \uCCAD\uAD6C \uC81C\uCD9C",
  THIS_POLICY_HAS_EXPIRED: "\uC774 \uC815\uCC45 \uAE30\uAC04\uC740 \uB9CC\uB8CC\uB418\uC5C8\uC9C0\uB9CC \uC774 \uD61C\uD0DD\uC5D0 \uB300\uD55C \uACFC\uAC70 \uCCAD\uAD6C\uB97C \uC81C\uCD9C\uD560 \uC218 \uC788\uB294 \uAE30\uAC04\uC774 {days}\uC77C \uB0A8\uC558\uC2B5\uB2C8\uB2E4",
  THIS_POLICY_EXPIRES_TOMORROW: "\uC774 \uC815\uCC45 \uAE30\uAC04\uC740 \uB9CC\uB8CC\uB418\uC5C8\uC9C0\uB9CC \uC624\uB298\uC740 \uC774 \uD61C\uD0DD\uC5D0 \uB300\uD55C \uACFC\uAC70 \uCCAD\uAD6C\uB97C \uC81C\uCD9C\uD560 \uC218 \uC788\uB294 \uB9C8\uC9C0\uB9C9 \uB0A0\uC785\uB2C8\uB2E4",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "\uAD6C\uB9E4/\uC11C\uBE44\uC2A4 \uB0A0\uC9DC\uAC00 <strong>{endDate}</strong> \uC774\uC804\uC778 \uACFC\uAC70 \uCCAD\uAD6C\uB294 <strong>{inactiveDate}</strong>\uAE4C\uC9C0 \uC81C\uCD9C\uD574\uC57C \uD569\uB2C8\uB2E4",
  EXPIRES_ON_DATE: "{date}\uC5D0 \uB9CC\uB8CC\uB428",
  ASSIGNED: "\uD560\uB2F9\uB41C",
  ASSIGNED_ON: "{date}\uC5D0 \uB9CC\uB8CC\uB428",
  ASSIGNED_TODAY: "\uC624\uB298",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "\uC815\uCC45 \uAE30\uAC04\uC774 \uB9CC\uB8CC\uB418\uAE30 \uC804\uC5D0 \uC774 \uD61C\uD0DD\uC744 \uC0AC\uC6A9\uD560 \uC218 \uC788\uB294 \uAE30\uAC04\uC774 {days}\uC77C \uB0A8\uC558\uC2B5\uB2C8\uB2E4",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "\uB9CC\uB8CC \uD6C4\uC5D0\uB294 <strong>{inactiveDate}</strong>\uAE4C\uC9C0 \uAD6C\uB9E4/\uC11C\uBE44\uC2A4 \uB0A0\uC9DC\uAC00 <strong>{endDate}</strong> \uC774\uC804\uC774\uAC70\uB098 \uADF8\uB0A0\uC778 \uACBD\uC6B0 \uCCAD\uAD6C\uB97C \uC81C\uCD9C\uD574\uC57C \uD569\uB2C8\uB2E4",
  LOAD_MORE: "\uB354 \uBCF4\uAE30",
  SHOW_MORE: "\uB354 \uBCF4\uAE30",
  LINK_OUT_MESSAGE: "\uAF2D \uBD10\uC57C \uD560 \uAC83",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4, \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD55C \uAC83 \uAC19\uC2B5\uB2C8\uB2E4.",
  AGENT_PERMISSION_ERROR: "\uC774 \uB0B4\uC6A9\uC744 \uBCFC \uC218 \uC788\uB294 \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  TRY_AGAIN: "\uB2E4\uC2DC \uC2DC\uB3C4",
  WARNING_OPENS_NEW_TAB: "\uC0C8 \uD0ED\uC5D0\uC11C \uC5F4\uAE30",
  ANY_HEALTH_CHANGES: "\uCD5C\uADFC \uAC74\uAC15\uC5D0 \uBCC0\uD654\uAC00 \uC788\uC5C8\uB098\uC694?",
  LAST_UPDATED_DATE: "\uB9C8\uC9C0\uB9C9 \uC5C5\uB370\uC774\uD2B8 {date}",
  UPDATE_HEALTH_PROFILE: "\uAC74\uAC15 \uD504\uB85C\uD544 \uC5C5\uB370\uC774\uD2B8",
  HEALTH_PROFILE_COMPLETION_POINTS: "{points} \uD3EC\uC778\uD2B8\uB97C \uD68D\uB4DD\uD558\uC168\uC2B5\uB2C8\uB2E4",
  OPENS_IN_A_NEW_TAB: "\uC0C8 \uD0ED\uC5D0\uC11C \uC5F4\uAE30",
  COMPLETED: "\uC644\uB8CC",
  COMPLETED_X_TIMES: "{amount, plural, other {{amount}\uBC88 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4}}",
  REMOVE_FROM_JOURNEY: "\uC5EC\uC815\uC5D0\uC11C \uC81C\uAC70",
  GO_TO_JOURNEY: "\uC5EC\uC815\uC73C\uB85C \uC774\uB3D9",
  ABOUT_THIS_PROGRAM: "\uC774 \uD504\uB85C\uADF8\uB7A8 \uC815\uBCF4",
  HEALTH_JOURNEY: "\uAC74\uAC15 \uC5EC\uC815",
  REMOVE_ACTIVITY: "\uD65C\uB3D9\uC744 \uC81C\uAC70\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  REMOVE_ACTIVITY_PROMPT: "\uD65C\uB3D9 \uC81C\uAC70",
  REMOVE_ACTIVITY_BUTTON: "\uC81C\uAC70",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "\uC5EC\uC815\uC5D0\uC11C \uC774 \uD65C\uB3D9\uC744 \uC81C\uAC70\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "\uB354 \uC774\uC0C1 \uC5EC\uC815\uC5D0\uC11C \uBCFC \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  DISCOVER_ACTIVITIES: "\uC0C8\uB85C\uC6B4 \uD65C\uB3D9\uC744 \uBC1C\uACAC\uD558\uC138\uC694",
  DISCOVER_ACTIVITIES_CAPTION: "\uC5EC\uB7EC\uBD84\uC774 \uC88B\uC544\uD560 \uB9CC\uD55C \uC561\uD2F0\uBE44\uD2F0 \uCC3E\uC544\uBCF4\uAE30",
  ACTIVITIES: "\uD65C\uB3D9",
  ACTIVITIES_PLURALIZATION: "{count, plural, other {\uD65C\uB3D9}}",
  ACTIVITIES_PILL: "\uD65C\uB3D9",
  PROGRAM_LIBRARY: "\uD504\uB85C\uADF8\uB7A8 \uB77C\uC774\uBE0C\uB7EC\uB9AC",
  BROWSE_PROGRAMS: "\uD504\uB85C\uADF8\uB7A8 \uCC3E\uC544\uBCF4\uAE30",
  BACK_TO_JOURNEY: "\uC5EC\uC815\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
  GO_TO_HEALTH_JOURNEY: "\uAC74\uAC15 \uC5EC\uC815\uC73C\uB85C \uC774\uB3D9",
  ADD_TO_JOURNEY: "\uC5EC\uC815\uC5D0 \uCD94\uAC00",
  REQUEST_WAIT: "\uC694\uCCAD\uC744 \uC644\uB8CC\uD558\uB294 \uB3D9\uC548 \uC7A0\uC2DC \uAE30\uB2E4\uB824\uC8FC\uC138\uC694",
  JOURNEY_DELIGHT_1: "\uB3D9\uAE30 \uBD80\uC5EC\uB97C \uD1B5\uD574 \uC2DC\uC791\uD558\uC138\uC694. \uC2B5\uAD00\uC740 \uADC0\uD558\uB97C \uACC4\uC18D \uB098\uC544\uAC00\uAC8C \uD569\uB2C8\uB2E4. \uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4!",
  JOURNEY_DELIGHT_2: "\uD65C\uB3D9\uC131. \uC9D1\uC911. \uBAA8\uBA58\uD140\uC744 \uC720\uC9C0\uD558\uC138\uC694. \uADFC\uC0AC\uD55C \uBAA8\uC2B5\uC744 \uBCF4\uC5EC\uC8FC\uC138\uC694.",
  JOURNEY_DELIGHT_3: "\uC791\uC740 \uC2E4\uCC9C\uC774 \uD070 \uCC28\uC774\uB97C \uB9CC\uB4E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC5EC\uB7EC\uBD84\uC740 \uB098\uC544\uAC00\uACE0 \uC788\uC2B5\uB2C8\uB2E4!",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "{hasValue, select, true {\uC774\uAC83\uC740 \uC790\uC720 \uD14D\uC2A4\uD2B8 \uC635\uC158\uC785\uB2C8\uB2E4. \uD14D\uC2A4\uD2B8 \uC0C1\uC790\uB97C \uC774\uC6A9\uD574 \uC6D0\uD558\uB294 \uAC12\uC744 \uC785\uB825\uD558\uC138\uC694. \uD604\uC7AC \uAC12\uC740 {value}\uC785\uB2C8\uB2E4} other {\uC774\uAC83\uC740 \uC790\uC720 \uD14D\uC2A4\uD2B8 \uC635\uC158\uC785\uB2C8\uB2E4. \uD14D\uC2A4\uD2B8 \uC0C1\uC790\uB97C \uC774\uC6A9\uD574 \uC6D0\uD558\uB294 \uAC12\uC744 \uC785\uB825\uD558\uC138\uC694. \uD604\uC7AC \uAC12: \uBE44\uC5B4 \uC788\uC2B5\uB2C8\uB2E4}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "\uC774 \uC635\uC158\uC740 \uBC30\uD0C0\uC801\uC785\uB2C8\uB2E4. \uC774 \uC635\uC158\uC744 \uC120\uD0DD\uD558\uBA74 \uB2E4\uB978 \uBAA8\uB4E0 \uC635\uC158\uC774 \uC120\uD0DD \uD574\uC81C\uB429\uB2C8\uB2E4.",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "\uD604\uC7AC \uC120\uD0DD\uD55C \uC560\uB2C8\uBA54\uC774\uC158 \uC7AC\uC0DD \uB610\uB294 \uC77C\uC2DC \uC911\uC9C0",
  CENTIMETERS: "\uC13C\uD2F0\uBBF8\uD130",
  FEET: "\uD53C\uD2B8",
  INCHES: "\uC778\uCE58",
  KILOGRAMS: "\uD0AC\uB85C\uADF8\uB7A8",
  POUNDS: "\uD30C\uC6B4\uB4DC",
  EARN_UP_TO_X_POINTS: "\uCD5C\uB300 {points} \uD3EC\uC778\uD2B8 \uC801\uB9BD",
  EXPIRES_TODAY: "\uC624\uB298 \uB9CC\uB8CC",
  PROGRAM_DETAILS: "\uD504\uB85C\uADF8\uB7A8 \uC138\uBD80 \uC815\uBCF4",
  PROGRESS: "\uC9C4\uD589 \uC0C1\uD669",
  X_OF_Y_COMPLETE: "{completed}/{total} \uC644\uB8CC",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "{missed} \uB193\uCE68",
  MISSED: "\uB193\uCCE4\uB2E4",
  SCROLL_CAROUSEL_RIGHT: "\uCE90\uB7EC\uC140\uC744 \uC55E\uC73C\uB85C \uC2A4\uD06C\uB864\uD569\uB2C8\uB2E4",
  SCROLL_CAROUSEL_LEFT: "\uCE90\uB7EC\uC140\uC744 \uB4A4\uB85C \uC2A4\uD06C\uB864\uD569\uB2C8\uB2E4",
  SCROLL_CAROUSEL_FORWARD: "\uCE90\uB7EC\uC140\uC744 \uC55E\uC73C\uB85C \uC2A4\uD06C\uB864\uD569\uB2C8\uB2E4",
  SCROLL_CAROUSEL_BACK: "\uCE90\uB7EC\uC140\uC744 \uB4A4\uB85C \uC2A4\uD06C\uB864\uD569\uB2C8\uB2E4",
  YOU_HAVENT_STARTED_PROGRAMS: "\uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8\uC744 \uC2DC\uC791\uD558\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4",
  ADD_PROGRAM: "\uD504\uB85C\uADF8\uB7A8 \uCD94\uAC00",
  TAP_ADD_PROGRAM_BUTTON: "\uD504\uB85C\uADF8\uB7A8\uC744 \uCC3E\uC544\uBCF4\uB824\uBA74 '\uD504\uB85C\uADF8\uB7A8 \uCD94\uAC00' \uBC84\uD2BC\uC744 \uB204\uB974\uC138\uC694.",
  YOUVE_REACHED_PROGRAM_LIMIT: "\uD504\uB85C\uADF8\uB7A8 \uD55C\uB3C4\uC5D0 \uB3C4\uB2EC\uD588\uC2B5\uB2C8\uB2E4!",
  START_X_MORE_PROGRAMS: "{duration, plural, other {{duration,number}\uAC1C \uD504\uB85C\uADF8\uB7A8\uC744 \uB354 \uC2DC\uC791\uD558\uC138\uC694. \uBAA8\uBA58\uD140\uC744 \uC720\uC9C0\uD558\uC138\uC694.}}",
  OK: "\uC88B\uC544\uC694",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "\uD604\uC7AC \uC9C4\uD589 \uC911\uC778 \uD504\uB85C\uADF8\uB7A8 \uC911 \uD558\uB098\uB97C \uC644\uB8CC\uD574 \uC5EC\uC815\uC5D0 \uCD94\uAC00\uD558\uC138\uC694.",
  SHOW_LESS: "\uB35C \uBCF4\uAE30",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "\uD504\uB85C\uADF8\uB7A8 \uD55C\uB3C4\uC5D0 \uB3C4\uB2EC\uD55C \uAC83 \uAC19\uC2B5\uB2C8\uB2E4!",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "\uC0C8\uB85C\uC6B4 \uD504\uB85C\uADF8\uB7A8\uC744 \uCD94\uAC00\uD558\uAE30 \uC804\uC5D0 \uD604\uC7AC \uD65C\uC131\uD654\uB41C \uD504\uB85C\uADF8\uB7A8\uC744 \uC644\uB8CC\uD558\uC138\uC694. <strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "\uD504\uB85C\uADF8\uB7A8\uC744 \uC81C\uAC70\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  REMOVE_PROGRAM_BUTTON: "\uC81C\uAC70",
  ARE_YOU_SURE_REMOVE_PROGRAM: "\uC774 \uD504\uB85C\uADF8\uB7A8\uC744 \uC5EC\uC815\uC5D0\uC11C \uC81C\uAC70\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uD504\uB85C\uADF8\uB7A8\uC744 \uC9C4\uD589\uD558\uB294 \uB3D9\uC548 \uC9C4\uD589 \uC0C1\uD669\uC740 \uBAA8\uB450 \uC7AC\uC124\uC815\uB418\uBBC0\uB85C \uCC98\uC74C\uBD80\uD130 \uB2E4\uC2DC \uC2DC\uC791\uD574\uC57C \uD569\uB2C8\uB2E4.",
  PROGRAM_REMOVED: "\uD504\uB85C\uADF8\uB7A8 \uC81C\uAC70",
  PROGRAM_ADDED: "\uD504\uB85C\uADF8\uB7A8 \uCD94\uAC00",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "\uC774\uC81C \uC5EC\uC815\uC5D0 \uD504\uB85C\uADF8\uB7A8\uC774 \uD45C\uC2DC\uB429\uB2C8\uB2E4",
  PROGRAM_ADD_ERROR: "\uD604\uC7AC\uB85C\uC11C\uB294 \uD504\uB85C\uADF8\uB7A8\uC744 \uCD94\uAC00\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uAC70\uB098 \uB2E4\uB978 \uD504\uB85C\uADF8\uB7A8\uC744 \uC120\uD0DD\uD558\uC138\uC694.",
  PROGRAM_REMOVE_ERROR: "\uD604\uC7AC\uB85C\uC11C\uB294 \uD574\uB2F9 \uD504\uB85C\uADF8\uB7A8\uC744 \uC81C\uAC70\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
  SKIP_TO_CONTENT_MESSAGE: "\uCF58\uD150\uCE20 \uAC74\uB108\uB6F0\uAE30",
  MAIN_NAVIGATION_LIST_LABEL: "League \uD0D0\uC0C9 \uD56D\uBAA9",
  PAGE_TITLE_ACTIVITY_DETAILS: "\uD65C\uB3D9 \uC138\uBD80 \uC815\uBCF4",
  X_OF_Y: "{total}\uC758 {current}",
  ACTIVITY_COUNT_EDIT: "\uD3B8\uC9D1 {unit}",
  ACTIVITY_COUNT_GOAL: "\uBAA9\uD45C: {goal,number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: '\uC774 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uB824\uBA74 "\uC644\uB8CC\uB85C \uD45C\uC2DC"\uB97C \uD074\uB9AD\uD558\uC138\uC694.',
  ACTIVITY_COMPLETE_LATER_PROMPT: '\uACC4\uC18D\uD558\uB824\uBA74 "\uC5EC\uC815\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30"\uB97C \uD074\uB9AD\uD558\uACE0 \uB098\uC911\uC5D0 \uB3CC\uC544\uC640\uC11C \uC644\uB8CC\uB85C \uD45C\uC2DC\uD558\uC138\uC694.',
  ACTIVITY_COMPLETE_ERROR: "\uD604\uC7AC \uD65C\uB3D9\uC744 \uC644\uB8CC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
  ACTIVITY_DISMISS_ERROR: "\uD604\uC7AC \uD574\uB2F9 \uD65C\uB3D9\uC744 \uC0AD\uC81C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "\uC77C, \uC6D4, \uC5F0\uB3C4\uB97C \uC120\uD0DD\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_MONTH_YEAR: "\uC6D4\uACFC \uC5F0\uB3C4\uB97C \uC120\uD0DD\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "\uAE00\uC790 \uC218 \uC81C\uD55C\uC740 {limit}\uC785\uB2C8\uB2E4",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "{value}\uBCF4\uB2E4 \uD06C\uAC70\uB098 \uAC19\uC740 \uC22B\uC790\uB97C \uC785\uB825\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "{value} \uC774\uD558\uC758 \uC22B\uC790\uB97C \uC785\uB825\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "\uD544\uC218 \uC785\uB825 \uD56D\uBAA9\uC785\uB2C8\uB2E4",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "\uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "\uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "{date,date,medium} \uC774\uD6C4\uC758 \uB0A0\uC9DC\uB97C \uC120\uD0DD\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "{date,date,medium} \uC774\uC804 \uB0A0\uC9DC\uB97C \uC120\uD0DD\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "\uC815\uC218\uB97C \uC785\uB825\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "\uCD5C\uB300 {integer}\uAC1C\uC758 \uC635\uC158\uC744 \uC120\uD0DD\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "\uACC4\uC18D\uD558\uB824\uBA74 {integer} \uC635\uC158\uC744 \uC120\uD0DD\uD558\uC138\uC694",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "\uC120\uD0DD \uC2DC \uD544\uB4DC\uB97C \uBE44\uC6B8 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "\uBB34\uAC8C\uB294 \uCD5C\uC18C {imperial}\uD30C\uC6B4\uB4DC({metric}kg)\uC5EC\uC57C \uD569\uB2C8\uB2E4",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "\uBB34\uAC8C\uB294 \uCD5C\uB300 {imperial}\uD30C\uC6B4\uB4DC({metric}kg)\uC5EC\uC57C \uD569\uB2C8\uB2E4",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "\uB192\uC774\uB294 \uCD5C\uC18C {imperial}({metric} cm) \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "\uB192\uC774\uB294 \uCD5C\uB300 {imperial}({metric} cm)\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "\uCCB4\uC911\uC744 \uD0AC\uB85C\uADF8\uB7A8 \uB2E8\uC704\uB85C \uC785\uB825\uD558\uC138\uC694",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "\uCCB4\uC911\uC744 \uD30C\uC6B4\uB4DC \uB2E8\uC704\uB85C \uC785\uB825\uD558\uC138\uC694",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "\uD0A4\uB97C \uC13C\uD2F0\uBBF8\uD130 \uB2E8\uC704\uB85C \uC785\uB825\uD558\uC138\uC694",
  ENTER_YOUR_HEIGHT_IN_FEET: "\uD0A4\uB97C \uD53C\uD2B8 \uB2E8\uC704\uB85C \uC785\uB825\uD558\uC138\uC694",
  ENTER_YOUR_HEIGHT_IN_INCHES: "\uD0A4(\uC778\uCE58)\uB97C \uC785\uB825\uD558\uC138\uC694",
  GOAL_REACHED: "\uBAA9\uD45C\uB97C \uB2EC\uC131\uD588\uC2B5\uB2C8\uB2E4!",
  COMPLETE_ACTIVITY: "\uD65C\uB3D9 \uC644\uB8CC",
  EXIT_ACTIVITY_TITLE: "\uC774 \uD65C\uB3D9\uC744 \uC885\uB8CC\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  EXIT_ACTIVITY_BODY: "\uC9C4\uD589 \uC0C1\uD669\uC740 \uC800\uC7A5\uB418\uC9C0 \uC54A\uC73C\uBA70 \uD65C\uB3D9\uC744 \uCC98\uC74C\uBD80\uD130 \uB2E4\uC2DC \uC2DC\uC791\uD574\uC57C \uD569\uB2C8\uB2E4.",
  EXIT_ACTIVITY_CTA: "\uD65C\uB3D9 \uC885\uB8CC",
  ACTIVITY_NEXT_STEP: "\uD65C\uB3D9 \uACC4\uC18D\uD558\uAE30",
  ACTIVITY_PREV_STEP: "\uC774\uC804 \uB2E8\uACC4\uB85C \uB3CC\uC544\uAC00\uAE30",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "\uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8 \uBC94\uC8FC",
  NEXT_STEP: "\uB2E4\uC74C \uB2E8\uACC4",
  YOU_EARNED: "\uC801\uB9BD\uD55C \uAE08\uC561",
  YOU_EARNED_X_POINTS: "{points} \uD3EC\uC778\uD2B8\uB97C \uD68D\uB4DD\uD588\uC2B5\uB2C8\uB2E4!",
  CREATE: "{label} \uB9CC\uB4E4\uAE30",
  TOMORROW: "\uB0B4\uC77C",
  YESTERDAY: "\uC5B4\uC81C",
  BACK_TO_TODAY: "\uC624\uB298\uB85C \uB3CC\uC544\uAC00\uAE30",
  PREVIOUS_DAY: "\uC804\uB0A0",
  NEXT_DAY: "\uB2E4\uC74C \uB0A0",
  ACTIVITY_STATUS: "\uD65C\uB3D9 \uC0C1\uD0DC: ",
  ACTIVITY_STATUS_EXPIRED: "\uB9CC\uB8CC\uB428",
  ACTIVITY_STATUS_REMOVED: "\uAC74\uB108\uB6F0\uAE30",
  ACTIVITY_STATUS_UPCOMING: "\uACE7 \uC81C\uACF5 \uC608\uC815",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "\uC544\uC9C1 \uACC4\uD68D\uB41C \uAC8C \uC5C6\uC5B4\uC694!",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: "'\uCD94\uAC00' \uBC84\uD2BC\uC744 \uB20C\uB7EC \uC0C8\uB85C\uC6B4 \uD504\uB85C\uADF8\uB7A8\uC744 \uCC3E\uAC70\uB098 \uC544\uB798\uC5D0\uC11C \uD050\uB808\uC774\uD305\uB41C \uD65C\uB3D9 \uBAA9\uB85D\uC744 \uC0B4\uD3B4\uBCF4\uC138\uC694.",
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "\uC798\uD558\uACE0 \uACC4\uC2ED\uB2C8\uB2E4!",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "\uB0B4\uC77C \uB2E4\uC2DC \uC624\uC154\uC11C \uB354 \uB9CE\uC740 \uC815\uBCF4\uB97C \uD655\uC778\uD558\uAC70\uB098 \uC0C8\uB85C\uC6B4 \uD504\uB85C\uADF8\uB7A8\uACFC \uCD94\uCC9C \uD65C\uB3D9\uC744 \uB458\uB7EC\uBCF4\uC138\uC694.",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "\uC774\uB0A0\uC5D0 \uC608\uC57D\uB41C \uD65C\uB3D9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  NO_ACTIVITIES_PAST_HEADING: "\uC26C\uB294 \uB0A0",
  NO_ACTIVITIES_PAST_DESCRIPTION: "\uC624\uB298\uC740 \uD734\uC2DD\uC744 \uCDE8\uD558\uACE0 \uD68C\uBCF5\uD558\uB294 \uB370\uC5D0 \uC2DC\uAC04\uC744 \uC3DF\uC740 \uAC83 \uAC19\uB124\uC694.",
  BUTTON_BROWSE_PROGRAMS: "\uD504\uB85C\uADF8\uB7A8 \uCC3E\uC544\uBCF4\uAE30",
  IMG_ALT_HIGH_FIVE_HANDS: "\uD558\uC774\uD30C\uC774\uBE0C\uD558\uB294 \uB450 \uC190",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "\uBCBD \uD06C\uAE30\uC758 \uD504\uB85C\uADF8\uB7A8 \uBAA9\uB85D\uC744 \uC11C\uC11C \uC2A4\uD06C\uB864\uD558\uB294 \uC5EC\uC131",
  IMG_ALT_EMPTY_STATE_REST: "\uB530\uB73B\uD55C \uBA38\uADF8\uC794\uACFC \uD5E4\uB4DC\uD3F0\uC744 \uB07C\uACE0 \uD3B8\uC548\uD55C \uC758\uC790\uC5D0 \uC549\uC544 \uC26C\uACE0 \uC788\uB294 \uB0A8\uC790",
  THIS_IS_JUST_A_SNEAK_PEAK: "\uC774\uAC83\uC740 \uB2E8\uC9C0 \uC0B4\uC9DD \uB9DB\uB9CC \uBCF8 \uAC83\uC785\uB2C8\uB2E4!",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "\uB3CC\uC544\uC640\uC11C {date}\uC5D0 \uC774 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uC138\uC694.",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "\uB098\uC911\uC5D0 \uB2E4\uC2DC \uB3CC\uC544\uC640\uC11C \uC774 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uC138\uC694.",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "\uC774 \uD65C\uB3D9\uC744 \uBE7C \uB193\uC73C\uC2E0 \uAC83 \uAC19\uB124\uC694!",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "\uC774 \uD65C\uB3D9\uC740 {date}\uC5D0 \uB9CC\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "\uC774\uAC74 \uADC0\uD558\uB97C \uC704\uD55C \uAC83\uC774 \uC544\uB2CC \uAC83 \uAC19\uC2B5\uB2C8\uB2E4",
  YOU_SKIPPED_THIS_ACTIVITY: "\uC774 \uD65C\uB3D9\uC744 \uAC74\uB108\uB6F0\uC5C8\uC2B5\uB2C8\uB2E4.",
  NO_PROGRAMS: "\uC544\uC9C1 \uBCFC \uAC83\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
  NO_ACTIVITY_TITLE: "\uC544\uC9C1 \uC544\uBB34\uAC83\uB3C4 \uC5C6\uC2B5\uB2C8\uB2E4(\uC544\uC9C1!)",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "\uCC4C\uB9B0\uC9C0\uAC00 \uC2DC\uC791\uB418\uBA74 \uC5EC\uAE30\uC5D0 \uC21C\uC704\uAC00 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "\uACE7 \uCC4C\uB9B0\uC9C0\uC758 \uD558\uC774\uB77C\uC774\uD2B8\uB97C \uBCF4\uB824\uBA74 \uC5EC\uAE30\uB97C \uB2E4\uC2DC \uBC29\uBB38\uD558\uC138\uC694.",
  LEADERBOARD_ALL_TIME_RANKING: "\uC804\uCCB4 \uC2DC\uAC04 \uC21C\uC704",
  LEADERBOARD_CURRENT_USER_NAME: "{user} (\uB108)",
  ACTIVITY_PROGRESS: "\uD65C\uB3D9 \uC9C4\uD589 \uC0C1\uD669",
  YOUR_JOURNEY: "\uADC0\uD558\uC758 \uC5EC\uC815",
  SUGGESTED_FOR_YOU: "\uADC0\uD558\uB97C \uC704\uD55C \uC81C\uC548",
  NO_ACHIEVEMENTS_DESCRIPTION: "\uC5EC\uC815\uC5D0\uC11C \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uC5EC \uC5C5\uC801\uC744 \uC7A0\uAE08 \uD574\uC81C\uD558\uC138\uC694.",
  UNLOCK_ACHIEVEMENTS: "\uC774 \uD504\uB85C\uADF8\uB7A8\uC758 \uBAA8\uB4E0 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uBA74 \uC5C5\uC801\uC744 \uB2EC\uC131\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  MEMBERS: "\uD68C\uC6D0",
  SIGN_UP_PERIOD_PASSED_DETAIL: "\uB4F1\uB85D \uAE30\uAC04\uC774 \uC9C0\uB0AC\uC73C\uBBC0\uB85C \uB354 \uC774\uC0C1 \uC774 \uCC4C\uB9B0\uC9C0\uC5D0 \uB4F1\uB85D\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
  REFERENCES: "\uCC38\uACE0\uBB38\uD5CC",
  SIGN_UP_BY: "{date}\uAE4C\uC9C0 \uAC00\uC785\uD558\uC138\uC694",
  YOURE_GOOD_TO_GO: "\uC774\uC81C \uC2DC\uC791\uD560 \uC218 \uC788\uC5B4\uC694!",
  CHECK_CHALLENGE_DETAILS: "\uCC4C\uB9B0\uC9C0 \uC138\uBD80 \uC815\uBCF4\uB97C \uD655\uC778\uD558\uC5EC \uD65C\uB3D9\uC5D0 \uC775\uC219\uD574\uC9C0\uC138\uC694",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "\uB3C4\uC804\uC744 \uC2DC\uC791\uD560 \uC900\uBE44\uAC00 \uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uD300\uC5D0 \uAC00\uC785\uD558\uAC70\uB098 \uD300\uC744 \uB9CC\uB4E4\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
  CHALLENGE_ABOUT_TO_START: "\uCC4C\uB9B0\uC9C0\uAC00 \uACE7 \uC2DC\uC791\uB429\uB2C8\uB2E4!",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "<strong>{date}</strong>\uC5D0 \uCC4C\uB9B0\uC9C0\uAC00 \uC2DC\uC791\uB418\uBA74 \uC77C\uC77C \uD65C\uB3D9\uC774 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  NO_CHALLENGE_ACTIVITIES_TODAY: "\uC624\uB298\uC740 \uCC4C\uB9B0\uC9C0 \uD65C\uB3D9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: '"{query}"\uC744(\uB97C) \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.',
  COVERAGE_SEARCH_TRY_AGAIN: "\uB2E4\uB978 \uD0A4\uC6CC\uB4DC\uB97C \uC0AC\uC6A9\uD558\uC5EC \uB2E4\uC2DC \uAC80\uC0C9\uD574 \uBCF4\uC138\uC694.",
  EARN: "\uC801\uB9BD",
  WORD_WITH_EXCLAMATION_POINT: "{word}!",
  UPDATED: "\uC5C5\uB370\uC774\uD2B8",
  YOUR_PROGRESS: "\uC9C4\uD589 \uC0C1\uD669",
  YOUR_PROGRESS_IN_NUMBERS: "{current} \uC911 {total} {unit} \uB610\uB294 {percentage}% \uC644\uB8CC",
  OUTCOMES_FRICTION_SCREEN_TITLE: "\uAC1C\uC778\uD654 \uC81C\uC548...",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "\uADC0\uD558\uC758 \uB2F5\uBCC0\uC744 \uBC14\uD0D5\uC73C\uB85C \uADC0\uD558\uC5D0\uAC8C \uB3C4\uC6C0\uC774 \uB418\uB294 \uC790\uB8CC\uB97C \uCC3E\uC744 \uC218 \uC788\uB3C4\uB85D \uC7A0\uC2DC \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694",
  RELOAD_MY_SUGGESTIONS: "\uB0B4 \uC81C\uC548 \uB2E4\uC2DC \uB85C\uB4DC",
  YOUR_RANKING: "\uC21C\uC704",
  TOP_RANKED: "\uC0C1\uC704 \uB7AD\uD0B9",
  VIEW_CHALLENGE_DETAILS: "\uB3C4\uC804 \uC138\uBD80 \uC815\uBCF4 \uBCF4\uAE30",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "\uBD80\uC591\uAC00\uC871\uC744 \uCD94\uAC00\uD558\uB294 \uAC83\uC774 \uC720\uC6A9\uD55C \uC774\uC720\uAC00 \uAD81\uAE08\uD558\uC2ED\uB2C8\uAE4C?",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "\uADC0\uD558\uC758 \uBD80\uC591\uAC00\uC871\uB3C4 League\uC5D0 \uC811\uC18D\uD558\uC5EC \uB354\uC6B1 \uAC74\uAC15\uD55C \uC0B6\uC744 \uB204\uB9B4 \uC218 \uC788\uC2B5\uB2C8\uB2E4!",
  UNABLE_TO_LEAVE_CHALLENGE: "\uCC4C\uB9B0\uC9C0\uB97C \uC885\uB8CC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694!",
  SUCCESSFULLY_LEFT_CHALLENGE: "\uCC4C\uB9B0\uC9C0\uB97C \uC131\uACF5\uC801\uC73C\uB85C \uC885\uB8CC\uD588\uC2B5\uB2C8\uB2E4.",
  UNABLE_TO_JOIN_CHALLENGE: "\uCC4C\uB9B0\uC9C0\uC5D0 \uCC38\uC5EC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694!",
  JOIN_TEAM: "\uD300 \uAC00\uC785",
  X_TEAM_MEMBERS: "{memberTotal, plural, other {\uD300\uC6D0 {memberTotal,number}\uBA85}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, other {{spotsAvailable,number}\uAC1C\uC758 \uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uC790\uB9AC}}",
  HIGHLIGHTS: "\uD558\uC774\uB77C\uC774\uD2B8",
  CURRENT_RANK: "\uD604\uC7AC \uC21C\uC704",
  X_LIKES: "{activityLikes, plural, other {{activityLikes,number}\uAC1C\uC758 \uC88B\uC544\uC694}}",
  LIKE: "\uC88B\uC544\uC694",
  LIKED: "\uC88B\uC544\uC694\uB97C \uB20C\uB800\uC2B5\uB2C8\uB2E4",
  TEAM_INVITE_HEADING: "\uD568\uAED8\uD558\uBA74 \uB354 \uC88B\uC2B5\uB2C8\uB2E4!",
  TEAM_INVITE_DESCRIPTION: "\uCE5C\uAD6C\uB97C \uD300\uC5D0 \uCD08\uB300\uD558\uC5EC \uD568\uAED8 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uC138\uC694.",
  TEAM_INVITE_LINK_DESCRIPTION: "\uB9CE\uC73C\uBA74 \uB9CE\uC744\uC218\uB85D \uC88B\uC2B5\uB2C8\uB2E4! \uCE5C\uAD6C\uB4E4\uC774 \uADC0\uD558\uC758 \uD300\uC5D0 \uD569\uB958\uD560 \uC218 \uC788\uB3C4\uB85D \uC774 \uB9C1\uD06C\uB97C \uACF5\uC720\uD558\uC138\uC694.",
  TEAM_INVITE_LINK: "\uD300 \uCD08\uB300 \uB9C1\uD06C",
  INVITE: "\uCD08\uB300",
  LEAVE_TEAM: "\uD300 \uD0C8\uD1F4",
  INVITE_FRIENDS: "\uCE5C\uAD6C \uCD08\uB300",
  PRIVACY: "\uAC1C\uC778\uC815\uBCF4 \uBCF4\uD638 \uC124\uC815",
  JOIN_TEAM_SUCCESS_TITLE: "\uD300\uC5D0 \uC624\uC2E0 \uAC83\uC744 \uD658\uC601\uD569\uB2C8\uB2E4!",
  JOIN_TEAM_SUCCESS_DETAIL: "\uD568\uAED8 \uC774 \uD65C\uB3D9\uB4E4\uC744 \uC815\uBCF5\uD574 \uBD05\uC2DC\uB2E4.",
  JOIN_TEAM_SUCCESS_BUTTON: "\uD300 \uC138\uBD80 \uC815\uBCF4\uB85C \uC774\uB3D9",
  CANT_JOIN_TEAM_TITLE: "{teamName}\uC5D0 \uAC00\uC785\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  CANT_JOIN_TEAM_DETAIL: "\uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694! \uC774\uBBF8 \uD300\uC5D0 \uAC00\uC785\uD55C \uACBD\uC6B0 \uD604\uC7AC \uD300\uC5D0\uC11C \uD0C8\uD1F4\uD574\uC57C \uD300\uC5D0 \uAC00\uC785\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4 {teamName}.",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "\uC544\uC9C1 \uCC4C\uB9B0\uC9C0\uC5D0 \uB4F1\uB85D\uD558\uC9C0 \uC54A\uC73C\uC2E0 \uAC83 \uAC19\uC2B5\uB2C8\uB2E4. {teamName}\uC5D0 \uAC00\uC785\uD558\uB824\uBA74 \uBA3C\uC800 \uAC00\uC785\uD574\uC57C \uD569\uB2C8\uB2E4.",
  LEAVE_TEAM_QUESTION: "{teamName}\uC5D0\uC11C \uB098\uAC00\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  LEAVE_TEAM_DESCRIPTION: "{teamName}\uC5D0\uC11C \uB098\uAC00\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uADC0\uD558\uC758 \uC810\uC218\uB294 \uB354 \uC774\uC0C1 \uD300 \uD3C9\uADE0\uC758 \uC77C\uBD80\uB85C \uAC04\uC8FC\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  CHALLENGE_ACTIVITY_DETAIL: "\uC774 \uCC4C\uB9B0\uC9C0\uC5D0\uC11C \uB2E4\uB978 \uC0AC\uB78C\uB4E4\uC774 \uB0B4 \uD65C\uB3D9\uC744 \uBCFC \uC218 \uC788\uB3C4\uB85D \uD5C8\uC6A9\uD569\uB2C8\uB2E4.",
  SHOW_MY_ACTIVITY: "\uB0B4 \uD65C\uB3D9 \uD45C\uC2DC",
  YOUR_TEAM: "\uADC0\uD558\uC758 \uD300",
  JOIN_OR_CREATE_TEAM: "\uD300\uC5D0 \uAC00\uC785 \uB610\uB294 \uB9CC\uB4E4\uAE30",
  TEAMS: "\uD300",
  CHALLENGE_TEAMS_DESCRIPTION: "\uAE30\uC874 \uD300\uC5D0 \uAC00\uC785\uD558\uAC70\uB098 \uC9C1\uC811 \uD300\uC744 \uB9CC\uB4E4\uC5B4 \uD568\uAED8 \uCC4C\uB9B0\uC9C0\uC5D0\uC11C \uACBD\uC7C1\uD558\uC138\uC694!",
  JOIN_A_TEAM: "\uD300\uC5D0 \uAC00\uC785\uD558\uAE30",
  JOIN_TEAM_DESCRIPTION: "\uD568\uAED8 \uCC38\uC5EC\uD560 \uAE30\uC874 \uD300\uC744 \uAC80\uC0C9\uD558\uC5EC \uD568\uAED8 \uACBD\uC7C1\uD558\uC138\uC694!",
  SEARCH_FOR_TEAM: "\uD300 \uAC80\uC0C9",
  CREATE_A_TEAM: "\uD300 \uB9CC\uB4E4\uAE30",
  CREATE_TEAM_DESCRIPTION: "\uB098\uB9CC\uC758 \uC544\uBC14\uD0C0\uC640 \uD300 \uC774\uB984\uC744 \uC124\uC815\uD558\uC5EC \uD300\uC744 \uB9DE\uCDA4 \uC124\uC815\uD558\uC138\uC694. \uB2E4\uB978 \uC0AC\uB78C\uB4E4\uC744 \uCD08\uB300\uD574 \uBCF4\uC138\uC694!",
  UP_YOUR_GAME: "\uAC8C\uC784 \uB808\uBCA8 \uC5C5",
  UP_YOUR_GAME_DESCRIPTION: "\uC22B\uC790\uC5D0\uB294 \uD798\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uD300\uC5D0 \uAC00\uC785\uD558\uAC70\uB098 \uC9C1\uC811 \uD300\uC744 \uB9CC\uB4DC\uC138\uC694.",
  XTH_PLACE: "{currentRank, selectordinal, one {#st} two {#nd} few {#rd} other {#th} }\uC704",
  LEADERBOARD_RANKING: "\uC21C\uC704",
  LEADERBOARD_USERNAME: "\uC0AC\uC6A9\uC790 \uC774\uB984",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "\uCD08\uB300 \uB9C1\uD06C\uAC00 \uD074\uB9BD\uBCF4\uB4DC\uC5D0 \uBCF5\uC0AC\uB418\uC5C8\uC2B5\uB2C8\uB2E4!",
  DATE_AND_TIME: "\uB0A0\uC9DC \uBC0F \uC2DC\uAC04",
  SELECT_TEAM_AVATAR: "\uD300 \uC544\uBC14\uD0C0 \uC120\uD0DD",
  CHANGE_TEAM_AVATAR: "\uD300 \uC544\uBC14\uD0C0 \uBCC0\uACBD",
  TEAM_NAME_LABEL: "\uD300 \uC774\uB984",
  CREATE_TEAM: "\uD300 \uB9CC\uB4E4\uAE30",
  TEAM_NAME_ERROR: "\uD300 \uC774\uB984\uC744 \uC785\uB825\uD558\uC138\uC694.",
  SLIDE_OF_TOTAL: "\uC2AC\uB77C\uC774\uB4DC {key} \uC911 {total}",
  PAGE_OF_TOTAL: "{total}\uD398\uC774\uC9C0 {current}",
  NO_SOLUTIONS_AVAILABLE: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4\uB9CC, \uD604\uC7AC \uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uC194\uB8E8\uC158\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  SEE_ALL_ACHIEVEMENTS: "\uBAA8\uB4E0 \uC5C5\uC801 \uBCF4\uAE30",
  VIEW_ALL_ACHIEVEMENTS: "\uBAA8\uB4E0 \uC5C5\uC801 \uBCF4\uAE30",
  TAKE_ME_BACK: "\uB3CC\uC544\uAC00\uAE30",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "\uC774 \uC544\uBC14\uD0C0\uB294 \uD300\uC758 \uD504\uB85C\uD544\uACFC \uC21C\uC704\uD45C\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  CREATE_TEAM_SUCCESS: "\uD300\uC774 \uC900\uBE44\uB418\uC5C8\uC2B5\uB2C8\uB2E4!",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "\uD300 \uAD6C\uCD95\uC744 \uC2DC\uC791\uD558\uC138\uC694. \uC774 \uB9C1\uD06C\uB97C \uB2E4\uB978 \uC0AC\uB78C\uB4E4\uACFC \uACF5\uC720\uD558\uBA74 \uD568\uAED8 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  VIEW_TEAM_DETAILS: "\uD300 \uC138\uBD80 \uC815\uBCF4 \uBCF4\uAE30",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "\uAE30\uB2A5 \uD558\uC774\uB77C\uC774\uD2B8 \uCE90\uB7EC\uC140",
  CHALLENGE_FAQ_HEADING: "\uCC4C\uB9B0\uC9C0\uC5D0 \uB300\uD574 \uAD81\uAE08\uD55C \uC810\uC774 \uC788\uC73C\uC2E0\uAC00\uC694?",
  CHALLENGE_FAQ_CONTENT: "\uC790\uC8FC \uBB3B\uB294 \uC9C8\uBB38\uC740 {helpCenterLink}\uC5D0\uC11C \uD655\uC778\uD558\uC138\uC694.",
  VIEW_THIS_RESOURCE: "\uC774 \uB9AC\uC18C\uC2A4 \uBCF4\uAE30",
  RECOMMENDED_NEXT_STEP: "\uCD94\uCC9C\uD558\uB294 \uB2E4\uC74C \uB2E8\uACC4",
  CARE_COLLECTIONS: "\uCF00\uC5B4 \uCEEC\uB809\uC158",
  PLAN_PROGRESS: "\uD50C\uB79C \uC9C4\uD589",
  SINCE_LAST_WEEK: "\uC9C0\uB09C \uC8FC\uBD80\uD130",
  INSIGHT_CARD_HEADER_DISTANCE: "\uAC70\uB9AC",
  INSIGHT_CARD_HEADER_STEPS: "\uB2E8\uACC4",
  MIN: "\uBD84",
  CAL: "Cal",
  KM: "km",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, other {\uACC4\uB2E8}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "\uC624\uB978 \uACC4\uB2E8 \uC218",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, other {\uACC4\uB2E8}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "\uD65C\uB3D9 \uC2DC\uAC04",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "\uC18C\uBAA8\uB41C \uC5D0\uB108\uC9C0",
  INSIGHT_CARD_HEADER_WEIGHT: "\uBB34\uAC8C",
  INSIGHT_CARD_HEADER_HEIGHT: "\uB192\uC774",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "\uB9C8\uC74C\uCC59\uAE40(\uBD84)",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "\uD5C8\uB9AC \uB458\uB808",
  DAY_OF_THE_WEEK: "\uC694\uC77C",
  DAY_OF_THE_MONTH: "\uC694\uC77C",
  INSIGHT_CHART_WEEK_TITLE: "\uC8FC\uAC04 {type} \uB9C9\uB300\uD615 \uCC28\uD2B8",
  INSIGHT_CHART_MONTH_TITLE: "\uC6D4\uAC04 {type} \uB9C9\uB300\uD615 \uCC28\uD2B8",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "\uC77C\uC8FC\uC77C \uB3D9\uC548 \uC694\uC77C\uBCC4 \uAC78\uC740 \uAC78\uC74C \uC218\uB294 {unit}\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "\uD55C \uB2EC \uB3D9\uC548 \uC694\uC77C\uBCC4 \uAC78\uC740 \uAC78\uC74C \uC218\uB294 {unit}\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "{date}\uC5D0 {distance} {unit} \uAC78\uC5C8\uC2B5\uB2C8\uB2E4",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "\uC77C\uC8FC\uC77C \uB3D9\uC548 \uC694\uC77C\uBCC4 \uAC78\uC740 \uAC78\uC74C \uC218\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "\uD55C \uB2EC \uB3D9\uC548 \uC694\uC77C\uBCC4 \uAC78\uC740 \uAC78\uC74C \uC218\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "{date}\uC5D0 {distance} {unit} \uAC78\uC5C8\uC2B5\uB2C8\uB2E4",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "\uC77C\uC8FC\uC77C \uB3D9\uC548 \uC694\uC77C\uBCC4 \uD0DC\uC6B4 \uC5D0\uB108\uC9C0\uB294 {unit}\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "\uD55C \uB2EC \uB3D9\uC548 \uC694\uC77C\uBCC4 \uD0DC\uC6B4 \uC5D0\uB108\uC9C0\uB294 {unit}\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "{date}\uC5D0 {energy} {unit} \uD0DC\uC6E0\uC2B5\uB2C8\uB2E4",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "\uC77C\uC8FC\uC77C \uB3D9\uC548 \uC694\uC77C\uBCC4 \uD65C\uB3D9 \uC2DC\uAC04(\uBD84)\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "\uD55C \uB2EC \uB3D9\uC548 \uC694\uC77C\uBCC4 \uD65C\uB3D9 \uC2DC\uAC04(\uBD84)\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "{date}\uC5D0 {value}\uC758 \uD65C\uB3D9 \uC2DC\uAC04\uC744 \uBCF4\uB0C8\uC2B5\uB2C8\uB2E4",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "\uC77C\uC8FC\uC77C \uB3D9\uC548 \uC694\uC77C\uBCC4 \uC624\uB978 \uACC4\uB2E8 \uC218\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "\uD55C \uB2EC \uB3D9\uC548 \uC694\uC77C\uBCC4 \uC624\uB978 \uACC4\uB2E8 \uC218\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "{date}\uC5D0 {value}\uAC1C\uC758 \uACC4\uB2E8\uC744 \uC62C\uB790\uC2B5\uB2C8\uB2E4",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "\uC77C\uC8FC\uC77C \uB3D9\uC548 \uC694\uC77C\uBCC4 \uB9C8\uC74C\uCC59\uAE40\uC5D0 \uD560\uC560\uD55C \uC2DC\uAC04\uC758 \uC591\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "\uD55C \uB2EC \uB3D9\uC548 \uC694\uC77C\uBCC4 \uB9C8\uC74C\uCC59\uAE40\uC5D0 \uD560\uC560\uD55C \uC2DC\uAC04\uC758 \uC591\uC785\uB2C8\uB2E4.",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "{date}\uC5D0 {value}\uC744 \uB9C8\uC74C\uCC59\uAE40\uC5D0 \uD560\uC560\uD588\uC2B5\uB2C8\uB2E4",
  DATAPOINT_CATEGORY_ACTIVITY: "\uD65C\uB3D9",
  DATAPOINT_CATEGORY_BODY: "\uC2E0\uCCB4",
  MANAGE_UNITS_APPLYING_CHANGES: "\uBCC0\uACBD \uC801\uC6A9 \uC911...",
  MANAGE_UNITS_CHANGES_SAVED: "{time}\uC5D0 \uBCC0\uACBD \uC0AC\uD56D\uC774 \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
  START_TRACKING_HEALTH_HEADER: "\uAC74\uAC15 \uCD94\uC801 \uC2DC\uC791",
  START_TRACKING_HEALTH_BODY: "\uC2DC\uAC04 \uACBD\uACFC\uC5D0 \uB530\uB978 \uC9C0\uD45C\uB97C \uC27D\uAC8C \uCD94\uC801\uD558\uC5EC \uC5BC\uB9C8\uB098 \uC654\uB294\uC9C0, \uAC74\uAC15 \uBAA9\uD45C\uB97C \uD5A5\uD574 \uC5B4\uB5BB\uAC8C \uB178\uB825\uD560 \uC218 \uC788\uB294\uC9C0 \uD655\uC778\uD558\uC138\uC694.",
  BROWSE_SUPPORTED_DATA: "\uBB34\uC5C7\uC744 \uCD94\uC801\uD560 \uC218 \uC788\uB098\uC694?",
  LAST_UPDATED_CARRIER_DATE: "\uB9C8\uC9C0\uB9C9 \uC5C5\uB370\uC774\uD2B8: {carrier}\uC5D0\uC11C {date}",
  ACCUMULATION_PERIOD: "\uB204\uC801 \uAE30\uAC04: <strong>{start} - {end}</strong>",
  NO_DATA_AVAILABLE: "\uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  LAST_7_DAYS: "\uC9C0\uB09C 7\uC77C",
  LAST_30_DAYS: "\uC9C0\uB09C 30\uC77C",
  OLDER: "\uB354 \uC624\uB798\uB41C",
  GET_ASSISTANCE_NOW: "\uCF00\uC5B4 \uD300\uACFC \uCC44\uD305\uD558\uAE30",
  GET_ASSISTANCE_NOW_SUBHEADING: "\uB2F9\uC0AC\uC758 \uC778\uC99D\uB41C \uC758\uB8CC \uC804\uBB38\uAC00 \uBC0F \uACE0\uAC1D \uC11C\uBE44\uC2A4 \uC9C1\uC6D0\uACFC \uCC44\uD305\uD574 \uBCF4\uC138\uC694. \uD574\uB2F9 \uC9C0\uC5ED\uC5D0\uC11C \uAC00\uC7A5 \uC800\uB834\uD558\uACE0 \uD488\uC9C8\uC774 \uC6B0\uC218\uD55C \uC758\uB8CC \uC11C\uBE44\uC2A4\uB97C \uC548\uB0B4\uD558\uC5EC \uBE44\uC6A9\uC744 \uC808\uAC10\uD560 \uC218 \uC788\uB3C4\uB85D \uB3C4\uC640\uC904 \uBFD0\uB9CC \uC544\uB2C8\uB77C \uD61C\uD0DD\uC5D0 \uB300\uD55C \uBAA8\uB4E0 \uC9C8\uBB38\uC5D0 \uB2F5\uBCC0\uD574 \uB4DC\uB9BD\uB2C8\uB2E4.",
  HOW_CAN_WE_HELP: "\uC5B4\uB5BB\uAC8C \uB3C4\uC640\uB4DC\uB9B4\uAE4C\uC694?",
  LIVE_BALANCE_SPEND: "{spentAmount}/{totalAmount} \uC9C0\uCD9C",
  LIVE_BALANCE_REMAINING: "{amount} \uB0A8\uC74C",
  UPDATING_UNITS_OF_MEASURE: "\uCE21\uC815 \uB2E8\uC704 \uC5C5\uB370\uC774\uD2B8",
  CHANGING_YOUR_UNITS: "\uB2E8\uC704\uB97C \uBCC0\uACBD\uD558\uBA74 \uC571\uC5D0\uC11C \uD45C\uC2DC\uB418\uB294 \uBC29\uC2DD\uC5D0\uB3C4 \uC601\uD5A5\uC744 \uBBF8\uCE69\uB2C8\uB2E4.",
  MESSAGES: "\uBA54\uC2DC\uC9C0",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "\uBA54\uC2DC\uC9C0",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "{fileName}\uC774(\uAC00) \uC81C\uAC70\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
  SEND_NEW_MESSAGE: "\uC0C8 \uBA54\uC2DC\uC9C0 \uBCF4\uB0B4\uAE30",
  NEW_MESSAGE_DEFAULT: "\uC0C8 \uBA54\uC2DC\uC9C0\uC758 \uC2DC\uC791\uC785\uB2C8\uB2E4",
  NETWORK_MISSING_COVERAGE: "\uC0AC\uC6A9 \uC911\uC778 \uD50C\uB79C\uC5D0\uB294 {network} \uD61C\uD0DD\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  CARD_FRONT_FILENAME: "{benefitName}-\uC55E",
  CARD_BACK_FILENAME: "{benefitName}-\uB4A4",
  START_A_VISIT: "\uBC29\uBB38 \uC2DC\uC791",
  UNREAD: "\uC77D\uC9C0 \uC54A\uC74C",
  NO_MESSAGES_HEADING: "\uC544\uC9C1 \uBA54\uC2DC\uC9C0\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  NO_MESSAGES_DESCRIPTION: "\uC0C8 \uBA54\uC2DC\uC9C0\uB97C \uBCF4\uB0B4\uC11C \uC2DC\uC791\uD558\uC138\uC694!",
  RETURN_TO: "{location}(\uC73C)\uB85C \uB3CC\uC544\uAC00\uAE30",
  NO_RECOMENDATIONS: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uD604\uC7AC \uC774 \uAC74\uAC15 \uC601\uC5ED\uC5D0 \uB300\uD55C \uCD94\uCC9C \uC0AC\uD56D\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  CHALLENGES_LEGAL_DISCLAIMER: "\uC774 \uCC4C\uB9B0\uC9C0\uB294 \uBAA8\uB4E0 \uC0AC\uB78C\uC774 \uCC38\uC5EC\uD558\uC5EC \uACF5\uC815\uD55C \uB2F9\uCCA8 \uAE30\uD68C\uB97C \uC5BB\uC744 \uC218 \uC788\uB294 \uC990\uAC70\uC6B4 \uBC29\uBC95\uC73C\uB85C \uACE0\uC548\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uB54C\uB54C\uB85C \uCC4C\uB9B0\uC9C0 \uC8FC\uCD5C\uCE21\uC5D0\uC11C \uD604\uAE08\uC774\uB098 \uB2E4\uB978 \uC0C1\uD488\uC744 \uC81C\uACF5\uD560 \uC218\uB3C4 \uC788\uC2B5\uB2C8\uB2E4. League\uC5D0\uC11C\uB294 \uCC4C\uB9B0\uC9C0 \uAD6C\uC870\uB97C \uC124\uACC4\uD558\uC9C0 \uC54A\uC73C\uBA70, \uC0C1\uAE08 \uC81C\uACF5 \uC5EC\uBD80\uB97C \uACB0\uC815\uD558\uC9C0\uB3C4 \uC54A\uC2B5\uB2C8\uB2E4. \uC774 \uCC4C\uB9B0\uC9C0\uC758 \uAD6C\uC870\uC5D0 \uB300\uD55C \uC6B0\uB824\uAC00 \uC788\uAC70\uB098 \uB2F9\uCCA8 \uAE30\uD68C\uC5D0\uC11C \uC81C\uC678\uB418\uC5C8\uB2E4\uACE0 \uC0DD\uAC01\uB418\uBA74 \uCC4C\uB9B0\uC9C0 \uC8FC\uCD5C\uCE21\uC5D0 \uBB38\uC758\uD558\uC138\uC694.",
  ALL_RECORDED_DATA: "\uBAA8\uB4E0 \uAE30\uB85D \uB370\uC774\uD130",
  NEW_MESSAGE: "\uC0C8 \uBA54\uC2DC\uC9C0",
  MESSAGE_SUBJECT: "\uBA54\uC2DC\uC9C0 \uC81C\uBAA9(\uC120\uD0DD\uC0AC\uD56D)",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "\uCD5C\uB300 \uBB38\uC790 \uC218 \uC81C\uD55C({maximum})\uC744 \uCD08\uACFC\uD588\uC2B5\uB2C8\uB2E4",
  CHAR_LIMIT_EXCEEDED: "\uBB38\uC790 \uC81C\uD55C\uC744 \uCD08\uACFC\uD588\uC2B5\uB2C8\uB2E4({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "\uBA54\uC2DC\uC9C0 \uC8FC\uC81C \uC124\uC815",
  TO: "\uC218\uC2E0\uC790",
  CREATE_MESSAGE: "\uBA54\uC2DC\uC9C0 \uB9CC\uB4E4\uAE30",
  MEMBER_LABEL: "\uD68C\uC6D0",
  RESULT_COUNT: "{count, plural, other {{count,number}\uAC1C \uACB0\uACFC}}",
  USER_COLON: "{user}:",
  JOINED_CHALLENGE_HIGHLIGHT: "<strong>{challengeTitle}</strong>\uC5D0 \uAC00\uC785\uD588\uC2B5\uB2C8\uB2E4!",
  CREATED_TEAM_HIGHLIGHT: "<strong>{teamName}</strong>\uC744(\uB97C) \uC0DD\uC131\uD588\uC2B5\uB2C8\uB2E4!",
  JOINED_TEAM_HIGHLIGHT: "<strong>{teamName}</strong>\uC5D0 \uAC00\uC785\uD588\uC2B5\uB2C8\uB2E4!",
  STEPS_WALKED_HIGHLIGHT: "<strong> {total} \uAC78\uC74C\uC744 \uAC78\uC5C8\uC2B5\uB2C8\uB2E4!</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong> {total}\uBD84 \uB3D9\uC548 \uC6C0\uC9C1\uC600\uC2B5\uB2C8\uB2E4!</strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong> {total}\uBD84 \uC644\uB8CC\uD588\uC2B5\uB2C8\uB2E4!</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong> {achievement} \uC5C5\uC801\uC744 \uD68D\uB4DD\uD588\uC2B5\uB2C8\uB2E4!</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "<strong>\uD65C\uB3D9\uC744 \uC644\uB8CC\uD588\uC2B5\uB2C8\uB2E4!</strong>",
  NO_CARE_COLLECTIONS: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uC0AC\uC6A9\uD560 \uC218 \uC788\uB294 \uCF00\uC5B4 \uCEEC\uB809\uC158\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  WRITE_A_MESSAGE: "\uBA54\uC2DC\uC9C0 \uC791\uC131...",
  UPLOAD_MULTIPLE_FILES: "\uC5EC\uB7EC \uD30C\uC77C \uC5C5\uB85C\uB4DC",
  SEND: "\uBCF4\uB0B4\uAE30",
  SEND_MESSAGE: "\uBA54\uC2DC\uC9C0 \uBCF4\uB0B4\uAE30",
  DATA_SOURCES: "\uB370\uC774\uD130 \uC18C\uC2A4",
  SOURCES_CAN_UPDATE: "\uB2E4\uC74C \uC18C\uC2A4\uB97C \uC5C5\uB370\uC774\uD2B8\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "\uC5EC\uB7EC \uC18C\uC2A4\uAC00 \uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uACBD\uC6B0 \uAC00\uC7A5 \uD070 \uAC12\uC744 \uAC00\uC9C4 \uB370\uC774\uD130 \uC18C\uC2A4\uAC00 \uD558\uB8E8\uAC00 \uB05D\uB0A0 \uB54C(\uC624\uD6C4 11\uC2DC 59\uBD84)\uC5D0 \uAE30\uB85D\uB429\uB2C8\uB2E4.",
  APPLE_HEALTH: "Apple Health",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "Garmin",
  POLAR: "Polar",
  WITHINGS: "Withings",
  HEALTH_PLAN: "Health Plan",
  OMRON: "Omron",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "\uCE21\uC815 \uB2E8\uC704 \uC5C5\uB370\uC774\uD2B8",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "\uB2E8\uC704\uB97C \uBCC0\uACBD\uD558\uBA74 \uC571\uC5D0\uC11C \uB2E8\uC704\uAC00 \uD45C\uC2DC\uB418\uB294 \uBC29\uC2DD\uC5D0 \uC601\uD5A5\uC744 \uBBF8\uCE69\uB2C8\uB2E4",
  DOCUMENTS: "\uBB38\uC11C",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "\uBB38\uC11C",
  DOCUMENTS_FILTER_LOADING_TEXT: "\uD0ED \uBC0F \uD544\uD130\uAC00 \uB85C\uB4DC \uC911\uC785\uB2C8\uB2E4",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "{filter}\uC740(\uB294) \uD544\uC218\uC785\uB2C8\uB2E4",
  DOCUMENT_FILTER_X_REQUIRED: "{filter}(\uD544\uC218)",
  SHOW_RESULTS: "\uACB0\uACFC \uD45C\uC2DC",
  RESET_FILTERS: "\uD544\uD130 \uCD08\uAE30\uD654",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "\uC120\uD0DD\uD55C \uD544\uD130",
  DOCUMENTS_RESULTS_HEADING: "\uACB0\uACFC",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "\uBB38\uC11C \uC5C5\uB85C\uB4DC\uB85C \uAC74\uB108\uB6F0\uAE30",
  AVERAGE: "\uD3C9\uADE0",
  VIEW_ALL_METRIC_DATA: "\uBAA8\uB4E0 {metric} \uB370\uC774\uD130 \uBCF4\uAE30",
  EDIT_METRIC_DATA: "{metric} \uB370\uC774\uD130 \uD3B8\uC9D1",
  NEXT_WEEK: "\uB2E4\uC74C \uC8FC",
  PREVIOUS_WEEK: "\uC9C0\uB09C \uC8FC",
  NEXT_30_DAYS: "\uB2E4\uC74C 30\uC77C",
  PREVIOUS_30_DAYS: "\uC774\uC804 30\uC77C",
  UNABLE_TO_CREATE_TEAM: "\uC0C8 \uD300\uC744 \uB9CC\uB4E4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4 - \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694! \uC774\uBBF8 \uD300\uC5D0 \uAC00\uC785\uD55C \uACBD\uC6B0\uC5D0\uB294 \uC0C8 \uD300\uC744 \uB9CC\uB4E4\uAE30 \uC804\uC5D0 \uD604\uC7AC \uD300\uC5D0\uC11C \uD0C8\uD1F4\uD574\uC57C \uD569\uB2C8\uB2E4.",
  START_TRACKING_METRIC: "{metric} \uCD94\uC801 \uC2DC\uC791",
  CURRENTLY_NOT_SHARING_DATA: "\uD604\uC7AC {metric}\uC5D0 \uB300\uD55C \uB370\uC774\uD130\uB97C \uACF5\uC720\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4",
  SUCCESSFULLY_LEFT_TEAM: "{teamName}\uC744(\uB97C) \uC131\uACF5\uC801\uC73C\uB85C \uD0C8\uD1F4\uD588\uC2B5\uB2C8\uB2E4",
  LEAVE_TEAM_FAILURE: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694!",
  VIEW_DATA_SOURCES: "\uB370\uC774\uD130 \uC6D0\uBCF8 \uBCF4\uAE30",
  OR_CALL_PHONE: "\uB610\uB294 {phoneNumber}{extension}(\uC73C)\uB85C \uC804\uD654\uD558\uC138\uC694",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "\uD544\uD130\uC640 \uC77C\uCE58\uD558\uB294 \uBB38\uC11C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "\uB354 \uB9CE\uC740 \uACB0\uACFC\uB97C \uC5BB\uC73C\uB824\uBA74 \uC77C\uBD80 \uD544\uD130\uB97C \uC81C\uAC70\uD574 \uBCF4\uC138\uC694",
  DOCUMENTS_EMPTY_STATE_HEADING: "\uC544\uC9C1\uC740 \uBB38\uC11C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4!",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "\uBAA8\uB4E0 \uBB38\uC11C\uB97C \uD55C \uACF3\uC5D0\uC11C \uBCF4\uACE0 \uC815\uB9AC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team} (\uB108\uC758 \uD300)",
  YOUR_CARE_ADVISOR: "\uCF00\uC5B4 \uAC00\uC774\uB4DC",
  COMPOSE: "\uC791\uC131",
  COMPOSE_NEW_MESSAGE: "\uC0C8 \uBA54\uC2DC\uC9C0 \uC791\uC131\uD558\uAE30",
  POLICY_INFORMATION_UNAVAILABLE: "\uC774 \uC815\uCC45 \uAE30\uAC04\uC5D0\uB294 \uC0AC\uC6A9\uD560 \uC218 \uC788\uB294 \uC815\uBCF4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "\uC774 \uD68C\uC6D0\uC5D0 \uB300\uD55C \uC815\uBCF4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uB978 \uD68C\uC6D0\uC744 \uC2DC\uD5D8\uD574 \uBCF4\uC138\uC694.",
  MESSAGE_THREADS_LIST_SUBTITLE: "\uC800\uD76C \uD300\uC5D0\uC11C \uB2F5\uBCC0\uD558\uB294 \uB370 \uCD5C\uB300 2 \uC601\uC5C5\uC77C\uC774 \uC18C\uC694\uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC751\uAE09 \uC0C1\uD669\uC778 \uACBD\uC6B0 911\uC5D0 \uC804\uD654\uD558\uC138\uC694.",
  ALL_MESSAGES_FOR_THREAD_BANNER: "\uC774 \uCC44\uD305\uC744 \uD1B5\uD574 \uADC0\uD558\uC758 \uAC74\uAC15 \uC0C1\uD0DC\uC5D0 \uB300\uD55C \uAD6C\uCCB4\uC801\uC778 \uC815\uBCF4\uB97C \uBCF4\uB0B4\uC9C0 \uB9C8\uC138\uC694.",
  TROUBLE_DISPLAYING_DATA: "\uB370\uC774\uD130\uB97C \uD45C\uC2DC\uD558\uB294 \uB370 \uBB38\uC81C\uAC00 \uC788\uC2B5\uB2C8\uB2E4",
  RETRY: "\uB2E4\uC2DC \uC2DC\uB3C4",
  YOU: "\uB108",
  MESSAGES_IN_CONVERSATION_TITLED: "{subject}(\uC774)\uB77C\uB294 \uC81C\uBAA9\uC758 \uB300\uD654\uC5D0 \uC788\uB294 \uBA54\uC2DC\uC9C0",
  TEAM_AVERAGE: "\uD300 \uD3C9\uADE0",
  YOUR_METRICS: "\uB0B4 \uC9C0\uD45C",
  YOUR_METRICS_SUBTITLE: "\uC5F0\uACB0\uB41C \uC571 \uBC0F \uAE30\uAE30\uC758 \uAC00\uC7A5 \uCD5C\uADFC \uAC74\uAC15 \uB370\uC774\uD130\uC785\uB2C8\uB2E4.",
  APPS_AND_DEVICES: "\uC571 \uBC0F \uAE30\uAE30",
  APP_AND_DEVICE_METRICS: "\uC571 \uBC0F \uAE30\uAE30 \uC9C0\uD45C",
  VIEW_ALL_METRICS: "\uC571 \uBC0F \uAE30\uAE30 \uBA54\uD2B8\uB9AD \uBCF4\uAE30",
  RENAME: "\uC774\uB984 \uBC14\uAFB8\uAE30",
  ENTER_DOCUMENT_NAME: "\uBB38\uC11C \uC774\uB984\uC744 \uC785\uB825\uD558\uC138\uC694",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "\uC6B0\uC120\uC21C\uC704\uAC00 \uB0AE\uC740 \uD65C\uB3D9\uC785\uB2C8\uB2E4",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "\uC911\uAC04 \uC6B0\uC120\uC21C\uC704 \uD65C\uB3D9\uC785\uB2C8\uB2E4",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "\uC6B0\uC120\uC21C\uC704\uAC00 \uB192\uC740 \uD65C\uB3D9\uC785\uB2C8\uB2E4",
  ACTIVITY_EXPIRY_DATE: "{date}\uC5D0 \uB9CC\uB8CC\uB428",
  GENERIC_MESSAGING_ERROR: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD55C \uAC83 \uAC19\uC2B5\uB2C8\uB2E4. \uD398\uC774\uC9C0\uB97C \uC0C8\uB85C \uACE0\uCE58\uACE0 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uBCF4\uC138\uC694.",
  VIEW_THE_ACTIVITIES: "\uD65C\uB3D9 \uBCF4\uAE30",
  DRAG_FILES_HERE_OR: "\uD30C\uC77C\uC744 \uC5EC\uAE30\uB85C \uB4DC\uB798\uADF8\uD558\uAC70\uB098",
  ADD_A_DOCUMENT: "\uBB38\uC11C \uCD94\uAC00",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "\uBB38\uC11C \uC5C5\uB85C\uB4DC",
  ADD_DOCUMENT: "\uBB38\uC11C \uCD94\uAC00",
  REMOVE_FILE: "\uD30C\uC77C \uC81C\uAC70",
  REMOVE_FILE_FILENAME: "\uD30C\uC77C \uC81C\uAC70: {filename}({fileSize})",
  UNABLE_TO_SEND: "\uC804\uC1A1\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  ERROR_HEADING: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
  FILE_TYPE_NOT_SUPPORTED: "\uC9C0\uC6D0\uB418\uC9C0 \uC54A\uB294 \uD30C\uC77C \uD615\uC2DD",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "\uB2E4\uC74C \uD615\uC2DD \uC911 \uD558\uB098\uC758 \uD30C\uC77C\uC744 \uC0AC\uC6A9\uD558\uC138\uC694: {fileTypes}",
  RECENT_MESSAGES: "\uCD5C\uADFC \uBA54\uC2DC\uC9C0",
  NO_MESSAGES_YET: "\uC544\uC9C1 \uBA54\uC2DC\uC9C0\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4(\uC544\uC9C1!)",
  NO_MESSAGES_DETAIL: "\uADC0\uD558\uC5D0\uAC8C \uC9C0\uC815\uB41C \uCF00\uC5B4 \uAC00\uC774\uB4DC\uB294 \uADC0\uD558\uAC00 \uBAA8\uB4E0 \uB2E8\uACC4\uC5D0\uC11C \uBCF4\uB2E4 \uD604\uBA85\uD55C \uAC74\uAC15 \uAD00\uB9AC \uACB0\uC815\uC744 \uB0B4\uB9B4 \uC218 \uC788\uB3C4\uB85D \uB3C4\uC640\uB4DC\uB9BD\uB2C8\uB2E4.",
  START_A_NEW_MESSAGE: "\uC0C8 \uBA54\uC2DC\uC9C0 \uC2DC\uC791",
  USER_ADDED_DOCUMENT: "{user} \uBB38\uC11C\uB97C \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4",
  ERROR_SAVING_TRY_AGAIN: "\uBCC0\uACBD \uC0AC\uD56D\uC744 \uC800\uC7A5\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC2ED\uC2DC\uC624",
  MODIFIED_ON_DATE: "{date}\uC5D0 \uC218\uC815\uB428",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "{date} \uC5C5\uB85C\uB4DC",
  FILE_TYPE_TOO_LARGE: "\uD30C\uC77C \uD06C\uAE30\uAC00 \uB108\uBB34 \uD07D\uB2C8\uB2E4",
  MAXIMUM_FILE_SIZE_IS: "\uCD5C\uB300 \uD30C\uC77C \uD06C\uAE30\uB294 {fileSize}\uC785\uB2C8\uB2E4.",
  FILE_TOO_LARGE: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uD30C\uC77C\uC774 \uB108\uBB34 \uD07D\uB2C8\uB2E4.",
  FILE_SHOULD_BE_UNDER: "\uADC0\uD558\uC758 \uD30C\uC77C\uC740 {fileSize} \uC774\uD558\uC5EC\uC57C \uD569\uB2C8\uB2E4",
  ATTACHMENT_LIMIT_REACHED: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uD30C\uC77C\uC774 \uB108\uBB34 \uB9CE\uC2B5\uB2C8\uB2E4.",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "{maxNumberOfDocs, plural, other {\uCD5C\uB300 {maxNumberOfDocs,number}\uAC1C \uD30C\uC77C\uAE4C\uC9C0 \uCCA8\uBD80\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.}}",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uD574\uB2F9 \uD30C\uC77C \uD615\uC2DD\uC740 \uC9C0\uC6D0\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  ATTACHMENT_TYPES_ALLOWED: "\uB2E4\uC74C \uD615\uC2DD\uC73C\uB85C \uD30C\uC77C\uC744 \uCCA8\uBD80\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4: {fileTypes}",
  MESSAGES_PAGINATION_ERROR: "\uBA54\uC2DC\uC9C0\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. <button>\uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.</button>",
  TRANSACTION_TABLE_CAPTION: "\uAC70\uB798 \uB0B4\uC5ED",
  SELECT_PARTICIPANT_ERROR: "\uBA54\uC2DC\uC9C0\uB97C \uBCF4\uB0BC \uC0AC\uB78C\uC744 \uC120\uD0DD\uD558\uC138\uC694",
  PREVIEW_OF_FILE: "{fileName} \uBBF8\uB9AC \uBCF4\uAE30",
  VIEW_DOCUMENT_ACTIONS: "{documentName} \uC791\uC5C5 \uBCF4\uAE30.",
  VISIT_LINK: "\uB9C1\uD06C \uBC29\uBB38",
  COMPLETE_BY_DATE: "{date}\uAE4C\uC9C0 \uC644\uB8CC",
  ERROR_UPLOADING_YOUR_FILE: "\uD30C\uC77C \uC5C5\uB85C\uB4DC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  DOCUMENT_SUBMITTED: "\uC81C\uCD9C\uB41C \uBB38\uC11C",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "\uACE7 \uBB38\uC11C\uB97C \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uAE30\uB2E4\uB838\uB2E4\uAC00 \uD398\uC774\uC9C0\uB97C \uC0C8\uB85C\uACE0\uCE68\uD558\uC138\uC694.",
  ERROR_RENAMING_YOUR_DOCUMENT: "\uBB38\uC11C \uC774\uB984\uC744 \uBCC0\uACBD\uD558\uB294 \uB3D9\uC548 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  DOCUMENT_RENAMED_SUCCESSFULLY: "\uBB38\uC11C \uC774\uB984\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
  ERROR_HEADING_GENERIC: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  ERROR_HEADING_NO_CONNECTION: "\uC778\uD130\uB137 \uC5F0\uACB0 \uC5C6\uC74C",
  ERROR_CAPTION_BACKEND: "\uC11C\uBC84\uC5D0 \uC5F0\uACB0\uD558\uB294 \uB3D9\uC548 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.\n \uB098\uC911\uC5D0 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
  ERROR_CAPTION_NO_CONNECTION: "\uB370\uC774\uD130\uB97C \uB85C\uB4DC\uD558\uB294 \uB3D9\uC548 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.\n \uC778\uD130\uB137\uC744 \uD655\uC778\uD558\uACE0 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  SURE_YOU_WANT_TO_DELETE: "{name}\uC744(\uB97C) \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  CANNOT_BE_UNDONE: "\uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  DELETE_DOCUMENT: "\uBB38\uC11C \uC0AD\uC81C",
  DOCUMENT_DELETED: "\uBB38\uC11C \uC0AD\uC81C\uB428",
  ERROR_DELETING_YOUR_DOCUMENT: "\uBB38\uC11C \uC0AD\uC81C \uC911 \uC624\uB958 \uBC1C\uC0DD",
  ABOUT_ME: "\uB0B4 \uC18C\uAC1C",
  SEE_MORE: "\uB354 \uBCF4\uAE30",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "\uC774 \uD398\uC774\uC9C0\uB97C \uB85C\uB4DC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC778\uD130\uB137\uC744 \uD655\uC778\uD558\uACE0 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  WHO_IS_THIS_DOCUMENT_ABOUT: "\uC774 \uBB38\uC11C\uB294 \uB204\uAD6C\uC5D0 \uAD00\uD55C \uAC83\uC785\uB2C8\uAE4C?",
  SELECT_A_USER: "\uC0AC\uC6A9\uC790 \uC120\uD0DD",
  SELECT_DOCUMENT: "\uBB38\uC11C \uC120\uD0DD",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "\uC774\uB294 \uCF00\uC5B4 \uAC00\uC774\uB4DC\uAC00 \uC774 \uBB38\uC11C\uC640 \uAD00\uB828\uB41C \uC0AC\uB78C\uC744 \uC2DD\uBCC4\uD558\uB294 \uB370 \uB3C4\uC6C0\uC774 \uB429\uB2C8\uB2E4.",
  VIEW_ALL_MESSAGE_THREADS: "\uBAA8\uB4E0 \uBA54\uC2DC\uC9C0 \uC2A4\uB808\uB4DC \uBCF4\uAE30",
  TOGGLE_MENU: "\uBA54\uC2DC\uC9C0 \uBAA9\uB85D \uC804\uD658",
  METRICS: "\uC9C0\uD45C",
  UNREAD_MESSAGES: "\uC77D\uC9C0 \uC54A\uC740 \uBA54\uC2DC\uC9C0",
  WITH_DOCUMENT: "\uBB38\uC11C\uC640 \uD568\uAED8",
  X_TOKENS: "{tokenCount, plural, other {\uD1A0\uD070 {tokenCount,number}\uAC1C}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "\uD504\uB85C\uADF8\uB7A8 \uBC0F \uCC4C\uB9B0\uC9C0",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "\uC0C8\uB85C\uC6B4 \uAC83\uC744 \uC6D0\uD558\uC2DC\uB098\uC694?",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "\uADC0\uD558\uC758 \uAC1C\uC778 \uBAA9\uD45C\uC5D0 \uB9DE\uB294 \uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8\uACFC \uCC4C\uB9B0\uC9C0\uB97C \uCC3E\uC544\uBCF4\uC138\uC694.",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "\uD504\uB85C\uADF8\uB7A8 \uBC0F \uCC4C\uB9B0\uC9C0",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "\uC0C8\uB85C\uC6B4 \uAC83\uC744 \uC6D0\uD558\uC2DC\uB098\uC694?",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "\uADC0\uD558\uC758 \uAC1C\uC778 \uBAA9\uD45C\uC5D0 \uB9DE\uB294 \uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8\uACFC \uCC4C\uB9B0\uC9C0\uB97C \uCC3E\uC544\uBCF4\uC138\uC694.",
  EXPLORE_LIBRARY: "\uB77C\uC774\uBE0C\uB7EC\uB9AC \uC0B4\uD3B4\uBCF4\uAE30",
  YOUVE_GOT_THIS: "\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4!",
  YOUVE_GOT_THIS_NAME: "\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4, {firstName} \uB2D8!",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "\uAC74\uAC15 \uC5EC\uC815\uC5D0 \uC624\uC2E0 \uAC83\uC744 \uD658\uC601\uD569\uB2C8\uB2E4!",
  HEALTH_JOURNEY_PAGE: "\uAC74\uAC15 \uC5EC\uC815 \uD398\uC774\uC9C0",
  VIEW_DETAILS: "\uC790\uC138\uD55C \uB0B4\uC6A9 \uBCF4\uAE30",
  NEXT_ACTIVITY: "\uB2E4\uC74C \uD65C\uB3D9",
  X_OF_Y_ACTIVITIES: "{completed} / {total} \uD65C\uB3D9",
  X_PERCENTAGE: "{percent}%",
  SOLUTIONS: "\uC194\uB8E8\uC158",
  EXPLORE: "\uD0D0\uC0C9",
  DOCUMENT_NAME_TOO_LONG: "\uCD5C\uB300 \uBB38\uC790 \uC218 \uC81C\uD55C({maximum})\uC744 \uCD08\uACFC\uD588\uC2B5\uB2C8\uB2E4",
  CLAIM_PATIENT_NAME: "\uB300\uC0C1: {patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "\uD504\uB85C\uADF8\uB7A8\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uCC4C\uB9B0\uC9C0\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4. \uC77C\uD68C\uC131 \uD65C\uB3D9\uB9CC \uAC00\uB2A5\uD569\uB2C8\uB2E4.",
  FEEDBACK_LINK_LABEL: "\uC815\uBCF4\uC758 \uC720\uC6A9\uC131 \uD3C9\uAC00({maximum} \uC911 {value})",
  RECENT_ACHIEVEMENTS: "\uCD5C\uADFC \uC131\uACFC",
  ACHIEVEMENTS_BANNER_HEADER: "\uBC30\uC9C0\uB97C \uD68D\uB4DD\uD55C \uAC83\uC744 \uCD95\uD558\uD569\uB2C8\uB2E4!",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "\uC5EC\uB7EC\uBD84\uC774 \uAC74\uAC15\uACFC \uC6F0\uBE59\uC744 \uCD5C\uC6B0\uC120\uC73C\uB85C \uC0DD\uAC01\uD558\uB294 \uBAA8\uC2B5\uC744 \uBCF4\uACE0 \uC2F6\uC2B5\uB2C8\uB2E4! \uADC0\uD558\uAC00 \uC774\uB8E8\uACE0\uC790 \uB178\uB825\uD558\uACE0 \uC788\uB294 \uB2E4\uB978 \uC5C5\uC801\uC744 \uD655\uC778\uD574 \uBCF4\uC138\uC694.",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "\uAC74\uAC15\uACFC \uC6F0\uBE59\uC744 \uC6B0\uC120\uC2DC\uD558\uB294 \uC5EC\uB7EC\uBD84\uC758 \uBAA8\uC2B5\uC744 \uBCF4\uACE0 \uC2F6\uC2B5\uB2C8\uB2E4! \uADC0\uD558\uAC00 \uB2EC\uC131\uD55C \uB2E4\uB978 \uC5C5\uC801\uACFC \uD604\uC7AC \uB2EC\uC131\uD558\uACE0\uC790 \uD558\uB294 \uC5C5\uC801\uC744 \uD655\uC778\uD574 \uBCF4\uC138\uC694.",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "\uC544\uC9C1 \uC131\uACFC\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4!",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "\uC5EC\uC815\uC5D0\uC11C \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uC5EC \uC5C5\uC801\uC744 \uD68D\uB4DD\uD558\uC138\uC694.",
  HISTORY_EMPTY_STATE_HEADER: "\uC544\uC9C1 \uC544\uBB34\uAC83\uB3C4 \uC5C6\uC2B5\uB2C8\uB2E4 - \uC544\uC9C1!",
  HISTORY_EMPTY_STATE_DESCRIPTION: "\uD65C\uB3D9, \uD504\uB85C\uADF8\uB7A8 \uB610\uB294 \uCC4C\uB9B0\uC9C0\uB97C \uC644\uB8CC\uD558\uBA74 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  INDIVIDUAL_BALANCE: "{membersFullName}\uC758 {balance}",
  FAMILY_BALANCE: "\uAC00\uC871 {balance}",
  UPCOMING: "\uC608\uC815",
  REMOVED: "\uC81C\uAC70\uB428",
  COMPLETED_TODAY: "\uC644\uB8CC",
  CLAIMS_BENEFIT_CARD_TITLE: "\uBCF4\uD5D8 \uCCAD\uAD6C",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "\uCE58\uB8CC\uC5D0 \uB300\uD55C \uC9C0\uBD88\uD574\uC57C \uD560 \uAE08\uC561\uACFC \uBCF4\uD5D8\uC774 \uBCF4\uC7A5\uD558\uB294 \uAE08\uC561\uC744 \uCD94\uC801\uD558\uC138\uC694",
  CLAIMS_FILTER_PROPERTY_TYPE: "\uC11C\uBE44\uC2A4 \uC720\uD615",
  CLAIMS_FILTER_PROPERTY_STATUS: "\uCCAD\uAD6C \uC0C1\uD0DC",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "\uD68C\uC6D0",
  CLAIMS_FILTER_VALUE_ORAL: "\uCE58\uACFC",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "\uC758\uB8CC",
  CLAIMS_FILTER_VALUE_VISION: "\uBE44\uC804",
  CLAIMS_FILTER_VALUE_PHARMACY: "\uC57D\uAD6D",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "\uC804\uBB38\uAC00",
  CLAIMS_FILTER_VALUE_APPROVED: "\uC800\uD76C\uB294 \uC800\uD76C\uC758 \uBAAB\uC744 \uB2E4\uD569\uB2C8\uB2E4",
  CLAIMS_FILTER_VALUE_PENDING: "\uCC98\uB9AC \uC911",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "\uBD80\uBD84 \uBCF4\uC7A5",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "\uD3EC\uD568\uB418\uC9C0 \uC54A\uC74C",
  CLAIMS_FILTER_VALUE_ADJUSTED: "\uC870\uC815\uB428",
  DRINKS_PER_WEEK: "\uC8FC\uB2F9 \uC74C\uB8CC \uC218",
  OR_SELECT_BETWEEN_THESE: "\uB610\uB294 \uC774 \uC911\uC5D0\uC11C \uC120\uD0DD\uD558\uC138\uC694",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "\uB0A0\uC9DC \uBC94\uC704",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "\uC5F0\uAC04 \uB204\uACC4",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "\uC804\uB144\uB3C4",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "\uC9C0\uB09C 12\uAC1C\uC6D4",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "\uC9C0\uB09C 2\uB144",
  CLAIMS_VIEW_ALL: "\uBAA8\uB450 \uBCF4\uAE30",
  CHALLENGE_TYPE_GROUP_TAG: "\uADF8\uB8F9",
  CHALLENGE_TYPE_SOLO_TAG: "\uC194\uB85C",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "\uADF8\uB8F9 \uCC4C\uB9B0\uC9C0",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "\uC194\uB85C \uCC4C\uB9B0\uC9C0",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "\uB9C8\uC74C\uCC59\uAE40 \uC218\uD589, \uAC74\uAC15\uD55C \uC2DD\uC2B5\uAD00 \uC774\uB2C8\uC154\uD2F0\uBE0C, \uD53C\uD2B8\uB2C8\uC2A4 \uB300\uD68C, \uC2A4\uD2B8\uB808\uC2A4 \uAC10\uC18C \uD65C\uB3D9, \uAC78\uC74C \uC218 \uCC4C\uB9B0\uC9C0 \uB4F1\uC5D0 \uCC38\uC5EC\uD574 \uCC45\uC784\uAC10\uC744 \uAC16\uACE0 \uB3C4\uC804\uD558\uC138\uC694.",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "\uD63C\uC790 \uB3C4\uC804\uD558\uC5EC \uC2E0\uCCB4 \uAC74\uAC15, \uC601\uC591, \uC815\uC2E0 \uAC74\uAC15, \uC2A4\uD2B8\uB808\uC2A4 \uAD00\uB9AC \uB4F1\uC5D0 \uCD08\uC810\uC744 \uB9DE\uCD98 \uD65C\uB3D9 \uADF8\uB8F9\uC744 \uC644\uC218\uD574 \uBCF4\uC138\uC694.",
  HELPFUL_LINKS: "\uC720\uC6A9\uD55C \uB9C1\uD06C",
  HOW_CLAIMS_WORK: "\uCCAD\uAD6C \uC791\uB3D9 \uBC29\uC2DD",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "\uCCAD\uAD6C \uC791\uB3D9 \uBC29\uC2DD \uC774\uD574",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>\uB124\uD2B8\uC6CC\uD06C\uC5D0 \uC788\uB294 \uC758\uC0AC \uC911 \uD55C \uBA85\uC744 \uBC29\uBB38\uD558\uBA74 \uC9C0\uBD88\uC744 \uC694\uCCAD\uD569\uB2C8\uB2E4. \uBCF8\uC778 \uBD80\uB2F4\uAE08\uC774 \uC788\uB294 \uACBD\uC6B0 \uBCF4\uD5D8\uC0AC\uC5D0\uC11C \uC774\uB97C \uC9D5\uC218\uD55C \uD6C4 \uBCF4\uD5D8 \uD68C\uC0AC\uC5D0 \uCCAD\uAD6C\uB97C \uC81C\uCD9C\uD569\uB2C8\uB2E4.</p><p>\uADF8\uB7F0 \uB2E4\uC74C \uC218\uD61C \uACC4\uC57D\uC774\uB77C\uACE0\uB3C4 \uD558\uB294 \uBCF4\uD5D8 \uD50C\uB79C\uC744 \uAC80\uD1A0\uD558\uC5EC \uC11C\uBE44\uC2A4\uAC00 \uBCF4\uC7A5\uB418\uB294\uC9C0 \uD655\uC778\uD569\uB2C8\uB2E4. \uADF8\uB807\uB2E4\uBA74 \uD61C\uD0DD \uACC4\uC57D \uC870\uAC74\uC5D0 \uB530\uB77C \uC2B9\uC778\uD569\uB2C8\uB2E4. \uBCF5\uB9AC\uD6C4\uC0DD \uC81C\uB3C4\uC758 \uC77C\uBD80\uB85C \uBCF4\uC7A5\uB418\uC9C0 \uC54A\uB294 \uACBD\uC6B0 \uC11C\uBE44\uC2A4 \uBE44\uC6A9\uC744 \uC9C0\uBD88\uD560 \uCC45\uC784\uC740 \uADC0\uD558\uC5D0\uAC8C \uC788\uC2B5\uB2C8\uB2E4.</p>",
  CLAIMS_STATUS_DEFINITIONS: "\uCCAD\uAD6C \uC0C1\uD0DC \uC815\uC758",
  CLAIMS_STATUS_DEFINITION_APPROVED: "\uB2F9\uC0AC\uAC00 \uBE44\uC6A9\uC744 \uC9C0\uBD88\uD55C\uB2E4\uB294 \uAC83\uC740 \uCCAD\uAD6C\uAC00 \uC2B9\uC778\uB418\uACE0 \uADC0\uD558\uC758 \uD50C\uB79C\uC5D0 \uB530\uB77C \uCCAD\uAD6C \uAE08\uC561\uC758 \uC77C\uBD80 \uB610\uB294 \uC804\uBD80\uB97C \uB2F9\uC0AC\uAC00 \uC9C0\uBD88\uD558\uB294 \uB370 \uB3D9\uC758\uD55C\uB2E4\uB294 \uC758\uBBF8\uC785\uB2C8\uB2E4.",
  CLAIMS_STATUS_DEFINITION_PENDING: "\uCC98\uB9AC\uD55C\uB2E4\uB294 \uAC83\uC740 \uB204\uAC00 \uBB34\uC5C7\uC744 \uC9C0\uBD88\uD574\uC57C \uD558\uB294\uC9C0 \uD30C\uC545\uD55C\uB2E4\uB294 \uC758\uBBF8\uC785\uB2C8\uB2E4.",
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: "\uBD80\uBD84 \uBCF4\uC7A5\uC740 \uCCAD\uAD6C \uAE08\uC561\uC758 \uC77C\uBD80\uB97C \uBCF4\uC0C1\uD55C\uB2E4\uB294 \uC758\uBBF8\uC785\uB2C8\uB2E4.",
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: "\uBCF4\uC7A5\uB418\uC9C0 \uC54A\uB294\uB2E4\uB294 \uAC83\uC740 \uD574\uB2F9 \uC11C\uBE44\uC2A4\uAC00 \uADC0\uD558\uC758 \uD61C\uD0DD \uD50C\uB79C\uC5D0 \uD3EC\uD568\uB418\uC9C0 \uC54A\uC544\uC11C \uADC0\uD558\uAC00 \uBE44\uC6A9\uC744 \uC9C0\uBD88\uD574\uC57C \uD55C\uB2E4\uB294 \uAC83\uC744 \uC758\uBBF8\uD569\uB2C8\uB2E4.",
  CLAIMS_STATUS_DEFINITION_ADJUSTED: "\uC870\uC815\uC774\uB780 \uC5EC\uAE30\uC11C \uBB34\uC5B8\uAC00\uAC00 \uBCC0\uACBD\uB418\uC5C8\uB2E4\uB294 \uC758\uBBF8\uC774\uBBC0\uB85C, \uC6B0\uB9AC\uB294 \uC774 \uC8FC\uC7A5\uC744 \uB2E4\uC2DC \uC0B4\uD3B4\uBD10\uC57C \uD588\uC2B5\uB2C8\uB2E4.",
  YOUR_COST: "\uBE44\uC6A9:",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {\uC758\uB8CC \uCCAD\uAD6C} oral {\uCE58\uACFC \uCCAD\uAD6C} vision {\uC548\uACFC \uCCAD\uAD6C} pharmacy {\uC57D\uAD6D \uCCAD\uAD6C} professional {\uC804\uBB38\uAC00 \uCCAD\uAD6C} other {\uCCAD\uAD6C}}",
  FILTER_BY: "\uD544\uD130\uB9C1 \uAE30\uC900",
  CLAIMS_LIST_EMPTY_HEADING: "\uC544\uC9C1 \uC544\uBB34\uAC83\uB3C4 \uC5C6\uC2B5\uB2C8\uB2E4!",
  CLAIMS_LIST_EMPTY_MESSAGE: "\uC81C\uCD9C\uB41C \uCCAD\uAD6C\uB294 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  CLAIMS_GENERIC_ERROR_HEADING: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  CLAIMS_GENERIC_ERROR_MESSAGE: "\uC800\uD76C \uCE21\uC5D0\uC11C \uAE30\uC220\uC801\uC778 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.{br} \uBA87 \uBD84 \uD6C4\uC5D0 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "\uADC0\uD558\uC758 \uBCF4\uD5D8\uC5D0\uC11C \uBCF4\uC7A5\uD558\uB294 \uB0B4\uC6A9\uACFC \uADC0\uD558\uAC00 \uC544\uC9C1 \uC758\uB8CC \uC11C\uBE44\uC2A4 \uC81C\uACF5\uC790\uC5D0\uAC8C \uC9C0\uBD88\uD574\uC57C \uD560 \uAE08\uC561\uC744 \uD655\uC778\uD558\uC138\uC694. \uC624\uB298 \uB0A0\uC9DC\uB85C\uBD80\uD130 2\uB144 \uC774\uB0B4\uC5D0 \uC81C\uCD9C\uD55C \uCCAD\uAD6C\uC5D0 \uC561\uC138\uC2A4\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "\uACB0\uACFC \uC5C6\uC74C",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "{filterCount, plural, other {\uC120\uD0DD\uD55C \uD544\uD130\uC640 \uC77C\uCE58\uD558\uB294 \uCCAD\uAD6C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4}}",
  VIEW_MY_PROFILE: "\uB0B4 \uD504\uB85C\uD544 \uBCF4\uAE30",
  PLAN_PROGRESS_COST_COVERAGE: "\uACC4\uD68D \uC9C4\uD589 \uC0C1\uD669 \uBC0F \uBE44\uC6A9",
  PLAN_PROGRESS_POP_UP_LABEL: "{title}(\uC774)\uB780 \uBB34\uC5C7\uC778\uAC00\uC694?",
  RECENT_CLAIMS_LIST_EMPTY: "\uC9C0\uB09C 90\uC77C \uC774\uB0B4\uC5D0 \uC81C\uCD9C\uB41C \uCCAD\uAD6C\uAC00 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4. '\uBAA8\uB450 \uBCF4\uAE30'\uB97C \uC120\uD0DD\uD558\uBA74 \uBAA8\uB4E0 \uCCAD\uAD6C\uC5D0 \uC811\uADFC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. ",
  DOWNLOAD_CLAIMS: "\uCCAD\uAD6C \uB2E4\uC6B4\uB85C\uB4DC",
  PLAIN_SOMETHING_WENT_WRONG: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "\uC18C\uC720\uAD8C \uC8FC\uC7A5\uC744 \uB2E4\uC6B4\uB85C\uB4DC\uD558\uB294 \uB3D9\uC548 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "\uADC0\uD558\uC758 \uD61C\uD0DD, \uBCF4\uD5D8\uCCAD\uAD6C \uBC0F \uAC74\uAC15 \uC815\uBCF4\uAC00 \uBAA8\uB450 \uD55C\uACF3\uC5D0\uC11C \uC81C\uACF5\uB429\uB2C8\uB2E4.",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "\uC2DC\uC791\uD560 \uC900\uBE44\uAC00 \uB418\uC168\uB098\uC694?",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "\uADC0\uD558\uC758 \uAC1C\uC778\uC801 \uBAA9\uD45C\uC5D0 \uB9DE\uB294 \uAC74\uAC15 \uD504\uB85C\uADF8\uB7A8\uACFC \uCC4C\uB9B0\uC9C0\uB97C \uCC3E\uC73C\uB824\uBA74 \uB77C\uC774\uBE0C\uB7EC\uB9AC\uB97C \uD0D0\uC0C9\uD574 \uBCF4\uC138\uC694.",
  USER_DROPDOWN_BTN_LABEL: "\uD504\uB85C\uD544 \uBA54\uB274",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "\uC790\uAE30 \uBD80\uB2F4\uAE08\uACFC \uACF5\uB3D9 \uBCF4\uD5D8\uC774\uB780 \uBB34\uC5C7\uC778\uAC00\uC694?",
  ANSWERS_NOT_SAVED: "\uC9C0\uAE08\uC740 \uB2F5\uBCC0\uC744 \uC800\uC7A5\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB098\uC911\uC5D0 \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC2ED\uC2DC\uC624",
  SESSION_ABOUT_TO_TIMEOUT: "\uC77C\uC815 \uC2DC\uAC04 \uB3D9\uC548 \uC544\uBB34\uB7F0 \uD65C\uB3D9\uC774 \uC5C6\uC5C8\uAE30 \uB54C\uBB38\uC5D0 \uC138\uC158 \uC2DC\uAC04\uC774 \uACE7 \uCD08\uACFC\uB429\uB2C8\uB2E4. \uC138\uC158\uC744 \uACC4\uC18D\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "\uC77C\uC815 \uC2DC\uAC04 \uB3D9\uC548 \uC544\uBB34\uB7F0 \uD65C\uB3D9\uC774 \uC5C6\uC5C8\uAE30 \uB54C\uBB38\uC5D0 \uC138\uC158 \uC2DC\uAC04\uC774 \uACE7 \uCD08\uACFC\uB429\uB2C8\uB2E4.",
  SESSION_TIMEOUT: "\uC138\uC158 \uD0C0\uC784\uC544\uC6C3",
  CONTINUE_SESSION: "\uC138\uC158 \uACC4\uC18D\uD558\uAE30",
  SESSION_LOG_OUT: "\uB85C\uADF8\uC544\uC6C3",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "\uBC30\uC1A1 \uC8FC\uC18C \uC120\uD0DD",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "\uC0C8 \uCE74\uB4DC\uAC00 \uD544\uC694\uD55C \uC0AC\uB78C\uC740 \uB204\uAD6C\uC778\uAC00\uC694?",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "\uD55C \uBA85 \uC774\uC0C1\uC758 \uBA64\uBC84\uB97C \uC120\uD0DD\uD558\uC138\uC694.",
  DEFAULT_ADDRESS: "\uAE30\uBCF8 \uC8FC\uC18C",
  CHOOSE_NEW_ADDRESS: "\uC0C8 \uC8FC\uC18C \uC120\uD0DD",
  CHOOSE_NEW_ADDRESS_HINT: "\uCE74\uB4DC \uBC30\uC1A1\uC744 \uC704\uD574 \uB2E4\uB978 \uC6B0\uD3B8 \uC8FC\uC18C\uB97C \uC785\uB825\uD569\uB2C8\uB2E4. \uC774\uB807\uAC8C \uD574\uB3C4 \uACC4\uC815\uC758 \uAE30\uBCF8 \uC8FC\uC18C\uB294 \uBCC0\uACBD\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  CARD_ACTIONS: "\uCE74\uB4DC \uB3D9\uC791",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "\uC774 \uCC4C\uB9B0\uC9C0\uC758 \uBAA8\uB4E0 \uD65C\uB3D9\uC744 \uC644\uB8CC\uD558\uBA74 \uC5C5\uC801\uC744 \uC5BB\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "\uC544\uC9C1 \uC544\uBB34\uAC83\uB3C4 \uC5C6\uC2B5\uB2C8\uB2E4 - \uC544\uC9C1!",
  CHALLENGE_ACTIVITY_UP_NEXT: "\uB2E4\uC74C\uC740",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "\uCC4C\uB9B0\uC9C0\uAC00 \uC2DC\uC791\uB418\uBA74 \uB2E4\uC2DC \uB3CC\uC544\uC624\uC138\uC694!",
  CHALLENGE_ACTIVITY_COMPLETED: "\uC644\uB8CC",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "\uC644\uB8CC\uD55C \uD65C\uB3D9\uC774 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  CHALLENGE_ACTIVITY_MISSED: "\uB193\uCCE4\uB2E4",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "\uB193\uCE5C \uD65C\uB3D9\uC774 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  CHALLENGE_COMPLETED_TITLE: "\uC774\uC81C \uB05D\uB0AC\uC2B5\uB2C8\uB2E4!",
  CHALLENGE_COMPLETED_BODY: "\uB354 \uC774\uC0C1 \uC644\uB8CC\uD560 \uD65C\uB3D9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uC798\uD588\uC5B4!",
  ACHIEVEMENT_EARNED: "\uC5C5\uC801 \uD68D\uB4DD \uCD95\uD558",
  ACHIEVEMENT_PROGRESS: "\uC131\uCDE8 \uC9C4\uD589 \uC0C1\uD669",
  ADD_DATA: "\uB370\uC774\uD130 \uCD94\uAC00",
  CONFIRM_DETAILS: "\uC138\uBD80 \uC815\uBCF4 \uD655\uC778",
  MEDICAL_ID_CARD: "\uC758\uB8CC ID \uCE74\uB4DC",
  NEW_ADDRESS: "\uC0C8 \uBC30\uC1A1 \uC8FC\uC18C",
  MESSAGE_CENTER: "\uBA54\uC2DC\uC9C0 \uC13C\uD130",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "\uBA54\uC2DC\uC9D5",
  CHOOSE_NEW_ADDRESS_DESC: "\uCE74\uB4DC \uBC30\uC1A1\uC744 \uC704\uD574 \uB2E4\uB978 \uC6B0\uD3B8 \uC8FC\uC18C\uB97C \uC785\uB825\uD569\uB2C8\uB2E4. \uC774\uB807\uAC8C \uD574\uB3C4 \uACC4\uC815\uC758 \uAE30\uBCF8 \uC8FC\uC18C\uB294 \uBCC0\uACBD\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. \uC6B0\uD3B8 \uC8FC\uC18C\uB294 \uBBF8\uAD6D \uB0B4\uC5D0 \uC788\uC5B4\uC57C \uD569\uB2C8\uB2E4.",
  SELECT_STATE: "\uC8FC\uB97C \uC120\uD0DD\uD558\uC138\uC694",
  REQUIRED_FIELD: "{field}\uC740(\uB294) \uD544\uC218 \uC785\uB825 \uD56D\uBAA9\uC785\uB2C8\uB2E4",
  CLAIMS_YOUR_COST: "\uBE44\uC6A9",
  CLAIMS_PROCESSING_DESCRIPTION: "\uC774 \uCCAD\uAD6C\uB294 \uC544\uC9C1 \uCC98\uB9AC \uC911\uC785\uB2C8\uB2E4. \uAC80\uD1A0\uD560 \uC900\uBE44\uAC00 \uB418\uBA74 \uC54C\uB9BC\uC744 \uBC1B\uAC8C \uB429\uB2C8\uB2E4.",
  DOWNLOAD_BENEFITS_EXPLANATION: "\uD61C\uD0DD \uC124\uBA85 \uB2E4\uC6B4\uB85C\uB4DC",
  DATA_COLLECTED: "\uC218\uC9D1\uB41C \uB370\uC774\uD130 {date}",
  MEMBER_CARD_ON_ITS_WAY: "\uC0C8 \uD68C\uC6D0 ID \uCE74\uB4DC\uAC00 \uBC30\uC1A1 \uC911\uC785\uB2C8\uB2E4.",
  MEMBER_CARD_ARRIVAL: "\uC0C8 \uD68C\uC6D0 ID \uCE74\uB4DC\uB294 \uC601\uC5C5\uC77C \uAE30\uC900 \uC57D 10\uC77C \uD6C4\uC5D0 \uB3C4\uCC29\uD569\uB2C8\uB2E4.",
  TRY_LATER: "\uB098\uC911\uC5D0 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
  RESUBMIT: "\uB2E4\uC2DC \uC81C\uCD9C",
  ORDER_NEW_CARD_HEADING: "\uC0C8 \uCE74\uB4DC \uC8FC\uBB38",
  CANCEL_ORDER_NEW_CARD_TITLE: "\uC8FC\uBB38\uC744 \uCDE8\uC18C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
  CANCEL_ORDER_NEW_CARD_DESC: "\uB098\uC911\uC5D0 \uC5B8\uC81C\uB4E0\uC9C0 \uB2E4\uC2DC \uB3CC\uC544\uC62C \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
  CANCEL_ORDER: "\uC8FC\uBB38 \uCDE8\uC18C",
  STEP: "\uB2E8\uACC4",
  ORDER_CARD: "\uC8FC\uBB38 \uCE74\uB4DC",
  PLEASE_MAKE_A_SELECTION: "\uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
  TOTAL_COST_OVERVIEW: "\uCD1D \uBE44\uC6A9 \uAC1C\uC694",
  TOTAL_COST_DESCRIPTION: "\uCCAD\uAD6C \uC11C\uBE44\uC2A4\uC5D0\uC11C \uBC1C\uC0DD\uD55C \uBE44\uC6A9\uC758 \uD569\uACC4\uC785\uB2C8\uB2E4.",
  SUPPORT: "\uC9C0\uC6D0",
  GET_SUPPORT: "\uC9C0\uC6D0 \uBC1B\uAE30",
  MESSAGE_WITH_A_MEMBER: "\uADC0\uD558\uC758 \uCCAD\uAD6C\uC5D0 \uB300\uD55C \uC9C8\uBB38\uC774 \uC788\uC73C\uBA74 \uD68C\uC6D0 \uB3C4\uC6B0\uBBF8\uC5D0\uAC8C \uBB38\uC758\uD558\uC138\uC694.",
  CLAIMS_SERVICE_DETAILS_HEADING: "\uC11C\uBE44\uC2A4 \uC138\uBD80 \uC815\uBCF4",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "\uCD94\uAC00 \uC790\uB8CC",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "\uADC0\uD558\uAC00 \uBC1B\uC740 \uAC1C\uBCC4 \uC11C\uBE44\uC2A4\uC5D0 \uB300\uD55C \uC138\uBD80 \uB0B4\uC6A9\uACFC \uD574\uB2F9 \uC11C\uBE44\uC2A4\uC640 \uAD00\uB828\uB41C \uBE44\uC6A9\uC785\uB2C8\uB2E4.",
  CLAIMS_SERVICE_DETAILS_SERVICE: "\uC11C\uBE44\uC2A4",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "Rx \uC774\uB984",
  CLAIMS_SERVICE_DETAILS_DATE: "\uC11C\uBE44\uC2A4 \uB0A0\uC9DC",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "\uC808\uCC28 \uCF54\uB4DC",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "Rx \uBC88\uD638",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "\uBE44\uC6A9",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "\uBE44\uC6A9 \uC138\uBD80 \uC815\uBCF4",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "\uBE44\uC6A9 \uC138\uBD80 \uC815\uBCF4 \uD45C\uC2DC",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "\uBE44\uC6A9 \uC138\uBD80 \uC815\uBCF4 \uC228\uAE30\uAE30",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "\uBE44\uC6A9 \uB0B4\uC5ED",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "\uC544\uB798 \uAE08\uC561\uC740 \uD50C\uB79C \uC9C4\uD589\uC5D0 \uC0AC\uC6A9\uB429\uB2C8\uB2E4",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "\uACC4\uC18D\uD558\uB824\uBA74 \uD55C \uBA85 \uC774\uC0C1\uC758 \uAD6C\uC131\uC6D0\uC744 \uC120\uD0DD\uD558\uC138\uC694",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "\uD544\uB4DC\uC5D0 \uC798\uBABB\uB41C \uBB38\uC790\uAC00 \uC788\uC2B5\uB2C8\uB2E4",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "\uACF5\uAE09\uC790\uAC00 \uC694\uAE08\uC744 \uCCAD\uAD6C\uD568",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "\uAE30\uD0C0 \uBCF4\uD5D8 \uC9C0\uBD88",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "\uD68C\uC6D0 \uD560\uC778",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "\uADC0\uD558\uC758 \uD50C\uB79C\uC740 \uACF5\uAE09\uC5C5\uCCB4\uC640 \uD611\uC0C1\uD558\uC5EC \uD560\uC778\uB41C \uAC00\uACA9\uC73C\uB85C \uC81C\uACF5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "\uD68C\uC6D0 \uD560\uC778 \uC124\uBA85",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "\uB2E4\uB978 \uBCF4\uD5D8\uC5D0\uC11C \uC9C0\uBD88\uD558\uB294 \uAE08\uC561\uC785\uB2C8\uB2E4.",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: "\uAE30\uD0C0 \uBCF4\uD5D8 \uC9C0\uAE09 \uC124\uBA85",
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {{paymentDate} \uD50C\uB79C \uC9C0\uBD88\uC561} undefined {\uB0B4 \uD50C\uB79C \uC9C0\uBD88\uC561}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "\uADC0\uD558\uC758 \uBCF4\uD5D8 \uD50C\uB79C\uC5D0\uC11C \uC758\uB8CC \uC11C\uBE44\uC2A4\uC5D0 \uB300\uD574 \uBCF4\uC7A5\uD558\uB294 \uAE08\uC561\uC785\uB2C8\uB2E4.",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: "\uADC0\uD558\uC758 \uD50C\uB79C\uC5D0 \uB300\uD55C \uC9C0\uBD88 \uC124\uBA85",
  CLAIMS_COST_DETAILS_YOUR_COST: "\uBE44\uC6A9",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "\uC11C\uBE44\uC2A4 \uBE44\uACE0",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "\uB124\uD2B8\uC6CC\uD06C \uB0B4",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "\uB124\uD2B8\uC6CC\uD06C \uC678\uBD80",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "\uACC4\uC18D\uD558\uB824\uBA74 \uC8FC\uC18C \uC635\uC158\uC744 \uC120\uD0DD\uD558\uC2ED\uC2DC\uC624",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "\uB3C4\uC2DC\uB294 \uC22B\uC790\uB97C \uD3EC\uD568\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  MESSAGE_CLOSED_TEXT: "\uC774 \uBA54\uC2DC\uC9C0\uAC00 \uC804\uB2EC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "\uB204\uC801 \uBE44\uC6A9, \uBE44\uC6A9 \uBD84\uB2F4\uAE08 \uB4F1\uC5D0 \uB300\uD55C \uAC1C\uC694\uB97C \uD655\uC778\uD558\uC138\uC694.",
  VIEW_PLAN_PROGRESS: "\uACC4\uD68D \uC9C4\uD589 \uC0C1\uD669 \uBCF4\uAE30",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "\uADC0\uD558\uB294 \uC11C\uBE44\uC2A4 \uC81C\uACF5\uC790\uB85C\uBD80\uD130 \uC774 \uCCAD\uAD6C\uC640 \uAD00\uB828\uB41C \uCCAD\uAD6C\uC11C\uB97C \uBC1B\uAC8C \uB429\uB2C8\uB2E4.",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "\uD61C\uD0DD \uC124\uBA85\uC744 \uB2E4\uC6B4\uB85C\uB4DC\uD558\uB294 \uB3D9\uC548 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
  END_CHAT_MODAL_DESCRIPTION: "\uACC4\uC18D\uD558\uBA74 \uCC44\uD305\uC774 \uC885\uB8CC\uB429\uB2C8\uB2E4. \uC55E\uC73C\uB85C\uB294 \uC774 \uB300\uD654\uB85C \uB2E4\uC2DC \uB3CC\uC544\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
  RETURN_TO_CHAT: "\uCC44\uD305\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
  CHATS_IN_CONVERSATION: "\uB300\uD654 \uC18D\uC758 \uCC44\uD305",
  LIVE_CHAT_CHARACTER_LIMIT: "\uBB38\uC790 \uC81C\uD55C\uC744 \uCD08\uACFC\uD588\uC2B5\uB2C8\uB2E4({characterLimit})",
  CHAT_MESSAGE_INPUT: "\uCC44\uD305 \uBA54\uC2DC\uC9C0 \uC785\uB825",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {\uACF5\uC81C\uC561} non_covered {\uD3EC\uD568\uB418\uC9C0 \uC54A\uC74C} copayment {\uBCF8\uC778 \uBD80\uB2F4\uAE08} coinsurance {\uACF5\uB3D9 \uBCF4\uD5D8} may_be_billed {\uCCAD\uAD6C \uAC00\uB2A5} amount_exceeding_maximum {\uCD5C\uB300 \uCD08\uACFC \uAE08\uC561} additional_member_liability {\uCD94\uAC00 \uD68C\uC6D0 \uCC45\uC784} special_processing {\uD2B9\uC218 \uCC98\uB9AC} paid_to_member {\uD68C\uC6D0\uC5D0\uAC8C \uC9C0\uBD88} plan_paid_to_you {\uADC0\uD558\uC5D0\uAC8C \uC9C0\uBD88\uB41C \uD50C\uB79C} other {\uAE30\uD0C0}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "\uC0C8 \uBA54\uC2DC\uC9C0",
  QUESTIONNAIRE_FLOW_CLOSE: "\uB2EB\uAE30",
  QUESTIONNAIRE_FLOW_GO_BACK: "\uB4A4\uB85C \uAC00\uAE30",
  QUESTIONNAIRE_FLOW_CONTINUE: "\uACC4\uC18D\uD558\uAE30",
  QUESTIONNAIRE_FLOW_RETRY: "\uB2E4\uC2DC \uC2DC\uB3C4",
  CLEAR_ALL: "\uBAA8\uB450 \uC9C0\uC6B0\uAE30",
  HEALTH_REPORT_PAGE_HEADING: "\uB0B4 \uAC74\uAC15 \uBCF4\uACE0\uC11C",
  USERNAME_HAS_JOINED_THE_CHAT: "{username}\uC774(\uAC00) \uCC44\uD305\uC5D0 \uCC38\uC5EC\uD588\uC2B5\uB2C8\uB2E4",
  USERNAME_HAS_LEFT_THE_CHAT: "{username}\uC774(\uAC00) \uCC44\uD305\uC744 \uB098\uAC14\uC2B5\uB2C8\uB2E4",
  PERVIOUS_MONTH: "\uC9C0\uB09C \uB2EC \uBC84\uD2BC",
  NEXT_MONTH: "\uB2E4\uC74C \uB2EC \uBC84\uD2BC",
  CHAT_CLOSED: "\uC774 \uCC44\uD305\uC740 \uC885\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
  CONNECTING_YOU_WITH_AN_AGENT: "\uC0C1\uB2F4\uC6D0\uACFC \uC5F0\uACB0 \uC911...",
  LAST_DAY_TO_USE_BALANCE: "\uC794\uC561 \uC0AC\uC6A9 \uB9C8\uC9C0\uB9C9 \uB0A0",
  LAST_DAY_TO_SUBMIT_CLAIMS: "\uCCAD\uAD6C \uC81C\uCD9C \uB9C8\uC9C0\uB9C9 \uB0A0",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "\uB2EB\uAE30",
  LIVE_CHAT_LANDING_PAGE_TITLE: "\uCC44\uD305",
  LIVE_CHAT_DELIVERY_FAILURE: "\uBC30\uC1A1\uB418\uC9C0 \uC54A\uC74C",
  LIVE_CHAT_RESEND_MESSAGE: "\uBA54\uC2DC\uC9C0 \uC7AC\uC804\uC1A1",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "\uBA54\uC2DC\uC9C0\uB97C \uBCF4\uB0B4\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uADC0\uD558\uC758 \uBA54\uC2DC\uC9C0\uAC00 \uC800\uD76C \uD300\uC5D0 \uC804\uB2EC\uB418\uC9C0 \uC54A\uC740 \uAC83 \uAC19\uC2B5\uB2C8\uB2E4.",
  VIEW_TOOLTIP: "{tooltipLabel} \uD234\uD301 \uBCF4\uAE30",
  NO_DATA_SOURCES: "\uB370\uC774\uD130 \uC18C\uC2A4 \uC5C6\uC74C",
  MAX_VALUE_ERROR_MESSAGE: "{max} \uC774\uD558\uC758 \uAC12\uC744 \uC785\uB825\uD558\uC138\uC694",
  MIN_VALUE_ERROR_MESSAGE: "{min} \uC774\uC0C1\uC758 \uAC12\uC744 \uC785\uB825\uD558\uC138\uC694",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "\uAC12\uC744 \uC120\uD0DD\uD558\uC138\uC694",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "\uC2DC\uC791 \uB0A0\uC9DC\uB294 \uC885\uB8CC \uB0A0\uC9DC\uBCF4\uB2E4 \uBE68\uB77C\uC57C \uD569\uB2C8\uB2E4",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "\uAE30\uAC04\uC740 24\uC2DC\uAC04\uC744 \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  NO_FUTURE_DATES: "\uB0A0\uC9DC\uB294 \uBBF8\uB798\uAC00 \uB420 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  CLAIMS_REMOVE_FILTER: "{label} \uD544\uD130\uB97C \uC81C\uAC70\uD569\uB2C8\uB2E4.",
  REMOVE_X_FILTER: "{label} \uD544\uD130\uB97C \uC81C\uAC70\uD569\uB2C8\uB2E4.",
  WALLET_BENEFIT_STATUS_PENDING: "\uBCF4\uB958 \uC911",
  WALLET_BENEFIT_STATUS_ACTIVE: "\uD65C\uC131 \uC0C1\uD0DC",
  WALLET_BENEFIT_STATUS_PAST: "\uACFC\uAC70",
  CLAIMS_CLAIMS_LIST_HEADING: "\uB0B4 \uCCAD\uAD6C",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "\uC544\uC9C1 \uC544\uBB34\uAC83\uB3C4 \uC5C6\uC2B5\uB2C8\uB2E4!",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "\uD65C\uC131 \uC0C1\uD0DC\uC778 \uD61C\uD0DD\uC740 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "\uD61C\uD0DD",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "\uD61C\uD0DD - ID \uCE74\uB4DC",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "\uCCAD\uAD6C",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "\uCCAD\uAD6C \uC138\uBD80 \uC815\uBCF4",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "\uCCAD\uAD6C \uC138\uBD80 \uC815\uBCF4 - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "\uBCF4\uB958 \uC911",
  CLAIMS_STATUS_PENDING: "\uBCF4\uB958 \uC911",
  CSE_END_IMPERSONATION_MODE: "\uC0AC\uCE6D \uBAA8\uB4DC \uC885\uB8CC",
  CSE_RESTRICTED_ACTION: "\uC81C\uD55C\uB41C \uD589\uB3D9",
  CSE_RESTRICTED_ACTION_MESSAGE: "\uC0AC\uC6A9\uC790 \uC2DC\uBBAC\uB808\uC774\uC158 \uBAA8\uB4DC\uC5D0\uC11C\uB294 \uD68C\uC6D0\uC758 \uD3EC\uD138\uB9CC \uBCFC \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uD68C\uC6D0\uC744 \uB300\uB9AC\uD558\uB294 \uD589\uC704\uB294 \uD5C8\uC6A9\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  CSE_RESTRICTED_VIEW_MESSAGE: "\uC774 \uC139\uC158\uC5D0 \uC811\uADFC\uD560 \uC218 \uC788\uB294 \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  CSE_IMPERSONATION_MASONRY_HEADER: "\uC9C0\uC6D0 \uC694\uC6D0\uC744 \uC704\uD55C \uC81C\uD55C\uB41C \uCF58\uD150\uCE20",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "\uD2B9\uC815 \uC704\uC82F\uC774\uB098 \uCF58\uD150\uCE20\uB97C \uBCFC \uC218 \uC788\uB294 \uAD8C\uD55C\uC774 \uC5C6\uC744 \uC218\uB3C4 \uC788\uC2B5\uB2C8\uB2E4.",
  CSE_IMPERSONATION_BANNER_TEXT: "{memberName}\uC5D0 \uB300\uD55C \uBCF4\uAE30 \uC804\uC6A9 \uBAA8\uB4DC\uC785\uB2C8\uB2E4.",
  CLAIMS_SUMMARY_PAGE_HEADING: "\uCCAD\uAD6C",
  HEALTH_INSURANCE_GLOSSARY: "\uAC74\uAC15 \uBCF4\uD5D8 \uC6A9\uC5B4\uC9D1",
  CLAIMS_FILTER_LOADING_TEXT: "\uD0ED \uBC0F \uD544\uD130\uAC00 \uB85C\uB4DC \uC911\uC785\uB2C8\uB2E4",
  ANDROID_HEALTH_CONNECT: "Health Connect",
  GET_CARE_SERVICE_CARD_CTA: "\uC11C\uBE44\uC2A4 \uBCF4\uAE30",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "\uB2E4\uC74C \uB2E8\uACC4",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "\uC774\uC804 \uB2E8\uACC4",
  HEIGHT: "\uB192\uC774",
  WEIGHT: "\uBB34\uAC8C"
};

// src/strings/fil.json
var fil_default = {
  COMPLETED_ACTIVITIES: "Mga Natapos na Aktibidad",
  AVAILABLE: "Available",
  SEE_MORE_ACTIVITIES: "Tumingin Pa",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "Tumingin Pa ng Mga Natapos na Aktibidad",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "Kapag natapos mo ang mga aktibidad, lalabas ang mga iyon dito!",
  MISSED_ACTIVITIES: "Mga Napalampas na Aktibidad",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "Tumingin Pa ng Mga Napalampas na Aktibidad",
  MISSED_ACTIVITIES_EMPTY_STATE: "Kapag may napalampas kang mga aktibidad, lalabas ang mga iyon dito!",
  DISPLAYING_ACTIVITIES_FROM_DATE: "Ipinapakita ang mga aktibidad mula noong {date}",
  STANDALONE_ACTIVITIES: "Mga Standalone na Aktibidad",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "Wala pang gagawin dito! Bumalik ka mamaya.",
  VIEW_ALL_STANDALONE_ACTIVITIES: "Tingnan Ang Lahat ng Standalone na Aktibidad",
  MODULE_TEMPLATE_BUTTON: "Button ng Template ng Module",
  ACHIEVEMENTS: "Mga achievement",
  VIEW_ACHIEVEMENTS: "Tingnan ang mga achievement",
  DEPENDENT_NOTIFICATION_MESSAGE: "Maaaring mag-imbita ng mga bagong dependent",
  DEPENDENT_COVERED_BY_HSA_ONLY: "Tandaan: Sasagutin lang ang dependent na ito sa ilalim ng iyong Health Spending Account.",
  WALLET_LANDING_NAV_LABEL: "Pag-Navigate sa Wallet",
  VIEW_CARD_FOR_DEPENDENT: "Tingnan ang card para kay {dependentName}",
  LEARN_MORE_LEAGUE_FAMILY: "tungkol sa League Family para sa iyong mga dependent",
  GO_TO_LIFEMARKET: "Pumunta sa Lifemarket para matingnan ang numero ng order {invoiceId}",
  GO_TO_CLAIM: "Tingnan ang mga detalye ng claim para sa {title}",
  GO_TO_RECEIPT: "Tingnan ang resibo para sa {title}",
  LINK_TO_TRANSACTION_DETAILS: "Link sa mga detalye",
  VIEW_CLAIM_LABEL_HIDDEN: "Tingnan ang {vendor} claim {referenceId}",
  QLE_LINK_LABEL: "Alamin pa ang tungkol sa mga kwalipikadong kaganapan sa buhay",
  PLEASE_CALL: "Tumawag sa {phoneNumber}",
  BROWSE_FILES: "Mag-browse ng Mga File",
  TOTAL_BALANCE: "Kabuuang Balanse",
  LIFEMARKET: "Lifemarket",
  ABOUT: "Tungkol sa",
  LEAGUE_CREDIT_DESCRIPTION_1: `Maaari mong gastusin ang League Credit sa {lifemarketLink} sa iba't ibang uri ng istilo ng pamumuhay at mga produkto at serbisyo para sa mabuting kalusugan. Ang tanging bagay na hindi mo puwedeng pagkagastusan nito ay anumang bagay na may markang "League Partner Offer" (Alok ng League Partner).`,
  LEAGUE_CREDIT_DESCRIPTION_2: 'Para magamit ang iyong League Credit, piliin ang " League Credit " mula sa listahan ng mga opsyon sa pagbabayad kapag nag-checkout ka sa Lifemarket. {learnMoreLink}',
  DID_YOU_GET_A_PROMO_CODE: "Nakakuha Ka Ba ng Promo Code?",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "Kung nakatanggap ka ng credit sa store o promo code, maaari mong ilagay iyon dito para matanggap ang mga credit. Kapag nailagay na, awtomatikong ia-apply ang mga iyon sa iyong account.",
  GET_CARE: "Kumuha ng Pangangalaga",
  DISCLAIMER: "Disclaimer",
  JOINED: "Sumali",
  ROLLED_OVER: "Ipinasa sa Susunod",
  NEED_MORE_INFO_SINGLE_CLAIM: "Kailangan ng iyong claim ang higit pang impormasyon para maproseso. Tingnan ang mga detalye sa ibaba.",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "Kailangan ng iyong mga claim ang higit pang impormasyon para maproseso. Tingnan ang mga detalye sa ibaba.",
  RECENT_CLAIMS: "Mga Claim Kamakailan",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "Kung mayroon kang agarang claim, makipag-ugnayan sa <a>Customer Support</a>.",
  ABOUT_THIS_BENEFIT: "Tungkol sa Benepisyo na ito",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "Ginawang available sa iyo ng iyong employer ang benepisyong ito. Para sa mga tanong o detalyadong impormasyon, makipag-ugnayan sa iyong HR administrator.",
  ID_LIKE_TO_DOTDOTDOT: "Gusto ko sanang ...",
  HAS_DEPENDENTS_DESCRIPTION: "Nakalista sa ibaba ang iyong mga dependent para sa benepisyong ito. Kung may naranasan kang pangyayari sa buhay kamakailan lang, posibleng kwalipikado ka na baguhin ang iyong mga dependent. I-click ang button sa ibaba para matingnan kung kwalipikado ka sa ilang hakbang lang.",
  HAS_NO_DEPENDENTS_DESCRIPTION: "Wala kang anumang dependent na nauugnay sa benepisyo na ito. Kung may naranasan kang pangyayari sa buhay kamakailan lang, posibleng kwalipikado ka na dagdagan o baguhin ang iyong mga dependent. I-click ang button sa ibaba para matingnan kung kwalipikado ka sa ilang hakbang lang.",
  ADD_A_DEPENDENT_CAPITAL: "Magdagdag ng Dependent",
  WALLET_FEEDBACK_TITLE: "Tulungan kaming paggandahin ang experience sa Wallet",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: "Gaano kapaki-pakinabang ang impormasyong ibinigay para sa iyong benepisyong \u201C{benefitName}\u201D?",
  WALLET_FEEDBACK_SUBTITLE_OTHER: "Gaano kapaki-pakinabang ang impormasyong ibinigay para sa iyong {cardName}?",
  GO_BACK: "Bumalik",
  WALLET_LANDING_TITLE: "Ang impormasyon ng iyong mga benepisyo at coverage, lahat sa iisang lugar.",
  WALLET_LANDING_BODY: "Tahanan ng mga benepisyo, programa at detalye ng coverage ng iyong employer ang iyong digital wallet. Hanapin kung ano ang kailangan mo sa isang mabilis na pag-tap.",
  POLICY_ACTIVE: "Aktibong Policy",
  REMAINING_BALANCE: "Natitirang Balanse",
  CONTACT_CUSTOMER_CARE: "makipag-ugnayan sa Customer Care",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "Naku, mukhang nagkaproblema",
  WERE_HAVING_TROUBLE: "Nagkakaproblema kami sa pag-load ng impormasyong ito, subukang i-refresh ang page. Kung magpapatuloy ang problema, {custCareLink}.",
  IN_NETWORK: "Nasa Network",
  OUT_OF_NETWORK: "Wala sa Network",
  PROGRAMS: "Mga programa",
  PROGRAM_CREATED_BY: "Programang Ginawa ng",
  TOP_FOCUS_AREA: "Nangungunang bahaging pagtutuunan",
  DIMENSION_OF_HEALTH: "SUKAT NG KALUSUGAN",
  READING_MORE_ABOUT_DIMENSIONS: "Interesadong magbasa pa tungkol sa kung paano nakakaapekto ang mga sukat sa pangkalahatang kalusugan mo",
  READ_OUR_ARTICLE: "Basahin ang Aming Artikulo",
  HEALTH_PROGRAMS_FOR_YOU: "Mga Programang Pangkalusugan para sa iyo",
  COMPLETE_ACTIVITIES: "Kumpletuhin ang mga aktibidad para maabot ang iyong mga layunin sa kalusugan. Inirerekomenda batay sa Profile ng Kalusugan mo.",
  FT: "ft",
  CM: "cm",
  IN: "in",
  LB: "lb",
  KG: "kg",
  LETS_BEGIN: "Simulan Natin!",
  KEEP_GOING: "Magpatuloy",
  SKIP: "Laktawan",
  HP_ONBOARDING_SUBMIT: "Magsimula Tayo",
  FOR_PROVIDERS_AND_PHARMACISTS: "Para sa mga Provider/Parmasyutiko",
  WEVE_PICKED_UP: "Binalikan namin kung saan ka tumigil.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "Ilang tanong ka na lang para makakuha ng {points} points.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "Ilang tanong ka na lang para matapos ang assessment.",
  PRESS_ENTER: "Pindutin ang Enter",
  SELECT_ALL_THAT_APPLY: "Piliin ang lahat ng naaangkop",
  SELECT_UP_TO: "Pumili ng hanggang {number}",
  AMOUNT_QUESTIONS: "{amount} (na) tanong",
  QUESTION_PROGRESS: "TANONG {questionNumber} NG {totalQuestions}",
  COMPLETE: "Kumpleto na",
  VIEW_MORE: "Tumingin Pa",
  DOWNLOAD_APP_STORE: "I-download sa App Store",
  DOWNLOAD_PLAY_STORE: "Kunin ito sa Google Play",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "Ikinalulungkot namin, nag-expire na ang page na hiniling mo o hindi na valid.",
  EARNED_X_TIMES: "Nakakuha ng {amount}X",
  EARNED: "Nakuha",
  YOUVE_EARNED_TOTAL: "Nakakuha ka ng kabuuang",
  YOUVE_EARNED_GRAND_TOTAL: "Nakakuha ka ng pangkalahatang kabuuang",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "Nakakuha ka ng Achievement Badge",
  CONGRATS_COMPLETED: "Congrats! Nakumpleto mo na",
  VIEW_MY_HEALTH_DASHBOARD: "Tingnan ang Aking Dashboard sa Kalusugan",
  LATEST_INSIGHTS_METRICS_AND_MORE: "Mga pinakabagong insight, sukatan, at higit pa",
  FEEDBACK_TITLE: "Gaano nakatutulong ang impormasyong ito?",
  FEEDBACK_MIN_SCALE: "Hindi nakatutulong",
  FEEDBACK_MAX_SCALE: "Talagang nakatutulong",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "May naranasan ka bang mga pagbabago sa iyong buhay o pamilya kamakailan lang?",
  DID_YOU_RECENTLY: "Nagpakasal ka ba o nanganak kamakailan? Posibleng maging kwalipikado ka sa ilang partikular na pangyayari sa buhay para sa muling pagpapatala.",
  LEARN_MORE: "Alamin Pa",
  VIEW_ACTIVITY: "Tingnan ang Aktibidad",
  CONTINUE: "Magpatuloy",
  ARIA_CIRCLED_CHECKMARK: "Binilugan na puting checkmark",
  NOT_NOW: "Hindi Ngayon",
  VAL_POINTS: "{points} points",
  ACTIONS: "Mga aksyon",
  VIEW_ALL_CLAIMS: "Tingnan ang Lahat ng Claim",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "Dapat naka-check ang box na ito para magpatuloy",
  PLEASE_NOTE: "Pakitandaan",
  COPY: "Kopyahin",
  STATUS: "Status",
  STARTED: "Nagsimula na",
  APPROVED: "Naaprubahan",
  CANCELLED: "Nakansela",
  UPLOADED_DOCUMENT: "Na-upload noong {date} \u2022 In-upload ni {user}",
  MORE_INFORMATION_NEEDED: "Kailangan ang Impormasyon",
  PAID: "Bayad na",
  PENDING_EXTERNAL_REVIEW: "Ipinadala para Masuri sa Labas",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "Higit Pang Impormasyon sa Paunang pag-apruba",
  PREAPPROVAL_REJECTED: "Tinanggihan ang Paunang pag-apruba",
  PREAPPROVED: "Inaprubahan na muna",
  REJECTED: "Tinanggihan",
  SUBMITTED_FOR_PREAPPROVAL: "Naisumite para sa Paunang pag-apruba",
  SUBMITTED: "Naisumite na",
  WALLET: "Wallet",
  AMOUNT: "Halaga",
  ADD: "Idagdag",
  EDIT: "I-edit",
  DETAILS: "Mga Detalye",
  AND: "at",
  TODAY: "Ngayong araw",
  CLAIMS: "Mga Claim",
  GROUP_ID: "Group Id",
  ZIP_CODE: "Zip Code",
  CITY: "Lungsod",
  STATE: "Estado",
  REMOVE: "Alisin",
  HEALTH_SPENDING_ACCOUNT: "Account na Panggastos sa Kalusugan",
  HSA: "HSA",
  LIFESTYLE_SPENDING_ACCOUNT: "Account na Panggastos sa Istilo ng Pamumuhay",
  LSA: "LSA",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "Health Reimbursement Arrangement (HRA)",
  DRUGS: "Gamot",
  TRAVEL: "Paglalakbay",
  HEALTH_INSURANCE: "Insurance sa Kalusugan",
  SETTINGS: "Mga Setting",
  ACTIVE: "Aktibo",
  ENDED: "Natapos na",
  SUSPENDED: "Sinuspinde",
  PENDING: "Nakabinbin",
  LOADING: "Naglo-load",
  ERROR_STATE_TITLE: "Nagkaproblema",
  HELP_EMAIL: "help@league.com",
  SUBMIT: "Isumite",
  EARN_NUMBER_POINTS_FULL: "Makakuha ng {points} points",
  CLOSE: "Isara",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "Buong taon ang challenge na ito",
  END_DAY_AT_TIME: "Matatapos sa {day}",
  ENDED_DAY_AT_TIME: "Natapos noong {day}",
  START_DAY_AT_TIME: "Magsisimula sa {day}",
  SPONSOR_LOGO: "{name} logo",
  START_AND_END_DATES: "Magsisimula sa {startDate} \u2022 Matatapos sa {endDate}",
  SIGN_UP_BY_DATE: "Mag-sign up hanggang {date}",
  SIGN_UP_BEGINS_DATE: "Magsisimula ang pag-sign-up sa {date}",
  SIGN_UP_DATE_PASSED: "Lumipas na ang petsa ng pag-sign up",
  SIGN_UP_PERIOD_PASSED: "Lumipas na ang panahon ng pag-sign up",
  CHALLENGE_DETAILS: "Mga Detalye ng Challenge",
  CHALLENGE_PROGRESS: "Pagsulong ng Challenge",
  X_PERCENT_COMPLETE: "{number}% ang Nakumpleto",
  SIGN_UP_FOR_CHALLENGE: "Mag-sign Up",
  GROUP_CHALLENGE: "Hamon ng Grupo",
  SOLO_CHALLENGE: "Solo Challenge",
  TRACK_WEARABLES_ALT_TEXT: "Taong tumitingin sa data ng smart watch",
  CONNECT_A_DEVICE: "Magkonekta ng Device",
  DEVICES_CONNECTED_TITLE: "Mukhang nakakonekta ka na!",
  DEVICES_CONNECTED_DESCRIPTION: "Sini-sync na namin ang iyong data. Kung pipiliin mong awtomatikong subaybayan, maaari naming awtomatikong markahan na nakumpleto ang mga aktibidad gamit ang {dataPoint} ng iyong device.",
  DEVICES_UNCONNECTED_TITLE: "Ikonekta natin ang iyong device!",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "Awtomatikong sinusubaybayan",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "Manual na sinusubaybayan",
  DEVICES_UNCONNECTED_DESCRIPTION: "Ikonekta ang mga paborito mong app sa kalusugan at device para awtomatikong markahan na nakumpleto ang mga aktibidad.",
  TRACK_CHALLENGE_AUTOMATICALLY: "Awtomatikong subaybayan",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "Kung pipiliin mong awtomatikong subaybayan, pagkatapos mong mag-sign up, {instructionsLink} para maikonekta ang iyong device gamit ang League mobile app.",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "sundin ang mga hakbang na ito",
  TRACK_CHALLENGE_MANUALLY: "Manual na subaybayan na lang",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "Kung pipiliin mong manual na subaybayan, maaari ka pa ring magkonekta ng device sa ibang pagkakataon. {learnMoreLink} ang tungkol sa manual na pagsubaybay sa pagsulong mo.",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "Alamin ang higit pa",
  TRACK_HEALTH_DATA_ALT: "Dashboard na may graph na sumusubaybay sa data ng kalusugan",
  TRACK_MANUALLY_CONFIRM_TITLE: "Sigurado ka bang gusto mong manual na subaybayan?",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "Maikokonekta mo pa rin ang isang device sa ibang pagkakataon",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "Sigurado ka bang gusto mong lumabas?",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "Kung pipiliin mong lumabas, maaaring hindi ma-save ang mga binago mo.",
  EXIT: "Lumabas",
  CHALLENGE: "Challenge",
  CHALLENGES: "Mga Challenge",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "Mag-sign up para sa challenge na makumpleto ang mga araw-araw na aktibidad at makipagpaligsahan sa iyong mga kauri!",
  THIS_CHALLENGE_HAS_ENDED: "Natapos na ang challenge na ito",
  COMPLETED_ON_DATE: "Nakumpleto noong {date}",
  ENDED_ON_DATE: "Natapos noong {date}",
  LEAVE_CHALLENGE: "Umalis sa Challenge",
  LEAVE_CHALLENGE_QUESTION: "Umalis sa Challenge?",
  LEAVE_CHALLENGE_DETAIL: "Sigurado ka bang gusto mong alisin ang challenge na ito sa iyong paglalakbay? Mare-reset ang anumang pagsulong sa challenge na ito.",
  LEAVE: "Umalis",
  SIGN_UP_FOR_THE_CHALLENGE: "Mag-sign Up para sa Challenge",
  CHALLENGE_SENSITIVE_DATA: "Napakasensitibo ng iyong data sa kalusugan at personal na impormasyon at responsibilidad namin na panatilihing pribado at ligtas ito. Talagang sineseryoso namin ang responsibilidad na ito.",
  CHALLENGE_USING_DATA_TO: "Kapag nag-sign up ka para sa isang challenge, ginagamit namin ang iyong data para:",
  COMPLETE_ACTIVITIES_SHORT: "Kumpletuhin ang mga aktibidad",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "Maibahagi ang pagsulong mo sa challenge sa iyong employer",
  SHARE_CHALLENGE_PROGRESS: "Maibahagi ang pagsulong mo sa challenge sa iyong mga kauri (at employer)",
  RANK_SCORE: "I-rank ang iyong score sa isang leaderboard",
  DETERMINE_CHALLENGE_WINNERS: "Tukuyin ang mga nanalo sa challenge",
  ACCEPT_BEFORE_CONTINUE: "Dapat mong tanggapin ang patakaran sa privacy bago magpatuloy.",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "Sa pamamagitan ng pag-check sa kahon na ito, sumasang-ayon ako sa <a>patakaran sa privacy.</a>",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "Alamin pa ang tungkol sa kung paano pinamamamahalaan at ginagamit ng League ang iyong data sa aming <a>patakaran sa privacy.</a>",
  PRIZE: "Premyo",
  RULES: "Mga tuntunin",
  LEADERBOARD: "Leaderboard",
  TOOLTIP_ACTION_DESCRIPTION: "Tingnan ang pamantayan sa pag-rank sa leaderboard",
  MARK_AS_DONE: "Markahan Bilang Tapos Na",
  CLOSE_ACTIVITY: "Isara ang Aktibidad",
  GOAL_INCOMPLETE: "Dapat mong kumpletuhin ang layunin bago magpatuloy.",
  PROGRESS_RECORD_ERROR: "Nagka-isyu sa pag-record ng huli mong pagsulong.",
  YOUVE_COMPLETED: "Nakumpleto mo",
  ACTIVITY_COMPLETE: "Tapos na ang aktibidad",
  PAGE_NOT_FOUND_HEADER: "Hindi Nakita ang Page",
  PAGE_NOT_FOUND_CONTENT: "Mukhang medyo naligaw ka!",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "Hayaang tulungan ka naming mahanap ang sarili mo.",
  PAGE_NOT_FOUND_BUTTON: "Umuwi",
  REWARD_POINTS: "Reward Points",
  REWARD_POINTS_EARNED: "Mga Nakuhang Reward Points",
  REWARD_POINTS_MISSED: "Mga Hindi Nakuhang Reward Points",
  X_REWARD_POINTS: "{points} (na) Reward Points",
  X_LEAGUE_POINTS: "{points} (na) League Points",
  REWARDS_PROGRAM_ELIGIBLE: "Kwalipikado sa Rewards Program",
  EXPIRY_DATE: "Petsa ng Pag-expire",
  NO_EXPIRY_DATE: "Walang petsa ng pag-expire",
  POINTS_BALANCE: "Balanse sa Points",
  REDEEM_NEXT_TIER_POINTS: "{points} ka na lang para ma-redeem ang {value}.",
  VIEW_YOUR_REWARDS: "Tingnan ang Iyong Mga Reward",
  REDEEMABLE: "Mare-redeem sa halagang {value}.",
  ACTIVATION_PENDING: "Nakabinbin ang pag-activate",
  EXPIRED: "Nag-expire na",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "Aktibo ang iyong account at sine-set up na.",
  PLEASE_TRY_AGAIN_SHORTLY: "Pakisubukan ulit sa ilang sandali",
  MANAGE_DEPENDENTS: "Pamahalaan ang mga Dependent",
  VIEW_CARD: "Tingnan ang Card",
  MEMBER_ID: "Member ID",
  SPOUSE: "Asawa",
  COMMON_LAW: "Common-law",
  CHILD: "Bata",
  PARENT: "Magulang",
  GRANDPARENT: "Lolo / Lola",
  GRANDCHILD: "Apo",
  UNCLE_AUNT: "Tito / Tita",
  NEPHEW_NIECE: "Pamangking Lalaki / Pamangking Babae",
  INELIGIBLE_CHILD: "Hindi Kwalipikadong Bata",
  SIBLING: "Kapatid",
  OTHER: "Iba pa",
  DEPENDENT_CARD_HOLDER: "Card ni {dependentName} para sa",
  DATE: "PETSA",
  DESCRIPTION: "Paglalarawan",
  TYPE: "Uri",
  ID: "Id",
  CLAIM: "Claim",
  TOP_UP: "I-top Up",
  ADJUSTMENT: "Adjustment",
  REFUND: "Refund",
  LEAGUE_CREDIT: "League Credit",
  CREDIT_CARD: "Credit Card",
  CGA: "CGA",
  NO_TRANSACTIONS_YET: "Wala pang transaksyon",
  NO_MORE_TRANSACTIONS: "Wala nang transaksyon",
  PROMO_CODE: "Promo code",
  PROMO_CODE_NOT_FOUND: "Hindi valid na promo code",
  PROMO_CODE_SUCCESS: "Na-redeem na ang promo code!",
  TOTAL_CREDIT: "Kabuuang Credit:",
  CREDIT_INFORMATION: "Impormasyon sa Credit",
  ENTER_CODE: "Ilagay ang Code",
  DOCUMENT_NAME: "Pangalan ng dokumento",
  REQUIRED: "Kinakailangan",
  CANCEL: "Kanselahin",
  CONFIRM: "Kumpirmahin",
  ALLOCATION: "Paglalaan",
  COVERED: "Sinasagot / Sinasaklaw",
  NOT_COVERED: "Hindi sinasagot",
  COVERED_WITH_RESTRICTIONS: "Sinasagot na may mga paghihigpit",
  EXPAND_ALL: "I-expand Lahat",
  COLLAPSE_ALL: "I-collapse Lahat",
  EXAMPLES: "Mga halimbawa: ",
  RESTRICTIONS: "Mga paghihigpit",
  SEARCH_COVERAGE: "Hanapin ang iyong coverage",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "Awtomatikong mag-a-update ang mga resulta ng paghahanap habang nagta-type ka",
  INDIVIDUAL: "Indibidwal",
  INDIVIDUAL_CAP: "Indibidwal",
  DEPENDENTS: "Mga Dependent",
  SELECT_ONE: "Pumili ng isa",
  MORE_OPTIONS: "Higit Pang Opsyon",
  VENDOR: "Vendor",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "awtomatikong idaragdag ang mga ito sa iyong {benefitName}.",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "<strong>{percent}%</strong> ang idadagdag sa iyong <strong>{benefitName}</strong>",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "<strong>{percent}%</strong> nito ang idadagdag sa iyong <strong>{benefitName}</strong>.",
  DOCUMENT_TITLE: "{title} - League",
  MEDICAL: "Medikal",
  TOTAL: "Kabuuan",
  PROFILE_UPDATE_FAILED: "Hindi Na-update ang Profile",
  PROFILE_PHOTO: "Larawan ng profile",
  PROFILE_PHOTO_DESCRIPTION: "Lumalabas ang larawang ito sa iyong profile ng user",
  SELECT_AVATAR: "Pumili ng Avatar",
  SELECT_AVATAR_ERROR: "Dapat kang pumili ng avatar bago magpatuloy.",
  GET_AVATARS_ERROR: "Oops! Nagka-isyu sa pag-load ng mga avatar. Bumalik at pumili ng ibang opsyon.",
  ADDRESS_LINE_1: "Linya ng Address 1",
  ADDRESS_LINE_2: "Linya ng Address 2",
  LANGUAGE_SELECTOR_ENGLISH: "English",
  LANGUAGE_SELECTOR_FRENCH: "Fran\xE7ais",
  LANGUAGE_SELECTOR_SPANISH: "Espa\xF1ol",
  DOMESTIC_PARTNERSHIP: "Pagli-live In",
  YES: "Oo",
  NO: "Hindi",
  WHY_DO_YOU_ASK: "Bakit mo itinatanong?",
  LAST_DAY_OF_ENROLLMENT: "Huling araw ng panahon ng pagpapatala: {lastDay}",
  WALLET_WELCOME: "Welcome sa iyong Wallet!",
  WALLET_DESCRIPTION: "Ang iyong wallet ay kung saan maa-access mo ang iyong mga benepisyo, magsusumite ng mga claim, at titingnan ang iyong coverage.  Makapagsusumite ka ng mga claim para sa mga nagastos sa panahon ng pagpapatala sa sandaling maging aktibo ang iyong wallet. Samantala, {healthProgramLink}!",
  HEALTH_PROGRAM_LINK_COPY: "subukan ang isa sa aming mga programang pangkalusugan",
  FINALIZE_SELECTIONS_HEADER_COPY: "Gusto mo bang mas maagang ma-access ang iyong mga benepisyo?",
  FINALIZE_SELECTIONS_COPY_1: "Sa pag-finalize ng mga pinili mo, agad naming maipapadala sa carrier ang mga detalye ng plano mo! Nangangahulugan na hindi mo kailangang maghintay hanggang sa katapusan ng iyong panahon ng pagpapatala para matingnan ang iyong wallet.",
  FINALIZE_SELECTIONS_COPY_2: "Nangangahulugan din ito na hindi ka makakagawa ng anumang pagbabago sa iyong plano matapos i-click ang isumite.",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "Nauunawaan ko na hindi ko mababago ang mga pinili ko",
  FINALIZE_PLAN_SELECTIONS: "I-finalize ang Mga Pinili sa Plano",
  ERROR_FINALIZING_SELECTIONS: "Nagka-error sa pagsumite ng mga pinili mo.",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "Mukhang hindi kumpleto ang mga pinili mo. Tapusin ang pagpapatala at subukan ulit.",
  SELECTIONS_FINALIZED_SUCCESS: "Na-finalize na ang mga pinili!",
  TOTAL_REWARDS_LABEL: "Buod ng Coverage",
  TOTAL_REWARDS_DESCRIPTION: "Suriin ang mga pinili sa plano na ginawa mo sa iyong pagpapatala.",
  TOTAL_REWARDS_LINK: "Tingnan ang Buod ng Coverage",
  CHANGE_PLAN_LABEL: "Baguhin ang Mga Pinili sa Plano",
  CHANGE_PLAN_DESCRIPTION: "Gusto mong gumawa ng mga pagbabago sa plano o personal na impormasyon mo?",
  CHANGE_PLAN_LINK: "Baguhin ang Mga Pinili sa Plano",
  DISMISS_TOAST: "I-dismiss ang notification",
  UPLOAD_DOCUMENT: "Mag-upload ng Dokumento",
  REQUIRED_FORM: "Kinakailangan",
  PERCENTAGE_USED: "Porsiyentong nagamit.",
  SUCCESS: "Tagumpay!",
  ERROR: "Error",
  OVERVIEW: "Pangkalahatang ideya",
  THIS_FIELD_IS_REQUIRED: "Kinakailangan ang field na ito",
  INVALID_ZIP_CODE_NO_FORMAT: "Hindi valid na zip code",
  INVALID_DAY_ERROR_MESSAGE: "Hindi valid ang araw na pinili mo.",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "Dapat hindi hihigit sa {length} (na) character",
  COMPLETED_STATE_TITLE: "Nakumpleto na",
  DRAG_OR_UPLOAD_PHOTO: "Mag-upload ng larawan ng dokumentong ito",
  ADD_IMAGE: "Magdagdag pa ng mga page sa dokumentong ito (kung naaangkop)",
  IDENTIFICATION_TYPE_TWO_FILES: "Puwede lang magkaroon ng hanggang 2 suportang larawan ang mga dokumentong pagkakakilanlan.",
  WELCOME: "Welcome",
  GOT_IT: "Naintindihan Ko!",
  SAVE: "I-save",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "Tingnan ang higit pang detalye",
  LAST_UPDATED: "Huling Na-update",
  CLAIM_REF_NUMBER: "Claim #",
  VIEW_ALL: "Tingnan ang Lahat",
  HRA: "HRA",
  SEARCH: "Maghanap",
  DAY: "Araw",
  MONTH: "Buwan",
  YEAR: "Taon",
  BACK: "Bumalik",
  NEXT: "Susunod",
  PREVIOUS: "Nakaraan",
  HELP_CENTER: "Help Center",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "I-minimize ang chat",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "Available ang mga iminumungkahing tugon",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "Nagta-type ang ka-chat",
  VIEW_DOCUMENT: "Tingnan ang Dokumento:",
  CATEGORIES: "Mga kategorya",
  ACTIVE_PROGRAM: "{length, plural, one {Aktibong Programa} other {Mga Aktibong Programa}}",
  ACTIVE_CHALLENGE: "{length, plural, one {Aktibong Challenge} other {Mga Aktibong Challenge}}",
  PROGRAM_PROGRESS: "Pagsulong ng Programa",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "Maaari ka ring mag-type para makagawa ng bagong opsyon!",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "Piliin ang iyong mga opsyon",
  COVERAGE: "Coverage",
  HEALTH_PROFILE: "Profile ng Kalusugan",
  HEALTH_PROGRAM: "Programang Pangkalusugan",
  HEALTH_PROGRAMS: "Mga Programa sa Kalusugan",
  STEPS: "hakbang",
  ACTIVE_DURATION: "mga minutong aktibo",
  MINDFUL_DURATION: "mga minuto ng pagtutuon",
  FLOORS_CLIMBED: "mga naakyat na baitang",
  DISTANCE: "layo ng nilakbay",
  ENERGY_BURNED: "na-burn na calories",
  METRIC_DETAILS: "Mga Detalye ng Sukatan",
  HEALTH_PROGRAMS_SYNC_DEVICE: "I-sync ang iyong {device} para sa pinakamainam na experience sa programa.",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "Konektadong programang pangkalusugan",
  TRACK_AUTOMATICALLY: "Awtomatikong Subaybayan ang Pagsulong Mo",
  SYNC_ISSUE: "ISYU SA PAG-SYNC",
  FIX_SYNC_ISSUE: "Ayusin ang isyung ito",
  DATAPOINT_SYNC_ISSUE: "Nagka-isyu sa pag-sync ng iyong {dataPoint}.",
  CONNECT_TO_TRACK: "Ikonekta ang mga paborito mong app sa kalusugan at mga device para awtomatikong markahan na nakumpleto ang mga aktibidad.",
  CONNECT_APPS_DEVICES: "Magkonekta ng mga app at device",
  ADD_APPS_DEVICES: "Tingnan ang Mga App at Device",
  MANAGE_UNITS: "Palitan ang mga unit ng sukat",
  DONT_CONNECT_APPS_DEVICES: "Huwag awtomatikong subaybayan ang pagsulong ko",
  CONNECTING_UNAVAILABLE: "Hindi available sa web ang pagkonekta ng mga app at device",
  CONNECTING_UNAVAILABLE_DETAILS: "Sa ngayon, bisitahin ang League mobile app para maikonekta ang mga paborito mong app at device sa kalusugan.",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "Sa ngayon, bisitahin ang League mobile app para maikonekta ang mga paborito mong app at device sa kalusugan. Kung gusto mong malaman ang iba pa, makakahanap ka ng buong listahan ng",
  CONNECT_DEVICES_LEARN_MORE: "Alamin Pa",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "mga app at device na kasalukuyang sinusuportahan",
  DONT_HAVE_THE_APP: "Wala ka bang League mobile app?",
  WHAT_DEVICES_CAN_CONNECT: "Aling mga app at device ang sinusuportahan?",
  CONNECTED_DEVICES_IMAGE_ALT: "Taong nagsusuri ng data mula sa konektadong device",
  MORE_GREAT_RESOURCES: "Higit pang magagandang resource",
  HISTORY: "History",
  TOP_MATCH: "Nangungunang katugma",
  CHAT: "Mag-chat",
  END_CHAT: "Tapusin ang Chat",
  BECOMES_ACTIVE_ON_DATE: "Magiging aktibo sa {date}",
  BECOMES_ACTIVE_IN_X_DAYS: "Magiging aktibo ang benepisyong ito sa loob ng {days} (na) araw",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "Maaari ka lang magsumite ng mga claim na may petsa ng pagbili/serbisyo sa pagitan ng <strong>{dateRange}</strong> para sa benepisyong ito. Tingnan ang mga detalye ng iyong coverage para sa higit pang impormasyon.",
  SUBMIT_PAST_CLAIMS_BY_DATE: "Isumite ang mga nakaraang claim hanggang {date}",
  THIS_POLICY_HAS_EXPIRED: "Nag-expire na ang panahon ng policy na ito, pero mayroon ka pang {days} (na) araw para magsumite ng mga nakaraang claim para sa benepisyong ito",
  THIS_POLICY_EXPIRES_TOMORROW: "Nag-expire na ang panahon ng policy na ito, pero ngayon ang huling araw mo para magsumite ng mga nakaraang claim para sa benepisyong ito",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "Mayroon kang hanggang <strong>{inactiveDate}</strong> para magsumite ng mga nakaraang claim na may petsa ng pagbili/serbisyo sa o bago sumapit ang <strong>{endDate}</strong>",
  EXPIRES_ON_DATE: "Mag-e-expire sa {date}",
  ASSIGNED: "Naitalaga",
  ASSIGNED_ON: "noong {date}",
  ASSIGNED_TODAY: "ngayon",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "Mayroon ka pang {days} (na) araw para gamitin ang benepisyong ito bago mag-expire ang panahon ng policy",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "Pagkatapos mag-expire, magkakaroon ka ng hanggang <strong>{inactiveDate}</strong> para magsumite ng mga claim na may petsa ng pagbili/serbisyo sa o bago sumapit ang <strong>{endDate}</strong>",
  LOAD_MORE: "Mag-load pa",
  SHOW_MORE: "Magpakita pa",
  LINK_OUT_MESSAGE: "Dapat Makita",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "Oops, parang may problema.",
  AGENT_PERMISSION_ERROR: "Wala kang pahintulot na tingnan ito.",
  TRY_AGAIN: "Subukan muli",
  WARNING_OPENS_NEW_TAB: "Magbubukas sa panibagong tab",
  ANY_HEALTH_CHANGES: "Nagkaroon ba ng anumang kamakailang pagbabago sa iyong kalusugan?",
  LAST_UPDATED_DATE: "Huling na-update {date}",
  UPDATE_HEALTH_PROFILE: "I-update ang Profile ng Kalusugan mo",
  HEALTH_PROFILE_COMPLETION_POINTS: "Nakakuha ka ng {points} points",
  OPENS_IN_A_NEW_TAB: "Magbubukas sa panibagong tab",
  COMPLETED: "Nakumpleto na",
  COMPLETED_X_TIMES: "{amount, plural, one {Nakumpleto nang {amount} beses} other {Nakumpleto nang {amount} (na) beses}}",
  REMOVE_FROM_JOURNEY: "Alisin sa paglalakbay",
  GO_TO_JOURNEY: "Pumunta sa Paglalakbay",
  ABOUT_THIS_PROGRAM: "Tungkol sa programang ito",
  HEALTH_JOURNEY: "Paglalakbay sa Kalusugan",
  REMOVE_ACTIVITY: "Alisin ang Aktibidad?",
  REMOVE_ACTIVITY_PROMPT: "Alisin ang Aktibidad",
  REMOVE_ACTIVITY_BUTTON: "Alisin",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "Sigurado ka bang gusto mong alisin ang aktibidad na ito sa iyong paglalakbay? Hindi na maa-undo ang aksyong ito.",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "Hindi mo na ito makikita sa iyong paglalakbay",
  DISCOVER_ACTIVITIES: "Tumuklas ng mga bagong aktibidad",
  DISCOVER_ACTIVITIES_CAPTION: "Mag-browse ng mga aktibidad na sa tingin namin ay magugustuhan mo",
  ACTIVITIES: "Mga aktibidad",
  ACTIVITIES_PLURALIZATION: "{count, plural, one {Aktibidad} other {Mga aktibidad}}",
  ACTIVITIES_PILL: "Mga aktibidad",
  PROGRAM_LIBRARY: "Library ng Programa",
  BROWSE_PROGRAMS: "Mag-browse ng Mga Programa",
  BACK_TO_JOURNEY: "Bumalik sa Paglalakbay",
  GO_TO_HEALTH_JOURNEY: "Pumunta sa paglalakbay sa kalusugan",
  ADD_TO_JOURNEY: "Idagdag sa Paglalakbay",
  REQUEST_WAIT: "Pakihintay habang kinukumpleto namin ang kahilingan mo",
  JOURNEY_DELIGHT_1: "Magsisimula kang maudyok. Makakasanayan mo ito. Kaya mo 'yan!",
  JOURNEY_DELIGHT_2: "Aktibo. Nakatuon. Nananatili sa iyong lane. Gustung-gusto naming makita ito.",
  JOURNEY_DELIGHT_3: "Maaaring magdulot ng malaking pagbabago ang maliliit na hakbang. Ginagawa mo ang hakbang na iyon!",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "{hasValue, select, true {Free-text na opsyon ito; gamitin ang text box para mailagay ang sarili mong value. Ang kasalukuyang value ay: {value}} other {Free-text na opsyon ito; gamitin ang text box para mailagay ang sarili mong value. Bakante ang kasalukuyang value}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "Eksklusibo ang opsyong ito; ang pagpili nito ay magde-desect sa lahat ng iba pa",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "I-play o i-pause ang kasalukuyang napiling animation",
  CENTIMETERS: "Sentimetro",
  FEET: "Talampakan",
  INCHES: "Pulgada",
  KILOGRAMS: "Kilo",
  POUNDS: "Libras",
  EARN_UP_TO_X_POINTS: "Makakuha ng hanggang {points} points",
  EXPIRES_TODAY: "Mag-e-expire ngayon",
  PROGRAM_DETAILS: "Mga Detalye ng Programa",
  PROGRESS: "Pagsulong",
  X_OF_Y_COMPLETE: "Nakumpleto ang {completed}/{total}",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "Napalampas ang {missed}",
  MISSED: "Napalampas",
  SCROLL_CAROUSEL_RIGHT: "I-scroll ang carousel pasulong",
  SCROLL_CAROUSEL_LEFT: "I-scroll ang carousel pabalik",
  SCROLL_CAROUSEL_FORWARD: "I-scroll ang carousel pasulong",
  SCROLL_CAROUSEL_BACK: "I-scroll ang carousel pabalik",
  YOU_HAVENT_STARTED_PROGRAMS: "Wala ka pang sinimulan na anumang programang pangkalusugan",
  ADD_PROGRAM: "Magdagdag ng Programa",
  TAP_ADD_PROGRAM_BUTTON: "I-tap ang button na 'Magdagdag ng Programa' para mag-browse ng mga program.",
  YOUVE_REACHED_PROGRAM_LIMIT: "Naabot mo na ang limit ng iyong programa!",
  START_X_MORE_PROGRAMS: "{duration, plural, one {Magsimula ng {duration,number} pang programa. Ituloy ang buwelo.} other {Magsimula ng {duration,number} pang programa. Ituloy ang buwelo.}}",
  OK: "OK",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "Kumpletuhin ang isa sa iyong mga aktibong programa para maidagdag ito sa iyong paglalakbay.",
  SHOW_LESS: "Magpakita ng Kaunti",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "Mukhang naabot mo na ang limit ng iyong programa!",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "Kumpletuhin ang isang aktibong programa bago magdagdag ng bago. <strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "Alisin ang programa?",
  REMOVE_PROGRAM_BUTTON: "Alisin",
  ARE_YOU_SURE_REMOVE_PROGRAM: "Sigurado ka bang gusto mong alisin ang program na ito sa iyong paglalakbay? Mare-reset ang anumang pagsulong sa programang ito at kailangan mong magsimula muli sa simula.",
  PROGRAM_REMOVED: "Inalis na ang programa",
  PROGRAM_ADDED: "Idinagdag ang programa",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "Lalabas na ngayon ang programa sa iyong paglalakbay",
  PROGRAM_ADD_ERROR: "Hindi namin maidagdag ang programa sa oras na ito. Pakisubukan ulit o pumili ng ibang programa.",
  PROGRAM_REMOVE_ERROR: "Hindi namin naalis ang program sa oras na ito. Pakisubukan ulit.",
  SKIP_TO_CONTENT_MESSAGE: "Lumaktaw patungo sa content",
  MAIN_NAVIGATION_LIST_LABEL: "Mga item sa pag-navigate ng League",
  PAGE_TITLE_ACTIVITY_DETAILS: "Mga detalye ng aktibidad",
  X_OF_Y: "{current} sa {total}",
  ACTIVITY_COUNT_EDIT: "I-edit ang {unit}",
  ACTIVITY_COUNT_GOAL: "Layunin: {goal,number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: 'Para makumpleto ang aktibidad na ito, i-click ang "Markahan na Tapos na".',
  ACTIVITY_COMPLETE_LATER_PROMPT: 'Para magpatuloy, i-click ang "Bumalik sa Paglalakbay" at bumalik sa ibang pagkakataon para markahang tapos na ito.',
  ACTIVITY_COMPLETE_ERROR: "Hindi namin nakumpleto ang aktibidad sa oras na ito. Pakisubukan ulit.",
  ACTIVITY_DISMISS_ERROR: "Hindi namin maalis ang aktibidad sa oras na ito. Pakisubukan ulit.",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "Pumili ng araw, buwan, at taon",
  TOOLBOX_VALIDATION_MONTH_YEAR: "Pumili ng buwan at taon",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "{limit} ang limit ng character",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "Maglagay ng numerong katumbas o higit sa {value}",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "Maglagay ng numerong katumbas o mas mababa sa {value}",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "Kinakailangang field ito",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "Gumawa ng pagpili",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "Gumawa ng pagpili",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "Pumili ng petsang sumunod sa {date,date,medium}",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "Pumili ng petsang nauna sa {date,date,medium}",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "Maglagay ng buong numero",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "Pumili ng maximum na {integer} (na) opsyon",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "Pumili ng {integer} (na) opsyon para magpatuloy",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "Hindi puwedeng bakante ang field kapag pinili",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "Kailangang hindi bababa sa {imperial}lb ({metric}kg) ang timbang",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "Kailangang hanggang {imperial}lb ({metric}kg) lang ang timbang",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "Kailangang hindi bababa sa {imperial} ({metric} cm) ang taas",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "Kailangang hanggang {imperial} ({metric} cm) lang ang taas",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "Ilagay ang iyong timbang sa kilo",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "Ilagay ang iyong timbang sa libras",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "Ilagay ang iyong taas sa sentimetro",
  ENTER_YOUR_HEIGHT_IN_FEET: "Ilagay ang iyong taas sa talampakan",
  ENTER_YOUR_HEIGHT_IN_INCHES: "Ipasok ang iyong taas sa pulgada",
  GOAL_REACHED: "Naabot mo ang layunin!",
  COMPLETE_ACTIVITY: "Kumpletuhin ang Aktibidad",
  EXIT_ACTIVITY_TITLE: "Sigurado ka bang gusto mong umalis sa aktibidad na ito?",
  EXIT_ACTIVITY_BODY: "Hindi mase-save ang pagsulong mo at kakailanganin mong i-restart ang aktibidad mula sa simula.",
  EXIT_ACTIVITY_CTA: "Umalis sa Aktibidad",
  ACTIVITY_NEXT_STEP: "Ituloy ang aktibidad",
  ACTIVITY_PREV_STEP: "Bumalik sa nakaraang hakbang",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "Kategorya ng Programang Pangkalusugan",
  NEXT_STEP: "Susunod na Hakbang",
  YOU_EARNED: "Nakakuha ka",
  YOU_EARNED_X_POINTS: "Nakakuha ka ng {points} points!",
  CREATE: "Gumawa ng {label}",
  TOMORROW: "Bukas",
  YESTERDAY: "Kahapon",
  BACK_TO_TODAY: "Bumalik sa ngayon",
  PREVIOUS_DAY: "Nakaraang araw",
  NEXT_DAY: "Susunod na araw",
  ACTIVITY_STATUS: "Status ng aktibidad: ",
  ACTIVITY_STATUS_EXPIRED: "Nag-expire na",
  ACTIVITY_STATUS_REMOVED: "Nilaktawan",
  ACTIVITY_STATUS_UPCOMING: "Malapit Nang Maging Available",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "Wala pang plano - sa ngayon!",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: "I-tap ang button na 'Magdagdag' para makahanap ng mga bagong programa o i-browse ang na-curate na listahan ng mga aktibidad sa ibaba.",
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "Magaling, sunod-sunod ang suwerte mo!",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "Bumalik bukas para sa higit pa o i-browse ang mga bagong programa at iminungkahing aktibidad.",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "Wala kang anumang aktibidad na nakaiskedyul para sa araw na ito.",
  NO_ACTIVITIES_PAST_HEADING: "Araw ng Pahinga",
  NO_ACTIVITIES_PAST_DESCRIPTION: "Mukhang kinuha mo ang araw na ito para mag-relax at makabawi.",
  BUTTON_BROWSE_PROGRAMS: "Mag-browse ng Mga Programa",
  IMG_ALT_HIGH_FIVE_HANDS: "Dalawang kamay na nagha-high five",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "Babaeng nakatayo at nagsu-scroll sa isang listahan ng mga programa na singlaki ng pader",
  IMG_ALT_EMPTY_STATE_REST: "Lalaking nagpapahinga sa isang komportableng upuan na may mainit na mug at headphones",
  THIS_IS_JUST_A_SNEAK_PEAK: "Pasilip lang ito!",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "Bumalik at kumpletuhin ang aktibidad na ito sa {date}.",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "Bumalik mamaya para tapusin ang aktibidad na ito.",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "Mukhang napalampas mo ang isang ito!",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "Nag-expire ang aktibidad na ito noong {date}.",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "Mukhang hindi para sa iyo ang isang ito",
  YOU_SKIPPED_THIS_ACTIVITY: "Nilaktawan mo ang aktibidad na ito.",
  NO_PROGRAMS: "Wala pang makikita rito sa ngayon",
  NO_ACTIVITY_TITLE: "Wala kahit ano rito (sa ngayon!)",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "Ipapakita namin dito ang rank mo sa sandaling magsimula ang challenge.",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "Tumingin ulit dito para makita ang mga highlight ng challenge sa lalong madaling panahon.",
  LEADERBOARD_ALL_TIME_RANKING: "All Time Ranking",
  LEADERBOARD_CURRENT_USER_NAME: "{user} (Ikaw)",
  ACTIVITY_PROGRESS: "Pagsulong ng Aktibidad",
  YOUR_JOURNEY: "Ang iyong paglalakbay",
  SUGGESTED_FOR_YOU: "Iminungkahi Para sa Iyo",
  NO_ACHIEVEMENTS_DESCRIPTION: "Kumpletuhin ang mga aktibidad sa iyong Paglalakbay para simulan ang pag-unlock ng mga achievement.",
  UNLOCK_ACHIEVEMENTS: "Mag-unlock ng achievement para sa pagkumpleto ng lahat ng aktibidad sa programang ito.",
  MEMBERS: "Mga Miyembro",
  SIGN_UP_PERIOD_PASSED_DETAIL: "Hindi ka na makaka-sign up para sa challenge na ito dahil lumipas na ang panahon ng pag-sign up.",
  REFERENCES: "Mga Sanggunian",
  SIGN_UP_BY: "Mag-sign up hanggang {date}",
  YOURE_GOOD_TO_GO: "Handa ka na!",
  CHECK_CHALLENGE_DETAILS: "Suriin ang mga detalye ng challenge para malaman mo ang mga aktibidad",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "Handa ka nang simulan ang challenge. Gusto mo bang sumali o gumawa ng team?",
  CHALLENGE_ABOUT_TO_START: "Magsisimula na ang iyong challenge!",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "Lalabas dito ang mga araw-araw na aktibidad kapag nagsimula ang iyong challenge sa <strong>{date}</strong>.",
  NO_CHALLENGE_ACTIVITIES_TODAY: "Walang aktibidad sa challenge para sa iyo ngayon.",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: 'Hindi mahanap ang "{query}".',
  COVERAGE_SEARCH_TRY_AGAIN: "Subukang hanapin ulit gamit ang ibang keyword.",
  EARN: "Makakuha",
  WORD_WITH_EXCLAMATION_POINT: "{word}!",
  UPDATED: "Na-update",
  YOUR_PROGRESS: "Ang pagsulong mo",
  YOUR_PROGRESS_IN_NUMBERS: "Nakumpleto ang {current} sa {total} {unit} o {percentage} (na) porsyento ",
  OUTCOMES_FRICTION_SCREEN_TITLE: "Pine-personalize ang mga suhestyon...",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "Bigyan kami ng ilang sandali para makahanap ng ilang kapaki-pakinabang na resource para sa iyo batay sa iyong mga sagot",
  RELOAD_MY_SUGGESTIONS: "I-reload ang mga suhestyon ko",
  YOUR_RANKING: "Ang iyong ranking",
  TOP_RANKED: "Mga Nangunguna sa Rank",
  VIEW_CHALLENGE_DETAILS: "Tingnan ang Mga Detalye ng Challenge",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "Nagtataka ka ba kung bakit kapaki-pakinabang na idagdag ang iyong mga dependent?",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "Puwedeng ma-access ng iyong mga dependent ang League at mag-enjoy din ng mas malusog na buhay!",
  UNABLE_TO_LEAVE_CHALLENGE: "Hindi makaalis sa challenge, pakisubukan ulit!",
  SUCCESSFULLY_LEFT_CHALLENGE: "Matagumpay kang nakaalis sa challenge.",
  UNABLE_TO_JOIN_CHALLENGE: "Hindi makasali sa challenge, pakisubukan ulit!",
  JOIN_TEAM: "Sumali sa Team",
  X_TEAM_MEMBERS: "{memberTotal, plural, one {{memberTotal,number} miyembro ng team} other {{memberTotal,number} (na) miyembro ng team}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, one {{spotsAvailable,number} spot ang available} other {{spotsAvailable,number} (na) spot ang available}}",
  HIGHLIGHTS: "Mga highlight",
  CURRENT_RANK: "Kasalukuyang Rank",
  X_LIKES: "{activityLikes, plural, one {{activityLikes,number} Like} other {{activityLikes,number} (na) Like}}",
  LIKE: "I-like",
  LIKED: "Ni-like",
  TEAM_INVITE_HEADING: "Mas mabuting sama-sama tayo!",
  TEAM_INVITE_DESCRIPTION: "Imbitahan ang mga kaibigan na sumali sa iyong team para sama-samang kumpletuhin ang mga aktibidad.",
  TEAM_INVITE_LINK_DESCRIPTION: "Mas marami, mas masaya! Ibahagi ang link na ito sa mga kaibigan para makasali sila sa iyong team.",
  TEAM_INVITE_LINK: "Link ng imbitasyon sa team",
  INVITE: "Mag-imbita",
  LEAVE_TEAM: "Umalis sa Team",
  INVITE_FRIENDS: "Imbitahan ang Mga Kaibigan",
  PRIVACY: "Mga Setting ng Privacy",
  JOIN_TEAM_SUCCESS_TITLE: "Welcome sa team!",
  JOIN_TEAM_SUCCESS_DETAIL: "Sama-sama nating pagtagumpayan ang mga aktibidad na ito.",
  JOIN_TEAM_SUCCESS_BUTTON: "Pumunta sa Mga Detalye ng Team",
  CANT_JOIN_TEAM_TITLE: "Hindi makakasali sa {teamName}",
  CANT_JOIN_TEAM_DETAIL: "Pakisubukan ulit! Kung kasali ka na sa isang team, kailangan mong umalis sa iyong kasalukuyang team bago ka makasali sa {teamName}.",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "Mukhang hindi ka pa nakapag-sign up para sa challenge. Kakailanganin mong mag-sign up bago ka makasali sa {teamName}.",
  LEAVE_TEAM_QUESTION: "Umalis sa {teamName}?",
  LEAVE_TEAM_DESCRIPTION: "Sigurado ka bang gusto mong umalis sa {teamName}? Hindi na ituturing na bahagi ng average ng team ang iyong score.",
  CHALLENGE_ACTIVITY_DETAIL: "Payagan ang iba na makita ang iyong aktibidad sa challenge na ito.",
  SHOW_MY_ACTIVITY: "Ipakita ang Aking Aktibidad",
  YOUR_TEAM: "Ang iyong team",
  JOIN_OR_CREATE_TEAM: "Sumali o Gumawa ng Team",
  TEAMS: "Mga Team",
  CHALLENGE_TEAMS_DESCRIPTION: "Sumali sa isang kasalukuyang team o gumawa ng sa iyo para sama-samang makipagpaligsahan sa challenge!",
  JOIN_A_TEAM: "Sumali sa isang Team",
  JOIN_TEAM_DESCRIPTION: "Maghanap ng kasalukuyang team na sasalihan at kasamang makikipagpaligsahan!",
  SEARCH_FOR_TEAM: "Maghanap ng team",
  CREATE_A_TEAM: "Gumawa ng Team",
  CREATE_TEAM_DESCRIPTION: "I-customize ang iyong team sa pamamagitan ng pagtatakda ng sarili mong avatar at pangalan ng team. Imbitahan ang iba na sumama sa iyo!",
  UP_YOUR_GAME: "I-level up ang iyong laro",
  UP_YOUR_GAME_DESCRIPTION: "May lakas kapag marami. Sumali sa isang team o gumawa ng sa iyo.",
  XTH_PLACE: "{currentRank, selectordinal, one {#st} two {#nd} few {#rd} other {#th} } Place",
  LEADERBOARD_RANKING: "Ranking",
  LEADERBOARD_USERNAME: "Username",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "Naikopya ang link ng imbitasyon sa clipboard!",
  DATE_AND_TIME: "Petsa at Oras",
  SELECT_TEAM_AVATAR: "Pumili ng Avatar ng Team",
  CHANGE_TEAM_AVATAR: "Palitan ang Avatar ng Team",
  TEAM_NAME_LABEL: "Pangalan ng Team",
  CREATE_TEAM: "Gumawa ng Team",
  TEAM_NAME_ERROR: "Maglagay ng pangalan para sa iyong team.",
  SLIDE_OF_TOTAL: "Slide {key} ng {total}",
  PAGE_OF_TOTAL: "Page {current} ng {total}",
  NO_SOLUTIONS_AVAILABLE: "Ikinalulungkot namin, walang solusyon sa ngayon.",
  SEE_ALL_ACHIEVEMENTS: "Tingnan ang lahat ng achievement",
  VIEW_ALL_ACHIEVEMENTS: "Tingnan ang lahat ng tagumpay",
  TAKE_ME_BACK: "Ibalik Mo Ako",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "Lumalabas ang avatar na ito sa profile ng iyong team at sa leaderboard.",
  CREATE_TEAM_SUCCESS: "Handa na ang iyong team!",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "Simulan ang pagbuo ng iyong team. Ibahagi ang link na ito sa iba para sama-sama ninyong kumpletuhin ang mga aktibidad.",
  VIEW_TEAM_DETAILS: "Tingnan ang Mga Detalye ng Team",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "Itinatampok ang highlight carousel",
  CHALLENGE_FAQ_HEADING: "May mga tanong ka ba tungkol sa mga Challenge?",
  CHALLENGE_FAQ_CONTENT: "Tingnan ang aming {helpCenterLink} para sa lahat ng iyong madalas itanong.",
  VIEW_THIS_RESOURCE: "Tingnan ang Resource na Ito",
  RECOMMENDED_NEXT_STEP: "Inirerekomendang Susunod na Hakbang",
  CARE_COLLECTIONS: "Mga Koleksyon ng Pangangalaga",
  PLAN_PROGRESS: "Pagsulong ng Plano",
  SINCE_LAST_WEEK: "mula noong nakaraang linggo",
  INSIGHT_CARD_HEADER_DISTANCE: "Distansya",
  INSIGHT_CARD_HEADER_STEPS: "Mga hakbang",
  MIN: "min",
  CAL: "Cal",
  KM: "km",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, one {hakbang} other {mga hakbang}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "Mga Naakyat na Baitang",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, one {baitang} other {mga baitang}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "Mga Aktibong Minuto",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "Na-burn na Lakas",
  INSIGHT_CARD_HEADER_WEIGHT: "Timbang",
  INSIGHT_CARD_HEADER_HEIGHT: "Taas",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "Mga Minuto ng Pagtutuon",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "Sukat ng Baywang",
  DAY_OF_THE_WEEK: "ARAW NG LINGGO",
  DAY_OF_THE_MONTH: "ARAW NG BUWAN",
  INSIGHT_CHART_WEEK_TITLE: "Lingguhang {type} bar chart",
  INSIGHT_CHART_MONTH_TITLE: "Buwanang {type} bar chart",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "Bilang ng {unit} na nilakad mo bawat araw ng linggo.",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "Bilang ng {unit} na nilakad mo bawat araw ng buwan.",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "Naglakad ka ng {distance} {unit} noong {date}",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "Bilang ng hakbang na nilakad mo bawat araw ng linggo.",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "Bilang ng hakbang na nilakad mo bawat araw ng buwan.",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "Naglakad ka ng {distance} {unit} noong {date}",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "Bilang ng {unit} na na-burn mo bawat araw ng linggo.",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "Bilang ng {unit} na na-burn mo bawat araw ng buwan.",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "Nag-burn ka ang {energy} {unit} noong {date}",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "Bilang ng aktibong minuto sa bawat araw ng linggo.",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "Bilang ng aktibong minuto sa bawat araw ng buwan.",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "Mayroon kang {value} (na) aktibong minuto noong {date}",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "Bilang ng baitang na naakyat mo bawat araw ng linggo.",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "Bilang ng baitang na naakyat mo bawat araw ng buwan.",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "Naakyat mo ang {value} (na) baitang ng hagdan noong {date}",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "Bilang ng minuto ng pagtutuon sa bawat araw ng linggo.",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "Bilang ng minuto ng pagtutuon sa bawat araw ng buwan.",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "Mayroon kang {value} mga minuto ng pagtutuon noong {date}",
  DATAPOINT_CATEGORY_ACTIVITY: "Aktibidad",
  DATAPOINT_CATEGORY_BODY: "Katawan",
  MANAGE_UNITS_APPLYING_CHANGES: "Ina-apply ang mga pagbabago...",
  MANAGE_UNITS_CHANGES_SAVED: "Nai-save ang mga pagbabago nang {time}",
  START_TRACKING_HEALTH_HEADER: "Simulan ang pagsubaybay sa iyong kalusugan",
  START_TRACKING_HEALTH_BODY: "Walang-hirap na subaybayan ang iyong mga sukat sa paglipas ng panahon para makita kung gaano kalayo na ang narating mo at kung paano mo sisikapin ang iyong mga layunin sa kalusugan.",
  BROWSE_SUPPORTED_DATA: "Ano ang puwede kong subaybayan?",
  LAST_UPDATED_CARRIER_DATE: "Huling na-update: {date} mula sa {carrier}",
  ACCUMULATION_PERIOD: "Panahon ng pag-ipon: <strong>{start} - {end}</strong>",
  NO_DATA_AVAILABLE: "Walang available na data",
  LAST_7_DAYS: "Huling 7 Araw",
  LAST_30_DAYS: "Huling 30 Araw",
  OLDER: "Mas matanda",
  GET_ASSISTANCE_NOW: "Mag-chat sa iyong grupo ng pangangalaga",
  GET_ASSISTANCE_NOW_SUBHEADING: "Mag-chat sa aming mga sertipikadong propesyonal sa kalusugan at mga kawani ng customer care. Matutulungan ka nilang makatipid ng pera sa pamamagitan ng pagtuturo sa iyo sa pinakaabot-kaya, pinakamataas na kalidad ng pangangalaga sa iyong lugar pati na rin sagutin ang anumang tanong tungkol sa mga benepisyo mo.",
  HOW_CAN_WE_HELP: "Paano Kami Makatutulong?",
  LIVE_BALANCE_SPEND: "{spentAmount} sa {totalAmount} ang nagastos",
  LIVE_BALANCE_REMAINING: "{amount} (na) lang",
  UPDATING_UNITS_OF_MEASURE: "Pag-update sa mga unit ng sukat",
  CHANGING_YOUR_UNITS: "Makakaapekto ang pagpapalit ng iyong mga unit sa kung paano ipinapakita ang mga ito sa app.",
  MESSAGES: "Mga mensahe",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "Mga mensahe",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "Inalis na ang {fileName}.",
  SEND_NEW_MESSAGE: "Magpadala ng bagong mensahe",
  NEW_MESSAGE_DEFAULT: "Ito ang simula ng bagong mensahe",
  NETWORK_MISSING_COVERAGE: "Walang coverage ng {network} ang plano mo.",
  CARD_FRONT_FILENAME: "{benefitName}-harap",
  CARD_BACK_FILENAME: "{benefitName}-likod",
  START_A_VISIT: "Magsimula ng Pagbisita",
  UNREAD: "Hindi pa nabasa",
  NO_MESSAGES_HEADING: "Walang pang mensahe",
  NO_MESSAGES_DESCRIPTION: "Magsimula sa pagpapadala ng bagong mensahe!",
  RETURN_TO: "Bumalik sa {location}",
  NO_RECOMENDATIONS: "Pasensya na, wala kaming anumang rekomendasyon para sa bahaging ito ng kalusugan sa ngayon.",
  CHALLENGES_LEGAL_DISCLAIMER: "Inilaan ang Challenge na ito na maging masayang paraan para makilahok ang lahat at makakuha ng patas na pagkakataong manalo. Paminsan-minsan, posibleng mag-alok ng cash o iba pang premyo ang organizer ng Challenge. Hindi dinisenyo ng League ang istraktura ng Challenge o nagpapasya kung mag-aalok ng mga premyo. Kung mayroon kang mga alalahanin tungkol sa istraktura ng Challenge na ito o naniniwalang hindi ka kasama sa pagkakataong manalo, makipag-usap sa organizer ng Challenge.",
  ALL_RECORDED_DATA: "Lahat ng Naitalang Data",
  NEW_MESSAGE: "Bagong Mensahe",
  MESSAGE_SUBJECT: "Paksa ng mensahe (opsyonal)",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "Sobra ka sa maximum na limit ng character ({maximum})",
  CHAR_LIMIT_EXCEEDED: "Sumobra sa limit ng character ({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "Itakda ang paksa ng mensahe",
  TO: "Para kay",
  CREATE_MESSAGE: "Gumawa ng Mensahe",
  MEMBER_LABEL: "MIYEMBRO",
  RESULT_COUNT: "{count, plural, one {{count,number} resulta} other {{count,number} (na) resulta}}",
  USER_COLON: "{user}:",
  JOINED_CHALLENGE_HIGHLIGHT: "Sumali sa <strong>{challengeTitle}</strong>!",
  CREATED_TEAM_HIGHLIGHT: "Ginawa ang <strong>ha{teamName}</strong>!",
  JOINED_TEAM_HIGHLIGHT: "Sumali sa <strong>{teamName}</strong>!",
  STEPS_WALKED_HIGHLIGHT: "<strong>Naglakad ng {total} (na) hakbang!</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong>Kumilos nang {total} (na) minuto!</strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong>Nakumpleto ang {total} (na) minuto!</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong>Nakakuha ng {achievement} (na) Achievement!</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "<strong>Nakumpleto ang isang aktibidad!</strong>",
  NO_CARE_COLLECTIONS: "Pasensya na, walang available na koleksyon ng pangangalaga.",
  WRITE_A_MESSAGE: "Magsulat ng mensahe...",
  UPLOAD_MULTIPLE_FILES: "Mag-upload ng maraming file",
  SEND: "Ipadala",
  SEND_MESSAGE: "Magpadala ng mensahe",
  DATA_SOURCES: "Mga Source ng Data",
  SOURCES_CAN_UPDATE: "Pinapayagan ang mga sumusunod na source na i-update ang iyong",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "Kapag maraming source ang available, itatala ang source ng data na may pinakamalaking value sa katapusan ng araw (11:59pm).",
  APPLE_HEALTH: "Apple Health",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "Garmin",
  POLAR: "Polar",
  WITHINGS: "Withings",
  HEALTH_PLAN: "Plano sa Kalusugan",
  OMRON: "Omron",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "Pag-update sa mga unit ng sukat",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "Makakaapekto ang pagpapalit ng iyong mga unit sa kung paano ipinapakita ang mga ito sa app",
  DOCUMENTS: "Mga Dokumento",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "Mga dokumento",
  DOCUMENTS_FILTER_LOADING_TEXT: "Nilo-load ang mga tab at filter",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "Kinakailangan ang {filter}",
  DOCUMENT_FILTER_X_REQUIRED: "{filter} (kinakailangan)",
  SHOW_RESULTS: "Ipakita ang Mga Resulta",
  RESET_FILTERS: "I-reset ang Mga Filter",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "Mga napiling filter",
  DOCUMENTS_RESULTS_HEADING: "Mga resulta",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "Laktawan para mag-upload ng dokumento",
  AVERAGE: "Average",
  VIEW_ALL_METRIC_DATA: "Tingnan ang lahat ng {metric} data",
  EDIT_METRIC_DATA: "I-edit ang data ng {metric}",
  NEXT_WEEK: "Susunod na linggo",
  PREVIOUS_WEEK: "Nakaraang linggo",
  NEXT_30_DAYS: "Susunod na 30 Araw",
  PREVIOUS_30_DAYS: "Nakaraang 30 Araw",
  UNABLE_TO_CREATE_TEAM: "Hindi makagawa ng bagong team - pakisubukan ulit! Kung kasali ka na sa isang team, kailangan mong umalis sa iyong kasalukuyang team bago gumawa ng bago.",
  START_TRACKING_METRIC: "Simulan ang pagsubaybay sa {metric}",
  CURRENTLY_NOT_SHARING_DATA: "Kasalukuyang hindi ka nagbabahagi ng anumang data para sa {metric}",
  SUCCESSFULLY_LEFT_TEAM: "Matagumpay kang umalis sa {teamName}",
  LEAVE_TEAM_FAILURE: "Nagkaproblema, pakisubukan ulit!",
  VIEW_DATA_SOURCES: "Tingnan ang mga source ng data",
  OR_CALL_PHONE: "o tumawag sa {phoneNumber}{extension}",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "Walang dokumentong tumutugma sa iyong mga filter",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "Subukang alisin ang ilan sa iyong mga filter para makakuha pa ng mga resulta",
  DOCUMENTS_EMPTY_STATE_HEADING: "Walang (pang) dokumento rito!",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "Isang lugar para tingnan at isaayos ang lahat ng dokumento mo",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team} (Ang iyong team)",
  YOUR_CARE_ADVISOR: "Ang Iyong Gabay sa Pangangalaga",
  COMPOSE: "Mag-compose",
  COMPOSE_NEW_MESSAGE: "Gumawa ng bagong mensahe",
  POLICY_INFORMATION_UNAVAILABLE: "Walang available na impormasyon para sa panahon ng policy na ito.",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "Walang impormasyon para sa miyembrong ito. Subukan ang ibang miyembro.",
  MESSAGE_THREADS_LIST_SUBTITLE: "Maaaring abutin nang hanggang 2 business day bago makatugon ang aming team. Kung medikal na emergency ito, tumawag sa 911.",
  ALL_MESSAGES_FOR_THREAD_BANNER: "Huwag magpadala sa amin ng partikular na impormasyon tungkol sa iyong mga kundisyon sa kalusugan sa chat na ito.",
  TROUBLE_DISPLAYING_DATA: "Nagkakaproblema kami sa pagpapakita ng iyong data",
  RETRY: "Subukan muli",
  YOU: "Ikaw",
  MESSAGES_IN_CONVERSATION_TITLED: "Mga mensahe sa pag-uusap na pinamagatang {subject}",
  TEAM_AVERAGE: "Average ng Team",
  YOUR_METRICS: "Ang Aking Mga Sukatan",
  YOUR_METRICS_SUBTITLE: "Ang pinakabagong data sa kalusugan mula sa iyong mga konektadong app at device.",
  APPS_AND_DEVICES: "Mga App at Device",
  APP_AND_DEVICE_METRICS: "Mga Sukatan ng App at Device",
  VIEW_ALL_METRICS: "Tingnan ang mga sukatan ng mga app at device",
  RENAME: "I-rename",
  ENTER_DOCUMENT_NAME: "Maglagay ng pangalan ng dokumento",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "Mababang priyoridad na aktibidad",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "Katamtamang priyoridad na aktibidad ito",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "Mataas na priyoridad na aktibidad ito",
  ACTIVITY_EXPIRY_DATE: "Mag-e-expire sa {date}",
  GENERIC_MESSAGING_ERROR: "Mukhang nagkaproblema, paki-refresh ang page at subukan ulit.",
  VIEW_THE_ACTIVITIES: "Tingnan ang Mga Aktibidad",
  DRAG_FILES_HERE_OR: "I-drag ang mga file dito o",
  ADD_A_DOCUMENT: "Magdagdag ng dokumento",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "Mag-upload ng dokumento",
  ADD_DOCUMENT: "Magdagdag ng dokumento",
  REMOVE_FILE: "Alisin ang file",
  REMOVE_FILE_FILENAME: "Alisin ang file: {filename} ({fileSize})",
  UNABLE_TO_SEND: "Hindi maipadala, pakisubukan ulit",
  ERROR_HEADING: "Nagkaproblema sa panig namin.",
  FILE_TYPE_NOT_SUPPORTED: "Hindi suportado ang uri ng file",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "Gumamit ng file sa isa sa mga format na ito: {fileTypes}",
  RECENT_MESSAGES: "Mga kamakailang mensahe",
  NO_MESSAGES_YET: "Wala kang mensahe (sa ngayon!)",
  NO_MESSAGES_DETAIL: "Tutulungan ka ng itinalaga mong Gabay sa Pangangalaga sa paggawa ng mas matatalinong desisyon sa pangangalaga sa kalusugan sa bawat hakbang.",
  START_A_NEW_MESSAGE: "Magsimula ng panibagong mensahe",
  USER_ADDED_DOCUMENT: "Nagdagdag ng dokumento si {user}",
  ERROR_SAVING_TRY_AGAIN: "Error sa pag-save ng mga pagbabago. Pakisubukan ulit",
  MODIFIED_ON_DATE: "Binago noong {date}",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "Na-upload noong {date}",
  FILE_TYPE_TOO_LARGE: "Napakalaki ng file",
  MAXIMUM_FILE_SIZE_IS: "Ang maximum na laki ng file ay {fileSize}.",
  FILE_TOO_LARGE: "Oops. Napakalaki ng file na iyan.",
  FILE_SHOULD_BE_UNDER: "Dapat wala pang {fileSize} ang iyong file",
  ATTACHMENT_LIMIT_REACHED: "Oops. Napakaraming file niyan.",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "{maxNumberOfDocs, plural, one {Makakapag-attach ka ng hanggang {maxNumberOfDocs,number} file.} other {Makakapag-attach ka ng hanggang {maxNumberOfDocs,number} (na) file.}}",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "Oops. Hindi suportado ang uri ng file na iyan.",
  ATTACHMENT_TYPES_ALLOWED: "Maaari mong i-attach ang iyong file sa mga format na ito: {fileTypes}",
  MESSAGES_PAGINATION_ERROR: "Hindi mai-load ang mga mensahe. <button>Subukan ulit.</button>",
  TRANSACTION_TABLE_CAPTION: "History ng Transaksyon",
  SELECT_PARTICIPANT_ERROR: "Pumili ng taong padadalhan ng mensahe",
  PREVIEW_OF_FILE: "Preview ng {fileName}",
  VIEW_DOCUMENT_ACTIONS: "Tingnan ang {documentName} mga aksyon.",
  VISIT_LINK: "Bisitahin ang Link",
  COMPLETE_BY_DATE: "Kumpletuhin hanggang {date}",
  ERROR_UPLOADING_YOUR_FILE: "Error sa pag-upload ng iyong file. Subukan ulit.",
  DOCUMENT_SUBMITTED: "Naisumite na ang dokumento",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "Malapit nang maging available ang dokumento mo. Maghintay nang ilang sandali at i-refresh ang page.",
  ERROR_RENAMING_YOUR_DOCUMENT: "Error sa pag-rename ng dokumento mo. Subukan ulit.",
  DOCUMENT_RENAMED_SUCCESSFULLY: "Matagumpay na na-rename ang dokumento.",
  ERROR_HEADING_GENERIC: "Nagkaproblema",
  ERROR_HEADING_NO_CONNECTION: "Walang koneksyon sa internet",
  ERROR_CAPTION_BACKEND: "Nagkaroon ng problema sa pagkonekta sa aming server.\n Pakisubukan muli mamaya.",
  ERROR_CAPTION_NO_CONNECTION: "Nagka-problema sa pag-load ng data.\n  Suriin ang iyong internet at subukan ulit.",
  SURE_YOU_WANT_TO_DELETE: "Sigurado ka bang gusto mong i-delete ang {name}",
  CANNOT_BE_UNDONE: "Hindi maa-undo ang aksyon na ito.",
  DELETE_DOCUMENT: "I-delete ang dokumento",
  DOCUMENT_DELETED: "Na-delete na ang dokumento",
  ERROR_DELETING_YOUR_DOCUMENT: "Error sa pag-delete ng dokumento mo",
  ABOUT_ME: "Tungkol sa Akin",
  SEE_MORE: "TINGNAN ANG IBA PA",
  X_ABOUT_X: "{left} tungkol sa {right}",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "Hindi namin ma-load ang page na ito. Suriin ang iyong internet at subukan ulit.",
  WHO_IS_THIS_DOCUMENT_ABOUT: "Tungkol kanino ang dokumentong ito?",
  SELECT_A_USER: "Pumili ng user",
  SELECT_DOCUMENT: "Pumili ng dokumento",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "Tinutulungan nito ang iyong Gabay sa Pangangalaga na matukoy ang taong nauugnay sa dokumentong ito.",
  VIEW_ALL_MESSAGE_THREADS: "Tingnan ang lahat ng thread ng mensahe",
  TOGGLE_MENU: "I-toggle ang listahan ng mensahe",
  METRICS: "Mga sukatan",
  UNREAD_MESSAGES: "Mga Hindi Nabasang Mensahe",
  WITH_DOCUMENT: "may dokumento",
  X_TOKENS: "{tokenCount, plural, one {{tokenCount,number} token} other {{tokenCount,number} (na) token}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "Mga Programa at Challenge",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "Handa ka ba para sa bagong bagay?",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "Maghanap ng Mga Programa sa Kalusugan at Challenge na tugma sa iyong mga personal na layunin.",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "Mga Programa at Hamon",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "Handa ka ba para sa isang bagay na bago?",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "Maghanap ng Mga Programa at Hamon sa Kalusugan na tumutugma sa mga personal na layunin mo.",
  EXPLORE_LIBRARY: "Galugarin ang Library",
  YOUVE_GOT_THIS: "Kaya mo 'yan!",
  YOUVE_GOT_THIS_NAME: "Kaya mo 'yan, {firstName}!",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "Welcome sa iyong paglalakbay sa kalusugan!",
  HEALTH_JOURNEY_PAGE: "Page ng Paglalakbay sa Kalusugan",
  VIEW_DETAILS: "Tingnan ang Mga Detalye",
  NEXT_ACTIVITY: "Susunod na Aktibidad",
  X_OF_Y_ACTIVITIES: "{completed} sa {total} (na) aktibidad",
  X_PERCENTAGE: "{percent}%",
  SOLUTIONS: "Mga Solusyon",
  EXPLORE: "I-explore",
  DOCUMENT_NAME_TOO_LONG: "Lampas ka sa maximum na limitasyon ng character ({maximum})",
  CLAIM_PATIENT_NAME: "Para sa: {patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "Walang Programa. Walang Challenge. Mga one-off na aktibidad lang.",
  FEEDBACK_LINK_LABEL: "I-rate ang impormasyon {value} mula sa {maximum} sa pagiging kapaki-pakinabang",
  RECENT_ACHIEVEMENTS: "Mga Kamakailang Nagawa",
  ACHIEVEMENTS_BANNER_HEADER: "Congrats sa pagkamit ng mga badge na ito!",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "Gustung-gusto naming makitang inuuna mo ang iyong kalusugan at kagalingan! Tingnan ang iba pang achievement na sinisikap mo.",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "Gustung-gusto naming makitang inuuna mo ang iyong kalusugan at kagalingan! Tingnan ang iba pang achievement na nakuha mo at sinisikap mo.",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "Wala (pang) nagawa!",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "Kumpletuhin ang mga aktibidad sa iyong Journey upang simulang makagawa ng mga achievement.",
  HISTORY_EMPTY_STATE_HEADER: "Wala pang anuman dito!",
  HISTORY_EMPTY_STATE_DESCRIPTION: "Kapag nakumpleto mo ang isang aktibidad, programa, o challenge, lalabas iyon dito.",
  INDIVIDUAL_BALANCE: "{balance} ni {membersFullName}",
  FAMILY_BALANCE: "{balance} ng Pamilya",
  UPCOMING: "Susunod",
  REMOVED: "Inalis",
  COMPLETED_TODAY: "Nakumpleto na",
  CLAIMS_BENEFIT_CARD_TITLE: "Mga Claim sa Insurance",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "Subaybayan kung ano ang utang mo para sa pangangalaga sa iyo at kung magkano ang sagot ng insurance",
  CLAIMS_FILTER_PROPERTY_TYPE: "Uri ng Serbisyo",
  CLAIMS_FILTER_PROPERTY_STATUS: "Katayuan ng Claim",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "MIYEMBRO",
  CLAIMS_FILTER_VALUE_ORAL: "Dental",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "Medikal",
  CLAIMS_FILTER_VALUE_VISION: "Paningin",
  CLAIMS_FILTER_VALUE_PHARMACY: "Botika",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "Propesyonal",
  CLAIMS_FILTER_VALUE_APPROVED: "Babayaran Namin ang Bahagi Namin",
  CLAIMS_FILTER_VALUE_PENDING: "Pinoproseso pa",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "Hindi Buong Sinasagot",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "Hindi Sinasagot",
  CLAIMS_FILTER_VALUE_ADJUSTED: "Ini-adjust",
  DRINKS_PER_WEEK: "Mga inumin kada linggo",
  OR_SELECT_BETWEEN_THESE: "o pumili sa pagitan ng mga ito",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "Range ng Petsa",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "Taon hanggang Ngayon",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "Naunang Taon",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "Huling 12 Buwan",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "Huling 2 Taon",
  CLAIMS_VIEW_ALL: "Tingnan ang Lahat",
  CHALLENGE_TYPE_GROUP_TAG: "Grupo",
  CHALLENGE_TYPE_SOLO_TAG: "Solo",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "Mga Challenge sa Grupo",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "Mga Solong Challenge",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "Manatiling may pananagutan habang sinusubok mo ang iyong sarili sa maingat na pagtatrabaho, mabubuting inisyatibo sa pagkain, mga kumpetisyon sa fitness, mga aktibidad sa pagbawas ng stress, mga challenge sa hakbang, at marami pang iba.",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "Mag-solo at subukin ang sarili na makumpleto ang mga grupo ng mga aktibidad na nakatuon sa pisikal na fitness, nutrisyon, kalusugan ng pag-iisip, pamamahala ng stress, at marami pang iba.",
  HELPFUL_LINKS: "Mga Nakatutulong na Link",
  HOW_CLAIMS_WORK: "Paano Gumagana ang Mga Claim",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "Pag-alam sa kung paano gumagana ang iyong mga claim",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>Kapag binisita mo ang isa sa mga doktor sa iyong network, humihiling sila ng bayad. Kung may copay ka, kukunin nila ito sa iyo at pagkatapos ay magsusumite sila ng claim sa kumpanya ng insurance.</p><p>Pagkatapos ay susuriin namin ang iyong plano ng insurance, kilala rin bilang kontrata sa benepisyo, para matukoy kung sagot ang serbisyo. Kung oo, aaprubahan namin ito batay sa mga tuntunin ng kontrata sa benepisyo. Kung hindi ito sinasagot bilang bahagi ng iyong plano sa benepisyo, ikaw ang responsable sa pagbabayad sa serbisyo.</p>",
  CLAIMS_STATUS_DEFINITIONS: "Mga Kahulugan ng Status ng Pag-claim",
  CLAIMS_STATUS_DEFINITION_APPROVED: "Ibig sabihin ng babayaran namin ang aming bahagi ay inaprubahan ang claim at sumasang-ayon kami na bayaran ang ilan o ang buong claim batay sa plano mo.",
  CLAIMS_STATUS_DEFINITION_PENDING: "Ibig sabihin ng pinoproseso pa ay inaalam namin kung sino ang dapat magbayad at kung ano iyon.",
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: "Ibig sabihin ng hindi buong sinasagot ay sasagutin namin ang isang bahagi ng iyong claim.",
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: "Ibig sabihin ng hindi sagot ay hindi sinasagot ang serbisyo sa iyong plano ng benepisyo at kailangan mong bayaran ito.",
  CLAIMS_STATUS_DEFINITION_ADJUSTED: "Ang ibig sabihin ng adjusted ay may nagbago rito, kaya kinailangan naming tingnan ulit ang claim na ito.",
  YOUR_COST: "Ang iyong Gastos:",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {Medikal na Claim} oral {Dental Claim} vision {Claim sa Paningin} pharmacy {Claim sa Botika} professional {Propesyonal na Claim} other {Claim}}",
  FILTER_BY: "I-filter Ayon sa",
  CLAIMS_LIST_EMPTY_HEADING: "Wala pang kahit ano rito!",
  CLAIMS_LIST_EMPTY_MESSAGE: "Lalabas dito ang mga claim na naisumite.",
  CLAIMS_GENERIC_ERROR_HEADING: "Nagkaproblema",
  CLAIMS_GENERIC_ERROR_MESSAGE: "May teknikal na problema sa panig namin.{br}Pakisubukan ulit sa loob ng ilang minuto.",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "Tingnan kung ano ang sagot ng iyong insurance, at kung ano ang maaaring babayaran mo pa sa iyong provider. Maa-access mo ang iyong mga claim na naisumite sa loob ng dalawang taon hanggang sa petsa ngayon.",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "Walang resulta",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "{filterCount, plural, one {Walang claim na tumutugma sa filter na pinili mo} other {Walang claim na tumutugma sa mga filter na pinili mo}}",
  VIEW_MY_PROFILE: "Tingnan ang Aking Profile",
  PLAN_PROGRESS_COST_COVERAGE: "Pagsulong ng plano at mga gastusin",
  PLAN_PROGRESS_POP_UP_LABEL: "Ano ba ang {title}?",
  RECENT_CLAIMS_LIST_EMPTY: "Lalabas dito ang mga claim na naisumite sa nakalipas na 90 araw. Maa-access mo ang lahat ng claim sa pamamagitan ng pagpili sa 'Tingnan lahat'. ",
  DOWNLOAD_CLAIMS: "I-download ang Mga Claim",
  PLAIN_SOMETHING_WENT_WRONG: "Nagkaproblema",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "Nagka-problema sa pag-download ng iyong mga claim.",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "Ang iyong mga benepisyo, claim, at impormasyon sa kalusugan, lahat sa isang lugar.",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "Handa Ka Na Bang Magsimula?",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "Mag-browse sa aming library para makahanap ng Mga Programa sa Kalusugan at Challenge na tugma sa iyong mga personal na layunin.",
  USER_DROPDOWN_BTN_LABEL: "Menu ng Profile",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "Ano ba ang copay at coinsurance?",
  ANSWERS_NOT_SAVED: "Hindi namin mase-save ang mga sagot mo sa ngayon. Pakisubukan ulit sa mamaya",
  SESSION_ABOUT_TO_TIMEOUT: "Matagal kang hindi aktibo at malapit nang mag-time out ang iyong sesyon. Gusto mo bang ituloy ang iyong sesyon?",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "Matagal kang hindi aktibo at malapit nang mag-time out ang iyong sesyon.",
  SESSION_TIMEOUT: "Nag-timeout ang Sesyon",
  CONTINUE_SESSION: "Ituloy ang Sesyon",
  SESSION_LOG_OUT: "Log Out",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "Pumili ng address para sa paghahatid",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "Sino ang nangangailangan ng bagong card?",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "Pumili ng isa o higit pang miyembro.",
  DEFAULT_ADDRESS: "Default na Address",
  CHOOSE_NEW_ADDRESS: "Pumili ng bagong address",
  CHOOSE_NEW_ADDRESS_HINT: "Magbigay ng ibang mailing address para sa paghahatid ng card. Hindi nito babaguhin ang default na address ng iyong account.",
  CARD_ACTIONS: "Mga aksyon sa card",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "Makakuha ng achievement sa pagkumpleto ng lahat ng aktibidad sa challenge na ito",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "Wala pang anuman dito!",
  CHALLENGE_ACTIVITY_UP_NEXT: "Susunod",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "Bumalik kapag nagsimula ang challenge!",
  CHALLENGE_ACTIVITY_COMPLETED: "Nakumpleto na",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "Lalabas dito ang mga aktibidad na nakumpleto mo.",
  CHALLENGE_ACTIVITY_MISSED: "Napalampas",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "Lalabas dito ang mga aktibidad na napalampas mo.",
  CHALLENGE_COMPLETED_TITLE: "Tapos na!",
  CHALLENGE_COMPLETED_BODY: "Wala nang tatapusing aktibidad. Magaling ang ginawa mo!",
  ACHIEVEMENT_EARNED: "Dapat ipagdiwang ang achievement",
  ACHIEVEMENT_PROGRESS: "Pagsulong ng achievement",
  ADD_DATA: "Magdagdag ng Data",
  CONFIRM_DETAILS: "Kumpirmahin ang mga detalye mo",
  MEDICAL_ID_CARD: "Medikal na ID card",
  NEW_ADDRESS: "Bagong address ng paghahatid",
  MESSAGE_CENTER: "Center ng Mensahe",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "Pagmemensahe",
  CHOOSE_NEW_ADDRESS_DESC: "Magbigay ng ibang mailing address para sa paghahatid ng card. Hindi nito babaguhin ang default na address ng iyong account. Dapat nasa Estados Unidos ang mailing address.",
  SELECT_STATE: "Pumili ng estado",
  REQUIRED_FIELD: "Kinakailangang field ang {field}",
  CLAIMS_YOUR_COST: "Ang iyong gastusin",
  CLAIMS_PROCESSING_DESCRIPTION: "Pinoproseso pa namin ang claim na ito. Ino-notify ka kapag handa nang masuri mo ito.",
  DOWNLOAD_BENEFITS_EXPLANATION: "I-download ang Paliwanag sa Mga Benepisyo",
  DATA_COLLECTED: "Nakuha ang data noong {date}",
  MEMBER_CARD_ON_ITS_WAY: "Paparating na ang bago mong member ID card.",
  MEMBER_CARD_ARRIVAL: "Darating ang (mga) bagong member ID card sa loob ng humigit-kumulang 10 business day.",
  TRY_LATER: "Pakisubukan muli mamaya.",
  RESUBMIT: "Isumite muli",
  ORDER_NEW_CARD_HEADING: "Umorder ng bagong card",
  CANCEL_ORDER_NEW_CARD_TITLE: "Sigurado ka bang gusto mong kanselahin ang iyong order?",
  CANCEL_ORDER_NEW_CARD_DESC: "Maaari kang bumalik kahit kailan.",
  CANCEL_ORDER: "Kanselahin ang order",
  STEP: "Hakbang",
  ORDER_CARD: "Umorder ng card",
  PLEASE_MAKE_A_SELECTION: "Gumawa ng pagpili",
  TOTAL_COST_OVERVIEW: "Kabuuang Pangkalahatang-ideya ng Gastusin",
  TOTAL_COST_DESCRIPTION: "Ang kabuuan ng mga gastusin mula sa mga serbisyo ng iyong claim.",
  SUPPORT: "Suporta",
  GET_SUPPORT: "Kumuha ng Suporta",
  MESSAGE_WITH_A_MEMBER: "Makipag-ugnayan sa isang tagapagtaguyod ng miyembro para sa mga tanong tungkol sa iyong claim.",
  CLAIMS_SERVICE_DETAILS_HEADING: "Mga Detalye ng Serbisyo",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "Mga Karagdagang Resource",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "Isang breakdown ng mga indibidwal na serbisyo na natanggap mo at ang mga gastusing kaugnay ng mga ito.",
  CLAIMS_SERVICE_DETAILS_SERVICE: "Serbisyo",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "Pangalan ng Rx",
  CLAIMS_SERVICE_DETAILS_DATE: "Petsa ng Serbisyo",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "Code ng Procedure",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "Rx Number",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "Ang Iyong Gastusin",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "Mga Detalye ng Gastusin",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "Ipakita ang Mga Detalye ng Gastusin",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "Itago ang Mga Detalye ng Gastusin",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "Breakdown ng iyong gastusin",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "Napupunta ang mga halaga sa ibaba sa pagsulong ng plano mo",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "Pumili ng isa o higit pang miyembro para magpatuloy",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "May mga hindi valid na character ang field",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "Sinisingil ang Provider",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "Iba pang Binayarang Insurance",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "Ang Iyong Diskwento sa Miyembro",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "Nakikipag-areglo sa iyong provider ang iyong plano para mabigyan ka ng rate na may diskwento.",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "Paliwanag sa iyong diskwento sa miyembro",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "Halagang binabayaran ng iba mong insurance.",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: "Paliwanag sa iba pang binayaran ng insurance",
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {Nagbayad ang Plano Mo noong {paymentDate}} undefined {Nagbayad ang Plano Mo n}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "Ang halagang sasagutin ng plano mo para sa mga serbisyo sa pangangalagang pangkalusugan.",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: "Paliwanag sa iba pang binayaran ng plano mo",
  CLAIMS_COST_DETAILS_YOUR_COST: "Ang Iyong Gastusin",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "Komento sa Serbisyo",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "Nasa Network",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "Wala sa Network",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "Pumili ng opsyon sa address para magpatuloy",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "Hindi pwedeng may mga numero ang lungsod",
  MESSAGE_CLOSED_TEXT: "Naihatid na ang mensaheng ito.",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "Kumuha ng pangkalahatang-ideya ukol sa iyong mga naipong gastusin, paghahati sa gastusin (cost-share), at iba pa.",
  VIEW_PLAN_PROGRESS: "Tingnan ang Pagsulong ng Plano",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "Makakatanggap ka ng bayarin na kaugnay ng claim na ito mula sa iyong provider.",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "Nagka-problema sa pag-download ng paliwanag ng mga benepisyo mo.",
  END_CHAT_MODAL_DESCRIPTION: "Magwawakas ang chat sa pagpapatuloy. Hindi ka na makakabalik sa pag-uusap na ito sa susunod.",
  RETURN_TO_CHAT: "Bumalik sa Chat",
  CHATS_IN_CONVERSATION: "Mga chat sa pag uusap",
  LIVE_CHAT_CHARACTER_LIMIT: "Naabot na ang limit ng character ({characterLimit})",
  CHAT_MESSAGE_INPUT: "Pag-input ng chat",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {DEDUCTIBLE} non_covered {Hindi Sinasagot} copayment {Copayment} coinsurance {Coinsurance} may_be_billed {Posibleng Singilin Ka} amount_exceeding_maximum {Halaga na Higit pa sa Maximum} additional_member_liability {Karagdagang Pananagutan ng Miyembro} special_processing {Espesyal na Pagproseso} paid_to_member {Binayaran Sa Miyembro} plan_paid_to_you {Binayaran Ka Ng Plano} other {Iba pa}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "Bagong Mensahe",
  QUESTIONNAIRE_FLOW_CLOSE: "Isara",
  QUESTIONNAIRE_FLOW_GO_BACK: "Bumalik",
  QUESTIONNAIRE_FLOW_CONTINUE: "Magpatuloy",
  QUESTIONNAIRE_FLOW_RETRY: "Subukan muli",
  CLEAR_ALL: "I-clear Lahat",
  HEALTH_REPORT_PAGE_HEADING: "Ang Iyong Ulat sa Kalusugan",
  USERNAME_HAS_JOINED_THE_CHAT: "Sumali sa chat si {username}",
  USERNAME_HAS_LEFT_THE_CHAT: "Umalis sa chat si {username}",
  PERVIOUS_MONTH: "Button ng nakaraang buwan",
  NEXT_MONTH: "Button ng susunod na buwan",
  CHAT_CLOSED: "Sarado na ang chat na ito.",
  CONNECTING_YOU_WITH_AN_AGENT: "Ikinonekta ka sa isang agent...",
  LAST_DAY_TO_USE_BALANCE: "Huling araw para magamit ang balanse",
  LAST_DAY_TO_SUBMIT_CLAIMS: "Huling araw para magsumite ng mga claim",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "Nagkaproblema",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "Nagka-isyu, pakisubukan ulit",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "Nagkaproblema",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "Nagka-isyu, pakisubukan ulit",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "Isara",
  LIVE_CHAT_LANDING_PAGE_TITLE: "Mag-chat",
  LIVE_CHAT_DELIVERY_FAILURE: "Hindi naihatid",
  LIVE_CHAT_RESEND_MESSAGE: "Ipadala muli ang mensahe",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "Hindi naipadala ang mensahe",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "Pasensya na, mukhang hindi nakarating ang iyong mensahe sa aming team.",
  VIEW_TOOLTIP: "tingnan ang {tooltipLabel} tooltip",
  NO_DATA_SOURCES: "Walang Source ng Data",
  MAX_VALUE_ERROR_MESSAGE: "Maglagay ng value na mas mababa o katumbas ng {max}",
  MIN_VALUE_ERROR_MESSAGE: "Maglagay ng value na mas mataas o katumbas ng {min}",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "Pumili ng isang value",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "Dapat mas maaga ang petsa ng pagsisimula kaysa petsa ng pagtatapos",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "Hindi puwedeng higit pa sa 24 na oras ang tagal",
  NO_FUTURE_DATES: "Hindi puwedeng sa hinaharap pa ang petsa",
  CLAIMS_REMOVE_FILTER: "Alisin ang filter na {label}.",
  REMOVE_X_FILTER: "Alisin ang filter na {label}.",
  WALLET_BENEFIT_STATUS_PENDING: "Nakabinbin",
  WALLET_BENEFIT_STATUS_ACTIVE: "Aktibo",
  WALLET_BENEFIT_STATUS_PAST: "Nalipas",
  CLAIMS_CLAIMS_LIST_HEADING: "Ang Iyong Mga Claim",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "Wala pang kahit ano rito!",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "Lalabas dito ang mga aktibong benepisyo.",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "Mga Benepisyo",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "Mga Benepisyo - Mga ID Card",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "Mga Claim",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "Mga Detalye ng Claim",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "Mga Detalye ng Claim - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "Nakabinbin",
  CLAIMS_STATUS_PENDING: "Nakabinbin",
  CSE_END_IMPERSONATION_MODE: "Tapusin ang mode ng pagpapanggap",
  CSE_RESTRICTED_ACTION: "Pinaghihigpitang aksyon",
  CSE_RESTRICTED_ACTION_MESSAGE: "Sa user simulation mode, puwede mo lang tingnan ang portal ng miyembro. Hindi pinapayagan ang mga aksyon sa ngalan ng mga miyembro.",
  CSE_RESTRICTED_VIEW_MESSAGE: "Wala kang pahintulot na i-access ang seksyong ito.",
  CSE_IMPERSONATION_MASONRY_HEADER: "Pinaghihigpitang content para sa mga support agent",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "Maaaring wala kang pahintulot na tingnan ang ilang partikular na widget o content.",
  CSE_IMPERSONATION_BANNER_TEXT: "View-only mode para kay {memberName}.",
  CLAIMS_SUMMARY_PAGE_HEADING: "Mga Claim",
  HEALTH_INSURANCE_GLOSSARY: "Talasalitaan ng Insurance sa Kalusugan",
  CLAIMS_FILTER_LOADING_TEXT: "Nilo-load ang mga tab at filter",
  ANDROID_HEALTH_CONNECT: "Health Connect",
  GET_CARE_SERVICE_CARD_CTA: "Tingnan ang Serbisyo",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "Susunod na Hakbang",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "Nakaraang hakbang",
  HEIGHT: "Taas",
  WEIGHT: "Timbang"
};

// src/strings/fr-CA.json
var fr_CA_default = {
  COMPLETED_ACTIVITIES: "Activit\xE9s termin\xE9es",
  AVAILABLE: "Disponible",
  SEE_MORE_ACTIVITIES: "Voir plus",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "Voir plus d\u2019activit\xE9s termin\xE9es",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "Lorsque vous r\xE9alisez des activit\xE9s, elles apparaissent ici !",
  MISSED_ACTIVITIES: "Activit\xE9s manqu\xE9es",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "Voir plus d\u2019activit\xE9s manqu\xE9es",
  MISSED_ACTIVITIES_EMPTY_STATE: "Si vous manquez des activit\xE9s, elles appara\xEEtront ici !",
  DISPLAYING_ACTIVITIES_FROM_DATE: "Affichage des activit\xE9s depuis {date}",
  STANDALONE_ACTIVITIES: "Activit\xE9s autonomes",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "Il n'y a rien \xE0 faire ici pour l'instant ! Revenez plus tard.",
  VIEW_ALL_STANDALONE_ACTIVITIES: "Voir toutes les activit\xE9s autonomes",
  MODULE_TEMPLATE_BUTTON: "Bouton de mod\xE8le de module",
  ACHIEVEMENTS: "R\xE9alisations",
  VIEW_ACHIEVEMENTS: "Voir les r\xE9alisations",
  DEPENDENT_NOTIFICATION_MESSAGE: "De nouvelles personnes \xE0 charge pourraient \xEAtre invit\xE9es",
  DEPENDENT_COVERED_BY_HSA_ONLY: "Remarque : Cette personne \xE0 charge sera uniquement couverte par votre compte soins de sant\xE9.",
  WALLET_LANDING_NAV_LABEL: "Navigation dans le portefeuille",
  VIEW_CARD_FOR_DEPENDENT: "Voir la carte pour {dependentName}",
  LEARN_MORE_LEAGUE_FAMILY: "\xE0 propos de League Famille pour vos personnes \xE0 charge",
  GO_TO_LIFEMARKET: "Rendez-vous sur le March\xE9vie pour voir la commande num\xE9ro {invoiceId}",
  GO_TO_CLAIM: "Voir les d\xE9tails de la demande pour {title}",
  GO_TO_RECEIPT: "Voir les re\xE7us pour {title}",
  LINK_TO_TRANSACTION_DETAILS: "Lien vers les d\xE9tails",
  VIEW_CLAIM_LABEL_HIDDEN: "Afficher la demande {vendor} {referenceId}",
  QLE_LINK_LABEL: "En apprendre plus sur les \xE9v\xE9nements de vie admissibles",
  PLEASE_CALL: "Veuillez appeler {phoneNumber}",
  BROWSE_FILES: "Parcourir les fichiers",
  TOTAL_BALANCE: "Solde total",
  LIFEMARKET: "March\xE9vie",
  ABOUT: "\xC0 propos",
  LEAGUE_CREDIT_DESCRIPTION_1: "Vous pouvez d\xE9penser votre Cr\xE9dit League dans le {lifemarketLink} pour payer une vari\xE9t\xE9 de produits et de services bien-\xEAtre. Vous ne pouvez pas d\xE9penser votre Cr\xE9dit League sur les produits indiqu\xE9s comme \xAB Offre partenaire League \xBB.",
  LEAGUE_CREDIT_DESCRIPTION_2: "Pour utiliser votre Cr\xE9dit League, s\xE9lectionnez \xAB Cr\xE9dit League \xBB dans la liste des options de paiement lorsque vous \xEAtes pr\xEAt \xE0 payer sur March\xE9vie. {learnMoreLink}",
  DID_YOU_GET_A_PROMO_CODE: "Avez-vous un Code de promotion?",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "Si vous avez re\xE7u du cr\xE9dit ou un code de promotion, vous pouvez le renseigner ici pour recevoir vos cr\xE9dits. Ils seront automatiquement ajout\xE9s \xE0 votre compte.",
  GET_CARE: "Soutien et soins",
  DISCLAIMER: "Avis de non-responsabilit\xE9",
  JOINED: "Rejoint",
  ROLLED_OVER: "Report\xE9",
  NEED_MORE_INFO_SINGLE_CLAIM: "Votre demande n\xE9cessite besoin de plus d'informations pour \xEAtre trait\xE9e. Voir les d\xE9tails ci-dessous",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "Vos demandes n\xE9cessitent plus d'informations pour \xEAtre trait\xE9es. Voir les d\xE9tails ci-dessous",
  RECENT_CLAIMS: "Demandes r\xE9centes",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "Si votre demande de remboursement est urgente, veuillez communiquer avec le <a>service \xE0 la client\xE8le</a>.",
  ABOUT_THIS_BENEFIT: "\xC0 propos de cette prestation",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "Cette prestation est mise \xE0 votre disposition par votre employeur. Pour toute question ou information d\xE9taill\xE9e, veuillez vous adresser \xE0 votre administrateur RH.",
  ID_LIKE_TO_DOTDOTDOT: "J'aimerais...",
  HAS_DEPENDENTS_DESCRIPTION: "Les personnes \xE0 votre charge pour cette prestation sont \xE9num\xE9r\xE9es ci-dessous. Si vous avez r\xE9cemment v\xE9cu un \xE9v\xE9nement de vie, vous pourriez \xEAtre autoris\xE9 \xE0 apporter des modifications \xE0 vos personnes \xE0 charge. Cliquez sur le bouton ci-dessous pour v\xE9rifier en quelques \xE9tapes si vous y avez droit.",
  HAS_NO_DEPENDENTS_DESCRIPTION: "Vous n'avez pas de personnes \xE0 charge associ\xE9es \xE0 cette prestation. Si vous avez r\xE9cemment v\xE9cu un \xE9v\xE9nement de vie, vous pourriez avoir le droit d'ajouter ou de modifier vos personnes \xE0 charge. Cliquez sur le bouton ci-dessous pour v\xE9rifier en quelques \xE9tapes si vous y avez droit.",
  ADD_A_DEPENDENT_CAPITAL: "Ajouter une personne \xE0 charge",
  WALLET_FEEDBACK_TITLE: "Aidez-nous \xE0 am\xE9liorer l'exp\xE9rience portefeuille",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: "Avez-vous trouv\xE9 l'information \xE0 propos de la garantie \u201C{benefitName}\u201D utile?",
  WALLET_FEEDBACK_SUBTITLE_OTHER: "Les informations fournies pour {cardName} vous ont-elles \xE9t\xE9 utiles?",
  GO_BACK: "Retour",
  WALLET_LANDING_TITLE: "Les informations sur votre couverture et vos avantages sociaux r\xE9unies dans un seul et m\xEAme endroit.",
  WALLET_LANDING_BODY: "Votre portefeuille \xE9lectronique contient les avantages sociaux de votre employeur, programmes et les d\xE9tails de votre couverture. Trouvez ce dont vous avez besoin en un clin d'\u0153il.",
  POLICY_ACTIVE: "Police active",
  REMAINING_BALANCE: "Solde",
  CONTACT_CUSTOMER_CARE: "communiquer avec le service \xE0 la client\xE8le",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "Oh oh! Une erreur est survenue",
  WERE_HAVING_TROUBLE: "Nous avons du mal \xE0 charger cette information. Veuillez actualiser la page. Si le probl\xE8me persiste, veuillez contacter {custCareLink}.",
  IN_NETWORK: "Au sein du r\xE9seau",
  OUT_OF_NETWORK: "Hors-R\xE9seau",
  PROGRAMS: "Programmes",
  PROGRAM_CREATED_BY: "Programme cr\xE9\xE9 par",
  TOP_FOCUS_AREA: "Domaine prioritaire",
  DIMENSION_OF_HEALTH: "DIMENSION DE LA SANT\xC9",
  READING_MORE_ABOUT_DIMENSIONS: "Vous souhaitez en savoir plus sur l'impact des dimensions sur votre sant\xE9 g\xE9n\xE9rale?",
  READ_OUR_ARTICLE: "Lisez notre article",
  HEALTH_PROGRAMS_FOR_YOU: "Programmes sant\xE9 pour vous",
  COMPLETE_ACTIVITIES: "Compl\xE9tez les activit\xE9s pour atteindre vos objectifs de sant\xE9. Recommand\xE9 en fonction de votre profil de sant\xE9.",
  FT: "pi",
  CM: "cm",
  IN: "po",
  LB: "lb",
  KG: "kg",
  LETS_BEGIN: "Commen\xE7ons!",
  KEEP_GOING: "Continuez!",
  SKIP: "Sauter",
  HP_ONBOARDING_SUBMIT: "Commen\xE7ons!",
  FOR_PROVIDERS_AND_PHARMACISTS: "Pour les fournisseurs/pharmaciens",
  WEVE_PICKED_UP: "Nous reprenons l\xE0 o\xF9 vous \xE9tiez.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "Il ne vous reste plus que quelques questions avant d'accumuler {points} points.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "Il ne vous reste plus que quelques questions pour compl\xE9ter l'\xE9valuation.",
  PRESS_ENTER: "Suivant",
  SELECT_ALL_THAT_APPLY: "S\xE9lectionner tout ce qui s'applique.",
  SELECT_UP_TO: "S\xE9lectionnez jusqu'\xE0 {number}",
  AMOUNT_QUESTIONS: "{amount} questions",
  QUESTION_PROGRESS: "QUESTION {questionNumber} DE {totalQuestions}",
  COMPLETE: "Termin\xE9",
  VIEW_MORE: "Voir plus",
  DOWNLOAD_APP_STORE: "T\xE9l\xE9charger sur l'App Store",
  DOWNLOAD_PLAY_STORE: "T\xE9l\xE9charger sur Google Play",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "Nous sommes d\xE9sol\xE9s, la page demand\xE9e a expir\xE9 ou n\u2019est plus valide.",
  EARNED_X_TIMES: "Gagn\xE9 {amount}X",
  EARNED: "accumul\xE9",
  YOUVE_EARNED_TOTAL: "Vous avez accumul\xE9 un total de",
  YOUVE_EARNED_GRAND_TOTAL: "Vous avez accumul\xE9 un grand total de",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "Vous avez gagn\xE9 un badge de r\xE9alisation",
  CONGRATS_COMPLETED: "F\xE9licitations, vous avez compl\xE9t\xE9",
  VIEW_MY_HEALTH_DASHBOARD: "Consulter le tableau de bord de ma sant\xE9",
  LATEST_INSIGHTS_METRICS_AND_MORE: "Derni\xE8rs aper\xE7us, mesures et plus encore",
  FEEDBACK_TITLE: "Est-ce que cette information \xE9tait utile?",
  FEEDBACK_MIN_SCALE: "Inutile",
  FEEDBACK_MAX_SCALE: "Tr\xE8s utile",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "Avez-vous r\xE9cemment v\xE9cu des changements dans votre vie ou votre famille?",
  DID_YOU_RECENTLY: "Vous \xEAtes-vous r\xE9cemment mari\xE9 ou avez-vous r\xE9cemment eu un enfant? Certains \xE9v\xE9nements de la vie sont admissibles pour une r\xE9inscription.",
  LEARN_MORE: "En savoir plus",
  VIEW_ACTIVITY: "Voir l'activit\xE9",
  CONTINUE: "Poursuivre",
  ARIA_CIRCLED_CHECKMARK: "Coche blanche entour\xE9e d'un cercle",
  NOT_NOW: "Pas maintenant",
  VAL_POINTS: "{points} points",
  ACTIONS: "Actions",
  VIEW_ALL_CLAIMS: "Voir toutes les demandes",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "Cochez cette case pour continuer",
  PLEASE_NOTE: "Veuillez noter",
  COPY: "Copier",
  STATUS: "Statut",
  STARTED: "Commenc\xE9",
  APPROVED: "Approuv\xE9e",
  CANCELLED: "Annul\xE9e",
  UPLOADED_DOCUMENT: "Import\xE9 le {date} \u2022 Import\xE9 par {user}",
  MORE_INFORMATION_NEEDED: "A besoin de renseignements",
  PAID: "Pay\xE9e",
  PENDING_EXTERNAL_REVIEW: "Envoy\xE9 pour \xE9valuation externe",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "Pr\xE9approbation \u2013 Renseignements compl\xE9mentaires",
  PREAPPROVAL_REJECTED: "Pr\xE9approbation refus\xE9e",
  PREAPPROVED: "Pr\xE9approuv\xE9e",
  REJECTED: "Refus\xE9e",
  SUBMITTED_FOR_PREAPPROVAL: "Soumise pour approbation pr\xE9alable",
  SUBMITTED: "Soumise",
  WALLET: "Portefeuille",
  AMOUNT: "Montant",
  ADD: "Ajouter",
  EDIT: "Modifier",
  DETAILS: "D\xE9tails",
  AND: "et",
  TODAY: "Aujourd'hui",
  CLAIMS: "Demandes",
  GROUP_ID: "Identifiant du groupe",
  ZIP_CODE: "Code ZIP",
  CITY: "Ville",
  STATE: "\xC9tat",
  REMOVE: "Retirer",
  HEALTH_SPENDING_ACCOUNT: "Compte soins de sant\xE9",
  HSA: "CSS",
  LIFESTYLE_SPENDING_ACCOUNT: "Compte de d\xE9penses bien-\xEAtre",
  LSA: "CDB",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "Compte de remboursement des frais de sant\xE9",
  DRUGS: "M\xE9dicaments",
  TRAVEL: "Voyage",
  HEALTH_INSURANCE: "Assurance maladie",
  SETTINGS: "Param\xE8tres",
  ACTIVE: "Actif",
  ENDED: "Termin\xE9",
  SUSPENDED: "Suspendu",
  PENDING: "En attente",
  LOADING: "Chargement",
  ERROR_STATE_TITLE: "Une erreur est survenue",
  HELP_EMAIL: "aide@league.com",
  SUBMIT: "Soumettre",
  EARN_NUMBER_POINTS_FULL: "Accumulez {points} points",
  CLOSE: "Fermer",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "Ce d\xE9fi se d\xE9roule toute l'ann\xE9e",
  END_DAY_AT_TIME: "Se termine le {day}",
  ENDED_DAY_AT_TIME: "Termin\xE9 le {day}",
  START_DAY_AT_TIME: "Commence le {day}",
  SPONSOR_LOGO: "logo {name}",
  START_AND_END_DATES: "Commence le {startDate} - Se termine le {endDate}",
  SIGN_UP_BY_DATE: "Inscrivez-vous avant {date}",
  SIGN_UP_BEGINS_DATE: "L'inscription commence le {date}",
  SIGN_UP_DATE_PASSED: "La date d'inscription est pass\xE9e",
  SIGN_UP_PERIOD_PASSED: "Date d'inscription d\xE9pass\xE9e",
  CHALLENGE_DETAILS: "D\xE9tails du d\xE9fi",
  CHALLENGE_PROGRESS: "Progression du d\xE9fi",
  X_PERCENT_COMPLETE: "{number}% termin\xE9",
  SIGN_UP_FOR_CHALLENGE: "S'inscrire au d\xE9fi",
  GROUP_CHALLENGE: "D\xE9fi de groupe",
  SOLO_CHALLENGE: "D\xE9fi solo",
  TRACK_WEARABLES_ALT_TEXT: "Une personne regardant les donn\xE9es de sa montre connect\xE9e",
  CONNECT_A_DEVICE: "Connecter un appareil",
  DEVICES_CONNECTED_TITLE: "On dirait que vous \xEAtes d\xE9j\xE0 connect\xE9!",
  DEVICES_CONNECTED_DESCRIPTION: "Nous synchronisons d\xE9j\xE0 vos donn\xE9es. Si vous choisissez le suivi automatique, nous pourrons automatiquement marquer les activit\xE9s comme faites en utilisant {dataPoint} de votre appareil.",
  DEVICES_UNCONNECTED_TITLE: "Connexion de votre appareil",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "Suivre automatiquement",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "Suivre manuellement",
  DEVICES_UNCONNECTED_DESCRIPTION: "Connectez vos applications et appareils de suivi de sant\xE9 pr\xE9f\xE9r\xE9s pour marquer automatiquement les activit\xE9s comme faites.",
  TRACK_CHALLENGE_AUTOMATICALLY: "Suivre automatiquement",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "Si vous d\xE9cidez de faire le suivi automatiquement, apr\xE8s votre adh\xE9sion, {instructionsLink} pour connecter votre appareil en utilisant l'application mobile League.",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "suivez ces \xE9tapes",
  TRACK_CHALLENGE_MANUALLY: "Suivre manuellement \xE0 la place",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "Si vous choisissez d'effectuer un suivi manuel, vous pourrez toujours connecter un appareil ult\xE9rieurement. {learnMoreLink} sur le suivi manuel de votre progression.",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "En savoir plus",
  TRACK_HEALTH_DATA_ALT: "Tableau de bord avec un graphique de suivi des donn\xE9es de sant\xE9",
  TRACK_MANUALLY_CONFIRM_TITLE: "Voulez-vous vraiment faire le suivi manuellement?",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "Vous pouvez toujours connecter un appareil ult\xE9rieurement",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "Vous \xEAtes s\xFBr de vouloir sortir ?",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "Si vous choisissez de quitter, il se peut que vos modifications ne soient pas enregistr\xE9es.",
  EXIT: "Sortie",
  CHALLENGE: "D\xE9fi",
  CHALLENGES: "D\xE9fis",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "Inscrivez-vous \xE0 un d\xE9fi pour r\xE9aliser des activit\xE9s quotidiennes et rivalisez avec vos coll\xE8gues!",
  THIS_CHALLENGE_HAS_ENDED: "Ce d\xE9fi est termin\xE9",
  COMPLETED_ON_DATE: "Compl\xE9t\xE9 le {date}",
  ENDED_ON_DATE: "Termin\xE9 le {date}",
  LEAVE_CHALLENGE: "Quitter le d\xE9fi",
  LEAVE_CHALLENGE_QUESTION: "Quitter le d\xE9fi?",
  LEAVE_CHALLENGE_DETAIL: "\xCAtes-vous s\xFBr de vouloir supprimer ce d\xE9fi de votre parcours? Toute progression dans ce d\xE9fi sera remise \xE0 z\xE9ro.",
  LEAVE: "Quitter",
  SIGN_UP_FOR_THE_CHALLENGE: "Inscrivez-vous pour le d\xE9fi",
  CHALLENGE_SENSITIVE_DATA: "Vos donn\xE9es de sant\xE9 et vos renseignements personnels sont extr\xEAmement sensibles et il nous incombe de les prot\xE9ger et de les pr\xE9server. Nous prenons cette responsabilit\xE9 tr\xE8s au s\xE9rieux.",
  CHALLENGE_USING_DATA_TO: "Lorsque vous vous inscrivez \xE0 un d\xE9fi, nous utilisons vos donn\xE9es pour :",
  COMPLETE_ACTIVITIES_SHORT: "Activit\xE9s compl\xE8tes",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "Partagez les progr\xE8s de votre d\xE9fi avec votre employeur",
  SHARE_CHALLENGE_PROGRESS: "Partagez les progr\xE8s de votre d\xE9fi avec vos coll\xE8gues (et votre employeur).",
  RANK_SCORE: "Classez votre score dans un tableau de classement",
  DETERMINE_CHALLENGE_WINNERS: "D\xE9terminer les gagnants du d\xE9fi",
  ACCEPT_BEFORE_CONTINUE: "Vous devez accepter avant de continuer.",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "En cochant cette case, j'accepte la <a>politique de confidentialit\xE9.</a>",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "Pour en savoir plus sur la fa\xE7on dont League g\xE8re et utilise vos donn\xE9es, consultez notre <a>politique de confidentialit\xE9.</a>",
  PRIZE: "Prix",
  RULES: "R\xE8gles",
  LEADERBOARD: "Tableau de classement",
  TOOLTIP_ACTION_DESCRIPTION: "Voir le crit\xE8re de classement",
  MARK_AS_DONE: "Marquer comme fait",
  CLOSE_ACTIVITY: "Fermer l'activit\xE9",
  GOAL_INCOMPLETE: "Vous devez compl\xE9ter l'objectif avant de continuer.",
  PROGRESS_RECORD_ERROR: "Un probl\xE8me est survenu lors de l'enregistrement de votre derni\xE8re progression.",
  YOUVE_COMPLETED: "Vous avez termin\xE9",
  ACTIVITY_COMPLETE: "Activit\xE9 termin\xE9e",
  PAGE_NOT_FOUND_HEADER: "Page introuvable",
  PAGE_NOT_FOUND_CONTENT: "Vous avez l'air un peu perdu!",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "Laissez-nous vous aider \xE0 retrouver votre chemin.",
  PAGE_NOT_FOUND_BUTTON: "Retour \xE0 la page d'accueil",
  REWARD_POINTS: "Points de r\xE9compense",
  REWARD_POINTS_EARNED: "Points de r\xE9compense accumul\xE9s",
  REWARD_POINTS_MISSED: "Points de r\xE9compense manqu\xE9s",
  X_REWARD_POINTS: "{points} points de r\xE9compense",
  X_LEAGUE_POINTS: "{points} points League",
  REWARDS_PROGRAM_ELIGIBLE: "Admissible au programme de r\xE9compenses",
  EXPIRY_DATE: "Date d'expiration",
  NO_EXPIRY_DATE: "Aucune date d'expiration",
  POINTS_BALANCE: "Solde de points",
  REDEEM_NEXT_TIER_POINTS: "Il vous reste {points} avant de pouvoir \xE9changer {value}.",
  VIEW_YOUR_REWARDS: "Voir vos r\xE9compenses",
  REDEEMABLE: "\xC9changeable contre {value}.",
  ACTIVATION_PENDING: "En attente d'activation",
  EXPIRED: "Expir\xE9e",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "Votre compte est actif et en cours de cr\xE9ation.",
  PLEASE_TRY_AGAIN_SHORTLY: "Veuillez r\xE9essayer sous peu",
  MANAGE_DEPENDENTS: "G\xE9rer mes personnes \xE0 charge",
  VIEW_CARD: "Voir la carte",
  MEMBER_ID: "Identifiant du membre",
  SPOUSE: "Conjoint",
  COMMON_LAW: "Conjoint de fait",
  CHILD: "Enfant",
  PARENT: "Parent",
  GRANDPARENT: "Grand-parent",
  GRANDCHILD: "Petit-enfant",
  UNCLE_AUNT: "Oncle / tante",
  NEPHEW_NIECE: "Neveu / ni\xE8ce",
  INELIGIBLE_CHILD: "Enfant non admissible",
  SIBLING: "Fr\xE8re ou s\u0153ur",
  OTHER: "Autre",
  DEPENDENT_CARD_HOLDER: "Carte de {dependentName} pour",
  DATE: "Date",
  DESCRIPTION: "Description",
  TYPE: "Type",
  ID: "Identifiant",
  CLAIM: "Demande",
  TOP_UP: "Recharger",
  ADJUSTMENT: "Modification",
  REFUND: "Remboursement",
  LEAGUE_CREDIT: "Cr\xE9dit League",
  CREDIT_CARD: "Carte de cr\xE9dit",
  CGA: "CBF",
  NO_TRANSACTIONS_YET: "Aucune transaction n'a encore \xE9t\xE9 effectu\xE9e",
  NO_MORE_TRANSACTIONS: "Aucune autre transaction",
  PROMO_CODE: "Code promotionnel",
  PROMO_CODE_NOT_FOUND: "Code de promotion invalide",
  PROMO_CODE_SUCCESS: "Le code de promotion a \xE9t\xE9 \xE9chang\xE9!",
  TOTAL_CREDIT: "Cr\xE9dit total :",
  CREDIT_INFORMATION: "Renseignements sur le cr\xE9dit",
  ENTER_CODE: "Entrez un code",
  DOCUMENT_NAME: "Nom du document",
  REQUIRED: "Requis",
  CANCEL: "Annuler",
  CONFIRM: "Confirmer",
  ALLOCATION: "Allocation",
  COVERED: "Couvert",
  NOT_COVERED: "Non couvert",
  COVERED_WITH_RESTRICTIONS: "Couvert, sous r\xE9serve de restrictions",
  EXPAND_ALL: "Tout afficher",
  COLLAPSE_ALL: "Tout r\xE9duire",
  EXAMPLES: "Exemples : ",
  RESTRICTIONS: "Restrictions",
  SEARCH_COVERAGE: "Rechercher votre couverture",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "Les r\xE9sultats de la recherche s'actualisent automatiquement au fur et \xE0 mesure que vous tapez",
  INDIVIDUAL: "Individuel",
  INDIVIDUAL_CAP: "Individuel",
  DEPENDENTS: "Personnes \xE0 charge",
  SELECT_ONE: "S\xE9lectionnez en une",
  MORE_OPTIONS: "Plus d'options",
  VENDOR: "Fournisseur",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "ils seront automatiquement ajout\xE9s \xE0 votre {benefitName}.",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "<strong>{percent}%</strong> sera ajout\xE9 \xE0 votre <strong>{benefitName}</strong>",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "<strong>{percent}%</strong> sera ajout\xE9 \xE0 votre  <strong>{benefitName}</strong>.",
  DOCUMENT_TITLE: "{title} - League",
  MEDICAL: "Soins m\xE9dicaux",
  TOTAL: "Total",
  PROFILE_UPDATE_FAILED: "\xC9chec de la mise \xE0 jour du profil",
  PROFILE_PHOTO: "Photo de profil",
  PROFILE_PHOTO_DESCRIPTION: "Cette photo appara\xEEt sur votre profil d'utilisateur et votre profil Sant\xE9",
  SELECT_AVATAR: "S\xE9lectionnez un avatar",
  SELECT_AVATAR_ERROR: "Vous devez choisir un avatar avant de continuer.",
  GET_AVATARS_ERROR: "Oups! Il y a eu un probl\xE8me pour charger les avatars. Veuillez rafra\xEEchir et r\xE9essayer.",
  ADDRESS_LINE_1: "Adresse ligne 1",
  ADDRESS_LINE_2: "Adresse ligne 2",
  LANGUAGE_SELECTOR_ENGLISH: "Anglais",
  LANGUAGE_SELECTOR_FRENCH: "Fran\xE7ais",
  LANGUAGE_SELECTOR_SPANISH: "Espa\xF1ol",
  DOMESTIC_PARTNERSHIP: "Partenariat domestique",
  YES: "Oui",
  NO: "Non",
  WHY_DO_YOU_ASK: "Pourquoi avez-vous besoin de cette information?",
  LAST_DAY_OF_ENROLLMENT: "Dernier jour de la p\xE9riode d\u2019inscription : {lastDay}",
  WALLET_WELCOME: "Bienvenue dans votre portefeuille!",
  WALLET_DESCRIPTION: "Votre portefeuille est l\u2019endroit o\xF9 vous pourrez acc\xE9der \xE0 vos avantages sociaux, soumettre des demandes et v\xE9rifier votre couverture. Vous pourrez soumettre des demandes pour frais engag\xE9s durant la p\xE9riode d\u2019inscription une fois que votre portefeuille sera activ\xE9. En attendant, {healthProgramLink}!",
  HEALTH_PROGRAM_LINK_COPY: "essayez l\u2019un de nos programmes de sant\xE9",
  FINALIZE_SELECTIONS_HEADER_COPY: "Souhaitez-vous acc\xE9der \xE0 vos avantages sociaux plus t\xF4t?",
  FINALIZE_SELECTIONS_COPY_1: "En finalisant vos s\xE9lections, nous pourrons imm\xE9diatement envoyer les d\xE9tails de votre r\xE9gime \xE0 l\u2019assureur! Cela signifie que vous ne devrez pas attendre la fin de votre p\xE9riode d\u2019inscription pour consulter votre portefeuille.",
  FINALIZE_SELECTIONS_COPY_2: "Cela signifie \xE9galement qu'une fois que vous aurez cliqu\xE9 sur soumettre, vous ne pourrez plus modifier votre r\xE9gime.",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "Je comprends que je ne pourrai plus modifier mes s\xE9lections.",
  FINALIZE_PLAN_SELECTIONS: "Finaliser les s\xE9lections de mon r\xE9gime.",
  ERROR_FINALIZING_SELECTIONS: "Une erreur est survenue, nous n\u2019avons pas pu soumettre vos s\xE9lections.",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "Vos s\xE9lections semblent incompl\xE8tes. Veuillez r\xE9aliser votre inscription et r\xE9essayer.",
  SELECTIONS_FINALIZED_SUCCESS: "S\xE9lection termin\xE9e!",
  TOTAL_REWARDS_LABEL: "R\xE9capitulatif de votre couverture",
  TOTAL_REWARDS_DESCRIPTION: "R\xE9viser la s\xE9lection du r\xE9gime effectu\xE9e pendant l\u2019inscription.",
  TOTAL_REWARDS_LINK: "Voir le r\xE9capitulatif de votre couverture",
  CHANGE_PLAN_LABEL: "Changer les s\xE9lections du r\xE9gime",
  CHANGE_PLAN_DESCRIPTION: "Souhaitez-vous modifier votre r\xE9gime ou vos renseignements personnels?",
  CHANGE_PLAN_LINK: "Modifier la s\xE9lection du r\xE9gime",
  DISMISS_TOAST: "Ignorer la notification",
  UPLOAD_DOCUMENT: "T\xE9l\xE9verser un document",
  REQUIRED_FORM: "Requis",
  PERCENTAGE_USED: "Pourcentage utilis\xE9.",
  SUCCESS: "C'est r\xE9ussi!",
  ERROR: "Erreur",
  OVERVIEW: "Aper\xE7u",
  THIS_FIELD_IS_REQUIRED: "Ce champs est obligatoire",
  INVALID_ZIP_CODE_NO_FORMAT: "Code postal invalide",
  INVALID_DAY_ERROR_MESSAGE: "Le jour que vous avez s\xE9lectionn\xE9 est invalide.",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "Doit comporter {length} caract\xE8res au maximum",
  COMPLETED_STATE_TITLE: "Compl\xE9t\xE9",
  DRAG_OR_UPLOAD_PHOTO: "T\xE9l\xE9versez une photo de ce document",
  ADD_IMAGE: "Ajoutez plus de pages \xE0 ce document (le cas \xE9ch\xE9ant)",
  IDENTIFICATION_TYPE_TWO_FILES: "Les documents d\u2019identification ne peuvent comporter que 2 images.",
  WELCOME: "Bienvenue",
  GOT_IT: "J'ai compris",
  SAVE: "Sauvegarder",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "Voir plus de d\xE9tails",
  LAST_UPDATED: "Derni\xE8re mise \xE0 jour",
  CLAIM_REF_NUMBER: "Num\xE9ro de demande",
  VIEW_ALL: "Voir tout",
  HRA: "ERSS",
  SEARCH: "Rechercher",
  DAY: "Jour",
  MONTH: "Mois",
  YEAR: "Ann\xE9e",
  BACK: "Retour",
  NEXT: "Suivant",
  PREVIOUS: "Pr\xE9c\xE9dent",
  HELP_CENTER: "Centre d'assistance",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "R\xE9duire le clavardage",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "R\xE9ponses sugg\xE9r\xE9es disponibles",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "Un participant au clavardage est en train d'\xE9crire",
  VIEW_DOCUMENT: "Afficher le document\xA0:",
  CATEGORIES: "Cat\xE9gories",
  ACTIVE_PROGRAM: "{length, plural, one {Programmes actifs} other {Programmes actifs}}",
  ACTIVE_CHALLENGE: "{length, plural, one {D\xE9fi en cours} other {D\xE9fis en cours}}",
  PROGRAM_PROGRESS: "Progr\xE8s des programmes",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "Vous pouvez aussi \xE9crire quelque chose pour cr\xE9er une nouvelle option!",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "S\xE9lectionnez vos options",
  COVERAGE: "Couverture",
  HEALTH_PROFILE: "Profil sant\xE9",
  HEALTH_PROGRAM: "Programme sant\xE9",
  HEALTH_PROGRAMS: "Programmes sant\xE9",
  STEPS: "pas",
  ACTIVE_DURATION: "minutes actives",
  MINDFUL_DURATION: "minutes de pleine conscience",
  FLOORS_CLIMBED: "\xE9tages mont\xE9s",
  DISTANCE: "distance parcourue",
  ENERGY_BURNED: "calories br\xFBl\xE9es",
  METRIC_DETAILS: "D\xE9tails des mesures",
  HEALTH_PROGRAMS_SYNC_DEVICE: "Synchronisez votre {device} pour une exp\xE9rience optimale du programme.",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "Programme sant\xE9 connect\xE9",
  TRACK_AUTOMATICALLY: "Suivez vos progr\xE8s automatiquement",
  SYNC_ISSUE: "PROBL\xC8ME DE SYNCHRONISATION",
  FIX_SYNC_ISSUE: "R\xE9soudre ce probl\xE8me",
  DATAPOINT_SYNC_ISSUE: "Un probl\xE8me est survenu lors de la synchronisation de votre {dataPoint}.",
  CONNECT_TO_TRACK: "Connectez vos applications et appareils de sant\xE9 pr\xE9f\xE9r\xE9s pour marquer automatiquement les activit\xE9s comme termin\xE9es.",
  CONNECT_APPS_DEVICES: "Connecter des applis et des appareils",
  ADD_APPS_DEVICES: "Voir les applications et les appareils",
  MANAGE_UNITS: "Modifier les unit\xE9s de mesure",
  DONT_CONNECT_APPS_DEVICES: "Ne pas suivre mes progr\xE8s automatiquement",
  CONNECTING_UNAVAILABLE: "La connexion d'applications et d'appareils n'est pas disponible sur le Web",
  CONNECTING_UNAVAILABLE_DETAILS: "Pour l'instant, veuillez consulter l'application mobile League pour connecter vos applications et appareils de sant\xE9 pr\xE9f\xE9r\xE9s.",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "Pour l'instant, veuillez consulter l'application mobile League pour connecter vos applications et appareils de sant\xE9 pr\xE9f\xE9r\xE9s. Si vous souhaitez en savoir plus, vous pouvez trouver une liste compl\xE8te des",
  CONNECT_DEVICES_LEARN_MORE: "En savoir plus",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "applications et appareils actuellement pris en charge",
  DONT_HAVE_THE_APP: "Vous n'avez pas l'application mobile League ?",
  WHAT_DEVICES_CAN_CONNECT: "Quelles sont les applications et les appareils pris en charge ?",
  CONNECTED_DEVICES_IMAGE_ALT: "Personne examinant les donn\xE9es d'un appareil connect\xE9",
  MORE_GREAT_RESOURCES: "Encore plus de ressources",
  HISTORY: "Historique",
  TOP_MATCH: "Meilleurs r\xE9sultats",
  CHAT: "Clavarder",
  END_CHAT: "Terminer le clavardage",
  BECOMES_ACTIVE_ON_DATE: "Devient actif le {date}",
  BECOMES_ACTIVE_IN_X_DAYS: "Cette garantie s'active dans {days} jours",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "Pour cet avantage, vous pouvez seulement soumettre des demandes avec une date d'achat/service du <strong>{dateRange}</strong>. Consultez les d\xE9tails de votre couverture pour en savoir plus.",
  SUBMIT_PAST_CLAIMS_BY_DATE: "Soumettre des demandes ant\xE9rieures avant le {date}",
  THIS_POLICY_HAS_EXPIRED: "Cette p\xE9riode contractuelle est expir\xE9e, mais vous avez encore {days} jours pour soumettre des demandes ant\xE9rieures au titre de cet avantage.",
  THIS_POLICY_EXPIRES_TOMORROW: "Cette p\xE9riode contractuelle est expir\xE9e et aujourd'hui est votre dernier jour pour pr\xE9senter des demandes ant\xE9rieures au titre de cet avantage",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "Vous avez jusqu'\xE0 <strong>{inactiveDate}</strong> pour soumettre des anciennes demandes d'achats/services effectu\xE9es avant le <strong>{endDate}</strong>.",
  EXPIRES_ON_DATE: "Expire le {date}",
  ASSIGNED: "Attribu\xE9",
  ASSIGNED_ON: "le {date}",
  ASSIGNED_TODAY: "aujourd'hui",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "Vous avez {days} jours pour utiliser cette prestation avant que la p\xE9riode contractuelle arrive \xE0 terme",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "Apr\xE8s l'expiration, vous aurez jusqu'au {inactiveDate} pour soumettre des demandes avec une date d'achat/de service le ou avant le {endDate}",
  LOAD_MORE: "Charger plus de r\xE9sultats",
  SHOW_MORE: "Afficher plus",
  LINK_OUT_MESSAGE: "\xC0 voir",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "Oups, il semble y avoir eu un probl\xE8me.",
  AGENT_PERMISSION_ERROR: "Vous n'avez pas l'autorisation de voir cela.",
  TRY_AGAIN: "R\xE9essayer",
  WARNING_OPENS_NEW_TAB: "S'ouvre dans un nouvel onglet",
  ANY_HEALTH_CHANGES: "Y a-t-il eu des changements r\xE9cents dans votre sant\xE9?",
  LAST_UPDATED_DATE: "Derni\xE8re mise \xE0 jour le {date}",
  UPDATE_HEALTH_PROFILE: "Mettre \xE0 jour votre profil sant\xE9",
  HEALTH_PROFILE_COMPLETION_POINTS: "Vous avez accumul\xE9 {points} points",
  OPENS_IN_A_NEW_TAB: "S'ouvre dans un nouvel onglet",
  COMPLETED: "Compl\xE9t\xE9",
  COMPLETED_X_TIMES: "{amount, plural, one {Compl\xE9t\xE9 {amount} fois} other {Compl\xE9t\xE9 {amount} fois}}",
  REMOVE_FROM_JOURNEY: "Retirer du parcours",
  GO_TO_JOURNEY: "Aller au parcours",
  ABOUT_THIS_PROGRAM: "\xC0 propos de ce programme",
  HEALTH_JOURNEY: "Parcours sant\xE9",
  REMOVE_ACTIVITY: "Supprimer l'activit\xE9?",
  REMOVE_ACTIVITY_PROMPT: "Supprimer l'activit\xE9",
  REMOVE_ACTIVITY_BUTTON: "Retirer",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "\xCAtes-vous certain de vouloir supprimer cette activit\xE9 de votre parcours sant\xE9? Cette action est irr\xE9versible.",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "Vous ne le verrez plus dans votre parcours",
  DISCOVER_ACTIVITIES: "D\xE9couvrir de nouvelles activit\xE9s",
  DISCOVER_ACTIVITIES_CAPTION: "Parcourez les activit\xE9s qui devraient vous plaire",
  ACTIVITIES: "Activit\xE9s",
  ACTIVITIES_PLURALIZATION: "{count, plural, one {Activit\xE9} other {Activit\xE9s}}",
  ACTIVITIES_PILL: "Activit\xE9s",
  PROGRAM_LIBRARY: "Tous les programmes",
  BROWSE_PROGRAMS: "Parcourir les programmes",
  BACK_TO_JOURNEY: "Revenir au parcours",
  GO_TO_HEALTH_JOURNEY: "Aller au Parcours sant\xE9",
  ADD_TO_JOURNEY: "Ajouter au parcours",
  REQUEST_WAIT: "Veuillez patienter pendant que nous traitons votre demande",
  JOURNEY_DELIGHT_1: "La motivation vous fait d\xE9marrer. L'habitude vous fait avancer. \xC0 vous de jouer!",
  JOURNEY_DELIGHT_2: "Actif. Concentr\xE9. Dans votre voie. C'est ce que nous aimons voir.",
  JOURNEY_DELIGHT_3: "Les petits pas peuvent faire une grande diff\xE9rence. Vous faites ce pas!",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "{hasValue, select, true {Il s'agit d'une option de texte libre; utilisez la zone de texte pour saisir votre propre valeur. La valeur actuelle est\xA0: {value}} other {Il s'agit d'une option de texte libre; utilisez la zone de texte pour saisir votre propre valeur. La valeur actuelle est\xA0: vide}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "Cette option est exclusive; la s\xE9lectionner d\xE9s\xE9lectionnera toutes les autres",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "Lancer ou mettre en pause l'animation s\xE9lectionn\xE9e",
  CENTIMETERS: "Centim\xE8tres",
  FEET: "Pieds",
  INCHES: "Pouces",
  KILOGRAMS: "Kilogrammes",
  POUNDS: "Livres sterling",
  EARN_UP_TO_X_POINTS: "Accumulez jusqu'\xE0 {points} points",
  EXPIRES_TODAY: "Expire aujourd'hui",
  PROGRAM_DETAILS: "D\xE9tails du programme",
  PROGRESS: "Progr\xE8s",
  X_OF_Y_COMPLETE: "{completed} / {total} termin\xE9",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "{missed} manqu\xE9",
  MISSED: "Manqu\xE9",
  SCROLL_CAROUSEL_RIGHT: "Faites d\xE9filer le carrousel vers l'avant",
  SCROLL_CAROUSEL_LEFT: "Faites d\xE9filer le carrousel vers l'arri\xE8re",
  SCROLL_CAROUSEL_FORWARD: "Faites d\xE9filer le carrousel vers l'avant",
  SCROLL_CAROUSEL_BACK: "Faites d\xE9filer le carrousel vers l'arri\xE8re",
  YOU_HAVENT_STARTED_PROGRAMS: "Vous n\u2019avez pas commenc\xE9 de programmes sant\xE9",
  ADD_PROGRAM: "Ajouter un programme",
  TAP_ADD_PROGRAM_BUTTON: 'Cliquez sur le bouton "Ajouter un programme" pour parcourir les programmes.',
  YOUVE_REACHED_PROGRAM_LIMIT: "Vous avez atteint la limite de votre programme!",
  START_X_MORE_PROGRAMS: "{duration, plural, one {Commencez {duration,number} plus de programmes. Maintenez l'\xE9lan.} other {Commencez {duration,number} plus de programmes. Maintenez l'\xE9lan.}}",
  OK: "D'accord",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "Terminez l\u2019un de vos programmes actifs pour ajouter celui-ci \xE0 votre parcours.",
  SHOW_LESS: "Voir moins",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "Il semble que vous avez atteint la limite de votre programme!",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "Terminez un programme en cours avant d'en ajouter un nouveau. <strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "Retirer le programme?",
  REMOVE_PROGRAM_BUTTON: "Retirer",
  ARE_YOU_SURE_REMOVE_PROGRAM: "\xCAtes-vous s\xFBr de vouloir retirer ce programme de votre parcours? Toute progression dans ce programme sera r\xE9initialis\xE9e et vous devrez recommencer.",
  PROGRAM_REMOVED: "Programme retir\xE9",
  PROGRAM_ADDED: "Programme ajout\xE9",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "Le programme appara\xEEtra d\xE9sormais dans votre parcours",
  PROGRAM_ADD_ERROR: "Nous n'avons pas \xE9t\xE9 en mesure d'ajouter le programme pour le moment. Veuillez r\xE9essayer ou choisir un autre programme.",
  PROGRAM_REMOVE_ERROR: "Nous n'avons pas \xE9t\xE9 en mesure de supprimer le programme pour le moment. Veuillez r\xE9essayer.",
  SKIP_TO_CONTENT_MESSAGE: "Aller au contenu",
  MAIN_NAVIGATION_LIST_LABEL: "\xC9l\xE9ments de navigation de League",
  PAGE_TITLE_ACTIVITY_DETAILS: "D\xE9tails de l'activit\xE9",
  X_OF_Y: "{current} sur {total}",
  ACTIVITY_COUNT_EDIT: "Modifier {unit}",
  ACTIVITY_COUNT_GOAL: "Objectif: {goal,number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: `Cette activit\xE9 ne sera pas termin\xE9e tant que vous n'aurez pas "marquer comme fait".`,
  ACTIVITY_COMPLETE_LATER_PROMPT: 'Pour continuer, cliquez sur "Retour au parcours" et revenez plus tard pour le marquer comme fait.',
  ACTIVITY_COMPLETE_ERROR: "Nous n'avons pas \xE9t\xE9 en mesure de compl\xE9ter l'activit\xE9 pour le moment. Veuillez r\xE9essayer.",
  ACTIVITY_DISMISS_ERROR: "Nous n'avons pas \xE9t\xE9 en mesure de supprimer l'activit\xE9 pour le moment. Veuillez r\xE9essayer.",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "Veuillez s\xE9lectionner un jour, un mois et une ann\xE9e",
  TOOLBOX_VALIDATION_MONTH_YEAR: "Veuillez s\xE9lectionner un mois et une ann\xE9e",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "La limite de caract\xE8res est {limit}",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "Entrez un nombre \xE9gal ou sup\xE9rieur \xE0 {value}",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "Entrez un nombre \xE9gal ou inf\xE9rieur \xE0 {value}",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "Ce champ est obligatoire",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "Veuillez faire une s\xE9lection",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "Veuillez faire une s\xE9lection",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "S\xE9lectionnez une date post\xE9rieure \xE0 {date,date,medium}",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "S\xE9lectionnez une date ant\xE9rieure \xE0 {date,date,medium}",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "Entrez un nombre entier",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "S\xE9lectionnez un maximum de {integer} options",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "S\xE9lectionnez {integer} options pour continuer",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "Le champ ne peut pas \xEAtre vide lorsqu'il est s\xE9lectionn\xE9",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "Le poids doit \xEAtre d'au moins {imperial}\xA0lb ({metric}\xA0kg)",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "Le poids doit \xEAtre au plus {imperial}\xA0lb ({metric}\xA0kg)",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "La hauteur doit \xEAtre d'au moins {imperial} ({metric}\xA0cm)",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "La hauteur doit \xEAtre au maximum {imperial} ({metric} cm)",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "Entrez votre poids en kilogrammes",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "Entrez votre poids en livres",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "Entrez votre taille en centim\xE8tres",
  ENTER_YOUR_HEIGHT_IN_FEET: "Entrez votre taille en pieds",
  ENTER_YOUR_HEIGHT_IN_INCHES: "Entrez votre taille en pouces",
  GOAL_REACHED: "Vous avez atteint l'objectif!",
  COMPLETE_ACTIVITY: "Activit\xE9 termin\xE9e",
  EXIT_ACTIVITY_TITLE: "\xCAtes-vous certain de vouloir quitter cette activit\xE9?",
  EXIT_ACTIVITY_BODY: "Votre progression ne sera pas sauvegard\xE9e et vous devrez recommencer l'activit\xE9 depuis le d\xE9but.",
  EXIT_ACTIVITY_CTA: "Quitter l'activit\xE9",
  ACTIVITY_NEXT_STEP: "Continuer l'activit\xE9",
  ACTIVITY_PREV_STEP: "\u2190 Retour \xE0 l'\xE9tape pr\xE9c\xE9dente",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "Cat\xE9gorie du programme sant\xE9",
  NEXT_STEP: "\xC9tape suivante",
  YOU_EARNED: "Vous avez accumul\xE9",
  YOU_EARNED_X_POINTS: "Vous avez accumul\xE9 {points} points!",
  CREATE: "Cr\xE9er {label}",
  TOMORROW: "Demain",
  YESTERDAY: "Hier",
  BACK_TO_TODAY: "Retour \xE0 aujourd'hui",
  PREVIOUS_DAY: "Jour pr\xE9c\xE9dent",
  NEXT_DAY: "Jour suivant",
  ACTIVITY_STATUS: "Statut de l'activit\xE9 : ",
  ACTIVITY_STATUS_EXPIRED: "Expir\xE9e",
  ACTIVITY_STATUS_REMOVED: "Ignor\xE9",
  ACTIVITY_STATUS_UPCOMING: "Disponible prochainement",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "Rien de pr\xE9vu pour l'instant",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: `Appuyez sur le bouton "Ajouter" pour trouver de nouveaux programmes ou parcourir une liste d'activit\xE9s s\xE9lectionn\xE9es juste pour vous.`,
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "Bravo, vous \xEAtes sur une lanc\xE9e!",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "Revenez demain pour en savoir plus ou parcourez les activit\xE9s propos\xE9es.",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "Vous n'avez pas d'activit\xE9s pr\xE9vues pour ce jour.",
  NO_ACTIVITIES_PAST_HEADING: "Jour de repos",
  NO_ACTIVITIES_PAST_DESCRIPTION: "On dirait que vous avez pris cette journ\xE9e pour vous d\xE9tendre et r\xE9cup\xE9rer.",
  BUTTON_BROWSE_PROGRAMS: "Parcourir les programmes",
  IMG_ALT_HIGH_FIVE_HANDS: "Deux mains font un tape m'en cinq.",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "Femme debout faisant d\xE9filer une liste de programmes de la taille d'un mur.",
  IMG_ALT_EMPTY_STATE_REST: "Homme se reposant dans un fauteuil confortable avec une boisson chaude et des \xE9couteurs.",
  THIS_IS_JUST_A_SNEAK_PEAK: "Ce n'est qu'un avant-go\xFBt!",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "Revenez et terminez cette activit\xE9 le {date}.",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "Revenez plus tard pour compl\xE9ter cette activit\xE9.",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "On dirait que vous avez manqu\xE9 cette activit\xE9!",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "Cette activit\xE9 a expir\xE9 le {date}.",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "On dirait que cette activit\xE9 n'\xE9tait pas faite pour toi.",
  YOU_SKIPPED_THIS_ACTIVITY: "Vous avez saut\xE9 cette activit\xE9.",
  NO_PROGRAMS: "Rien \xE0 voir ici pour l'instant",
  NO_ACTIVITY_TITLE: "Il n\u2019y a rien \xE0 voir par ici (pour le moment!)",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "Rendez-vous ici pour voir votre classement lorsque le d\xE9fi commencera.",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "Rendez-vous ici pour voir les highlights de vos d\xE9fis.",
  LEADERBOARD_ALL_TIME_RANKING: "Classement de tous les temps",
  LEADERBOARD_CURRENT_USER_NAME: "{user} (Vous)",
  ACTIVITY_PROGRESS: "Progression de l'activit\xE9",
  YOUR_JOURNEY: "Votre parcours",
  SUGGESTED_FOR_YOU: "Sugg\xE9r\xE9 pour vous",
  NO_ACHIEVEMENTS_DESCRIPTION: "Terminez les activit\xE9s de votre parcours pour commencer \xE0 d\xE9bloquer des R\xE9alisations.",
  UNLOCK_ACHIEVEMENTS: "D\xE9bloquez une r\xE9alisation pour avoir termin\xE9 toutes les activit\xE9s de ce programme.",
  MEMBERS: "Membres",
  SIGN_UP_PERIOD_PASSED_DETAIL: "Vous ne pouvez plus vous inscrire \xE0 ce d\xE9fi car la date d'inscription est pass\xE9e.",
  REFERENCES: "R\xE9f\xE9rences",
  SIGN_UP_BY: "Inscrivez-vous avant {date}",
  YOURE_GOOD_TO_GO: "Vous \xEAtes pr\xEAt \xE0 partir!",
  CHECK_CHALLENGE_DETAILS: "Consultez les d\xE9tails du d\xE9fi pour vous familiariser avec les activit\xE9s",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "Commen\xE7ons le d\xE9fi. Voulez-vous rejoindre ou cr\xE9er une \xE9quipe?",
  CHALLENGE_ABOUT_TO_START: "Votre d\xE9fi est sur le point de commencer!",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "Les activit\xE9s quotidiennes appara\xEEtront ici au d\xE9but de votre d\xE9fi, le <strong>{date}</strong>.",
  NO_CHALLENGE_ACTIVITIES_TODAY: "Il n'y a pas d'activit\xE9s dans ce d\xE9fi pour vous aujourd'hui.",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: '"{query}" introuvable.',
  COVERAGE_SEARCH_TRY_AGAIN: "Essayez de rechercher \xE0 nouveau avec un mot-cl\xE9 diff\xE9rent.",
  EARN: "Accumuler",
  WORD_WITH_EXCLAMATION_POINT: "{word}!",
  UPDATED: "Actualis\xE9",
  YOUR_PROGRESS: "Votre progr\xE8s",
  YOUR_PROGRESS_IN_NUMBERS: "{current} of {total} {unit} ou {percentage}\xA0pourcentage termin\xE9",
  OUTCOMES_FRICTION_SCREEN_TITLE: "Des suggestions de personnalisation...",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "Laissez-nous le temps de trouver des ressources utiles pour vous en fonction de vos r\xE9ponses.",
  RELOAD_MY_SUGGESTIONS: "Rechargez mes suggestions",
  YOUR_RANKING: "Votre classement",
  TOP_RANKED: "Mieux class\xE9",
  VIEW_CHALLENGE_DETAILS: "Afficher les d\xE9tails du d\xE9fi",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "Demandez-vous la raison pour laquelle il est avantageux d'ajouter vos personnes \xE0 charge?",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "Vos personnes \xE0 charge peuvent acc\xE9der League et profiter elles aussi d'une vie plus saine!",
  UNABLE_TO_LEAVE_CHALLENGE: "Impossible de quitter le d\xE9fi, veuillez r\xE9essayer!",
  SUCCESSFULLY_LEFT_CHALLENGE: "Vous avez quitt\xE9 le d\xE9fi avec succ\xE8s!",
  UNABLE_TO_JOIN_CHALLENGE: "Impossible de rejoindre le d\xE9fi, veuillez r\xE9essayer!",
  JOIN_TEAM: "Rejoindre une \xE9quipe",
  X_TEAM_MEMBERS: "{memberTotal, plural, one {{memberTotal,number} membre de l'\xE9quipe} other {{memberTotal,number} membres de l'\xE9quipe}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, one {{spotsAvailable,number} place disponible} other {{spotsAvailable,number} places disponibles}}",
  HIGHLIGHTS: "Points saillants",
  CURRENT_RANK: "Rang actuel",
  X_LIKES: "{activityLikes, plural, one {{activityLikes,number} J'aime} other {{activityLikes,number} J'aime}}",
  LIKE: "Aimer",
  LIKED: "Aim\xE9",
  TEAM_INVITE_HEADING: "Nous sommes meilleurs ensemble!",
  TEAM_INVITE_DESCRIPTION: "Invitez des amis \xE0 se joindre \xE0 votre \xE9quipe pour r\xE9aliser des activit\xE9s ensemble.",
  TEAM_INVITE_LINK_DESCRIPTION: "Plus on est de fous, plus on rit! Partagez ce lien avec vos amis pour qu'ils puissent se joindre \xE0 votre \xE9quipe.",
  TEAM_INVITE_LINK: "Lien d'invitation pour l'\xE9quipe",
  INVITE: "Invitation",
  LEAVE_TEAM: "Quitter l'\xE9quipe",
  INVITE_FRIENDS: "Inviter des amis",
  PRIVACY: "Confidentialit\xE9",
  JOIN_TEAM_SUCCESS_TITLE: "Bienvenue dans l'\xE9quipe!",
  JOIN_TEAM_SUCCESS_DETAIL: "Conqu\xE9rons ces activit\xE9s ensemble.",
  JOIN_TEAM_SUCCESS_BUTTON: "Afficher les d\xE9tails de l'\xE9quipe",
  CANT_JOIN_TEAM_TITLE: "Vous ne pouvez pas encore rejoindre {teamName}",
  CANT_JOIN_TEAM_DETAIL: "Veuillez vous inscrire au d\xE9fi afin de rejoindre {teamName}",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "Il semble que vous ne vous soyez pas encore inscrit au d\xE9fi. Vous devez vous inscrire avant de pouvoir rejoindre {teamName}.",
  LEAVE_TEAM_QUESTION: "Voulez-vous quitter {teamName}?",
  LEAVE_TEAM_DESCRIPTION: "\xCAtes-vous s\xFBr de vouloir quitter {teamName}? Votre score ne sera plus consid\xE9r\xE9 comme faisant partie de la moyenne de l\u2019\xE9quipe.",
  CHALLENGE_ACTIVITY_DETAIL: "Permettez aux utilisateurs de voir quand vous terminez une activit\xE9 ou gagnez une r\xE9alisation en participant \xE0 un d\xE9fi.",
  SHOW_MY_ACTIVITY: "Afficher mon activit\xE9",
  YOUR_TEAM: "Votre \xE9quipe",
  JOIN_OR_CREATE_TEAM: "Rejoindre/cr\xE9er une \xE9quipe",
  TEAMS: "\xC9quipes",
  CHALLENGE_TEAMS_DESCRIPTION: "Rejoignez-vous \xE0 une \xE9quipe existante ou cr\xE9ez v\xF4tre propre \xE9quipe pour relever le d\xE9fi ensemble!",
  JOIN_A_TEAM: "Rejoindre une \xE9quipe",
  JOIN_TEAM_DESCRIPTION: "Recherchez une \xE9quipe existante \xE0 rejoindre et participez \xE0 la comp\xE9tition ensemble!",
  SEARCH_FOR_TEAM: "Rechercher une \xE9quipe",
  CREATE_A_TEAM: "Cr\xE9er une \xE9quipe",
  CREATE_TEAM_DESCRIPTION: "Personnalisez votre \xE9quipe en d\xE9finissant votre propre avatar et votre propre nom. Invitez d'autres personnes \xE0 se joindre \xE0 vous!",
  UP_YOUR_GAME: "Am\xE9liorez votre niveau!",
  UP_YOUR_GAME_DESCRIPTION: "L'union fait la force. Rejoignez une \xE9quipe ou cr\xE9ez la v\xF4tre.",
  XTH_PLACE: "{currentRank, selectordinal, one {#\xE8re} other {#e} } place",
  LEADERBOARD_RANKING: "Classement",
  LEADERBOARD_USERNAME: "Nom d'utilisateur",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "Le lien de l'invitation a \xE9t\xE9 copi\xE9 dans le presse-papiers!",
  DATE_AND_TIME: "Date et heure",
  SELECT_TEAM_AVATAR: "S\xE9lectionner l'avatar de l'\xE9quipe",
  CHANGE_TEAM_AVATAR: "Modifier l'avatar de l'\xE9quipe",
  TEAM_NAME_LABEL: "Nom de l'\xE9quipe",
  CREATE_TEAM: "Cr\xE9er une \xE9quipe",
  TEAM_NAME_ERROR: "Veuillez entrer un nom pour votre \xE9quipe.",
  SLIDE_OF_TOTAL: "Diapositive {key} sur {total}",
  PAGE_OF_TOTAL: "Page {current} sur {total}",
  NO_SOLUTIONS_AVAILABLE: "Nous sommes d\xE9sol\xE9s, aucune solution n'est disponible pour le moment.",
  SEE_ALL_ACHIEVEMENTS: "Voir toutes les r\xE9alisations",
  VIEW_ALL_ACHIEVEMENTS: "Voir toutes les r\xE9alisations",
  TAKE_ME_BACK: "Revenir en arri\xE8re",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "Cet avatar appara\xEEt sur le profil de votre \xE9quipe et dans le classement.",
  CREATE_TEAM_SUCCESS: "Votre \xE9quipe est pr\xEAte!",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "Commencez \xE0 constituer votre \xE9quipe. Partagez ce lien avec les autres participants afin de r\xE9aliser les activit\xE9s ensemble.",
  VIEW_TEAM_DETAILS: "Afficher les d\xE9tails de l'\xE9quipe",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "Caract\xE9ristiques du carrousel",
  CHALLENGE_FAQ_HEADING: "Vous avez des questions sur les D\xE9fis?",
  CHALLENGE_FAQ_CONTENT: "Consultez notre {helpCenterLink} pour toutes vos questions fr\xE9quemment pos\xE9es.",
  VIEW_THIS_RESOURCE: "Voir cette ressource",
  RECOMMENDED_NEXT_STEP: "\xC9tape suivante recommand\xE9e",
  CARE_COLLECTIONS: "Collections de soins",
  PLAN_PROGRESS: "Progression du plan",
  SINCE_LAST_WEEK: "depuis la semaine derni\xE8re",
  INSIGHT_CARD_HEADER_DISTANCE: "Distance",
  INSIGHT_CARD_HEADER_STEPS: "Pas",
  MIN: "min",
  CAL: "Cal",
  KM: "km",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, one {pas} other {pas}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "\xC9tages mont\xE9s",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, one {\xE9tage} other {\xE9tages}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "Minutes actives",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "\xC9nergie br\xFBl\xE9e",
  INSIGHT_CARD_HEADER_WEIGHT: "Poids",
  INSIGHT_CARD_HEADER_HEIGHT: "Taille",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "Minutes de pleine conscience",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "Tour de taille",
  DAY_OF_THE_WEEK: "JOUR DE LA SEMAINE",
  DAY_OF_THE_MONTH: "JOUR DU MOIS",
  INSIGHT_CHART_WEEK_TITLE: "Graphique \xE0 barres hebdomadaire {type}",
  INSIGHT_CHART_MONTH_TITLE: "Graphique \xE0 barres {type} mensuel",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "Le nombre de {unit} que vous avez march\xE9 chaque jour de la semaine.",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "Le nombre de {unit} que vous avez march\xE9 chaque jour du mois.",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "Vous avez march\xE9 {distance} {unit} le {date}",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "Le nombre de pas que vous avez march\xE9 chaque jour de la semaine.",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "Le nombre de pas que vous avez march\xE9 chaque jour du mois.",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "Vous avez march\xE9 {distance} {unit} le {date}",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "Le nombre de {unit} que vous avez br\xFBl\xE9 chaque jour de la semaine.",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "Le nombre de {unit} que vous avez br\xFBl\xE9 chaque jour du mois.",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "Vous avez br\xFBl\xE9 {energy} {unit} le {date}",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "Le nombre de minutes d'activit\xE9s pour chaque jour de la semaine.",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "Le nombre de minutes d'activit\xE9s chaque jour du mois.",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "Vous avez b\xE9n\xE9fici\xE9 de {value} minutes d'activit\xE9s le {date}",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "Le nombre d'\xE9tages que vous avez mont\xE9s chaque jour de la semaine.",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "Le nombre d'\xE9tages que vous avez mont\xE9s chaque jour du mois.",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "Vous avez mont\xE9 {value} \xE9tages le {date}",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "Le nombre de minutes de pleine conscience pour chaque jour de la semaine.",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "Le nombre de minutes de pleine conscience chaque jour du mois.",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "Vous avez b\xE9n\xE9fici\xE9 de {value} minutes de pleine conscience le {date}",
  DATAPOINT_CATEGORY_ACTIVITY: "Activit\xE9",
  DATAPOINT_CATEGORY_BODY: "Corps",
  MANAGE_UNITS_APPLYING_CHANGES: "Vos modifications sont en cours d'application\u2026",
  MANAGE_UNITS_CHANGES_SAVED: "Modifications enregistr\xE9es \xE0 {time}",
  START_TRACKING_HEALTH_HEADER: "Commencez \xE0 suivre votre sant\xE9",
  START_TRACKING_HEALTH_BODY: "Faites facilement le suivi de vos mesures au fil du temps pour voir o\xF9 vous en \xEAtes et comment vous pouvez atteindre vos objectifs de sant\xE9.",
  BROWSE_SUPPORTED_DATA: "Que puis-je suivre?",
  LAST_UPDATED_CARRIER_DATE: "Derni\xE8re mise \xE0 jour\xA0: {date} de {carrier}",
  ACCUMULATION_PERIOD: "P\xE9riode d'accumulation\xA0: <strong> {start} - {end} </strong>",
  NO_DATA_AVAILABLE: "Aucune donn\xE9e disponible",
  LAST_7_DAYS: "7 derniers jours",
  LAST_30_DAYS: "30 derniers jours",
  OLDER: "Plus vieux",
  GET_ASSISTANCE_NOW: "Obtenir de l'aide maintenant",
  GET_ASSISTANCE_NOW_SUBHEADING: "Clavardez avec nos professionnels de la sant\xE9 agr\xE9\xE9s et notre service \xE0 la client\xE8le. Ils peuvent vous aider \xE0 \xE9pargner de l'argent en vous dirigeant aux meilleurs soins de sant\xE9 les plus abordables dans votre r\xE9gion, ainsi qu'\xE0 r\xE9pondre \xE0 vos questions sur vos avantages sociaux.",
  HOW_CAN_WE_HELP: "Comment pouvons-nous aider?",
  LIVE_BALANCE_SPEND: "{spentAmount} sur {totalAmount} d\xE9pens\xE9",
  LIVE_BALANCE_REMAINING: "{amount} restant",
  UPDATING_UNITS_OF_MEASURE: "Mise \xE0 jour des unit\xE9s de mesure",
  CHANGING_YOUR_UNITS: "La modification de vos unit\xE9s affectera la fa\xE7on dont elles sont affich\xE9es dans l'application.",
  MESSAGES: "Messages",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "Messages",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "{fileName} a \xE9t\xE9 supprim\xE9.",
  SEND_NEW_MESSAGE: "Envoyer un nouveau message",
  NEW_MESSAGE_DEFAULT: "Ceci est le d\xE9but d'un nouveau message",
  NETWORK_MISSING_COVERAGE: "Votre r\xE9gime n'inclut aucune couverture {network}",
  CARD_FRONT_FILENAME: "{benefitName}-recto",
  CARD_BACK_FILENAME: "{benefitName}-verso",
  START_A_VISIT: "Commencez une visite",
  UNREAD: "Non lu",
  NO_MESSAGES_HEADING: "Aucun message pour le moment",
  NO_MESSAGES_DESCRIPTION: "Commencez par envoyer un nouveau message!",
  RETURN_TO: "Retour \xE0 {location}",
  NO_RECOMENDATIONS: "D\xE9sol\xE9, nous n'avons pas de recommandations \xE0 vous proposer dans ce domaine de sant\xE9 pour le moment.",
  CHALLENGES_LEGAL_DISCLAIMER: "Ce d\xE9fi se veut un moyen amusant pour tous de participer et d'avoir une chance \xE9quitable de gagner. Parfois, des prix en esp\xE8ces ou autres peuvent \xEAtre offerts par l'organisateur du d\xE9fi. League ne con\xE7oit pas la structure du d\xE9fi, ni si celui-ci offre des prix. Si vous avez des pr\xE9occupations concernant la structure de ce d\xE9fi ou si vous pensez avoir \xE9t\xE9 exclu de la chance de gagner, veuillez en parler \xE0 l'organisateur du d\xE9fi.",
  ALL_RECORDED_DATA: "Toutes les donn\xE9es enregistr\xE9es",
  NEW_MESSAGE: "Nouveau message",
  MESSAGE_SUBJECT: "Objet du message (facultatif)",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "Vous avez d\xE9pass\xE9 la limite maximale de caract\xE8res ({maximum})",
  CHAR_LIMIT_EXCEEDED: "Limite de caract\xE8res d\xE9pass\xE9e ({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "D\xE9finissez le sujet du message",
  TO: "\xC0",
  CREATE_MESSAGE: "Cr\xE9er un message",
  MEMBER_LABEL: "Membre",
  RESULT_COUNT: "{count, plural, one {{count,number} r\xE9sultat} other {{count,number} r\xE9sultats}}",
  USER_COLON: "{user} :",
  JOINED_CHALLENGE_HIGHLIGHT: "A rejoint <strong>{challengeTitle}</strong>!",
  CREATED_TEAM_HIGHLIGHT: "A cr\xE9\xE9 <strong>{teamName}</strong>!",
  JOINED_TEAM_HIGHLIGHT: "A rejoint <strong>{teamName}</strong>!",
  STEPS_WALKED_HIGHLIGHT: "<strong>A fait {total} pas hier!</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong>A boug\xE9 pendant {total} minutes aujourd'hui!</strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong>Termin\xE9 {total} minutes !</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong>Objectif {achievement} atteint!</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "Une activit\xE9 a \xE9t\xE9 termin\xE9e!",
  NO_CARE_COLLECTIONS: "D\xE9sol\xE9, il n'y a aucune collection de soins disponible.",
  WRITE_A_MESSAGE: "\xC9crivez un message...",
  UPLOAD_MULTIPLE_FILES: "T\xE9l\xE9charger plusieurs fichiers",
  SEND: "Envoyer",
  SEND_MESSAGE: "Envoyer",
  DATA_SOURCES: "Sources de donn\xE9es",
  SOURCES_CAN_UPDATE: "Les sources ci-dessous sont autoris\xE9es \xE0 mettre \xE0 jour votre",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "Lorsque plusieurs sources sont disponibles, la source de donn\xE9es ayant la plus grande valeur sera enregistr\xE9e \xE0 la fin de la journ\xE9e (23h59).",
  APPLE_HEALTH: "Apple Sant\xE9",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "Garmin",
  POLAR: "Polar",
  WITHINGS: "Withings",
  HEALTH_PLAN: "Plan de sant\xE9",
  OMRON: "Omron",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "Mise \xE0 jour des unit\xE9s de mesure",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "La modification de vos unit\xE9s affectera la fa\xE7on dont elles sont affich\xE9es dans l'application",
  DOCUMENTS: "Documents",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "Documents",
  DOCUMENTS_FILTER_LOADING_TEXT: "Les onglets et les filtres sont en cours de chargement",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "{filter} est requis",
  DOCUMENT_FILTER_X_REQUIRED: "{filter} (requis)",
  SHOW_RESULTS: "Afficher les r\xE9sultats",
  RESET_FILTERS: "R\xE9initialiser les filtres",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "Filtres s\xE9lectionn\xE9s",
  DOCUMENTS_RESULTS_HEADING: "R\xE9sultats",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "Passer au t\xE9l\xE9versement du document",
  AVERAGE: "Moyenne",
  VIEW_ALL_METRIC_DATA: "Afficher toutes les donn\xE9es relatives \xE0 la mesure {metric}",
  EDIT_METRIC_DATA: "Modifier les donn\xE9es {metric}",
  NEXT_WEEK: "Semaine prochaine",
  PREVIOUS_WEEK: "Semaine derni\xE8re",
  NEXT_30_DAYS: "30 prochains jours",
  PREVIOUS_30_DAYS: "30 jours pr\xE9c\xE9dents",
  UNABLE_TO_CREATE_TEAM: "Impossible de cr\xE9er une \xE9quipe. Veuillez r\xE9essayer.",
  START_TRACKING_METRIC: "Commencer \xE0 suivre {metric}",
  CURRENTLY_NOT_SHARING_DATA: "Vous ne partagez actuellement aucune donn\xE9e pour {metric}",
  SUCCESSFULLY_LEFT_TEAM: "Vous avez quitter {teamName}",
  LEAVE_TEAM_FAILURE: "Une erreur est survenue. Veuillez r\xE9essayer!",
  VIEW_DATA_SOURCES: "Afficher les sources de donn\xE9es",
  OR_CALL_PHONE: "ou appelez le {phoneNumber}{extension}",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "Aucun document ne correspond \xE0 vos filtres",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "Essayez de supprimer certains de vos filtres pour obtenir plus de r\xE9sultats",
  DOCUMENTS_EMPTY_STATE_HEADING: "Aucun document ici (pour l'instant!)",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "Un seul endroit pour visualiser et organiser tous vos documents",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team} (Votre \xE9quipe)",
  YOUR_CARE_ADVISOR: "Votre conseiller en soins",
  COMPOSE: "Composer",
  COMPOSE_NEW_MESSAGE: "Composer un nouveau message",
  POLICY_INFORMATION_UNAVAILABLE: "Il n'y a pas d'information disponible pour cette p\xE9riode contractuelle.",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "Il n'y a aucune information pour ce membre. Veuillez essayer un autre membre.",
  MESSAGE_THREADS_LIST_SUBTITLE: "La r\xE9ponse de notre \xE9quipe peut prendre jusqu'\xE0 2 jours ouvrables. S'il s'agit d'une urgence m\xE9dicale, appelez le 911.",
  ALL_MESSAGES_FOR_THREAD_BANNER: "Veuillez ne pas nous envoyer d'informations sp\xE9cifiques sur votre \xE9tat de sant\xE9 par le biais de ce clavardage.",
  TROUBLE_DISPLAYING_DATA: "Nous avons des difficult\xE9s \xE0 afficher vos donn\xE9es",
  RETRY: "R\xE9essayer",
  YOU: "Vous",
  MESSAGES_IN_CONVERSATION_TITLED: "Messages dans la conversation intitul\xE9e {subject}",
  TEAM_AVERAGE: "Moyenne de l'\xE9quipe",
  YOUR_METRICS: "Mes mesures",
  YOUR_METRICS_SUBTITLE: "Les donn\xE9es de sant\xE9 les plus r\xE9centes de vos applis et appareils connect\xE9s.",
  APPS_AND_DEVICES: "Applis et appareils",
  APP_AND_DEVICE_METRICS: "Mesures de l\u2019appli et de l\u2019appareil",
  VIEW_ALL_METRICS: "Afficher les mesures des applis et des appareils",
  RENAME: "Renommer",
  ENTER_DOCUMENT_NAME: "Veuillez ajouter un nom pour ce document",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "Il s'agit d'une activit\xE9 peu prioritaire",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "Il s'agit d'une activit\xE9 de priorit\xE9 moyenne",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "Il s'agit d'une activit\xE9 de haute priorit\xE9",
  ACTIVITY_EXPIRY_DATE: "Expire le {date}",
  GENERIC_MESSAGING_ERROR: "Il semble qu'il y ait eu un probl\xE8me, veuillez rafra\xEEchir la page et r\xE9essayer.",
  VIEW_THE_ACTIVITIES: "Voir les activit\xE9s",
  DRAG_FILES_HERE_OR: "Faites glisser les fichiers ici ou",
  ADD_A_DOCUMENT: "Ajouter un document",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "T\xE9l\xE9charger un document",
  ADD_DOCUMENT: "Ajouter un document",
  REMOVE_FILE: "Supprimer le fichier",
  REMOVE_FILE_FILENAME: "Supprimer le fichier : {filename}{fileSize}",
  UNABLE_TO_SEND: "Impossible d'envoyer, veuillez r\xE9essayer",
  ERROR_HEADING: "Quelque chose a mal tourn\xE9 de notre c\xF4t\xE9.",
  FILE_TYPE_NOT_SUPPORTED: "Ce type de fichier n'est pas pris en charge",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "Utilisez un fichier dans l'un de ces formats : {fileTypes}",
  RECENT_MESSAGES: "Messages r\xE9cents",
  NO_MESSAGES_YET: "Vous n'avez aucun message (pour le moment!)",
  NO_MESSAGES_DETAIL: "Votre conseiller en soins vous aidera \xE0 prendre des d\xE9cisions plus avis\xE9es en mati\xE8re de soins de sant\xE9, \xE0 chaque \xE9tape du parcours.",
  START_A_NEW_MESSAGE: "Commencer un nouveau message",
  USER_ADDED_DOCUMENT: "{user} a ajout\xE9 un document",
  ERROR_SAVING_TRY_AGAIN: "Erreur de sauvegarde des modifications. Veuillez r\xE9essayer",
  MODIFIED_ON_DATE: "Modifi\xE9 le {date}",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "T\xE9l\xE9vers\xE9 {date}",
  FILE_TYPE_TOO_LARGE: "La taille du fichier est trop grande",
  MAXIMUM_FILE_SIZE_IS: "La taille de fichier maximale est {fileSize}\xA0MB.",
  FILE_TOO_LARGE: "Oups. Ce fichier est trop volumineux.",
  FILE_SHOULD_BE_UNDER: "Votre fichier doit \xEAtre inf\xE9rieur \xE0 {fileSize}",
  ATTACHMENT_LIMIT_REACHED: "Oups. Il y a trop de fichiers.",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "{maxNumberOfDocs, plural, one {Vous pouvez joindre jusqu'\xE0 {maxNumberOfDocs,number} file.} other {Vous pouvez joindre jusqu'\xE0 {maxNumberOfDocs,number} files.}}",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "Oups. Ce type de fichier n'est pas pris en charge.",
  ATTACHMENT_TYPES_ALLOWED: "Vous pouvez joindre votre fichier dans ces formats : {fileTypes}",
  MESSAGES_PAGINATION_ERROR: "Impossible de charger les messages. <button>R\xE9essayez.</button>",
  TRANSACTION_TABLE_CAPTION: "Historique des transactions",
  SELECT_PARTICIPANT_ERROR: "S\xE9lectionnez une personne \xE0 qui envoyer un message",
  PREVIEW_OF_FILE: "Aper\xE7u de {fileName}",
  VIEW_DOCUMENT_ACTIONS: "Afficher les actions {documentName}.",
  VISIT_LINK: "Visitez le lien",
  COMPLETE_BY_DATE: "\xC0 terminer avant le {date}",
  ERROR_UPLOADING_YOUR_FILE: "Erreur de chargement de votre fichier. R\xE9essayer.",
  DOCUMENT_SUBMITTED: "Document soumis",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "Votre document sera bient\xF4t disponible. Veuillez patienter quelques instants et rafra\xEEchir la page.",
  ERROR_RENAMING_YOUR_DOCUMENT: "Erreur de renommage de votre document. R\xE9essayer.",
  DOCUMENT_RENAMED_SUCCESSFULLY: "Document renomm\xE9 avec succ\xE8s.",
  ERROR_HEADING_GENERIC: "Une erreur est survenue",
  ERROR_HEADING_NO_CONNECTION: "Aucune connexion Internet",
  ERROR_CAPTION_BACKEND: "Un probl\xE8me est survenu lors de la connexion \xE0 notre serveur.\n Veuillez r\xE9essayer plus tard",
  ERROR_CAPTION_NO_CONNECTION: "Un probl\xE8me est survenu lors du chargement des donn\xE9es.\n Veuillez v\xE9rifier votre Internet et r\xE9essayer.",
  SURE_YOU_WANT_TO_DELETE: "\xCAtes-vous certain de vouloir supprimer {name}",
  CANNOT_BE_UNDONE: "Cette action est irr\xE9versible",
  DELETE_DOCUMENT: "Supprimer le document",
  DOCUMENT_DELETED: "Document supprim\xE9",
  ERROR_DELETING_YOUR_DOCUMENT: "Erreur lors de la suppression de votre document",
  ABOUT_ME: "\xC0 propos de moi",
  SEE_MORE: "EN SAVOIR PLUS",
  X_ABOUT_X: "{left} concernant {right}",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "Nous n'arrivons pas \xE0 charger cette page. Veuillez v\xE9rifier votre Internet et r\xE9essayer.",
  WHO_IS_THIS_DOCUMENT_ABOUT: "\xC0 qui s'adresse le document?",
  SELECT_A_USER: "S\xE9lectionnez un utilisateur",
  SELECT_DOCUMENT: "S\xE9lectionner le document",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "Cela aide votre conseiller en soins \xE0 identifier la personne \xE0 laquelle se rapporte ce document.",
  VIEW_ALL_MESSAGE_THREADS: "Voir tous les messages",
  TOGGLE_MENU: "Afficher la liste des messages",
  METRICS: "Mesures",
  UNREAD_MESSAGES: "Messages non lus",
  WITH_DOCUMENT: "avec document",
  X_TOKENS: "{tokenCount, plural, one {{tokenCount,number} jeton} other {{tokenCount,number} jetons}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "Programmes et d\xE9fis",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "Pr\xEAt \xE0 commencer?",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "Parcourez notre biblioth\xE8que pour trouver les programmes et d\xE9fis de sant\xE9 qui correspondent \xE0 vos objectifs personnels.",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "Programmes et d\xE9fis",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "Pr\xEAt \xE0 commencer?",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "Parcourez notre biblioth\xE8que pour trouver les programmes et d\xE9fis de sant\xE9 qui correspondent \xE0 vos objectifs personnels.",
  EXPLORE_LIBRARY: "Parcourir la biblioth\xE8que",
  YOUVE_GOT_THIS: "Vous pouvez le faire!",
  YOUVE_GOT_THIS_NAME: "Vous pouvez le faire, {firstName}!",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "Bienvenue dans votre exp\xE9rience Parcours sant\xE9!",
  HEALTH_JOURNEY_PAGE: "Page du Parcours Sant\xE9",
  VIEW_DETAILS: "Consulter les d\xE9tails",
  NEXT_ACTIVITY: "Activit\xE9 suivante",
  X_OF_Y_ACTIVITIES: "{completed} sur {total} activit\xE9s",
  X_PERCENTAGE: "{percent}%",
  SOLUTIONS: "Solutions",
  EXPLORE: "Parcourir",
  DOCUMENT_NAME_TOO_LONG: "Vous avez d\xE9pass\xE9 la limite maximale de caract\xE8res ({maximum})",
  CLAIM_PATIENT_NAME: "Pour\xA0: {patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "Aucun programme. Aucun d\xE9fi. Juste des activit\xE9s de temps en temps.",
  FEEDBACK_LINK_LABEL: "\xC9valuez l'information {value} sur {maximum} en termes d'utilit\xE9",
  RECENT_ACHIEVEMENTS: "R\xE9alisations r\xE9centes",
  ACHIEVEMENTS_BANNER_HEADER: "F\xE9licitations pour l'obtention de ces badges!",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "Nous aimons vous voir prioriser votre sant\xE9 et votre bien-\xEAtre! D\xE9couvrez les autres r\xE9alisations vers lesquelles vous travaillez.",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "Nous aimons vous voir prioriser votre sant\xE9 et votre bien-\xEAtre! D\xE9couvrez les autres r\xE9alisations que vous avez obtenues et celles vers lesquelles vous travaillez.",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "Rien de r\xE9alis\xE9 (pour l'instant!)",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "Terminez les activit\xE9s de votre parcours pour commencer \xE0 accumuler des r\xE9alisations.",
  HISTORY_EMPTY_STATE_HEADER: "Il n'y a rien \xE0 voir ici!",
  HISTORY_EMPTY_STATE_DESCRIPTION: "Lorsque vous terminez une activit\xE9, un programme ou un d\xE9fi, il appara\xEEt ici.",
  INDIVIDUAL_BALANCE: "{balance} de {membersFullName}",
  FAMILY_BALANCE: "Famille {balance}",
  UPCOMING: "\xC0 venir",
  REMOVED: "Supprim\xE9",
  COMPLETED_TODAY: "Compl\xE9t\xE9",
  CLAIMS_BENEFIT_CARD_TITLE: "R\xE9clamations d\u2019assurance",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "Suivez les sommes que vous devez payer pour vos soins et le montant que l'assurance couvrira.",
  CLAIMS_FILTER_PROPERTY_TYPE: "Type de service",
  CLAIMS_FILTER_PROPERTY_STATUS: "Statut de la demande",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "Membre",
  CLAIMS_FILTER_VALUE_ORAL: "Soins dentaires",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "Institutionnel",
  CLAIMS_FILTER_VALUE_VISION: "Vue",
  CLAIMS_FILTER_VALUE_PHARMACY: "Pharmacie",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "Professionnel",
  CLAIMS_FILTER_VALUE_APPROVED: "Nous jouerons notre r\xF4le",
  CLAIMS_FILTER_VALUE_PENDING: "Traitement",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "Partiellement couvert",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "Non couvert",
  CLAIMS_FILTER_VALUE_ADJUSTED: "Ajust\xE9",
  DRINKS_PER_WEEK: "Boissons par semaine",
  OR_SELECT_BETWEEN_THESE: "ou s\xE9lectionnez l'une des options suivantes",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "Fourchette de dates",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "Depuis le d\xE9but de l'ann\xE9e",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "Ann\xE9e pr\xE9c\xE9dente",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "12 derniers mois",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "2 derni\xE8res ann\xE9es",
  CLAIMS_VIEW_ALL: "Voir tout",
  CHALLENGE_TYPE_GROUP_TAG: "Groupe",
  CHALLENGE_TYPE_SOLO_TAG: "Solo",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "D\xE9fis de groupe",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "D\xE9fis en solo",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "Restez responsable lorsque vous vous mettez au d\xE9fi avec un travail de pleine conscience, des initiatives en mati\xE8re d'alimentation saine, des comp\xE9titions de fitness, des activit\xE9s de r\xE9duction du stress, des d\xE9fis de pas, et bien plus encore.",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "Lancez-vous en solo et mettez-vous au d\xE9fi de r\xE9aliser des activit\xE9s ax\xE9es sur le conditionnement physique, la nutrition, la sant\xE9 mentale, la gestion du stress, et bien d'autres choses encore.",
  HELPFUL_LINKS: "Liens utiles",
  HOW_CLAIMS_WORK: "Comment fonctionnent les r\xE9clamations",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "Comprendre le fonctionnement de vos r\xE9clamations",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>Lorsque vous consultez l'un des m\xE9decins de votre r\xE9seau, il demande \xE0 \xEAtre pay\xE9. Si vous devez payer une quote-part, il la per\xE7oit aupr\xE8s de vous et soumet ensuite une demande de remboursement \xE0 la compagnie d'assurance.</p><p>Nous examinons alors votre r\xE9gime d'assurance, \xE9galement appel\xE9 contrat de garanties, afin de d\xE9terminer si le service est couvert. Si c'est le cas, nous l'approuverons sur la base des termes du contrat de garanties. Si le service n'est pas couvert par votre r\xE9gime d'assurance, il vous incombe de le payer.</p>",
  CLAIMS_STATUS_DEFINITIONS: "D\xE9finitions de l\u2019\xE9tat de la demande",
  CLAIMS_STATUS_DEFINITION_APPROVED: '"Nous jouerons notre r\xF4le" signifie que la r\xE9clamation est approuv\xE9e et que nous acceptons de payer une partie ou la totalit\xE9 de la r\xE9clamation en fonction de votre r\xE9gime.',
  CLAIMS_STATUS_DEFINITION_PENDING: '"Traitement" signifie que nous sommes en train de d\xE9terminer qui doit quoi.',
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: '"Partiellement couvert" signifie que nous couvrirons une partie de votre r\xE9clamation.',
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: `"Non couvert" signifie que le service n'est pas couvert par votre r\xE9gime d'avantages sociaux et que vous devrez le payer.`,
  CLAIMS_STATUS_DEFINITION_ADJUSTED: '"Ajust\xE9" signifie que quelque chose a chang\xE9 et que nous avons d\xFB r\xE9examiner cette demande.',
  YOUR_COST: "Votre co\xFBt :",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {R\xE9clamation m\xE9dicale} oral {R\xE9clamation dentaire} vision {R\xE9clamation pour les soins de la vue} pharmacy {R\xE9clamation pharmaceutique} professional {R\xE9clamation professionnelle} other {R\xE9clamation}}",
  FILTER_BY: "Filtrer par",
  CLAIMS_LIST_EMPTY_HEADING: "Rien ici pour l'instant!",
  CLAIMS_LIST_EMPTY_MESSAGE: "Les demandes soumises s'afficheront ici.",
  CLAIMS_GENERIC_ERROR_HEADING: "Une erreur est survenue",
  CLAIMS_GENERIC_ERROR_MESSAGE: "Il y a eu un probl\xE8me technique de notre c\xF4t\xE9.{br}Veuillez r\xE9essayer dans quelques minutes.",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "V\xE9rifiez ce que votre assurance a pris en charge et ce que vous devez encore \xE0 votre prestataire. Vous pouvez acc\xE9der \xE0 vos demandes de remboursement soumises dans les deux ann\xE9es pr\xE9c\xE9dant la date d'aujourd'hui.",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "Aucun r\xE9sultats",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "{filterCount, plural, one {Aucune demande ne correspond au filter que vous avez choisi.} other {Aucune demande ne correspond au filters que vous avez choisi.}}",
  VIEW_MY_PROFILE: "Voir mon profil",
  PLAN_PROGRESS_COST_COVERAGE: "Progression et cotisation du r\xE9gime",
  PLAN_PROGRESS_POP_UP_LABEL: "Qu\u2019est-ce qu\u2019un {title}?",
  RECENT_CLAIMS_LIST_EMPTY: `Les demandes soumises au cours des 90 derniers jours s'affichent ici. Vous pouvez acc\xE9der \xE0 toutes les demandes en s\xE9lectionnant "Voir tout". `,
  DOWNLOAD_CLAIMS: "T\xE9l\xE9charger les demandes",
  PLAIN_SOMETHING_WENT_WRONG: "Une erreur est survenue",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "Un probl\xE8me est survenu lors du t\xE9l\xE9chargement de vos demandes.",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "Les informations sur vos avantages sociaux, vos r\xE9clamations et votre bien-\xEAtre r\xE9unis \xE0 un seul endroit.",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "Pr\xEAt \xE0 commencer?",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "Parcourez notre biblioth\xE8que pour trouver les programmes et d\xE9fis de sant\xE9 qui correspondent \xE0 vos objectifs personnels.",
  USER_DROPDOWN_BTN_LABEL: "Menu du profil",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "Qu\u2019est-ce qu\u2019une quote-part et une coassurance?",
  ANSWERS_NOT_SAVED: "Nous ne sommes pas en mesure d'enregistrer vos r\xE9ponses pour le moment. Veuillez r\xE9essayer plus tard",
  SESSION_ABOUT_TO_TIMEOUT: "Vous avez \xE9t\xE9 inactif pendant un certain temps et votre session est sur le point d'expirer. Voulez-vous continuer votre session?",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "Vous avez \xE9t\xE9 inactif pendant un certain temps et votre session est sur le point d'expirer.",
  SESSION_TIMEOUT: "Expiration de la session",
  CONTINUE_SESSION: "Continuer la session",
  SESSION_LOG_OUT: "Se d\xE9connecter",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "S\xE9lectionnez une adresse de livraison",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "Qui a besoin d\u2019une nouvelle carte?",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "S\xE9lectionnez un ou plusieurs membres.",
  DEFAULT_ADDRESS: "Adresse par d\xE9faut",
  CHOOSE_NEW_ADDRESS: "Choisissez une nouvelle adresse",
  CHOOSE_NEW_ADDRESS_HINT: "Indiquez une autre adresse postale pour la livraison de la carte. Cela ne modifiera pas l'adresse par d\xE9faut de votre compte.",
  CARD_ACTIONS: "Actions de carte",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "Obtenez une r\xE9alisation en effectuant toutes les activit\xE9s de ce d\xE9fi.",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "Il n'y a rien \xE0 voir ici!",
  CHALLENGE_ACTIVITY_UP_NEXT: "Suivant",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "Rendez-vous ici lorsque le d\xE9fi commencera!",
  CHALLENGE_ACTIVITY_COMPLETED: "Termin\xE9es",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "Les activit\xE9s que vous avez termin\xE9es appara\xEEtront ici.",
  CHALLENGE_ACTIVITY_MISSED: "Manqu\xE9es",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "Les activit\xE9s que vous avez manqu\xE9es appara\xEEtront ici.",
  CHALLENGE_COMPLETED_TITLE: "Nous avons termin\xE9!",
  CHALLENGE_COMPLETED_BODY: "Il ne reste plus d'activit\xE9s \xE0 r\xE9aliser. Joli travail!",
  ACHIEVEMENT_EARNED: "C\xE9l\xE9bration de la r\xE9alisation",
  ACHIEVEMENT_PROGRESS: "Progression des r\xE9alisations",
  ADD_DATA: "Ajouter des donn\xE9es",
  CONFIRM_DETAILS: "Confirmez vos coordonn\xE9es",
  MEDICAL_ID_CARD: "Carte d'identit\xE9 m\xE9dicale",
  NEW_ADDRESS: "Nouvelle adresse de livraison",
  MESSAGE_CENTER: "Centre de messagerie",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "Messagerie",
  CHOOSE_NEW_ADDRESS_DESC: "Indiquez une autre adresse postale pour la livraison de la carte. Cela ne modifiera pas l'adresse par d\xE9faut de votre compte. L'adresse postale doit \xEAtre situ\xE9e aux \xC9tats-Unis.",
  SELECT_STATE: "S\xE9lectionner un \xE9tat",
  REQUIRED_FIELD: "{field} est un champ obligatoire",
  CLAIMS_YOUR_COST: "Votre co\xFBt",
  CLAIMS_PROCESSING_DESCRIPTION: "Nous sommes en train de traiter cette demande. Vous serez inform\xE9 lorsqu'elle sera pr\xEAte \xE0 \xEAtre examin\xE9e.",
  DOWNLOAD_BENEFITS_EXPLANATION: "T\xE9l\xE9charger les d\xE9tails des avantages",
  DATA_COLLECTED: "Donn\xE9es collect\xE9es {date}",
  MEMBER_CARD_ON_ITS_WAY: "Votre nouvelle carte d'identit\xE9 de membre est en route.",
  MEMBER_CARD_ARRIVAL: "La nouvelle carte d'identit\xE9 de membre sera livr\xE9e dans un d\xE9lai d'environ 10 jours ouvrables.",
  TRY_LATER: "Veuillez r\xE9essayer plus tard",
  RESUBMIT: "Resoumettre",
  ORDER_NEW_CARD_HEADING: "Commander une nouvelle carte",
  CANCEL_ORDER_NEW_CARD_TITLE: "\xCAtes-vous s\xFBr de vouloir annuler votre commande?",
  CANCEL_ORDER_NEW_CARD_DESC: "Vous pouvez toujours revenir plus tard.",
  CANCEL_ORDER: "Annuler la commande",
  STEP: "\xC9tape",
  ORDER_CARD: "Carte de commande",
  PLEASE_MAKE_A_SELECTION: "Veuillez faire une s\xE9lection",
  TOTAL_COST_OVERVIEW: "Aper\xE7u du co\xFBt total",
  TOTAL_COST_DESCRIPTION: "La somme des co\xFBts des services de votre demande. Cela n\u2019inclut pas les paiements que vous avez effectu\xE9s \xE0 votre prestataire, comme les quotes-parts.",
  SUPPORT: "Soutien",
  GET_SUPPORT: "Obtenir de l\u2019aide",
  MESSAGE_WITH_A_MEMBER: "Envoyez un message \xE0 un conseiller pour obtenir de l\u2019aide relative aux questions sur vos demandes de r\xE8glement concernant vos prestations ou vos soins.",
  CLAIMS_SERVICE_DETAILS_HEADING: "D\xE9tails du service",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "Ressources suppl\xE9mentaires",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "Une r\xE9partition des services individuels que vous avez re\xE7us et de leurs co\xFBts associ\xE9s.",
  CLAIMS_SERVICE_DETAILS_SERVICE: "Service",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "Nom de Rx",
  CLAIMS_SERVICE_DETAILS_DATE: "Service",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "Code de proc\xE9dure",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "Num\xE9ro Rx",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "Votre co\xFBt",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "D\xE9tails des co\xFBts",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "Afficher les d\xE9tails des co\xFBts",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "Masquer les d\xE9tails des co\xFBts",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "R\xE9partition de votre co\xFBt",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "Les montants ci-dessous servent \xE0 la progression de votre r\xE9gime",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "S\xE9lectionnez un ou plusieurs membres pour continuer",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "Le champ contient des caract\xE8res non valides",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "Prestataire factur\xE9",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "Autre assurance a pay\xE9",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "Votre rabais de membre",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "Votre r\xE9gime a n\xE9goci\xE9 avec votre prestataire un tarif pr\xE9f\xE9rentiel.",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "Explication de la r\xE9duction accord\xE9e aux membres",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "Le montant pay\xE9 par votre autre assurance.",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: 'Explication de "votre autre assurance a pay\xE9"',
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {Nous avons pay\xE9 le {paymentDate}} undefined {Nous avons pay\xE9}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "Le montant couvert par votre r\xE9gime pour les services de soins de sant\xE9.",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: 'Explication de "votre r\xE9gime a pay\xE9"',
  CLAIMS_COST_DETAILS_YOUR_COST: "Votre co\xFBt",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "Remarque sur le service",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "En r\xE9seau",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "Hors r\xE9seau",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "S\xE9lectionnez une option d'adresse pour continuer",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "La ville ne peut pas contenir de chiffres",
  MESSAGE_CLOSED_TEXT: "Ce message a \xE9t\xE9 envoy\xE9.",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "Obtenez un aper\xE7u de vos co\xFBts cumul\xE9s, de vos niveaux de couverture et plus encore.",
  VIEW_PLAN_PROGRESS: "Afficher la progression du plan",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "Vous recevrez une facture associ\xE9e \xE0 cette demande de la part de votre fournisseur.",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "Un probl\xE8me est survenu lors du t\xE9l\xE9chargement des d\xE9tails de vos avantages.",
  END_CHAT_MODAL_DESCRIPTION: "Continuer mettra fin au clavardage. Vous ne pourrez plus revenir sur cette conversation \xE0 l'avenir.",
  RETURN_TO_CHAT: "Retourner au clavardage",
  CHATS_IN_CONVERSATION: "Clavardages en direct",
  LIVE_CHAT_CHARACTER_LIMIT: "Limite de caract\xE8res atteinte ({characterLimit})",
  CHAT_MESSAGE_INPUT: "Saisie du message de clavardage",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {Deductible} non_covered {Non couvert} copayment {Copayment} coinsurance {Coinsurance} may_be_billed {May Be Billed} amount_exceeding_maximum {Amount Exceeding Maximum} additional_member_liability {Additional Member Liability} special_processing {Special Processing} paid_to_member {Paid To Member} plan_paid_to_you {Plan Paid To You} other {Other}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "Nouveau message",
  QUESTIONNAIRE_FLOW_CLOSE: "Fermer",
  QUESTIONNAIRE_FLOW_GO_BACK: "Retour",
  QUESTIONNAIRE_FLOW_CONTINUE: "Continuer",
  QUESTIONNAIRE_FLOW_RETRY: "R\xE9essayer",
  CLEAR_ALL: "Tout effacer",
  HEALTH_REPORT_PAGE_HEADING: "Votre rapport de sant\xE9",
  USERNAME_HAS_JOINED_THE_CHAT: "{username} a rejoint le clavardage",
  USERNAME_HAS_LEFT_THE_CHAT: "{username} a quitt\xE9 le clavardage",
  PERVIOUS_MONTH: "Mois pr\xE9c\xE9dent",
  NEXT_MONTH: "Mois prochain",
  CHAT_CLOSED: "Le clavardage est termin\xE9.",
  CONNECTING_YOU_WITH_AN_AGENT: "Vous mettre en relation avec un agent...",
  LAST_DAY_TO_USE_BALANCE: "Dernier jour pour utiliser le solde",
  LAST_DAY_TO_SUBMIT_CLAIMS: "Dernier jour pour soumettre des r\xE9clamations",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "Une erreur est survenue",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "Un probl\xE8me est survenu, veuillez r\xE9essayer.",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "Une erreur est survenue",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "Un probl\xE8me est survenu, veuillez r\xE9essayer.",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "Fermer",
  LIVE_CHAT_LANDING_PAGE_TITLE: "Clavardage",
  LIVE_CHAT_DELIVERY_FAILURE: "Non livr\xE9",
  LIVE_CHAT_RESEND_MESSAGE: "Renvoyer le message",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "Le message n'a pas pu \xEAtre envoy\xE9",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "D\xE9sol\xE9, il semble que votre message n'ait pas \xE9t\xE9 transmis \xE0 notre \xE9quipe.",
  VIEW_TOOLTIP: "afficher l'info-bulle {tooltipLabel}",
  NO_DATA_SOURCES: "Aucune source de donn\xE9es",
  MAX_VALUE_ERROR_MESSAGE: "Veuillez entrer une valeur inf\xE9rieure ou \xE9gale \xE0 {max}",
  MIN_VALUE_ERROR_MESSAGE: "Veuillez entrer une valeur sup\xE9rieure ou \xE9gale \xE0 {min}",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "Veuillez s\xE9lectionner une valeur",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "La date de d\xE9but doit \xEAtre ant\xE9rieure \xE0 la date de fin.",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "La dur\xE9e ne peut exc\xE9der 24 heures",
  NO_FUTURE_DATES: "La date ne peut pas \xEAtre dans le futur",
  CLAIMS_REMOVE_FILTER: "Supprimer le filtre {label}.",
  REMOVE_X_FILTER: "Supprimer le filtre {label}.",
  WALLET_BENEFIT_STATUS_PENDING: "En attente",
  WALLET_BENEFIT_STATUS_ACTIVE: "Actif",
  WALLET_BENEFIT_STATUS_PAST: "Pass\xE9",
  CLAIMS_CLAIMS_LIST_HEADING: "Tes demandes",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "Rien ici pour l'instant!",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "Les avantages actifs appara\xEEtront ici.",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "Avantages sociaux",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "Avantages sociaux - Cartes d'identit\xE9",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "Demandes",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "D\xE9tails de la demande",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "D\xE9tails de la demande - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "En attente",
  CLAIMS_STATUS_PENDING: "En attente",
  CSE_END_IMPERSONATION_MODE: "Mettre fin au mode d'emprunt d'identit\xE9",
  CSE_RESTRICTED_ACTION: "Action restreinte",
  CSE_RESTRICTED_ACTION_MESSAGE: "En mode simulation utilisateur, vous ne pouvez qu'afficher le portail du membre. Les actions au nom des membres ne sont pas autoris\xE9es.",
  CSE_RESTRICTED_VIEW_MESSAGE: "Vous n'avez pas l'autorisation d'acc\xE9der \xE0 cette section.",
  CSE_IMPERSONATION_MASONRY_HEADER: "Contenu restreint pour les agents de soutien",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "Vous n'avez peut-\xEAtre pas l'autorisation de voir certains widgets ou contenus.",
  CSE_IMPERSONATION_BANNER_TEXT: "Mode de visualisation seulement pour {memberName}.",
  CLAIMS_SUMMARY_PAGE_HEADING: "Demandes",
  HEALTH_INSURANCE_GLOSSARY: "Glossaire de l'assurance-maladie",
  CLAIMS_FILTER_LOADING_TEXT: "Les onglets et les filtres sont en cours de chargement",
  ANDROID_HEALTH_CONNECT: "Sant\xE9 Connect",
  GET_CARE_SERVICE_CARD_CTA: "Afficher le service",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "\xC9tape suivante",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "\xC9tape pr\xE9c\xE9dente",
  HEIGHT: "Taille",
  WEIGHT: "Poids"
};

// src/strings/vi.json
var vi_default = {
  COMPLETED_ACTIVITIES: "C\xE1c ho\u1EA1t \u0111\u1ED9ng \u0111\xE3 ho\xE0n th\xE0nh",
  AVAILABLE: "C\xF3 s\u1EB5n",
  SEE_MORE_ACTIVITIES: "Xem th\xEAm",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "Xem th\xEAm c\xE1c ho\u1EA1t \u0111\u1ED9ng \u0111\xE3 ho\xE0n th\xE0nh",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "Khi b\u1EA1n ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng, ch\xFAng s\u1EBD xu\u1EA5t hi\u1EC7n \u1EDF \u0111\xE2y!",
  MISSED_ACTIVITIES: "C\xE1c ho\u1EA1t \u0111\u1ED9ng b\u1ECB b\u1ECF l\u1EE1",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "Xem th\xEAm c\xE1c ho\u1EA1t \u0111\u1ED9ng b\u1ECB b\u1ECF l\u1EE1",
  MISSED_ACTIVITIES_EMPTY_STATE: "Khi b\u1EA1n b\u1ECF l\u1EE1 c\xE1c ho\u1EA1t \u0111\u1ED9ng, ch\xFAng s\u1EBD xu\u1EA5t hi\u1EC7n \u1EDF \u0111\xE2y!",
  DISPLAYING_ACTIVITIES_FROM_DATE: "Hi\u1EC3n th\u1ECB c\xE1c ho\u1EA1t \u0111\u1ED9ng k\u1EC3 t\u1EEB {date}",
  STANDALONE_ACTIVITIES: "Ho\u1EA1t \u0111\u1ED9ng \u0111\u1ED9c l\u1EADp",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "Ch\u01B0a c\xF3 g\xEC \u0111\u1EC3 l\xE0m \u1EDF \u0111\xE2y! Quay l\u1EA1i sau.",
  VIEW_ALL_STANDALONE_ACTIVITIES: "Xem t\u1EA5t c\u1EA3 c\xE1c ho\u1EA1t \u0111\u1ED9ng \u0111\u1ED9c l\u1EADp",
  MODULE_TEMPLATE_BUTTON: "N\xFAt m\u1EABu m\xF4-\u0111un",
  ACHIEVEMENTS: "Th\xE0nh t\u1EF1u",
  VIEW_ACHIEVEMENTS: "Xem th\xE0nh t\xEDch",
  DEPENDENT_NOTIFICATION_MESSAGE: "Ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c m\u1EDBi c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c m\u1EDDi",
  DEPENDENT_COVERED_BY_HSA_ONLY: "L\u01B0u \xFD: Ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c n\xE0y s\u1EBD ch\u1EC9 \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m theo T\xE0i kho\u1EA3n chi ti\xEAu y t\u1EBF c\u1EE7a b\u1EA1n.",
  WALLET_LANDING_NAV_LABEL: "\u0110i\u1EC1u h\u01B0\u1EDBng v\xED \u0111i\u1EC7n t\u1EED",
  VIEW_CARD_FOR_DEPENDENT: "Xem th\u1EBB cho {dependentName}",
  LEARN_MORE_LEAGUE_FAMILY: "v\u1EC1 League Family d\xE0nh cho ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c c\u1EE7a b\u1EA1n",
  GO_TO_LIFEMARKET: "V\xE0o Lifemarket \u0111\u1EC3 xem s\u1ED1 \u0111\u01A1n h\xE0ng {invoiceId}",
  GO_TO_CLAIM: "Xem chi ti\u1EBFt y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m cho {title}",
  GO_TO_RECEIPT: "Xem bi\xEAn lai cho {title}",
  LINK_TO_TRANSACTION_DETAILS: "Li\xEAn k\u1EBFt \u0111\u1EBFn chi ti\u1EBFt",
  VIEW_CLAIM_LABEL_HIDDEN: "Xem {vendor} y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m {referenceId}",
  QLE_LINK_LABEL: "T\xECm hi\u1EC3u th\xEAm v\u1EC1 c\xE1c s\u1EF1 ki\u1EC7n cu\u1ED9c s\u1ED1ng \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n",
  PLEASE_CALL: "Vui l\xF2ng g\u1ECDi {phoneNumber}",
  BROWSE_FILES: "Duy\u1EC7t t\u1EADp tin",
  TOTAL_BALANCE: "T\u1ED5ng s\u1ED1 d\u01B0",
  LIFEMARKET: "Lifemarked",
  ABOUT: "V\u1EC1",
  LEAGUE_CREDIT_DESCRIPTION_1: 'B\u1EA1n c\xF3 th\u1EC3 s\u1EED d\u1EE5ng League Credit trong {lifemarketLink} \u0111\u1EC3 mua nhi\u1EC1u s\u1EA3n ph\u1EA9m v\xE0 d\u1ECBch v\u1EE5 v\u1EC1 phong c\xE1ch s\u1ED1ng v\xE0 ch\u0103m s\xF3c s\u1EE9c kh\u1ECFe. Th\u1EE9 duy nh\u1EA5t b\u1EA1n kh\xF4ng th\u1EC3 d\xF9ng \u0111\u1EC3 chi ti\xEAu l\xE0 b\u1EA5t k\u1EF3 th\u1EE9 g\xEC \u0111\u01B0\u1EE3c \u0111\xE1nh d\u1EA5u l\xE0 "\u01AFu \u0111\xE3i \u0111\u1ED1i t\xE1c c\u1EE7a Li\xEAn \u0111o\xE0n".',
  LEAGUE_CREDIT_DESCRIPTION_2: '\u0110\u1EC3 s\u1EED d\u1EE5ng League Credit, h\xE3y ch\u1ECDn "League Credit" t\u1EEB danh s\xE1ch c\xE1c t\xF9y ch\u1ECDn thanh to\xE1n khi b\u1EA1n thanh to\xE1n t\u1EA1i Lifemarket. {learnMoreLink}',
  DID_YOU_GET_A_PROMO_CODE: "B\u1EA1n c\xF3 nh\u1EADn \u0111\u01B0\u1EE3c m\xE3 khuy\u1EBFn m\xE3i kh\xF4ng?",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "N\u1EBFu b\u1EA1n nh\u1EADn \u0111\u01B0\u1EE3c kho\u1EA3n t\xEDn d\u1EE5ng c\u1EEDa h\xE0ng ho\u1EB7c m\xE3 khuy\u1EBFn m\u1EA1i, b\u1EA1n c\xF3 th\u1EC3 nh\u1EADp v\xE0o \u0111\xE2y \u0111\u1EC3 nh\u1EADn kho\u1EA3n t\xEDn d\u1EE5ng. Sau khi nh\u1EADp, ch\xFAng s\u1EBD \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng \xE1p d\u1EE5ng cho t\xE0i kho\u1EA3n c\u1EE7a b\u1EA1n.",
  GET_CARE: "Nh\u1EADn s\u1EF1 ch\u0103m s\xF3c",
  DISCLAIMER: "Tuy\xEAn b\u1ED1 mi\u1EC5n tr\u1EEB tr\xE1ch nhi\u1EC7m",
  JOINED: "\u0110\xE3 tham gia",
  ROLLED_OVER: "Chuy\u1EC3n qua",
  NEED_MORE_INFO_SINGLE_CLAIM: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n c\u1EA7n th\xEAm th\xF4ng tin \u0111\u1EC3 \u0111\u01B0\u1EE3c x\u1EED l\xFD. Xem chi ti\u1EBFt b\xEAn d\u01B0\u1EDBi.",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n c\u1EA7n th\xEAm th\xF4ng tin \u0111\u1EC3 \u0111\u01B0\u1EE3c x\u1EED l\xFD. Xem chi ti\u1EBFt b\xEAn d\u01B0\u1EDBi.",
  RECENT_CLAIMS: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m g\u1EA7n \u0111\xE2y",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "N\u1EBFu b\u1EA1n c\xF3 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m kh\u1EA9n c\u1EA5p, vui l\xF2ng li\xEAn h\u1EC7 v\u1EDBi <a>b\u1ED9 ph\u1EADn H\u1ED7 tr\u1EE3 kh\xE1ch h\xE0ng</a>.",
  ABOUT_THIS_BENEFIT: "Gi\u1EDBi thi\u1EC7u quy\u1EC1n l\u1EE3i n\xE0y",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "Quy\u1EC1n l\u1EE3i n\xE0y \u0111\u01B0\u1EE3c cung c\u1EA5p cho b\u1EA1n b\u1EDFi ng\u01B0\u1EDDi s\u1EED d\u1EE5ng lao \u0111\u1ED9ng c\u1EE7a b\u1EA1n. N\u1EBFu c\xF3 th\u1EAFc m\u1EAFc ho\u1EB7c mu\u1ED1n bi\u1EBFt th\xF4ng tin chi ti\u1EBFt, vui l\xF2ng li\xEAn h\u1EC7 v\u1EDBi qu\u1EA3n tr\u1ECB vi\xEAn nh\xE2n s\u1EF1 c\u1EE7a b\u1EA1n.",
  ID_LIKE_TO_DOTDOTDOT: "T\xF4i mu\u1ED1n...",
  HAS_DEPENDENTS_DESCRIPTION: "Nh\u1EEFng ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c c\u1EE7a b\u1EA1n h\u01B0\u1EDFng quy\u1EC1n l\u1EE3i n\xE0y \u0111\u01B0\u1EE3c li\u1EC7t k\xEA d\u01B0\u1EDBi \u0111\xE2y. N\u1EBFu g\u1EA7n \u0111\xE2y b\u1EA1n \u0111\xE3 tr\u1EA3i qua m\u1ED9t s\u1EF1 ki\u1EC7n trong cu\u1ED9c s\u1ED1ng, b\u1EA1n c\xF3 th\u1EC3 \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n \u0111\u1EC3 thay \u0111\u1ED5i ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c c\u1EE7a m\xECnh. Nh\u1EA5p v\xE0o n\xFAt b\xEAn d\u01B0\u1EDBi \u0111\u1EC3 ki\u1EC3m tra xem b\u1EA1n c\xF3 \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n hay kh\xF4ng ch\u1EC9 trong v\xE0i b\u01B0\u1EDBc.",
  HAS_NO_DEPENDENTS_DESCRIPTION: "B\u1EA1n kh\xF4ng c\xF3 ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c n\xE0o \u0111\u01B0\u1EE3c h\u01B0\u1EDFng quy\u1EC1n l\u1EE3i n\xE0y. N\u1EBFu g\u1EA7n \u0111\xE2y b\u1EA1n \u0111\xE3 tr\u1EA3i qua m\u1ED9t s\u1EF1 ki\u1EC7n trong cu\u1ED9c s\u1ED1ng, b\u1EA1n c\xF3 th\u1EC3 \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n \u0111\u1EC3 th\xEAm ho\u1EB7c thay \u0111\u1ED5i ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c c\u1EE7a m\xECnh. Nh\u1EA5p v\xE0o n\xFAt b\xEAn d\u01B0\u1EDBi \u0111\u1EC3 ki\u1EC3m tra xem b\u1EA1n c\xF3 \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n hay kh\xF4ng ch\u1EC9 trong v\xE0i b\u01B0\u1EDBc.",
  ADD_A_DEPENDENT_CAPITAL: "Th\xEAm ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c",
  WALLET_FEEDBACK_TITLE: "H\xE3y gi\xFAp ch\xFAng t\xF4i c\u1EA3i thi\u1EC7n tr\u1EA3i nghi\u1EC7m V\xED \u0111i\u1EC7n t\u1EED",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: "Th\xF4ng tin \u0111\u01B0\u1EE3c cung c\u1EA5p cho quy\u1EC1n l\u1EE3i \u201C{benefitName}\u201D c\u1EE7a b\u1EA1n h\u1EEFu \xEDch nh\u01B0 th\u1EBF n\xE0o?",
  WALLET_FEEDBACK_SUBTITLE_OTHER: "Th\xF4ng tin \u0111\u01B0\u1EE3c cung c\u1EA5p cho {cardName} c\u1EE7a b\u1EA1n h\u1EEFu \xEDch nh\u01B0 th\u1EBF n\xE0o?",
  GO_BACK: "Quay l\u1EA1i",
  WALLET_LANDING_TITLE: "Th\xF4ng tin v\u1EC1 quy\u1EC1n l\u1EE3i v\xE0 ph\u1EA1m vi b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n, t\u1EA5t c\u1EA3 \u0111\u1EC1u c\xF3 \u1EDF m\u1ED9t n\u01A1i.",
  WALLET_LANDING_BODY: "V\xED k\u1EF9 thu\u1EADt s\u1ED1 c\u1EE7a b\u1EA1n l\xE0 n\u01A1i l\u01B0u tr\u1EEF c\xE1c l\u1EE3i \xEDch, ch\u01B0\u01A1ng tr\xECnh v\xE0 chi ti\u1EBFt b\u1EA3o hi\u1EC3m c\u1EE7a ch\u1EE7 lao \u0111\u1ED9ng c\u1EE7a b\u1EA1n. T\xECm n\u1ED9i dung b\u1EA1n c\u1EA7n ch\u1EC9 b\u1EB1ng m\u1ED9t l\u1EA7n nh\u1EA5n nhanh.",
  POLICY_ACTIVE: "Ho\u1EA1t \u0111\u1ED9ng ch\xEDnh s\xE1ch",
  REMAINING_BALANCE: "S\u1ED1 d\u01B0 c\xF2n l\u1EA1i",
  CONTACT_CUSTOMER_CARE: "Li\xEAn h\u1EC7 b\u1ED9 ph\u1EADn Ch\u0103m s\xF3c kh\xE1ch h\xE0ng",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "C\xF3 v\u1EBB nh\u01B0 \u0111\xE3 x\u1EA3y ra l\u1ED7i",
  WERE_HAVING_TROUBLE: "Ch\xFAng t\xF4i g\u1EB7p s\u1EF1 c\u1ED1 khi t\u1EA3i th\xF4ng tin n\xE0y, vui l\xF2ng th\u1EED l\xE0m m\u1EDBi trang. N\u1EBFu v\u1EA5n \u0111\u1EC1 v\u1EABn ti\u1EBFp di\u1EC5n, vui l\xF2ng {custCareLink}.",
  IN_NETWORK: "Trong m\u1EA1ng l\u01B0\u1EDBi",
  OUT_OF_NETWORK: "Ngo\xE0i m\u1EA1ng l\u01B0\u1EDBi",
  PROGRAMS: "Ch\u01B0\u01A1ng tr\xECnh",
  PROGRAM_CREATED_BY: "Ch\u01B0\u01A1ng tr\xECnh \u0111\u01B0\u1EE3c t\u1EA1o b\u1EDFi",
  TOP_FOCUS_AREA: "Khu v\u1EF1c t\u1EADp trung h\xE0ng \u0111\u1EA7u",
  DIMENSION_OF_HEALTH: "KH\xCDA C\u1EA0NH C\u1EE6A S\u1EE8C KH\u1ECEE",
  READING_MORE_ABOUT_DIMENSIONS: "B\u1EA1n c\xF3 mu\u1ED1n t\xECm hi\u1EC3u th\xEAm v\u1EC1 c\xE1ch c\xE1c kh\xEDa c\u1EA1nh \u1EA3nh h\u01B0\u1EDFng \u0111\u1EBFn s\u1EE9c kh\u1ECFe t\u1ED5ng th\u1EC3 c\u1EE7a m\xECnh kh\xF4ng?",
  READ_OUR_ARTICLE: "\u0110\u1ECDc b\xE0i vi\u1EBFt c\u1EE7a ch\xFAng t\xF4i",
  HEALTH_PROGRAMS_FOR_YOU: "Ch\u01B0\u01A1ng tr\xECnh s\u1EE9c kh\u1ECFe d\xE0nh cho b\u1EA1n",
  COMPLETE_ACTIVITIES: "Ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng \u0111\u1EC3 \u0111\u1EA1t \u0111\u01B0\u1EE3c m\u1EE5c ti\xEAu s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n. \u0110\u01B0\u1EE3c \u0111\u1EC1 xu\u1EA5t d\u1EF1a tr\xEAn H\u1ED3 s\u01A1 s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n.",
  FT: "ft",
  CM: "cm",
  IN: "in",
  LB: "lb",
  KG: "kg",
  LETS_BEGIN: "H\xE3y b\u1EAFt \u0111\u1EA7u",
  KEEP_GOING: "Ti\u1EBFp t\u1EE5c",
  SKIP: "B\u1ECF qua",
  HP_ONBOARDING_SUBMIT: "H\xE3y b\u1EAFt \u0111\u1EA7u",
  FOR_PROVIDERS_AND_PHARMACISTS: "D\xE0nh cho nh\xE0 cung c\u1EA5p/d\u01B0\u1EE3c s\u0129",
  WEVE_PICKED_UP: "Ch\xFAng t\xF4i ti\u1EBFp t\u1EE5c t\u1EEB ch\u1ED7 b\u1EA1n d\u1EEBng l\u1EA1i.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "B\u1EA1n ch\u1EC9 c\xF2n v\xE0i c\xE2u h\u1ECFi n\u1EEFa l\xE0 ki\u1EBFm \u0111\u01B0\u1EE3c {points} \u0111i\u1EC3m.",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "B\u1EA1n ch\u1EC9 c\xF2n v\xE0i c\xE2u h\u1ECFi n\u1EEFa l\xE0 ho\xE0n th\xE0nh b\xE0i \u0111\xE1nh gi\xE1.",
  PRESS_ENTER: "Nh\u1EA5n Enter",
  SELECT_ALL_THAT_APPLY: "Ch\u1ECDn t\u1EA5t c\u1EA3 nh\u1EEFng \u0111i\u1EC1u th\xEDch h\u1EE3p",
  SELECT_UP_TO: "Ch\u1ECDn t\u1ED1i \u0111a {number}",
  AMOUNT_QUESTIONS: "{amount} c\xE2u h\u1ECFi",
  QUESTION_PROGRESS: "C\xC2U H\u1ECEI {questionNumber} C\u1EE6A {totalQuestions}",
  COMPLETE: "Ho\xE0n th\xE0nh",
  VIEW_MORE: "Xem th\xEAm",
  DOWNLOAD_APP_STORE: "T\u1EA3i xu\u1ED1ng tr\xEAn App Store",
  DOWNLOAD_PLAY_STORE: "T\u1EA3i xu\u1ED1ng tr\xEAn Google Play",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "Ch\xFAng t\xF4i r\u1EA5t ti\u1EBFc, trang b\u1EA1n y\xEAu c\u1EA7u \u0111\xE3 h\u1EBFt h\u1EA1n ho\u1EB7c kh\xF4ng c\xF2n hi\u1EC7u l\u1EF1c.",
  EARNED_X_TIMES: "Ki\u1EBFm \u0111\u01B0\u1EE3c {amount}X",
  EARNED: "\u0110\xE3 ki\u1EBFm \u0111\u01B0\u1EE3c",
  YOUVE_EARNED_TOTAL: "B\u1EA1n \u0111\xE3 ki\u1EBFm \u0111\u01B0\u1EE3c t\u1ED5ng c\u1ED9ng",
  YOUVE_EARNED_GRAND_TOTAL: "B\u1EA1n \u0111\xE3 ki\u1EBFm \u0111\u01B0\u1EE3c t\u1ED5ng c\u1ED9ng",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "B\u1EA1n \u0111\xE3 \u0111\u1EA1t \u0111\u01B0\u1EE3c Huy hi\u1EC7u Th\xE0nh t\xEDch",
  CONGRATS_COMPLETED: "Xin ch\xFAc m\u1EEBng! B\u1EA1n \u0111\xE3 ho\xE0n th\xE0nh",
  VIEW_MY_HEALTH_DASHBOARD: "Xem B\u1EA3ng \u0111i\u1EC1u khi\u1EC3n s\u1EE9c kh\u1ECFe c\u1EE7a t\xF4i",
  LATEST_INSIGHTS_METRICS_AND_MORE: "Th\xF4ng tin chi ti\u1EBFt, s\u1ED1 li\u1EC7u m\u1EDBi nh\u1EA5t v\xE0 nhi\u1EC1u h\u01A1n n\u1EEFa",
  FEEDBACK_TITLE: "Th\xF4ng tin n\xE0y h\u1EEFu \xEDch nh\u01B0 th\u1EBF n\xE0o?",
  FEEDBACK_MIN_SCALE: "Kh\xF4ng h\u1EEFu \xEDch",
  FEEDBACK_MAX_SCALE: "R\u1EA5t h\u1EEFu \xEDch",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "G\u1EA7n \u0111\xE2y b\u1EA1n \u0111\xE3 tr\u1EA3i qua nh\u1EEFng thay \u0111\u1ED5i trong cu\u1ED9c s\u1ED1ng ho\u1EB7c gia \u0111\xECnh c\u1EE7a b\u1EA1n?",
  DID_YOU_RECENTLY: "B\u1EA1n m\u1EDBi k\u1EBFt h\xF4n ho\u1EB7c sinh con ph\u1EA3i kh\xF4ng? M\u1ED9t s\u1ED1 s\u1EF1 ki\u1EC7n trong cu\u1ED9c s\u1ED1ng c\xF3 th\u1EC3 \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n cho b\u1EA1n \u0111\u0103ng k\xFD l\u1EA1i.",
  LEARN_MORE: "T\xECm hi\u1EC3u th\xEAm",
  VIEW_ACTIVITY: "Xem Ho\u1EA1t \u0111\u1ED9ng",
  CONTINUE: "Ti\u1EBFp t\u1EE5c",
  ARIA_CIRCLED_CHECKMARK: "D\u1EA5u t\xEDch m\xE0u tr\u1EAFng \u0111\u01B0\u1EE3c bao quanh b\u1EDFi m\u1ED9t v\xF2ng tr\xF2n",
  NOT_NOW: "Kh\xF4ng ph\u1EA3i b\xE2y gi\u1EDD",
  VAL_POINTS: "{points} \u0111i\u1EC3m",
  ACTIONS: "H\xE0nh \u0111\u1ED9ng",
  VIEW_ALL_CLAIMS: "Xem t\u1EA5t c\u1EA3 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "H\u1ED9p n\xE0y ph\u1EA3i \u0111\u01B0\u1EE3c ch\u1ECDn \u0111\u1EC3 ti\u1EBFp t\u1EE5c",
  PLEASE_NOTE: "Xin l\u01B0u \xFD",
  COPY: "Sao ch\xE9p",
  STATUS: "Tr\u1EA1ng th\xE1i",
  STARTED: "B\u1EAFt \u0111\u1EA7u",
  APPROVED: "Ch\u1EA5p thu\u1EADn",
  CANCELLED: "\u0110\xE3 h\u1EE7y",
  UPLOADED_DOCUMENT: "\u0110\xE3 t\u1EA3i l\xEAn {date} \u2022 \u0110\xE3 t\u1EA3i l\xEAn b\u1EDFi {user}",
  MORE_INFORMATION_NEEDED: "C\u1EA7n th\xF4ng tin",
  PAID: "\u0110\xE3 tr\u1EA3 ti\u1EC1n",
  PENDING_EXTERNAL_REVIEW: "\u0110\xE3 g\u1EEDi \u0111\u1EC3 \u0111\xE1nh gi\xE1 b\xEAn ngo\xE0i",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "Ph\xEA duy\u1EC7t tr\u01B0\u1EDBc Th\xF4ng tin th\xEAm",
  PREAPPROVAL_REJECTED: "Ph\xEA duy\u1EC7t tr\u01B0\u1EDBc b\u1ECB t\u1EEB ch\u1ED1i",
  PREAPPROVED: "\u0110\u01B0\u1EE3c ph\xEA duy\u1EC7t tr\u01B0\u1EDBc",
  REJECTED: "B\u1ECB t\u1EEB ch\u1ED1i",
  SUBMITTED_FOR_PREAPPROVAL: "\u0110\xE3 g\u1EEDi \u0111\u1EC3 ph\xEA duy\u1EC7t tr\u01B0\u1EDBc",
  SUBMITTED: "G\u1EEDi",
  WALLET: "V\xED \u0111i\u1EC7n t\u1EED",
  AMOUNT: "S\u1ED1 ti\u1EC1n",
  ADD: "Th\xEAm v\xE0o",
  EDIT: "S\u1EEDa",
  DETAILS: "Chi ti\u1EBFt",
  AND: "v\xE0",
  TODAY: "H\xF4m nay",
  CLAIMS: "Y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng",
  GROUP_ID: "Id nh\xF3m",
  ZIP_CODE: "M\xE3 b\u01B0u ch\xEDnh",
  CITY: "Th\xE0nh ph\u1ED1",
  STATE: "Ti\u1EC3u bang",
  REMOVE: "X\xF3a",
  HEALTH_SPENDING_ACCOUNT: "T\xE0i kho\u1EA3n chi ti\xEAu s\u1EE9c kh\u1ECFe",
  HSA: "HSA",
  LIFESTYLE_SPENDING_ACCOUNT: "T\xE0i kho\u1EA3n chi ti\xEAu l\u1ED1i s\u1ED1ng",
  LSA: "LSA",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "Th\u1ECFa thu\u1EADn ho\xE0n tr\u1EA3 chi ph\xED y t\u1EBF",
  DRUGS: "Thu\u1ED1c",
  TRAVEL: "Du l\u1ECBch",
  HEALTH_INSURANCE: "B\u1EA3o hi\u1EC3m y t\u1EBF",
  SETTINGS: "C\xE0i \u0111\u1EB7t",
  ACTIVE: "T\xEDch c\u1EF1c",
  ENDED: "\u0110\xE3 k\u1EBFt th\xFAc",
  SUSPENDED: "B\u1ECB t\u1EA1m ng\u01B0ng",
  PENDING: "Ch\u1EDD gi\u1EA3i quy\u1EBFt",
  LOADING: "\u0110ang t\u1EA3i",
  ERROR_STATE_TITLE: "\u0110\xE3 c\xF3 l\u1ED7i g\xEC \u0111\xF3",
  HELP_EMAIL: "help@league.com",
  SUBMIT: "G\u1EEDi",
  EARN_NUMBER_POINTS_FULL: "Ki\u1EBFm \u0111\u01B0\u1EE3c {points} \u0111i\u1EC3m",
  CLOSE: "\u0110\xF3ng",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "Th\u1EED th\xE1ch n\xE0y di\u1EC5n ra quanh n\u0103m",
  END_DAY_AT_TIME: "K\u1EBFt th\xFAc {day}",
  ENDED_DAY_AT_TIME: "\u0110\xE3 k\u1EBFt th\xFAc {day}",
  START_DAY_AT_TIME: "B\u1EAFt \u0111\u1EA7u {day}",
  SPONSOR_LOGO: "Logo {name}",
  START_AND_END_DATES: "B\u1EAFt \u0111\u1EA7u {startDate} \u2022 K\u1EBFt th\xFAc {endDate}",
  SIGN_UP_BY_DATE: "\u0110\u0103ng k\xFD tr\u01B0\u1EDBc {date}",
  SIGN_UP_BEGINS_DATE: "\u0110\u0103ng k\xFD b\u1EAFt \u0111\u1EA7u {date}",
  SIGN_UP_DATE_PASSED: "Ng\xE0y \u0111\u0103ng k\xFD \u0111\xE3 qua",
  SIGN_UP_PERIOD_PASSED: "Th\u1EDDi gian \u0111\u0103ng k\xFD \u0111\xE3 qua",
  CHALLENGE_DETAILS: "Chi ti\u1EBFt th\u1EED th\xE1ch",
  CHALLENGE_PROGRESS: "Ti\u1EBFn tr\xECnh th\xE1ch th\u1EE9c",
  X_PERCENT_COMPLETE: "{number}% Ho\xE0n th\xE0nh",
  SIGN_UP_FOR_CHALLENGE: "\u0110\u0103ng k\xFD",
  GROUP_CHALLENGE: "Th\u1EED th\xE1ch nh\xF3m",
  SOLO_CHALLENGE: "Th\u1EED th\xE1ch \u0111\u01A1n",
  TRACK_WEARABLES_ALT_TEXT: "Ng\u01B0\u1EDDi \u0111ang xem d\u1EEF li\u1EC7u \u0111\u1ED3ng h\u1ED3 th\xF4ng minh",
  CONNECT_A_DEVICE: "K\u1EBFt n\u1ED1i thi\u1EBFt b\u1ECB",
  DEVICES_CONNECTED_TITLE: "C\xF3 v\u1EBB nh\u01B0 b\u1EA1n \u0111\xE3 k\u1EBFt n\u1ED1i!",
  DEVICES_CONNECTED_DESCRIPTION: "Ch\xFAng t\xF4i \u0111ang \u0111\u1ED3ng b\u1ED9 h\xF3a d\u1EEF li\u1EC7u c\u1EE7a b\u1EA1n. N\u1EBFu b\u1EA1n ch\u1ECDn theo d\xF5i t\u1EF1 \u0111\u1ED9ng, ch\xFAng t\xF4i c\xF3 th\u1EC3 t\u1EF1 \u0111\u1ED9ng \u0111\xE1nh d\u1EA5u c\xE1c ho\u1EA1t \u0111\u1ED9ng l\xE0 \u0111\xE3 ho\xE0n t\u1EA5t b\u1EB1ng c\xE1ch s\u1EED d\u1EE5ng {dataPoint} tr\xEAn thi\u1EBFt b\u1ECB c\u1EE7a b\u1EA1n.",
  DEVICES_UNCONNECTED_TITLE: "H\xE3y k\u1EBFt n\u1ED1i thi\u1EBFt b\u1ECB c\u1EE7a b\u1EA1n!",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "Theo d\xF5i t\u1EF1 \u0111\u1ED9ng",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "Theo d\xF5i th\u1EE7 c\xF4ng",
  DEVICES_UNCONNECTED_DESCRIPTION: "K\u1EBFt n\u1ED1i c\xE1c \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB s\u1EE9c kh\u1ECFe y\xEAu th\xEDch c\u1EE7a b\u1EA1n \u0111\u1EC3 t\u1EF1 \u0111\u1ED9ng \u0111\xE1nh d\u1EA5u c\xE1c ho\u1EA1t \u0111\u1ED9ng l\xE0 \u0111\xE3 ho\xE0n th\xE0nh.",
  TRACK_CHALLENGE_AUTOMATICALLY: "Theo d\xF5i t\u1EF1 \u0111\u1ED9ng",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "N\u1EBFu b\u1EA1n ch\u1ECDn theo d\xF5i t\u1EF1 \u0111\u1ED9ng, sau khi \u0111\u0103ng k\xFD, {instructionsLink} \u0111\u1EC3 k\u1EBFt n\u1ED1i thi\u1EBFt b\u1ECB c\u1EE7a b\u1EA1n b\u1EB1ng \u1EE9ng d\u1EE5ng di \u0111\u1ED9ng League.",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "l\xE0m theo c\xE1c b\u01B0\u1EDBc sau",
  TRACK_CHALLENGE_MANUALLY: "Thay v\xE0o \u0111\xF3, h\xE3y theo d\xF5i th\u1EE7 c\xF4ng",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "N\u1EBFu b\u1EA1n ch\u1ECDn theo d\xF5i th\u1EE7 c\xF4ng, b\u1EA1n v\u1EABn c\xF3 th\u1EC3 k\u1EBFt n\u1ED1i thi\u1EBFt b\u1ECB sau. {learnMoreLink} v\u1EC1 vi\u1EC7c theo d\xF5i ti\u1EBFn tr\xECnh c\u1EE7a b\u1EA1n theo c\xE1ch th\u1EE7 c\xF4ng.",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "T\xECm hi\u1EC3u th\xEAm",
  TRACK_HEALTH_DATA_ALT: "B\u1EA3ng \u0111i\u1EC1u khi\u1EC3n v\u1EDBi bi\u1EC3u \u0111\u1ED3 theo d\xF5i d\u1EEF li\u1EC7u s\u1EE9c kh\u1ECFe",
  TRACK_MANUALLY_CONFIRM_TITLE: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n theo d\xF5i th\u1EE7 c\xF4ng kh\xF4ng?",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "B\u1EA1n v\u1EABn c\xF3 th\u1EC3 k\u1EBFt n\u1ED1i thi\u1EBFt b\u1ECB sau n\xE0y",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n tho\xE1t kh\xF4ng?",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "N\u1EBFu b\u1EA1n ch\u1ECDn tho\xE1t, c\xE1c thay \u0111\u1ED5i c\u1EE7a b\u1EA1n c\xF3 th\u1EC3 kh\xF4ng \u0111\u01B0\u1EE3c l\u01B0u.",
  EXIT: "Tho\xE1t",
  CHALLENGE: "Th\u1EED th\xE1ch",
  CHALLENGES: "Th\u1EED th\xE1ch",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "\u0110\u0103ng k\xFD tham gia th\u1EED th\xE1ch \u0111\u1EC3 ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng h\xE0ng ng\xE0y v\xE0 c\u1EA1nh tranh v\u1EDBi b\u1EA1n b\xE8 c\u1EE7a m\xECnh!",
  THIS_CHALLENGE_HAS_ENDED: "Th\u1EED th\xE1ch n\xE0y \u0111\xE3 k\u1EBFt th\xFAc",
  COMPLETED_ON_DATE: "Ho\xE0n th\xE0nh v\xE0o {date}",
  ENDED_ON_DATE: "\u0110\xE3 k\u1EBFt th\xFAc v\xE0o {date}",
  LEAVE_CHALLENGE: "R\u1EDDi kh\u1ECFi th\u1EED th\xE1ch",
  LEAVE_CHALLENGE_QUESTION: "R\u1EDDi kh\u1ECFi th\u1EED th\xE1ch?",
  LEAVE_CHALLENGE_DETAIL: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n lo\u1EA1i b\u1ECF th\u1EED th\xE1ch n\xE0y kh\u1ECFi h\xE0nh tr\xECnh c\u1EE7a m\xECnh kh\xF4ng? M\u1ECDi ti\u1EBFn tri\u1EC3n trong th\u1EED th\xE1ch n\xE0y s\u1EBD \u0111\u01B0\u1EE3c thi\u1EBFt l\u1EADp l\u1EA1i.",
  LEAVE: "R\u1EDDi kh\u1ECFi",
  SIGN_UP_FOR_THE_CHALLENGE: "\u0110\u0103ng k\xFD th\u1EED th\xE1ch",
  CHALLENGE_SENSITIVE_DATA: "D\u1EEF li\u1EC7u s\u1EE9c kh\u1ECFe v\xE0 th\xF4ng tin c\xE1 nh\xE2n c\u1EE7a b\u1EA1n r\u1EA5t nh\u1EA1y c\u1EA3m v\xE0 ch\xFAng t\xF4i c\xF3 tr\xE1ch nhi\u1EC7m b\u1EA3o m\u1EADt v\xE0 gi\u1EEF b\xED m\u1EADt nh\u1EEFng th\xF4ng tin n\xE0y. Ch\xFAng t\xF4i r\u1EA5t coi tr\u1ECDng tr\xE1ch nhi\u1EC7m n\xE0y.",
  CHALLENGE_USING_DATA_TO: "Khi b\u1EA1n \u0111\u0103ng k\xFD tham gia th\u1EED th\xE1ch, ch\xFAng t\xF4i s\u1EBD s\u1EED d\u1EE5ng d\u1EEF li\u1EC7u c\u1EE7a b\u1EA1n \u0111\u1EC3:",
  COMPLETE_ACTIVITIES_SHORT: "Ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "Chia s\u1EBB ti\u1EBFn tr\xECnh th\u1EED th\xE1ch c\u1EE7a b\u1EA1n v\u1EDBi ng\u01B0\u1EDDi s\u1EED d\u1EE5ng lao \u0111\u1ED9ng c\u1EE7a b\u1EA1n",
  SHARE_CHALLENGE_PROGRESS: "Chia s\u1EBB ti\u1EBFn tr\xECnh th\u1EED th\xE1ch c\u1EE7a b\u1EA1n v\u1EDBi \u0111\u1ED3ng nghi\u1EC7p (v\xE0 ng\u01B0\u1EDDi s\u1EED d\u1EE5ng lao \u0111\u1ED9ng)",
  RANK_SCORE: "X\u1EBFp h\u1EA1ng \u0111i\u1EC3m s\u1ED1 c\u1EE7a b\u1EA1n trong b\u1EA3ng x\u1EBFp h\u1EA1ng",
  DETERMINE_CHALLENGE_WINNERS: "X\xE1c \u0111\u1ECBnh ng\u01B0\u1EDDi chi\u1EBFn th\u1EAFng th\u1EED th\xE1ch",
  ACCEPT_BEFORE_CONTINUE: "B\u1EA1n ph\u1EA3i ch\u1EA5p nh\u1EADn ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt tr\u01B0\u1EDBc khi ti\u1EBFp t\u1EE5c.",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "B\u1EB1ng c\xE1ch \u0111\xE1nh d\u1EA5u v\xE0o \xF4 n\xE0y, t\xF4i \u0111\u1ED3ng \xFD v\u1EDBi <a>ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt.</a>",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "T\xECm hi\u1EC3u th\xEAm v\u1EC1 c\xE1ch League qu\u1EA3n l\xFD v\xE0 s\u1EED d\u1EE5ng d\u1EEF li\u1EC7u c\u1EE7a b\u1EA1n trong <a>ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt</a> c\u1EE7a ch\xFAng t\xF4i.",
  PRIZE: "Ph\u1EA7n th\u01B0\u1EDFng",
  RULES: "Quy t\u1EAFc",
  LEADERBOARD: "B\u1EA3ng x\u1EBFp h\u1EA1ng",
  TOOLTIP_ACTION_DESCRIPTION: "Xem ti\xEAu ch\xED x\u1EBFp h\u1EA1ng b\u1EA3ng x\u1EBFp h\u1EA1ng",
  MARK_AS_DONE: "\u0110\xE1nh d\u1EA5u \u0110\xE3 xong",
  CLOSE_ACTIVITY: "\u0110\xF3ng Ho\u1EA1t \u0111\u1ED9ng",
  GOAL_INCOMPLETE: "B\u1EA1n ph\u1EA3i ho\xE0n th\xE0nh m\u1EE5c ti\xEAu tr\u01B0\u1EDBc khi ti\u1EBFp t\u1EE5c.",
  PROGRESS_RECORD_ERROR: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi ghi l\u1EA1i ti\u1EBFn tr\xECnh cu\u1ED1i c\xF9ng c\u1EE7a b\u1EA1n.",
  YOUVE_COMPLETED: "B\u1EA1n \u0111\xE3 ho\xE0n th\xE0nh",
  ACTIVITY_COMPLETE: "Ho\u1EA1t \u0111\u1ED9ng \u0111\xE3 ho\xE0n t\u1EA5t",
  PAGE_NOT_FOUND_HEADER: "Kh\xF4ng t\xECm th\u1EA5y trang",
  PAGE_NOT_FOUND_CONTENT: "C\xF3 v\u1EBB nh\u01B0 b\u1EA1n \u0111\xE3 b\u1ECB l\u1EA1c m\u1ED9t ch\xFAt!",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "Ch\xFAng t\xF4i s\u1EBD gi\xFAp b\u1EA1n t\xECm th\u1EA5y ch\xEDnh m\xECnh.",
  PAGE_NOT_FOUND_BUTTON: "Tr\u1EDF v\u1EC1 nh\xE0",
  REWARD_POINTS: "\u0110i\u1EC3m th\u01B0\u1EDFng",
  REWARD_POINTS_EARNED: "\u0110i\u1EC3m th\u01B0\u1EDFng t\xEDch l\u0169y \u0111\u01B0\u1EE3c",
  REWARD_POINTS_MISSED: "\u0110i\u1EC3m th\u01B0\u1EDFng b\u1ECB b\u1ECF l\u1EE1",
  X_REWARD_POINTS: "{points} \u0110i\u1EC3m th\u01B0\u1EDFng",
  X_LEAGUE_POINTS: "{points} \u0110i\u1EC3m League",
  REWARDS_PROGRAM_ELIGIBLE: "Ch\u01B0\u01A1ng tr\xECnh ph\u1EA7n th\u01B0\u1EDFng \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n",
  EXPIRY_DATE: "Ng\xE0y h\u1EBFt h\u1EA1n",
  NO_EXPIRY_DATE: "Kh\xF4ng c\xF3 ng\xE0y h\u1EBFt h\u1EA1n",
  POINTS_BALANCE: "S\u1ED1 d\u01B0 \u0111i\u1EC3m",
  REDEEM_NEXT_TIER_POINTS: "B\u1EA1n c\xF2n {points} n\u1EEFa l\xE0 c\xF3 th\u1EC3 \u0111\u1ED5i \u0111\u01B0\u1EE3c {value}.",
  VIEW_YOUR_REWARDS: "Xem ph\u1EA7n th\u01B0\u1EDFng c\u1EE7a b\u1EA1n",
  REDEEMABLE: "C\xF3 th\u1EC3 \u0111\u1ED5i l\u1EA5y {value}.",
  ACTIVATION_PENDING: "\u0110ang ch\u1EDD k\xEDch ho\u1EA1t",
  EXPIRED: "H\u1EBFt h\u1EA1n",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "T\xE0i kho\u1EA3n c\u1EE7a b\u1EA1n \u0111ang ho\u1EA1t \u0111\u1ED9ng v\xE0 \u0111ang trong qu\xE1 tr\xECnh thi\u1EBFt l\u1EADp.",
  PLEASE_TRY_AGAIN_SHORTLY: "Vui l\xF2ng th\u1EED l\u1EA1i sau \xEDt ph\xFAt",
  MANAGE_DEPENDENTS: "Qu\u1EA3n l\xFD ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c",
  VIEW_CARD: "Xem th\u1EBB",
  MEMBER_ID: "ID th\xE0nh vi\xEAn",
  SPOUSE: "V\u1EE3/ch\u1ED3ng",
  COMMON_LAW: "Th\xF4ng lu\u1EADt",
  CHILD: "\u0110\u1EE9a tr\u1EBB",
  PARENT: "Cha//m\u1EB9",
  GRANDPARENT: "\xD4ng b\xE0",
  GRANDCHILD: "Ch\xE1u",
  UNCLE_AUNT: "Ch\xFA/D\xEC",
  NEPHEW_NIECE: "Ch\xE1u trai/Ch\xE1u g\xE1i",
  INELIGIBLE_CHILD: "Tr\u1EBB em kh\xF4ng \u0111\u1EE7 \u0111i\u1EC1u ki\u1EC7n",
  SIBLING: "Anh ch\u1ECB em ru\u1ED9t",
  OTHER: "Kh\xE1c",
  DEPENDENT_CARD_HOLDER: "Th\u1EBB c\u1EE7a {dependentName} cho",
  DATE: "NG\xC0Y",
  DESCRIPTION: "M\xF4 t\u1EA3",
  TYPE: "Ki\u1EC3u",
  ID: "Id",
  CLAIM: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  TOP_UP: "N\u1EA1p ti\u1EC1n",
  ADJUSTMENT: "\u0110i\u1EC1u ch\u1EC9nh",
  REFUND: "Ho\xE0n ti\u1EC1n",
  LEAGUE_CREDIT: "T\xEDn d\u1EE5ng League",
  CREDIT_CARD: "Th\u1EBB t\xEDn d\u1EE5ng",
  CGA: "CGA",
  NO_TRANSACTIONS_YET: "Ch\u01B0a c\xF3 giao d\u1ECBch n\xE0o",
  NO_MORE_TRANSACTIONS: "Kh\xF4ng c\xF2n giao d\u1ECBch",
  PROMO_CODE: "M\xE3 khuy\u1EBFn m\u1EA1i",
  PROMO_CODE_NOT_FOUND: "M\xE3 khuy\u1EBFn m\xE3i kh\xF4ng h\u1EE3p l\u1EC7",
  PROMO_CODE_SUCCESS: "M\xE3 khuy\u1EBFn m\xE3i \u0111\xE3 \u0111\u1ED5i!",
  TOTAL_CREDIT: "T\u1ED5ng s\u1ED1 t\xEDn d\u1EE5ng:",
  CREDIT_INFORMATION: "Th\xF4ng tin t\xEDn d\u1EE5ng",
  ENTER_CODE: "Nh\u1EADp m\xE3",
  DOCUMENT_NAME: "T\xEAn t\xE0i li\u1EC7u",
  REQUIRED: "B\u1EAFt bu\u1ED9c",
  CANCEL: "H\u1EE7y",
  CONFIRM: "X\xE1c nh\u1EADn",
  ALLOCATION: "Ph\xE2n b\u1ED5",
  COVERED: "\u0110\xE3 che ph\u1EE7",
  NOT_COVERED: "Kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m",
  COVERED_WITH_RESTRICTIONS: "\u0110\u01B0\u1EE3c b\u1EA3o hi\u1EC3m v\u1EDBi c\xE1c h\u1EA1n ch\u1EBF",
  EXPAND_ALL: "M\u1EDF r\u1ED9ng t\u1EA5t c\u1EA3",
  COLLAPSE_ALL: "Thu g\u1ECDn t\u1EA5t c\u1EA3",
  EXAMPLES: "V\xED d\u1EE5: ",
  RESTRICTIONS: "H\u1EA1n ch\u1EBF",
  SEARCH_COVERAGE: "T\xECm ki\u1EBFm ph\u1EA1m vi b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "K\u1EBFt qu\u1EA3 t\xECm ki\u1EBFm s\u1EBD t\u1EF1 \u0111\u1ED9ng c\u1EADp nh\u1EADt khi b\u1EA1n nh\u1EADp",
  INDIVIDUAL: "C\xE1 nh\xE2n",
  INDIVIDUAL_CAP: "C\xE1 nh\xE2n",
  DEPENDENTS: "Ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c",
  SELECT_ONE: "Ch\u1ECDn m\u1ED9t",
  MORE_OPTIONS: "Th\xEAm t\xF9y ch\u1ECDn",
  VENDOR: "Nh\xE0 cung c\u1EA5p",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "ch\xFAng s\u1EBD t\u1EF1 \u0111\u1ED9ng \u0111\u01B0\u1EE3c th\xEAm v\xE0o {benefitName} c\u1EE7a b\u1EA1n.",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "<strong>{percent}%</strong> s\u1EBD \u0111\u01B0\u1EE3c th\xEAm v\xE0o <strong>{benefitName}</strong>c\u1EE7a b\u1EA1n",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "<strong>{percent}%</strong> trong s\u1ED1 \u0111\xF3 s\u1EBD \u0111\u01B0\u1EE3c th\xEAm v\xE0o <strong>{benefitName}</strong> c\u1EE7a b\u1EA1n.",
  DOCUMENT_TITLE: "{title} - League",
  MEDICAL: "Y t\u1EBF",
  TOTAL: "T\u1ED5ng",
  PROFILE_UPDATE_FAILED: "C\u1EADp nh\u1EADt h\u1ED3 s\u01A1 kh\xF4ng th\xE0nh c\xF4ng",
  PROFILE_PHOTO: "\u1EA2nh h\u1ED3 s\u01A1",
  PROFILE_PHOTO_DESCRIPTION: "\u1EA2nh n\xE0y hi\u1EC3n th\u1ECB tr\xEAn h\u1ED3 s\u01A1 ng\u01B0\u1EDDi d\xF9ng c\u1EE7a b\u1EA1n",
  SELECT_AVATAR: "Ch\u1ECDn h\xECnh \u0111\u1EA1i di\u1EC7n",
  SELECT_AVATAR_ERROR: "B\u1EA1n ph\u1EA3i ch\u1ECDn h\xECnh \u0111\u1EA1i di\u1EC7n tr\u01B0\u1EDBc khi ti\u1EBFp t\u1EE5c.",
  GET_AVATARS_ERROR: "R\u1EA5t ti\u1EBFc! \u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi t\u1EA3i \u1EA3nh \u0111\u1EA1i di\u1EC7n. Vui l\xF2ng quay l\u1EA1i v\xE0 ch\u1ECDn t\xF9y ch\u1ECDn kh\xE1c.",
  ADDRESS_LINE_1: "D\xF2ng \u0111\u1ECBa ch\u1EC9 1",
  ADDRESS_LINE_2: "D\xF2ng \u0111\u1ECBa ch\u1EC9 2",
  LANGUAGE_SELECTOR_ENGLISH: "Ti\u1EBFng Anh",
  LANGUAGE_SELECTOR_FRENCH: "Ti\u1EBFng Ph\xE1p",
  LANGUAGE_SELECTOR_SPANISH: "Ti\u1EBFng T\xE2y Ban Nha",
  DOMESTIC_PARTNERSHIP: "Quan h\u1EC7 chung s\u1ED1ng",
  YES: "C\xF3",
  NO: "Kh\xF4ng",
  WHY_DO_YOU_ASK: "T\u1EA1i sao b\u1EA1n h\u1ECFi?",
  LAST_DAY_OF_ENROLLMENT: "Ng\xE0y cu\u1ED1i c\xF9ng c\u1EE7a th\u1EDDi gian ghi danh: {lastDay}",
  WALLET_WELCOME: "Ch\xE0o m\u1EEBng b\u1EA1n \u0111\u1EBFn v\u1EDBi V\xED \u0111i\u1EC7n t\u1EED c\u1EE7a b\u1EA1n!",
  WALLET_DESCRIPTION: "V\xED l\xE0 n\u01A1i b\u1EA1n c\xF3 th\u1EC3 truy c\u1EADp c\xE1c quy\u1EC1n l\u1EE3i, g\u1EEDi y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m v\xE0 ki\u1EC3m tra ph\u1EA1m vi b\u1EA3o hi\u1EC3m c\u1EE7a m\xECnh.  B\u1EA1n s\u1EBD c\xF3 th\u1EC3 g\u1EEDi y\xEAu c\u1EA7u thanh to\xE1n c\xE1c chi ph\xED ph\xE1t sinh trong th\u1EDDi gian \u0111\u0103ng k\xFD sau khi v\xED c\u1EE7a b\u1EA1n \u0111\u01B0\u1EE3c k\xEDch ho\u1EA1t. Trong th\u1EDDi gian ch\u1EDD \u0111\u1EE3i, {healthProgramLink}!",
  HEALTH_PROGRAM_LINK_COPY: "th\u1EED m\u1ED9t trong nh\u1EEFng ch\u01B0\u01A1ng tr\xECnh s\u1EE9c kh\u1ECFe c\u1EE7a ch\xFAng t\xF4i",
  FINALIZE_SELECTIONS_HEADER_COPY: "B\u1EA1n c\xF3 mu\u1ED1n \u0111\u01B0\u1EE3c h\u01B0\u1EDFng quy\u1EC1n l\u1EE3i s\u1EDBm h\u01A1n kh\xF4ng?",
  FINALIZE_SELECTIONS_COPY_1: "Sau khi ho\xE0n t\u1EA5t l\u1EF1a ch\u1ECDn c\u1EE7a b\u1EA1n, ch\xFAng t\xF4i s\u1EBD c\xF3 th\u1EC3 g\u1EEDi ngay th\xF4ng tin chi ti\u1EBFt v\u1EC1 g\xF3i d\u1ECBch v\u1EE5 c\u1EE7a b\u1EA1n t\u1EDBi nh\xE0 m\u1EA1ng! \u0110i\u1EC1u n\xE0y c\xF3 ngh\u0129a l\xE0 b\u1EA1n s\u1EBD kh\xF4ng ph\u1EA3i \u0111\u1EE3i \u0111\u1EBFn khi k\u1EBFt th\xFAc th\u1EDDi gian \u0111\u0103ng k\xFD \u0111\u1EC3 xem v\xED \u0111i\u1EC7n t\u1EED c\u1EE7a m\xECnh.",
  FINALIZE_SELECTIONS_COPY_2: "\u0110i\u1EC1u n\xE0y c\u0169ng c\xF3 ngh\u0129a l\xE0 b\u1EA1n s\u1EBD kh\xF4ng th\u1EC3 th\u1EF1c hi\u1EC7n b\u1EA5t k\u1EF3 thay \u0111\u1ED5i n\xE0o cho k\u1EBF ho\u1EA1ch c\u1EE7a m\xECnh sau khi nh\u1EA5p v\xE0o g\u1EEDi.",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "T\xF4i hi\u1EC3u r\u1EB1ng t\xF4i s\u1EBD kh\xF4ng th\u1EC3 thay \u0111\u1ED5i l\u1EF1a ch\u1ECDn c\u1EE7a m\xECnh",
  FINALIZE_PLAN_SELECTIONS: "Ho\xE0n thi\u1EC7n c\xE1c l\u1EF1a ch\u1ECDn k\u1EBF ho\u1EA1ch",
  ERROR_FINALIZING_SELECTIONS: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi g\u1EEDi l\u1EF1a ch\u1ECDn c\u1EE7a b\u1EA1n.",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "C\xF3 v\u1EBB nh\u01B0 l\u1EF1a ch\u1ECDn c\u1EE7a b\u1EA1n ch\u01B0a \u0111\u1EA7y \u0111\u1EE7. Vui l\xF2ng ho\xE0n t\u1EA5t \u0111\u0103ng k\xFD v\xE0 th\u1EED l\u1EA1i.",
  SELECTIONS_FINALIZED_SUCCESS: "L\u1EF1a ch\u1ECDn \u0111\xE3 ho\xE0n t\u1EA5t!",
  TOTAL_REWARDS_LABEL: "T\xF3m t\u1EAFt ph\u1EA1m vi b\u1EA3o hi\u1EC3m",
  TOTAL_REWARDS_DESCRIPTION: "Xem l\u1EA1i c\xE1c l\u1EF1a ch\u1ECDn k\u1EBF ho\u1EA1ch m\xE0 b\u1EA1n \u0111\xE3 th\u1EF1c hi\u1EC7n trong qu\xE1 tr\xECnh \u0111\u0103ng k\xFD.",
  TOTAL_REWARDS_LINK: "Xem t\xF3m t\u1EAFt ph\u1EA1m vi b\u1EA3o hi\u1EC3m",
  CHANGE_PLAN_LABEL: "Thay \u0111\u1ED5i l\u1EF1a ch\u1ECDn ch\u01B0\u01A1ng tr\xECnh",
  CHANGE_PLAN_DESCRIPTION: "B\u1EA1n mu\u1ED1n thay \u0111\u1ED5i ch\u01B0\u01A1ng tr\xECnh ho\u1EB7c th\xF4ng tin c\xE1 nh\xE2n c\u1EE7a m\xECnh?",
  CHANGE_PLAN_LINK: "Thay \u0111\u1ED5i l\u1EF1a ch\u1ECDn ch\u01B0\u01A1ng tr\xECnh",
  DISMISS_TOAST: "Lo\u1EA1i b\u1ECF th\xF4ng b\xE1o",
  UPLOAD_DOCUMENT: "T\u1EA3i l\xEAn t\xE0i li\u1EC7u",
  REQUIRED_FORM: "B\u1EAFt bu\u1ED9c",
  PERCENTAGE_USED: "T\u1EF7 l\u1EC7 ph\u1EA7n tr\u0103m \u0111\xE3 s\u1EED d\u1EE5ng.",
  SUCCESS: "Th\xE0nh c\xF4ng!",
  ERROR: "L\u1ED7i",
  OVERVIEW: "T\u1ED5ng quan",
  THIS_FIELD_IS_REQUIRED: "Tr\u01B0\u1EDDng n\xE0y l\xE0 b\u1EAFt bu\u1ED9c",
  INVALID_ZIP_CODE_NO_FORMAT: "M\xE3 zip kh\xF4ng h\u1EE3p l\u1EC7",
  INVALID_DAY_ERROR_MESSAGE: "Ng\xE0y b\u1EA1n ch\u1ECDn kh\xF4ng h\u1EE3p l\u1EC7.",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "Ph\u1EA3i c\xF3 nhi\u1EC1u nh\u1EA5t {length} k\xFD t\u1EF1",
  COMPLETED_STATE_TITLE: "Ho\xE0n th\xE0nh",
  DRAG_OR_UPLOAD_PHOTO: "T\u1EA3i l\xEAn \u1EA3nh ch\u1EE5p t\xE0i li\u1EC7u n\xE0y",
  ADD_IMAGE: "Th\xEAm nhi\u1EC1u trang h\u01A1n v\xE0o t\xE0i li\u1EC7u n\xE0y (n\u1EBFu c\xF3)",
  IDENTIFICATION_TYPE_TWO_FILES: "T\xE0i li\u1EC7u nh\u1EADn d\u1EA1ng ch\u1EC9 c\xF3 th\u1EC3 c\xF3 t\u1ED1i \u0111a 2 h\xECnh \u1EA3nh h\u1ED7 tr\u1EE3.",
  WELCOME: "Ch\xE0o m\u1EEBng",
  GOT_IT: "Hi\u1EC3u r\u1ED3i!",
  SAVE: "L\u01B0u",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "Xem th\xEAm chi ti\u1EBFt",
  LAST_UPDATED: "C\u1EADp nh\u1EADt l\u1EA7n cu\u1ED1i",
  CLAIM_REF_NUMBER: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m #",
  VIEW_ALL: "Xem t\u1EA5t c\u1EA3",
  HRA: "HRA",
  SEARCH: "T\xECm ki\u1EBFm",
  DAY: "Ng\xE0y",
  MONTH: "Th\xE1ng",
  YEAR: "N\u0103m",
  BACK: "Quay l\u1EA1i",
  NEXT: "Ti\u1EBFp theo",
  PREVIOUS: "Tr\u01B0\u1EDBc",
  HELP_CENTER: "Trung t\xE2m tr\u1EE3 gi\xFAp",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "Thu nh\u1ECF cu\u1ED9c tr\xF2 chuy\u1EC7n",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "C\xF3 s\u1EB5n c\xE2u tr\u1EA3 l\u1EDDi \u0111\u1EC1 xu\u1EA5t",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "Ng\u01B0\u1EDDi tham gia tr\xF2 chuy\u1EC7n \u0111ang \u0111\xE1nh m\xE1y",
  VIEW_DOCUMENT: "Xem t\xE0i li\u1EC7u:",
  CATEGORIES: "Lo\u1EA1i",
  ACTIVE_PROGRAM: "{length, plural, other {Ch\u01B0\u01A1ng tr\xECnh ho\u1EA1t \u0111\u1ED9ng}}",
  ACTIVE_CHALLENGE: "{length, plural, other {Th\u1EED th\xE1ch ho\u1EA1t \u0111\u1ED9ng}}",
  PROGRAM_PROGRESS: "Ti\u1EBFn \u0111\u1ED9 ch\u01B0\u01A1ng tr\xECnh",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "B\u1EA1n c\u0169ng c\xF3 th\u1EC3 nh\u1EADp \u0111\u1EC3 t\u1EA1o t\xF9y ch\u1ECDn m\u1EDBi!",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "Ch\u1ECDn t\xF9y ch\u1ECDn c\u1EE7a b\u1EA1n",
  COVERAGE: "B\u1EA3o hi\u1EC3m",
  HEALTH_PROFILE: "H\u1ED3 s\u01A1 s\u1EE9c kh\u1ECFe",
  HEALTH_PROGRAM: "Ch\u01B0\u01A1ng tr\xECnh y t\u1EBF",
  HEALTH_PROGRAMS: "Ch\u01B0\u01A1ng tr\xECnh Y t\u1EBF",
  STEPS: "b\u01B0\u1EDBc",
  ACTIVE_DURATION: "ph\xFAt ho\u1EA1t \u0111\u1ED9ng",
  MINDFUL_DURATION: "ph\xFAt ch\xE1nh ni\u1EC7m",
  FLOORS_CLIMBED: "b\u1EADc thang \u0111\xE3 leo",
  DISTANCE: "qu\xE3ng \u0111\u01B0\u1EDDng \u0111\xE3 \u0111i",
  ENERGY_BURNED: "L\u01B0\u1EE3ng calo b\u1ECB \u0111\u1ED1t ch\xE1y",
  METRIC_DETAILS: "Chi ti\u1EBFt s\u1ED1 li\u1EC7u",
  HEALTH_PROGRAMS_SYNC_DEVICE: "\u0110\u1ED3ng b\u1ED9 h\xF3a {device} c\u1EE7a b\u1EA1n \u0111\u1EC3 c\xF3 tr\u1EA3i nghi\u1EC7m ch\u01B0\u01A1ng tr\xECnh t\u1ED1i \u01B0u.",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "Ch\u01B0\u01A1ng tr\xECnh s\u1EE9c kh\u1ECFe k\u1EBFt n\u1ED1i",
  TRACK_AUTOMATICALLY: "T\u1EF1 \u0111\u1ED9ng theo d\xF5i ti\u1EBFn tr\xECnh c\u1EE7a b\u1EA1n",
  SYNC_ISSUE: "V\u1EA4N \u0110\u1EC0 \u0110\u1ED2NG B\u1ED8",
  FIX_SYNC_ISSUE: "Kh\u1EAFc ph\u1EE5c s\u1EF1 c\u1ED1 n\xE0y",
  DATAPOINT_SYNC_ISSUE: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi \u0111\u1ED3ng b\u1ED9 {dataPoint} c\u1EE7a b\u1EA1n.",
  CONNECT_TO_TRACK: "K\u1EBFt n\u1ED1i c\xE1c \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB s\u1EE9c kh\u1ECFe y\xEAu th\xEDch c\u1EE7a b\u1EA1n \u0111\u1EC3 t\u1EF1 \u0111\u1ED9ng \u0111\xE1nh d\u1EA5u c\xE1c ho\u1EA1t \u0111\u1ED9ng l\xE0 \u0111\xE3 ho\xE0n th\xE0nh.",
  CONNECT_APPS_DEVICES: "K\u1EBFt n\u1ED1i \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB",
  ADD_APPS_DEVICES: "Xem \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB",
  MANAGE_UNITS: "Thay \u0111\u1ED5i \u0111\u01A1n v\u1ECB \u0111o l\u01B0\u1EDDng",
  DONT_CONNECT_APPS_DEVICES: "Kh\xF4ng t\u1EF1 \u0111\u1ED9ng theo d\xF5i ti\u1EBFn tr\xECnh c\u1EE7a t\xF4i",
  CONNECTING_UNAVAILABLE: "K\u1EBFt n\u1ED1i \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB kh\xF4ng kh\u1EA3 d\u1EE5ng tr\xEAn web",
  CONNECTING_UNAVAILABLE_DETAILS: "Hi\u1EC7n t\u1EA1i, vui l\xF2ng truy c\u1EADp \u1EE9ng d\u1EE5ng di \u0111\u1ED9ng League \u0111\u1EC3 k\u1EBFt n\u1ED1i c\xE1c \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB s\u1EE9c kh\u1ECFe y\xEAu th\xEDch c\u1EE7a b\u1EA1n.",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "Hi\u1EC7n t\u1EA1i, vui l\xF2ng truy c\u1EADp \u1EE9ng d\u1EE5ng di \u0111\u1ED9ng League \u0111\u1EC3 k\u1EBFt n\u1ED1i c\xE1c \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB s\u1EE9c kh\u1ECFe y\xEAu th\xEDch c\u1EE7a b\u1EA1n. N\u1EBFu b\u1EA1n mu\u1ED1n t\xECm hi\u1EC3u th\xEAm, b\u1EA1n c\xF3 th\u1EC3 t\xECm th\u1EA5y danh s\xE1ch \u0111\u1EA7y \u0111\u1EE7",
  CONNECT_DEVICES_LEARN_MORE: "T\xECm hi\u1EC3u th\xEAm",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "\u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB hi\u1EC7n \u0111ang \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3",
  DONT_HAVE_THE_APP: "B\u1EA1n kh\xF4ng c\xF3 \u1EE9ng d\u1EE5ng di \u0111\u1ED9ng League?",
  WHAT_DEVICES_CAN_CONNECT: "Nh\u1EEFng \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB n\xE0o \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3?",
  CONNECTED_DEVICES_IMAGE_ALT: "Ng\u01B0\u1EDDi \u0111ang xem x\xE9t d\u1EEF li\u1EC7u t\u1EEB thi\u1EBFt b\u1ECB \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i",
  MORE_GREAT_RESOURCES: "Nhi\u1EC1u ngu\u1ED3n t\xE0i nguy\xEAn tuy\u1EC7t v\u1EDDi h\u01A1n",
  HISTORY: "L\u1ECBch s\u1EED",
  TOP_MATCH: "Tr\u1EADn \u0111\u1EA5u \u0111\u1EC9nh cao",
  CHAT: "Tr\xF2 chuy\u1EC7n",
  END_CHAT: "K\u1EBFt th\xFAc tr\xF2 chuy\u1EC7n",
  BECOMES_ACTIVE_ON_DATE: "Tr\u1EDF n\xEAn ho\u1EA1t \u0111\u1ED9ng v\xE0o {date}",
  BECOMES_ACTIVE_IN_X_DAYS: "Quy\u1EC1n l\u1EE3i n\xE0y s\u1EBD c\xF3 hi\u1EC7u l\u1EF1c sau {days} ng\xE0y",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "B\u1EA1n ch\u1EC9 c\xF3 th\u1EC3 g\u1EEDi y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m v\u1EDBi ng\xE0y mua/d\u1ECBch v\u1EE5 t\u1EEB <strong>{dateRange}</strong> tr\u1EDF \u0111i cho quy\u1EC1n l\u1EE3i n\xE0y. Xem chi ti\u1EBFt b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n \u0111\u1EC3 bi\u1EBFt th\xEAm th\xF4ng tin.",
  SUBMIT_PAST_CLAIMS_BY_DATE: "G\u1EEDi y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m tr\u01B0\u1EDBc \u0111\xE2y b\u1EB1ng c\xE1ch {date}",
  THIS_POLICY_HAS_EXPIRED: "Th\u1EDDi h\u1EA1n b\u1EA3o hi\u1EC3m n\xE0y \u0111\xE3 h\u1EBFt h\u1EA1n, nh\u01B0ng b\u1EA1n v\u1EABn c\xF2n {days} ng\xE0y \u0111\u1EC3 g\u1EEDi y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m tr\u01B0\u1EDBc \u0111\xF3 cho quy\u1EC1n l\u1EE3i n\xE0y",
  THIS_POLICY_EXPIRES_TOMORROW: "Th\u1EDDi h\u1EA1n b\u1EA3o hi\u1EC3m n\xE0y \u0111\xE3 h\u1EBFt h\u1EA1n, nh\u01B0ng h\xF4m nay l\xE0 ng\xE0y cu\u1ED1i c\xF9ng \u0111\u1EC3 b\u1EA1n g\u1EEDi y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m tr\u01B0\u1EDBc \u0111\xE2y cho quy\u1EC1n l\u1EE3i n\xE0y",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "B\u1EA1n c\xF3 th\u1EDDi gian cho \u0111\u1EBFn <strong>{inactiveDate}</strong> \u0111\u1EC3 g\u1EEDi c\xE1c y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m tr\u01B0\u1EDBc \u0111\xE2y c\xF3 ng\xE0y mua/d\u1ECBch v\u1EE5 v\xE0o ho\u1EB7c tr\u01B0\u1EDBc <strong>{endDate}</strong>",
  EXPIRES_ON_DATE: "H\u1EBFt h\u1EA1n v\xE0o {date}",
  ASSIGNED: "\u0110\xE3 giao",
  ASSIGNED_ON: "v\xE0o {date}",
  ASSIGNED_TODAY: "h\xF4m nay",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "B\u1EA1n c\xF2n {days} ng\xE0y \u0111\u1EC3 s\u1EED d\u1EE5ng quy\u1EC1n l\u1EE3i n\xE0y tr\u01B0\u1EDBc khi th\u1EDDi h\u1EA1n b\u1EA3o hi\u1EC3m h\u1EBFt h\u1EA1n",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "Sau khi h\u1EBFt h\u1EA1n, b\u1EA1n s\u1EBD c\xF3 \u0111\u1EBFn <strong>{inactiveDate}</strong> \u0111\u1EC3 g\u1EEDi khi\u1EBFu n\u1EA1i c\xF3 ng\xE0y mua/d\u1ECBch v\u1EE5 v\xE0o ho\u1EB7c tr\u01B0\u1EDBc <strong>{endDate}</strong>",
  LOAD_MORE: "T\u1EA3i th\xEAm",
  SHOW_MORE: "Hi\u1EC3n th\u1ECB th\xEAm",
  LINK_OUT_MESSAGE: "Ph\u1EA3i xem",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "R\u1EA5t ti\u1EBFc, c\xF3 v\u1EBB \u0111\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1.",
  AGENT_PERMISSION_ERROR: "B\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n xem n\u1ED9i dung n\xE0y.",
  TRY_AGAIN: "Th\u1EED l\u1EA1i",
  WARNING_OPENS_NEW_TAB: "M\u1EDF trong m\u1ED9t tab m\u1EDBi",
  ANY_HEALTH_CHANGES: "G\u1EA7n \u0111\xE2y s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n c\xF3 thay \u0111\u1ED5i g\xEC kh\xF4ng?",
  LAST_UPDATED_DATE: "C\u1EADp nh\u1EADt l\u1EA7n cu\u1ED1i {date}",
  UPDATE_HEALTH_PROFILE: "C\u1EADp nh\u1EADt H\u1ED3 s\u01A1 s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n",
  HEALTH_PROFILE_COMPLETION_POINTS: "B\u1EA1n \u0111\xE3 ki\u1EBFm \u0111\u01B0\u1EE3c {points} \u0111i\u1EC3m",
  OPENS_IN_A_NEW_TAB: "M\u1EDF trong m\u1ED9t tab m\u1EDBi",
  COMPLETED: "Ho\xE0n th\xE0nh",
  COMPLETED_X_TIMES: "{amount, plural, other {\u0110\xE3 ho\xE0n th\xE0nh {amount} l\u1EA7n}}",
  REMOVE_FROM_JOURNEY: "X\xF3a kh\u1ECFi h\xE0nh tr\xECnh",
  GO_TO_JOURNEY: "\u0110i \u0111\u1EBFn H\xE0nh tr\xECnh",
  ABOUT_THIS_PROGRAM: "Gi\u1EDBi thi\u1EC7u ch\u01B0\u01A1ng tr\xECnh n\xE0y",
  HEALTH_JOURNEY: "H\xE0nh tr\xECnh s\u1EE9c kh\u1ECFe",
  REMOVE_ACTIVITY: "X\xF3a ho\u1EA1t \u0111\u1ED9ng?",
  REMOVE_ACTIVITY_PROMPT: "X\xF3a Ho\u1EA1t \u0111\u1ED9ng",
  REMOVE_ACTIVITY_BUTTON: "X\xF3a",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a ho\u1EA1t \u0111\u1ED9ng n\xE0y kh\u1ECFi h\xE0nh tr\xECnh c\u1EE7a m\xECnh kh\xF4ng? Kh\xF4ng th\u1EC3 ho\xE0n t\xE1c h\xE0nh \u0111\u1ED9ng n\xE0y.",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "B\u1EA1n s\u1EBD kh\xF4ng c\xF2n nh\xECn th\u1EA5y n\xF3 trong h\xE0nh tr\xECnh c\u1EE7a b\u1EA1n n\u1EEFa",
  DISCOVER_ACTIVITIES: "Kh\xE1m ph\xE1 c\xE1c ho\u1EA1t \u0111\u1ED9ng m\u1EDBi",
  DISCOVER_ACTIVITIES_CAPTION: "Duy\u1EC7t qua c\xE1c ho\u1EA1t \u0111\u1ED9ng m\xE0 ch\xFAng t\xF4i ngh\u0129 b\u1EA1n s\u1EBD th\xEDch",
  ACTIVITIES: "C\xE1c ho\u1EA1t \u0111\u1ED9ng",
  ACTIVITIES_PLURALIZATION: "{count, plural, other {C\xE1c ho\u1EA1t \u0111\u1ED9ng}}",
  ACTIVITIES_PILL: "C\xE1c ho\u1EA1t \u0111\u1ED9ng",
  PROGRAM_LIBRARY: "Th\u01B0 vi\u1EC7n ch\u01B0\u01A1ng tr\xECnh",
  BROWSE_PROGRAMS: "Duy\u1EC7t c\xE1c ch\u01B0\u01A1ng tr\xECnh",
  BACK_TO_JOURNEY: "Quay l\u1EA1i h\xE0nh tr\xECnh",
  GO_TO_HEALTH_JOURNEY: "\u0110i \u0111\u1EBFn h\xE0nh tr\xECnh ch\u0103m s\xF3c s\u1EE9c kh\u1ECFe",
  ADD_TO_JOURNEY: "Th\xEAm v\xE0o h\xE0nh tr\xECnh",
  REQUEST_WAIT: "Vui l\xF2ng \u0111\u1EE3i trong khi ch\xFAng t\xF4i ho\xE0n t\u1EA5t y\xEAu c\u1EA7u c\u1EE7a b\u1EA1n",
  JOURNEY_DELIGHT_1: "\u0110\u1ED9ng l\u1EF1c gi\xFAp b\u1EA1n b\u1EAFt \u0111\u1EA7u. Th\xF3i quen gi\xFAp b\u1EA1n ti\u1EBFp t\u1EE5c. B\u1EA1n \u0111\xE3 hi\u1EC3u r\u1ED3i \u0111\u1EA5y!",
  JOURNEY_DELIGHT_2: "T\xEDch c\u1EF1c T\u1EADp trung. \u0110i \u0111\xFAng l\xE0n \u0111\u01B0\u1EDDng c\u1EE7a m\xECnh. Ch\xFAng t\xF4i r\u1EA5t th\xEDch nh\xECn th\u1EA5y \u0111i\u1EC1u \u0111\xF3.",
  JOURNEY_DELIGHT_3: "Nh\u1EEFng b\u01B0\u1EDBc nh\u1ECF c\xF3 th\u1EC3 t\u1EA1o n\xEAn s\u1EF1 kh\xE1c bi\u1EC7t l\u1EDBn. B\u1EA1n \u0111ang th\u1EF1c hi\u1EC7n b\u01B0\u1EDBc \u0111\xF3!",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "{hasValue, select, true {\u0110\xE2y l\xE0 t\xF9y ch\u1ECDn v\u0103n b\u1EA3n t\u1EF1 do; s\u1EED d\u1EE5ng h\u1ED9p v\u0103n b\u1EA3n \u0111\u1EC3 nh\u1EADp gi\xE1 tr\u1ECB c\u1EE7a ri\xEAng b\u1EA1n. Gi\xE1 tr\u1ECB hi\u1EC7n t\u1EA1i l\xE0: {value}} other {\u0110\xE2y l\xE0 t\xF9y ch\u1ECDn v\u0103n b\u1EA3n t\u1EF1 do; s\u1EED d\u1EE5ng h\u1ED9p v\u0103n b\u1EA3n \u0111\u1EC3 nh\u1EADp gi\xE1 tr\u1ECB c\u1EE7a ri\xEAng b\u1EA1n. Gi\xE1 tr\u1ECB hi\u1EC7n t\u1EA1i l\xE0: r\u1ED7ng}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "T\xF9y ch\u1ECDn n\xE0y l\xE0 \u0111\u1ED9c quy\u1EC1n; ch\u1ECDn n\xF3 s\u1EBD b\u1ECF ch\u1ECDn t\u1EA5t c\u1EA3 c\xE1c t\xF9y ch\u1ECDn kh\xE1c",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "Ph\xE1t ho\u1EB7c t\u1EA1m d\u1EEBng ho\u1EA1t \u1EA3nh hi\u1EC7n \u0111ang \u0111\u01B0\u1EE3c ch\u1ECDn",
  CENTIMETERS: "Cm",
  FEET: "Feet",
  INCHES: "Inch",
  KILOGRAMS: "Kg",
  POUNDS: "Pound",
  EARN_UP_TO_X_POINTS: "Ki\u1EBFm t\u1EDBi {points} \u0111i\u1EC3m",
  EXPIRES_TODAY: "H\u1EBFt h\u1EA1n h\xF4m nay",
  PROGRAM_DETAILS: "Chi ti\u1EBFt ch\u01B0\u01A1ng tr\xECnh",
  PROGRESS: "Ti\u1EBFn tri\u1EC3n",
  X_OF_Y_COMPLETE: "{completed}/{total} ho\xE0n th\xE0nh",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "{missed} b\u1ECF l\u1EE1",
  MISSED: "B\u1ECF l\u1EE1",
  SCROLL_CAROUSEL_RIGHT: "Cu\u1ED9n v\xF2ng quay v\u1EC1 ph\xEDa tr\u01B0\u1EDBc",
  SCROLL_CAROUSEL_LEFT: "Cu\u1ED9n v\xF2ng quay v\u1EC1 ph\xEDa sau",
  SCROLL_CAROUSEL_FORWARD: "Cu\u1ED9n v\xF2ng quay v\u1EC1 ph\xEDa tr\u01B0\u1EDBc",
  SCROLL_CAROUSEL_BACK: "Cu\u1ED9n v\xF2ng quay v\u1EC1 ph\xEDa sau",
  YOU_HAVENT_STARTED_PROGRAMS: "B\u1EA1n ch\u01B0a b\u1EAFt \u0111\u1EA7u b\u1EA5t k\u1EF3 ch\u01B0\u01A1ng tr\xECnh s\u1EE9c kh\u1ECFe n\xE0o",
  ADD_PROGRAM: "Th\xEAm ch\u01B0\u01A1ng tr\xECnh",
  TAP_ADD_PROGRAM_BUTTON: 'Nh\u1EA5n v\xE0o n\xFAt "Th\xEAm ch\u01B0\u01A1ng tr\xECnh" \u0111\u1EC3 duy\u1EC7t c\xE1c ch\u01B0\u01A1ng tr\xECnh.',
  YOUVE_REACHED_PROGRAM_LIMIT: "B\u1EA1n \u0111\xE3 \u0111\u1EA1t \u0111\u1EBFn gi\u1EDBi h\u1EA1n ch\u01B0\u01A1ng tr\xECnh c\u1EE7a m\xECnh!",
  START_X_MORE_PROGRAMS: "{duration, plural, other {B\u1EAFt \u0111\u1EA7u th\xEAm {duration,number} ch\u01B0\u01A1ng tr\xECnh n\u1EEFa. Gi\u1EEF v\u1EEFng \u0111\xE0 ph\xE1t tri\u1EC3n.}}",
  OK: "OK",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "Ho\xE0n th\xE0nh m\u1ED9t trong c\xE1c ch\u01B0\u01A1ng tr\xECnh \u0111ang ho\u1EA1t \u0111\u1ED9ng c\u1EE7a b\u1EA1n \u0111\u1EC3 th\xEAm \u0111i\u1EC1u n\xE0y v\xE0o h\xE0nh tr\xECnh c\u1EE7a b\u1EA1n.",
  SHOW_LESS: "Hi\u1EC3n th\u1ECB \xEDt h\u01A1n",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "C\xF3 v\u1EBB nh\u01B0 b\u1EA1n \u0111\xE3 \u0111\u1EA1t \u0111\u1EBFn gi\u1EDBi h\u1EA1n ch\u01B0\u01A1ng tr\xECnh!",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "Ho\xE0n th\xE0nh m\u1ED9t ch\u01B0\u01A1ng tr\xECnh \u0111ang ho\u1EA1t \u0111\u1ED9ng tr\u01B0\u1EDBc khi th\xEAm m\u1ED9t ch\u01B0\u01A1ng tr\xECnh m\u1EDBi. <strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "X\xF3a ch\u01B0\u01A1ng tr\xECnh?",
  REMOVE_PROGRAM_BUTTON: "X\xF3a",
  ARE_YOU_SURE_REMOVE_PROGRAM: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a ch\u01B0\u01A1ng tr\xECnh n\xE0y kh\u1ECFi h\xE0nh tr\xECnh c\u1EE7a m\xECnh kh\xF4ng? M\u1ECDi ti\u1EBFn tr\xECnh trong ch\u01B0\u01A1ng tr\xECnh n\xE0y s\u1EBD b\u1ECB thi\u1EBFt l\u1EADp l\u1EA1i v\xE0 b\u1EA1n s\u1EBD ph\u1EA3i b\u1EAFt \u0111\u1EA7u l\u1EA1i t\u1EEB \u0111\u1EA7u.",
  PROGRAM_REMOVED: "Ch\u01B0\u01A1ng tr\xECnh b\u1ECB x\xF3a",
  PROGRAM_ADDED: "Ch\u01B0\u01A1ng tr\xECnh \u0111\u01B0\u1EE3c th\xEAm",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "Ch\u01B0\u01A1ng tr\xECnh b\xE2y gi\u1EDD s\u1EBD xu\u1EA5t hi\u1EC7n trong h\xE0nh tr\xECnh c\u1EE7a b\u1EA1n",
  PROGRAM_ADD_ERROR: "Ch\xFAng t\xF4i kh\xF4ng th\u1EC3 th\xEAm ch\u01B0\u01A1ng tr\xECnh v\xE0o th\u1EDDi \u0111i\u1EC3m n\xE0y. Vui l\xF2ng th\u1EED l\u1EA1i ho\u1EB7c ch\u1ECDn ch\u01B0\u01A1ng tr\xECnh kh\xE1c.",
  PROGRAM_REMOVE_ERROR: "Ch\xFAng t\xF4i kh\xF4ng th\u1EC3 x\xF3a ch\u01B0\u01A1ng tr\xECnh t\u1EA1i th\u1EDDi \u0111i\u1EC3m n\xE0y. Vui l\xF2ng th\u1EED l\u1EA1i.",
  SKIP_TO_CONTENT_MESSAGE: "Chuy\u1EC3n \u0111\u1EBFn n\u1ED9i dung",
  MAIN_NAVIGATION_LIST_LABEL: "C\xE1c m\u1EE5c \u0111i\u1EC1u h\u01B0\u1EDBng League",
  PAGE_TITLE_ACTIVITY_DETAILS: "Chi ti\u1EBFt ho\u1EA1t \u0111\u1ED9ng",
  X_OF_Y: "{current} c\u1EE7a {total}",
  ACTIVITY_COUNT_EDIT: "Ch\u1EC9nh s\u1EEDa {unit}",
  ACTIVITY_COUNT_GOAL: "M\u1EE5c ti\xEAu: {goal,number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: '\u0110\u1EC3 ho\xE0n t\u1EA5t ho\u1EA1t \u0111\u1ED9ng n\xE0y, h\xE3y nh\u1EA5p v\xE0o "\u0110\xE1nh d\u1EA5u l\xE0 xong".',
  ACTIVITY_COMPLETE_LATER_PROMPT: '\u0110\u1EC3 ti\u1EBFp t\u1EE5c, h\xE3y nh\u1EA5p v\xE0o "Quay l\u1EA1i h\xE0nh tr\xECnh" v\xE0 quay l\u1EA1i sau \u0111\u1EC3 \u0111\xE1nh d\u1EA5u l\xE0 ho\xE0n t\u1EA5t.',
  ACTIVITY_COMPLETE_ERROR: "Ch\xFAng t\xF4i kh\xF4ng th\u1EC3 ho\xE0n th\xE0nh ho\u1EA1t \u0111\u1ED9ng n\xE0y v\xE0o th\u1EDDi \u0111i\u1EC3m n\xE0y. Vui l\xF2ng th\u1EED l\u1EA1i.",
  ACTIVITY_DISMISS_ERROR: "Ch\xFAng t\xF4i kh\xF4ng th\u1EC3 x\xF3a ho\u1EA1t \u0111\u1ED9ng v\xE0o th\u1EDDi \u0111i\u1EC3m n\xE0y. Vui l\xF2ng th\u1EED l\u1EA1i.",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "Vui l\xF2ng ch\u1ECDn ng\xE0y, th\xE1ng v\xE0 n\u0103m",
  TOOLBOX_VALIDATION_MONTH_YEAR: "Vui l\xF2ng ch\u1ECDn th\xE1ng v\xE0 n\u0103m",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "Gi\u1EDBi h\u1EA1n k\xFD t\u1EF1 l\xE0 {limit}",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "Nh\u1EADp m\u1ED9t s\u1ED1 b\u1EB1ng ho\u1EB7c l\u1EDBn h\u01A1n {value}",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "Nh\u1EADp m\u1ED9t s\u1ED1 b\u1EB1ng ho\u1EB7c nh\u1ECF h\u01A1n {value}",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "\u0110\xE2y l\xE0 tr\u01B0\u1EDDng b\u1EAFt bu\u1ED9c",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "Vui l\xF2ng l\u1EF1a ch\u1ECDn",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "Vui l\xF2ng l\u1EF1a ch\u1ECDn",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "Ch\u1ECDn ng\xE0y sau {date,date,medium}",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "Ch\u1ECDn ng\xE0y tr\u01B0\u1EDBc {date,date,medium}",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "Nh\u1EADp s\u1ED1 nguy\xEAn",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "Ch\u1ECDn t\u1ED1i \u0111a {integer} t\xF9y ch\u1ECDn",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "Ch\u1ECDn {integer} t\xF9y ch\u1ECDn \u0111\u1EC3 ti\u1EBFp t\u1EE5c",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "Tr\u01B0\u1EDDng kh\xF4ng \u0111\u01B0\u1EE3c \u0111\u1EC3 tr\u1ED1ng khi \u0111\u01B0\u1EE3c ch\u1ECDn",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "C\xE2n n\u1EB7ng ph\u1EA3i \xEDt nh\u1EA5t l\xE0 {imperial}lb ({metric}kg)",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "C\xE2n n\u1EB7ng ph\u1EA3i nhi\u1EC1u nh\u1EA5t l\xE0 {imperial}lb ({metric}kg)",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "Chi\u1EC1u cao ph\u1EA3i \xEDt nh\u1EA5t l\xE0 {imperial} ({metric} cm)",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "Chi\u1EC1u cao ph\u1EA3i nhi\u1EC1u nh\u1EA5t l\xE0 {imperial} ({metric} cm)",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "Nh\u1EADp c\xE2n n\u1EB7ng c\u1EE7a b\u1EA1n t\xEDnh b\u1EB1ng kil\xF4gam",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "Nh\u1EADp c\xE2n n\u1EB7ng c\u1EE7a b\u1EA1n t\xEDnh b\u1EB1ng pound",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "Nh\u1EADp chi\u1EC1u cao c\u1EE7a b\u1EA1n t\xEDnh b\u1EB1ng cm",
  ENTER_YOUR_HEIGHT_IN_FEET: "Nh\u1EADp chi\u1EC1u cao c\u1EE7a b\u1EA1n t\xEDnh b\u1EB1ng feet",
  ENTER_YOUR_HEIGHT_IN_INCHES: "Nh\u1EADp chi\u1EC1u cao c\u1EE7a b\u1EA1n t\xEDnh b\u1EB1ng inch",
  GOAL_REACHED: "B\u1EA1n \u0111\xE3 \u0111\u1EA1t \u0111\u01B0\u1EE3c m\u1EE5c ti\xEAu!",
  COMPLETE_ACTIVITY: "Ho\xE0n th\xE0nh ho\u1EA1t \u0111\u1ED9ng",
  EXIT_ACTIVITY_TITLE: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n r\u1EDDi kh\u1ECFi ho\u1EA1t \u0111\u1ED9ng n\xE0y kh\xF4ng?",
  EXIT_ACTIVITY_BODY: "Ti\u1EBFn tr\xECnh c\u1EE7a b\u1EA1n s\u1EBD kh\xF4ng \u0111\u01B0\u1EE3c l\u01B0u v\xE0 b\u1EA1n s\u1EBD ph\u1EA3i kh\u1EDFi \u0111\u1ED9ng l\u1EA1i ho\u1EA1t \u0111\u1ED9ng t\u1EEB \u0111\u1EA7u.",
  EXIT_ACTIVITY_CTA: "R\u1EDDi kh\u1ECFi ho\u1EA1t \u0111\u1ED9ng",
  ACTIVITY_NEXT_STEP: "Ti\u1EBFp t\u1EE5c ho\u1EA1t \u0111\u1ED9ng",
  ACTIVITY_PREV_STEP: "Quay l\u1EA1i b\u01B0\u1EDBc tr\u01B0\u1EDBc",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "Danh m\u1EE5c ch\u01B0\u01A1ng tr\xECnh s\u1EE9c kh\u1ECFe",
  NEXT_STEP: "B\u01B0\u1EDBc ti\u1EBFp theo",
  YOU_EARNED: "B\u1EA1n \u0111\xE3 ki\u1EBFm \u0111\u01B0\u1EE3c",
  YOU_EARNED_X_POINTS: "B\u1EA1n \u0111\xE3 ki\u1EBFm \u0111\u01B0\u1EE3c {points} \u0111i\u1EC3m!",
  CREATE: "T\u1EA1o {label}",
  TOMORROW: "Ng\xE0y mai",
  YESTERDAY: "H\xF4m qua",
  BACK_TO_TODAY: "Quay l\u1EA1i h\xF4m nay",
  PREVIOUS_DAY: "Ng\xE0y h\xF4m tr\u01B0\u1EDBc",
  NEXT_DAY: "Ng\xE0y h\xF4m sau",
  ACTIVITY_STATUS: "T\xECnh tr\u1EA1ng ho\u1EA1t \u0111\u1ED9ng: ",
  ACTIVITY_STATUS_EXPIRED: "H\u1EBFt h\u1EA1n",
  ACTIVITY_STATUS_REMOVED: "B\u1ECF qua",
  ACTIVITY_STATUS_UPCOMING: "S\u1EAFp c\xF3",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "Ch\u01B0a c\xF3 k\u1EBF ho\u1EA1ch!",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: 'Nh\u1EA5n v\xE0o n\xFAt "Th\xEAm" \u0111\u1EC3 t\xECm ch\u01B0\u01A1ng tr\xECnh m\u1EDBi ho\u1EB7c duy\u1EC7t danh s\xE1ch ho\u1EA1t \u0111\u1ED9ng \u0111\u01B0\u1EE3c tuy\u1EC3n ch\u1ECDn b\xEAn d\u01B0\u1EDBi.',
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "L\xE0m t\u1ED1t l\u1EAFm, b\u1EA1n \u0111ang ti\u1EBFn tri\u1EC3n t\u1ED1t!",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "H\xE3y quay l\u1EA1i v\xE0o ng\xE0y mai \u0111\u1EC3 bi\u1EBFt th\xEAm ho\u1EB7c duy\u1EC7t qua c\xE1c ch\u01B0\u01A1ng tr\xECnh m\u1EDBi v\xE0 c\xE1c ho\u1EA1t \u0111\u1ED9ng \u0111\u01B0\u1EE3c \u0111\u1EC1 xu\u1EA5t.",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "B\u1EA1n kh\xF4ng c\xF3 ho\u1EA1t \u0111\u1ED9ng n\xE0o \u0111\u01B0\u1EE3c l\xEAn l\u1ECBch cho ng\xE0y n\xE0y.",
  NO_ACTIVITIES_PAST_HEADING: "Ng\xE0y ngh\u1EC9",
  NO_ACTIVITIES_PAST_DESCRIPTION: "C\xF3 v\u1EBB nh\u01B0 b\u1EA1n \u0111\xE3 d\xE0nh ng\xE0y n\xE0y \u0111\u1EC3 th\u01B0 gi\xE3n v\xE0 h\u1ED3i ph\u1EE5c.",
  BUTTON_BROWSE_PROGRAMS: "Duy\u1EC7t c\xE1c ch\u01B0\u01A1ng tr\xECnh",
  IMG_ALT_HIGH_FIVE_HANDS: "Hai b\xE0n tay \u0111\u1EADp m\u1EA1nh v\xE0o nhau",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "Ng\u01B0\u1EDDi ph\u1EE5 n\u1EEF \u0111\u1EE9ng v\xE0 cu\u1ED9n m\u1ED9t danh s\xE1ch ch\u01B0\u01A1ng tr\xECnh d\xE0i b\u1EB1ng b\u1EE9c t\u01B0\u1EDDng",
  IMG_ALT_EMPTY_STATE_REST: "Ng\u01B0\u1EDDi \u0111\xE0n \xF4ng ngh\u1EC9 ng\u01A1i tr\xEAn m\u1ED9t chi\u1EBFc gh\u1EBF tho\u1EA3i m\xE1i v\u1EDBi m\u1ED9t chi\u1EBFc c\u1ED1c \u1EA5m \xE1p v\xE0 tai nghe",
  THIS_IS_JUST_A_SNEAK_PEAK: "\u0110\xE2y ch\u1EC9 l\xE0 m\u1ED9t c\xE1i nh\xECn tho\xE1ng qua!",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "Quay l\u1EA1i v\xE0 ho\xE0n th\xE0nh ho\u1EA1t \u0111\u1ED9ng n\xE0y v\xE0o {date}.",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "H\xE3y quay l\u1EA1i sau \u0111\u1EC3 ho\xE0n th\xE0nh ho\u1EA1t \u0111\u1ED9ng n\xE0y.",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "C\xF3 v\u1EBB nh\u01B0 b\u1EA1n \u0111\xE3 b\u1ECF l\u1EE1 \u0111i\u1EC1u n\xE0y!",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "Ho\u1EA1t \u0111\u1ED9ng n\xE0y \u0111\xE3 h\u1EBFt h\u1EA1n v\xE0o {date}.",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "C\xF3 v\u1EBB nh\u01B0 c\xE1i n\xE0y kh\xF4ng d\xE0nh cho b\u1EA1n",
  YOU_SKIPPED_THIS_ACTIVITY: "B\u1EA1n \u0111\xE3 b\u1ECF qua ho\u1EA1t \u0111\u1ED9ng n\xE0y.",
  NO_PROGRAMS: "Ch\u01B0a c\xF3 g\xEC \u0111\u1EC3 xem \u1EDF \u0111\xE2y",
  NO_ACTIVITY_TITLE: "Kh\xF4ng c\xF3 g\xEC \u1EDF \u0111\xE2y (ch\u01B0a!)",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "Ch\xFAng t\xF4i s\u1EBD hi\u1EC3n th\u1ECB th\u1EE9 h\u1EA1ng c\u1EE7a b\u1EA1n t\u1EA1i \u0111\xE2y khi th\u1EED th\xE1ch b\u1EAFt \u0111\u1EA7u.",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "H\xE3y quay l\u1EA1i \u0111\xE2y \u0111\u1EC3 xem nh\u1EEFng \u0111i\u1EC3m n\u1ED5i b\u1EADt c\u1EE7a th\u1EED th\xE1ch s\u1EDBm nh\u1EA5t.",
  LEADERBOARD_ALL_TIME_RANKING: "X\u1EBFp h\u1EA1ng m\u1ECDi th\u1EDDi \u0111\u1EA1i",
  LEADERBOARD_CURRENT_USER_NAME: "{user} (B\u1EA1n)",
  ACTIVITY_PROGRESS: "Ti\u1EBFn \u0111\u1ED9 ho\u1EA1t \u0111\u1ED9ng",
  YOUR_JOURNEY: "H\xE0nh tr\xECnh c\u1EE7a b\u1EA1n",
  SUGGESTED_FOR_YOU: "\u0110\u1EC1 xu\u1EA5t cho b\u1EA1n",
  NO_ACHIEVEMENTS_DESCRIPTION: "Ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng trong H\xE0nh tr\xECnh c\u1EE7a b\u1EA1n \u0111\u1EC3 b\u1EAFt \u0111\u1EA7u m\u1EDF kh\xF3a th\xE0nh t\xEDch.",
  UNLOCK_ACHIEVEMENTS: "M\u1EDF kh\xF3a th\xE0nh t\xEDch khi ho\xE0n th\xE0nh t\u1EA5t c\u1EA3 c\xE1c ho\u1EA1t \u0111\u1ED9ng trong ch\u01B0\u01A1ng tr\xECnh n\xE0y.",
  MEMBERS: "Th\xE0nh vi\xEAn",
  SIGN_UP_PERIOD_PASSED_DETAIL: "B\u1EA1n kh\xF4ng th\u1EC3 \u0111\u0103ng k\xFD th\u1EED th\xE1ch n\xE0y n\u1EEFa v\xEC th\u1EDDi gian \u0111\u0103ng k\xFD \u0111\xE3 qua.",
  REFERENCES: "T\xE0i li\u1EC7u tham kh\u1EA3o",
  SIGN_UP_BY: "\u0110\u0103ng k\xFD tr\u01B0\u1EDBc {date}",
  YOURE_GOOD_TO_GO: "B\u1EA1n \u0111\xE3 s\u1EB5n s\xE0ng r\u1ED3i!",
  CHECK_CHALLENGE_DETAILS: "Ki\u1EC3m tra th\xF4ng tin chi ti\u1EBFt v\u1EC1 th\u1EED th\xE1ch \u0111\u1EC3 l\xE0m quen v\u1EDBi c\xE1c ho\u1EA1t \u0111\u1ED9ng",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "B\u1EA1n \u0111\xE3 s\u1EB5n s\xE0ng \u0111\u1EC3 b\u1EAFt \u0111\u1EA7u th\u1EED th\xE1ch. B\u1EA1n c\xF3 mu\u1ED1n tham gia ho\u1EB7c t\u1EA1o m\u1ED9t \u0111\u1ED9i kh\xF4ng?",
  CHALLENGE_ABOUT_TO_START: "Th\u1EED th\xE1ch c\u1EE7a b\u1EA1n s\u1EAFp b\u1EAFt \u0111\u1EA7u!",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "C\xE1c ho\u1EA1t \u0111\u1ED9ng h\xE0ng ng\xE0y s\u1EBD xu\u1EA5t hi\u1EC7n t\u1EA1i \u0111\xE2y khi th\u1EED th\xE1ch c\u1EE7a b\u1EA1n b\u1EAFt \u0111\u1EA7u v\xE0o <strong>{date}</strong>.",
  NO_CHALLENGE_ACTIVITIES_TODAY: "H\xF4m nay kh\xF4ng c\xF3 ho\u1EA1t \u0111\u1ED9ng th\u1EED th\xE1ch n\xE0o d\xE0nh cho b\u1EA1n.",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: 'Kh\xF4ng t\xECm th\u1EA5y "{query}".',
  COVERAGE_SEARCH_TRY_AGAIN: "H\xE3y th\u1EED t\xECm ki\u1EBFm l\u1EA1i b\u1EB1ng m\u1ED9t t\u1EEB kh\xF3a kh\xE1c.",
  EARN: "T\xEDch l\u0169y",
  WORD_WITH_EXCLAMATION_POINT: "{word}!",
  UPDATED: "\u0110\xE3 c\u1EADp nh\u1EADt",
  YOUR_PROGRESS: "Ti\u1EBFn \u0111\u1ED9 c\u1EE7a b\u1EA1n",
  YOUR_PROGRESS_IN_NUMBERS: "{current} ho\xE0n th\xE0nh {total} {unit} ho\u1EB7c {percentage} ph\u1EA7n tr\u0103m",
  OUTCOMES_FRICTION_SCREEN_TITLE: "C\xE1 nh\xE2n h\xF3a c\xE1c \u0111\u1EC1 xu\u1EA5t...",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "H\xE3y cho ch\xFAng t\xF4i m\u1ED9t ch\xFAt th\u1EDDi gian \u0111\u1EC3 t\xECm m\u1ED9t s\u1ED1 t\xE0i nguy\xEAn h\u1EEFu \xEDch cho b\u1EA1n d\u1EF1a tr\xEAn c\xE2u tr\u1EA3 l\u1EDDi c\u1EE7a b\u1EA1n",
  RELOAD_MY_SUGGESTIONS: "T\u1EA3i l\u1EA1i \u0111\u1EC1 xu\u1EA5t c\u1EE7a t\xF4i",
  YOUR_RANKING: "Th\u1EE9 h\u1EA1ng c\u1EE7a b\u1EA1n",
  TOP_RANKED: "X\u1EBFp h\u1EA1ng h\xE0ng \u0111\u1EA7u",
  VIEW_CHALLENGE_DETAILS: "Xem chi ti\u1EBFt th\u1EED th\xE1ch",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "B\u1EA1n c\xF3 th\u1EAFc m\u1EAFc t\u1EA1i sao vi\u1EC7c th\xEAm ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c l\u1EA1i c\xF3 l\u1EE3i kh\xF4ng?",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "Ng\u01B0\u1EDDi ph\u1EE5 thu\u1ED9c c\u1EE7a b\u1EA1n c\xF3 th\u1EC3 truy c\u1EADp League v\xE0 t\u1EADn h\u01B0\u1EDFng m\u1ED9t cu\u1ED9c s\u1ED1ng l\xE0nh m\u1EA1nh h\u01A1n!",
  UNABLE_TO_LEAVE_CHALLENGE: "Kh\xF4ng th\u1EC3 r\u1EDDi kh\u1ECFi th\u1EED th\xE1ch, vui l\xF2ng th\u1EED l\u1EA1i!",
  SUCCESSFULLY_LEFT_CHALLENGE: "B\u1EA1n \u0111\xE3 th\xE0nh c\xF4ng r\u1EDDi kh\u1ECFi th\u1EED th\xE1ch.",
  UNABLE_TO_JOIN_CHALLENGE: "Kh\xF4ng th\u1EC3 tham gia th\u1EED th\xE1ch, vui l\xF2ng th\u1EED l\u1EA1i!",
  JOIN_TEAM: "Tham gia nh\xF3m",
  X_TEAM_MEMBERS: "{memberTotal, plural, other {{memberTotal,number} th\xE0nh vi\xEAn trong nh\xF3m}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, other {{spotsAvailable,number} v\u1ECB tr\xED c\xF3 s\u1EB5n}}",
  HIGHLIGHTS: "N\u1ED5i b\u1EADt",
  CURRENT_RANK: "X\u1EBFp h\u1EA1ng hi\u1EC7n t\u1EA1i",
  X_LIKES: "{activityLikes, plural, other {{activityLikes,number} l\u01B0\u1EE3t th\xEDch}}",
  LIKE: "Th\xEDch",
  LIKED: "\u0110\xE3 th\xEDch",
  TEAM_INVITE_HEADING: "Ch\xFAng ta c\xF9ng nhau t\u1ED1t h\u01A1n!",
  TEAM_INVITE_DESCRIPTION: "M\u1EDDi b\u1EA1n b\xE8 tham gia nh\xF3m c\u1EE7a b\u1EA1n \u0111\u1EC3 c\xF9ng nhau ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng.",
  TEAM_INVITE_LINK_DESCRIPTION: "C\xE0ng \u0111\xF4ng c\xE0ng vui! Chia s\u1EBB li\xEAn k\u1EBFt n\xE0y v\u1EDBi b\u1EA1n b\xE8 \u0111\u1EC3 h\u1ECD c\xF3 th\u1EC3 tham gia nh\xF3m c\u1EE7a b\u1EA1n.",
  TEAM_INVITE_LINK: "Li\xEAn k\u1EBFt m\u1EDDi nh\xF3m",
  INVITE: "L\u1EDDi m\u1EDDi",
  LEAVE_TEAM: "R\u1EDDi kh\u1ECFi nh\xF3m",
  INVITE_FRIENDS: "M\u1EDDi b\u1EA1n b\xE8",
  PRIVACY: "C\xE0i \u0111\u1EB7t quy\u1EC1n ri\xEAng t\u01B0",
  JOIN_TEAM_SUCCESS_TITLE: "Ch\xE0o m\u1EEBng b\u1EA1n \u0111\u1EBFn v\u1EDBi nh\xF3m!",
  JOIN_TEAM_SUCCESS_DETAIL: "H\xE3y c\xF9ng nhau chinh ph\u1EE5c nh\u1EEFng ho\u1EA1t \u0111\u1ED9ng n\xE0y nh\xE9.",
  JOIN_TEAM_SUCCESS_BUTTON: "\u0110i \u0111\u1EBFn Chi ti\u1EBFt nh\xF3m",
  CANT_JOIN_TEAM_TITLE: "Kh\xF4ng th\u1EC3 tham gia {teamName}",
  CANT_JOIN_TEAM_DETAIL: "Vui l\xF2ng th\u1EED l\u1EA1i! N\u1EBFu b\u1EA1n \u0111\xE3 tham gia m\u1ED9t nh\xF3m, b\u1EA1n s\u1EBD c\u1EA7n r\u1EDDi kh\u1ECFi nh\xF3m hi\u1EC7n t\u1EA1i tr\u01B0\u1EDBc khi c\xF3 th\u1EC3 tham gia {teamName}.",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "C\xF3 v\u1EBB nh\u01B0 b\u1EA1n ch\u01B0a \u0111\u0103ng k\xFD th\u1EED th\xE1ch. B\u1EA1n c\u1EA7n ph\u1EA3i \u0111\u0103ng k\xFD tr\u01B0\u1EDBc khi c\xF3 th\u1EC3 tham gia {teamName}.",
  LEAVE_TEAM_QUESTION: "R\u1EDDi kh\u1ECFi {teamName}?",
  LEAVE_TEAM_DESCRIPTION: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n r\u1EDDi kh\u1ECFi {teamName} kh\xF4ng? \u0110i\u1EC3m c\u1EE7a b\u1EA1n s\u1EBD kh\xF4ng c\xF2n \u0111\u01B0\u1EE3c t\xEDnh v\xE0o \u0111i\u1EC3m trung b\xECnh c\u1EE7a \u0111\u1ED9i n\u1EEFa.",
  CHALLENGE_ACTIVITY_DETAIL: "Cho ph\xE9p ng\u01B0\u1EDDi kh\xE1c th\u1EA5y ho\u1EA1t \u0111\u1ED9ng c\u1EE7a b\u1EA1n trong th\u1EED th\xE1ch n\xE0y.",
  SHOW_MY_ACTIVITY: "Hi\u1EC3n th\u1ECB ho\u1EA1t \u0111\u1ED9ng c\u1EE7a t\xF4i",
  YOUR_TEAM: "Nh\xF3m c\u1EE7a b\u1EA1n",
  JOIN_OR_CREATE_TEAM: "Tham gia ho\u1EB7c t\u1EA1o nh\xF3m",
  TEAMS: "Nh\xF3m",
  CHALLENGE_TEAMS_DESCRIPTION: "Tham gia m\u1ED9t nh\xF3m hi\u1EC7n c\xF3 ho\u1EB7c t\u1EA1o nh\xF3m c\u1EE7a ri\xEAng b\u1EA1n \u0111\u1EC3 c\xF9ng nhau c\u1EA1nh tranh trong th\u1EED th\xE1ch!",
  JOIN_A_TEAM: "Tham gia nh\xF3m",
  JOIN_TEAM_DESCRIPTION: "T\xECm ki\u1EBFm m\u1ED9t nh\xF3m hi\u1EC7n c\xF3 \u0111\u1EC3 tham gia v\xE0 c\u1EA1nh tranh c\xF9ng nhau!",
  SEARCH_FOR_TEAM: "T\xECm ki\u1EBFm m\u1ED9t nh\xF3m",
  CREATE_A_TEAM: "T\u1EA1o nh\xF3m",
  CREATE_TEAM_DESCRIPTION: "T\xF9y ch\u1EC9nh nh\xF3m c\u1EE7a b\u1EA1n b\u1EB1ng c\xE1ch \u0111\u1EB7t h\xECnh \u0111\u1EA1i di\u1EC7n v\xE0 t\xEAn nh\xF3m. M\u1EDDi nh\u1EEFng ng\u01B0\u1EDDi kh\xE1c tham gia c\xF9ng b\u1EA1n!",
  UP_YOUR_GAME: "N\xE2ng c\u1EA5p tr\xF2 ch\u01A1i c\u1EE7a b\u1EA1n",
  UP_YOUR_GAME_DESCRIPTION: "C\xF3 s\u1EE9c m\u1EA1nh v\u1EC1 s\u1ED1 l\u01B0\u1EE3ng. Tham gia m\u1ED9t nh\xF3m ho\u1EB7c t\u1EF1 t\u1EA1o nh\xF3m c\u1EE7a ri\xEAng b\u1EA1n.",
  XTH_PLACE: "{currentRank, selectordinal, m\u1ED9t {#st} hai {#nd} \xEDt {#rd} kh\xE1c {#th} } V\u1ECB tr\xED",
  LEADERBOARD_RANKING: "X\u1EBFp h\u1EA1ng",
  LEADERBOARD_USERNAME: "T\xEAn \u0111\u0103ng nh\u1EADp",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "\u0110\xE3 sao ch\xE9p li\xEAn k\u1EBFt m\u1EDDi v\xE0o b\u1EA3ng t\u1EA1m!",
  DATE_AND_TIME: "Ng\xE0y v\xE0 gi\u1EDD",
  SELECT_TEAM_AVATAR: "Ch\u1ECDn h\xECnh \u0111\u1EA1i di\u1EC7n c\u1EE7a nh\xF3m",
  CHANGE_TEAM_AVATAR: "Thay \u0111\u1ED5i h\xECnh \u0111\u1EA1i di\u1EC7n c\u1EE7a nh\xF3m",
  TEAM_NAME_LABEL: "T\xEAn nh\xF3m",
  CREATE_TEAM: "T\u1EA1o nh\xF3m",
  TEAM_NAME_ERROR: "Vui l\xF2ng nh\u1EADp t\xEAn nh\xF3m c\u1EE7a b\u1EA1n.",
  SLIDE_OF_TOTAL: "Trang tr\xECnh b\xE0y {key} c\u1EE7a {total}",
  PAGE_OF_TOTAL: "Trang {current} c\u1EE7a {total}",
  NO_SOLUTIONS_AVAILABLE: "Ch\xFAng t\xF4i r\u1EA5t ti\u1EBFc, hi\u1EC7n t\u1EA1i kh\xF4ng c\xF3 gi\u1EA3i ph\xE1p n\xE0o c\xF3 s\u1EB5n.",
  SEE_ALL_ACHIEVEMENTS: "Xem t\u1EA5t c\u1EA3 th\xE0nh t\xEDch",
  VIEW_ALL_ACHIEVEMENTS: "Xem t\u1EA5t c\u1EA3 th\xE0nh t\xEDch",
  TAKE_ME_BACK: "\u0110\u01B0a t\xF4i tr\u1EDF l\u1EA1i",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "H\xECnh \u0111\u1EA1i di\u1EC7n n\xE0y s\u1EBD hi\u1EC3n th\u1ECB tr\xEAn h\u1ED3 s\u01A1 nh\xF3m c\u1EE7a b\u1EA1n v\xE0 tr\xEAn b\u1EA3ng x\u1EBFp h\u1EA1ng.",
  CREATE_TEAM_SUCCESS: "Nh\xF3m c\u1EE7a b\u1EA1n \u0111\xE3 s\u1EB5n s\xE0ng!",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "B\u1EAFt \u0111\u1EA7u x\xE2y d\u1EF1ng nh\xF3m c\u1EE7a b\u1EA1n. Chia s\u1EBB li\xEAn k\u1EBFt n\xE0y v\u1EDBi ng\u01B0\u1EDDi kh\xE1c \u0111\u1EC3 b\u1EA1n c\xF3 th\u1EC3 c\xF9ng nhau th\u1EF1c hi\u1EC7n ho\u1EA1t \u0111\u1ED9ng.",
  VIEW_TEAM_DETAILS: "Xem chi ti\u1EBFt nh\xF3m",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "T\xEDnh n\u0103ng n\u1ED5i b\u1EADt v\xF2ng quay",
  CHALLENGE_FAQ_HEADING: "B\u1EA1n c\xF3 th\u1EAFc m\u1EAFc v\u1EC1 Th\u1EED th\xE1ch kh\xF4ng?",
  CHALLENGE_FAQ_CONTENT: "H\xE3y xem {helpCenterLink} c\u1EE7a ch\xFAng t\xF4i \u0111\u1EC3 bi\u1EBFt t\u1EA5t c\u1EA3 c\xE1c c\xE2u h\u1ECFi th\u01B0\u1EDDng g\u1EB7p c\u1EE7a b\u1EA1n.",
  VIEW_THIS_RESOURCE: "Xem t\xE0i nguy\xEAn n\xE0y",
  RECOMMENDED_NEXT_STEP: "B\u01B0\u1EDBc ti\u1EBFp theo \u0111\u01B0\u1EE3c \u0111\u1EC1 xu\u1EA5t",
  CARE_COLLECTIONS: "B\u1ED9 s\u01B0u t\u1EADp ch\u0103m s\xF3c",
  PLAN_PROGRESS: "Ti\u1EBFn \u0111\u1ED9 k\u1EBF ho\u1EA1ch",
  SINCE_LAST_WEEK: "t\u1EEB tu\u1EA7n tr\u01B0\u1EDBc",
  INSIGHT_CARD_HEADER_DISTANCE: "Kho\u1EA3ng c\xE1ch",
  INSIGHT_CARD_HEADER_STEPS: "B\u01B0\u1EDBc",
  MIN: "ph\xFAt",
  CAL: "Ca-lo",
  KM: "km",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, other {b\u01B0\u1EDBc}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "B\u1EADc thang \u0111\xE3 leo",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, other {b\u1EADc thang}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "Ph\xFAt ho\u1EA1t \u0111\u1ED9ng",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "N\u0103ng l\u01B0\u1EE3ng b\u1ECB \u0111\u1ED1t ch\xE1y",
  INSIGHT_CARD_HEADER_WEIGHT: "C\xE2n n\u1EB7ng",
  INSIGHT_CARD_HEADER_HEIGHT: "Chi\u1EC1u cao",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "Ph\xFAt ch\xE1nh ni\u1EC7m",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "V\xF2ng eo",
  DAY_OF_THE_WEEK: "NG\xC0Y TRONG TU\u1EA6N",
  DAY_OF_THE_MONTH: "NG\xC0Y TRONG TH\xC1NG",
  INSIGHT_CHART_WEEK_TITLE: "Bi\u1EC3u \u0111\u1ED3 thanh {type} h\xE0ng tu\u1EA7n",
  INSIGHT_CHART_MONTH_TITLE: "Bi\u1EC3u \u0111\u1ED3 thanh {type} h\xE0ng th\xE1ng",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "S\u1ED1 {unit} b\u1EA1n \u0111\xE3 \u0111i m\u1ED7i ng\xE0y trong tu\u1EA7n.",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "S\u1ED1 {unit} b\u1EA1n \u0111\xE3 \u0111i m\u1ED7i ng\xE0y trong th\xE1ng.",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "B\u1EA1n \u0111\xE3 \u0111i b\u1ED9 {distance} {unit} v\xE0o {date}",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "S\u1ED1 b\u01B0\u1EDBc b\u1EA1n \u0111\xE3 \u0111i m\u1ED7i ng\xE0y trong tu\u1EA7n.",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "S\u1ED1 b\u01B0\u1EDBc b\u1EA1n \u0111\xE3 \u0111i m\u1ED7i ng\xE0y trong th\xE1ng.",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "B\u1EA1n \u0111\xE3 \u0111i b\u1ED9 {distance} {unit} v\xE0o {date}",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "S\u1ED1 l\u01B0\u1EE3ng {unit} b\u1EA1n \u0111\xE3 \u0111\u1ED1t ch\xE1y m\u1ED7i ng\xE0y trong tu\u1EA7n.",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "S\u1ED1 l\u01B0\u1EE3ng {unit} b\u1EA1n \u0111\xE3 \u0111\u1ED1t ch\xE1y m\u1ED7i ng\xE0y trong th\xE1ng.",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "B\u1EA1n \u0111\xE3 \u0111\u1ED1t ch\xE1y {energy} {unit} v\xE0o {date}",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "S\u1ED1 ph\xFAt ho\u1EA1t \u0111\u1ED9ng cho m\u1ED7i ng\xE0y trong tu\u1EA7n.",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "S\u1ED1 ph\xFAt ho\u1EA1t \u0111\u1ED9ng m\u1ED7i ng\xE0y trong th\xE1ng.",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "B\u1EA1n \u0111\xE3 c\xF3 {value} ph\xFAt ho\u1EA1t \u0111\u1ED9ng v\xE0o {date}",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "S\u1ED1 b\u1EADc thang b\u1EA1n \u0111\xE3 leo m\u1ED7i ng\xE0y trong tu\u1EA7n.",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "S\u1ED1 b\u1EADc thang b\u1EA1n \u0111\xE3 leo m\u1ED7i ng\xE0y trong th\xE1ng.",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "B\u1EA1n \u0111\xE3 leo l\xEAn {value} b\u1EADc thang v\xE0p {date}",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "S\u1ED1 ph\xFAt ch\xE1nh ni\u1EC7m cho m\u1ED7i ng\xE0y trong tu\u1EA7n.",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "S\u1ED1 ph\xFAt ch\xE1nh ni\u1EC7m cho m\u1ED7i ng\xE0y trong th\xE1ng.",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "B\u1EA1n \u0111\xE3 c\xF3 {value} ph\xFAt ch\xE1nh ni\u1EC7m v\xE0o {date}",
  DATAPOINT_CATEGORY_ACTIVITY: "Ho\u1EA1t \u0111\u1ED9ng",
  DATAPOINT_CATEGORY_BODY: "C\u01A1 th\u1EC3",
  MANAGE_UNITS_APPLYING_CHANGES: "\xC1p d\u1EE5ng c\xE1c thay \u0111\u1ED5i...",
  MANAGE_UNITS_CHANGES_SAVED: "Thay \u0111\u1ED5i \u0111\u01B0\u1EE3c l\u01B0u v\xE0o l\xFAc {time}",
  START_TRACKING_HEALTH_HEADER: "B\u1EAFt \u0111\u1EA7u theo d\xF5i s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n",
  START_TRACKING_HEALTH_BODY: "D\u1EC5 d\xE0ng theo d\xF5i s\u1ED1 li\u1EC7u c\u1EE7a b\u1EA1n theo th\u1EDDi gian \u0111\u1EC3 xem b\u1EA1n \u0111\xE3 ti\u1EBFn tri\u1EC3n \u0111\u1EBFn \u0111\xE2u v\xE0 l\xE0m th\u1EBF n\xE0o \u0111\u1EC3 \u0111\u1EA1t \u0111\u01B0\u1EE3c m\u1EE5c ti\xEAu s\u1EE9c kh\u1ECFe c\u1EE7a m\xECnh.",
  BROWSE_SUPPORTED_DATA: "T\xF4i c\xF3 th\u1EC3 theo d\xF5i nh\u1EEFng g\xEC?",
  LAST_UPDATED_CARRIER_DATE: "C\u1EADp nh\u1EADt l\u1EA7n cu\u1ED1i: {date} t\u1EEB {carrier}",
  ACCUMULATION_PERIOD: "Th\u1EDDi gian t\xEDch l\u0169y: <strong>{start} - {end}</strong>",
  NO_DATA_AVAILABLE: "Kh\xF4ng c\xF3 d\u1EEF li\u1EC7u",
  LAST_7_DAYS: "7 ng\xE0y g\u1EA7n nh\u1EA5t",
  LAST_30_DAYS: "30 ng\xE0y qua",
  OLDER: "C\u0169 h\u01A1n",
  GET_ASSISTANCE_NOW: "Tr\xF2 chuy\u1EC7n v\u1EDBi nh\xF3m ch\u0103m s\xF3c c\u1EE7a b\u1EA1n",
  GET_ASSISTANCE_NOW_SUBHEADING: "Tr\xF2 chuy\u1EC7n v\u1EDBi c\xE1c chuy\xEAn gia y t\u1EBF v\xE0 nh\xE2n vi\xEAn ch\u0103m s\xF3c kh\xE1ch h\xE0ng \u0111\u01B0\u1EE3c ch\u1EE9ng nh\u1EADn c\u1EE7a ch\xFAng t\xF4i. H\u1ECD c\xF3 th\u1EC3 gi\xFAp b\u1EA1n ti\u1EBFt ki\u1EC7m ti\u1EC1n b\u1EB1ng c\xE1ch gi\u1EDBi thi\u1EC7u cho b\u1EA1n d\u1ECBch v\u1EE5 ch\u0103m s\xF3c s\u1EE9c kh\u1ECFe ch\u1EA5t l\u01B0\u1EE3ng cao v\xE0 gi\xE1 c\u1EA3 ph\u1EA3i ch\u0103ng nh\u1EA5t trong khu v\u1EF1c c\u1EE7a b\u1EA1n c\u0169ng nh\u01B0 tr\u1EA3 l\u1EDDi m\u1ECDi c\xE2u h\u1ECFi v\u1EC1 quy\u1EC1n l\u1EE3i c\u1EE7a b\u1EA1n.",
  HOW_CAN_WE_HELP: "Ch\xFAng t\xF4i c\xF3 th\u1EC3 gi\xFAp g\xEC cho b\u1EA1n?",
  LIVE_BALANCE_SPEND: "{spentAmount} trong s\u1ED1 {totalAmount} \u0111\xE3 chi",
  LIVE_BALANCE_REMAINING: "{amount} c\xF2n l\u1EA1i",
  UPDATING_UNITS_OF_MEASURE: "C\u1EADp nh\u1EADt \u0111\u01A1n v\u1ECB \u0111o l\u01B0\u1EDDng",
  CHANGING_YOUR_UNITS: "Vi\u1EC7c thay \u0111\u1ED5i \u0111\u01A1n v\u1ECB s\u1EBD \u1EA3nh h\u01B0\u1EDFng \u0111\u1EBFn c\xE1ch ch\xFAng \u0111\u01B0\u1EE3c hi\u1EC3n th\u1ECB trong \u1EE9ng d\u1EE5ng.",
  MESSAGES: "Tin nh\u1EAFn",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "Tin nh\u1EAFn",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "{fileName} \u0111\xE3 b\u1ECB x\xF3a.",
  SEND_NEW_MESSAGE: "G\u1EEDi tin nh\u1EAFn m\u1EDBi",
  NEW_MESSAGE_DEFAULT: "\u0110\xE2y l\xE0 s\u1EF1 kh\u1EDFi \u0111\u1EA7u c\u1EE7a m\u1ED9t th\xF4ng \u0111i\u1EC7p m\u1EDBi",
  NETWORK_MISSING_COVERAGE: "Ch\u01B0\u01A1ng tr\xECnh c\u1EE7a b\u1EA1n kh\xF4ng c\xF3 b\u1EA3o hi\u1EC3m {network}.",
  CARD_FRONT_FILENAME: "{benefitName}-m\u1EB7t tr\u01B0\u1EDBc",
  CARD_BACK_FILENAME: "{benefitName}-m\u1EB7t sau",
  START_A_VISIT: "B\u1EAFt \u0111\u1EA7u chuy\u1EBFn th\u0103m",
  UNREAD: "Ch\u01B0a \u0111\u1ECDc",
  NO_MESSAGES_HEADING: "Ch\u01B0a c\xF3 tin nh\u1EAFn",
  NO_MESSAGES_DESCRIPTION: "B\u1EAFt \u0111\u1EA7u b\u1EB1ng c\xE1ch g\u1EEDi m\u1ED9t tin nh\u1EAFn m\u1EDBi!",
  RETURN_TO: "Quay l\u1EA1i {location}",
  NO_RECOMENDATIONS: "R\u1EA5t ti\u1EBFc, hi\u1EC7n t\u1EA1i ch\xFAng t\xF4i kh\xF4ng c\xF3 khuy\u1EBFn ngh\u1ECB n\xE0o cho l\u0129nh v\u1EF1c s\u1EE9c kh\u1ECFe n\xE0y.",
  CHALLENGES_LEGAL_DISCLAIMER: "Th\u1EED th\xE1ch n\xE0y \u0111\u01B0\u1EE3c thi\u1EBFt k\u1EBF \u0111\u1EC3 t\u1EA1o ra m\u1ED9t c\xE1ch th\xFA v\u1ECB cho m\u1ECDi ng\u01B0\u1EDDi tham gia v\xE0 c\xF3 c\u01A1 h\u1ED9i chi\u1EBFn th\u1EAFng c\xF4ng b\u1EB1ng. \u0110\xF4i khi, ng\u01B0\u1EDDi t\u1ED5 ch\u1EE9c Th\u1EED th\xE1ch c\xF3 th\u1EC3 trao t\u1EB7ng ti\u1EC1n m\u1EB7t ho\u1EB7c c\xE1c gi\u1EA3i th\u01B0\u1EDFng kh\xE1c. Leauge kh\xF4ng thi\u1EBFt k\u1EBF c\u1EA5u tr\xFAc Th\u1EED th\xE1ch ho\u1EB7c quy\u1EBFt \u0111\u1ECBnh c\xF3 cung c\u1EA5p gi\u1EA3i th\u01B0\u1EDFng hay kh\xF4ng. N\u1EBFu b\u1EA1n c\xF3 th\u1EAFc m\u1EAFc v\u1EC1 c\u1EA5u tr\xFAc c\u1EE7a Th\u1EED th\xE1ch n\xE0y ho\u1EB7c tin r\u1EB1ng m\xECnh \u0111\xE3 b\u1ECB lo\u1EA1i kh\u1ECFi c\u01A1 h\u1ED9i chi\u1EBFn th\u1EAFng, vui l\xF2ng trao \u0111\u1ED5i v\u1EDBi ng\u01B0\u1EDDi t\u1ED5 ch\u1EE9c Th\u1EED th\xE1ch.",
  ALL_RECORDED_DATA: "T\u1EA5t c\u1EA3 d\u1EEF li\u1EC7u \u0111\u01B0\u1EE3c ghi l\u1EA1i",
  NEW_MESSAGE: "Tin nh\u1EAFn m\u1EDBi",
  MESSAGE_SUBJECT: "Ch\u1EE7 \u0111\u1EC1 th\u01B0 (t\xF9y ch\u1ECDn)",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "B\u1EA1n \u0111\xE3 v\u01B0\u1EE3t qu\xE1 gi\u1EDBi h\u1EA1n k\xFD t\u1EF1 t\u1ED1i \u0111a ({maximum})",
  CHAR_LIMIT_EXCEEDED: "Gi\u1EDBi h\u1EA1n k\xFD t\u1EF1 v\u01B0\u1EE3t qu\xE1 ({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "\u0110\u1EB7t ch\u1EE7 \u0111\u1EC1 tin nh\u1EAFn",
  TO: "\u0110\u1EBFn",
  CREATE_MESSAGE: "T\u1EA1o tin nh\u1EAFn",
  MEMBER_LABEL: "TH\xC0NH VI\xCAN",
  RESULT_COUNT: "{count, plural, other {{count,number} k\u1EBFt qu\u1EA3}}",
  USER_COLON: "{user}:",
  JOINED_CHALLENGE_HIGHLIGHT: "\u0110\xE3 tham gia <strong>{challengeTitle}</strong>!",
  CREATED_TEAM_HIGHLIGHT: "\u0110\xE3 t\u1EA1o <strong>{teamName}</strong>!",
  JOINED_TEAM_HIGHLIGHT: "\u0110\xE3 tham gia <strong>{teamName}</strong>!",
  STEPS_WALKED_HIGHLIGHT: "<strong>\u0110\xE3 \u0111i \u0111\u01B0\u1EE3c {total} b\u01B0\u1EDBc!</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong>\u0110\xE3 v\u1EADn \u0111\u1ED9ng {total} ph\xFAt!</strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong>\u0110\xE3 ho\xE0n th\xE0nh {total} ph\xFAt!</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong>\u0110\xE3 \u0111\u1EA1t \u0111\u01B0\u1EE3c th\xE0nh t\xEDch {achievement} !</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "<strong>\u0110\xE3 ho\xE0n th\xE0nh m\u1ED9t ho\u1EA1t \u0111\u1ED9ng!</strong>",
  NO_CARE_COLLECTIONS: "R\u1EA5t ti\u1EBFc, kh\xF4ng c\xF3 b\u1ED9 s\u01B0u t\u1EADp ch\u0103m s\xF3c n\xE0o c\xF3 s\u1EB5n.",
  WRITE_A_MESSAGE: "Vi\u1EBFt tin nh\u1EAFn...",
  UPLOAD_MULTIPLE_FILES: "T\u1EA3i l\xEAn nhi\u1EC1u t\u1EADp tin",
  SEND: "G\u1EEDi",
  SEND_MESSAGE: "G\u1EEDi tin nh\u1EAFn",
  DATA_SOURCES: "Ngu\u1ED3n d\u1EEF li\u1EC7u",
  SOURCES_CAN_UPDATE: "C\xE1c ngu\u1ED3n sau \u0111\xE2y \u0111\u01B0\u1EE3c ph\xE9p c\u1EADp nh\u1EADt c\u1EE7a b\u1EA1n",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "Khi c\xF3 nhi\u1EC1u ngu\u1ED3n d\u1EEF li\u1EC7u, ngu\u1ED3n d\u1EEF li\u1EC7u c\xF3 gi\xE1 tr\u1ECB l\u1EDBn nh\u1EA5t s\u1EBD \u0111\u01B0\u1EE3c ghi l\u1EA1i v\xE0o cu\u1ED1i ng\xE0y (11:59 t\u1ED1i).",
  APPLE_HEALTH: "Apple Health",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "Garmin",
  POLAR: "Polar",
  WITHINGS: "Withings",
  HEALTH_PLAN: "Ch\u01B0\u01A1ng tr\xECnh b\u1EA3o hi\u1EC3m y t\u1EBF",
  OMRON: "Omron",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "C\u1EADp nh\u1EADt \u0111\u01A1n v\u1ECB \u0111o l\u01B0\u1EDDng",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "Vi\u1EC7c thay \u0111\u1ED5i \u0111\u01A1n v\u1ECB s\u1EBD \u1EA3nh h\u01B0\u1EDFng \u0111\u1EBFn c\xE1ch ch\xFAng \u0111\u01B0\u1EE3c hi\u1EC3n th\u1ECB trong \u1EE9ng d\u1EE5ng.",
  DOCUMENTS: "T\xE0i li\u1EC7u",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "T\xE0i li\u1EC7u",
  DOCUMENTS_FILTER_LOADING_TEXT: "C\xE1c tab v\xE0 b\u1ED9 l\u1ECDc \u0111ang t\u1EA3i",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "{filter} l\xE0 b\u1EAFt bu\u1ED9c.",
  DOCUMENT_FILTER_X_REQUIRED: "{filter} (b\u1EAFt bu\u1ED9c)",
  SHOW_RESULTS: "Hi\u1EC3n th\u1ECB k\u1EBFt qu\u1EA3",
  RESET_FILTERS: "\u0110\u1EB7t l\u1EA1i b\u1ED9 l\u1ECDc",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "B\u1ED9 l\u1ECDc \u0111\xE3 ch\u1ECDn",
  DOCUMENTS_RESULTS_HEADING: "K\u1EBFt qu\u1EA3",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "B\u1ECF qua \u0111\u1EC3 t\u1EA3i l\xEAn t\xE0i li\u1EC7u",
  AVERAGE: "Trung b\xECnh",
  VIEW_ALL_METRIC_DATA: "Xem t\u1EA5t c\u1EA3 {metric} d\u1EEF li\u1EC7u",
  EDIT_METRIC_DATA: "Ch\u1EC9nh s\u1EEDa d\u1EEF li\u1EC7u {metric}",
  NEXT_WEEK: "Tu\u1EA7n t\u1EDBi",
  PREVIOUS_WEEK: "Tu\u1EA7n tr\u01B0\u1EDBc",
  NEXT_30_DAYS: "30 ng\xE0y ti\u1EBFp theo",
  PREVIOUS_30_DAYS: "30 ng\xE0y tr\u01B0\u1EDBc",
  UNABLE_TO_CREATE_TEAM: "Kh\xF4ng th\u1EC3 t\u1EA1o nh\xF3m m\u1EDBi - vui l\xF2ng th\u1EED l\u1EA1i! N\u1EBFu b\u1EA1n \u0111\xE3 tham gia m\u1ED9t nh\xF3m, b\u1EA1n s\u1EBD c\u1EA7n ph\u1EA3i r\u1EDDi kh\u1ECFi nh\xF3m hi\u1EC7n t\u1EA1i c\u1EE7a m\xECnh tr\u01B0\u1EDBc khi t\u1EA1o m\u1ED9t nh\xF3m m\u1EDBi.",
  START_TRACKING_METRIC: "B\u1EAFt \u0111\u1EA7u theo d\xF5i {metric}",
  CURRENTLY_NOT_SHARING_DATA: "Hi\u1EC7n t\u1EA1i b\u1EA1n kh\xF4ng chia s\u1EBB b\u1EA5t k\u1EF3 d\u1EEF li\u1EC7u n\xE0o cho {metric}",
  SUCCESSFULLY_LEFT_TEAM: "B\u1EA1n \u0111\xE3 th\xE0nh c\xF4ng r\u1EDDi kh\u1ECFi th\u1EED th\xE1ch {teamName}.",
  LEAVE_TEAM_FAILURE: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1, vui l\xF2ng th\u1EED l\u1EA1i!",
  VIEW_DATA_SOURCES: "Xem ngu\u1ED3n d\u1EEF li\u1EC7u",
  OR_CALL_PHONE: "ho\u1EB7c g\u1ECDi {phoneNumber}{extension}",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "Kh\xF4ng c\xF3 t\xE0i li\u1EC7u n\xE0o kh\u1EDBp v\u1EDBi b\u1ED9 l\u1ECDc c\u1EE7a b\u1EA1n",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "H\xE3y th\u1EED lo\u1EA1i b\u1ECF m\u1ED9t s\u1ED1 b\u1ED9 l\u1ECDc c\u1EE7a b\u1EA1n \u0111\u1EC3 c\xF3 th\xEAm k\u1EBFt qu\u1EA3",
  DOCUMENTS_EMPTY_STATE_HEADING: "Kh\xF4ng c\xF3 t\xE0i li\u1EC7u \u1EDF \u0111\xE2y (ch\u01B0a!)",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "M\u1ED9t n\u01A1i \u0111\u1EC3 xem v\xE0 s\u1EAFp x\u1EBFp t\u1EA5t c\u1EA3 c\xE1c t\xE0i li\u1EC7u c\u1EE7a b\u1EA1n",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team} (Nh\xF3m c\u1EE7a b\u1EA1n)",
  YOUR_CARE_ADVISOR: "H\u01B0\u1EDBng d\u1EABn ch\u0103m s\xF3c c\u1EE7a b\u1EA1n",
  COMPOSE: "So\u1EA1n",
  COMPOSE_NEW_MESSAGE: "So\u1EA1n tin nh\u1EAFn m\u1EDBi",
  POLICY_INFORMATION_UNAVAILABLE: "Kh\xF4ng c\xF3 th\xF4ng tin n\xE0o c\xF3 s\u1EB5n cho th\u1EDDi h\u1EA1n b\u1EA3o hi\u1EC3m n\xE0y.",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "Kh\xF4ng c\xF3 th\xF4ng tin cho th\xE0nh vi\xEAn n\xE0y. Vui l\xF2ng th\u1EED m\u1ED9t th\xE0nh vi\xEAn kh\xE1c.",
  MESSAGE_THREADS_LIST_SUBTITLE: "C\xF3 th\u1EC3 m\u1EA5t t\u1EDBi 2 ng\xE0y l\xE0m vi\u1EC7c \u0111\u1EC3 nh\xF3m c\u1EE7a ch\xFAng t\xF4i ph\u1EA3n h\u1ED3i. N\u1EBFu \u0111\xE2y l\xE0 tr\u01B0\u1EDDng h\u1EE3p c\u1EA5p c\u1EE9u y t\u1EBF, h\xE3y g\u1ECDi 911.",
  ALL_MESSAGES_FOR_THREAD_BANNER: "Vui l\xF2ng kh\xF4ng g\u1EEDi cho ch\xFAng t\xF4i th\xF4ng tin c\u1EE5 th\u1EC3 v\u1EC1 t\xECnh tr\u1EA1ng s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n qua cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y.",
  TROUBLE_DISPLAYING_DATA: "Ch\xFAng t\xF4i \u0111ang g\u1EB7p s\u1EF1 c\u1ED1 khi hi\u1EC3n th\u1ECB d\u1EEF li\u1EC7u c\u1EE7a b\u1EA1n",
  RETRY: "Th\u1EED l\u1EA1i",
  YOU: "B\u1EA1n",
  MESSAGES_IN_CONVERSATION_TITLED: "Tin nh\u1EAFn trong cu\u1ED9c tr\xF2 chuy\u1EC7n c\xF3 ti\xEAu \u0111\u1EC1 {subject}",
  TEAM_AVERAGE: "\u0110i\u1EC3m trung b\xECnh c\u1EE7a nh\xF3m",
  YOUR_METRICS: "S\u1ED1 li\u1EC7u c\u1EE7a t\xF4i",
  YOUR_METRICS_SUBTITLE: "D\u1EEF li\u1EC7u s\u1EE9c kh\u1ECFe m\u1EDBi nh\u1EA5t t\u1EEB c\xE1c \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i c\u1EE7a b\u1EA1n.",
  APPS_AND_DEVICES: "\u1EE8ng d\u1EE5ng & Thi\u1EBFt b\u1ECB",
  APP_AND_DEVICE_METRICS: "Ch\u1EC9 s\u1ED1 \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB",
  VIEW_ALL_METRICS: "Xem s\u1ED1 li\u1EC7u \u1EE9ng d\u1EE5ng v\xE0 thi\u1EBFt b\u1ECB",
  RENAME: "\u0110\u1ED5i t\xEAn",
  ENTER_DOCUMENT_NAME: "Vui l\xF2ng nh\u1EADp t\xEAn t\xE0i li\u1EC7u",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "\u0110\xE2y l\xE0 m\u1ED9t ho\u1EA1t \u0111\u1ED9ng c\xF3 m\u1EE9c \u0111\u1ED9 \u01B0u ti\xEAn th\u1EA5p",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "\u0110\xE2y l\xE0 m\u1ED9t ho\u1EA1t \u0111\u1ED9ng c\xF3 m\u1EE9c \u0111\u1ED9 \u01B0u ti\xEAn trung b\xECnh",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "\u0110\xE2y l\xE0 m\u1ED9t ho\u1EA1t \u0111\u1ED9ng c\xF3 m\u1EE9c \u0111\u1ED9 \u01B0u ti\xEAn cao",
  ACTIVITY_EXPIRY_DATE: "H\u1EBFt h\u1EA1n {date}",
  GENERIC_MESSAGING_ERROR: "C\xF3 v\u1EBB nh\u01B0 \u0111\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1, vui l\xF2ng l\xE0m m\u1EDBi trang v\xE0 th\u1EED l\u1EA1i.",
  VIEW_THE_ACTIVITIES: "Xem c\xE1c ho\u1EA1t \u0111\u1ED9ng",
  DRAG_FILES_HERE_OR: "K\xE9o t\u1EADp tin v\xE0o \u0111\xE2y ho\u1EB7c",
  ADD_A_DOCUMENT: "Th\xEAm t\xE0i li\u1EC7u",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "T\u1EA3i l\xEAn t\xE0i li\u1EC7u",
  ADD_DOCUMENT: "Th\xEAm t\xE0i li\u1EC7u",
  REMOVE_FILE: "X\xF3a t\u1EADp tin",
  REMOVE_FILE_FILENAME: "X\xF3a t\u1EADp tin: {filename} ({fileSize})",
  UNABLE_TO_SEND: "Kh\xF4ng th\u1EC3 g\u1EEDi, vui l\xF2ng th\u1EED l\u1EA1i",
  ERROR_HEADING: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 \u1EDF ph\xEDa ch\xFAng t\xF4i.",
  FILE_TYPE_NOT_SUPPORTED: "Lo\u1EA1i t\u1EADp tin kh\xF4ng \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "S\u1EED d\u1EE5ng t\u1EADp tin \u1EDF m\u1ED9t trong c\xE1c \u0111\u1ECBnh d\u1EA1ng sau: {fileTypes}",
  RECENT_MESSAGES: "Tin nh\u1EAFn g\u1EA7n \u0111\xE2y",
  NO_MESSAGES_YET: "B\u1EA1n kh\xF4ng c\xF3 tin nh\u1EAFn n\xE0o (ch\u01B0a!)",
  NO_MESSAGES_DETAIL: "H\u01B0\u1EDBng d\u1EABn ch\u0103m s\xF3c \u0111\u01B0\u1EE3c ch\u1EC9 \u0111\u1ECBnh s\u1EBD h\u1ED7 tr\u1EE3 b\u1EA1n \u0111\u01B0a ra quy\u1EBFt \u0111\u1ECBnh ch\u0103m s\xF3c s\u1EE9c kh\u1ECFe th\xF4ng minh h\u01A1n \u1EDF m\u1ECDi b\u01B0\u1EDBc.",
  START_A_NEW_MESSAGE: "B\u1EAFt \u0111\u1EA7u m\u1ED9t tin nh\u1EAFn m\u1EDBi",
  USER_ADDED_DOCUMENT: "{user} \u0111\xE3 th\xEAm t\xE0i li\u1EC7u",
  ERROR_SAVING_TRY_AGAIN: "L\u1ED7i khi l\u01B0u thay \u0111\u1ED5i. Vui l\xF2ng th\u1EED l\u1EA1i.",
  MODIFIED_ON_DATE: "\u0110\xE3 s\u1EEDa \u0111\u1ED5i v\xE0o {date}",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "\u0110\xE3 t\u1EA3i l\xEAn {date}",
  FILE_TYPE_TOO_LARGE: "K\xEDch th\u01B0\u1EDBc t\u1EADp tin qu\xE1 l\u1EDBn",
  MAXIMUM_FILE_SIZE_IS: "K\xEDch th\u01B0\u1EDBc t\u1EADp tin t\u1ED1i \u0111a l\xE0 {fileSize}.",
  FILE_TOO_LARGE: "R\u1EA5t ti\u1EBFc. T\u1EADp tin \u0111\xF3 qu\xE1 l\u1EDBn.",
  FILE_SHOULD_BE_UNDER: "T\u1EADp tin c\u1EE7a b\u1EA1n ph\u1EA3i n\u1EB1m trong {fileSize}",
  ATTACHMENT_LIMIT_REACHED: "R\u1EA5t ti\u1EBFc. C\xF3 qu\xE1 nhi\u1EC1u t\u1EADp tin.",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "{maxNumberOfDocs, plural, other {B\u1EA1n c\xF3 th\u1EC3 \u0111\xEDnh k\xE8m t\u1ED1i \u0111a {maxNumberOfDocs,number} t\u1EADp tin.}}",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "R\u1EA5t ti\u1EBFc. Lo\u1EA1i t\u1EADp tin \u0111\xF3 kh\xF4ng \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3.",
  ATTACHMENT_TYPES_ALLOWED: "B\u1EA1n c\xF3 th\u1EC3 \u0111\xEDnh k\xE8m t\u1EADp tin c\u1EE7a m\xECnh theo c\xE1c \u0111\u1ECBnh d\u1EA1ng sau: {fileTypes}",
  MESSAGES_PAGINATION_ERROR: "Kh\xF4ng th\u1EC3 t\u1EA3i tin nh\u1EAFn. <button>Th\u1EED l\u1EA1i.</button>",
  TRANSACTION_TABLE_CAPTION: "L\u1ECBch s\u1EED giao d\u1ECBch",
  SELECT_PARTICIPANT_ERROR: "Ch\u1ECDn ai \u0111\xF3 \u0111\u1EC3 g\u1EEDi tin nh\u1EAFn",
  PREVIEW_OF_FILE: "Xem tr\u01B0\u1EDBc {fileName}",
  VIEW_DOCUMENT_ACTIONS: "Xem h\xE0nh \u0111\u1ED9ng {documentName}.",
  VISIT_LINK: "Truy c\u1EADp li\xEAn k\u1EBFt",
  COMPLETE_BY_DATE: "Ho\xE0n th\xE0nh b\u1EDFi {date}",
  ERROR_UPLOADING_YOUR_FILE: "C\xF3 l\u1ED7i khi t\u1EA3i t\u1EADp tin c\u1EE7a b\u1EA1n l\xEAn. Th\u1EED l\u1EA1i.",
  DOCUMENT_SUBMITTED: "T\xE0i li\u1EC7u \u0111\xE3 n\u1ED9p",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "T\xE0i li\u1EC7u c\u1EE7a b\u1EA1n s\u1EBD s\u1EDBm c\xF3 s\u1EB5n. Vui l\xF2ng \u0111\u1EE3i v\xE0i ph\xFAt v\xE0 l\xE0m m\u1EDBi trang.",
  ERROR_RENAMING_YOUR_DOCUMENT: "C\xF3 l\u1ED7i khi \u0111\u1ED5i t\xEAn t\xE0i li\u1EC7u c\u1EE7a b\u1EA1n. Th\u1EED l\u1EA1i.",
  DOCUMENT_RENAMED_SUCCESSFULLY: "\u0110\xE3 \u0111\u1ED5i t\xEAn t\xE0i li\u1EC7u th\xE0nh c\xF4ng.",
  ERROR_HEADING_GENERIC: "\u0110\xE3 c\xF3 l\u1ED7i g\xEC \u0111\xF3",
  ERROR_HEADING_NO_CONNECTION: "Kh\xF4ng c\xF3 k\u1EBFt n\u1ED1i internet",
  ERROR_CAPTION_BACKEND: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi k\u1EBFt n\u1ED1i v\u1EDBi m\xE1y ch\u1EE7 c\u1EE7a ch\xFAng t\xF4i.\n Vui l\xF2ng th\u1EED l\u1EA1i sau.",
  ERROR_CAPTION_NO_CONNECTION: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi t\u1EA3i d\u1EEF li\u1EC7u.\n Ki\u1EC3m tra Internet c\u1EE7a b\u1EA1n v\xE0 th\u1EED l\u1EA1i.",
  SURE_YOU_WANT_TO_DELETE: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a {name} kh\xF4ng?",
  CANNOT_BE_UNDONE: "H\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c.",
  DELETE_DOCUMENT: "X\xF3a t\xE0i li\u1EC7u",
  DOCUMENT_DELETED: "T\xE0i li\u1EC7u \u0111\xE3 x\xF3a",
  ERROR_DELETING_YOUR_DOCUMENT: "L\u1ED7i khi x\xF3a t\xE0i li\u1EC7u c\u1EE7a b\u1EA1n",
  ABOUT_ME: "V\u1EC1 t\xF4i",
  SEE_MORE: "XEM TH\xCAM",
  X_ABOUT_X: "{left} v\u1EC1 {right}",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "Ch\xFAng t\xF4i kh\xF4ng th\u1EC3 t\u1EA3i trang n\xE0y. Ki\u1EC3m tra Internet c\u1EE7a b\u1EA1n v\xE0 th\u1EED l\u1EA1i.",
  WHO_IS_THIS_DOCUMENT_ABOUT: "T\xE0i li\u1EC7u n\xE0y n\xF3i v\u1EC1 ai?",
  SELECT_A_USER: "Ch\u1ECDn ng\u01B0\u1EDDi d\xF9ng",
  SELECT_DOCUMENT: "Ch\u1ECDn t\xE0i li\u1EC7u",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "\u0110i\u1EC1u n\xE0y gi\xFAp H\u01B0\u1EDBng d\u1EABn ch\u0103m s\xF3c c\u1EE7a b\u1EA1n x\xE1c \u0111\u1ECBnh \u0111\u01B0\u1EE3c ng\u01B0\u1EDDi m\xE0 t\xE0i li\u1EC7u n\xE0y li\xEAn quan \u0111\u1EBFn.",
  VIEW_ALL_MESSAGE_THREADS: "Xem t\u1EA5t c\u1EA3 c\xE1c ch\u1EE7 \u0111\u1EC1 tin nh\u1EAFn",
  TOGGLE_MENU: "Chuy\u1EC3n \u0111\u1ED5i danh s\xE1ch tin nh\u1EAFn",
  METRICS: "S\u1ED1 li\u1EC7u",
  UNREAD_MESSAGES: "Tin nh\u1EAFn ch\u01B0a \u0111\u1ECDc",
  WITH_DOCUMENT: "v\u1EDBi t\xE0i li\u1EC7u",
  X_TOKENS: "{tokenCount, plural, other {{tokenCount,number} m\xE3 th\xF4ng b\xE1o}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "Ch\u01B0\u01A1ng tr\xECnh v\xE0 th\u1EED th\xE1ch",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "B\u1EA1n \u0111\xE3 s\u1EB5n s\xE0ng cho \u0111i\u1EC1u g\xEC \u0111\xF3 m\u1EDBi ch\u01B0a?",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "T\xECm c\xE1c ch\u01B0\u01A1ng tr\xECnh v\xE0 th\u1EED th\xE1ch v\u1EC1 s\u1EE9c kh\u1ECFe ph\xF9 h\u1EE3p v\u1EDBi m\u1EE5c ti\xEAu c\xE1 nh\xE2n c\u1EE7a b\u1EA1n.",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "Ch\u01B0\u01A1ng tr\xECnh v\xE0 th\u1EED th\xE1ch",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "B\u1EA1n \u0111\xE3 s\u1EB5n s\xE0ng cho \u0111i\u1EC1u g\xEC \u0111\xF3 m\u1EDBi ch\u01B0a?",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "T\xECm c\xE1c ch\u01B0\u01A1ng tr\xECnh v\xE0 th\u1EED th\xE1ch v\u1EC1 s\u1EE9c kh\u1ECFe ph\xF9 h\u1EE3p v\u1EDBi m\u1EE5c ti\xEAu c\xE1 nh\xE2n c\u1EE7a b\u1EA1n.",
  EXPLORE_LIBRARY: "Kh\xE1m ph\xE1 Th\u01B0 vi\u1EC7n",
  YOUVE_GOT_THIS: "B\u1EA1n l\xE0m \u0111\u01B0\u1EE3c r\u1ED3i!",
  YOUVE_GOT_THIS_NAME: "B\u1EA1n l\xE0m \u0111\u01B0\u1EE3c m\xE0, {firstName}!",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "Ch\xE0o m\u1EEBng b\u1EA1n \u0111\u1EBFn v\u1EDBi h\xE0nh tr\xECnh s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n!",
  HEALTH_JOURNEY_PAGE: "Trang H\xE0nh Tr\xECnh S\u1EE9c Kh\u1ECFe",
  VIEW_DETAILS: "Xem chi ti\u1EBFt",
  NEXT_ACTIVITY: "Ho\u1EA1t \u0111\u1ED9ng ti\u1EBFp theo",
  X_OF_Y_ACTIVITIES: "{completed} trong s\u1ED1 {total} ho\u1EA1t \u0111\u1ED9ng",
  X_PERCENTAGE: "{percent}%",
  SOLUTIONS: "Gi\u1EA3i ph\xE1p",
  EXPLORE: "Kh\xE1m ph\xE1",
  DOCUMENT_NAME_TOO_LONG: "B\u1EA1n \u0111\xE3 v\u01B0\u1EE3t qu\xE1 gi\u1EDBi h\u1EA1n k\xFD t\u1EF1 t\u1ED1i \u0111a ({maximum})",
  CLAIM_PATIENT_NAME: "\u0110\u1ED1i v\u1EDBi: {patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "Kh\xF4ng c\xF3 ch\u01B0\u01A1ng tr\xECnh. Kh\xF4ng c\xF3 th\xE1ch th\u1EE9c. Ch\u1EC9 l\xE0 nh\u1EEFng ho\u1EA1t \u0111\u1ED9ng di\u1EC5n ra m\u1ED9t l\u1EA7n.",
  FEEDBACK_LINK_LABEL: "\u0110\xE1nh gi\xE1 th\xF4ng tin {value} trong s\u1ED1 {maximum} v\u1EC1 t\xEDnh h\u1EEFu \xEDch",
  RECENT_ACHIEVEMENTS: "Th\xE0nh t\xEDch g\u1EA7n \u0111\xE2y",
  ACHIEVEMENTS_BANNER_HEADER: "Ch\xFAc m\u1EEBng b\u1EA1n \u0111\xE3 \u0111\u1EA1t \u0111\u01B0\u1EE3c nh\u1EEFng huy hi\u1EC7u n\xE0y!",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "Ch\xFAng t\xF4i r\u1EA5t th\xEDch th\u1EA5y b\u1EA1n \u01B0u ti\xEAn s\u1EE9c kh\u1ECFe v\xE0 h\u1EA1nh ph\xFAc c\u1EE7a m\xECnh! H\xE3y xem nh\u1EEFng th\xE0nh t\u1EF1u kh\xE1c m\xE0 b\u1EA1n \u0111ang h\u01B0\u1EDBng t\u1EDBi.",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "Ch\xFAng t\xF4i r\u1EA5t th\xEDch th\u1EA5y b\u1EA1n \u01B0u ti\xEAn s\u1EE9c kh\u1ECFe v\xE0 h\u1EA1nh ph\xFAc c\u1EE7a m\xECnh! H\xE3y xem nh\u1EEFng th\xE0nh t\u1EF1u kh\xE1c m\xE0 b\u1EA1n \u0111\xE3 \u0111\u1EA1t \u0111\u01B0\u1EE3c v\xE0 nh\u1EEFng th\xE0nh t\u1EF1u b\u1EA1n \u0111ang h\u01B0\u1EDBng t\u1EDBi.",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "(Ch\u01B0a) c\xF3 th\xE0nh t\xEDch n\xE0o!",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "Ho\xE0n th\xE0nh c\xE1c ho\u1EA1t \u0111\u1ED9ng trong H\xE0nh tr\xECnh c\u1EE7a b\u1EA1n \u0111\u1EC3 b\u1EAFt \u0111\u1EA7u ki\u1EBFm th\xE0nh t\xEDch.",
  HISTORY_EMPTY_STATE_HEADER: "Ch\u01B0a c\xF3 g\xEC \u1EDF \u0111\xE2y!",
  HISTORY_EMPTY_STATE_DESCRIPTION: "Khi b\u1EA1n ho\xE0n th\xE0nh m\u1ED9t ho\u1EA1t \u0111\u1ED9ng, ch\u01B0\u01A1ng tr\xECnh ho\u1EB7c th\u1EED th\xE1ch, n\xF3 s\u1EBD xu\u1EA5t hi\u1EC7n \u1EDF \u0111\xE2y.",
  INDIVIDUAL_BALANCE: "{balance} c\u1EE7a {membersFullName}",
  FAMILY_BALANCE: "Gia \u0111\xECnh {balance}",
  UPCOMING: "S\u1EAFp t\u1EDBi",
  REMOVED: "\u0110\xE3 x\xF3a",
  COMPLETED_TODAY: "Ho\xE0n th\xE0nh",
  CLAIMS_BENEFIT_CARD_TITLE: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "Theo d\xF5i s\u1ED1 ti\u1EC1n b\u1EA1n n\u1EE3 cho vi\u1EC7c ch\u0103m s\xF3c c\u1EE7a m\xECnh v\xE0 s\u1ED1 ti\u1EC1n b\u1EA3o hi\u1EC3m s\u1EBD chi tr\u1EA3",
  CLAIMS_FILTER_PROPERTY_TYPE: "Lo\u1EA1i d\u1ECBch v\u1EE5",
  CLAIMS_FILTER_PROPERTY_STATUS: "T\xECnh tr\u1EA1ng y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "TH\xC0NH VI\xCAN",
  CLAIMS_FILTER_VALUE_ORAL: "Nha khoa",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "Y t\u1EBF",
  CLAIMS_FILTER_VALUE_VISION: "Th\u1ECB gi\xE1c",
  CLAIMS_FILTER_VALUE_PHARMACY: "Nh\xE0 thu\u1ED1c",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "Chuy\xEAn nghi\u1EC7p",
  CLAIMS_FILTER_VALUE_APPROVED: "Ch\xFAng t\xF4i s\u1EBD tr\u1EA3 ph\u1EA7n c\u1EE7a ch\xFAng t\xF4i",
  CLAIMS_FILTER_VALUE_PENDING: "X\u1EED l\xFD",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "\u0110\u01B0\u1EE3c b\u1EA3o hi\u1EC3m m\u1ED9t ph\u1EA7n",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "Kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m",
  CLAIMS_FILTER_VALUE_ADJUSTED: "\u0110\xE3 \u0111i\u1EC1u ch\u1EC9nh",
  DRINKS_PER_WEEK: "\u0110\u1ED3 u\u1ED1ng m\u1ED7i tu\u1EA7n",
  OR_SELECT_BETWEEN_THESE: "ho\u1EB7c l\u1EF1a ch\u1ECDn gi\u1EEFa nh\u1EEFng \u0111i\u1EC1u n\xE0y",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "Ph\u1EA1m vi ng\xE0y",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "N\u0103m \u0111\u1EBFn nay",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "N\u0103m tr\u01B0\u1EDBc",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "12 th\xE1ng qua",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "2 n\u0103m qua",
  CLAIMS_VIEW_ALL: "Xem t\u1EA5t c\u1EA3",
  CHALLENGE_TYPE_GROUP_TAG: "Nh\xF3m",
  CHALLENGE_TYPE_SOLO_TAG: "M\u1ED9t m\xECnh",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "Th\u1EED th\xE1ch nh\xF3m",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "Th\u1EED th\xE1ch \u0111\u01A1n",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "H\xE3y c\xF3 tr\xE1ch nhi\u1EC7m khi th\u1EED th\xE1ch b\u1EA3n th\xE2n b\u1EB1ng c\xE1c ho\u1EA1t \u0111\u1ED9ng ch\xE1nh ni\u1EC7m, s\xE1ng ki\u1EBFn \u0103n u\u1ED1ng l\xE0nh m\u1EA1nh, cu\u1ED9c thi th\u1EC3 d\u1EE5c, ho\u1EA1t \u0111\u1ED9ng gi\u1EA3m c\u0103ng th\u1EB3ng, th\u1EED th\xE1ch b\u01B0\u1EDBc \u0111i, v.v.",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "H\xE3y t\u1EF1 m\xECnh th\u1EED th\xE1ch b\u1EA3n th\xE2n b\u1EB1ng c\xE1ch ho\xE0n th\xE0nh c\xE1c nh\xF3m ho\u1EA1t \u0111\u1ED9ng t\u1EADp trung v\xE0o th\u1EC3 ch\u1EA5t, dinh d\u01B0\u1EE1ng, s\u1EE9c kh\u1ECFe tinh th\u1EA7n, qu\u1EA3n l\xFD c\u0103ng th\u1EB3ng, v.v.",
  HELPFUL_LINKS: "Li\xEAn k\u1EBFt h\u1EEFu \xEDch",
  HOW_CLAIMS_WORK: "C\xE1ch y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m ho\u1EA1t \u0111\u1ED9ng",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "Hi\u1EC3u c\xE1ch y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng c\u1EE7a b\u1EA1n ho\u1EA1t \u0111\u1ED9ng",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>Khi b\u1EA1n \u0111\u1EBFn kh\xE1m m\u1ED9t trong nh\u1EEFng b\xE1c s\u0129 trong m\u1EA1ng l\u01B0\u1EDBi c\u1EE7a m\xECnh, h\u1ECD s\u1EBD y\xEAu c\u1EA7u thanh to\xE1n. N\u1EBFu b\u1EA1n c\xF3 \u0111\u1ED3ng thanh to\xE1n, h\u1ECD s\u1EBD thu ti\u1EC1n t\u1EEB b\u1EA1n v\xE0 sau \u0111\xF3 g\u1EEDi y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng \u0111\u1EBFn c\xF4ng ty b\u1EA3o hi\u1EC3m.</p><p>Sau \u0111\xF3, ch\xFAng t\xF4i xem x\xE9t k\u1EBF ho\u1EA1ch b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n, c\xF2n \u0111\u01B0\u1EE3c g\u1ECDi l\xE0 h\u1EE3p \u0111\u1ED3ng ph\xFAc l\u1EE3i c\u1EE7a b\u1EA1n, \u0111\u1EC3 x\xE1c \u0111\u1ECBnh xem d\u1ECBch v\u1EE5 c\xF3 \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m hay kh\xF4ng. N\u1EBFu c\xF3, ch\xFAng t\xF4i s\u1EBD ch\u1EA5p thu\u1EADn d\u1EF1a tr\xEAn c\xE1c \u0111i\u1EC1u kho\u1EA3n c\u1EE7a h\u1EE3p \u0111\u1ED3ng ph\xFAc l\u1EE3i. N\u1EBFu kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m nh\u01B0 m\u1ED9t ph\u1EA7n c\u1EE7a k\u1EBF ho\u1EA1ch ph\xFAc l\u1EE3i c\u1EE7a b\u1EA1n, b\u1EA1n c\xF3 tr\xE1ch nhi\u1EC7m thanh to\xE1n cho d\u1ECBch v\u1EE5.</p>",
  CLAIMS_STATUS_DEFINITIONS: "\u0110\u1ECBnh ngh\u0129a tr\u1EA1ng th\xE1i y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  CLAIMS_STATUS_DEFINITION_APPROVED: "Ch\xFAng t\xF4i s\u1EBD thanh to\xE1n ph\u1EA7n c\u1EE7a m\xECnh c\xF3 ngh\u0129a l\xE0 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m \u0111\u01B0\u1EE3c ch\u1EA5p thu\u1EADn v\xE0 ch\xFAng t\xF4i \u0111\u1ED3ng \xFD thanh to\xE1n m\u1ED9t ph\u1EA7n ho\u1EB7c to\xE0n b\u1ED9 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m d\u1EF1a tr\xEAn ch\u01B0\u01A1ng tr\xECnh c\u1EE7a b\u1EA1n.",
  CLAIMS_STATUS_DEFINITION_PENDING: "\u0110ang x\u1EED l\xFD c\xF3 ngh\u0129a l\xE0 ch\xFAng t\xF4i \u0111ang t\xECm ra ai n\u1EE3 c\xE1i g\xEC.",
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: "B\u1EA3o hi\u1EC3m m\u1ED9t ph\u1EA7n c\xF3 ngh\u0129a l\xE0 ch\xFAng t\xF4i s\u1EBD b\u1EA3o hi\u1EC3m m\u1ED9t ph\u1EA7n y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n.",
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: "Kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m c\xF3 ngh\u0129a l\xE0 d\u1ECBch v\u1EE5 \u0111\xF3 kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m trong g\xF3i ph\xFAc l\u1EE3i c\u1EE7a b\u1EA1n v\xE0 b\u1EA1n s\u1EBD ph\u1EA3i tr\u1EA3 ti\u1EC1n.",
  CLAIMS_STATUS_DEFINITION_ADJUSTED: "\u0110i\u1EC1u ch\u1EC9nh c\xF3 ngh\u0129a l\xE0 c\xF3 \u0111i\u1EC1u g\xEC \u0111\xF3 \u0111\xE3 thay \u0111\u1ED5i \u1EDF \u0111\xE2y, v\xEC v\u1EADy ch\xFAng t\xF4i ph\u1EA3i xem x\xE9t l\u1EA1i y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m n\xE0y.",
  YOUR_COST: "Chi ph\xED c\u1EE7a b\u1EA1n:",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m y t\u1EBF} oral {Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m nha khoa} vision {Y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng th\u1ECB gi\xE1c} pharmacy {Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m nh\xE0 thu\u1ED1c} professional {Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m chuy\xEAn m\xF4n} other {Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m}}",
  FILTER_BY: "L\u1ECDc theo",
  CLAIMS_LIST_EMPTY_HEADING: "Ch\u01B0a c\xF3 g\xEC \u1EDF \u0111\xE2y!",
  CLAIMS_LIST_EMPTY_MESSAGE: "C\xE1c y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m \u0111\xE3 g\u1EEDi s\u1EBD hi\u1EC3n th\u1ECB \u1EDF \u0111\xE2y.",
  CLAIMS_GENERIC_ERROR_HEADING: "\u0110\xE3 c\xF3 l\u1ED7i g\xEC \u0111\xF3",
  CLAIMS_GENERIC_ERROR_MESSAGE: "C\xF3 m\u1ED9t v\u1EA5n \u0111\u1EC1 k\u1EF9 thu\u1EADt \u1EDF ph\xEDa ch\xFAng t\xF4i.{br}Vui l\xF2ng th\u1EED l\u1EA1i trong v\xE0i ph\xFAt n\u1EEFa.",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "Xem nh\u1EEFng g\xEC b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n \u0111\xE3 chi tr\u1EA3 v\xE0 nh\u1EEFng g\xEC b\u1EA1n c\xF3 th\u1EC3 v\u1EABn c\xF2n n\u1EE3 nh\xE0 cung c\u1EA5p. B\u1EA1n c\xF3 th\u1EC3 truy c\u1EADp v\xE0o c\xE1c y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m \u0111\xE3 n\u1ED9p trong v\xF2ng hai n\u0103m t\xEDnh \u0111\u1EBFn ng\xE0y h\xF4m nay.",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "Kh\xF4ng c\xF3 k\u1EBFt qu\u1EA3",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "{filterCount, plural, other {Kh\xF4ng c\xF3 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m n\xE0o kh\u1EDBp v\u1EDBi b\u1ED9 l\u1ECDc b\u1EA1n \u0111\xE3 ch\u1ECDn}}",
  VIEW_MY_PROFILE: "Xem h\u1ED3 s\u01A1 c\u1EE7a t\xF4i",
  PLAN_PROGRESS_COST_COVERAGE: "Ti\u1EBFn \u0111\u1ED9 k\u1EBF ho\u1EA1ch v\xE0 chi ph\xED",
  PLAN_PROGRESS_POP_UP_LABEL: "{title} l\xE0 g\xEC?",
  RECENT_CLAIMS_LIST_EMPTY: "C\xE1c y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m \u0111\u01B0\u1EE3c g\u1EEDi trong v\xF2ng 90 ng\xE0y qua s\u1EBD hi\u1EC3n th\u1ECB t\u1EA1i \u0111\xE2y. B\u1EA1n c\xF3 th\u1EC3 truy c\u1EADp t\u1EA5t c\u1EA3 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m b\u1EB1ng c\xE1ch ch\u1ECDn 'Xem t\u1EA5t c\u1EA3'. ",
  DOWNLOAD_CLAIMS: "T\u1EA3i xu\u1ED1ng y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  PLAIN_SOMETHING_WENT_WRONG: "\u0110\xE3 c\xF3 l\u1ED7i g\xEC \u0111\xF3",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "\u0110\xE3 c\xF3 s\u1EF1 c\u1ED1 khi t\u1EA3i xu\u1ED1ng y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n.",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "Ph\xFAc l\u1EE3i, y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng v\xE0 th\xF4ng tin s\u1EE9c kh\u1ECFe to\xE0n di\u1EC7n c\u1EE7a b\u1EA1n, t\u1EA5t c\u1EA3 \u1EDF c\xF9ng m\u1ED9t n\u01A1i.",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "S\u1EB5n s\xE0ng \u0111\u1EC3 b\u1EAFt \u0111\u1EA7u ch\u01B0a?",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "Duy\u1EC7t th\u01B0 vi\u1EC7n c\u1EE7a ch\xFAng t\xF4i \u0111\u1EC3 t\xECm c\xE1c Ch\u01B0\u01A1ng tr\xECnh v\xE0 Th\u1EED th\xE1ch v\u1EC1 S\u1EE9c kh\u1ECFe ph\xF9 h\u1EE3p v\u1EDBi m\u1EE5c ti\xEAu c\xE1 nh\xE2n c\u1EE7a b\u1EA1n.",
  USER_DROPDOWN_BTN_LABEL: "Menu h\u1ED3 s\u01A1",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "\u0110\u1ED3ng thanh to\xE1n v\xE0 \u0111\u1ED3ng b\u1EA3o hi\u1EC3m l\xE0 g\xEC?",
  ANSWERS_NOT_SAVED: "Ch\xFAng t\xF4i kh\xF4ng th\u1EC3 l\u01B0u c\xE2u tr\u1EA3 l\u1EDDi c\u1EE7a b\u1EA1n ngay l\xFAc n\xE0y. Vui l\xF2ng th\u1EED l\u1EA1i sau.",
  SESSION_ABOUT_TO_TIMEOUT: "B\u1EA1n \u0111\xE3 kh\xF4ng ho\u1EA1t \u0111\u1ED9ng trong m\u1ED9t th\u1EDDi gian v\xE0 phi\xEAn c\u1EE7a b\u1EA1n s\u1EAFp h\u1EBFt th\u1EDDi gian. B\u1EA1n c\xF3 mu\u1ED1n ti\u1EBFp t\u1EE5c phi\xEAn l\xE0m vi\u1EC7c c\u1EE7a m\xECnh kh\xF4ng?",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "B\u1EA1n \u0111\xE3 kh\xF4ng ho\u1EA1t \u0111\u1ED9ng trong m\u1ED9t th\u1EDDi gian v\xE0 phi\xEAn c\u1EE7a b\u1EA1n s\u1EAFp h\u1EBFt th\u1EDDi gian.",
  SESSION_TIMEOUT: "H\u1EBFt th\u1EDDi gian phi\xEAn",
  CONTINUE_SESSION: "Ti\u1EBFp t\u1EE5c phi\xEAn",
  SESSION_LOG_OUT: "\u0110\u0103ng xu\u1EA5t",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "Ch\u1ECDn \u0111\u1ECBa ch\u1EC9 giao h\xE0ng",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "Ai c\u1EA7n th\u1EBB m\u1EDBi?",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "Ch\u1ECDn m\u1ED9t ho\u1EB7c nhi\u1EC1u th\xE0nh vi\xEAn",
  DEFAULT_ADDRESS: "\u0110\u1ECBa ch\u1EC9 m\u1EB7c \u0111\u1ECBnh",
  CHOOSE_NEW_ADDRESS: "Ch\u1ECDn m\u1ED9t \u0111\u1ECBa ch\u1EC9 m\u1EDBi",
  CHOOSE_NEW_ADDRESS_HINT: "Cung c\u1EA5p m\u1ED9t \u0111\u1ECBa ch\u1EC9 g\u1EEDi th\u01B0 kh\xE1c \u0111\u1EC3 nh\u1EADn thi\u1EC7p. \u0110i\u1EC1u n\xE0y s\u1EBD kh\xF4ng thay \u0111\u1ED5i \u0111\u1ECBa ch\u1EC9 m\u1EB7c \u0111\u1ECBnh c\u1EE7a t\xE0i kho\u1EA3n c\u1EE7a b\u1EA1n.",
  CARD_ACTIONS: "H\xE0nh \u0111\u1ED9ng th\u1EBB",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "\u0110\u1EA1t \u0111\u01B0\u1EE3c th\xE0nh t\xEDch khi ho\xE0n th\xE0nh t\u1EA5t c\u1EA3 c\xE1c ho\u1EA1t \u0111\u1ED9ng trong th\u1EED th\xE1ch n\xE0y",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "Ch\u01B0a c\xF3 g\xEC \u1EDF \u0111\xE2y!",
  CHALLENGE_ACTIVITY_UP_NEXT: "Ti\u1EBFp theo",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "H\xE3y quay l\u1EA1i khi th\u1EED th\xE1ch b\u1EAFt \u0111\u1EA7u!",
  CHALLENGE_ACTIVITY_COMPLETED: "Ho\xE0n th\xE0nh",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "C\xE1c ho\u1EA1t \u0111\u1ED9ng b\u1EA1n ho\xE0n th\xE0nh s\u1EBD hi\u1EC3n th\u1ECB \u1EDF \u0111\xE2y.",
  CHALLENGE_ACTIVITY_MISSED: "B\u1ECF l\u1EE1",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "C\xE1c ho\u1EA1t \u0111\u1ED9ng b\u1EA1n b\u1ECF l\u1EE1 s\u1EBD hi\u1EC3n th\u1ECB \u1EDF \u0111\xE2y.",
  CHALLENGE_COMPLETED_TITLE: "V\u1EADy l\xE0 xong!",
  CHALLENGE_COMPLETED_BODY: "Kh\xF4ng c\xF2n ho\u1EA1t \u0111\u1ED9ng n\xE0o \u0111\u1EC3 ho\xE0n th\xE0nh n\u1EEFa. L\xE0m t\u1ED1t l\u1EAFm!",
  ACHIEVEMENT_EARNED: "Th\xE0nh t\xEDch \u0111\u1EA1t \u0111\u01B0\u1EE3c l\u1EC5 k\u1EF7 ni\u1EC7m",
  ACHIEVEMENT_PROGRESS: "Ti\u1EBFn \u0111\u1ED9 \u0111\u1EA1t \u0111\u01B0\u1EE3c",
  ADD_DATA: "Th\xEAm d\u1EEF li\u1EC7u",
  CONFIRM_DETAILS: "X\xE1c nh\u1EADn th\xF4ng tin chi ti\u1EBFt c\u1EE7a b\u1EA1n",
  MEDICAL_ID_CARD: "Th\u1EBB c\u0103n c\u01B0\u1EDBc y t\u1EBF",
  NEW_ADDRESS: "\u0110\u1ECBa ch\u1EC9 giao h\xE0ng m\u1EDBi",
  MESSAGE_CENTER: "Trung t\xE2m Th\xF4ng b\xE1o",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "Tin nh\u1EAFn",
  CHOOSE_NEW_ADDRESS_DESC: "Cung c\u1EA5p m\u1ED9t \u0111\u1ECBa ch\u1EC9 g\u1EEDi th\u01B0 kh\xE1c \u0111\u1EC3 nh\u1EADn thi\u1EC7p. \u0110i\u1EC1u n\xE0y s\u1EBD kh\xF4ng thay \u0111\u1ED5i \u0111\u1ECBa ch\u1EC9 m\u1EB7c \u0111\u1ECBnh c\u1EE7a t\xE0i kho\u1EA3n c\u1EE7a b\u1EA1n. \u0110\u1ECBa ch\u1EC9 g\u1EEDi th\u01B0 ph\u1EA3i \u1EDF trong ph\u1EA1m vi Hoa K\u1EF3.",
  SELECT_STATE: "Ch\u1ECDn m\u1ED9t ti\u1EC3u bang",
  REQUIRED_FIELD: "{field} l\xE0 m\u1ED9t tr\u01B0\u1EDDng b\u1EAFt bu\u1ED9c",
  CLAIMS_YOUR_COST: "Chi ph\xED c\u1EE7a b\u1EA1n:",
  CLAIMS_PROCESSING_DESCRIPTION: "Ch\xFAng t\xF4i v\u1EABn \u0111ang x\u1EED l\xFD y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m n\xE0y. B\u1EA1n s\u1EBD \u0111\u01B0\u1EE3c th\xF4ng b\xE1o khi b\xE0i vi\u1EBFt \u0111\xE3 s\u1EB5n s\xE0ng \u0111\u1EC3 b\u1EA1n xem x\xE9t.",
  DOWNLOAD_BENEFITS_EXPLANATION: "T\u1EA3i xu\u1ED1ng Gi\u1EA3i th\xEDch v\u1EC1 Quy\u1EC1n l\u1EE3i",
  DATA_COLLECTED: "D\u1EEF li\u1EC7u \u0111\u01B0\u1EE3c thu th\u1EADp {date}",
  MEMBER_CARD_ON_ITS_WAY: "Th\u1EBB ID th\xE0nh vi\xEAn m\u1EDBi c\u1EE7a b\u1EA1n \u0111ang tr\xEAn \u0111\u01B0\u1EDDng \u0111\u1EBFn.",
  MEMBER_CARD_ARRIVAL: "Th\u1EBB ID th\xE0nh vi\xEAn m\u1EDBi s\u1EBD \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn trong kho\u1EA3ng 10 ng\xE0y l\xE0m vi\u1EC7c.",
  TRY_LATER: "Vui l\xF2ng th\u1EED l\u1EA1i sau.",
  RESUBMIT: "G\u1EEDi l\u1EA1i",
  ORDER_NEW_CARD_HEADING: "\u0110\u1EB7t mua th\u1EBB m\u1EDBi",
  CANCEL_ORDER_NEW_CARD_TITLE: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n h\u1EE7y \u0111\u01A1n h\xE0ng c\u1EE7a m\xECnh kh\xF4ng?",
  CANCEL_ORDER_NEW_CARD_DESC: "B\u1EA1n lu\xF4n c\xF3 th\u1EC3 quay l\u1EA1i sau.",
  CANCEL_ORDER: "H\u1EE7y \u0111\u01A1n h\xE0ng",
  STEP: "B\u01B0\u1EDBc",
  ORDER_CARD: "Th\u1EBB \u0111\u1EB7t h\xE0ng",
  PLEASE_MAKE_A_SELECTION: "Vui l\xF2ng l\u1EF1a ch\u1ECDn",
  TOTAL_COST_OVERVIEW: "T\u1ED5ng quan v\u1EC1 t\u1ED5ng chi ph\xED",
  TOTAL_COST_DESCRIPTION: "T\u1ED5ng chi ph\xED t\u1EEB c\xE1c d\u1ECBch v\u1EE5 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n.",
  SUPPORT: "H\u1ED7 tr\u1EE3",
  GET_SUPPORT: "Nh\u1EADn h\u1ED7 tr\u1EE3",
  MESSAGE_WITH_A_MEMBER: "Li\xEAn h\u1EC7 v\u1EDBi lu\u1EADt s\u01B0 th\xE0nh vi\xEAn n\u1EBFu c\xF3 th\u1EAFc m\u1EAFc v\u1EC1 y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n.",
  CLAIMS_SERVICE_DETAILS_HEADING: "Chi ti\u1EBFt d\u1ECBch v\u1EE5",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "T\xE0i nguy\xEAn b\u1ED5 sung",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "Ph\xE2n t\xEDch chi ti\u1EBFt t\u1EEBng d\u1ECBch v\u1EE5 b\u1EA1n nh\u1EADn \u0111\u01B0\u1EE3c v\xE0 chi ph\xED li\xEAn quan \u0111\u1EBFn ch\xFAng.",
  CLAIMS_SERVICE_DETAILS_SERVICE: "D\u1ECBch v\u1EE5",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "T\xEAn c\u1EE7a Rx",
  CLAIMS_SERVICE_DETAILS_DATE: "Ng\xE0y d\u1ECBch v\u1EE5",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "M\xE3 th\u1EE7 t\u1EE5c",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "S\u1ED1 Rx",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "Chi ph\xED c\u1EE7a b\u1EA1n:",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "Chi ti\u1EBFt chi ph\xED",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "Hi\u1EC3n th\u1ECB chi ti\u1EBFt chi ph\xED",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "\u1EA8n chi ti\u1EBFt chi ph\xED",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "Ph\xE2n t\xEDch chi ph\xED c\u1EE7a b\u1EA1n",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "S\u1ED1 ti\u1EC1n d\u01B0\u1EDBi \u0111\xE2y s\u1EBD \u0111\u01B0\u1EE3c chuy\u1EC3n v\xE0o ti\u1EBFn \u0111\u1ED9 k\u1EBF ho\u1EA1ch c\u1EE7a b\u1EA1n",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "Ch\u1ECDn m\u1ED9t ho\u1EB7c nhi\u1EC1u th\xE0nh vi\xEAn \u0111\u1EC3 ti\u1EBFp t\u1EE5c",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "Tr\u01B0\u1EDDng ch\u1EE9a c\xE1c k\xFD t\u1EF1 kh\xF4ng h\u1EE3p l\u1EC7",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "Nh\xE0 cung c\u1EA5p b\u1ECB t\xEDnh ph\xED",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "B\u1EA3o hi\u1EC3m kh\xE1c \u0111\xE3 thanh to\xE1n",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "Gi\u1EA3m gi\xE1 cho th\xE0nh vi\xEAn c\u1EE7a b\u1EA1n",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "K\u1EBF ho\u1EA1ch c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\xE0m ph\xE1n v\u1EDBi nh\xE0 cung c\u1EA5p \u0111\u1EC3 gi\u1EA3m gi\xE1 cho b\u1EA1n.",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "Gi\u1EA3i th\xEDch gi\u1EA3m gi\xE1 th\xE0nh vi\xEAn c\u1EE7a b\u1EA1n",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "S\u1ED1 ti\u1EC1n b\u1EA3o hi\u1EC3m kh\xE1c c\u1EE7a b\u1EA1n tr\u1EA3.",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: "Gi\u1EA3i th\xEDch chi tr\u1EA3 b\u1EA3o hi\u1EC3m kh\xE1c",
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {G\xF3i c\u01B0\u1EDBc c\u1EE7a b\u1EA1n \u0111\xE3 thanh to\xE1n v\xE0o {paymentDate}} undefined {G\xF3i c\u1EE7a b\u1EA1n \u0111\xE3 thanh to\xE1n}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "S\u1ED1 ti\u1EC1n m\xE0 ch\u01B0\u01A1ng tr\xECnh c\u1EE7a b\u1EA1n chi tr\u1EA3 cho c\xE1c d\u1ECBch v\u1EE5 ch\u0103m s\xF3c s\u1EE9c kh\u1ECFe.",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: "Gi\u1EA3i th\xEDch v\u1EC1 k\u1EBF ho\u1EA1ch tr\u1EA3 ti\u1EC1n c\u1EE7a b\u1EA1n",
  CLAIMS_COST_DETAILS_YOUR_COST: "Chi ph\xED c\u1EE7a b\u1EA1n:",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "Ghi ch\xFA d\u1ECBch v\u1EE5",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "Trong m\u1EA1ng l\u01B0\u1EDBi",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "Ngo\xE0i m\u1EA1ng l\u01B0\u1EDBi",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "Ch\u1ECDn m\u1ED9t t\xF9y ch\u1ECDn \u0111\u1ECBa ch\u1EC9 \u0111\u1EC3 ti\u1EBFp t\u1EE5c",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "Th\xE0nh ph\u1ED1 kh\xF4ng th\u1EC3 ch\u1EE9a s\u1ED1",
  MESSAGE_CLOSED_TEXT: "Th\xF4ng \u0111i\u1EC7p n\xE0y \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi \u0111i.",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "Xem t\u1ED5ng quan v\u1EC1 chi ph\xED t\xEDch l\u0169y, chia s\u1EBB chi ph\xED v\xE0 h\u01A1n th\u1EBF n\u1EEFa.",
  VIEW_PLAN_PROGRESS: "Xem ti\u1EBFn \u0111\u1ED9 k\u1EBF ho\u1EA1ch",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "B\u1EA1n s\u1EBD nh\u1EADn \u0111\u01B0\u1EE3c h\xF3a \u0111\u01A1n li\xEAn quan \u0111\u1EBFn y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m n\xE0y t\u1EEB nh\xE0 cung c\u1EA5p c\u1EE7a b\u1EA1n.",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi t\u1EA3i xu\u1ED1ng l\u1EDDi gi\u1EA3i th\xEDch v\u1EC1 quy\u1EC1n l\u1EE3i c\u1EE7a b\u1EA1n.",
  END_CHAT_MODAL_DESCRIPTION: "Ti\u1EBFp t\u1EE5c s\u1EBD k\u1EBFt th\xFAc cu\u1ED9c tr\xF2 chuy\u1EC7n. B\u1EA1n s\u1EBD kh\xF4ng th\u1EC3 quay l\u1EA1i cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y trong t\u01B0\u01A1ng lai.",
  RETURN_TO_CHAT: "Quay l\u1EA1i \u1EE9ng d\u1EE5ng Tr\xF2 chuy\u1EC7n",
  CHATS_IN_CONVERSATION: "Tr\xF2 chuy\u1EC7n trong cu\u1ED9c tr\xF2 chuy\u1EC7n",
  LIVE_CHAT_CHARACTER_LIMIT: "\u0110\xE3 \u0111\u1EA1t \u0111\u1EBFn gi\u1EDBi h\u1EA1n k\xFD t\u1EF1 ({characterLimit})",
  CHAT_MESSAGE_INPUT: "Nh\u1EADp tin nh\u1EAFn tr\xF2 chuy\u1EC7n",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {KH\u1EA4U TR\u1EEA} non_covered {Kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o hi\u1EC3m} copayment {\u0110\u1ED3ng thanh to\xE1n} coinsurance {\u0110\u1ED3ng b\u1EA3o hi\u1EC3m} may_be_billed {B\u1EA1n c\xF3 th\u1EC3 b\u1ECB t\xEDnh ph\xED} amount_exceeding_maximum {S\u1ED1 ti\u1EC1n v\u01B0\u1EE3t qu\xE1 t\u1ED1i \u0111a} additional_member_liability {Tr\xE1ch nhi\u1EC7m b\u1ED5 sung c\u1EE7a th\xE0nh vi\xEAn} special_processing {X\u1EED l\xFD \u0111\u1EB7c bi\u1EC7t} paid_to_member {Tr\u1EA3 ti\u1EC1n cho th\xE0nh vi\xEAn} plan_paid_to_you {K\u1EBF ho\u1EA1ch tr\u1EA3 ti\u1EC1n cho b\u1EA1n} other {Kh\xE1c}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "Tin nh\u1EAFn m\u1EDBi",
  QUESTIONNAIRE_FLOW_CLOSE: "\u0110\xF3ng",
  QUESTIONNAIRE_FLOW_GO_BACK: "Quay l\u1EA1i",
  QUESTIONNAIRE_FLOW_CONTINUE: "Ti\u1EBFp t\u1EE5c",
  QUESTIONNAIRE_FLOW_RETRY: "Th\u1EED l\u1EA1i",
  CLEAR_ALL: "X\xF3a t\u1EA5t c\u1EA3",
  HEALTH_REPORT_PAGE_HEADING: "B\xE1o c\xE1o s\u1EE9c kh\u1ECFe c\u1EE7a b\u1EA1n",
  USERNAME_HAS_JOINED_THE_CHAT: "{username} \u0111\xE3 tham gia tr\xF2 chuy\u1EC7n",
  USERNAME_HAS_LEFT_THE_CHAT: "{username} \u0111\xE3 r\u1EDDi kh\u1ECFi cu\u1ED9c tr\xF2 chuy\u1EC7n",
  PERVIOUS_MONTH: "N\xFAt Th\xE1ng tr\u01B0\u1EDBc",
  NEXT_MONTH: "N\xFAt Th\xE1ng t\u1EDBi",
  CHAT_CLOSED: "Cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y \u0111\xE3 b\u1ECB \u0111\xF3ng.",
  CONNECTING_YOU_WITH_AN_AGENT: "K\u1EBFt n\u1ED1i b\u1EA1n v\u1EDBi m\u1ED9t nh\xE2n vi\xEAn...",
  LAST_DAY_TO_USE_BALANCE: "Ng\xE0y cu\u1ED1i c\xF9ng \u0111\u1EC3 s\u1EED d\u1EE5ng s\u1ED1 d\u01B0",
  LAST_DAY_TO_SUBMIT_CLAIMS: "Ng\xE0y cu\u1ED1i c\xF9ng \u0111\u1EC3 n\u1ED9p y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "\u0110\xE3 c\xF3 l\u1ED7i g\xEC \u0111\xF3",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1, vui l\xF2ng th\u1EED l\u1EA1i",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "\u0110\xE3 c\xF3 l\u1ED7i g\xEC \u0111\xF3",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1, vui l\xF2ng th\u1EED l\u1EA1i",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "\u0110\xF3ng",
  LIVE_CHAT_LANDING_PAGE_TITLE: "Tr\xF2 chuy\u1EC7n",
  LIVE_CHAT_DELIVERY_FAILURE: "Kh\xF4ng \u0111\u01B0\u1EE3c giao",
  LIVE_CHAT_RESEND_MESSAGE: "G\u1EEDi l\u1EA1i tin nh\u1EAFn",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "Tin nh\u1EAFn kh\xF4ng g\u1EEDi \u0111\u01B0\u1EE3c",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "R\u1EA5t ti\u1EBFc, c\xF3 v\u1EBB nh\u01B0 tin nh\u1EAFn c\u1EE7a b\u1EA1n kh\xF4ng \u0111\u1EBFn \u0111\u01B0\u1EE3c nh\xF3m c\u1EE7a ch\xFAng t\xF4i.",
  VIEW_TOOLTIP: "xem {tooltipLabel} ch\xFA gi\u1EA3i c\xF4ng c\u1EE5",
  NO_DATA_SOURCES: "Kh\xF4ng c\xF3 ngu\u1ED3n d\u1EEF li\u1EC7u",
  MAX_VALUE_ERROR_MESSAGE: "Vui l\xF2ng nh\u1EADp m\u1ED9t gi\xE1 tr\u1ECB nh\u1ECF h\u01A1n ho\u1EB7c b\u1EB1ng {max}",
  MIN_VALUE_ERROR_MESSAGE: "Vui l\xF2ng nh\u1EADp m\u1ED9t gi\xE1 tr\u1ECB l\u1EDBn h\u01A1n ho\u1EB7c b\u1EB1ng {min}",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "Vui l\xF2ng ch\u1ECDn m\u1ED9t gi\xE1 tr\u1ECB",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "Ng\xE0y b\u1EAFt \u0111\u1EA7u ph\u1EA3i s\u1EDBm h\u01A1n ng\xE0y k\u1EBFt th\xFAc",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "Th\u1EDDi gian kh\xF4ng \u0111\u01B0\u1EE3c v\u01B0\u1EE3t qu\xE1 24 gi\u1EDD",
  NO_FUTURE_DATES: "Ng\xE0y kh\xF4ng th\u1EC3 c\xF3 trong t\u01B0\u01A1ng lai",
  CLAIMS_REMOVE_FILTER: "X\xF3a b\u1ED9 l\u1ECDc {label}.",
  REMOVE_X_FILTER: "X\xF3a b\u1ED9 l\u1ECDc {label}.",
  WALLET_BENEFIT_STATUS_PENDING: "Ch\u1EDD gi\u1EA3i quy\u1EBFt",
  WALLET_BENEFIT_STATUS_ACTIVE: "T\xEDch c\u1EF1c",
  WALLET_BENEFIT_STATUS_PAST: "Qu\xE1 kh\u1EE9",
  CLAIMS_CLAIMS_LIST_HEADING: "Y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m c\u1EE7a b\u1EA1n",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "Ch\u01B0a c\xF3 g\xEC \u1EDF \u0111\xE2y!",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "Nh\u1EEFng l\u1EE3i \xEDch \u0111ang \xE1p d\u1EE5ng s\u1EBD hi\u1EC3n th\u1ECB \u1EDF \u0111\xE2y.",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "Ph\xFAc l\u1EE3i",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "Quy\u1EC1n l\u1EE3i - Th\u1EBB ID",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "Y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "Chi ti\u1EBFt y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "Chi ti\u1EBFt y\xEAu c\u1EA7u b\u1EA3o hi\u1EC3m - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "Ch\u1EDD gi\u1EA3i quy\u1EBFt",
  CLAIMS_STATUS_PENDING: "Ch\u1EDD gi\u1EA3i quy\u1EBFt",
  CSE_END_IMPERSONATION_MODE: "K\u1EBFt th\xFAc ch\u1EBF \u0111\u1ED9 m\u1EA1o danh",
  CSE_RESTRICTED_ACTION: "H\xE0nh \u0111\u1ED9ng b\u1ECB h\u1EA1n ch\u1EBF",
  CSE_RESTRICTED_ACTION_MESSAGE: "Trong ch\u1EBF \u0111\u1ED9 m\xF4 ph\u1ECFng ng\u01B0\u1EDDi d\xF9ng, b\u1EA1n ch\u1EC9 c\xF3 th\u1EC3 xem c\u1ED5ng th\xF4ng tin c\u1EE7a th\xE0nh vi\xEAn. Kh\xF4ng \u0111\u01B0\u1EE3c ph\xE9p th\u1EF1c hi\u1EC7n h\xE0nh \u0111\u1ED9ng thay m\u1EB7t cho th\xE0nh vi\xEAn.",
  CSE_RESTRICTED_VIEW_MESSAGE: "Qu\xFD v\u1ECB kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp v\xE0o ph\u1EA7n n\xE0y.",
  CSE_IMPERSONATION_MASONRY_HEADER: "N\u1ED9i dung b\u1ECB h\u1EA1n ch\u1EBF cho nh\xE2n vi\xEAn h\u1ED7 tr\u1EE3",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "Qu\xFD v\u1ECB c\xF3 th\u1EC3 kh\xF4ng c\xF3 quy\u1EC1n xem m\u1ED9t s\u1ED1 ti\u1EC7n \xEDch ho\u1EB7c n\u1ED9i dung nh\u1EA5t \u0111\u1ECBnh.",
  CSE_IMPERSONATION_BANNER_TEXT: "Ch\u1EBF \u0111\u1ED9 ch\u1EC9 xem cho {memberName}.",
  CLAIMS_SUMMARY_PAGE_HEADING: "Y\xEAu c\u1EA7u b\u1ED3i th\u01B0\u1EDDng",
  HEALTH_INSURANCE_GLOSSARY: "T\u1EEB \u0111i\u1EC3n b\u1EA3o hi\u1EC3m y t\u1EBF",
  CLAIMS_FILTER_LOADING_TEXT: "C\xE1c tab v\xE0 b\u1ED9 l\u1ECDc \u0111ang t\u1EA3i",
  ANDROID_HEALTH_CONNECT: "K\u1EBFt n\u1ED1i s\u1EE9c kh\u1ECFe",
  GET_CARE_SERVICE_CARD_CTA: "Xem d\u1ECBch v\u1EE5",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "B\u01B0\u1EDBc ti\u1EBFp theo",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "B\u01B0\u1EDBc tr\u01B0\u1EDBc \u0111\xF3",
  HEIGHT: "Chi\u1EC1u cao",
  WEIGHT: "C\xE2n n\u1EB7ng"
};

// src/strings/zh.json
var zh_default = {
  COMPLETED_ACTIVITIES: "\u5DF2\u5B8C\u6210\u7684\u6D3B\u52A8",
  AVAILABLE: "\u53EF\u7528",
  SEE_MORE_ACTIVITIES: "\u67E5\u770B\u66F4\u591A",
  SEE_MORE_COMPLETED_ACTIVITIES_ARIA_LABEL: "\u67E5\u770B\u66F4\u591A\u5DF2\u5B8C\u6210\u7684\u6D3B\u52A8",
  COMPLETED_ACTIVITIES_EMPTY_STATE: "\u60A8\u5B8C\u6210\u540E\u7684\u6D3B\u52A8\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\uFF01",
  MISSED_ACTIVITIES: "\u9519\u8FC7\u7684\u6D3B\u52A8",
  SEE_MORE_MISSED_ACTIVITIES_ARIA_LABEL: "\u67E5\u770B\u66F4\u591A\u9519\u8FC7\u7684\u6D3B\u52A8",
  MISSED_ACTIVITIES_EMPTY_STATE: "\u60A8\u9519\u8FC7\u7684\u6D3B\u52A8\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\uFF01",
  DISPLAYING_ACTIVITIES_FROM_DATE: "\u663E\u793A\u81EA {date} \u4EE5\u6765\u7684\u6D3B\u52A8",
  STANDALONE_ACTIVITIES: "\u72EC\u7ACB\u6D3B\u52A8",
  STANDALONE_ACTIVITIES_EMPTY_STATE: "\u8FD9\u91CC\u6682\u65F6\u8FD8\u6CA1\u6709\u4EFB\u4F55\u4E8B\u60C5\u53EF\u505A\uFF01\u8BF7\u665A\u70B9\u518D\u6765\u3002",
  VIEW_ALL_STANDALONE_ACTIVITIES: "\u67E5\u770B\u6240\u6709\u72EC\u7ACB\u6D3B\u52A8",
  MODULE_TEMPLATE_BUTTON: "\u6A21\u5757\u6A21\u677F\u6309\u94AE",
  ACHIEVEMENTS: "\u6210\u5C31",
  VIEW_ACHIEVEMENTS: "\u67E5\u770B\u6210\u5C31",
  DEPENDENT_NOTIFICATION_MESSAGE: "\u53EF\u4EE5\u9080\u8BF7\u7684\u53D7\u629A\u517B\u4EBA",
  DEPENDENT_COVERED_BY_HSA_ONLY: "\u6CE8\u610F\uFF1A\u8BE5\u53D7\u629A\u517B\u4EBA\u4EC5\u7531\u60A8\u7684\u5065\u5EB7\u652F\u51FA\u8D26\u6237\u627F\u4FDD\u3002",
  WALLET_LANDING_NAV_LABEL: "\u94B1\u5305\u5BFC\u822A",
  VIEW_CARD_FOR_DEPENDENT: "\u67E5\u770B {dependentName} \u7684\u5361\u7247",
  LEARN_MORE_LEAGUE_FAMILY: "\u5173\u4E8E\u60A8\u7684\u53D7\u629A\u517B\u4EBA\u7684 League Family",
  GO_TO_LIFEMARKET: "\u524D\u5F80 Lifemarket \u67E5\u770B\u8BA2\u5355\u7F16\u53F7 {invoiceId}",
  GO_TO_CLAIM: "\u67E5\u770B{title}\u7684\u7D22\u8D54\u8BE6\u60C5",
  GO_TO_RECEIPT: "\u67E5\u770B{title}\u7684\u6536\u636E",
  LINK_TO_TRANSACTION_DETAILS: "\u8BE6\u60C5\u94FE\u63A5",
  VIEW_CLAIM_LABEL_HIDDEN: "\u67E5\u770B {vendor} \u7D22\u8D54 {referenceId}",
  QLE_LINK_LABEL: "\u8BE6\u7EC6\u4E86\u89E3\u7B26\u5408\u6761\u4EF6\u7684\u4EBA\u751F\u53D8\u6545",
  PLEASE_CALL: "\u8BF7\u81F4\u7535{phoneNumber}",
  BROWSE_FILES: "\u6D4F\u89C8\u6587\u4EF6",
  TOTAL_BALANCE: "\u603B\u4F59\u989D",
  LIFEMARKET: "Lifemarket",
  ABOUT: "\u5173\u4E8E",
  LEAGUE_CREDIT_DESCRIPTION_1: "\u60A8\u53EF\u4EE5\u5728 {lifemarketLink} \u4E2D\u82B1\u8D39 League \u79EF\u5206\u5151\u6362\u5404\u79CD\u751F\u6D3B\u65B9\u5F0F\u548C\u5065\u5EB7\u4EA7\u54C1\u53CA\u670D\u52A1\u3002\u53EA\u6709\u6807\u6709\u201CLeague \u5408\u4F5C\u4F19\u4F34\u63D0\u4F9B\u201D\u5B57\u6837\u7684\u4EA7\u54C1\u548C\u670D\u52A1\u65E0\u6CD5\u4F7F\u7528 League \u79EF\u5206\u5151\u6362\u3002",
  LEAGUE_CREDIT_DESCRIPTION_2: "\u8981\u4F7F\u7528\u60A8\u7684 League \u79EF\u5206\uFF0C\u8BF7\u5728 Lifemarket \u7ED3\u8D26\u65F6\u4ECE\u4ED8\u6B3E\u9009\u9879\u5217\u8868\u4E2D\u9009\u62E9\u201CLeague \u79EF\u5206\u201D\u3002{learnMoreLink}",
  DID_YOU_GET_A_PROMO_CODE: "\u60A8\u6536\u5230\u4FC3\u9500\u4EE3\u7801\u4E86\u5417\uFF1F",
  DID_YOU_GET_A_PROMO_CODE_DESCRIPTION: "\u5982\u679C\u5DF2\u7ECF\u6536\u5230\u4E86\u5546\u5E97\u79EF\u5206\u6216\u4FC3\u9500\u4EE3\u7801\uFF0C\u53EF\u4EE5\u5728\u8FD9\u91CC\u8F93\u5165\u83B7\u53D6\u79EF\u5206\u3002\u8F93\u5165\u540E\uFF0C\u79EF\u5206\u5373\u4F1A\u81EA\u52A8\u5E94\u7528\u5230\u60A8\u7684\u8D26\u6237\u3002",
  GET_CARE: "\u83B7\u5F97\u62A4\u7406",
  DISCLAIMER: "\u514D\u8D23\u58F0\u660E",
  JOINED: "\u5DF2\u52A0\u5165",
  ROLLED_OVER: "\u5DF2\u6EDA\u52A8\u9F20\u6807",
  NEED_MORE_INFO_SINGLE_CLAIM: "\u60A8\u9700\u8981\u63D0\u4F9B\u66F4\u591A\u4FE1\u606F\uFF0C\u6211\u4EEC\u624D\u80FD\u5904\u7406\u60A8\u7684\u7D22\u8D54\u3002\u8BF7\u67E5\u4E0B\u4EE5\u4E0B\u8BE6\u60C5\u3002",
  NEED_MORE_INFO_MULTIPLE_CLAIMS: "\u60A8\u9700\u8981\u63D0\u4F9B\u66F4\u591A\u4FE1\u606F\uFF0C\u6211\u4EEC\u624D\u80FD\u5904\u7406\u60A8\u7684\u7D22\u8D54\u3002\u8BF7\u67E5\u4E0B\u4EE5\u4E0B\u8BE6\u60C5\u3002",
  RECENT_CLAIMS: "\u6700\u8FD1\u7684\u7D22\u8D54",
  IF_YOU_HAVE_AN_URGENT_CLAIM_PLEASE_CONTACT_CUSTOMER_SUPPORT: "\u5982\u679C\u60A8\u6709\u7D27\u6025\u7D22\u8D54\uFF0C\u8BF7\u8054\u7CFB<a>\u5BA2\u6237\u652F\u6301\u90E8\u95E8</a>\u3002",
  ABOUT_THIS_BENEFIT: "\u5173\u4E8E\u8FD9\u9879\u798F\u5229",
  ABOUT_THIS_BENEFIT_DESCRIPTION: "\u8FD9\u9879\u798F\u5229\u7531\u60A8\u7684\u96C7\u4E3B\u4E3A\u60A8\u63D0\u4F9B\u3002\u5982\u6709\u7591\u95EE\u6216\u9700\u8981\u4E86\u89E3\u8BE6\u7EC6\u4FE1\u606F\uFF0C\u8BF7\u8054\u7CFB\u60A8\u7684\u4EBA\u529B\u8D44\u6E90\u90E8\u95E8\u7BA1\u7406\u5458\u3002",
  ID_LIKE_TO_DOTDOTDOT: "\u6211\u60F3\u8981\u2026\u2026",
  HAS_DEPENDENTS_DESCRIPTION: "\u60A8\u53EF\u4EE5\u4EAB\u53D7\u8FD9\u9879\u798F\u5229\u7684\u53D7\u629A\u517B\u4EBA\u7684\u540D\u5355\u5982\u4E0B\u6240\u793A\u3002\u5982\u679C\u60A8\u8FD1\u671F\u7ECF\u5386\u4E86\u4EBA\u751F\u53D8\u6545\uFF0C\u5219\u53EF\u80FD\u6709\u8D44\u683C\u66F4\u6539\u60A8\u7684\u53D7\u629A\u517B\u4EBA\u3002\u70B9\u51FB\u4E0B\u65B9\u6309\u94AE\uFF0C\u53EA\u9700\u51E0\u6B65\u5373\u53EF\u67E5\u770B\u60A8\u662F\u5426\u7B26\u5408\u6761\u4EF6\u3002",
  HAS_NO_DEPENDENTS_DESCRIPTION: "\u60A8\u6CA1\u6709\u4EFB\u4F55\u4E0E\u8FD9\u9879\u798F\u5229\u5173\u8054\u7684\u53D7\u629A\u517B\u4EBA\u3002\u5982\u679C\u60A8\u8FD1\u671F\u7ECF\u5386\u4E86\u4EBA\u751F\u53D8\u6545\uFF0C\u5219\u53EF\u80FD\u6709\u8D44\u683C\u6DFB\u52A0\u6216\u66F4\u6539\u60A8\u7684\u53D7\u629A\u517B\u4EBA\u3002\u70B9\u51FB\u4E0B\u65B9\u6309\u94AE\uFF0C\u53EA\u9700\u51E0\u6B65\u5373\u53EF\u67E5\u770B\u60A8\u662F\u5426\u7B26\u5408\u6761\u4EF6\u3002",
  ADD_A_DEPENDENT_CAPITAL: "\u6DFB\u52A0\u53D7\u629A\u517B\u4EBA",
  WALLET_FEEDBACK_TITLE: "\u5E2E\u52A9\u6211\u4EEC\u6539\u8FDB\u201C\u94B1\u5305\u201D\u7684\u4F53\u9A8C",
  WALLET_FEEDBACK_SUBTITLE_BENEFIT: "\u4E3A\u60A8\u7684\u201C{benefitName}\u201D\u798F\u5229\u63D0\u4F9B\u7684\u4FE1\u606F\u5BF9\u60A8\u7684\u5E2E\u52A9\u6709\u591A\u5927\uFF1F",
  WALLET_FEEDBACK_SUBTITLE_OTHER: "{cardName}\u6240\u63D0\u4F9B\u7684\u4FE1\u606F\u5BF9\u60A8\u7684\u5E2E\u52A9\u6709\u591A\u5927\uFF1F",
  GO_BACK: "\u8FD4\u56DE",
  WALLET_LANDING_TITLE: "\u60A8\u7684\u798F\u5229\u548C\u4FDD\u9669\u8303\u56F4\u4FE1\u606F\u5168\u90E8\u4FDD\u5B58\u5728\u4E00\u5904\u3002",
  WALLET_LANDING_BODY: "\u60A8\u7684\u96C7\u4E3B\u798F\u5229\u3001\u8BA1\u5212\u548C\u4FDD\u9669\u8303\u56F4\u8BE6\u60C5\u5168\u90E8\u6C47\u603B\u5728\u60A8\u7684\u6570\u5B57\u94B1\u5305\u4E2D\u3002\u53EA\u9700\u70B9\u51FB\u4E00\u4E0B\u5373\u53EF\u5FEB\u901F\u627E\u5230\u6240\u9700\u4FE1\u606F\u3002",
  POLICY_ACTIVE: "\u5DF2\u6FC0\u6D3B\u653F\u7B56",
  REMAINING_BALANCE: "\u5269\u4F59\u4F59\u989D",
  CONTACT_CUSTOMER_CARE: "\u8054\u7CFB\u5BA2\u6237\u670D\u52A1",
  UH_OH_LOOKS_LIKE_SOMETHING_WENT_WRONG: "\u5443\uFF0C\u597D\u50CF\u51FA\u4E86\u95EE\u9898",
  WERE_HAVING_TROUBLE: "\u52A0\u8F7D\u6B64\u4FE1\u606F\u65F6\u9047\u5230\u95EE\u9898\uFF0C\u8BF7\u5C1D\u8BD5\u5237\u65B0\u9875\u9762\u3002\u5982\u679C\u95EE\u9898\u4F9D\u7136\u5B58\u5728\uFF0C\u8BF7\u8054\u7CFB {custCareLink}\u3002",
  IN_NETWORK: "\u7F51\u7EDC\u5185",
  OUT_OF_NETWORK: "\u7F51\u7EDC\u5916",
  PROGRAMS: "\u8BA1\u5212",
  PROGRAM_CREATED_BY: "\u8BA1\u5212\u521B\u5EFA\u8005",
  TOP_FOCUS_AREA: "\u91CD\u70B9\u5173\u6CE8\u9886\u57DF",
  DIMENSION_OF_HEALTH: "\u5065\u5EB7\u7EF4\u5EA6",
  READING_MORE_ABOUT_DIMENSIONS: "\u60F3\u8981\u8BE6\u7EC6\u4E86\u89E3\u5404\u4E2A\u7EF4\u5EA6\u5982\u4F55\u5F71\u54CD\u60A8\u7684\u6574\u4F53\u5065\u5EB7\uFF1F",
  READ_OUR_ARTICLE: "\u9605\u8BFB\u6211\u4EEC\u7684\u6587\u7AE0",
  HEALTH_PROGRAMS_FOR_YOU: "\u4E3A\u60A8\u7CBE\u9009\u7684\u5065\u5EB7\u8BA1\u5212",
  COMPLETE_ACTIVITIES: "\u5B8C\u6210\u6D3B\u52A8\u5373\u53EF\u5B9E\u73B0\u60A8\u7684\u5065\u5EB7\u76EE\u6807\u3002\u6839\u636E\u60A8\u7684\u5065\u5EB7\u6863\u6848\u63A8\u8350\u3002",
  FT: "\u82F1\u5C3A",
  CM: "\u5398\u7C73",
  IN: "\u5728",
  LB: "\u78C5",
  KG: "\u5343\u514B",
  LETS_BEGIN: "\u6211\u4EEC\u5F00\u59CB\u5427\uFF01",
  KEEP_GOING: "\u7EE7\u7EED\u524D\u8FDB",
  SKIP: "\u8DF3\u8FC7",
  HP_ONBOARDING_SUBMIT: "\u8BA9\u6211\u4EEC\u5F00\u59CB\u5427",
  FOR_PROVIDERS_AND_PHARMACISTS: "\u9488\u5BF9\u533B\u7597\u63D0\u4F9B\u8005/\u836F\u5242\u5E08",
  WEVE_PICKED_UP: "\u6211\u4EEC\u4ECE\u60A8\u4E0A\u6B21\u4E2D\u65AD\u7684\u5730\u65B9\u7EE7\u7EED\u3002",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NUMBER: "\u53EA\u9700\u56DE\u7B54\u51E0\u4E2A\u95EE\u9898\u5373\u53EF\u83B7\u5F97 {points} \u70B9\u79EF\u5206\u3002",
  YOURE_QUESTIONS_AWAY_WITHOUT_QUESTIONS_NOR_POINTS: "\u53EA\u9700\u56DE\u7B54\u51E0\u4E2A\u95EE\u9898\u5373\u53EF\u5B8C\u6210\u672C\u6B21\u8BC4\u4F30\u3002",
  PRESS_ENTER: "\u6309 Enter \u952E",
  SELECT_ALL_THAT_APPLY: "\u9009\u62E9\u6240\u6709\u9002\u7528\u9879",
  SELECT_UP_TO: "\u6700\u591A\u9009\u62E9 {number}",
  AMOUNT_QUESTIONS: "{amount} \u4E2A\u95EE\u9898",
  QUESTION_PROGRESS: "\u7B2C {questionNumber} \u9898/\u5171 {totalQuestions} \u9898",
  COMPLETE: "\u5B8C\u6210",
  VIEW_MORE: "\u67E5\u770B\u66F4\u591A",
  DOWNLOAD_APP_STORE: "\u5728 App Store \u4E0B\u8F7D",
  DOWNLOAD_PLAY_STORE: "\u5728 Google Play \u83B7\u53D6",
  WE_RE_SORRY_REQUESTED_PAGE_EXPIRED_OR_NOT_VALID: "\u5F88\u62B1\u6B49\uFF0C\u60A8\u8BF7\u6C42\u7684\u9875\u9762\u5DF2\u8FC7\u671F\u6216\u4E0D\u518D\u6709\u6548\u3002",
  EARNED_X_TIMES: "\u5DF2\u83B7\u5F97 {amount} \u500D",
  EARNED: "\u5DF2\u83B7\u5F97",
  YOUVE_EARNED_TOTAL: "\u60A8\u4E00\u5171\u83B7\u5F97\u4E86",
  YOUVE_EARNED_GRAND_TOTAL: "\u60A8\u4E00\u5171\u83B7\u5F97\u4E86",
  YOUVE_EARNED_ACHIEVEMENT_BADGE: "\u60A8\u83B7\u5F97\u4E86\u4E00\u679A\u6210\u5C31\u5FBD\u7AE0",
  CONGRATS_COMPLETED: "\u606D\u559C\uFF01\u60A8\u5DF2\u5B8C\u6210",
  VIEW_MY_HEALTH_DASHBOARD: "\u67E5\u770B\u201C\u6211\u7684\u5065\u5EB7\u4EEA\u8868\u677F\u201D",
  LATEST_INSIGHTS_METRICS_AND_MORE: "\u6700\u65B0\u89C1\u89E3\u3001\u6307\u6807\u7B49",
  FEEDBACK_TITLE: "\u8FD9\u4E9B\u4FE1\u606F\u6709\u591A\u5927\u5E2E\u52A9\uFF1F",
  FEEDBACK_MIN_SCALE: "\u6CA1\u6709\u5E2E\u52A9",
  FEEDBACK_MAX_SCALE: "\u975E\u5E38\u6709\u5E2E\u52A9",
  HAVE_YOU_RECENTLY_EXPERIENCED_CHANGES: "\u60A8\u7684\u751F\u6D3B\u6216\u5BB6\u5EAD\u8FD1\u671F\u662F\u5426\u7ECF\u5386\u4E86\u53D8\u6545\uFF1F",
  DID_YOU_RECENTLY: "\u60A8\u6700\u8FD1\u662F\u5426\u7ED3\u5A5A\u6216\u751F\u5B69\u5B50\u4E86\uFF1F\u67D0\u4E9B\u4EBA\u751F\u53D8\u6545\u53EF\u80FD\u4F7F\u60A8\u6709\u8D44\u683C\u91CD\u65B0\u6CE8\u518C\u3002",
  LEARN_MORE: "\u4E86\u89E3\u66F4\u591A",
  VIEW_ACTIVITY: "\u67E5\u770B\u6D3B\u52A8",
  CONTINUE: "\u7EE7\u7EED",
  ARIA_CIRCLED_CHECKMARK: "\u5706\u5708\u5708\u4F4F\u7684\u767D\u8272\u5BF9\u52FE",
  NOT_NOW: "\u6682\u65F6\u4E0D\u9700\u8981",
  VAL_POINTS: "{points} \u79EF\u5206",
  ACTIONS: "\u64CD\u4F5C",
  VIEW_ALL_CLAIMS: "\u67E5\u770B\u6240\u6709\u7D22\u8D54",
  THIS_BOX_MUST_BE_CHECKED_TO_CONTINUE: "\u5FC5\u987B\u9009\u4E2D\u6B64\u6846\u624D\u80FD\u7EE7\u7EED",
  PLEASE_NOTE: "\u8BF7\u6CE8\u610F",
  COPY: "\u590D\u5236",
  STATUS: "\u72B6\u6001",
  STARTED: "\u5F00\u59CB\u4E86",
  APPROVED: "\u5DF2\u6279\u51C6",
  CANCELLED: "\u5DF2\u53D6\u6D88",
  UPLOADED_DOCUMENT: "\u4E0A\u4F20\u65E5\u671F\uFF1A{date} \u2022 \u7531 {user} \u4E0A\u4F20",
  MORE_INFORMATION_NEEDED: "\u9700\u6C42\u4FE1\u606F",
  PAID: "\u5DF2\u4ED8\u8D39",
  PENDING_EXTERNAL_REVIEW: "\u9001\u4EA4\u5916\u90E8\u5BA1\u67E5",
  PREAPPROVAL_MORE_INFORMATION_NEEDED: "\u9884\u6279\u51C6\u66F4\u591A\u4FE1\u606F",
  PREAPPROVAL_REJECTED: "\u5DF2\u62D2\u7EDD\u9884\u6279\u51C6",
  PREAPPROVED: "\u9884\u6279\u51C6",
  REJECTED: "\u9A73\u56DE",
  SUBMITTED_FOR_PREAPPROVAL: "\u5DF2\u63D0\u4EA4\u8FDB\u884C\u9884\u6279\u51C6",
  SUBMITTED: "\u5DF2\u63D0\u4EA4",
  WALLET: "\u94B1\u5305",
  AMOUNT: "\u91D1\u989D",
  ADD: "\u6DFB\u52A0",
  EDIT: "\u7F16\u8F91",
  DETAILS: "\u8BE6\u60C5",
  AND: "\u548C",
  TODAY: "\u4ECA\u5929",
  CLAIMS: "\u7D22\u8D54",
  GROUP_ID: "\u56E2\u4F53 ID",
  ZIP_CODE: "\u90AE\u653F\u7F16\u7801",
  CITY: "\u57CE\u5E02",
  STATE: "\u5DDE",
  REMOVE: "\u79FB\u9664",
  HEALTH_SPENDING_ACCOUNT: "\u5065\u5EB7\u652F\u51FA\u8D26\u6237",
  HSA: "HSA",
  LIFESTYLE_SPENDING_ACCOUNT: "\u751F\u6D3B\u65B9\u5F0F\u652F\u51FA\u8D26\u6237",
  LSA: "LSA",
  HEALTH_REIMBURSEMENT_ARRANGEMENT: "\u5065\u5EB7\u62A5\u9500\u5B89\u6392",
  DRUGS: "\u836F\u7269",
  TRAVEL: "\u65C5\u884C",
  HEALTH_INSURANCE: "\u5065\u5EB7\u4FDD\u9669",
  SETTINGS: "\u8BBE\u7F6E",
  ACTIVE: "\u6D3B\u8DC3",
  ENDED: "\u5DF2\u7ED3\u675F",
  SUSPENDED: "\u6682\u505C",
  PENDING: "\u5F85\u5904\u7406",
  LOADING: "\u52A0\u8F7D\u4E2D",
  ERROR_STATE_TITLE: "\u51FA\u4E86\u95EE\u9898",
  HELP_EMAIL: "help@league.com",
  SUBMIT: "\u63D0\u4EA4",
  EARN_NUMBER_POINTS_FULL: "\u83B7\u5F97 {points} \u70B9\u79EF\u5206",
  CLOSE: "\u5173\u95ED",
  THIS_CHALLENGE_RUNS_YEAR_ROUND: "\u8FD9\u9879\u6311\u6218\u6301\u7EED\u4E00\u6574\u5E74",
  END_DAY_AT_TIME: "\u5728 {day} \u7ED3\u675F",
  ENDED_DAY_AT_TIME: "\u5DF2\u5728 {day} \u7ED3\u675F",
  START_DAY_AT_TIME: "\u5F00\u59CB{day}",
  SPONSOR_LOGO: "{name} \u5FBD\u6807",
  START_AND_END_DATES: "\u5F00\u59CB\u65E5\u671F\uFF1A{startDate} \u2022 \u7ED3\u675F\u65E5\u671F\uFF1A{endDate}",
  SIGN_UP_BY_DATE: "\u6CE8\u518C\u622A\u6B62\u65E5\u671F\uFF1A{date}",
  SIGN_UP_BEGINS_DATE: "\u6CE8\u518C\u5F00\u59CB\u65E5\u671F\uFF1A{date}",
  SIGN_UP_DATE_PASSED: "\u6CE8\u518C\u65E5\u671F\u5DF2\u8FC7",
  SIGN_UP_PERIOD_PASSED: "\u6CE8\u518C\u671F\u5DF2\u8FC7",
  CHALLENGE_DETAILS: "\u6311\u6218\u8BE6\u60C5",
  CHALLENGE_PROGRESS: "\u6311\u6218\u8FDB\u5EA6",
  X_PERCENT_COMPLETE: "{number}% \u5DF2\u5B8C\u6210",
  SIGN_UP_FOR_CHALLENGE: "\u6CE8\u518C",
  GROUP_CHALLENGE: "\u56E2\u4F53\u6311\u6218",
  SOLO_CHALLENGE: "\u5355\u4EBA\u6311\u6218",
  TRACK_WEARABLES_ALT_TEXT: "\u67E5\u770B\u667A\u80FD\u624B\u8868\u6570\u636E\u7684\u4EBA",
  CONNECT_A_DEVICE: "\u8FDE\u63A5\u8BBE\u5907",
  DEVICES_CONNECTED_TITLE: "\u770B\u6765\u60A8\u5DF2\u7ECF\u8FDE\u63A5\u4E0A\u4E86\uFF01",
  DEVICES_CONNECTED_DESCRIPTION: "\u5DF2\u7ECF\u5F00\u59CB\u540C\u6B65\u60A8\u7684\u6570\u636E\u3002\u5982\u679C\u60A8\u9009\u62E9\u81EA\u52A8\u8DDF\u8E2A\uFF0C\u6211\u4EEC\u53EF\u4EE5\u4F7F\u7528\u60A8\u8BBE\u5907\u7684{dataPoint}\u81EA\u52A8\u5C06\u6D3B\u52A8\u7968\u623F\u4E3A\u5B8C\u6210\u3002",
  DEVICES_UNCONNECTED_TITLE: "\u8BA9\u6211\u4EEC\u8FDE\u63A5\u60A8\u7684\u8BBE\u5907\u5427\uFF01",
  DEVICES_UNCONNECTED_TRACK_AUTOMATICALLY_SUBTITLE: "\u81EA\u52A8\u8DDF\u8E2A",
  DEVICES_UNCONNECTED_TRACK_MANUALLY_SUBTITLE: "\u624B\u52A8\u8DDF\u8E2A",
  DEVICES_UNCONNECTED_DESCRIPTION: "\u8FDE\u63A5\u60A8\u559C\u7231\u7684\u5065\u5EB7\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\uFF0C\u4EE5\u81EA\u52A8\u5C06\u6D3B\u52A8\u6807\u8BB0\u4E3A\u5B8C\u6210\u3002",
  TRACK_CHALLENGE_AUTOMATICALLY: "\u81EA\u52A8\u8DDF\u8E2A",
  TRACK_CHALLENGE_AUTOMATICALLY_DESCRIPTION: "\u5982\u679C\u60A8\u9009\u62E9\u81EA\u52A8\u8DDF\u8E2A\uFF0C\u5219\u5728\u60A8\u6CE8\u518C\u540E\uFF0C\u6839\u636E {instructionsLink} \u4F7F\u7528 League \u79FB\u52A8\u5E94\u7528\u7A0B\u5E8F\u8FDE\u63A5\u60A8\u7684\u8BBE\u5907\u3002",
  TRACK_CHALLENGE_AUTOMATICALLY_INSTRUCTION: "\u6309\u7167\u4EE5\u4E0B\u6B65\u9AA4\u8FDB\u884C\u64CD\u4F5C",
  TRACK_CHALLENGE_MANUALLY: "\u6539\u4E3A\u624B\u52A8\u8DDF\u8E2A",
  TRACK_CHALLENGE_MANUALLY_CHANGE_LATER: "\u5982\u679C\u60A8\u9009\u62E9\u624B\u52A8\u8DDF\u8E2A\uFF0C\u7A0D\u540E\u4ECD\u7136\u53EF\u4EE5\u8FDE\u63A5\u8BBE\u5907\u3002{learnMoreLink} \u4E86\u89E3\u5173\u4E8E\u624B\u52A8\u8DDF\u8E2A\u8FDB\u5EA6\u7684\u66F4\u591A\u4FE1\u606F\u3002",
  TRACK_CHALLENGE_MANUALLY_LEARN_MORE: "\u4E86\u89E3\u66F4\u591A",
  TRACK_HEALTH_DATA_ALT: "\u4EE5\u56FE\u8868\u5F62\u5F0F\u8DDF\u8E2A\u5065\u5EB7\u6570\u636E\u7684\u4EEA\u8868\u677F",
  TRACK_MANUALLY_CONFIRM_TITLE: "\u4F60\u786E\u5B9A\u8981\u624B\u52A8\u8FFD\u8E2A\u5417\uFF1F",
  TRACK_MANUALLY_CONFIRM_CONNECT_LATER_DESCRIPTION: "\u60A8\u7A0D\u540E\u4ECD\u7136\u53EF\u4EE5\u8FDE\u63A5\u8BBE\u5907",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_TITLE: "\u60A8\u786E\u5B9A\u8981\u9000\u51FA\u5417\uFF1F",
  EXIT_CHALLENGE_SIGN_UP_CONFIRM_DESCRIPTION: "\u5982\u679C\u9009\u62E9\u9000\u51FA\uFF0C\u5219\u4E0D\u4F1A\u4FDD\u5B58\u60A8\u7684\u66F4\u6539\u3002",
  EXIT: "\u9000\u51FA",
  CHALLENGE: "\u6311\u6218",
  CHALLENGES: "\u6311\u6218",
  CHALLENGE_PROGRAM_LIBRARY_DESCRIPTION: "\u6CE8\u518C\u63A5\u53D7\u5B8C\u6210\u65E5\u5E38\u6D3B\u52A8\u7684\u6311\u6218\uFF0C\u548C\u60A8\u7684\u540C\u4F34\u4EEC\u4E00\u8F83\u9AD8\u4E0B\u5427\uFF01",
  THIS_CHALLENGE_HAS_ENDED: "\u8FD9\u9879\u6311\u6218\u5DF2\u7ED3\u675F",
  COMPLETED_ON_DATE: "\u5DF2\u4E8E{date}\u5B8C\u6210",
  ENDED_ON_DATE: "\u5DF2\u5728 {date} \u7ED3\u675F",
  LEAVE_CHALLENGE: "\u9000\u51FA\u6311\u6218",
  LEAVE_CHALLENGE_QUESTION: "\u9000\u51FA\u6311\u6218\uFF1F",
  LEAVE_CHALLENGE_DETAIL: "\u60A8\u786E\u5B9A\u8981\u4ECE\u60A8\u7684\u5065\u5EB7\u4E4B\u65C5\u4E2D\u79FB\u9664\u8FD9\u9879\u6311\u6218\u5417\uFF1F\u8FD9\u9879\u6311\u6218\u5DF2\u53D6\u5F97\u7684\u4EFB\u4F55\u8FDB\u5EA6\u90FD\u4F1A\u88AB\u91CD\u7F6E\u3002",
  LEAVE: "\u9000\u51FA",
  SIGN_UP_FOR_THE_CHALLENGE: "\u6CE8\u518C\u53C2\u52A0\u6311\u6218",
  CHALLENGE_SENSITIVE_DATA: "\u60A8\u7684\u5065\u5EB7\u6570\u636E\u548C\u4E2A\u4EBA\u4FE1\u606F\u5C5E\u4E8E\u9AD8\u654F\u611F\u4FE1\u606F\uFF0C\u6211\u4EEC\u6709\u8D23\u4EFB\u4FDD\u62A4\u5B83\u7684\u79C1\u5BC6\u548C\u5B89\u5168\u3002\u5BF9\u4E8E\u8FD9\u9879\u8D23\u4EFB\uFF0C\u6211\u4EEC\u8D23\u65E0\u65C1\u8D37\u3002",
  CHALLENGE_USING_DATA_TO: "\u5728\u60A8\u6CE8\u518C\u53C2\u52A0\u6311\u6218\u65F6\uFF0C\u6211\u4EEC\u4F1A\u4F7F\u7528\u60A8\u7684\u6570\u636E\uFF1A",
  COMPLETE_ACTIVITIES_SHORT: "\u5B8C\u6210\u6D3B\u52A8",
  SHARE_CHALLENGE_PROGRESS_WITH_EMPLOYER: "\u4E0E\u96C7\u4E3B\u5206\u4EAB\u60A8\u7684\u6311\u6218\u8FDB\u5EA6",
  SHARE_CHALLENGE_PROGRESS: "\u4E0E\u540C\u4E8B\uFF08\u548C\u96C7\u4E3B\uFF09\u5206\u4EAB\u60A8\u7684\u6311\u6218\u8FDB\u5EA6",
  RANK_SCORE: "\u4E0A\u699C\u79EF\u5206\u699C",
  DETERMINE_CHALLENGE_WINNERS: "\u786E\u5B9A\u6311\u6218\u83B7\u80DC\u8005",
  ACCEPT_BEFORE_CONTINUE: "\u5728\u7EE7\u7EED\u64CD\u4F5C\u4E4B\u524D\uFF0C\u60A8\u5FC5\u987B\u63A5\u53D7\u9690\u79C1\u653F\u7B56\u3002",
  CHECK_TO_CONFIRM_PRIVACY_POLICY: "\u52FE\u9009\u6B64\u6846\u5373\u8868\u793A\u6211\u540C\u610F<a>\u9690\u79C1\u653F\u7B56</a>\u3002",
  LEARN_MORE_ABOUT_PRIVACY_POLICY: "\u9605\u8BFB\u6211\u4EEC\u7684<a>\u9690\u79C1\u653F\u7B56</a>\uFF0C\u8BE6\u7EC6\u4E86\u89E3 League \u5982\u4F55\u7BA1\u7406\u548C\u4F7F\u7528\u60A8\u7684\u6570\u636E\u3002",
  PRIZE: "\u5956\u54C1",
  RULES: "\u89C4\u5219",
  LEADERBOARD: "\u6392\u884C\u699C",
  TOOLTIP_ACTION_DESCRIPTION: "\u67E5\u770B\u79EF\u5206\u699C\u6392\u540D\u6807\u51C6",
  MARK_AS_DONE: "\u6807\u8BB0\u4E3A\u5DF2\u5B8C\u6210",
  CLOSE_ACTIVITY: "\u5173\u95ED\u6D3B\u52A8",
  GOAL_INCOMPLETE: "\u5728\u7EE7\u7EED\u64CD\u4F5C\u4E4B\u524D\uFF0C\u60A8\u5FC5\u987B\u5B8C\u6210\u76EE\u6807\u3002",
  PROGRESS_RECORD_ERROR: "\u60A8\u4E0A\u6B21\u7684\u8FDB\u5EA6\u6709\u95EE\u9898\u3002",
  YOUVE_COMPLETED: "\u60A8\u5DF2\u5B8C\u6210",
  ACTIVITY_COMPLETE: "\u6D3B\u52A8\u5DF2\u5B8C\u6210",
  PAGE_NOT_FOUND_HEADER: "\u672A\u627E\u5230\u9875\u9762",
  PAGE_NOT_FOUND_CONTENT: "\u60A8\u770B\u8D77\u6765\u4F3C\u4E4E\u8FF7\u8DEF\u4E86\uFF01",
  PAGE_NOT_FOUND_CONTENT_SECONDARY: "\u8BA9\u6211\u4EEC\u5E2E\u60A8\u91CD\u65B0\u627E\u5230\u65B9\u5411\u3002",
  PAGE_NOT_FOUND_BUTTON: "\u8FD4\u56DE\u9996\u9875",
  REWARD_POINTS: "\u5956\u52B1\u79EF\u5206",
  REWARD_POINTS_EARNED: "\u8D5A\u53D6\u7684\u5956\u52B1\u79EF\u5206",
  REWARD_POINTS_MISSED: "\u9519\u5931\u7684\u5956\u52B1\u79EF\u5206",
  X_REWARD_POINTS: "{points} \u5956\u52B1\u79EF\u5206",
  X_LEAGUE_POINTS: "{points} \u70B9 League \u79EF\u5206",
  REWARDS_PROGRAM_ELIGIBLE: "\u7B26\u5408\u6761\u4EF6\u7684\u5956\u52B1\u8BA1\u5212",
  EXPIRY_DATE: "\u5230\u671F\u65E5\u671F",
  NO_EXPIRY_DATE: "\u672A\u8BBE\u7F6E\u8FC7\u671F\u65E5\u671F",
  POINTS_BALANCE: "\u79EF\u5206\u4F59\u989D",
  REDEEM_NEXT_TIER_POINTS: "\u60A8\u8FD8\u5DEE {points} \u70B9\u79EF\u5206\u5373\u53EF\u5151\u6362 {value}\u3002",
  VIEW_YOUR_REWARDS: "\u67E5\u770B\u60A8\u7684\u5956\u52B1",
  REDEEMABLE: "\u53EF\u5151\u6362 {value}\u3002",
  ACTIVATION_PENDING: "\u5F85\u6FC0\u6D3B",
  EXPIRED: "\u5DF2\u8FC7\u671F",
  YOUR_ACCOUNT_IS_ACTIVE_AND_IN_THE_PROCESS_OF_BEING_SET_UP: "\u60A8\u7684\u8D26\u6237\u5DF2\u6FC0\u6D3B\uFF0C\u6B63\u5728\u8BBE\u7F6E\u4E2D\u3002",
  PLEASE_TRY_AGAIN_SHORTLY: "\u8BF7\u7A0D\u540E\u91CD\u8BD5",
  MANAGE_DEPENDENTS: "\u7BA1\u7406\u53D7\u629A\u517B\u4EBA",
  VIEW_CARD: "\u67E5\u770B\u5361\u7247",
  MEMBER_ID: "\u6210\u5458 ID",
  SPOUSE: "\u914D\u5076",
  COMMON_LAW: "\u666E\u901A\u6CD5",
  CHILD: "\u513F\u7AE5",
  PARENT: "\u7236\u6BCD",
  GRANDPARENT: "\u7956\u7236\u6BCD",
  GRANDCHILD: "\u5B59\u8F88",
  UNCLE_AUNT: "\u53D4\u53D4\uFF08\u8205\u8205\uFF09/\u59D1\u59D1\uFF08\u8205\u5988\uFF09",
  NEPHEW_NIECE: "\u4F84\u5B50/\u4F84\u5973",
  INELIGIBLE_CHILD: "\u4E0D\u7B26\u5408\u6761\u4EF6\u7684\u513F\u7AE5",
  SIBLING: "\u5144\u5F1F\u59D0\u59B9",
  OTHER: "\u5176\u4ED6",
  DEPENDENT_CARD_HOLDER: "{dependentName}\u7684\u5361\u7247",
  DATE: "\u65E5\u671F",
  DESCRIPTION: "\u63CF\u8FF0",
  TYPE: "\u7C7B\u578B",
  ID: "ID",
  CLAIM: "\u7D22\u8D54",
  TOP_UP: "\u5145\u503C",
  ADJUSTMENT: "\u8C03\u6574",
  REFUND: "\u9000\u6B3E",
  LEAGUE_CREDIT: "\u8054\u8D5B\u79EF\u5206",
  CREDIT_CARD: "\u4FE1\u7528\u5361",
  CGA: "CGA",
  NO_TRANSACTIONS_YET: "\u5C1A\u65E0\u4EA4\u6613",
  NO_MORE_TRANSACTIONS: "\u4E0D\u518D\u6709\u4EFB\u4F55\u4EA4\u6613",
  PROMO_CODE: "\u4FC3\u9500\u4EE3\u7801",
  PROMO_CODE_NOT_FOUND: "\u4FC3\u9500\u4EE3\u7801\u65E0\u6548",
  PROMO_CODE_SUCCESS: "\u4FC3\u9500\u4EE3\u7801\u5DF2\u5151\u6362\uFF01",
  TOTAL_CREDIT: "\u603B\u79EF\u5206\uFF1A",
  CREDIT_INFORMATION: "\u4FE1\u7528\u4FE1\u606F",
  ENTER_CODE: "\u8F93\u5165\u4EE3\u7801",
  DOCUMENT_NAME: "\u6587\u4EF6\u540D\u79F0",
  REQUIRED: "\u9700\u8981",
  CANCEL: "\u53D6\u6D88",
  CONFIRM: "\u786E\u8BA4",
  ALLOCATION: "\u5206\u914D",
  COVERED: "\u5DF2\u8986\u76D6",
  NOT_COVERED: "\u672A\u8986\u76D6",
  COVERED_WITH_RESTRICTIONS: "\u6709\u9650\u5236\u6761\u4EF6\u53C2\u4FDD",
  EXPAND_ALL: "\u5168\u90E8\u5C55\u5F00",
  COLLAPSE_ALL: "\u5168\u90E8\u6298\u53E0",
  EXAMPLES: "\u4F8B\u5982\uFF1A ",
  RESTRICTIONS: "\u9650\u5236\u6761\u4EF6",
  SEARCH_COVERAGE: "\u641C\u7D22\u60A8\u7684\u4FDD\u9669\u8303\u56F4",
  COVERAGE_SEARCH_BEHAVIOUR_DESCRIPTION: "\u5728\u60A8\u8F93\u5165\u7684\u8FC7\u7A0B\u4E2D\uFF0C\u641C\u7D22\u7ED3\u679C\u4F1A\u81EA\u52A8\u66F4\u65B0",
  INDIVIDUAL: "\u4E2A\u4EBA",
  INDIVIDUAL_CAP: "\u4E2A\u4EBA",
  DEPENDENTS: "\u5BB6\u5C5E",
  SELECT_ONE: "\u9009\u62E9\u4E00\u4E2A",
  MORE_OPTIONS: "\u66F4\u591A\u9009\u9879",
  VENDOR: "\u4F9B\u5E94\u5546",
  FLEX_DOLLAR_INFO_TEXT_SINGLE: "\u4F1A\u81EA\u52A8\u6DFB\u52A0\u5230\u60A8\u7684 {benefitName}\u3002",
  FLEX_DOLLAR_INFO_TEXT_MULTIPLE: "\u4F1A\u5C06 <strong>{percent}%</strong> \u6DFB\u52A0\u5230\u60A8\u7684 <strong>{benefitName}</strong>",
  LEFTOVER_FLEX_DOLLAR_INFO_TEXT_SINGLE: "\u4F1A\u5C06\u5176 <strong>{percent}%</strong> \u6DFB\u52A0\u5230\u60A8\u7684 <strong>{benefitName}</strong>\u3002",
  DOCUMENT_TITLE: "{title} - League",
  MEDICAL: "\u533B\u7597",
  TOTAL: "\u603B\u8BA1",
  PROFILE_UPDATE_FAILED: "\u4E2A\u4EBA\u8D44\u6599\u66F4\u65B0\u5931\u8D25",
  PROFILE_PHOTO: "\u4E2A\u4EBA\u8D44\u6E90\u7167\u7247",
  PROFILE_PHOTO_DESCRIPTION: "\u8FD9\u5F20\u7167\u7247\u663E\u793A\u5728\u60A8\u7684\u7528\u6237\u4E2A\u4EBA\u8D44\u6599\u4E2D",
  SELECT_AVATAR: "\u9009\u62E9\u5934\u50CF",
  SELECT_AVATAR_ERROR: "\u5728\u7EE7\u7EED\u64CD\u4F5C\u4E4B\u524D\uFF0C\u4F60\u5FC5\u987B\u9009\u62E9\u4E00\u4E2A\u5934\u50CF\u3002",
  GET_AVATARS_ERROR: "\u54CE\u5440\uFF01\u52A0\u8F7D\u5934\u50CF\u65F6\u51FA\u73B0\u4E86\u95EE\u9898\u3002\u8BF7\u8FD4\u56DE\uFF0C\u7136\u540E\u9009\u62E9\u5176\u4ED6\u9009\u9879\u3002",
  ADDRESS_LINE_1: "\u5730\u5740\u7B2C 1 \u884C",
  ADDRESS_LINE_2: "\u5730\u5740\u7B2C 2 \u884C",
  LANGUAGE_SELECTOR_ENGLISH: "\u82F1\u8BED",
  LANGUAGE_SELECTOR_FRENCH: "\u6CD5\u8BED",
  LANGUAGE_SELECTOR_SPANISH: "\u897F\u73ED\u7259\u8BED",
  DOMESTIC_PARTNERSHIP: "\u540C\u5C45\u5173\u7CFB",
  YES: "\u662F",
  NO: "\u4E0D",
  WHY_DO_YOU_ASK: "\u60A8\u4E3A\u4EC0\u4E48\u8981\u95EE\u8FD9\u4E2A\u95EE\u9898\uFF1F",
  LAST_DAY_OF_ENROLLMENT: "\u6CE8\u518C\u671F\u6700\u540E\u4E00\u5929\uFF1A{lastDay}",
  WALLET_WELCOME: "\u6B22\u8FCE\u6765\u5230\u60A8\u7684\u94B1\u5305\uFF01",
  WALLET_DESCRIPTION: "\u5728\u8FD9\u91CC\uFF0C\u60A8\u53EF\u4EE5\u67E5\u770B\u81EA\u5DF1\u6709\u54EA\u4E9B\u798F\u5229\uFF0C\u63D0\u4EA4\u7D22\u8D54\u5E76\u67E5\u770B\u60A8\u7684\u4FDD\u9669\u8303\u56F4\u3002\u94B1\u5305\u6FC0\u6D3B\u540E\uFF0C\u60A8\u5C31\u53EF\u4EE5\u4E3A\u5728\u6CE8\u518C\u671F\u95F4\u4EA7\u751F\u7684\u8D39\u7528\u63D0\u4EA4\u7D22\u8D54\u3002\u540C\u65F6\uFF0C{healthProgramLink}\uFF01",
  HEALTH_PROGRAM_LINK_COPY: "\u5C1D\u8BD5\u52A0\u5165\u6211\u4EEC\u7684\u5176\u4E2D\u4E00\u9879\u5065\u5EB7\u8BA1\u5212",
  FINALIZE_SELECTIONS_HEADER_COPY: "\u60F3\u8981\u66F4\u5FEB\u4EAB\u53D7\u5230\u60A8\u7684\u798F\u5229\uFF1F",
  FINALIZE_SELECTIONS_COPY_1: "\u5F85\u60A8\u6700\u7EC8\u786E\u5B9A\u9009\u62E9\u540E\uFF0C\u6211\u4EEC\u5C31\u53EF\u4EE5\u7ACB\u5373\u5C06\u60A8\u7684\u8BA1\u5212\u8BE6\u60C5\u53D1\u9001\u7ED9\u627F\u4FDD\u4EBA\uFF01\u4E5F\u5C31\u662F\u8BF4\uFF0C\u60A8\u4E0D\u5FC5\u7B49\u5230\u60A8\u7684\u6CE8\u518C\u671F\u7ED3\u675F\u5373\u53EF\u67E5\u770B\u60A8\u7684\u94B1\u5305\u3002",
  FINALIZE_SELECTIONS_COPY_2: "\u4E5F\u610F\u5473\u7740\u5728\u70B9\u51FB\u63D0\u4EA4\u540E\uFF0C\u60A8\u5C31\u65E0\u6CD5\u518D\u5BF9\u60A8\u7684\u8BA1\u5212\u8FDB\u884C\u4EFB\u4F55\u66F4\u6539\u3002",
  FINALIZE_SELECTIONS_ACKNOWLEDGEMENT: "\u6211\u4E86\u89E3\u65E0\u6CD5\u518D\u66F4\u6539\u6211\u7684\u9009\u62E9",
  FINALIZE_PLAN_SELECTIONS: "\u6700\u7EC8\u786E\u5B9A\u8BA1\u5212\u9009\u62E9",
  ERROR_FINALIZING_SELECTIONS: "\u63D0\u4EA4\u60A8\u7684\u9009\u62E9\u65F6\u51FA\u9519\u4E86\u3002",
  ERROR_FINALIZING_SELECTIONS_INVALID_SELECTIONS: "\u770B\u6765\u60A8\u7684\u9009\u62E9\u4E0D\u5B8C\u6574\uFF0C\u8BF7\u5B8C\u6210\u6CE8\u518C\uFF0C\u7136\u540E\u91CD\u8BD5\u3002",
  SELECTIONS_FINALIZED_SUCCESS: "\u5DF2\u6700\u7EC8\u786E\u5B9A\u9009\u62E9\uFF01",
  TOTAL_REWARDS_LABEL: "\u4FDD\u9669\u8303\u56F4\u6458\u8981",
  TOTAL_REWARDS_DESCRIPTION: "\u67E5\u770B\u60A8\u5728\u6CE8\u518C\u671F\u95F4\u7ED9\u51FA\u7684\u8BA1\u5212\u9009\u62E9\u3002",
  TOTAL_REWARDS_LINK: "\u67E5\u770B\u4FDD\u9669\u8303\u56F4\u6458\u8981",
  CHANGE_PLAN_LABEL: "\u66F4\u6539\u8BA1\u5212\u9009\u62E9",
  CHANGE_PLAN_DESCRIPTION: "\u8981\u66F4\u6539\u60A8\u7684\u8BA1\u5212\u6216\u4E2A\u4EBA\u4FE1\u606F\uFF1F",
  CHANGE_PLAN_LINK: "\u66F4\u6539\u8BA1\u5212\u9009\u62E9",
  DISMISS_TOAST: "\u5220\u9664\u901A\u77E5",
  UPLOAD_DOCUMENT: "\u4E0A\u4F20\u6587\u4EF6",
  REQUIRED_FORM: "\u9700\u8981",
  PERCENTAGE_USED: "\u5DF2\u7528\u767E\u5206\u6BD4\u3002",
  SUCCESS: "\u6210\u529F\uFF01",
  ERROR: "\u9519\u8BEF",
  OVERVIEW: "\u6982\u8FF0",
  THIS_FIELD_IS_REQUIRED: "\u6B64\u5B57\u6BB5\u4E3A\u5FC5\u586B\u9879",
  INVALID_ZIP_CODE_NO_FORMAT: "\u65E0\u6548\u7684\u90AE\u653F\u7F16\u7801",
  INVALID_DAY_ERROR_MESSAGE: "\u60A8\u9009\u62E9\u7684\u65E5\u671F\u65E0\u6548\u3002",
  UNIQUE_IDENTIFIER_MAX_LENGTH_ERROR: "\u6700\u591A\u4E0D\u80FD\u8D85\u8FC7 {length} \u4E2A\u5B57\u7B26",
  COMPLETED_STATE_TITLE: "\u5DF2\u5B8C\u6210",
  DRAG_OR_UPLOAD_PHOTO: "\u4E0A\u4F20\u6B64\u6587\u4EF6\u7684\u7167\u7247",
  ADD_IMAGE: "\u5411\u6B64\u6587\u4EF6\u6DFB\u52A0\u66F4\u591A\u9875\uFF08\u5982\u9002\u7528\uFF09",
  IDENTIFICATION_TYPE_TWO_FILES: "\u8EAB\u4EFD\u8BC1\u4EF6\u6700\u591A\u53EA\u80FD\u6709 2 \u5F20\u652F\u6301\u56FE\u7247\u3002",
  WELCOME: "\u6B22\u8FCE",
  GOT_IT: "\u660E\u767D\u4E86\uFF01",
  SAVE: "\u4FDD\u5B58",
  VIEW_MORE_DETAILS_SENTENCE_CASE: "\u67E5\u770B\u66F4\u591A\u8BE6\u60C5",
  LAST_UPDATED: "\u6700\u540E\u66F4\u65B0",
  CLAIM_REF_NUMBER: "\u7D22\u8D54\u7F16\u53F7",
  VIEW_ALL: "\u67E5\u770B\u5168\u90E8",
  HRA: "HRA",
  SEARCH: "\u641C\u7D22",
  DAY: "\u65E5",
  MONTH: "\u6708",
  YEAR: "\u5E74",
  BACK: "\u8FD4\u56DE",
  NEXT: "\u4E0B\u4E00\u4E2A",
  PREVIOUS: "\u4E0A\u4E00\u9875",
  HELP_CENTER: "\u5E2E\u52A9\u4E2D\u5FC3",
  LIVE_CHAT_TOP_BAR_MINIMIZE: "\u6700\u5C0F\u5316\u804A\u5929",
  LIVE_CHAT_FEED_NEW_CHIPS_ALT: "\u5EFA\u8BAE\u7684\u56DE\u590D\u53EF\u7528",
  LIVE_CHAT_AGENT_TYPING_BUBBLE_ALT: "\u804A\u5929\u53C2\u4E0E\u8005\u6B63\u5728\u8F93\u5165",
  VIEW_DOCUMENT: "\u67E5\u770B\u6587\u4EF6\uFF1A",
  CATEGORIES: "\u7C7B\u522B",
  ACTIVE_PROGRAM: "{length, plural, other {\u5DF2\u53C2\u52A0\u7684\u8BA1\u5212}}",
  ACTIVE_CHALLENGE: "{length, plural, other {\u5DF2\u53C2\u52A0\u7684\u6311\u6218}}",
  PROGRAM_PROGRESS: "\u8BA1\u5212\u8FDB\u5C55",
  MULTI_SELECT_DEFAULT_CAN_CREATE: "\u60A8\u4E5F\u53EF\u4EE5\u8F93\u5165\u6765\u521B\u5EFA\u4E00\u4E2A\u65B0\u9009\u9879\uFF01",
  MULTI_SELECT_DEFAULT_PLACEHOLDER: "\u9009\u62E9\u60A8\u7684\u9009\u9879",
  COVERAGE: "\u4FDD\u9669\u8303\u56F4",
  HEALTH_PROFILE: "\u5065\u5EB7\u6863\u6848",
  HEALTH_PROGRAM: "\u5065\u5EB7\u8BA1\u5212",
  HEALTH_PROGRAMS: "\u5065\u5EB7\u8BA1\u5212",
  STEPS: "\u6B65\u9AA4",
  ACTIVE_DURATION: "\u8FD0\u52A8\u5206\u949F\u6570",
  MINDFUL_DURATION: "\u6B63\u5FF5\u5206\u949F\u6570",
  FLOORS_CLIMBED: "\u6500\u722C\u822A\u73ED\u6570",
  DISTANCE: "\u65C5\u884C\u8DDD\u79BB",
  ENERGY_BURNED: "\u71C3\u70E7\u7684\u5361\u8DEF\u91CC",
  METRIC_DETAILS: "\u6307\u6807\u8BE6\u60C5",
  HEALTH_PROGRAMS_SYNC_DEVICE: "\u540C\u6B65{device}\u4EE5\u83B7\u5F97\u6700\u4F73\u8BA1\u5212\u4F53\u9A8C\u3002",
  HEALTH_PROGRAMS_CONNECTED_PROGRAM: "\u5DF2\u8FDE\u63A5\u7684\u5065\u5EB7\u8BA1\u5212",
  TRACK_AUTOMATICALLY: "\u81EA\u52A8\u8DDF\u8E2A\u60A8\u7684\u8FDB\u5EA6",
  SYNC_ISSUE: "\u540C\u6B65\u95EE\u9898",
  FIX_SYNC_ISSUE: "\u4FEE\u590D\u6B64\u95EE\u9898",
  DATAPOINT_SYNC_ISSUE: "\u54CE\u5440\uFF0C\u540C\u6B65\u60A8\u7684{dataPoint}\u65F6\u51FA\u73B0\u4E86\u95EE\u9898\u3002",
  CONNECT_TO_TRACK: "\u8FDE\u63A5\u60A8\u559C\u7231\u7684\u5065\u5EB7\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\uFF0C\u4EE5\u81EA\u52A8\u5C06\u6D3B\u52A8\u6807\u8BB0\u4E3A\u5B8C\u6210\u3002",
  CONNECT_APPS_DEVICES: "\u8FDE\u63A5\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907",
  ADD_APPS_DEVICES: "\u67E5\u770B\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907",
  MANAGE_UNITS: "\u66F4\u6539\u6D4B\u91CF\u5355\u4F4D",
  DONT_CONNECT_APPS_DEVICES: "\u4E0D\u8981\u81EA\u52A8\u8DDF\u8E2A\u6211\u7684\u8FDB\u5EA6",
  CONNECTING_UNAVAILABLE: "\u4E0D\u80FD\u5728 Web \u7AEF\u8FDE\u63A5\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907",
  CONNECTING_UNAVAILABLE_DETAILS: "\u63A5\u4E0B\u6765\uFF0C\u8BF7\u8BBF\u95EE League \u79FB\u52A8\u5E94\u7528\u7A0B\u5E8F\uFF0C\u8FDE\u63A5\u60A8\u559C\u7231\u7684\u5065\u5EB7\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\u3002",
  CONNECTING_UNAVAILABLE_DETAILS_WITH_LINK: "\u63A5\u4E0B\u6765\uFF0C\u8BF7\u8BBF\u95EE League \u79FB\u52A8\u5E94\u7528\u7A0B\u5E8F\uFF0C\u8FDE\u63A5\u60A8\u559C\u7231\u7684\u5065\u5EB7\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\u3002\u5982\u9700\u4E86\u89E3\u66F4\u591A\u4FE1\u606F\uFF0C\u53EF\u4EE5\u67E5\u770B\u5B8C\u6574\u6E05\u5355",
  CONNECT_DEVICES_LEARN_MORE: "\u4E86\u89E3\u66F4\u591A",
  APPS_AND_DEVICES_CURRENTLY_SUPPORTED: "\u4E86\u89E3\u76EE\u524D\u652F\u6301\u54EA\u4E9B\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907",
  DONT_HAVE_THE_APP: "\u6CA1\u6709 League \u79FB\u52A8\u5E94\u7528\u7A0B\u5E8F\uFF1F",
  WHAT_DEVICES_CAN_CONNECT: "\u76EE\u524D\u652F\u6301\u54EA\u4E9B\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\uFF1F",
  CONNECTED_DEVICES_IMAGE_ALT: "\u67E5\u770B\u5DF2\u8FDE\u63A5\u8BBE\u5907\u4E0A\u7684\u6570\u636E\u7684\u4EBA\u5458",
  MORE_GREAT_RESOURCES: "\u66F4\u591A\u4F18\u8D28\u8D44\u6E90",
  HISTORY: "\u5386\u53F2",
  TOP_MATCH: "\u9876\u7EA7\u6BD4\u8D5B",
  CHAT: "\u804A\u5929",
  END_CHAT: "\u7ED3\u675F\u804A\u5929",
  BECOMES_ACTIVE_ON_DATE: "\u5728 {date} \u65F6\u53D8\u4E3A\u6FC0\u6D3B\u72B6\u6001",
  BECOMES_ACTIVE_IN_X_DAYS: "\u8BE5\u798F\u5229\u5728 {days} \u5929\u540E\u751F\u6548",
  YOU_CAN_ONLY_SUBMIT_CLAIMS_IN_DATE_RANGE: "\u60A8\u53EA\u80FD\u63D0\u4EA4\u8D2D\u4E70/\u670D\u52A1\u65E5\u671F\u5728 <strong>{dateRange}</strong> \u4E4B\u95F4\u7684\u6B64\u9879\u798F\u5229\u7684\u7D22\u8D54\u3002\u67E5\u770B\u60A8\u7684\u4FDD\u9669\u8BE6\u60C5\uFF0C\u4E86\u89E3\u66F4\u591A\u4FE1\u606F\u3002",
  SUBMIT_PAST_CLAIMS_BY_DATE: "\u5728 {date} \u524D\u63D0\u4EA4\u8FC7\u5F80\u7D22\u8D54",
  THIS_POLICY_HAS_EXPIRED: "\u6B64\u4FDD\u5355\u671F\u9650\u5DF2\u8FC7\uFF0C\u4F46\u60A8\u4ECD\u6709\xB7{days}\xB7\u5929\u7684\u65F6\u95F4\u63D0\u4EA4\u6B64\u798F\u5229\u7684\u8FC7\u5F80\u7D22\u8D54",
  THIS_POLICY_EXPIRES_TOMORROW: "\u6B64\u4FDD\u5355\u671F\u5DF2\u7ED3\u675F\uFF0C\u4F46\u4ECA\u5929\u662F\u63D0\u4EA4\u6B64\u798F\u5229\u8FC7\u5F80\u7D22\u8D54\u7684\u6700\u540E\u671F\u9650",
  YOU_HAVE_UNTIL_DATE_TO_SUBMIT_PAST_CLAIMS: "\u60A8\u9700\u5728\xB7<strong>{inactiveDate}</strong>\xB7\u524D\u63D0\u4EA4\u8D2D\u4E70/\u670D\u52A1\u65E5\u671F\u4E0D\u665A\u4E8E\xB7<strong>{endDate}</strong>\xB7\u7684\u8FC7\u5F80\u7D22\u8D54",
  EXPIRES_ON_DATE: "\u4E8E {date} \u5230\u671F",
  ASSIGNED: "\u5DF2\u5206\u914D",
  ASSIGNED_ON: "\u4E8E {date}",
  ASSIGNED_TODAY: "\u4ECA\u5929",
  YOU_HAVE_X_DAYS_BEFORE_EXPIRY: "\u60A8\u8FD8\u5269 {days} \u5929\u53EF\u4EE5\u4F7F\u7528\u8FD9\u9879\u798F\u5229\uFF0C\u4E4B\u540E\u4FDD\u5355\u5C31\u4F1A\u5230\u671F",
  AFTER_EXPIRY_YOU_HAVE_UNTIL_DATE_TO_SUBMIT: "\u60A8\u9700\u5728\xB7<strong>{inactiveDate}</strong> \u524D\u63D0\u4EA4\u8D2D\u4E70/\u670D\u52A1\u65E5\u671F\u4E0D\u665A\u4E8E\xB7<strong>{endDate}</strong>\xB7\u7684\u8FC7\u5F80\u7D22\u8D54",
  LOAD_MORE: "\u52A0\u8F7D\u66F4\u591A",
  SHOW_MORE: "\u663E\u793A\u66F4\u591A",
  LINK_OUT_MESSAGE: "\u5FC5\u770B",
  THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM: "\u54CE\u5440\uFF0C\u597D\u50CF\u51FA\u4E86\u70B9\u95EE\u9898\u3002",
  AGENT_PERMISSION_ERROR: "\u60A8\u6CA1\u6709\u67E5\u770B\u6B64\u5185\u5BB9\u7684\u6743\u9650\u3002",
  TRY_AGAIN: "\u8BF7\u91CD\u8BD5",
  WARNING_OPENS_NEW_TAB: "\u5728\u65B0\u7684\u6807\u7B7E\u9875\u4E2D\u6253\u5F00",
  ANY_HEALTH_CHANGES: "\u60A8\u7684\u5065\u5EB7\u72B6\u6001\u6700\u8FD1\u662F\u5426\u6709\u4EFB\u4F55\u53D8\u5316\uFF1F",
  LAST_UPDATED_DATE: "\u6700\u540E\u66F4\u65B0\u4E8E{date}",
  UPDATE_HEALTH_PROFILE: "\u66F4\u65B0\u60A8\u7684\u5065\u5EB7\u6863\u6848",
  HEALTH_PROFILE_COMPLETION_POINTS: "\u60A8\u83B7\u5F97\u4E86 {points} \u70B9\u79EF\u5206",
  OPENS_IN_A_NEW_TAB: "\u5728\u65B0\u7684\u6807\u7B7E\u9875\u4E2D\u6253\u5F00",
  COMPLETED: "\u5DF2\u5B8C\u6210",
  COMPLETED_X_TIMES: "{amount, plural, other {\u5DF2\u5B8C\u6210 {amount} \u6B21}}",
  REMOVE_FROM_JOURNEY: "\u4ECE\u65C5\u7A0B\u4E2D\u79FB\u9664",
  GO_TO_JOURNEY: "\u524D\u5F80\u65C5\u7A0B",
  ABOUT_THIS_PROGRAM: "\u5173\u4E8E\u672C\u8BA1\u5212",
  HEALTH_JOURNEY: "\u5065\u5EB7\u4E4B\u65C5",
  REMOVE_ACTIVITY: "\u79FB\u9664\u6D3B\u52A8\uFF1F",
  REMOVE_ACTIVITY_PROMPT: "\u79FB\u9664\u6D3B\u52A8",
  REMOVE_ACTIVITY_BUTTON: "\u79FB\u9664",
  ARE_YOU_SURE_REMOVE_ACTIVITY: "\u60A8\u786E\u5B9A\u8981\u4ECE\u60A8\u7684\u5065\u5EB7\u4E4B\u65C5\u4E2D\u79FB\u9664\u8FD9\u9879\u6D3B\u52A8\u5417\uFF1F\u6B64\u64CD\u4F5C\u65E0\u6CD5\u64A4\u9500\u3002",
  YOU_WILL_NO_LONGER_SEE_IN_JOURNEY: "\u60A8\u5C06\u4E0D\u4F1A\u5728\u65C5\u7A0B\u4E2D\u518D\u770B\u5230\u5B83",
  DISCOVER_ACTIVITIES: "\u53D1\u73B0\u65B0\u6D3B\u52A8",
  DISCOVER_ACTIVITIES_CAPTION: "\u6D4F\u89C8\u6211\u4EEC\u8BA4\u4E3A\u60A8\u4F1A\u559C\u6B22\u7684\u6D3B\u52A8",
  ACTIVITIES: "\u6D3B\u52A8",
  ACTIVITIES_PLURALIZATION: "{count, plural, other {\u6D3B\u52A8}}",
  ACTIVITIES_PILL: "\u6D3B\u52A8",
  PROGRAM_LIBRARY: "\u8BA1\u5212\u5E93",
  BROWSE_PROGRAMS: "\u6D4F\u89C8\u8BA1\u5212",
  BACK_TO_JOURNEY: "\u8FD4\u56DE\u65C5\u7A0B",
  GO_TO_HEALTH_JOURNEY: "\u524D\u5F80\u5065\u5EB7\u4E4B\u65C5",
  ADD_TO_JOURNEY: "\u6DFB\u52A0\u5230\u65C5\u7A0B",
  REQUEST_WAIT: "\u8BF7\u7A0D\u5019\uFF0C\u6B63\u5728\u5B8C\u6210\u60A8\u7684\u8BF7\u6C42",
  JOURNEY_DELIGHT_1: "\u6709\u52A8\u529B\u624D\u80FD\u8FC8\u5F00\u817F\u3002\u517B\u6210\u4E60\u60EF\u624D\u80FD\u575A\u6301\u8FD0\u52A8\u3002\u60A8\u4E00\u5B9A\u80FD\u505A\u5230\uFF01",
  JOURNEY_DELIGHT_2: "\u52A8\u8D77\u6765\u3002\u4FDD\u6301\u4E13\u6CE8\u3002\u8FC7\u597D\u81EA\u5DF1\u7684\u751F\u6D3B\u3002\u6211\u4EEC\u4E50\u4E8E\u770B\u5230\u5982\u6B64\u3002",
  JOURNEY_DELIGHT_3: "\u4E0D\u79EF\u7845\u6B65\uFF0C\u65E0\u4EE5\u81F3\u5343\u91CC\u3002\u60A8\u5DF2\u7ECF\u8FC8\u51FA\u4E86\u8FD9\u4E00\u6B65\uFF01",
  TOOLBOX_INPUT_OPTION_FREE_TEXT: "{hasValue, select, true {\u6B64\u9009\u9879\u53EF\u4EE5\u8F93\u5165\u4EFB\u610F\u5185\u5BB9\uFF1B\u4F7F\u7528\u8BE5\u6587\u672C\u6846\u8F93\u5165\u60A8\u81EA\u5DF1\u7684\u503C\u3002\u5F53\u524D\u503C\u4E3A\uFF1A{value}} other {\u6B64\u9009\u9879\u53EF\u4EE5\u8F93\u5165\u4EFB\u610F\u5185\u5BB9\uFF1B\u4F7F\u7528\u8BE5\u6587\u672C\u6846\u8F93\u5165\u60A8\u81EA\u5DF1\u7684\u503C\u3002\u5F53\u524D\u503C\u4E3A\uFF1A\u7A7A}}",
  TOOLBOX_INPUT_OPTION_EXCLUSIVE: "\u6B64\u9009\u9879\u5177\u6709\u6392\u4ED6\u6027\uFF1A\u9009\u62E9\u5B83\u5219\u4F1A\u53D6\u6D88\u9009\u62E9\u5176\u4ED6\u6240\u6709\u9009\u9879",
  ANIMATION_ACCESSIBILITY_PLAYBACK: "\u64AD\u653E\u6216\u6682\u505C\u5F53\u524D\u9009\u4E2D\u7684\u52A8\u753B",
  CENTIMETERS: "\u5398\u7C73",
  FEET: "\u811A",
  INCHES: "\u82F1\u5BF8",
  KILOGRAMS: "\u516C\u65A4",
  POUNDS: "\u78C5",
  EARN_UP_TO_X_POINTS: "\u6700\u9AD8\u53EF\u83B7\u5F97 {points} \u70B9\u79EF\u5206",
  EXPIRES_TODAY: "\u4ECA\u5929\u5230\u671F",
  PROGRAM_DETAILS: "\u8BA1\u5212\u8BE6\u60C5",
  PROGRESS: "\u8FDB\u5EA6",
  X_OF_Y_COMPLETE: "\u5DF2\u5B8C\u6210 {completed}/\u5171\u8BA1 {total}",
  X_OF_Y_COMPLETED_WITH_UNIT: "<span>{progress}</span> / {goal} {unit}",
  X_MISSED: "\u9519\u8FC7\u4E86 {missed}",
  MISSED: "\u9519\u8FC7",
  SCROLL_CAROUSEL_RIGHT: "\u5C06\u8F6E\u64AD\u56FE\u5411\u524D\u6EDA\u52A8",
  SCROLL_CAROUSEL_LEFT: "\u5C06\u8F6E\u64AD\u56FE\u5411\u540E\u6EDA\u52A8",
  SCROLL_CAROUSEL_FORWARD: "\u5C06\u8F6E\u64AD\u56FE\u5411\u524D\u6EDA\u52A8",
  SCROLL_CAROUSEL_BACK: "\u5C06\u8F6E\u64AD\u56FE\u5411\u540E\u6EDA\u52A8",
  YOU_HAVENT_STARTED_PROGRAMS: "\u60A8\u5C1A\u672A\u5F00\u59CB\u4EFB\u4F55\u5065\u5EB7\u8BA1\u5212",
  ADD_PROGRAM: "\u6DFB\u52A0\u8BA1\u5212",
  TAP_ADD_PROGRAM_BUTTON: "\u70B9\u51FB\u201C\u6DFB\u52A0\u8BA1\u5212\u201D\u6309\u94AE\u6D4F\u89C8\u8BA1\u5212\u3002",
  YOUVE_REACHED_PROGRAM_LIMIT: "\u60A8\u7684\u8BA1\u5212\u6570\u91CF\u5DF2\u7ECF\u8FBE\u5230\u4E0A\u9650\uFF01",
  START_X_MORE_PROGRAMS: "{duration, plural, other {\u518D\u5F00\u59CB {duration,number} \u4E2A\u8BA1\u5212\uFF0C\u4FDD\u6301\u826F\u597D\u52BF\u5934\u3002}}",
  OK: "\u597D\u7684",
  COMPLETE_ONE_OF_ACTIVE_PROGRAMS: "\u5B8C\u6210\u5DF2\u53C2\u52A0\u7684\u5176\u4E2D\u4E00\u4E2A\u8BA1\u5212\uFF0C\u5C06\u5176\u6DFB\u52A0\u5230\u60A8\u7684\u65C5\u7A0B\u4E2D\u3002",
  SHOW_LESS: "\u6536\u8D77",
  LOOKS_LIKE_REACHED_PROGRAM_LIMIT: "\u770B\u6765\u60A8\u7684\u8BA1\u5212\u6570\u91CF\u5DF2\u7ECF\u8FBE\u5230\u4E0A\u9650\uFF01",
  COMPLETE_ACTIVE_PROGRAM_BEFORE_NEW_ONE: "\u8BF7\u5148\u5B8C\u6210\u4E00\u4E2A\u5DF2\u53C2\u52A0\u7684\u8BA1\u5212\uFF0C\u7136\u540E\u518D\u6DFB\u52A0\u65B0\u7684\u8BA1\u5212\u3002<strong>{ctaText}</strong>",
  REMOVE_PROGRAM: "\u79FB\u9664\u8BA1\u5212\uFF1F",
  REMOVE_PROGRAM_BUTTON: "\u79FB\u9664",
  ARE_YOU_SURE_REMOVE_PROGRAM: "\u60A8\u786E\u5B9A\u8981\u4ECE\u60A8\u7684\u5065\u5EB7\u4E4B\u65C5\u4E2D\u79FB\u9664\u8FD9\u9879\u8BA1\u5212\u5417\uFF1F\u8FD9\u9879\u8BA1\u5212\u5DF2\u53D6\u5F97\u7684\u4EFB\u4F55\u8FDB\u5EA6\u90FD\u4F1A\u88AB\u91CD\u7F6E\uFF0C\u60A8\u9700\u8981\u4ECE\u5934\u518D\u6765\u3002",
  PROGRAM_REMOVED: "\u5DF2\u79FB\u9664\u8BA1\u5212",
  PROGRAM_ADDED: "\u5DF2\u6DFB\u52A0\u8BA1\u5212",
  THE_PROGRAM_WILL_APPEAR_IN_JOURNEY: "\u8BE5\u8BA1\u5212\u73B0\u5728\u4F1A\u663E\u793A\u5728\u60A8\u7684\u65C5\u7A0B\u4E2D",
  PROGRAM_ADD_ERROR: "\u6682\u65F6\u65E0\u6CD5\u6DFB\u52A0\u8BE5\u8BA1\u5212\uFF0C\u8BF7\u91CD\u8BD5\u6216\u9009\u62E9\u5176\u4ED6\u8BA1\u5212\u3002",
  PROGRAM_REMOVE_ERROR: "\u6682\u65F6\u65E0\u6CD5\u79FB\u9664\u8BE5\u8BA1\u5212\uFF0C\u8BF7\u91CD\u8BD5\u3002",
  SKIP_TO_CONTENT_MESSAGE: "\u8DF3\u81F3\u5185\u5BB9",
  MAIN_NAVIGATION_LIST_LABEL: "League \u5BFC\u822A\u9879",
  PAGE_TITLE_ACTIVITY_DETAILS: "\u6D3B\u52A8\u8BE6\u60C5",
  X_OF_Y: "{current}\u7684{total}",
  ACTIVITY_COUNT_EDIT: "\u7F16\u8F91{unit}",
  ACTIVITY_COUNT_GOAL: "\u76EE\u6807\uFF1A{goal,number} {unit}",
  ACTIVITY_COMPLETE_PROMPT: "\u8981\u5B8C\u6210\u8FD9\u9879\u6D3B\u52A8\uFF0C\u8BF7\u70B9\u51FB\u201C\u6807\u8BB0\u4E3A\u5B8C\u6210\u201D\u3002",
  ACTIVITY_COMPLETE_LATER_PROMPT: "\u8981\u7EE7\u7EED\uFF0C\u8BF7\u70B9\u51FB\u201C\u8FD4\u56DE\u65C5\u7A0B\u201D\uFF0C\u7A0D\u540E\u518D\u56DE\u6765\u6807\u8BB0\u4E3A\u5B8C\u6210\u3002",
  ACTIVITY_COMPLETE_ERROR: "\u6682\u65F6\u65E0\u6CD5\u5B8C\u6210\u8BE5\u6D3B\u52A8\uFF0C\u8BF7\u91CD\u8BD5\u3002",
  ACTIVITY_DISMISS_ERROR: "\u6682\u65F6\u65E0\u6CD5\u79FB\u9664\u8BE5\u6D3B\u52A8\uFF0C\u8BF7\u91CD\u8BD5\u3002",
  TOOLBOX_VALIDATION_DAY_MONTH_YEAR: "\u8BF7\u9009\u62E9\u5E74\u4EFD\u3001\u6708\u4EFD\u548C\u65E5\u671F",
  TOOLBOX_VALIDATION_MONTH_YEAR: "\u8BF7\u9009\u62E9\u5E74\u4EFD\u548C\u6708\u4EFD",
  TOOLBOX_VALIDATION_MAXIMUM_CHARACTERS: "\u5B57\u7B26\u9650\u5236\u4E3A {limit}",
  TOOLBOX_VALIDATION_NUMBER_MINIMUM: "\u8BF7\u8F93\u5165\u4E00\u4E2A\u4E0D\u5C0F\u4E8E {value} \u7684\u6570\u5B57",
  TOOLBOX_VALIDATION_NUMBER_MAXIMUM: "\u8BF7\u8F93\u5165\u4E00\u4E2A\u4E0D\u5927\u4E8E {value} \u7684\u6570\u5B57",
  TOOLBOX_VALIDATION_REQUIRED_FIELD: "\u8FD9\u662F\u5FC5\u586B\u5B57\u6BB5",
  TOOLBOX_VALIDATION_DATE_INPUT_REQUIRED: "\u8BF7\u9009\u62E9",
  TOOLBOX_VALIDATION_MULTI_SELECT_REQUIRED: "\u8BF7\u9009\u62E9",
  TOOLBOX_VALIDATION_SELECT_DATE_LATER: "\u8BF7\u9009\u62E9\u4E00\u4E2A\u665A\u4E8E {date,date,medium} \u7684\u65E5\u671F",
  TOOLBOX_VALIDATION_SELECT_DATE_EARLIER: "\u8BF7\u9009\u62E9\u4E00\u4E2A\u65E9\u4E8E {date,date,medium} \u7684\u65E5\u671F",
  TOOLBOX_VALIDATION_ENTER_WHOLE_NUMBER: "\u8BF7\u8F93\u5165\u4E00\u4E2A\u6574\u6570",
  TOOLBOX_VALIDATION_SELECT_MAX_SELECTION: "\u6700\u591A\u53EF\u4EE5\u9009\u62E9 {integer} \u4E2A\u9009\u9879",
  TOOLBOX_VALIDATION_SELECT_OPTIONS_TO_CONTINUE: "\u9009\u62E9{integer}\u9009\u9879\u4EE5\u7EE7\u7EED",
  TOOLBOX_VALIDATION_FIELD_CANNOT_BE_EMPTY: "\u9009\u62E9\u540E\uFF0C\u5B57\u6BB5\u4E0D\u80FD\u4E3A\u7A7A",
  TOOLBOX_VALIDATION_WEIGHT_MINIMUM: "\u4F53\u91CD\u9700\u8981\u4E0D\u4F4E\u4E8E {imperial}\u78C5 ({metric}\u516C\u65A4)",
  TOOLBOX_VALIDATION_WEIGHT_MAXIMUM: "\u4F53\u91CD\u9700\u8981\u4E0D\u9AD8\u4E8E {imperial}\u78C5 ({metric}\u516C\u65A4)",
  TOOLBOX_VALIDATION_HEIGHT_MINIMUM: "\u8EAB\u9AD8\u9700\u8981\u4E0D\u4F4E\u4E8E {imperial} ({metric} \u5398\u7C73)",
  TOOLBOX_VALIDATION_HEIGHT_MAXIMUM: "\u8EAB\u9AD8\u9700\u8981\u4E0D\u9AD8\u4E8E {imperial} ({metric} \u5398\u7C73)",
  ENTER_YOUR_WEIGHT_IN_KILOGRAMS: "\u8BF7\u8F93\u5165\u60A8\u7684\u4F53\u91CD\uFF08\u4EE5\u516C\u65A4\u4E3A\u5355\u4F4D\uFF09",
  ENTER_YOUR_WEIGHT_IN_POUNDS: "\u8BF7\u8F93\u5165\u60A8\u7684\u4F53\u91CD\uFF08\u4EE5\u78C5\u4E3A\u5355\u4F4D\uFF09",
  ENTER_YOUR_HEIGHT_IN_CENTIMETERS: "\u8BF7\u8F93\u5165\u60A8\u7684\u8EAB\u9AD8\uFF08\u4EE5\u5398\u7C73\u4E3A\u5355\u4F4D\uFF09",
  ENTER_YOUR_HEIGHT_IN_FEET: "\u8F93\u5165\u4F60\u7684\u8EAB\u9AD8\uFF08\u4EE5\u82F1\u5C3A\u4E3A\u5355\u4F4D\uFF09",
  ENTER_YOUR_HEIGHT_IN_INCHES: "\u8BF7\u8F93\u5165\u60A8\u7684\u8EAB\u9AD8\uFF08\u4EE5\u82F1\u5BF8\u4E3A\u5355\u4F4D\uFF09",
  GOAL_REACHED: "\u60A8\u5DF2\u8FBE\u6210\u76EE\u6807\uFF01",
  COMPLETE_ACTIVITY: "\u5B8C\u6210\u6D3B\u52A8",
  EXIT_ACTIVITY_TITLE: "\u60A8\u786E\u5B9A\u8981\u9000\u51FA\u8FD9\u9879\u6D3B\u52A8\u5417\uFF1F",
  EXIT_ACTIVITY_BODY: "\u4E0D\u4F1A\u4FDD\u5B58\u60A8\u7684\u8FDB\u5EA6\uFF0C\u60A8\u9700\u8981\u4ECE\u5934\u518D\u6765\u5F00\u59CB\u8FD9\u9879\u6D3B\u52A8\u3002",
  EXIT_ACTIVITY_CTA: "\u9000\u51FA\u6D3B\u52A8",
  ACTIVITY_NEXT_STEP: "\u7EE7\u7EED\u6D3B\u52A8",
  ACTIVITY_PREV_STEP: "\u8FD4\u56DE\u4E0A\u4E00\u6B65",
  PAGE_TITLE_HEALTH_PROGRAM_CATEGORY: "\u5065\u5EB7\u8BA1\u5212\u7C7B\u522B",
  NEXT_STEP: "\u4E0B\u4E00\u6B65",
  YOU_EARNED: "\u60A8\u5DF2\u83B7\u5F97",
  YOU_EARNED_X_POINTS: "\u60A8\u83B7\u5F97\u4E86 {points} \u70B9\u79EF\u5206\uFF01",
  CREATE: "\u521B\u5EFA{label}",
  TOMORROW: "\u660E\u5929",
  YESTERDAY: "\u6628\u5929",
  BACK_TO_TODAY: "\u56DE\u5230\u4ECA\u5929",
  PREVIOUS_DAY: "\u524D\u4E00\u5929",
  NEXT_DAY: "\u7B2C\u4E8C\u5929",
  ACTIVITY_STATUS: "\u6D3B\u52A8\u72B6\u6001\uFF1A ",
  ACTIVITY_STATUS_EXPIRED: "\u5DF2\u8FC7\u671F",
  ACTIVITY_STATUS_REMOVED: "\u5DF2\u8DF3\u8FC7",
  ACTIVITY_STATUS_UPCOMING: "\u5373\u5C06\u63A8\u51FA",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_OR_FUTURE_HEADING: "\u6682\u65F6\u8FD8\u6CA1\u6709\u8BA1\u5212\u4EFB\u4F55\u5185\u5BB9\uFF01",
  NO_ACTIVITIES_HAS_PROGRAMS_TODAY_DESCRIPTION: "\u70B9\u51FB\u201C\u6DFB\u52A0\u201D\u6309\u94AE\u67E5\u627E\u65B0\u7684\u8BA1\u5212\uFF0C\u6216\u6D4F\u89C8\u4E0B\u65B9\u7684\u7CBE\u9009\u6D3B\u52A8\u5217\u8868\u3002",
  NO_ACTIVITIES_COMPLETED_TODAY_HEADING: "\u5E72\u5F97\u597D\uFF0C\u60A8\u6210\u529F\u4E86\uFF01",
  NO_ACTIVITIES_COMPLETED_TODAY_DESCRIPTION: "\u660E\u5929\u56DE\u6765\u67E5\u770B\u66F4\u591A\u4FE1\u606F\uFF0C\u6216\u6D4F\u89C8\u65B0\u7684\u8BA1\u5212\u548C\u63A8\u8350\u7684\u6D3B\u52A8\u3002",
  NO_ACTIVITIES_FUTURE_DESCRIPTION: "\u60A8\u5728\u8FD9\u5929\u6CA1\u6709\u5B89\u6392\u4EFB\u4F55\u6D3B\u52A8\u3002",
  NO_ACTIVITIES_PAST_HEADING: "\u4F11\u606F\u65E5",
  NO_ACTIVITIES_PAST_DESCRIPTION: "\u770B\u6765\u60A8\u5C06\u8FD9\u5929\u7528\u6765\u8FDB\u884C\u653E\u677E\u548C\u6062\u590D\u4E86\u3002",
  BUTTON_BROWSE_PROGRAMS: "\u6D4F\u89C8\u8BA1\u5212",
  IMG_ALT_HIGH_FIVE_HANDS: "\u4E24\u53EA\u624B\u51FB\u638C",
  IMG_ALT_EMPTY_STATE_PROGRAMS: "\u4E00\u4F4D\u5973\u58EB\u7AD9\u7ACB\u7740\u6EDA\u52A8\u4E00\u9762\u5899\u90A3\u4E48\u5927\u7684\u8BA1\u5212\u5217\u8868",
  IMG_ALT_EMPTY_STATE_REST: "\u4E00\u4F4D\u7537\u58EB\u6234\u7740\u8033\u673A\u5750\u5728\u8212\u670D\u7684\u6905\u5B50\u4E0A\u4F11\u606F\uFF0C\u624B\u4E2D\u7684\u676F\u5B50\u5192\u7740\u70ED\u6C14",
  THIS_IS_JUST_A_SNEAK_PEAK: "\u8FD9\u8FD8\u53EA\u662F\u5148\u7779\u4E3A\u5FEB\uFF01",
  COME_BACK_COMPLETE_ACTIVITY_ON_DATE: "\u8BF7\u4E8E {date} \u56DE\u6765\u5E76\u5B8C\u6210\u8FD9\u9879\u6D3B\u52A8\u3002",
  COME_BACK_COMPLETE_ACTIVITY_LATER: "\u8BF7\u7A0D\u540E\u56DE\u6765\u5B8C\u6210\u8FD9\u9879\u6D3B\u52A8\u3002",
  LOOKS_LIKE_YOU_MISSED_THIS_ONE: "\u770B\u6765\u4F60\u9519\u8FC7\u4E86\u8FD9\u4E2A\uFF01",
  THIS_ACTIVITY_EXPIRED_ON_DATE: "\u6B64\u6D3B\u52A8\u5DF2\u4E8E {date} \u8FC7\u671F\u3002",
  LOOKS_LIKE_THIS_WASNT_MEANT_FOR_YOU: "\u770B\u6765\u8FD9\u9879\u6D3B\u52A8\u4E0D\u9002\u5408\u60A8",
  YOU_SKIPPED_THIS_ACTIVITY: "\u60A8\u8DF3\u8FC7\u4E86\u8FD9\u9879\u6D3B\u52A8\u3002",
  NO_PROGRAMS: "\u6682\u65F6\u6CA1\u6709\u4EFB\u4F55\u5185\u5BB9\u53EF\u4EE5\u67E5\u770B",
  NO_ACTIVITY_TITLE: "\u8FD9\u91CC\u6682\u65F6\u6CA1\u6709\u4EFB\u4F55\u5185\u5BB9",
  NO_LEADERBOARD_ACTIVITY_DESCRIPTION: "\u6311\u6218\u5F00\u59CB\u540E\uFF0C\u6211\u4EEC\u4F1A\u5728\u8FD9\u91CC\u663E\u793A\u60A8\u7684\u6392\u540D\u3002",
  NO_HIGHLIGHTS_ACTIVITY_DESCRIPTION: "\u8BF7\u7A0D\u540E\u56DE\u5230\u8FD9\u91CC\u67E5\u770B\u6311\u6218\u7684\u7CBE\u5F69\u4EAE\u70B9\u3002",
  LEADERBOARD_ALL_TIME_RANKING: "\u5386\u53F2\u603B\u6392\u540D",
  LEADERBOARD_CURRENT_USER_NAME: "{user}\uFF08\u60A8\uFF09",
  ACTIVITY_PROGRESS: "\u6D3B\u52A8\u8FDB\u5EA6",
  YOUR_JOURNEY: "\u4F60\u7684\u65C5\u7A0B",
  SUGGESTED_FOR_YOU: "\u4E3A\u60A8\u63D0\u4F9B\u7684\u5EFA\u8BAE",
  NO_ACHIEVEMENTS_DESCRIPTION: "\u5B8C\u6210\u201C\u65C5\u7A0B\u201D\u4E2D\u7684\u6D3B\u52A8\uFF0C\u5F00\u59CB\u89E3\u9501\u6210\u5C31\u3002",
  UNLOCK_ACHIEVEMENTS: "\u5B8C\u6210\u672C\u8BA1\u5212\u4E2D\u7684\u6240\u6709\u6D3B\u52A8\uFF0C\u53EF\u89E3\u9501\u4E00\u9879\u6210\u5C31\u3002",
  MEMBERS: "\u6210\u5458",
  SIGN_UP_PERIOD_PASSED_DETAIL: "\u7531\u4E8E\u6CE8\u518C\u671F\u5DF2\u8FC7\uFF0C\u60A8\u4E0D\u80FD\u518D\u6CE8\u518C\u53C2\u52A0\u8FD9\u9879\u6311\u6218\u3002",
  REFERENCES: "\u53C2\u8003\u6587\u732E",
  SIGN_UP_BY: "\u6CE8\u518C\u622A\u6B62\u65E5\u671F\uFF1A{date}",
  YOURE_GOOD_TO_GO: "\u60A8\u53EF\u4EE5\u5F00\u59CB\u4E86\uFF01",
  CHECK_CHALLENGE_DETAILS: "\u67E5\u770B\u6311\u6218\u8BE6\u60C5\uFF0C\u8BA9\u81EA\u5DF1\u719F\u6089\u6D3B\u52A8\u5185\u5BB9",
  CHALLENGE_LETS_START_TEAMS_ENABLED: "\u60A8\u53EF\u4EE5\u968F\u65F6\u5F00\u59CB\u6311\u6218\u3002\u60F3\u8981\u52A0\u5165\u56E2\u961F\uFF0C\u8FD8\u662F\u81EA\u5DF1\u521B\u5EFA\u56E2\u961F\uFF1F",
  CHALLENGE_ABOUT_TO_START: "\u60A8\u7684\u6311\u6218\u5373\u5C06\u5F00\u59CB\uFF01",
  ACTIVITIES_APPEAR_HERE_WHEN_CHALLENGE_STARTS: "\u60A8\u7684\u6311\u6218\u5728 <strong>{date}</strong> \u5F00\u59CB\u540E\uFF0C\u8FD9\u91CC\u4F1A\u663E\u793A\u65E5\u5E38\u6D3B\u52A8\u3002",
  NO_CHALLENGE_ACTIVITIES_TODAY: "\u4ECA\u5929\u6CA1\u6709\u6311\u6218\u6D3B\u52A8\u3002",
  COVERAGE_SEARCH_QUERY_NOT_FOUND: "\u65E0\u6CD5\u627E\u5230\u201C{query}\u201D\u3002",
  COVERAGE_SEARCH_TRY_AGAIN: "\u8BF7\u5C1D\u8BD5\u4F7F\u7528\u5176\u4ED6\u5173\u952E\u8BCD\uFF0C\u518D\u6B21\u8FDB\u884C\u641C\u7D22\u3002",
  EARN: "\u8D5A\u53D6",
  WORD_WITH_EXCLAMATION_POINT: "{word}\uFF01",
  UPDATED: "\u5DF2\u66F4\u65B0",
  YOUR_PROGRESS: "\u60A8\u7684\u8FDB\u5EA6",
  YOUR_PROGRESS_IN_NUMBERS: "{current}/{total} {unit}\u6216\u5DF2\u5B8C\u6210 {percentage}",
  OUTCOMES_FRICTION_SCREEN_TITLE: "\u4E2A\u6027\u5316\u5EFA\u8BAE\u2026\u2026",
  OUTCOMES_FRICTION_SCREEN_DESCRIPTION: "\u8BF7\u60A8\u7A0D\u7B49\uFF0C\u6211\u4EEC\u4F1A\u6839\u636E\u60A8\u7684\u56DE\u7B54\u4E3A\u60A8\u627E\u5230\u4E00\u4E9B\u6709\u5E2E\u52A9\u7684\u8D44\u6E90",
  RELOAD_MY_SUGGESTIONS: "\u91CD\u65B0\u52A0\u8F7D\u6211\u7684\u5EFA\u8BAE",
  YOUR_RANKING: "\u60A8\u7684\u6392\u540D",
  TOP_RANKED: "\u6700\u9AD8\u6392\u540D",
  VIEW_CHALLENGE_DETAILS: "\u67E5\u770B\u6311\u6218\u8BE6\u60C5",
  LEAGUE_FAMILY_EDUCATION_BANNER_HEADER: "\u60F3\u77E5\u9053\u6DFB\u52A0\u53D7\u629A\u517B\u4EBA\u6709\u54EA\u4E9B\u597D\u5904\uFF1F",
  LEAGUE_FAMILY_EDUCATION_BANNER_SUBHEADER: "\u60A8\u7684\u53D7\u629A\u517B\u4EBA\u4E5F\u53EF\u4EE5\u8BBF\u95EE League\uFF0C\u4EAB\u53D7\u66F4\u5065\u5EB7\u7684\u751F\u6D3B\uFF01",
  UNABLE_TO_LEAVE_CHALLENGE: "\u65E0\u6CD5\u9000\u51FA\u6311\u6218\uFF0C\u8BF7\u91CD\u8BD5\uFF01",
  SUCCESSFULLY_LEFT_CHALLENGE: "\u60A8\u5DF2\u6210\u529F\u9000\u51FA\u6311\u6218\u3002",
  UNABLE_TO_JOIN_CHALLENGE: "\u65E0\u6CD5\u52A0\u5165\u6311\u6218\uFF0C\u8BF7\u91CD\u8BD5\uFF01",
  JOIN_TEAM: "\u52A0\u5165\u56E2\u961F",
  X_TEAM_MEMBERS: "{memberTotal, plural, other {{memberTotal,number} \u540D\u56E2\u961F\u6210\u5458}}",
  X_SPOTS_AVAILABLE: "{spotsAvailable, plural, other {{spotsAvailable,number} \u4E2A\u53EF\u7528\u540D\u989D}}",
  HIGHLIGHTS: "\u4EAE\u70B9",
  CURRENT_RANK: "\u5F53\u524D\u6392\u540D",
  X_LIKES: "{activityLikes, plural, other {{activityLikes,number} \u4E2A\u8D5E}}",
  LIKE: "\u559C\u6B22",
  LIKED: "\u5DF2\u70B9\u8D5E",
  TEAM_INVITE_HEADING: "\u4E00\u8D77\u8FD0\u52A8\u6548\u679C\u66F4\u4F73\uFF01",
  TEAM_INVITE_DESCRIPTION: "\u9080\u8BF7\u670B\u53CB\u52A0\u5165\u60A8\u7684\u56E2\u961F\uFF0C\u4E00\u8D77\u5B8C\u6210\u6D3B\u52A8\u3002",
  TEAM_INVITE_LINK_DESCRIPTION: "\u4EBA\u6570\u8D8A\u591A\uFF0C\u4E50\u8DA3\u8D8A\u591A\uFF01\u4E0E\u670B\u53CB\u5206\u4EAB\u6B64\u94FE\u63A5\uFF0C\u9080\u8BF7\u4ED6\u4EEC\u52A0\u5165\u60A8\u7684\u56E2\u961F\u3002",
  TEAM_INVITE_LINK: "\u56E2\u961F\u9080\u8BF7\u94FE\u63A5",
  INVITE: "\u9080\u8BF7",
  LEAVE_TEAM: "\u79BB\u5F00\u56E2\u961F",
  INVITE_FRIENDS: "\u9080\u8BF7\u670B\u53CB",
  PRIVACY: "\u9690\u79C1\u8BBE\u7F6E",
  JOIN_TEAM_SUCCESS_TITLE: "\u6B22\u8FCE\u52A0\u5165\u56E2\u961F\uFF01",
  JOIN_TEAM_SUCCESS_DETAIL: "\u8BA9\u6211\u4EEC\u4E00\u8D77\u5F81\u670D\u8FD9\u4E9B\u6D3B\u52A8\u3002",
  JOIN_TEAM_SUCCESS_BUTTON: "\u524D\u5F80\u56E2\u961F\u8BE6\u60C5",
  CANT_JOIN_TEAM_TITLE: "\u65E0\u6CD5\u52A0\u5165{teamName}",
  CANT_JOIN_TEAM_DETAIL: "\u8BF7\u91CD\u8BD5\uFF01\u5982\u679C\u60A8\u5DF2\u7ECF\u52A0\u5165\u4E86\u67D0\u4E2A\u56E2\u961F\uFF0C\u5219\u9700\u8981\u5148\u9000\u51FA\u5F53\u524D\u7684\u56E2\u961F\uFF0C\u7136\u540E\u624D\u80FD\u52A0\u5165{teamName}\u3002",
  CANT_JOIN_TEAM_JOIN_CHALLENGE_DETAIL: "\u770B\u6765\u60A8\u8FD8\u6CA1\u6709\u6CE8\u518C\u53C2\u52A0\u8BE5\u6311\u6218\u3002\u60A8\u9700\u8981\u5148\u6CE8\u518C\uFF0C\u7136\u540E\u624D\u80FD\u52A0\u5165{teamName}\u3002",
  LEAVE_TEAM_QUESTION: "\u79BB\u5F00 {teamName}\uFF1F",
  LEAVE_TEAM_DESCRIPTION: "\u60A8\u786E\u5B9A\u8981\u9000\u51FA{teamName}\u5417\uFF1F\u60A8\u7684\u5206\u6570\u4E0D\u4F1A\u518D\u8BA1\u5165\u56E2\u961F\u7684\u5E73\u5747\u5206\u4E4B\u4E2D\u3002",
  CHALLENGE_ACTIVITY_DETAIL: "\u5141\u8BB8\u5176\u4ED6\u4EBA\u67E5\u770B\u60A8\u5728\u8FD9\u9879\u6311\u6218\u4E2D\u7684\u6D3B\u52A8\u3002",
  SHOW_MY_ACTIVITY: "\u663E\u793A\u6211\u7684\u6D3B\u52A8",
  YOUR_TEAM: "\u60A8\u7684\u56E2\u961F",
  JOIN_OR_CREATE_TEAM: "\u52A0\u5165\u6216\u521B\u5EFA\u56E2\u961F",
  TEAMS: "\u56E2\u961F",
  CHALLENGE_TEAMS_DESCRIPTION: "\u52A0\u5165\u73B0\u6709\u56E2\u961F\u6216\u521B\u5EFA\u5C5E\u4E8E\u60A8\u81EA\u5DF1\u7684\u56E2\u961F\uFF0C\u548C\u5176\u4ED6\u6210\u5458\u4E00\u8D77\u5B8C\u6210\u6311\u6218\uFF01",
  JOIN_A_TEAM: "\u52A0\u5165\u56E2\u961F",
  JOIN_TEAM_DESCRIPTION: "\u641C\u7D22\u73B0\u6709\u56E2\u961F\u52A0\u5165\uFF0C\u548C\u5176\u4ED6\u6210\u5458\u4E00\u8F83\u9AD8\u4E0B\uFF01",
  SEARCH_FOR_TEAM: "\u641C\u7D22\u56E2\u961F",
  CREATE_A_TEAM: "\u521B\u5EFA\u56E2\u961F",
  CREATE_TEAM_DESCRIPTION: "\u8BBE\u7F6E\u81EA\u5DF1\u7684\u5934\u50CF\u548C\u961F\u540D\uFF0C\u81EA\u5B9A\u4E49\u60A8\u7684\u56E2\u961F\u3002\u9080\u8BF7\u5176\u4ED6\u4EBA\u52A0\u5165\uFF01",
  UP_YOUR_GAME: "\u5347\u7EA7\u6E38\u620F",
  UP_YOUR_GAME_DESCRIPTION: "\u4EBA\u591A\u529B\u91CF\u5927\uFF01\u52A0\u5165\u56E2\u961F\u6216\u521B\u5EFA\u81EA\u5DF1\u7684\u56E2\u961F\u3002",
  XTH_PLACE: "{currentRank, selectordinal, one {#st} two {#nd} few {#rd} other {#th} } \u5730\u70B9",
  LEADERBOARD_RANKING: "\u6392\u540D",
  LEADERBOARD_USERNAME: "\u7528\u6237\u540D",
  INVITE_LINK_COPIED_TO_CLIPBOARD: "\u9080\u8BF7\u94FE\u63A5\u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F\uFF01",
  DATE_AND_TIME: "\u65E5\u671F\u548C\u65F6\u95F4",
  SELECT_TEAM_AVATAR: "\u9009\u62E9\u56E2\u961F\u5934\u50CF",
  CHANGE_TEAM_AVATAR: "\u66F4\u6539\u56E2\u961F\u5934\u50CF",
  TEAM_NAME_LABEL: "\u961F\u540D",
  CREATE_TEAM: "\u521B\u5EFA\u56E2\u961F",
  TEAM_NAME_ERROR: "\u8BF7\u4E3A\u60A8\u7684\u56E2\u961F\u8F93\u5165\u4E00\u4E2A\u540D\u5B57\u3002",
  SLIDE_OF_TOTAL: "\u7B2C {key} \u5F20/\u5171 {total} \u5F20\u5E7B\u706F\u7247",
  PAGE_OF_TOTAL: "\u7B2C {current} \u9875/\u5171 {total} \u9875",
  NO_SOLUTIONS_AVAILABLE: "\u5F88\u62B1\u6B49\uFF0C\u76EE\u524D\u6682\u65F6\u6CA1\u6709\u89E3\u51B3\u65B9\u6848\u3002",
  SEE_ALL_ACHIEVEMENTS: "\u67E5\u770B\u6240\u6709\u6210\u5C31",
  VIEW_ALL_ACHIEVEMENTS: "\u67E5\u770B\u6240\u6709\u6210\u5C31",
  TAKE_ME_BACK: "\u5E26\u6211\u8FD4\u56DE",
  TEAMS_SELECT_AVATAR_DESCRIPTION: "\u6B64\u5934\u50CF\u663E\u793A\u5728\u60A8\u56E2\u961F\u7684\u6863\u6848\u548C\u79EF\u5206\u699C\u4E0A\u3002",
  CREATE_TEAM_SUCCESS: "\u60A8\u7684\u56E2\u961F\u5DF2\u7ECF\u51C6\u5907\u5C31\u7EEA\uFF01",
  CREATE_TEAM_SUCCESS_DESCRIPTION: "\u5F00\u59CB\u7EC4\u5EFA\u56E2\u961F\u3002\u4E0E\u5176\u4ED6\u4EBA\u5206\u4EAB\u6B64\u94FE\u63A5\uFF0C\u4E00\u8D77\u5B8C\u6210\u6D3B\u52A8\u3002",
  VIEW_TEAM_DETAILS: "\u67E5\u770B\u56E2\u961F\u8BE6\u60C5",
  FEATURES_HIGHLIGHT_CAROUSEL_LABEL: "\u529F\u80FD\u4EAE\u70B9\u8F6E\u64AD",
  CHALLENGE_FAQ_HEADING: "\u5BF9\u6311\u6218\u6709\u7591\u95EE\uFF1F",
  CHALLENGE_FAQ_CONTENT: "\u70B9\u51FB {helpCenterLink}\uFF0C\u67E5\u770B\u6240\u6709\u5E38\u89C1\u95EE\u9898\u89E3\u7B54\u3002",
  VIEW_THIS_RESOURCE: "\u67E5\u770B\u6B64\u8D44\u6E90",
  RECOMMENDED_NEXT_STEP: "\u5EFA\u8BAE\u91C7\u53D6\u7684\u4E0B\u4E00\u6B65",
  CARE_COLLECTIONS: "\u7167\u62A4\u7CFB\u5217",
  PLAN_PROGRESS: "\u8BA1\u5212\u8FDB\u5EA6",
  SINCE_LAST_WEEK: "\u81EA\u4E0A\u5468\u4EE5\u6765",
  INSIGHT_CARD_HEADER_DISTANCE: "\u8DDD\u79BB",
  INSIGHT_CARD_HEADER_STEPS: "\u6B65\u9AA4",
  MIN: "\u5206\u949F",
  CAL: "\u5361\u8DEF\u91CC",
  KM: "\u516C\u91CC",
  INSIGHT_CARD_UNIT_STEPS: "{count, plural, other {\u6B65}}",
  INSIGHT_CARD_HEADER_FLOORS_CLIMBED: "\u6500\u722C\u822A\u73ED\u6570",
  INSIGHT_CARD_UNIT_FLOORS_CLIMBED: "{count, plural, other {\u822A\u73ED}}",
  INSIGHT_CARD_HEADER_ACTIVE_DURATION: "\u8FD0\u52A8\u5206\u949F\u6570",
  INSIGHT_CARD_HEADER_ENERGY_BURNED: "\u6D88\u8017\u7684\u80FD\u91CF",
  INSIGHT_CARD_HEADER_WEIGHT: "\u4F53\u91CD",
  INSIGHT_CARD_HEADER_HEIGHT: "\u8EAB\u9AD8",
  INSIGHT_CARD_HEADER_MINDFUL_DURATION: "\u6B63\u5FF5\u5206\u949F\u6570",
  INSIGHT_CARD_HEADER_WAIST_CIRCUMFERENCE: "\u8170\u56F4",
  DAY_OF_THE_WEEK: "\u661F\u671F\u51E0",
  DAY_OF_THE_MONTH: "\u672C\u6708\u5177\u4F53\u65E5\u671F",
  INSIGHT_CHART_WEEK_TITLE: "\u6BCF\u5468{type} \u67F1\u72B6\u56FE",
  INSIGHT_CHART_MONTH_TITLE: "\u6BCF\u6708{type}\u67F1\u72B6\u56FE",
  INSIGHT_CHART_DISTANCE_WEEK_DESCRIPTION: "\u672C\u5468\u6BCF\u5929\u8D70\u7684{unit}\u6570\u91CF\u3002",
  INSIGHT_CHART_DISTANCE_MONTH_DESCRIPTION: "\u672C\u6708\u6BCF\u5929\u8D70\u7684 {unit} \u6570\u91CF\u3002",
  INSIGHT_CHART_DISTANCE_DATUM_LABEL: "\u60A8\u5728 {date} \u8D70\u4E86 {distance} {unit}",
  INSIGHT_CHART_STEPS_WEEK_DESCRIPTION: "\u60A8\u5728\u672C\u5468\u6BCF\u5929\u8D70\u7684\u6B65\u6570\u3002",
  INSIGHT_CHART_STEPS_MONTH_DESCRIPTION: "\u60A8\u672C\u6708\u6BCF\u5929\u884C\u8D70\u7684\u6B65\u6570\u3002",
  INSIGHT_CHART_STEPS_DATUM_LABEL: "\u60A8\u5728 {date} \u8D70\u4E86 {distance} {unit}",
  INSIGHT_CHART_ENERGY_BURNED_WEEK_DESCRIPTION: "\u60A8\u5728\u672C\u5468\u6BCF\u5929\u71C3\u70E7\u7684 {unit} \u6570\u91CF\u3002",
  INSIGHT_CHART_ENERGY_BURNED_MONTH_DESCRIPTION: "\u60A8\u5728\u672C\u6708\u6BCF\u5929\u71C3\u70E7\u7684 {unit} \u6570\u91CF\u3002",
  INSIGHT_CHART_ENERGY_BURNED_DATUM_LABEL: "\u60A8\u5728 {date} \u71C3\u70E7\u4E86 {energy} {unit}",
  INSIGHT_CHART_ACTIVE_DURATION_WEEK_DESCRIPTION: "\u672C\u5468\u6BCF\u5929\u7684\u8FD0\u52A8\u5206\u949F\u6570\u3002",
  INSIGHT_CHART_ACTIVE_DURATION_MONTH_DESCRIPTION: "\u672C\u6708\u6BCF\u5929\u7684\u8FD0\u52A8\u5206\u949F\u6570\u3002",
  INSIGHT_CHART_ACTIVE_DURATION_DATUM_LABEL: "\u60A8\u5728 {date} \u8FD0\u52A8\u4E86 {value} \u5206\u949F",
  INSIGHT_CHART_FLOORS_CLIMBED_WEEK_DESCRIPTION: "\u60A8\u672C\u5468\u6BCF\u5929\u4E58\u5750\u7684\u822A\u73ED\u6570\u91CF\u3002",
  INSIGHT_CHART_FLOORS_CLIMBED_MONTH_DESCRIPTION: "\u60A8\u672C\u6708\u7B2C\u5929\u4E58\u5750\u7684\u822A\u73ED\u6570\u91CF\u3002",
  INSIGHT_CHART_FLOORS_CLIMBED_DATUM_LABEL: "\u60A8\u5728 {date} \u722C\u4E86 {value} \u7EA7\u53F0\u9636",
  INSIGHT_CHART_MINDFUL_DURATION_WEEK_DESCRIPTION: "\u672C\u5468\u6BCF\u5929\u7684\u6B63\u5FF5\u5206\u949F\u6570\u3002",
  INSIGHT_CHART_MINDFUL_DURATION_MONTH_DESCRIPTION: "\u672C\u6708\u6BCF\u5929\u7684\u6B63\u5FF5\u5206\u949F\u6570\u3002",
  INSIGHT_CHART_MINDFUL_DURATION_DATUM_LABEL: "\u60A8\u5728 {date} \u8FDB\u884C\u4E86 {value} \u5206\u949F\u6B63\u5FF5",
  DATAPOINT_CATEGORY_ACTIVITY: "\u6D3B\u52A8",
  DATAPOINT_CATEGORY_BODY: "\u8EAB\u4F53",
  MANAGE_UNITS_APPLYING_CHANGES: "\u6B63\u5728\u5E94\u7528\u66F4\u6539\u2026\u2026",
  MANAGE_UNITS_CHANGES_SAVED: "\u5DF2\u4E8E {time} \u4FDD\u5B58\u4E86\u66F4\u6539",
  START_TRACKING_HEALTH_HEADER: "\u5F00\u59CB\u8FFD\u8E2A\u60A8\u7684\u5065\u5EB7\u72B6\u51B5",
  START_TRACKING_HEALTH_BODY: "\u8F7B\u677E\u8DDF\u8E2A\u60A8\u5728\u4E00\u6BB5\u65F6\u95F4\u5185\u7684\u6307\u6807\uFF0C\u4E86\u89E3\u60A8\u7684\u8FDB\u5EA6\u4EE5\u53CA\u5982\u4F55\u624D\u80FD\u65E9\u65E5\u5B9E\u73B0\u5065\u5EB7\u76EE\u6807\u3002",
  BROWSE_SUPPORTED_DATA: "\u53EF\u4EE5\u8DDF\u8E2A\u54EA\u4E9B\u6307\u6807\uFF1F",
  LAST_UPDATED_CARRIER_DATE: "\u6700\u540E\u66F4\u65B0\uFF1A{date}\uFF0C\u6765\u81EA\u4E8E {carrier}",
  ACCUMULATION_PERIOD: "\u7D2F\u79EF\u671F\uFF1A<strong>{start} - {end}</strong>",
  NO_DATA_AVAILABLE: "\u65E0\u53EF\u7528\u6570\u636E",
  LAST_7_DAYS: "\u6700\u8FD1 7 \u5929",
  LAST_30_DAYS: "\u6700\u8FD1 30 \u5929",
  OLDER: "\u8001\u5E74",
  GET_ASSISTANCE_NOW: "\u4E0E\u60A8\u7684\u7167\u62A4\u56E2\u961F\u804A\u5929",
  GET_ASSISTANCE_NOW_SUBHEADING: "\u4E0E\u6211\u4EEC\u7ECF\u8FC7\u8BA4\u8BC1\u7684\u533B\u7597\u4E13\u4E1A\u4EBA\u58EB\u548C\u5BA2\u6237\u7167\u62A4\u5DE5\u4F5C\u4EBA\u5458\u804A\u5929\u3002\u4ED6\u4EEC\u53EF\u4EE5\u6307\u5BFC\u60A8\u627E\u5230\u60A8\u6240\u5728\u533A\u57DF\u6700\u7ECF\u6D4E\u5B9E\u60E0\u3001\u6700\u4F18\u8D28\u7684\u62A4\u7167\u670D\u52A1\uFF0C\u5E2E\u52A9\u60A8\u8282\u7701\u652F\u51FA\u3002\u8FD8\u80FD\u56DE\u7B54\u5173\u4E8E\u60A8\u6240\u4EAB\u798F\u5229\u7684\u4EFB\u4F55\u95EE\u9898\u3002",
  HOW_CAN_WE_HELP: "\u6211\u4EEC\u80FD\u4E3A\u60A8\u63D0\u4F9B\u54EA\u4E9B\u5E2E\u52A9\uFF1F",
  LIVE_BALANCE_SPEND: "\u5DF2\u82B1\u8D39 {spentAmount}/\u5171 {totalAmount}",
  LIVE_BALANCE_REMAINING: "\u8FD8\u5269 {amount}",
  UPDATING_UNITS_OF_MEASURE: "\u66F4\u65B0\u5EA6\u91CF\u5355\u4F4D",
  CHANGING_YOUR_UNITS: "\u66F4\u6539\u5355\u4F4D\u4F1A\u5F71\u54CD\u5B83\u4EEC\u5728\u5E94\u7528\u7A0B\u5E8F\u4E2D\u7684\u663E\u793A\u65B9\u5F0F\u3002",
  MESSAGES: "\u6D88\u606F",
  MESSAGING_MESSAGES_LANDING_PAGE_TITLE: "\u6D88\u606F",
  MESSAGING_FILE_REMOVAL_ANNOUNCEMENT: "{fileName} \u5DF2\u88AB\u5220\u9664\u3002",
  SEND_NEW_MESSAGE: "\u53D1\u9001\u65B0\u6D88\u606F",
  NEW_MESSAGE_DEFAULT: "\u8FD9\u4E00\u6BB5\u65B0\u6D88\u606F\u7684\u5F00\u573A\u767D",
  NETWORK_MISSING_COVERAGE: "{network}\u4E0D\u5728\u60A8\u8BA1\u5212\u7684\u4FDD\u9669\u8303\u56F4\u5185\u3002",
  CARD_FRONT_FILENAME: "{benefitName}-\u6B63\u9762",
  CARD_BACK_FILENAME: "{benefitName}-\u80CC\u9762",
  START_A_VISIT: "\u5F00\u59CB\u8BBF\u95EE",
  UNREAD: "\u672A\u8BFB",
  NO_MESSAGES_HEADING: "\u5C1A\u65E0\u6D88\u606F",
  NO_MESSAGES_DESCRIPTION: "\u53D1\u9001\u65B0\u6D88\u606F\u5373\u53EF\u5F00\u59CB\u804A\u5929\uFF01",
  RETURN_TO: "\u8FD4\u56DE\u5230{location}",
  NO_RECOMENDATIONS: "\u5F88\u62B1\u6B49\uFF0C\u6211\u4EEC\u6682\u65F6\u5BF9\u6B64\u5065\u5EB7\u9886\u57DF\u6CA1\u6709\u4EFB\u4F55\u5EFA\u8BAE\u3002",
  CHALLENGES_LEGAL_DISCLAIMER: "\u8FD9\u9879\u6311\u6218\u65E8\u5728\u4EE5\u6709\u8DA3\u7684\u65B9\u5F0F\u8BA9\u6BCF\u4E2A\u4EBA\u90FD\u53C2\u4E0E\u8FDB\u6765\uFF0C\u5E76\u62E5\u6709\u5747\u7B49\u7684\u673A\u4F1A\u83B7\u5F97\u80DC\u5229\u3002\u6311\u6218\u7EC4\u7EC7\u8005\u53EF\u968F\u65F6\u63D0\u4F9B\u73B0\u91D1\u6216\u5176\u4ED6\u5956\u54C1\u3002League \u4E0D\u8D1F\u8D23\u8BBE\u8BA1\u6311\u6218\u7684\u7ED3\u6784\uFF0C\u4E5F\u4E0D\u51B3\u5B9A\u662F\u5426\u63D0\u4F9B\u5956\u54C1\u5982\u679C\u5BF9\u4E8E\u8FD9\u9879\u6311\u6218\u7684\u7ED3\u6784\u6709\u4EFB\u4F55\u7591\u95EE\uFF0C\u6216\u89C9\u5F97\u81EA\u5DF1\u6839\u672C\u6CA1\u6709\u673A\u4F1A\u83B7\u80DC\uFF0C\u8BF7\u4E0E\u6311\u6218\u7EC4\u7EC7\u65B9\u8FDB\u884C\u6C9F\u901A\u3002",
  ALL_RECORDED_DATA: "\u8BB0\u5F55\u7684\u6240\u6709\u6570\u636E",
  NEW_MESSAGE: "\u65B0\u6D88\u606F",
  MESSAGE_SUBJECT: "\u4FE1\u606F\u4E3B\u9898\uFF08\u53EF\u9009\uFF09",
  THREAD_SUBJECT_MAX_LENGTH_ERROR: "\u5DF2\u8D85\u8FC7\u5B57\u7B26\u6570\u4E0A\u9650 ({maximum})",
  CHAR_LIMIT_EXCEEDED: "\u5B57\u7B26\u9650\u5236\u8D85\u8FC7 ({maximum})",
  MESSAGE_SUBJECT_PLACEHOLDER: "\u8BBE\u7F6E\u4FE1\u606F\u4E3B\u9898",
  TO: "\u6536\u4EF6\u4EBA",
  CREATE_MESSAGE: "\u521B\u5EFA\u6D88\u606F",
  MEMBER_LABEL: "\u6210\u5458",
  RESULT_COUNT: "{count, plural, other {{count,number} \u4E2A\u7ED3\u679C}}",
  USER_COLON: "{user}\uFF1A",
  JOINED_CHALLENGE_HIGHLIGHT: "\u52A0\u5165\u4E86 <strong>{challengeTitle}</strong>\uFF01",
  CREATED_TEAM_HIGHLIGHT: "\u521B\u5EFA\u4E86 <strong>{teamName}</strong>\uFF01",
  JOINED_TEAM_HIGHLIGHT: "\u52A0\u5165\u4E86 <strong>{teamName}</strong>\uFF01",
  STEPS_WALKED_HIGHLIGHT: "<strong>\u8D70\u4E86 {total} \u6B65\uFF01</strong>",
  ACTIVE_MINUTES_HIGHLIGHT: "<strong>\u8FD0\u52A8\u4E86 {total} \u5206\u949F\uFF01</strong>",
  MINDFUL_MINUTES_HIGHLIGHT: "<strong>\u5DF2\u5B8C\u6210{total}\u5206\u949F\uFF01</strong>",
  EARNED_ACHIEVEMENT_HIGHLIGHT: "<strong>\u83B7\u5F97\u4E86 {achievement} \u6210\u5C31\uFF01</strong>",
  COMPLETED_ACTIVITY_HIGHLIGHT: "<strong>\u5B8C\u6210\u4E86\u4E00\u9879\u6D3B\u52A8\uFF01</strong>",
  NO_CARE_COLLECTIONS: "\u5F88\u62B1\u6B49\uFF0C\u6CA1\u6709\u53EF\u7528\u7684\u7167\u62A4\u7CFB\u5217\u3002",
  WRITE_A_MESSAGE: "\u7F16\u5199\u6D88\u606F\u2026\u2026",
  UPLOAD_MULTIPLE_FILES: "\u4E0A\u4F20\u591A\u4E2A\u6587\u4EF6",
  SEND: "\u53D1\u9001",
  SEND_MESSAGE: "\u53D1\u9001\u6D88\u606F",
  DATA_SOURCES: "\u6570\u636E\u6765\u6E90",
  SOURCES_CAN_UPDATE: "\u5141\u8BB8\u4E0B\u5217\u6E90\u6765\u66F4\u65B0\u60A8\u7684",
  WHEN_MULTIPLE_SOURCES_AVAILABLE: "\u6709\u591A\u4E2A\u6E90\u65F6\uFF0C\u4E00\u5929\u7ED3\u675F\u65F6\uFF08\u5348\u591C 11:59\uFF09\u62E5\u6709\u6700\u5927\u503C\u7684\u6570\u636E\u6765\u6E90\u5C06\u4F1A\u8BB0\u5F55\u4E0B\u6765\u3002",
  APPLE_HEALTH: "Apple Health",
  GOOGLE_FIT: "Google Fit",
  FITBIT: "Fitbit",
  GARMIN: "\u4F73\u660E",
  POLAR: "\u535A\u80FD",
  WITHINGS: "Withings",
  HEALTH_PLAN: "\u5065\u5EB7\u8BA1\u5212",
  OMRON: "\u6B27\u59C6\u9F99",
  STRAVA: "Strava",
  IHEALTH: "iHealth",
  UPDATE_UNITS_OF_MEASURE_MODAL_TITLE: "\u66F4\u65B0\u6D4B\u91CF\u5355\u4F4D",
  UPDATE_UNITS_OF_MEASURE_MODAL_DESCRIPTION: "\u66F4\u6539\u5355\u4F4D\u4F1A\u5F71\u54CD\u5B83\u4EEC\u5728\u5E94\u7528\u7A0B\u5E8F\u4E2D\u7684\u663E\u793A\u65B9\u5F0F",
  DOCUMENTS: "\u6587\u4EF6",
  DOCUMENTS_DOCUMENTS_LANDING_PAGE_TITLE: "\u6587\u4EF6",
  DOCUMENTS_FILTER_LOADING_TEXT: "\u6B63\u5728\u52A0\u8F7D\u9009\u9879\u5361\u548C\u7B5B\u9009\u5668",
  DOCUMENTS_FILTER_X_IS_REQUIRED: "{filter} \u662F\u5FC5\u586B\u9879",
  DOCUMENT_FILTER_X_REQUIRED: "{filter}\uFF08\u5FC5\u586B\u9879\uFF09",
  SHOW_RESULTS: "\u663E\u793A\u7ED3\u679C",
  RESET_FILTERS: "\u91CD\u7F6E\u8FC7\u6EE4\u5668",
  DOCUMENTS_SELECTED_FILTERS_HEADING: "\u9009\u5B9A\u7684\u7B5B\u9009\u5668",
  DOCUMENTS_RESULTS_HEADING: "\u7ED3\u679C",
  DOCUMENTS_SKIP_MECHANISM_UPLOAD_DOCUMENT: "\u8DF3\u8FC7\u4EE5\u4E0A\u4F20\u6587\u4EF6",
  AVERAGE: "\u5E73\u5747",
  VIEW_ALL_METRIC_DATA: "\u67E5\u770B\u6240\u6709{metric}\u6570\u636E",
  EDIT_METRIC_DATA: "\u7F16\u8F91{metric}\u6570\u636E",
  NEXT_WEEK: "\u4E0B\u5468",
  PREVIOUS_WEEK: "\u4E0A\u5468",
  NEXT_30_DAYS: "\u672A\u6765 30 \u5929",
  PREVIOUS_30_DAYS: "\u524D 30 \u5929",
  UNABLE_TO_CREATE_TEAM: "\u65E0\u6CD5\u521B\u5EFA\u65B0\u56E2\u961F - \u8BF7\u91CD\u8BD5\uFF01\u5982\u679C\u60A8\u5DF2\u7ECF\u52A0\u5165\u4E86\u67D0\u4E2A\u56E2\u961F\uFF0C\u5219\u9700\u8981\u5148\u9000\u51FA\u5F53\u524D\u7684\u56E2\u961F\uFF0C\u7136\u540E\u624D\u80FD\u65B0\u5EFA\u56E2\u961F\u3002",
  START_TRACKING_METRIC: "\u5F00\u59CB\u8DDF\u8E2A{metric}",
  CURRENTLY_NOT_SHARING_DATA: "\u60A8\u76EE\u524D\u6CA1\u6709\u5206\u4EAB{metric}\u7684\u4EFB\u4F55\u6570\u636E",
  SUCCESSFULLY_LEFT_TEAM: "\u60A8\u5DF2\u6210\u529F\u9000\u51FA{teamName}",
  LEAVE_TEAM_FAILURE: "\u51FA\u9519\u4E86\uFF0C\u8BF7\u91CD\u8BD5\uFF01",
  VIEW_DATA_SOURCES: "\u67E5\u770B\u6570\u636E\u6E90",
  OR_CALL_PHONE: "\u6216\u81F4\u7535 {phoneNumber}{extension}",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_HEADING: "\u6CA1\u6709\u6587\u4EF6\u4E0E\u60A8\u7684\u7B5B\u9009\u5668\u5339\u914D",
  DOCUMENTS_NO_FILTERED_RESULTS_EMPTY_STATE_DESCRIPTION: "\u8BF7\u5C1D\u8BD5\u79FB\u9664\u4E00\u4E9B\u7B5B\u9009\u5668\uFF0C\u4EE5\u83B7\u5F97\u66F4\u591A\u7ED3\u679C",
  DOCUMENTS_EMPTY_STATE_HEADING: "\u8FD9\u91CC\u6682\u65F6\u8FD8\u6CA1\u6709\u4EFB\u4F55\u6587\u4EF6",
  DOCUMENTS_EMPTY_STATE_DESCRIPTION: "\u4E00\u7AD9\u5F0F\u67E5\u770B\u548C\u6574\u7406\u60A8\u7684\u6240\u6709\u6587\u4EF6",
  LEADERBOARD_CURRENT_TEAM_NAME: "{team}\uFF08\u60A8\u7684\u56E2\u961F\uFF09",
  YOUR_CARE_ADVISOR: "\u60A8\u7684\u62A4\u7406\u987E\u95EE",
  COMPOSE: "\u7F16\u5199",
  COMPOSE_NEW_MESSAGE: "\u7F16\u5199\u65B0\u6D88\u606F",
  POLICY_INFORMATION_UNAVAILABLE: "\u5173\u4E8E\u8BE5\u4FDD\u5355\u671F\u9650\uFF0C\u6CA1\u6709\u4EFB\u4F55\u4FE1\u606F\u3002",
  POLICY_INFORMATION_UNAVAILABLE_DEPENDENTS: "\u6CA1\u6709\u8BE5\u6210\u5458\u7684\u4FE1\u606F\uFF0C\u8BF7\u8BD5\u8BD5\u5176\u4ED6\u6210\u5458\u3002",
  MESSAGE_THREADS_LIST_SUBTITLE: "\u6211\u4EEC\u7684\u56E2\u961F\u5C06\u5728 2 \u4E2A\u5DE5\u4F5C\u65E5\u5185\u56DE\u590D\u60A8\u5982\u679C\u9047\u5230\u533B\u7597\u7D27\u6025\u60C5\u51B5\uFF0C\u8BF7\u62E8\u6253 911\u3002",
  ALL_MESSAGES_FOR_THREAD_BANNER: "\u8BF7\u4E0D\u8981\u901A\u8FC7\u6B64\u804A\u5929\u6846\u5411\u6211\u4EEC\u53D1\u9001\u4E0E\u60A8\u5065\u5EB7\u72B6\u51B5\u76F8\u5173\u7684\u5177\u4F53\u4FE1\u606F\u3002",
  TROUBLE_DISPLAYING_DATA: "\u663E\u793A\u60A8\u7684\u6570\u636E\u65F6\u9047\u5230\u4E86\u95EE\u9898",
  RETRY: "\u91CD\u8BD5",
  YOU: "\u4F60",
  MESSAGES_IN_CONVERSATION_TITLED: "\u6807\u9898\u4E3A\u201C{subject}\u201D\u7684\u5BF9\u8BDD\u4E2D\u7684\u6D88\u606F",
  TEAM_AVERAGE: "\u56E2\u961F\u5E73\u5747\u503C",
  YOUR_METRICS: "\u6211\u7684\u6307\u6807",
  YOUR_METRICS_SUBTITLE: "\u6765\u81EA\u60A8\u5DF2\u8FDE\u63A5\u7684\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\u7684\u6700\u65B0\u5065\u5EB7\u6570\u636E\u3002",
  APPS_AND_DEVICES: "\u5E94\u7528\u548C\u8BBE\u5907",
  APP_AND_DEVICE_METRICS: "\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\u6307\u6807",
  VIEW_ALL_METRICS: "\u67E5\u770B\u5E94\u7528\u7A0B\u5E8F\u548C\u8BBE\u5907\u6307\u6807",
  RENAME: "\u91CD\u547D\u540D",
  ENTER_DOCUMENT_NAME: "\u8BF7\u8F93\u5165\u6587\u6863\u540D\u79F0",
  LOW_PRIORITY_ACTIVITY_ALT_TEXT: "\u8FD9\u662F\u4E00\u9879\u4F4E\u4F18\u5148\u7EA7\u7684\u6D3B\u52A8",
  MEDIUM_PRIORITY_ACTIVITY_ALT_TEXT: "\u8FD9\u662F\u4E00\u9879\u4E2D\u4F18\u5148\u7EA7\u7684\u6D3B\u52A8",
  HIGH_PRIORITY_ACTIVITY_ALT_TEXT: "\u8FD9\u662F\u4E00\u9879\u9AD8\u4F18\u5148\u7EA7\u7684\u6D3B\u52A8",
  ACTIVITY_EXPIRY_DATE: "\u5230\u671F\u65E5\u671F\uFF1A{date}",
  GENERIC_MESSAGING_ERROR: "\u770B\u6765\u51FA\u73B0\u4E86\u95EE\u9898\uFF0C\u8BF7\u5237\u65B0\u9875\u9762\uFF0C\u7136\u540E\u91CD\u8BD5\u3002",
  VIEW_THE_ACTIVITIES: "\u67E5\u770B\u6D3B\u52A8",
  DRAG_FILES_HERE_OR: "\u5C06\u6587\u4EF6\u62D6\u653E\u5230\u6B64\u5904\uFF0C\u6216\u8005",
  ADD_A_DOCUMENT: "\u6DFB\u52A0\u6587\u4EF6",
  DOCUMENT_HEADER_UPLOAD_BUTTON: "\u4E0A\u4F20\u6587\u4EF6",
  ADD_DOCUMENT: "\u6DFB\u52A0\u6587\u6863",
  REMOVE_FILE: "\u79FB\u9664\u6587\u4EF6",
  REMOVE_FILE_FILENAME: "\u79FB\u9664\u6587\u4EF6\uFF1A{filename} ({fileSize})",
  UNABLE_TO_SEND: "\u65E0\u6CD5\u53D1\u9001\uFF0C\u8BF7\u91CD\u8BD5",
  ERROR_HEADING: "\u6211\u4EEC\u8FD9\u8FB9\u51FA\u4E86\u70B9\u95EE\u9898\u3002",
  FILE_TYPE_NOT_SUPPORTED: "\u4E0D\u652F\u6301\u7684\u6587\u4EF6\u7C7B\u578B",
  USE_FILE_IN_ONE_OF_THESE_FORMATS: "\u8BF7\u4F7F\u7528\u4EE5\u4E0B\u683C\u5F0F\u4E4B\u4E00\u7684\u6587\u4EF6\uFF1A{fileTypes}",
  RECENT_MESSAGES: "\u6700\u8FD1\u7684\u6D88\u606F",
  NO_MESSAGES_YET: "\u6682\u65F6\u8FD8\u6CA1\u6709\u6D88\u606F",
  NO_MESSAGES_DETAIL: "\u60A8\u6307\u5B9A\u7684\u62A4\u7406\u987E\u95EE\u5C06\u5E2E\u52A9\u60A8\u5728\u6BCF\u4E00\u6B65\u4E2D\u505A\u51FA\u66F4\u660E\u667A\u7684\u533B\u7597\u4FDD\u5065\u51B3\u5B9A\u3002",
  START_A_NEW_MESSAGE: "\u5F00\u59CB\u65B0\u6D88\u606F",
  USER_ADDED_DOCUMENT: "{user} \u6DFB\u52A0\u4E86\u4E00\u4E2A\u6587\u4EF6",
  ERROR_SAVING_TRY_AGAIN: "\u4FDD\u5B58\u66F4\u6539\u65F6\u51FA\u9519\uFF0C\u8BF7\u91CD\u8BD5\u3002",
  MODIFIED_ON_DATE: "\u4FEE\u6539\u65E5\u671F\uFF1A{date}",
  DOCUMENTS_DOCUMENT_UPLOADED_DATE: "\u4E0A\u4F20\u4E86 {date}",
  FILE_TYPE_TOO_LARGE: "\u6587\u4EF6\u592A\u5927\u4E86",
  MAXIMUM_FILE_SIZE_IS: "\u6700\u5927\u7684\u6587\u4EF6\u5927\u5C0F\u4E3A {fileSize}\u3002",
  FILE_TOO_LARGE: "\u54CE\u5440\uFF01\u6587\u4EF6\u592A\u5927\u4E86\u3002",
  FILE_SHOULD_BE_UNDER: "\u60A8\u7684\u6587\u4EF6\u5FC5\u987B\u5C0F\u4E8E {fileSize}",
  ATTACHMENT_LIMIT_REACHED: "\u54CE\u5440\uFF01\u6587\u4EF6\u592A\u591A\u4E86\u3002",
  MAXIMUM_NUM_OF_ATTACHMENTS_ALLOWED: "{maxNumberOfDocs, plural, other {\u60A8\u6700\u591A\u53EF\u4EE5\u9644\u52A0 {maxNumberOfDocs,number} \u4E2A\u6587\u4EF6\u3002}}",
  OOPS_FILE_TYPE_ISNT_SUPPORTED: "\u54CE\u5440\uFF01\u4E0D\u652F\u6301\u8BE5\u6587\u4EF6\u7C7B\u578B\u3002",
  ATTACHMENT_TYPES_ALLOWED: "\u60A8\u53EF\u4EE5\u9644\u52A0\u4EE5\u4E0B\u683C\u5F0F\u7684\u6587\u4EF6\uFF1A{fileTypes}",
  MESSAGES_PAGINATION_ERROR: "\u65E0\u6CD5\u52A0\u8F7D\u4FE1\u606F\uFF0C<button>\u8BF7\u91CD\u8BD5\u3002</button>",
  TRANSACTION_TABLE_CAPTION: "\u4EA4\u6613\u5386\u53F2",
  SELECT_PARTICIPANT_ERROR: "\u9009\u62E9\u8981\u7ED9\u5BF9\u65B9\u53D1\u9001\u4FE1\u606F\u7684\u67D0\u4EBA",
  PREVIEW_OF_FILE: "\u9884\u89C8{fileName}",
  VIEW_DOCUMENT_ACTIONS: "\u67E5\u770B {documentName} \u64CD\u4F5C\u3002",
  VISIT_LINK: "\u8BBF\u95EE\u94FE\u63A5",
  COMPLETE_BY_DATE: "\u5728{date}\u524D\u5B8C\u6210",
  ERROR_UPLOADING_YOUR_FILE: "\u4E0A\u4F20\u6587\u4EF6\u65F6\u51FA\u9519\uFF0C\u8BF7\u91CD\u8BD5\u3002",
  DOCUMENT_SUBMITTED: "\u6587\u4EF6\u5DF2\u63D0\u4EA4",
  YOUR_DOCUMENT_WILL_BE_AVAILABLE_SOON: "\u60A8\u7684\u6587\u4EF6\u5C06\u5F88\u5FEB\u53EF\u7528\u3002\u8BF7\u7A0D\u7B49\u7247\u523B\uFF0C\u7136\u540E\u5237\u65B0\u9875\u9762\u3002",
  ERROR_RENAMING_YOUR_DOCUMENT: "\u91CD\u547D\u540D\u6587\u4EF6\u65F6\u51FA\u9519\uFF0C\u8BF7\u91CD\u8BD5\u3002",
  DOCUMENT_RENAMED_SUCCESSFULLY: "\u5DF2\u6210\u529F\u547D\u540D\u6587\u4EF6\u3002",
  ERROR_HEADING_GENERIC: "\u51FA\u4E86\u95EE\u9898",
  ERROR_HEADING_NO_CONNECTION: "\u6CA1\u6709\u7F51\u7EDC\u8FDE\u63A5",
  ERROR_CAPTION_BACKEND: "\u8FDE\u63A5\u5230\u6211\u4EEC\u7684\u670D\u52A1\u5668\u65F6\u51FA\u73B0\u95EE\u9898\u3002\n \u8BF7\u7A0D\u540E\u518D\u8BD5\u3002",
  ERROR_CAPTION_NO_CONNECTION: "\u52A0\u8F7D\u6570\u636E\u65F6\u51FA\u73B0\u95EE\u9898\u3002\u8BF7\u68C0\u67E5\u60A8\u7684\u4E92\u8054\u7F51\uFF0C\u7136\u540E\u91CD\u8BD5\u3002",
  SURE_YOU_WANT_TO_DELETE: "\u60A8\u786E\u5B9A\u8981\u5220\u9664{name}\u5417\uFF1F",
  CANNOT_BE_UNDONE: "\u6B64\u64CD\u4F5C\u65E0\u6CD5\u64A4\u9500\u3002",
  DELETE_DOCUMENT: "\u5220\u9664\u6587\u6863",
  DOCUMENT_DELETED: "\u6587\u4EF6\u5DF2\u5220\u9664",
  ERROR_DELETING_YOUR_DOCUMENT: "\u5220\u9664\u60A8\u7684\u6587\u4EF6\u65F6\u51FA\u9519",
  ABOUT_ME: "\u5173\u4E8E\u6211",
  SEE_MORE: "\u67E5\u770B\u66F4\u591A",
  X_ABOUT_X: "{left}\u5173\u4E8E{right}",
  UNABLE_TO_LOAD_PAGE_TRY_AGAIN: "\u65E0\u6CD5\u52A0\u8F7D\u6B64\u9875\u9762\u3002\u8BF7\u68C0\u67E5\u60A8\u7684\u4E92\u8054\u7F51\uFF0C\u7136\u540E\u91CD\u8BD5\u3002",
  WHO_IS_THIS_DOCUMENT_ABOUT: "\u8FD9\u4EFD\u6587\u4EF6\u662F\u5173\u4E8E\u8C01\u7684\uFF1F",
  SELECT_A_USER: "\u9009\u62E9\u7528\u6237",
  SELECT_DOCUMENT: "\u9009\u62E9\u6587\u6863",
  THIS_HELPS_IDENTIFY_PERSON_DOCUMENT_RELATES_TO: "\u8FD9\u6709\u52A9\u4E8E\u60A8\u7684\u62A4\u7406\u987E\u95EE\u8BC6\u522B\u672C\u6587\u4EF6\u6D89\u53CA\u7684\u4EBA\u5458\u3002",
  VIEW_ALL_MESSAGE_THREADS: "\u67E5\u770B\u6240\u6709\u6D88\u606F\u8D34\u5B50",
  TOGGLE_MENU: "\u5207\u6362\u6D88\u606F\u5217\u8868",
  METRICS: "\u6307\u6807",
  UNREAD_MESSAGES: "\u672A\u8BFB\u6D88\u606F",
  WITH_DOCUMENT: "\uFF08\u5E26\u6709\u6587\u4EF6",
  X_TOKENS: "{tokenCount, plural, other {{tokenCount,number} \u4E2A\u4EE4\u724C\uFF1F}}",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_EYEBROW: "\u8BA1\u5212\u548C\u6311\u6218",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_HEADING: "\u51C6\u5907\u597D\u63A5\u53D7\u65B0\u5185\u5BB9\u4E86\u5417\uFF1F",
  NO_CONFIGURABLE_ACTIVITIES_EMPTY_STATE_DESCRIPTION: "\u67E5\u627E\u7B26\u5408\u60A8\u4E2A\u4EBA\u76EE\u6807\u7684\u5065\u5EB7\u8BA1\u5212\u548C\u6311\u6218\u3002",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_EYEBROW: "\u8BA1\u5212\u548C\u6311\u6218",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_HEADING: "\u51C6\u5907\u597D\u63A5\u53D7\u65B0\u5185\u5BB9\u4E86\u5417\uFF1F",
  NO_CONFIGURABLE_PROGRESS_EMPTY_STATE_DESCRIPTION: "\u67E5\u627E\u7B26\u5408\u60A8\u4E2A\u4EBA\u76EE\u6807\u7684\u5065\u5EB7\u8BA1\u5212\u548C\u6311\u6218\u3002",
  EXPLORE_LIBRARY: "\u6D4F\u89C8\u8D44\u6599\u5E93",
  YOUVE_GOT_THIS: "\u4F60\u4E00\u5B9A\u80FD\u505A\u5230\uFF01",
  YOUVE_GOT_THIS_NAME: "\u60A8\u6CA1\u95EE\u9898\u7684\uFF0C{firstName}\uFF01",
  CONFIGURABLE_JOURNEY_HEADER_SUB_TITLE: "\u6B22\u8FCE\u5F00\u542F\u60A8\u7684\u5065\u5EB7\u4E4B\u65C5\uFF01",
  HEALTH_JOURNEY_PAGE: "\u5065\u5EB7\u4E4B\u65C5\u9875\u9762",
  VIEW_DETAILS: "\u67E5\u770B\u8BE6\u60C5",
  NEXT_ACTIVITY: "\u4E0B\u4E00\u4E2A\u6D3B\u52A8",
  X_OF_Y_ACTIVITIES: "\u5DF2\u5B8C\u6210 {completed}/\u5171\u6709 {total} \u4E2A\u6D3B\u52A8",
  X_PERCENTAGE: "{percent}%",
  SOLUTIONS: "\u89E3\u51B3\u65B9\u6848",
  EXPLORE: "\u63A2\u7D22",
  DOCUMENT_NAME_TOO_LONG: "\u5DF2\u8D85\u8FC7\u5B57\u7B26\u6570\u4E0A\u9650 ({maximum})",
  CLAIM_PATIENT_NAME: "\u5BF9\u4E8E\uFF1A{patientName}",
  STANDALONE_ACTIVITIES_DESCRIPTION: "\u6CA1\u6709\u8BA1\u5212\u3002\u6CA1\u6709\u6311\u6218\u3002\u5C3D\u5728\u6D3B\u52A8\u4E2D\u3002",
  FEEDBACK_LINK_LABEL: "\u5728\u5E2E\u52A9\u7A0B\u5EA6\u4E0A\u770B\uFF0C\u4FE1\u606F\u8BC4\u5206\u4E3A {value}\uFF0C\u5171 {maximum}",
  RECENT_ACHIEVEMENTS: "\u8FD1\u671F\u6210\u5C31",
  ACHIEVEMENTS_BANNER_HEADER: "\u606D\u559C\u60A8\u83B7\u5F97\u8FD9\u4E9B\u5FBD\u7AE0\uFF01",
  SINGLE_ACHIEVEMENT_DESCRIPTION: "\u6211\u4EEC\u4E50\u4E8E\u770B\u5230\u60A8\u4F18\u5148\u8003\u8651\u81EA\u5DF1\u7684\u5065\u5EB7\u548C\u5E78\u798F\uFF01\u67E5\u770B\u60A8\u6B63\u5728\u52AA\u529B\u5B9E\u73B0\u7684\u5176\u4ED6\u6210\u5C31\u3002",
  MULTIPLE_ACHIEVEMENT_DESCRIPTION: "\u6211\u4EEC\u4E50\u4E8E\u770B\u5230\u60A8\u4F18\u5148\u8003\u8651\u81EA\u5DF1\u7684\u5065\u5EB7\u548C\u5E78\u798F\uFF01\u67E5\u770B\u60A8\u83B7\u5F97\u7684\u5176\u4ED6\u6210\u5C31\uFF0C\u4EE5\u53CA\u60A8\u6B63\u5728\u52AA\u529B\u5B9E\u73B0\u7684\u6210\u5C31\u3002",
  ACHIEVEMENTS_EMPTY_STATE_HEADER: "\u5C1A\u65E0\u6210\u5C31\uFF01",
  ACHIEVEMENTS_EMPTY_STATE_DESCRIPTION: "\u5B8C\u6210\u201C\u65C5\u7A0B\u201D\u4E2D\u7684\u6D3B\u52A8\uFF0C\u5F00\u59CB\u83B7\u5F97\u6210\u5C31\u3002",
  HISTORY_EMPTY_STATE_HEADER: "\u8FD9\u91CC\u6682\u65F6\u8FD8\u6CA1\u6709\u4EFB\u4F55\u5185\u5BB9\uFF01",
  HISTORY_EMPTY_STATE_DESCRIPTION: "\u5B8C\u6210\u4E00\u4E2A\u6D3B\u52A8\u3001\u8BA1\u5212\u6216\u6311\u6218\u540E\uFF0C\u4F1A\u51FA\u73B0\u5728\u8FD9\u91CC\u3002",
  INDIVIDUAL_BALANCE: "{membersFullName}\u7684{balance}",
  FAMILY_BALANCE: "\u5BB6\u5EAD {balance}",
  UPCOMING: "\u5373\u5C06\u63A8\u51FA",
  REMOVED: "\u79FB\u9664",
  COMPLETED_TODAY: "\u5DF2\u5B8C\u6210",
  CLAIMS_BENEFIT_CARD_TITLE: "\u4FDD\u9669\u7D22\u8D54",
  CLAIMS_BENEFIT_CARD_SUBTITLE: "\u8DDF\u8E2A\u60A8\u62E5\u6709\u54EA\u4E9B\u7167\u62A4\uFF0C\u4EE5\u53CA\u4FDD\u9669\u5C06\u4F1A\u627F\u4FDD\u7684\u8303\u56F4",
  CLAIMS_FILTER_PROPERTY_TYPE: "\u670D\u52A1\u7C7B\u578B",
  CLAIMS_FILTER_PROPERTY_STATUS: "\u7D22\u8D54\u72B6\u6001",
  CLAIMS_FILTER_PROPERTY_PATIENTNAME: "\u6210\u5458",
  CLAIMS_FILTER_VALUE_ORAL: "\u7259\u79D1",
  CLAIMS_FILTER_VALUE_INSTITUTIONAL: "\u533B\u7597",
  CLAIMS_FILTER_VALUE_VISION: "\u613F\u666F",
  CLAIMS_FILTER_VALUE_PHARMACY: "\u836F\u623F",
  CLAIMS_FILTER_VALUE_PROFESSIONAL: "\u4E13\u4E1A\u4EBA\u58EB",
  CLAIMS_FILTER_VALUE_APPROVED: "\u6211\u4EEC\u5C06\u4F1A\u652F\u4ED8\u6211\u4EEC\u7684\u90E8\u5206",
  CLAIMS_FILTER_VALUE_PENDING: "\u5904\u7406\u4E2D",
  CLAIMS_FILTER_VALUE_PARTIALLY_PAID: "\u90E8\u5206\u8986\u76D6",
  CLAIMS_FILTER_VALUE_NOT_COVERED: "\u672A\u8986\u76D6",
  CLAIMS_FILTER_VALUE_ADJUSTED: "\u8C03\u6574\u540E",
  DRINKS_PER_WEEK: "\u6BCF\u5468\u996E\u9152\u91CF",
  OR_SELECT_BETWEEN_THESE: "\u6216\u5728\u4EE5\u4E0B\u9009\u9879\u4E2D\u9009\u62E9",
  CLAIMS_FILTER_PROPERTY_SERVICEDATE: "\u65E5\u671F\u8303\u56F4",
  CLAIMS_FILTER_VALUE_YEAR_TO_DATE: "\u622A\u81F3\u76EE\u524D",
  CLAIMS_FILTER_VALUE_PRIOR_YEAR: "\u524D\u4E00\u5E74",
  CLAIMS_FILTER_VALUE_LAST_12_MONTHS: "\u8FC7\u53BB 12 \u4E2A\u6708",
  CLAIMS_FILTER_VALUE_LAST_2_YEARS: "\u8FC7\u53BB 2 \u5E74",
  CLAIMS_VIEW_ALL: "\u67E5\u770B\u5168\u90E8",
  CHALLENGE_TYPE_GROUP_TAG: "\u56E2\u4F53",
  CHALLENGE_TYPE_SOLO_TAG: "\u5355\u4EBA",
  VIEW_ALL_GROUP_CHALLENGES_HEADING: "\u56E2\u4F53\u6311\u6218",
  VIEW_ALL_SOLO_CHALLENGES_HEADING: "\u5355\u4EBA\u6311\u6218",
  VIEW_ALL_CHALLENGES_DESCRIPTION_GROUP: "\u901A\u8FC7\u8FDB\u884C\u6B63\u5FF5\u3001\u5065\u5EB7\u996E\u98DF\u8BA1\u5212\u3001\u5065\u8EAB\u6BD4\u8D5B\u3001\u89E3\u538B\u6D3B\u52A8\u3001\u6B65\u6570\u6311\u6218\u7B49\uFF0C\u6311\u6218\u81EA\u5DF1\uFF0C\u505A\u81EA\u5DF1\u7684\u4E3B\u4EBA\u3002",
  VIEW_ALL_CHALLENGES_DESCRIPTION_SOLO: "\u72EC\u81EA\u4E00\u4EBA\u6311\u6218\uFF0C\u5B8C\u6210\u4E13\u6CE8\u4E8E\u8EAB\u4F53\u7D20\u8D28\u3001\u8425\u517B\u3001\u5FC3\u7406\u5065\u5EB7\u3001\u538B\u529B\u7BA1\u7406\u7B49\u7684\u6D3B\u52A8\u3002",
  HELPFUL_LINKS: "\u5B9E\u7528\u94FE\u63A5",
  HOW_CLAIMS_WORK: "\u7D22\u8D54\u6D41\u7A0B",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TITLE: "\u4E86\u89E3\u60A8\u7684\u7D22\u8D54\u662F\u5982\u4F55\u8FD0\u4F5C\u7684",
  UNDERSTANDING_HOW_YOUR_CLAIMS_WORK_TEXT: "<p>\u5F53\u60A8\u9884\u7EA6\u81EA\u5DF1\u7F51\u7EDC\u4E2D\u7684\u4E00\u4F4D\u533B\u751F\u5C31\u8BCA\u65F6\uFF0C\u5BF9\u65B9\u4F1A\u8BF7\u6C42\u4ED8\u6B3E\u3002\u5982\u679C\u60A8\u6709\u5171\u4ED8\u989D\uFF0C\u4ED6\u4EEC\u4F1A\u5411\u60A8\u6536\u53D6\uFF0C\u7136\u540E\u5411\u4FDD\u9669\u516C\u53F8\u63D0\u4EA4\u7D22\u8D54\u3002</p><p>\u7136\u540E\uFF0C\u6211\u4EEC\u4F1A\u5BA1\u67E5\u60A8\u7684\u4FDD\u9669\u8BA1\u5212\uFF08\u4E5F\u79F0\u4E3A\u798F\u5229\u5408\u540C\uFF09\uFF0C\u4EE5\u786E\u5B9A\u670D\u52A1\u662F\u5426\u5728\u627F\u4FDD\u8303\u56F4\u5185\u3002\u5982\u679C\u662F\uFF0C\u6211\u4EEC\u5C06\u6839\u636E\u798F\u5229\u5408\u540C\u7684\u6761\u6B3E\u4E88\u4EE5\u6279\u51C6\u3002\u5982\u679C\u8BE5\u670D\u52A1\u4E0D\u5728\u798F\u5229\u8BA1\u5212\u7684\u627F\u4FDD\u8303\u56F4\u5185\uFF0C\u5219\u60A8\u6709\u8D23\u4EFB\u652F\u4ED8\u8BE5\u670D\u52A1\u7684\u8D39\u7528\u3002</p>",
  CLAIMS_STATUS_DEFINITIONS: "\u7D22\u8D54\u72B6\u6001\u5B9A\u4E49",
  CLAIMS_STATUS_DEFINITION_APPROVED: "\u6211\u4EEC\u5C06\u4F1A\u652F\u4ED8\u6211\u4EEC\u7684\u90E8\u5206\uFF0C\u4E5F\u5C31\u662F\u83B7\u5F97\u6279\u51C6\u7684\u7D22\u8D54\uFF0C\u4E14\u540C\u610F\u6839\u636E\u60A8\u7684\u8BA1\u5212\u652F\u4ED8\u90E8\u5206\u6216\u5168\u90E8\u7D22\u8D54\u3002",
  CLAIMS_STATUS_DEFINITION_PENDING: "\u201C\u5904\u7406\u4E2D\u201D\u610F\u5473\u7740\u6211\u4EEC\u6B63\u5728\u5F04\u6E05\u695A\u4F55\u4EBA\u6B20\u8D39\u591A\u5C11\u3002",
  CLAIMS_STATUS_DEFINITION_PARTIALLY_PAID: "\u201C\u90E8\u5206\u627F\u4FDD\u201D\u662F\u6307\u6211\u4EEC\u5C06\u627F\u62C5\u60A8\u7684\u90E8\u5206\u7D22\u8D54\u3002",
  CLAIMS_STATUS_DEFINITION_NOT_COVERED: "\u4E0D\u627F\u4FDD\u662F\u6307\u8FD9\u9879\u670D\u52A1\u4E0D\u5728\u798F\u5229\u8BA1\u5212\u7684\u627F\u4FDD\u8303\u56F4\u5185\uFF0C\u60A8\u9700\u8981\u81EA\u8D39\u652F\u4ED8\u8FD9\u7B14\u8D39\u7528\u3002",
  CLAIMS_STATUS_DEFINITION_ADJUSTED: "\u8C03\u6574\u540E\u610F\u5473\u7740\u53D1\u751F\u4E86\u4E00\u4E9B\u53D8\u5316\uFF0C\u56E0\u6B64\u6211\u4EEC\u4E0D\u5F97\u4E0D\u91CD\u65B0\u5BA1\u67E5\u8FD9\u7B14\u7D22\u8D54\u3002",
  YOUR_COST: "\u60A8\u7684\u8D39\u7528\uFF1A",
  CLAIMS_SERVICE_TYPE: "{type, select, institutional {\u533B\u7597\u7D22\u8D54} oral {\u7259\u79D1\u7D22\u8D54} vision {\u89C6\u529B\u7D22\u8D54} pharmacy {\u836F\u623F\u7D22\u8D54} professional {\u4E13\u4E1A\u7D22\u8D54} other {\u7D22\u8D54}}",
  FILTER_BY: "\u7B5B\u9009\u6761\u4EF6",
  CLAIMS_LIST_EMPTY_HEADING: "\u8FD9\u91CC\u6682\u65F6\u8FD8\u6CA1\u6709\u4EFB\u4F55\u5185\u5BB9\uFF01",
  CLAIMS_LIST_EMPTY_MESSAGE: "\u5DF2\u63D0\u4EA4\u7684\u7D22\u8D54\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\u3002",
  CLAIMS_GENERIC_ERROR_HEADING: "\u51FA\u4E86\u95EE\u9898",
  CLAIMS_GENERIC_ERROR_MESSAGE: "\u6211\u4EEC\u9047\u5230\u4E86\u6280\u672F\u95EE\u9898\u3002{br}\u8BF7\u51E0\u5206\u949F\u540E\u518D\u8BD5\u4E00\u6B21\u3002",
  CLAIMS_SUMMARY_PAGE_SUB_HEADING: "\u67E5\u770B\u60A8\u7684\u4FDD\u9669\u627F\u4FDD\u8303\u56F4\uFF0C\u4EE5\u53CA\u60A8\u53EF\u80FD\u8FD8\u6B20\u533B\u7597\u63D0\u4F9B\u8005\u591A\u5C11\u8D39\u7528\u3002\u60A8\u53EF\u4EE5\u67E5\u9605\u622A\u81F3\u4ECA\u65E5\u7684\u4E24\u5E74\u5185\u63D0\u4EA4\u7684\u7D22\u8D54\u3002",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_HEADING: "\u65E0\u7ED3\u679C",
  CLAIMS_LIST_NO_CLAIMS_MATCH_FILTER_MESSAGE: "{filterCount, plural, other {\u6CA1\u6709\u7B26\u5408\u60A8\u6240\u9009\u7B5B\u9009\u5668\u7684\u7D22\u8D54}}",
  VIEW_MY_PROFILE: "\u67E5\u770B\u6211\u7684\u4E2A\u4EBA\u8D44\u6599",
  PLAN_PROGRESS_COST_COVERAGE: "\u8BA1\u5212\u8FDB\u5EA6\u548C\u8D39\u7528",
  PLAN_PROGRESS_POP_UP_LABEL: "{title} \u662F\u4EC0\u4E48\uFF1F",
  RECENT_CLAIMS_LIST_EMPTY: "\u8FC7\u53BB 90 \u5929\u5185\u63D0\u4EA4\u7684\u7D22\u8D54\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\u3002\u9009\u62E9\u201C\u67E5\u770B\u5168\u90E8\u201D\u53EF\u4EE5\u8BBF\u95EE\u6240\u6709\u7D22\u8D54\u3002 ",
  DOWNLOAD_CLAIMS: "\u4E0B\u8F7D\u7D22\u8D54",
  PLAIN_SOMETHING_WENT_WRONG: "\u51FA\u4E86\u95EE\u9898",
  CLAIMS_SUMMARY_DOWNLOAD_ERROR: "\u4E0B\u8F7D\u60A8\u7684\u7D22\u8D54\u65F6\u51FA\u73B0\u95EE\u9898\u3002",
  WALLET_WITH_CLAIMS_LANDING_TITLE: "\u60A8\u7684\u798F\u5229\u3001\u7D22\u8D54\u548C\u5065\u5EB7\u4FE1\u606F\u5168\u90E8\u4FDD\u5B58\u5728\u4E00\u5904\u3002",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_HEADING: "\u51C6\u5907\u597D\u5F00\u59CB\u4E86\u5417\uFF1F",
  NO_CHALLENGES_PROGRAMS_EMPTY_STATE_BODY: "\u6D4F\u89C8\u6211\u4EEC\u7684\u8D44\u6599\u5E93\uFF0C\u67E5\u627E\u7B26\u5408\u60A8\u4E2A\u4EBA\u76EE\u6807\u7684\u5065\u5EB7\u8BA1\u5212\u548C\u6311\u6218\u3002",
  USER_DROPDOWN_BTN_LABEL: "\u4E2A\u4EBA\u8D44\u6599\u83DC\u5355",
  PROVIDER_SERVICE_POPOVER_BUTTON_NAME: "\u4EC0\u4E48\u662F\u5171\u4ED8\u989D\u548C\u5171\u540C\u4FDD\u9669\uFF1F",
  ANSWERS_NOT_SAVED: "\u6682\u65F6\u65E0\u6CD5\u4FDD\u5B58\u60A8\u7684\u56DE\u7B54\uFF0C\u8BF7\u7A0D\u540E\u518D\u8BD5\u3002",
  SESSION_ABOUT_TO_TIMEOUT: "\u60A8\u5DF2\u7ECF\u6709\u4E00\u6BB5\u65F6\u95F4\u6CA1\u6709\u6D3B\u52A8\u4E86\uFF0C\u60A8\u7684\u4F1A\u8BDD\u5373\u5C06\u8D85\u65F6\u3002\u8981\u7EE7\u7EED\u4F1A\u8BDD\u5417\uFF1F",
  SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR: "\u60A8\u5DF2\u7ECF\u6709\u4E00\u6BB5\u65F6\u95F4\u6CA1\u6709\u6D3B\u52A8\u4E86\uFF0C\u60A8\u7684\u4F1A\u8BDD\u5373\u5C06\u8D85\u65F6\u3002",
  SESSION_TIMEOUT: "\u4F1A\u8BDD\u8D85\u65F6",
  CONTINUE_SESSION: "\u7EE7\u7EED\u4F1A\u8BDD",
  SESSION_LOG_OUT: "\u6CE8\u9500",
  ORDER_NEW_CARD_ADDRESS_SELECT_HEADING: "\u9009\u62E9\u9001\u8D27\u5730\u5740",
  ORDER_NEW_CARD_MEMBER_SELECT_HEADING: "\u8C01\u9700\u8981\u65B0\u5361\uFF1F",
  ORDER_NEW_CARD_MEMBER_SELECT_HELP_TEXT: "\u9009\u62E9\u4E00\u540D\u6216\u591A\u540D\u6210\u5458\u3002",
  DEFAULT_ADDRESS: "\u9ED8\u8BA4\u5730\u5740",
  CHOOSE_NEW_ADDRESS: "\u9009\u62E9\u65B0\u5730\u5740",
  CHOOSE_NEW_ADDRESS_HINT: "\u4E3A\u5361\u7247\u5BC4\u9001\u63D0\u4F9B\u53E6\u4E00\u4E2A\u90AE\u5BC4\u5730\u5740\u3002\u8FD9\u4E0D\u4F1A\u66F4\u6539\u60A8\u8D26\u6237\u7684\u9ED8\u8BA4\u5730\u5740\u3002",
  CARD_ACTIONS: "\u5361\u7247\u64CD\u4F5C",
  EARN_ACHIEVEMENT_COMPLETING_ACTIVITIES_CHALLENGE: "\u5B8C\u6210\u672C\u6B21\u6311\u6218\u4E2D\u7684\u6240\u6709\u6D3B\u52A8\u5373\u53EF\u83B7\u5F97\u6210\u5C31",
  CHALLENGE_ACTIVITY_EMPTY_STATE_HEADER: "\u8FD9\u91CC\u6682\u65F6\u8FD8\u6CA1\u6709\u4EFB\u4F55\u5185\u5BB9\uFF01",
  CHALLENGE_ACTIVITY_UP_NEXT: "\u4E0B\u4E00\u4E2A",
  CHALLENGE_ACTIVITY_UP_NEXT_EMPTY_STATE: "\u6311\u6218\u5F00\u59CB\u65F6\u518D\u56DE\u6765\uFF01",
  CHALLENGE_ACTIVITY_COMPLETED: "\u5DF2\u5B8C\u6210",
  CHALLENGE_ACTIVITY_COMPLETED_EMPTY_STATE: "\u60A8\u5B8C\u6210\u7684\u6D3B\u52A8\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\u3002",
  CHALLENGE_ACTIVITY_MISSED: "\u9519\u8FC7",
  CHALLENGE_ACTIVITY_MISSED_EMPTY_STATE: "\u60A8\u9519\u8FC7\u7684\u6D3B\u52A8\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\u3002",
  CHALLENGE_COMPLETED_TITLE: "\u5230\u6B64\u7ED3\u675F\uFF01",
  CHALLENGE_COMPLETED_BODY: "\u6CA1\u6709\u66F4\u591A\u6D3B\u52A8\u9700\u8981\u5B8C\u6210\u3002\u5E72\u5F97\u6F02\u4EAE\uFF01",
  ACHIEVEMENT_EARNED: "\u83B7\u5F97\u6210\u5C31\u5E86\u795D\u6D3B\u52A8",
  ACHIEVEMENT_PROGRESS: "\u6210\u5C31\u8FDB\u5EA6",
  ADD_DATA: "\u6DFB\u52A0\u6570\u636E",
  CONFIRM_DETAILS: "\u786E\u8BA4\u60A8\u7684\u8BE6\u7EC6\u4FE1\u606F",
  MEDICAL_ID_CARD: "\u533B\u7597 ID \u5361",
  NEW_ADDRESS: "\u65B0\u9001\u8D27\u5730\u5740",
  MESSAGE_CENTER: "\u6D88\u606F\u4E2D\u5FC3",
  MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE: "\u6D88\u606F\u4F20\u9012",
  CHOOSE_NEW_ADDRESS_DESC: "\u4E3A\u5361\u7247\u5BC4\u9001\u63D0\u4F9B\u53E6\u4E00\u4E2A\u90AE\u5BC4\u5730\u5740\u3002\u8FD9\u4E0D\u4F1A\u66F4\u6539\u60A8\u8D26\u6237\u7684\u9ED8\u8BA4\u5730\u5740\u3002\u90AE\u5BC4\u5730\u5740\u5FC5\u987B\u5728\u7F8E\u56FD\u5883\u5185\u3002",
  SELECT_STATE: "\u9009\u62E9\u4E00\u4E2A\u5DDE",
  REQUIRED_FIELD: "{field} \u662F\u5FC5\u586B\u5B57\u6BB5",
  CLAIMS_YOUR_COST: "\u60A8\u7684\u8D39\u7528",
  CLAIMS_PROCESSING_DESCRIPTION: "\u6211\u4EEC\u4ECD\u5728\u5904\u7406\u8FD9\u7B14\u7D22\u8D54\u3002\u5F53\u5B83\u51C6\u5907\u597D\u4F9B\u60A8\u5BA1\u6838\u65F6\uFF0C\u60A8\u5C06\u6536\u5230\u901A\u77E5\u3002",
  DOWNLOAD_BENEFITS_EXPLANATION: "\u4E0B\u8F7D\u798F\u5229\u8BF4\u660E",
  DATA_COLLECTED: "\u6536\u96C6\u7684\u6570\u636E{date}",
  MEMBER_CARD_ON_ITS_WAY: "\u60A8\u7684\u65B0\u4F1A\u5458\u8EAB\u4EFD\u5361\u6B63\u5728\u90AE\u5BC4\u9014\u4E2D\u3002",
  MEMBER_CARD_ARRIVAL: "\u65B0\u7684\u4F1A\u5458\u8EAB\u4EFD\u5361\u5C06\u5728\u5927\u7EA6 10 \u4E2A\u5DE5\u4F5C\u65E5\u5185\u9001\u8FBE\u3002",
  TRY_LATER: "\u8BF7\u7A0D\u540E\u518D\u8BD5\u3002",
  RESUBMIT: "\u91CD\u65B0\u63D0\u4EA4",
  ORDER_NEW_CARD_HEADING: "\u8BA2\u8D2D\u4E00\u5F20\u65B0\u5361",
  CANCEL_ORDER_NEW_CARD_TITLE: "\u4F60\u786E\u5B9A\u8981\u53D6\u6D88\u8BA2\u5355\u5417\uFF1F",
  CANCEL_ORDER_NEW_CARD_DESC: "\u60A8\u53EF\u4EE5\u7A0D\u540E\u518D\u56DE\u6765\u3002",
  CANCEL_ORDER: "\u53D6\u6D88\u8BA2\u5355",
  STEP: "\u6B65\u9AA4",
  ORDER_CARD: "\u8BA2\u8D2D\u5361",
  PLEASE_MAKE_A_SELECTION: "\u8BF7\u9009\u62E9",
  TOTAL_COST_OVERVIEW: "\u603B\u8D39\u7528\u6982\u89C8",
  TOTAL_COST_DESCRIPTION: "\u60A8\u7684\u7D22\u8D54\u670D\u52A1\u7684\u8D39\u7528\u603B\u548C\u3002",
  SUPPORT: "\u652F\u6301",
  GET_SUPPORT: "\u83B7\u53D6\u652F\u6301",
  MESSAGE_WITH_A_MEMBER: "\u5982\u5BF9\u7D22\u8D54\u6709\u4EFB\u4F55\u7591\u95EE\uFF0C\u8BF7\u8054\u7CFB\u4F1A\u5458\u6743\u76CA\u7EF4\u62A4\u5458\u3002",
  CLAIMS_SERVICE_DETAILS_HEADING: "\u670D\u52A1\u8BE6\u60C5",
  CLAIMS_ADDITIONAL_RESOURCES_HEADING: "\u5176\u4ED6\u8D44\u6E90",
  CLAIMS_SERVICE_DETAILS_SUBHEADING: "\u60A8\u6536\u5230\u7684\u5404\u9879\u670D\u52A1\u53CA\u5176\u76F8\u5173\u8D39\u7528\u7684\u660E\u7EC6\u3002",
  CLAIMS_SERVICE_DETAILS_SERVICE: "\u670D\u52A1",
  CLAIMS_SERVICE_DETAILS_PHARMACY_SERVICE: "Rx \u7684\u540D\u79F0",
  CLAIMS_SERVICE_DETAILS_DATE: "\u670D\u52A1\u65E5\u671F",
  CLAIMS_SERVICE_DETAILS_SERVICE_PROCEDURE_CODE: "\u7A0B\u5E8F\u4EE3\u7801",
  CLAIMS_SERVICE_DETAILS_SERVICE_RX_NUMBER: "Rx \u7F16\u53F7",
  CLAIMS_SERVICE_DETAILS_SERVICE_YOUR_COST: "\u60A8\u7684\u8D39\u7528",
  CLAIMS_SERVICE_DETAILS_SERVICE_COST_DETAILS: "\u8D39\u7528\u8BE6\u60C5",
  CLAIMS_SERVICE_DETAILS_SHOW_COST_DETAILS: "\u663E\u793A\u8D39\u7528\u8BE6\u60C5",
  CLAIMS_SERVICE_DETAILS_HIDE_COST_DETAILS: "\u9690\u85CF\u8D39\u7528\u8BE6\u60C5",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_HEADING: "\u8D39\u7528\u660E\u7EC6",
  CLAIMS_SERVICE_DETAILS_YOUR_COST_BREAKDOWN_SUBHEADING: "\u4EE5\u4E0B\u91D1\u989D\u5C06\u7528\u4E8E\u60A8\u7684\u8BA1\u5212\u8FDB\u5EA6",
  WALLET_ORDER_NEW_CARD_SELECT_MEMBER_ERROR: "\u9009\u62E9\u4E00\u540D\u6216\u591A\u540D\u6210\u5458\u4EE5\u7EE7\u7EED",
  WALLET_INVALID_CHARACTER_ERROR_MESSAGE: "\u5B57\u6BB5\u5305\u542B\u65E0\u6548\u5B57\u7B26",
  CLAIMS_COST_DETAILS_PROVIDER_CHARGED: "\u533B\u7597\u63D0\u4F9B\u8005\u5DF2\u6536\u8D39",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE: "\u5176\u4ED6\u5DF2\u4ED8\u4FDD\u9669",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT: "\u60A8\u7684\u4F1A\u5458\u6298\u6263",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP: "\u5DF2\u4E0E\u533B\u7597\u63D0\u4F9B\u8005\u534F\u5546\u60A8\u7684\u8BA1\u5212\uFF0C\u4E3A\u60A8\u63D0\u4F9B\u6298\u6263\u4EF7\u3002",
  CLAIMS_COST_DETAILS_YOUR_MEMBER_DISCOUNT_TOOLTIP_LABEL: "\u60A8\u7684\u4F1A\u5458\u6298\u6263\u8BF4\u660E",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP: "\u60A8\u5176\u4ED6\u4FDD\u9669\u652F\u4ED8\u7684\u91D1\u989D\u3002",
  CLAIMS_COST_DETAILS_YOUR_OTHER_INSURANCE_TOOLTIP_LABEL: "\u5176\u4ED6\u5DF2\u4ED8\u4FDD\u9669\u8D39\u7684\u89E3\u91CA",
  CLAIMS_COST_DETAILS_WE_PAID_ON: "{paymentDate, select, other {\u60A8\u7684\u8BA1\u5212\u5DF2\u4E8E {paymentDate} \u652F\u4ED8\u8D39\u7528} undefined {\u60A8\u7684\u8BA1\u5212\u5DF2\u652F\u4ED8\u8D39\u7528}}",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP: "\u60A8\u7684\u8BA1\u5212\u6240\u6DB5\u76D6\u7684\u533B\u7597\u4FDD\u5065\u670D\u52A1\u91D1\u989D\u3002",
  CLAIMS_COST_DETAILS_WE_PAID_ON_TOOLTIP_LABEL: "\u60A8\u7684\u8BA1\u5212\u5DF2\u652F\u4ED8\u8D39\u7528\u8BF4\u660E",
  CLAIMS_COST_DETAILS_YOUR_COST: "\u60A8\u7684\u8D39\u7528",
  CLAIMS_SERVICE_DETAILS_SERVICE_REMARK: "\u670D\u52A1\u5907\u6CE8",
  CLAIMS_SERVICE_NETWORK_IN_NETWORK: "\u7F51\u7EDC\u5185",
  CLAIMS_SERVICE_NETWORK_OUT_OF_NETWORK: "\u7F51\u7EDC\u5916",
  WALLET_ORDER_NEW_CARD_SELECT_ADDRESS_OPTION_ERROR: "\u9009\u62E9\u5730\u5740\u9009\u9879\u7EE7\u7EED",
  WALLET_ORDER_NEW_CARD_NO_CITY_NUMBERS_ERROR: "\u57CE\u5E02\u4E0D\u80FD\u5305\u542B\u6570\u5B57",
  MESSAGE_CLOSED_TEXT: "\u6B64\u6D88\u606F\u5DF2\u9001\u8FBE\u3002",
  CLAIMS_PLAN_PROGRESS_DESCRIPTION: "\u4E86\u89E3\u60A8\u7684\u7D2F\u8BA1\u8D39\u7528\u3001\u5206\u644A\u8D39\u7528\u7B49\u3002",
  VIEW_PLAN_PROGRESS: "\u67E5\u770B\u8BA1\u5212\u8FDB\u5EA6",
  CLAIMS_BILL_PROVIDER_DESCRIPTION: "\u60A8\u5C06\u4ECE\u533B\u7597\u63D0\u4F9B\u8005\u5904\u6536\u5230\u4E0E\u8BE5\u7D22\u8D54\u76F8\u5173\u7684\u8D26\u5355\u3002",
  CLAIMS_EXPLANATION_OF_BENEFITS_DOWNLOAD_ERROR: "\u4E0B\u8F7D\u60A8\u7684\u798F\u5229\u8BF4\u660E\u65F6\u51FA\u73B0\u95EE\u9898\u3002",
  END_CHAT_MODAL_DESCRIPTION: "\u7EE7\u7EED\u64CD\u4F5C\u5C06\u4F1A\u7ED3\u675F\u804A\u5929\u3002\u60A8\u5C06\u65E0\u6CD5\u5728\u4EE5\u540E\u8FD4\u56DE\u6B64\u5BF9\u8BDD\u3002",
  RETURN_TO_CHAT: "\u8FD4\u56DE\u804A\u5929",
  CHATS_IN_CONVERSATION: "\u5BF9\u8BDD\u4E2D\u7684\u804A\u5929",
  LIVE_CHAT_CHARACTER_LIMIT: "\u5B57\u7B26\u6570\u5DF2\u8FBE\u4E0A\u9650 ({characterLimit})",
  CHAT_MESSAGE_INPUT: "\u804A\u5929\u4FE1\u606F\u8F93\u5165",
  CLAIMS_SERVICE_LINE_ITEM_LABEL: "{code, select, deductible {\u514D\u8D54\u989D} non_covered {\u4E0D\u5728\u627F\u4FDD\u8303\u56F4} copayment {\u5171\u4ED8\u989D} coinsurance {\u5171\u540C\u4FDD\u9669} may_be_billed {\u60A8\u53EF\u80FD\u9700\u8981\u81EA\u8D39} amount_exceeding_maximum {\u8D85\u8FC7\u6700\u5927\u9650\u989D} additional_member_liability {\u5176\u4ED6\u6210\u5458\u8D23\u4EFB} special_processing {\u7279\u6B8A\u5904\u7406} paid_to_member {\u652F\u4ED8\u7ED9\u4F1A\u5458} plan_paid_to_you {\u5DF2\u5411\u60A8\u652F\u4ED8\u7684\u8BA1\u5212} other {\u5176\u4ED6}}",
  QUESTIONNAIRE_FLOW_NEW_MESSAGE: "\u65B0\u6D88\u606F",
  QUESTIONNAIRE_FLOW_CLOSE: "\u5173\u95ED",
  QUESTIONNAIRE_FLOW_GO_BACK: "\u8FD4\u56DE",
  QUESTIONNAIRE_FLOW_CONTINUE: "\u7EE7\u7EED",
  QUESTIONNAIRE_FLOW_RETRY: "\u91CD\u8BD5",
  CLEAR_ALL: "\u6E05\u9664\u5168\u90E8",
  HEALTH_REPORT_PAGE_HEADING: "\u60A8\u7684\u5065\u5EB7\u62A5\u544A",
  USERNAME_HAS_JOINED_THE_CHAT: "{username} \u5DF2\u52A0\u5165\u804A\u5929",
  USERNAME_HAS_LEFT_THE_CHAT: "{username} \u5DF2\u7ECF\u9000\u51FA\u804A\u5929",
  PERVIOUS_MONTH: "\u4E0A\u4E2A\u6708\u6309\u94AE",
  NEXT_MONTH: "\u4E0B\u4E2A\u6708\u6309\u94AE",
  CHAT_CLOSED: "\u804A\u5929\u5DF2\u5173\u95ED\u3002",
  CONNECTING_YOU_WITH_AN_AGENT: "\u6B63\u5728\u5E2E\u60A8\u8054\u7CFB\u4EE3\u7406\u2026\u2026",
  LAST_DAY_TO_USE_BALANCE: "\u4F7F\u7528\u4F59\u989D\u7684\u6700\u540E\u4E00\u5929",
  LAST_DAY_TO_SUBMIT_CLAIMS: "\u63D0\u4EA4\u7D22\u8D54\u7684\u6700\u540E\u4E00\u5929",
  LIVE_CHAT_GENERIC_ERROR_TITLE: "\u51FA\u4E86\u95EE\u9898",
  LIVE_CHAT_GENERIC_ERROR_DESCRIPTION: "\u51FA\u73B0\u4E86\u95EE\u9898\uFF0C\u8BF7\u91CD\u8BD5",
  LIVE_CHAT_GENERIC_TOAST_ERROR_TITLE: "\u51FA\u4E86\u95EE\u9898",
  LIVE_CHAT_GENERIC_TOAST_ERROR_DESCRIPTION: "\u51FA\u73B0\u4E86\u95EE\u9898\uFF0C\u8BF7\u91CD\u8BD5",
  LIVE_CHAT_GENERIC_ERROR_BUTTON: "\u5173\u95ED",
  LIVE_CHAT_LANDING_PAGE_TITLE: "\u804A\u5929",
  LIVE_CHAT_DELIVERY_FAILURE: "\u672A\u9001\u8FBE",
  LIVE_CHAT_RESEND_MESSAGE: "\u91CD\u65B0\u53D1\u9001\u4FE1\u606F",
  LIVE_CHAT_MESSAGE_FAILED_TITLE: "\u6D88\u606F\u53D1\u9001\u5931\u8D25",
  LIVE_CHAT_MESSAGE_FAILED_BODY: "\u5F88\u62B1\u6B49\uFF0C\u60A8\u7684\u4FE1\u606F\u4F3C\u4E4E\u6CA1\u6709\u53D1\u9001\u7ED9\u6211\u4EEC\u7684\u56E2\u961F\u3002",
  VIEW_TOOLTIP: "\u67E5\u770B{tooltipLabel}\u63D0\u793A",
  NO_DATA_SOURCES: "\u65E0\u6570\u636E\u6765\u6E90",
  MAX_VALUE_ERROR_MESSAGE: "\u8BF7\u8F93\u5165\u5C0F\u4E8E\u6216\u7B49\u4E8E {max} \u7684\u503C",
  MIN_VALUE_ERROR_MESSAGE: "\u8BF7\u8F93\u5165\u5927\u4E8E\u6216\u7B49\u4E8E {min} \u7684\u503C",
  NO_VALUE_SELECTED_ERROR_MESSAGE: "\u8BF7\u9009\u62E9\u4E00\u4E2A\u503C",
  START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE: "\u5F00\u59CB\u65E5\u671F\u5FC5\u987B\u65E9\u4E8E\u7ED3\u675F\u65E5\u671F",
  DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE: "\u65F6\u957F\u4E0D\u80FD\u8D85\u8FC724\u5C0F\u65F6",
  NO_FUTURE_DATES: "\u65E5\u671F\u4E0D\u80FD\u5728\u672A\u6765",
  CLAIMS_REMOVE_FILTER: "\u79FB\u9664{label}\u7B5B\u9009\u5668\u3002",
  REMOVE_X_FILTER: "\u79FB\u9664{label}\u7B5B\u9009\u5668\u3002",
  WALLET_BENEFIT_STATUS_PENDING: "\u5F85\u5904\u7406",
  WALLET_BENEFIT_STATUS_ACTIVE: "\u6D3B\u8DC3",
  WALLET_BENEFIT_STATUS_PAST: "\u8FC7\u53BB",
  CLAIMS_CLAIMS_LIST_HEADING: "\u60A8\u7684\u7D22\u8D54",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_TITLE: "\u8FD9\u91CC\u6682\u65F6\u8FD8\u6CA1\u6709\u4EFB\u4F55\u5185\u5BB9\uFF01",
  WALLET_LANDING_BENEFITS_EMPTY_STATE_DESCRIPTION: "\u5DF2\u6FC0\u6D3B\u7684\u798F\u5229\u5C06\u4F1A\u663E\u793A\u5728\u8FD9\u91CC\u3002",
  BENEFITS_BENEFITS_LANDING_PAGE_TITLE: "\u798F\u5229",
  BENEFITS_BENEFITS_ID_CARDS_PAGE_TITLE: "\u798F\u5229 - \u8EAB\u4EFD\u8BC1",
  CLAIMS_CLAIMS_SUMMARY_PAGE_TITLE: "\u7D22\u8D54",
  CLAIMS_CLAIMS_DETAIL_PAGE_TITLE: "\u7D22\u8D54\u8BE6\u60C5",
  CLAIMS_CLAIMS_DETAIL_WITH_TITLE_PAGE_TITLE: "\u7D22\u8D54\u8BE6\u60C5 - {title}",
  CLAIMS_OWED_AMOUNT_MISSING_LABEL: "\u5F85\u5904\u7406",
  CLAIMS_STATUS_PENDING: "\u5F85\u5904\u7406",
  CSE_END_IMPERSONATION_MODE: "\u7ED3\u675F\u6A21\u4EFF\u6A21\u5F0F",
  CSE_RESTRICTED_ACTION: "\u53D7\u9650\u64CD\u4F5C",
  CSE_RESTRICTED_ACTION_MESSAGE: "\u5728\u7528\u6237\u6A21\u62DF\u6A21\u5F0F\u4E0B\uFF0C\u60A8\u53EA\u80FD\u67E5\u770B\u8BE5\u6210\u5458\u7684\u95E8\u6237\u3002\u4E0D\u5141\u8BB8\u4EE3\u8868\u6210\u5458\u6267\u884C\u64CD\u4F5C\u3002",
  CSE_RESTRICTED_VIEW_MESSAGE: "\u60A8\u6CA1\u6709\u8BBF\u95EE\u6B64\u90E8\u5206\u7684\u6743\u9650\u3002",
  CSE_IMPERSONATION_MASONRY_HEADER: "\u652F\u6301\u4EE3\u7406\u7684\u53D7\u9650\u5185\u5BB9",
  CSE_IMPERSONATION_MASONRY_MESSAGE: "\u60A8\u53EF\u80FD\u6CA1\u6709\u67E5\u770B\u67D0\u4E9B\u5C0F\u5DE5\u5177\u6216\u5185\u5BB9\u7684\u6743\u9650\u3002",
  CSE_IMPERSONATION_BANNER_TEXT: "{memberName} \u7684\u4EC5\u67E5\u770B\u6A21\u5F0F\u3002",
  CLAIMS_SUMMARY_PAGE_HEADING: "\u7D22\u8D54",
  HEALTH_INSURANCE_GLOSSARY: "\u5065\u5EB7\u4FDD\u9669\u672F\u8BED\u8868",
  CLAIMS_FILTER_LOADING_TEXT: "\u6B63\u5728\u52A0\u8F7D\u9009\u9879\u5361\u548C\u7B5B\u9009\u5668",
  ANDROID_HEALTH_CONNECT: "\u5065\u5EB7\u8FDE\u63A5",
  GET_CARE_SERVICE_CARD_CTA: "\u67E5\u770B\u670D\u52A1",
  BENEFITS_ORDER_NEW_CARD_NEXT_LABEL: "\u4E0B\u4E00\u6B65",
  BENEFITS_ORDER_NEW_CARD_PREVIOUS_LABEL: "\u4E0A\u4E00\u6B65",
  HEIGHT: "\u8EAB\u9AD8",
  WEIGHT: "\u4F53\u91CD"
};

// src/constants.ts
var DEFAULT_MESSAGES = {
  default: en_default,
  en: en_default,
  es: es_MX_default,
  ko: ko_default,
  fil: fil_default,
  fr: fr_CA_default,
  vi: vi_default,
  zh: zh_default
};
var supportedLocaleList = ["en", "es", "ko", "fil", "fr", "vi", "zh"];
var CA_COUNTRY_CODE = "CA";
var US_COUNTRY_CODE = "US";
var supportedLocaleMapping = {
  [CA_COUNTRY_CODE]: ["en", "fr"],
  [US_COUNTRY_CODE]: ["en", "es-MX"]
};
var DEFAULT_LOCALE = "en";
var LANGUAGE_COOKIE_NAME = "league_current_language";

// src/get-dom-lang.ts
var langElement = typeof document !== "undefined" ? document.documentElement : void 0;
var getDOMLang = () => (langElement == null ? void 0 : langElement.lang) || DEFAULT_LOCALE;
var setLangElement = (element) => {
  if (element.lang) {
    langElement = element;
  }
};
var getNearestLangElement = (element) => {
  let currentElement = element;
  while (currentElement) {
    if (currentElement.lang) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }
  return void 0;
};

// src/utils/cookie-utils.ts
import cookie from "cookie";
var internalSetCookie = (name, value) => {
  const rawCookie = {};
  rawCookie[name] = value;
  if (typeof value === "object") {
    rawCookie[name] = JSON.stringify(value);
  }
  document.cookie = cookie.serialize(name, rawCookie[name], { path: "/" });
  return document.cookie;
};
var getCookie = (name) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  const cookies = cookie.parse(document.cookie);
  const value = cookies && cookies[name];
  let parsedValue = null;
  try {
    parsedValue = JSON.parse(value);
  } catch (e) {
  }
  return parsedValue || value;
};

// src/get-current-locale.ts
var getCurrentLocale = (defaultLocale = DEFAULT_LOCALE) => {
  var _a, _b;
  const { config, status } = getConfig(false);
  const isConfigEmpty = status === ((_a = ConfigStoreStatus) == null ? void 0 : _a.Empty);
  const shouldIgnoreDOMLangAttributeForLocale = isConfigEmpty || // eslint-disable-next-line no-underscore-dangle
  !!((_b = config == null ? void 0 : config.__internal) == null ? void 0 : _b.shouldIgnoreDOMLangAttributeForLocale);
  return getCookie(LANGUAGE_COOKIE_NAME) || (!shouldIgnoreDOMLangAttributeForLocale ? getDOMLang() : void 0) || typeof navigator !== "undefined" && (navigator == null ? void 0 : navigator.language) || defaultLocale;
};

// src/get-react-intl-props.ts
import { isProduction } from "@leagueplatform/app-environment";
import { getConfig as getConfig2 } from "@leagueplatform/config";

// src/register-messages.tsx
import merge from "lodash/merge";
var registeredMessages = { default: {} };
function registerMessages(messages) {
  merge(registeredMessages, messages);
}

// src/get-react-intl-props.ts
var getReactIntlProps = (defaultLocale, supportedLocaleList2, locale = defaultLocale) => {
  var _a, _b;
  const language = locale.split("-")[0];
  const localeMessages = DEFAULT_MESSAGES[locale] || DEFAULT_MESSAGES[language];
  const registeredMessagesForLocale = registeredMessages[language] || registeredMessages.default;
  const customLocalesDeprecated = (_a = getConfig2().config.core.i18n) == null ? void 0 : _a.stringOverrides;
  const customLocales = ((_b = getConfig2().config.core.i18n) == null ? void 0 : _b.strings) || customLocalesDeprecated;
  const customLocaleMessages = (customLocales == null ? void 0 : customLocales[language]) || (customLocales == null ? void 0 : customLocales.default);
  const messages = __spreadValues(__spreadValues(__spreadValues(__spreadValues({}, DEFAULT_MESSAGES.default), localeMessages), registeredMessagesForLocale), customLocaleMessages);
  if (!isProduction() && typeof window !== "undefined" && localStorage.getItem("LEAGUE_SHOW_LOCALE_KEYS")) {
    Object.keys(messages).forEach((key) => messages[key] = `<${key}>`);
  }
  return {
    locale: supportedLocaleList2.includes(language) ? locale : defaultLocale,
    messages
  };
};

// src/get-app-supported-locale.ts
var getAppSupportedLocale = (defaultLocale = DEFAULT_LOCALE, supportedLocaleList2 = supportedLocaleList) => {
  var _a;
  const languageLocale = (_a = navigator == null ? void 0 : navigator.language) == null ? void 0 : _a.split("-")[0];
  if (languageLocale && supportedLocaleList2.includes(languageLocale))
    return languageLocale;
  return defaultLocale;
};

// src/intl-shape.ts
import PropTypes from "prop-types";
var intlShape = PropTypes.shape({
  formatMessage: PropTypes.func,
  formatHTMLMessage: PropTypes.func,
  formatNumber: PropTypes.func,
  formatDate: PropTypes.func
});

// src/utils/is-french.ts
var isFrench = (defaultLocale = DEFAULT_LOCALE) => getCurrentLocale(defaultLocale).includes("fr");

// src/utils/is-spanish.ts
var isSpanish = (defaultLocale = DEFAULT_LOCALE) => getCurrentLocale(defaultLocale).includes("es");

// src/league-intl-provider/league-intl-provider.component.tsx
import React3, { useState as useState2, useRef, useEffect as useEffect2 } from "react";
import { isNodeTestEnv as isNodeTestEnv2 } from "@leagueplatform/app-environment";

// src/league-intl-provider/intl-provider-wrapper.component.tsx
import React2, { useContext } from "react";
import { IntlProvider } from "react-intl";
import { isNodeTestEnv } from "@leagueplatform/app-environment";
var defaultRichTextElements = Object.fromEntries(
  ["strong", "p", "em", "li", "ul", "br", "span", "button"].map((tag) => {
    const formatter = (chunks) => chunks.length ? `<${tag}>${chunks}</${tag}>` : `<${tag} />`;
    return [tag, formatter];
  })
);
var LocaleContext = React2.createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {
  }
});
var onWarn = isNodeTestEnv() ? () => {
} : void 0;
var IntlProviderWrapper = ({ children }) => {
  const { locale } = useContext(LocaleContext);
  const intlProps = getReactIntlProps(
    DEFAULT_LOCALE,
    supportedLocaleList,
    locale
  );
  return /* @__PURE__ */ React2.createElement(
    IntlProvider,
    {
      onWarn,
      locale: intlProps.locale,
      messages: intlProps.messages,
      defaultRichTextElements
    },
    children
  );
};

// src/hooks/use-cookie/use-cookie-hook.ts
import { useState, useEffect } from "react";
var useCookie = (cookieName) => {
  const initialCookieValue = getCookie(cookieName);
  const [cookie2, setCookieState] = useState();
  const setCookie = (value) => {
    internalSetCookie(cookieName, value);
    setCookieState(value);
  };
  useEffect(() => {
    setCookieState(initialCookieValue);
  }, [initialCookieValue]);
  return { cookie: cookie2, setCookie };
};

// src/league-intl-provider/league-intl-provider.component.tsx
var LeagueIntlProvider = ({ children }) => {
  const { cookie: cookieLocale, setCookie: setLocale } = useCookie(LANGUAGE_COOKIE_NAME);
  const [resolvedLocale, setResolvedLocale] = useState2(
    () => cookieLocale || getCurrentLocale(DEFAULT_LOCALE)
  );
  const langDetectorRef = useRef(null);
  useEffect2(() => {
    let observer;
    if (langDetectorRef.current) {
      const nearestLangElement = getNearestLangElement(langDetectorRef.current);
      if (nearestLangElement) {
        setLangElement(nearestLangElement);
        setResolvedLocale(getCurrentLocale(DEFAULT_LOCALE));
        observer = new MutationObserver((mutations) => {
          const target = mutations[0].target;
          const newLang = target.lang;
          if (newLang) {
            setResolvedLocale(getCurrentLocale(DEFAULT_LOCALE));
          }
        });
        observer.observe(nearestLangElement, {
          attributeFilter: ["lang"]
        });
      }
    }
    return () => {
      if (observer) {
        observer.disconnect();
      }
      setLangElement(document.documentElement);
    };
  }, []);
  return /* @__PURE__ */ React3.createElement(
    LocaleContext.Provider,
    {
      value: { locale: resolvedLocale, setLocale }
    },
    /* @__PURE__ */ React3.createElement(IntlProviderWrapper, null, children),
    !isNodeTestEnv2() && /* @__PURE__ */ React3.createElement("span", { "aria-hidden": "true", id: "lang-detector", ref: langDetectorRef })
  );
};

// src/formatted-html-message.component.tsx
import React4 from "react";
var FormattedHTMLMessage = ({
  id,
  description,
  defaultMessage,
  values,
  ignoreTag,
  tagName = "span",
  children
}) => {
  const { formatMessage } = useIntl();
  const descriptor = {
    id,
    description,
    defaultMessage
  };
  const nodes = formatMessage(descriptor, values, { ignoreTag });
  if (children) {
    return children(Array.isArray(nodes) ? nodes : [nodes]);
  }
  const Tag = tagName;
  return /* @__PURE__ */ React4.createElement(
    Tag,
    {
      dangerouslySetInnerHTML: {
        __html: nodes
      }
    }
  );
};
export {
  CA_COUNTRY_CODE,
  DEFAULT_LOCALE,
  DEFAULT_MESSAGES,
  en_default as EN,
  es_MX_default as ES,
  fil_default as FIL,
  fr_CA_default as FR,
  FormattedHTMLMessage,
  ko_default as KO,
  LeagueIntlProvider,
  LocaleContext,
  US_COUNTRY_CODE,
  vi_default as VI,
  zh_default as ZH,
  defaultRichTextElements,
  getAppSupportedLocale,
  getCurrentLocale,
  getReactIntlProps,
  intlShape,
  isFrench,
  isSpanish,
  registerMessages,
  supportedLocaleList,
  supportedLocaleMapping,
  useIntl
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/locales'] = window.leagueSDKsPresent['@leagueplatform/locales'] || [];
    window.leagueSDKsPresent['@leagueplatform/locales'].push('2.23.5');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/locales'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/locales module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
